// _BUILD_ID = "accountability-20260402-v2"
import React, { useEffect, useState, useCallback, useRef, type ElementType } from "react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { useAuth } from "@/components/auth/AuthContext";
import { apiFetch, uploadFileToBase44 } from "@/lib/apiFetch";
import { normaliseReadiness } from "@/lib/normaliseReadiness";
import { BRANCHES, UNIT_TYPES_BY_BRANCH, GAMES_LIST as MC_GAMES, type Branch } from "@/lib/milsimConstants";
import { RIBBON_TEMPLATES, RIBBON_COUNTRIES, RIBBON_BRANCHES_BY_COUNTRY, type RibbonTemplate } from "@/lib/ribbonLibrary";
import { getRibbonModifiers } from "@/lib/ribbonModifiers";
import { RibbonBuilder, RibbonPreview, ribbonToSvgDataUri, type RibbonConfig, type StripePattern } from "@/components/RibbonBuilder";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Award,
  Ban,
  BarChart3,
  BookOpen,
  Brain,
  Calendar,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Clock,
  Crosshair,
  Crown,
  ExternalLink,
  Eye,
  EyeOff,
  FileCheck,
  FileText,
  Flag,
  Hash,
  GitBranch,
  GraduationCap,
  Link2,
  Loader2,
  Map,
  MapPin,
  Medal,
  Search,
  Megaphone,
  Pencil,
  PlaneTakeoff,
  Plus,
  Radio,
  RadioTower,
  RefreshCw,
  Zap,
  Save,
  Shield,
  ShieldCheck,
  Siren,
  Star,
  Target,
  Globe,
  Globe2,
  Trash2,
  TrendingUp,
  TrendingDown,
  Heart,
  Trophy,
  Upload,
  Users,
  Wifi,
  WifiOff,
  X,
  XCircle,
  UserCheck,
  UserMinus,
  UserMinus2,
  UserPlus,
  UserX,
  Archive,
  FileEdit,
  BookCheck,
  Bell,
  ChevronRight,
  ChevronLeft,
  Download,
  BrainCircuit,
  FlaskConical,
  Tag,
  Gamepad2,
  Server,
  Package,
  Lock,
  Copy,
  List,
  CheckSquare,
  Square,
  Swords,
  Antenna,
  PhoneCall,
  ShieldAlert,
  AlertOctagon,
  ListChecks,
  Layers,
  Milestone,
  Workflow,
  MessageSquare,
  Send,
  Flame,
  ClipboardCheck,
  Loader2,
  ScrollText,
  Twitter,
  Instagram,
  Music2,
  Gamepad2,
  Youtube,
  Tv2,
  Settings,
  Briefcase,
  Newspaper,
  Film,
  ImagePlus,
  Video,
} from "lucide-react";
const _BUILD = "20260510-groupcomms-live";

// ─── URL constants (declared here to avoid TDZ — used throughout file) ────────
const _PRO_STATUS_URL_MANAGE = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions/getProStatus";
const PRO_STATUS_URL_MANAGE  = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions/getProStatus";
import { normaliseAars, normaliseLoas, normaliseConductReports, normaliseSitreps, normaliseLaceReports, normaliseOps, normaliseApplications, normaliseBriefings } from '@/lib/normaliseApi';

// ╔══════════════════════════════════════════════════════════════╗
// ║              STRUCTURE ENGINE — v2.0                         ║
// ╚══════════════════════════════════════════════════════════════╝
interface StructureTier {
  id: string; label: string; minHeadcount: number; maxHeadcount: number;
  description: string; sublabel: string;
  idealRatios: { enlisted: number; nco: number; snco: number; officer: number; staff: number };
  maxOfficers: number; maxSNCOs: number; maxNCOs: number; unlocksRankClasses: string[];
}
// HC RANGES (British):
//   Fireteam:   1–7    (1–4 ideal, up to 7 before becoming a section)
//   Section:    8–35   (8–27 with 1 JNCO per 8, progress 4× headcount to get to platoon)
//   Platoon:    36–71  (36–72 with 1 officer per 36, 1 SNCO per section)
//   Company:    72–287 (72–288, platoon allowances ×2)
//   Battalion:  288–1151 (288–1152, company ×2)
//   Regiment:   1152–4607 (1152–4608, battalion ×2)
//   Battlegroup:4608+
const BRITISH_TIERS: StructureTier[] = [
  { id: "fireteam",    label: "Fireteam",    minHeadcount: 1,    maxHeadcount: 7,
    sublabel: "Headcount 1–7",
    description: "Fireteam / fire-and-manoeuvre pair. Enlisted & JNCOs only.",
    idealRatios: { enlisted: 80, nco: 20, snco: 0, officer: 0, staff: 0 },
    maxOfficers: 0, maxSNCOs: 0, maxNCOs: 1, unlocksRankClasses: ["enlisted","nco"] },
  { id: "section",     label: "Section",     minHeadcount: 8,    maxHeadcount: 35,
    sublabel: "Headcount 8–35",
    description: "Section / squad level. 1 JNCO per 8 men. Enlisted and JNCOs only.",
    idealRatios: { enlisted: 75, nco: 25, snco: 0, officer: 0, staff: 0 },
    maxOfficers: 0, maxSNCOs: 0, maxNCOs: 4, unlocksRankClasses: ["enlisted","nco"] },
  { id: "platoon",     label: "Platoon",     minHeadcount: 36,   maxHeadcount: 71,
    sublabel: "Headcount 36–71",
    description: "Unlocks Platoon Sgt, 2Lt / Lt Pl Comd. 1 Officer per 36 headcount.",
    idealRatios: { enlisted: 60, nco: 25, snco: 8, officer: 4, staff: 3 },
    maxOfficers: 1, maxSNCOs: 2, maxNCOs: 8, unlocksRankClasses: ["enlisted","nco","snco","junior_officer"] },
  { id: "company",     label: "Company",     minHeadcount: 72,   maxHeadcount: 287,
    sublabel: "Headcount 72–287",
    description: "Unlocks CSM, CQMS, OC (Capt/Major). Platoon allowances ×2.",
    idealRatios: { enlisted: 55, nco: 25, snco: 10, officer: 6, staff: 4 },
    maxOfficers: 2, maxSNCOs: 4, maxNCOs: 16, unlocksRankClasses: ["enlisted","nco","snco","junior_officer","senior_officer","staff"] },
  { id: "battalion",   label: "Battalion",   minHeadcount: 288,  maxHeadcount: 1151,
    sublabel: "Headcount 288–1151",
    description: "Unlocks RSM, Adjutant, CO (Lt Col+). Company allowances ×2.",
    idealRatios: { enlisted: 52, nco: 27, snco: 11, officer: 7, staff: 3 },
    maxOfficers: 4, maxSNCOs: 8, maxNCOs: 32, unlocksRankClasses: ["enlisted","nco","snco","junior_officer","senior_officer","staff"] },
  { id: "regiment",    label: "Regiment",    minHeadcount: 1152, maxHeadcount: 4607,
    sublabel: "Headcount 1152–4607",
    description: "Unlocks Regimental Colonel, RSM (Regimental), full staff corps.",
    idealRatios: { enlisted: 50, nco: 28, snco: 12, officer: 7, staff: 3 },
    maxOfficers: 8, maxSNCOs: 16, maxNCOs: 64, unlocksRankClasses: ["enlisted","nco","snco","junior_officer","senior_officer","staff"] },
  { id: "battlegroup", label: "Brigade", minHeadcount: 4608, maxHeadcount: 99999,
    sublabel: "Headcount 4608+",
    description: "Brigade scale. Full command structure.",
    idealRatios: { enlisted: 50, nco: 28, snco: 12, officer: 7, staff: 3 },
    maxOfficers: 16, maxSNCOs: 32, maxNCOs: 128, unlocksRankClasses: ["enlisted","nco","snco","junior_officer","senior_officer","general","staff"] },
];
// US mirrors same HC ranges but uses US nomenclature
const US_TIERS: StructureTier[] = [
  { id: "fireteam",    label: "Fire Team",   minHeadcount: 1,    maxHeadcount: 7,
    sublabel: "Headcount 1–7",
    description: "Fireteam level. E1–E4 only.",
    idealRatios: { enlisted: 80, nco: 20, snco: 0, officer: 0, staff: 0 },
    maxOfficers: 0, maxSNCOs: 0, maxNCOs: 1, unlocksRankClasses: ["enlisted","nco"] },
  { id: "section",     label: "Squad",       minHeadcount: 8,    maxHeadcount: 35,
    sublabel: "Headcount 8–35",
    description: "Squad level. 1 NCO per 8 men. E1–E5 only.",
    idealRatios: { enlisted: 75, nco: 25, snco: 0, officer: 0, staff: 0 },
    maxOfficers: 0, maxSNCOs: 0, maxNCOs: 4, unlocksRankClasses: ["enlisted","nco"] },
  { id: "platoon",     label: "Platoon",     minHeadcount: 36,   maxHeadcount: 71,
    sublabel: "Headcount 36–71",
    description: "Unlocks SSG / Plt Sgt, one 2LT/1LT.",
    idealRatios: { enlisted: 60, nco: 25, snco: 8, officer: 4, staff: 3 },
    maxOfficers: 1, maxSNCOs: 2, maxNCOs: 8, unlocksRankClasses: ["enlisted","nco","snco","junior_officer"] },
  { id: "company",     label: "Company",     minHeadcount: 72,   maxHeadcount: 287,
    sublabel: "Headcount 72–287",
    description: "Unlocks 1SG, XO, CO (CPT). Platoon allowances ×2.",
    idealRatios: { enlisted: 55, nco: 25, snco: 10, officer: 6, staff: 4 },
    maxOfficers: 2, maxSNCOs: 4, maxNCOs: 16, unlocksRankClasses: ["enlisted","nco","snco","junior_officer","senior_officer","staff"] },
  { id: "battalion",   label: "Battalion",   minHeadcount: 288,  maxHeadcount: 1151,
    sublabel: "Headcount 288–1151",
    description: "Unlocks CSM, XO (MAJ), CO (LTC). Company allowances ×2.",
    idealRatios: { enlisted: 52, nco: 27, snco: 11, officer: 7, staff: 3 },
    maxOfficers: 4, maxSNCOs: 8, maxNCOs: 32, unlocksRankClasses: ["enlisted","nco","snco","junior_officer","senior_officer","staff"] },
  { id: "regiment",    label: "Regiment",    minHeadcount: 1152, maxHeadcount: 4607,
    sublabel: "Headcount 1152–4607",
    description: "Unlocks Regimental CSM, full staff.",
    idealRatios: { enlisted: 50, nco: 28, snco: 12, officer: 7, staff: 3 },
    maxOfficers: 8, maxSNCOs: 16, maxNCOs: 64, unlocksRankClasses: ["enlisted","nco","snco","junior_officer","senior_officer","staff"] },
  { id: "battlegroup", label: "Brigade", minHeadcount: 4608, maxHeadcount: 99999,
    sublabel: "Headcount 4608+",
    description: "Brigade scale. Full command structure.",
    idealRatios: { enlisted: 50, nco: 28, snco: 12, officer: 7, staff: 3 },
    maxOfficers: 16, maxSNCOs: 32, maxNCOs: 128, unlocksRankClasses: ["enlisted","nco","snco","junior_officer","senior_officer","general","staff"] },
];
function getDoctrineTemplate(doctrine: string): StructureTier[] { return doctrine === "us" ? US_TIERS : BRITISH_TIERS; }
function getCurrentTier(doctrine: string, headcount: number): StructureTier { const tiers = getDoctrineTemplate(doctrine); let current = tiers[0]; for (const t of tiers) { if (headcount >= t.minHeadcount) current = t; } return current; }
function getNextTier(doctrine: string, headcount: number): StructureTier | null { const tiers = getDoctrineTemplate(doctrine); for (const t of tiers) { if (headcount < t.minHeadcount) return t; } return null; }
function isRankUnlocked(rank: any, currentTier: StructureTier, rosterSize: number): boolean { const rankClass = rank.rank_class ?? "enlisted"; const minSize = rank.min_roster_size ?? 0; if (minSize > 0 && rosterSize < minSize) return false; return currentTier.unlocksRankClasses.includes(rankClass); }
function getRankClassCounts(roster: any[], ranks: any[]): Record<string, number> { const rankById: Record<string, any> = {}; for (const r of ranks) rankById[r.id] = r; const counts: Record<string, number> = { enlisted: 0, nco: 0, snco: 0, junior_officer: 0, senior_officer: 0, general: 0, staff: 0, unranked: 0 }; for (const m of roster) { const rank = m.rankId ? rankById[m.rankId] : null; const cls = rank?.rank_class ?? "unranked"; counts[cls] = (counts[cls] ?? 0) + 1; } return counts; }
function checkPromotionAllowed(toRank: any, currentTier: StructureTier, roster: any[], ranks: any[], rosterSize: number): { allowed: boolean; reason?: string } {
  if (!isRankUnlocked(toRank, currentTier, rosterSize)) { const tiers = getDoctrineTemplate("british"); const next = tiers.find(t => t.unlocksRankClasses.includes(toRank.rank_class ?? "enlisted") && t.minHeadcount > rosterSize); return { allowed: false, reason: `"${toRank.name}" locked — need ${next?.minHeadcount ?? "more"} members to reach ${next?.label ?? "higher strength"}` }; }
  if (toRank.max_holders && toRank.max_holders > 0) { const holders = roster.filter((m: any) => m.rankId === toRank.id).length; if (holders >= toRank.max_holders) return { allowed: false, reason: `"${toRank.name}" is full (${holders}/${toRank.max_holders})` }; }
  const counts = getRankClassCounts(roster, ranks); const toClass = toRank.rank_class ?? "enlisted"; const isOfficerClass = ["junior_officer","senior_officer","general"].includes(toClass);
  // Dynamic officer cap: 1 per 36 headcount at platoon+, fixed at lower tiers
  const dynOfficerCap = currentTier.id === "platoon" ? Math.max(1, Math.floor(rosterSize / 36)) : currentTier.maxOfficers;
  const dynSncoCap = currentTier.id === "platoon" ? Math.max(2, Math.floor(rosterSize / 9)) : currentTier.maxSNCOs;
  const dynNcoCap = currentTier.maxNCOs > 0 ? Math.max(1, Math.floor(rosterSize / 8)) : 0;
  if (isOfficerClass && dynOfficerCap > 0 && ((counts.junior_officer ?? 0) + (counts.senior_officer ?? 0) + (counts.general ?? 0) ?? 0) >= dynOfficerCap) return { allowed: false, reason: `Officer cap reached (${dynOfficerCap} max at ${rosterSize} HC)` };
  if (toClass === "snco" && dynSncoCap > 0 && (counts.snco ?? 0) >= dynSncoCap) return { allowed: false, reason: `SNCO cap reached (${dynSncoCap} max at ${rosterSize} HC)` };
  if (toClass === "nco" && dynNcoCap > 0 && (counts.nco ?? 0) >= dynNcoCap) return { allowed: false, reason: `JNCO cap reached — 1 per 8 men (${dynNcoCap} max at ${rosterSize} HC)` };
  return { allowed: true };
}

const RANK_CLASS_COLOURS: Record<string, string> = {
  enlisted: "#4ade80", nco: "#60a5fa", snco: "#f59e0b",
  junior_officer: "#a78bfa", senior_officer: "#c084fc", general: "#f43f5e",
  staff: "#f87171", unranked: "#6b7280"
};
const RANK_CLASS_LABELS: Record<string, string> = {
  enlisted: "Enlisted", nco: "JNCO", snco: "SNCO",
  junior_officer: "Junior Officer", senior_officer: "Senior Officer", general: "General",
  staff: "Staff", unranked: "Unranked"
};

import { format, formatDistanceToNow } from "date-fns";
import OrbatBuilder from "@/components/OrbatBuilder";
import RangeCardBuilder from "@/components/RangeCardBuilder";
import TacticalPlanner from "@/components/TacticalPlanner";
import { useToast } from "@/hooks/use-toast";
import { generateBriefingSlides, generateBriefingDoc, generateAARDoc, exportRosterToSheets, exportOpsToSheets } from "@/lib/googleExport";

class PageErrorBoundary extends React.Component<{children: React.ReactNode}, {error: Error | null, info: string}> {
  constructor(props: any) { super(props); this.state = { error: null, info: "" }; }
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[PageErrorBoundary] CRASH:", error.message, info.componentStack);
    this.setState({ info: info.componentStack ?? "" });
  }
  render() {
    if (this.state.error) return (
      <div style={{padding: 24, background: "#1a0000", border: "2px solid red", color: "red", fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", zIndex: 9999, position: "relative"}}>
        <h2 style={{marginBottom: 8}}>PAGE CRASH — MilsimManage</h2>
        <p style={{marginBottom: 8}}>{this.state.error.message}</p>
        <pre style={{fontSize: 11}}>{this.state.error.stack?.slice(0,800)}</pre>
        <pre style={{fontSize: 10, color: "#ff8888"}}>{this.state.info}</pre>
        <button onClick={() => this.setState({error: null, info: ""})} style={{marginTop: 12, color: "white", background: "#600", padding: "4px 12px"}}>Retry</button>
      </div>
    );
    return this.props.children;
  }
}

class TabErrorBoundary extends React.Component<{children: React.ReactNode; tabName?: string}, {error: Error | null; stack: string}> {
  constructor(props: any) { super(props); this.state = { error: null, stack: "" }; }
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[TabErrorBoundary]", error, info);
    this.setState({ stack: info.componentStack ?? "" });
  }
  render() {
    if (this.state.error) return (
      <div className="p-6 border border-red-500/40 rounded-lg bg-red-500/10 space-y-2">
        <p className="font-display font-bold uppercase tracking-wider text-sm text-red-400">Component Error — {this.props.tabName ?? "Tab"}</p>
        <pre className="text-xs text-red-300 whitespace-pre-wrap break-all">{this.state.error.message}</pre>
        <pre className="text-xs text-red-300/60 whitespace-pre-wrap break-all">{this.state.error.stack}</pre>
        <pre className="text-xs text-yellow-400/80 whitespace-pre-wrap break-all">{this.state.stack?.slice(0,800)}</pre>
        <button onClick={() => this.setState({ error: null, stack: "" })} className="text-xs text-red-400 underline">Retry</button>
      </div>
    );
    return this.props.children;
  }
}


interface Role { id: string; name: string; description: string | null; sort_order?: number; sortOrder?: number }
interface Rank { id: string; name: string; abbreviation: string | null; tier: number; icon_url?: string | null; sort_order?: number }
interface RosterEntry { id: string; callsign: string; rankId: string | null; roleId: string | null; notes: string | null; status?: string; specialisations?: string[]; join_date?: string | null; ops_count?: number | null; }
interface AppQuestion { id: number; question: string; sortOrder: number; required: boolean }
interface MilsimAward { id: number; title: string; description: string | null; icon: string; awarded_by: string | null; awarded_at: string; roster_entry_id: number; callsign: string | null }

interface GroupDetail {
  id: number; name: string; slug: string; tagLine: string | null;
  description: string | null; discordUrl: string | null; websiteUrl: string | null;
  logoUrl: string | null; sops: string | null; orbat: string | null; status: string;
  stream_url: string | null; is_live: boolean;
  roles: Role[]; ranks: Rank[]; roster: RosterEntry[]; questions: AppQuestion[];
}

// PJHQ Navigation — top-level tabs
type Tab = 
  // Personnel
  "personnel" |
  // Intelligence
  "intelligence" |
  // Operations
  "operations_room" |
  // Logistics
  "logistics" |
  // Planning
  "planning" |
  "ao_map" |
  "coc_tab" |
  "reports_tab" |
  // Comms & Info Systems
  "j6" |
  // Development & Training
  "training" |
  // Human Resources
  "hr" |
  // Media & Recruitment
  "media" |
  // Analytics
  "analytics_hub" |
  // Settings
  "permissions"
  // Legacy aliases kept for backward compat with any deep links
  | "orbat_builder" | "roles" | "ranks" | "roster" | "recognition" | "stream" | "questions" | "operations" | "readiness" | "analytics" | "campaigns" | "reputation" | "loa" | "calendar" | "pipeline" | "legacy" | "developer" | "troops" | "events" | "eventhub" | "onboarding" | "criteria" | "health" | "intelligence" | "unit_info";

export default function MilsimManage() { return <PageErrorBoundary><MilsimManageInner /></PageErrorBoundary>; }

function MilsimManageInner() {
  const [, setLocation] = useLocation();
  const [tab, setTabState] = useState<Tab>(() => {
    try {
      const saved = localStorage.getItem("milsimManageTab") as Tab | null;
      return saved ?? "personnel";
    } catch { return "personnel"; }
  });
  const setTab = (t: Tab) => {
    setTabState(t);
    try { localStorage.setItem("milsimManageTab", t); } catch {}
  };
  const { user } = useAuth();
  const [group, setGroup] = useState<GroupDetail | null | undefined>(undefined);
  const [groupLoadError, setGroupLoadError] = useState(false);
  const [groupLoadRetry, setGroupLoadRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  // Fullscreen ORBAT state — must be unconditional (hooks rules)

  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [globalIsPro, setGlobalIsPro] = useState<boolean | null>(null);
  const [hqPermissions, setHqPermissions] = useState<Record<string, string> | '__owner__' | null>(null);

  useEffect(() => {
    // Serve cached group instantly, then refresh in background
    try {
      const cachedRaw = sessionStorage.getItem(`unithq_cache_${user?.id}`);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached?.group?.id) {
          setGroup(cached.group);
          setHqPermissions(cached.hqPermissions ?? '__owner__');
        }
      }
    } catch {}
    // Load group: first try as owner, then as member
    const loadGroup = async (attempt = 1) => {
      try {
        const ownGroup = await apiFetch<GroupDetail | null>("/milsimGroups?path=mine/own");
        console.log("[Unit HQ] mine/own response:", ownGroup?.id, ownGroup?.name);
        if (ownGroup?.id) {
          setGroup(ownGroup);
          setHqPermissions('__owner__');
          loadProStatus(ownGroup.id);
          try { sessionStorage.setItem(`unithq_cache_${user?.id}`, JSON.stringify({ group: ownGroup, hqPermissions: '__owner__' })); } catch {}
          try { sessionStorage.removeItem(`memberhq_cache_${user?.id}`); } catch {}
          return;
        }
        // mine/own returned but no id — could be null (no group) or malformed response
        console.warn("[Unit HQ] mine/own returned no id:", JSON.stringify(ownGroup)?.slice(0, 200));
      } catch (e: any) {
        console.error(`[Unit HQ] mine/own failed (attempt ${attempt}):`, e?.message ?? e);
        if (attempt < 3 && (e?.message?.includes("timeout") || e?.message?.includes("network") || e?.message?.includes("Failed to fetch"))) {
          await new Promise(r => setTimeout(r, 1500 * attempt));
          return loadGroup(attempt + 1);
        }
        setGroupLoadError(true);
      }
      // Not an owner — check HQ access grants
      try {
        const accesses = await apiFetch<any[]>("/milsimGroups?path=mine/hq-access");
        if (Array.isArray(accesses) && accesses.length > 0) {
          const access = accesses[0];
          let g: GroupDetail | null = access.group_detail ?? null;
          if (!g) {
            g = await apiFetch<GroupDetail | null>(`/milsimGroups?path=${access.group_id}/full`).catch(() => null);
          }
          if (g) {
            setGroup(g);
            setHqPermissions(access.is_owner ? '__owner__' : access.permissions);
            loadProStatus(access.group_id);
            return;
          }
        }
      } catch (e: any) {
        console.error("[Unit HQ] mine/hq-access failed:", e?.message ?? e);
      }
      // Both mine/own and hq-access returned cleanly but found no group — genuinely no group
      setGroup(null);
      setGroupLoadError(false);
      setHqPermissions(null);
    };
    loadGroup().catch((e: any) => {
      console.error('[Unit HQ] loadGroup unhandled error:', e);
      setGroupLoadError(true);
    });
  }, [user?.id, groupLoadRetry]);

  // Helper: check if current user can view/manage a tab
  const canView = (tabId: string): boolean => {
    if (hqPermissions === '__owner__') return true;
    if (!hqPermissions) return false;
    const p = (hqPermissions as Record<string, string>)[tabId];
    return p === 'view' || p === 'manage';
  };
  const canManage = (tabId: string): boolean => {
    if (hqPermissions === '__owner__') return true;
    if (!hqPermissions) return false;
    return (hqPermissions as Record<string, string>)[tabId] === 'manage';
  };
  const isOwner = hqPermissions === '__owner__';

  const loadProStatus = async (groupId: string, retries = 5) => {
    // Attempt function endpoint with retries + longer backoff
    for (let i = 0; i < retries; i++) {
      try {
        const res = await fetch(`${_PRO_STATUS_URL_MANAGE}?group_id=${groupId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setGlobalIsPro(!!data.is_pro);
        return;
      } catch {
        if (i < retries - 1) {
          await new Promise(r => setTimeout(r, 800 * (i + 1)));
        }
      }
    }
    // All retries failed — default to false (gates show). Paying users can refresh.
    console.warn("[loadProStatus] All retries failed — defaulting Pro to false");
    setGlobalIsPro(false);
  };

  // NOTE: No early returns here — early returns unmount the component and reset useState (including tab).
  // Instead we use inline conditionals in the render so tab state persists across group load.

  const showMsg = (okOrText: boolean | string, textOrType?: string) => {
    // Accept both (ok: boolean, text: string) and (text: string, type: "success"|"error")
    let ok: boolean;
    let text: string;
    if (typeof okOrText === "boolean") {
      ok = okOrText;
      text = textOrType ?? "";
    } else {
      ok = textOrType !== "error";
      text = okOrText;
    }
    setSaveMsg({ ok, text });
    setTimeout(() => setSaveMsg(null), 3500);
  };

  // Sidebar nav groups
  // Full tab definitions with permission keys
  const ALL_NAV_GROUPS: { label: string; pro?: boolean; items: { id: Tab; label: string; icon: typeof Shield; pro?: boolean; star?: boolean; permKey: string }[] }[] = [
    {
      label: "Unit Profile",
      items: [
        { id: "unit_info", label: "Unit Info & Discovery", icon: Settings, permKey: "onboarding" },
      ],
    },
    {
      label: "J1 — Personnel",
      items: [
        { id: "personnel",       label: "Personnel",         icon: Users,        permKey: "troops" },
        { id: "coc_tab",         label: "Chain of Command",  icon: ShieldCheck,  permKey: "troops" },
        { id: "orders_memos",    label: "Orders & Memos",    icon: ScrollText,   permKey: "troops" },
        { id: "alliances",       label: "Alliances",         icon: Shield,       permKey: "onboarding" },
      ],
    },
    {
      label: "J2 — Intelligence",
      items: [
        { id: "intelligence",    label: "Intelligence Room",   icon: Eye,    pro: true, permKey: "readiness" },
      ],
    },
    {
      label: "J3 — Operations",
      items: [
        { id: "operations_room", label: "Operations Room",   icon: Siren,        permKey: "eventhub" },
        { id: "reports_tab",     label: "Reports",           icon: ClipboardList, permKey: "eventhub" },
      ],
    },
    {
      label: "J4 — Logistics",
      items: [
        { id: "logistics",       label: "Logistics",         icon: Package,      permKey: "troops" },
      ],
    },
    {
      label: "J5 — Planning",
      items: [
        { id: "planning",        label: "Planning",          icon: ClipboardList,          permKey: "eventhub", pro: true },
        { id: "ao_map",          label: "Area of Operations Map",            icon: Map,          permKey: "eventhub", pro: true },
        { id: "orbat_builder",   label: "ORBAT Builder",     icon: GitBranch,    permKey: "eventhub", pro: true },
        { id: "range_card",      label: "Range Card Builder", icon: Target,      permKey: "eventhub", pro: true },
      ],
    },
    {
      label: "J6 — Comms & IS ",
      items: [
        { id: "j6",              label: "Comms & Info Sys",  icon: Antenna,      permKey: "troops" },
      ],
    },
    {
      label: "J7 — Dev & Training",
      items: [
        { id: "training",        label: "Dev & Training",    icon: Brain,        permKey: "troops" },
      ],
    },
    {
      label: "J8 — Human Resources",
      items: [
        { id: "hr",              label: "Human Resources",   icon: ClipboardList, permKey: "troops" },
      ],
    },
    {
      label: "J9 — Media & Recruitment",
      items: [
        { id: "media",           label: "Media & Recruit",   icon: Megaphone,    permKey: "onboarding" },
      ],
    },
    {
      label: "Command Analytics",
      items: [
        { id: "analytics_hub",   label: "Analytics",         icon: BarChart3, pro: true, permKey: "analytics" },
      ],
    },
    ...(isOwner ? [{
      label: "Command Settings",
      items: [
        { id: "permissions" as Tab, label: "HQ Permissions", icon: ShieldCheck, permKey: "permissions" },
      ],
    }] : []),
  ];

  // Filter nav groups based on permissions
  const NAV_GROUPS = ALL_NAV_GROUPS.map(group => ({
    ...group,
    items: group.items.filter(item => item.permKey === 'permissions' || canView(item.permKey)),
  })).filter(group => group.items.length > 0);

  if (group === undefined) return (
    <PortalLayout>
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
      </div>
    </PortalLayout>
  );

    if (groupLoadError && !group) return (
    <PortalLayout>
      <div className="text-center py-24 border border-dashed border-border rounded-lg">
        <Shield className="w-14 h-14 text-muted-foreground mx-auto mb-4 opacity-30" />
        <h2 className="font-display font-black text-xl uppercase tracking-wider text-foreground mb-3">Connection Error</h2>
        <p className="text-muted-foreground font-sans mb-6">Failed to load your unit data. This is usually a temporary network issue.</p>
        <button onClick={() => { setGroupLoadError(false); setGroupLoadRetry(k => k + 1); }}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-widest text-sm px-6 py-3 rounded clip-angled-sm transition-all">
          ↻ Retry
        </button>
      </div>
    </PortalLayout>
  );

if (group === null) return (
    <PortalLayout>
      <div className="text-center py-24 border border-dashed border-border rounded-lg">
        <Shield className="w-14 h-14 text-muted-foreground mx-auto mb-4 opacity-30" />
        {!user ? (
          <>
            <h2 className="font-display font-black text-xl uppercase tracking-wider text-foreground mb-3">Session Expired</h2>
            <p className="text-muted-foreground font-sans mb-6">Your session has expired. Please log in again to access your HQ.</p>
            <button onClick={() => setLocation("/login")}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-widest text-sm px-6 py-3 rounded clip-angled-sm transition-all">
              Log In
            </button>
          </>
        ) : (
          <>
            <h2 className="font-display font-black text-xl uppercase tracking-wider text-foreground mb-3">No Group Registered</h2>
            <p className="text-muted-foreground font-sans mb-6">You haven't registered a MilSim group yet.</p>
            <button onClick={() => setLocation("/milsim/register")}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-widest text-sm px-6 py-3 rounded clip-angled-sm transition-all">
              <Plus className="w-4 h-4" /> Register Your Unit
            </button>
          </>
        )}
      </div>
    </PortalLayout>
  );

  // ── Fullscreen AO Map mode ──────────────────────────────────────────────────
  if (tab === "ao_map") {
    if (!group) return <div className="fixed inset-0 z-[9999] bg-[#0d0e11] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
    if (globalIsPro === null) return <div className="fixed inset-0 z-[9999] bg-[#0d0e11] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
    if (!globalIsPro) return (
      <div className="fixed inset-0 z-[9999] bg-[#0d0e11] flex flex-col items-center justify-center gap-6 text-center">
        <Crown className="w-12 h-12 text-yellow-400 opacity-70" />
        <h2 className="font-display font-black text-3xl uppercase tracking-wider text-foreground">Commander Pro Required</h2>
        <a href="/commander-pro" className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-display font-black uppercase tracking-widest text-sm px-8 py-3 rounded">
          <Crown className="w-4 h-4" /> Upgrade to Pro — £10/mo
        </a>
        <button onClick={() => setTab("unit_info")} className="text-muted-foreground text-sm underline">← Back to Unit HQ</button>
      </div>
    );
    return (
      <div style={{position:"fixed",inset:0,zIndex:9999,background:"#0d0e11",display:"flex",flexDirection:"column",width:"100vw",height:"100vh"}}>
        <TacticalPlanner group={group} showMsg={showMsg} onBack={() => setTab("unit_info")} />
      </div>
    );
  }

  // ── Fullscreen ORBAT mode ───────────────────────────────────────────────────
  if (tab === "orbat_builder") {
    if (!group) return <div className="fixed inset-0 z-[9999] bg-[#0d0e11] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
    return <OrbatFullscreen group={group} setGroup={setGroup} showMsg={showMsg} onBack={() => setTab("unit_info")} />;
  }

  return (
    <PortalLayout wide>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="font-display font-black text-2xl uppercase tracking-wider text-foreground">{group.name}</h1>
              <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
                group.status === "featured" ? "bg-accent/20 text-accent border-accent/40"
                : group.status === "approved" ? "bg-primary/20 text-primary border-primary/40"
                : "bg-secondary text-muted-foreground border-border"
              }`}>{group.status}</span>
              {group.is_live && (
                <span className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-red-500/20 text-red-400 border-red-500/40 animate-pulse">
                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full" /> LIVE
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground font-sans">{group.tagLine ?? "No tag line set"}</p>
          </div>
          <a href={`/milsim/${group.slug}`}
            className="inline-flex items-center gap-2 border border-border hover:border-primary/50 text-muted-foreground hover:text-foreground font-display font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded clip-angled-sm transition-all">
            View Public Page <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {saveMsg && (
          <div className={`flex items-center gap-3 p-3 rounded-lg border text-sm font-sans ${saveMsg.ok ? "bg-primary/10 border-primary/30 text-primary" : "bg-destructive/10 border-destructive/30 text-destructive"}`}>
            {saveMsg.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
            {saveMsg.text}
          </div>
        )}

        {/* Sidebar + Content layout */}
        <div className={`flex gap-6 ${tab === "orbat_builder" || tab === "ao_map" ? "h-[calc(100vh-280px)]" : "min-h-[600px]"}`}>
          {/* Sidebar */}
          <nav className="w-52 shrink-0 space-y-5 overflow-y-auto">
            {NAV_GROUPS.map((group_nav) => (
              <div key={group_nav.label}>
                <div className="flex items-center gap-1.5 px-3 mb-1.5">
                  <p className="text-[9px] font-display font-black uppercase tracking-[0.2em] text-muted-foreground/50">{group_nav.label}</p>
                  {(group_nav as any).pro && (
                    <span className="flex items-center gap-0.5 text-[8px] font-display font-black uppercase tracking-widest px-1 py-0.5 rounded border bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
                      <Crown className="w-2 h-2" /> Pro
                    </span>
                  )}
                </div>
                <div className="space-y-0.5">
                  {group_nav.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setTab(item.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded text-left text-xs font-display font-bold uppercase tracking-wider transition-all ${
                        tab === item.id
                          ? "bg-primary/10 text-primary border border-primary/20"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/60 border border-transparent"
                      }`}
                    >
                      <item.icon className="w-3.5 h-3.5 shrink-0" />
                      <span className="flex-1">{item.label}</span>
                      {item.pro && <Crown className="w-3 h-3 text-yellow-400 shrink-0" />}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Main content */}
          <div className={`flex-1 min-w-0 ${tab === "orbat_builder" || tab === "ao_map" ? "flex flex-col h-full" : ""}`}>
            <div key={tab} className={tab === "orbat_builder" || tab === "ao_map" ? "h-full flex flex-col" : ""}>
              {/* ── PJHQ TOP-LEVEL TABS ── */}
              {tab === "unit_info"        && <TabErrorBoundary tabName="Unit Info"><InfoTab group={group} onSaved={setGroup} setSaving={setSaving} saving={saving} showMsg={showMsg} /></TabErrorBoundary>}
              {tab === "personnel"       && <TabErrorBoundary tabName="Personnel"><PersonnelTab       group={group} onUpdated={setGroup} showMsg={showMsg} globalIsPro={globalIsPro} /></TabErrorBoundary>}
              {tab === "intelligence"    && <TabErrorBoundary tabName="Intelligence Room"><S2IntelligenceRoom group={group} showMsg={showMsg} globalIsPro={globalIsPro} /></TabErrorBoundary>}
              {tab === "operations_room" && <TabErrorBoundary tabName="Operations Room"><OperationsRoomTab group={group} showMsg={showMsg} globalIsPro={globalIsPro} onUpdated={setGroup} /></TabErrorBoundary>}
              {tab === "coc_tab"          && <TabErrorBoundary tabName="Chain of Command"><COCStandaloneTab  group={group} onUpdated={setGroup} showMsg={showMsg} /></TabErrorBoundary>}
              {tab === "reports_tab"      && <TabErrorBoundary tabName="Reports"><OpsReportsPanel        group={group} showMsg={showMsg} /></TabErrorBoundary>}
              {tab === "logistics"       && <TabErrorBoundary tabName="Logistics"><LogisticsTab       group={group} showMsg={showMsg} globalIsPro={globalIsPro} /></TabErrorBoundary>}
              {tab === "planning"        && <TabErrorBoundary tabName="Planning"><PlanningTab         group={group} showMsg={showMsg} globalIsPro={globalIsPro} /></TabErrorBoundary>}
              {tab === "ao_map"          && <TabErrorBoundary tabName="AO Map">{globalIsPro === null ? <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div> : globalIsPro ? <AOMapFullTab group={group} showMsg={showMsg} /> : (
                <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
                  <Crown className="w-10 h-10 text-yellow-400 opacity-60" />
                  <h3 className="font-display font-black text-2xl uppercase tracking-wider text-foreground">Commander Pro Required</h3>
                  <p className="text-muted-foreground font-sans text-sm max-w-sm">The Area of Operations Map — including tactical drawing tools, artillery calculators, and operational overlays — is a Commander Pro feature.</p>
                  <a href="/commander-pro" className="inline-flex items-center gap-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 text-yellow-300 font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded transition-all mt-2">
                    <Crown className="w-3.5 h-3.5" /> Upgrade to Commander Pro
                  </a>
                </div>
              )}</TabErrorBoundary>}
              {tab === "orbat_builder"   && <TabErrorBoundary tabName="ORBAT Builder"><OrbatBuilderTab    group={group} onUpdated={setGroup} showMsg={showMsg} globalIsPro={globalIsPro} /></TabErrorBoundary>}
              {tab === "range_card"      && <TabErrorBoundary tabName="Range Card Builder">{globalIsPro === null ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div> : globalIsPro ? <RangeCardBuilder group={group} onBack={() => {}} /> : <div className="flex flex-col items-center justify-center py-20 text-center"><Target className="w-10 h-10 text-muted-foreground/30 mb-4" /><p className="font-display font-bold uppercase tracking-widest text-sm text-muted-foreground">Range Card Builder is a Commander Pro feature</p></div>}</TabErrorBoundary>}
              {tab === "j6"              && <TabErrorBoundary tabName="Comms & IS"><J6Tab             group={group} showMsg={showMsg} globalIsPro={globalIsPro} onUpdated={setGroup} /></TabErrorBoundary>}
              {tab === "training"        && <TabErrorBoundary tabName="Dev & Training"><TrainingTab   group={group} showMsg={showMsg} globalIsPro={globalIsPro} onUpdated={setGroup} /></TabErrorBoundary>}
              {tab === "hr"              && <TabErrorBoundary tabName="Human Resources"><HRTab        group={group} showMsg={showMsg} globalIsPro={globalIsPro} /></TabErrorBoundary>}
              {tab === "orders_memos"    && <TabErrorBoundary tabName="Orders & Memos"><OrdersMemosTab   group={group} showMsg={showMsg} /></TabErrorBoundary>}
              {tab === "media"           && <TabErrorBoundary tabName="Media & Recruit"><MediaTab     group={group} showMsg={showMsg} globalIsPro={globalIsPro} onUpdated={setGroup} /></TabErrorBoundary>}
              {tab === "analytics_hub"   && <TabErrorBoundary tabName="Analytics"><AnalyticsHubTab   group={group} showMsg={showMsg} globalIsPro={globalIsPro} /></TabErrorBoundary>}
              {tab === "alliances"        && <TabErrorBoundary tabName="Alliances"><AlliancesTab       group={group} showMsg={showMsg} globalIsPro={globalIsPro} /></TabErrorBoundary>}
              {tab === "permissions" && isOwner && <TabErrorBoundary tabName="Permissions"><PermissionsTab group={group} showMsg={showMsg} /></TabErrorBoundary>}
              {/* Legacy aliases — keep rendering in case any direct URL uses them */}
              {tab === "roles"      && <TabErrorBoundary tabName="Roles"><RolesTab group={group} onUpdated={setGroup} showMsg={showMsg} /></TabErrorBoundary>}
              {tab === "ranks"      && <TabErrorBoundary tabName="Ranks"><RanksTab group={group} onUpdated={setGroup} showMsg={showMsg} /></TabErrorBoundary>}
              {tab === "roster"     && <TabErrorBoundary tabName="Roster"><RosterTab group={group} onUpdated={setGroup} showMsg={showMsg} /></TabErrorBoundary>}
            </div>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}

function MField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const MC_COUNTRIES = [
  // Europe
  "🇬🇧 United Kingdom", "🇩🇪 Germany", "🇫🇷 France", "🇮🇹 Italy",
  "🇵🇱 Poland", "🇳🇱 Netherlands", "🇳🇴 Norway", "🇸🇪 Sweden",
  "🇩🇰 Denmark", "🇧🇪 Belgium", "🇪🇸 Spain", "🇵🇹 Portugal",
  "🇨🇿 Czech Republic", "🇸🇰 Slovakia", "🇭🇺 Hungary", "🇷🇴 Romania",
  "🇧🇬 Bulgaria", "🇬🇷 Greece", "🇭🇷 Croatia", "🇷🇸 Serbia",
  "🇺🇦 Ukraine", "🇫🇮 Finland", "🇦🇹 Austria", "🇨🇭 Switzerland",
  "🇱🇺 Luxembourg", "🇮🇪 Ireland", "🇱🇹 Lithuania", "🇱🇻 Latvia",
  "🇪🇪 Estonia", "🇸🇮 Slovenia",
  // North America
  "🇺🇸 United States", "🇨🇦 Canada", "🇲🇽 Mexico",
  // South America
  "🇧🇷 Brazil", "🇦🇷 Argentina", "🇨🇱 Chile", "🇨🇴 Colombia",
  "🇵🇪 Peru", "🇻🇪 Venezuela", "🇺🇾 Uruguay", "🇪🇨 Ecuador", "🇧🇴 Bolivia",
  // Middle East
  "🇹🇷 Turkey", "🇸🇦 Saudi Arabia", "🇮🇱 Israel", "🇦🇪 UAE",
  "🇶🇦 Qatar", "🇰🇼 Kuwait", "🇮🇶 Iraq", "🇮🇷 Iran",
  "🇯🇴 Jordan", "🇱🇧 Lebanon", "🇪🇬 Egypt", "🇲🇦 Morocco",
  // Africa
  "🇿🇦 South Africa", "🇳🇬 Nigeria", "🇰🇪 Kenya", "🇬🇭 Ghana",
  "🇪🇹 Ethiopia", "🇹🇿 Tanzania",
  // Asia
  "🇯🇵 Japan", "🇰🇷 South Korea", "🇨🇳 China", "🇮🇳 India",
  "🇵🇰 Pakistan", "🇧🇩 Bangladesh", "🇻🇳 Vietnam", "🇹🇭 Thailand",
  "🇵🇭 Philippines", "🇮🇩 Indonesia", "🇲🇾 Malaysia", "🇸🇬 Singapore",
  "🇹🇼 Taiwan", "🇭🇰 Hong Kong",
  // Oceania
  "🇦🇺 Australia", "🇳🇿 New Zealand", "🇵🇬 Papua New Guinea", "🇫🇯 Fiji",
  // Other
  "International", "Other",
];
const MC_LANGS = [
  "English", "German", "French", "Spanish", "Italian", "Polish",
  "Dutch", "Portuguese", "Norwegian", "Swedish", "Danish", "Finnish",
  "Turkish", "Arabic", "Hebrew", "Japanese", "Korean", "Chinese (Mandarin)",
  "Tagalog", "Vietnamese", "Thai", "Malay", "Indonesian",
  "Ukrainian", "Romanian", "Czech", "Slovak", "Hungarian", "Greek",
  "Croatian", "Serbian", "Bulgarian", "Estonian", "Latvian", "Lithuanian",
  "Hindi", "Other",
];

function InfoTab({ group, onSaved, setSaving, saving, showMsg }: any) {
  const { register, handleSubmit, watch, setValue } = useForm({ defaultValues: {
    name: group.name, tagLine: group.tagLine ?? "", description: group.description ?? "",
    discordUrl: group.discordUrl ?? "", websiteUrl: group.websiteUrl ?? "", steamGroupUrl: group.steamGroupUrl ?? "", logoUrl: group.logoUrl ?? "", bannerUrl: group.banner_url ?? "", bannerPosition: group.banner_position ?? "50% 50%",
    country: group.country ?? "", language: group.language ?? "",
    branch: group.branch ?? "", unitType: group.unitType ?? "",
    games: (group.games ?? []) as string[],
  }});
  const gamesValue: string[] = watch("games") ?? [];
  const branchValue: string = watch("branch") ?? "";
  const unitTypeOptions = branchValue ? (UNIT_TYPES_BY_BRANCH[branchValue as Branch] ?? []) : [];
  const onSubmit = async (data: any) => {
    setSaving(true);
    console.log('[InfoTab onSubmit] data:', JSON.stringify({ bannerUrl: data.bannerUrl, logoUrl: data.logoUrl, name: data.name }));
    try {
      const payload = { ...data, banner_url: data.bannerUrl, banner_position: data.bannerPosition };
      console.log('[InfoTab onSubmit] payload banner_url:', payload.banner_url);
      const updated = await apiFetch(`/milsimGroups?path=${group.id}/info`, { method: "PATCH", body: JSON.stringify(payload) });
      console.log('[InfoTab onSubmit] response banner_url:', (updated as any)?.banner_url);
      onSaved(updated);
      // Bust both caches so MemberHQ and UnitHQ pick up fresh data
      try { sessionStorage.removeItem(`memberhq_cache_${group.owner_id ?? ""}`); } catch {}
      try { Object.keys(sessionStorage).forEach(k => { if (k.startsWith("unithq_cache_") || k.startsWith("memberhq_cache_")) sessionStorage.removeItem(k); }); } catch {}
      showMsg(true, "Group info saved.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 max-w-2xl">
      <MField label="Unit Name"><input {...register("name")} className="mf-input" /></MField>
      <MField label="Tag Line"><input {...register("tagLine")} className="mf-input" /></MField>
      <MField label="Description"><textarea {...register("description")} rows={5} className="mf-input resize-none" /></MField>
      {/* Logo Upload */}
      <MField label="Logo Image">
        <div className="space-y-2">
          <LogoUploadField value={watch("logoUrl") ?? ""} onChange={(url) => setValue("logoUrl", url, { shouldDirty: true })} />
          <p className="text-[10px] text-muted-foreground font-sans">Upload a PNG/JPG (recommended: 400×400px square). Or paste a URL below.</p>
          <input
            value={watch("logoUrl") ?? ""}
            onChange={(e) => setValue("logoUrl", e.target.value, { shouldDirty: true })}
            className="mf-input text-xs"
            placeholder="https://i.imgur.com/..."
          />
        </div>
      </MField>
      {/* Banner Upload */}
      <MField label="Banner Image">
        <div className="space-y-2">
          <BannerUploadField
            value={watch("bannerUrl") ?? ""}
            onChange={(url) => setValue("bannerUrl", url, { shouldDirty: true })}
            onPositionChange={(pos) => setValue("bannerPosition" as any, `${pos.x}% ${pos.y}%`)}
            position={(() => { const p = watch("bannerPosition" as any) ?? "50% 50%"; const [px, py] = String(p).split(" ").map((v: string) => parseFloat(v)); return { x: isNaN(px) ? 50 : px, y: isNaN(py) ? 50 : py }; })()}
          />
          <p className="text-[10px] text-muted-foreground font-sans">Upload a wide banner image (recommended: 960×540px). Or paste a URL below. Drag the preview to set your focal point.</p>
          <input
            value={watch("bannerUrl") ?? ""}
            onChange={(e) => setValue("bannerUrl", e.target.value, { shouldDirty: true })}
            className="mf-input text-xs"
            placeholder="https://i.imgur.com/..."
          />
        </div>
      </MField>
      <MField label="Discord URL"><input {...register("discordUrl")} className="mf-input" placeholder="https://discord.gg/invite" /></MField>
      <MField label="Website URL"><input {...register("websiteUrl")} className="mf-input" placeholder="https://yourunit.com" /></MField>
      <MField label="Steam Group URL"><input {...register("steamGroupUrl")} className="mf-input" placeholder="https://steamcommunity.com/groups/..." /></MField>

      <div className="border-t border-border pt-5">
        <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-4">Discovery & Filtering</p>
        <div className="space-y-4">
          {/* Branch selector */}
          <MField label="Military Branch">
            <div className="flex flex-wrap gap-2 mt-1">
              {BRANCHES.map(b => {
                const sel = branchValue === b;
                return (
                  <button key={b} type="button"
                    onClick={() => { setValue("branch", b); setValue("unitType", ""); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${
                      sel ? "bg-primary/15 border-primary/50 text-primary" : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                    }`}>
                    <span className="w-2 h-2 rounded-full bg-current opacity-60 shrink-0" />{b}
                  </button>
                );
              })}
              {branchValue && (
                <button type="button" onClick={() => { setValue("branch", ""); setValue("unitType", ""); }}
                  className="px-2 py-1.5 rounded border border-border text-xs text-muted-foreground hover:text-destructive transition-colors">
                  ✕ Clear
                </button>
              )}
            </div>
          </MField>
          {/* Unit type — context-aware */}
          <MField label="Unit Type">
            <select {...register("unitType")} className="mf-input" disabled={!branchValue}>
              <option value="">{branchValue ? "Select unit type..." : "Select a branch first"}</option>
              {unitTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </MField>
          <MField label="Country / Nationality">
            <select {...register("country")} className="mf-input">
              <option value="">Select...</option>
              {MC_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </MField>
          <MField label="Primary Language">
            <select {...register("language")} className="mf-input">
              <option value="">Select...</option>
              {MC_LANGS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </MField>
          <MField label="Games You Play">
            <div className="flex flex-wrap gap-2 mt-1">
              {MC_GAMES.map(game => {
                const selected = gamesValue.includes(game);
                return (
                  <button key={game} type="button"
                    onClick={() => {
                      const next = selected ? gamesValue.filter(g => g !== game) : [...gamesValue, game];
                      setValue("games", next);
                    }}
                    className={`px-3 py-1.5 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${
                      selected ? "bg-primary/15 border-primary/50 text-primary" : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                    }`}
                  >
                    {game}
                  </button>
                );
              })}
            </div>
          </MField>
        </div>
      </div>

      <button type="submit" disabled={saving}
        className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-sm px-6 py-3 rounded clip-angled-sm transition-all disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
      </button>
    </form>
  );
}

// Forward ref so OrbatProGate can use it (full const defined near AnalyticsTab)
const ANALYTICS_URL = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions/groupAnalytics";
const CAMPAIGNS_URL = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions/campaigns";
const PIPELINE_URL = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions/milsimApplications";
const API_KEYS_URL = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions/milsimApiKeys";
const EVENT_TYPES = ["Op", "Training", "Meeting", "Social", "Admin", "Other"] as const;

/* ─── Doctrine Tab (Info + SOPs + Org Chart + Training Docs) ──────────────── */
type DoctrineSubTab = "info" | "sops" | "orgchart" | "training";

function DoctrineTab({ group, onSaved, setSaving, saving, showMsg, globalIsPro }: any) {
  const [sub, setSub] = useState<DoctrineSubTab>("info");

  const SUB_TABS: { id: DoctrineSubTab; label: string; icon: typeof Shield }[] = [
    { id: "info",      label: "Unit Info",      icon: Shield },
    { id: "sops",      label: "SOPs",           icon: BookOpen },
    { id: "orgchart",  label: "Org Chart",      icon: GitBranch },
    { id: "training",  label: "Training Docs",  icon: Brain },
  ];

  return (
    <div className="space-y-5">
      {/* Inner sub-tab bar */}
      <div className="flex items-center gap-1 border-b border-border pb-0 overflow-x-auto">
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => setSub(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-display font-bold uppercase tracking-widest border-b-2 transition-all whitespace-nowrap ${
              sub === t.id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}>
            <t.icon className="w-3.5 h-3.5 shrink-0" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Sub-tab content */}
      <div key={sub}>
        {sub === "info"     && <InfoTab group={group} onSaved={onSaved} setSaving={setSaving} saving={saving} showMsg={showMsg} />}
        {sub === "sops"     && <SopsOnlyTab group={group} onSaved={onSaved} setSaving={setSaving} saving={saving} showMsg={showMsg} />}
        {sub === "orgchart" && <OrgChartTab group={group} />}
        {sub === "training" && <TrainingDocsTab group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
      </div>
    </div>
  );
}

function SopsOnlyTab({ group, onSaved, setSaving, saving, showMsg }: any) {
  const [sopsText, setSopsText] = useState(group.sops ?? "");

  const saveSops = async () => {
    setSaving(true);
    try {
      const updated = await apiFetch(`/milsimGroups?path=${group.id}/info`, { method: "PATCH", body: JSON.stringify({ sops: sopsText }) });
      onSaved(updated);
      showMsg(true, "SOPs saved.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <BookOpen className="w-5 h-5 text-primary" />
        <h2 className="font-display font-black text-lg uppercase tracking-wider text-foreground">Standard Operating Procedures</h2>
      </div>
      <MField label="SOPs">
        <textarea value={sopsText} onChange={e => setSopsText(e.target.value)} rows={20}
          className="mf-input resize-y font-mono text-sm"
          placeholder="1. Comms discipline — PTT only when necessary&#10;2. Movement protocols..." />
      </MField>
      <button onClick={saveSops} disabled={saving}
        className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-sm px-6 py-3 rounded clip-angled-sm transition-all disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save SOPs
      </button>
    </div>
  );
}

function OrbatOnlyTab({ group, onSaved, setSaving, saving, showMsg }: any) {
  const [orbatJson, setOrbatJson] = useState(group.orbat ?? "");

  const saveOrbat = async () => {
    setSaving(true);
    try {
      const updated = await apiFetch(`/milsimGroups?path=${group.id}/info`, { method: "PATCH", body: JSON.stringify({ orbat: orbatJson }) });
      onSaved(updated);
      showMsg(true, "ORBAT saved.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <GitBranch className="w-5 h-5 text-primary" />
        <h2 className="font-display font-black text-lg uppercase tracking-wider text-foreground">ORBAT Builder</h2>
        <span className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
          <Crown className="w-2.5 h-2.5" /> Pro
        </span>
      </div>
      <OrbatProGate group={group} orbatJson={orbatJson} setOrbatJson={setOrbatJson} saveOrbat={saveOrbat} saving={saving} globalIsPro={undefined} />
    </div>
  );
}

function OrbatStandaloneTab({ group, onSaved, setSaving, saving, showMsg }: any) {
  const [orbatJson, setOrbatJson] = useState(group.orbat ?? "");

  const saveOrbat = async () => {
    setSaving(true);
    try {
      const updated = await apiFetch(`/milsimGroups?path=${group.id}/info`, { method: "PATCH", body: JSON.stringify({ orbat: orbatJson }) });
      onSaved(updated);
      showMsg(true, "ORBAT saved.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <GitBranch className="w-5 h-5 text-primary" />
        <h2 className="font-display font-black text-lg uppercase tracking-wider text-foreground">ORBAT Builder</h2>
        <span className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
          <Crown className="w-2.5 h-2.5" /> Pro
        </span>
      </div>
      <OrbatProGate group={group} orbatJson={orbatJson} setOrbatJson={setOrbatJson} saveOrbat={saveOrbat} saving={saving} globalIsPro={undefined} />
    </div>
  );
}

function OrbatProGate({ group, orbatJson, setOrbatJson, saveOrbat, saving, globalIsPro }: any) {
  const [isPro, setIsPro] = useState<boolean | null>(null);
  useEffect(() => {
    if (globalIsPro !== undefined && globalIsPro !== null) { setIsPro(globalIsPro); return; }
    fetch(`${_PRO_STATUS_URL_MANAGE}?group_id=${group.id}`)
      .then(r => r.json())
      .then(s => setIsPro(!!s.is_pro))
      .catch(() => setIsPro(false));
  }, [group.id, globalIsPro]);

  if (isPro === null) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (!isPro) return (
    <div className="flex flex-col items-center justify-center py-20 text-center max-w-md mx-auto gap-6">
      <div className="w-16 h-16 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center justify-center">
        <Crown className="w-8 h-8 text-yellow-400" />
      </div>
      <div>
        <h3 className="font-display font-black text-2xl uppercase tracking-wider text-foreground mb-2">Commander Pro Required</h3>
        <p className="text-muted-foreground font-sans leading-relaxed">
          The visual ORBAT Builder is a Pro feature. Build your command structure with NATO APP-6 symbology, drag-and-drop hierarchy, echelon markers, and print-ready PDF export.
        </p>
      </div>
      <a href="/commander-pro"
        className="inline-flex items-center gap-3 bg-yellow-500 hover:bg-yellow-400 text-black font-display font-black uppercase tracking-widest text-sm px-8 py-3 rounded transition-all shadow-[0_0_20px_hsla(48,96%,53%,0.3)]">
        <Crown className="w-4 h-4" /> Upgrade to Pro — £10/mo
      </a>
      <p className="text-xs text-muted-foreground font-sans">SOPs remain free — ORBAT is a Pro visual tool.</p>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0">
        <OrbatBuilder value={orbatJson} onChange={setOrbatJson} groupName={group.name} />
      </div>
      <div className="flex justify-end pt-2 shrink-0">
        <button onClick={saveOrbat} disabled={saving}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-sm px-6 py-3 rounded clip-angled-sm transition-all disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save ORBAT
        </button>
      </div>
    </div>
  );
}

// ─── Troop Management Tab (Roles + Ranks + Roster + Pipeline + LOA + Reputation) ──
function TroopManagementTab({ group, onUpdated, showMsg, globalIsPro }: any) {
  type TTSub = "roster" | "roles" | "ranks" | "coc" | "loa" | "reputation" | "doctrine" | "loadouts" | "servers" | "inactivity" | "duty" | "engagement" | "promotion" | "conduct_tracker" | "accountability";
  const [sub, setSub] = useState<TTSub>("roster");

  const SUBS: { id: TTSub; label: string; icon: React.ReactNode; pro?: boolean }[] = [
    { id: "roster",     label: "Roster",           icon: <Users className="w-3.5 h-3.5" /> },
    { id: "roles",      label: "Roles",            icon: <Crosshair className="w-3.5 h-3.5" /> },
    { id: "ranks",      label: "Ranks",            icon: <Award className="w-3.5 h-3.5" /> },
    { id: "coc",        label: "Chain of Command", icon: <ShieldCheck className="w-3.5 h-3.5" /> },
    { id: "loa",        label: "LOA Manager",      icon: <PlaneTakeoff className="w-3.5 h-3.5" /> },
    { id: "reputation", label: "Service Files",    icon: <Star className="w-3.5 h-3.5" /> },
    { id: "doctrine",   label: "Doctrine",         icon: <BookOpen className="w-3.5 h-3.5" /> },
    { id: "loadouts",   label: "Loadout Kits",      icon: <Package className="w-3.5 h-3.5" /> },
    { id: "servers",    label: "Game Servers",      icon: <Server className="w-3.5 h-3.5" /> },
    { id: "inactivity", label: "Inactivity",          icon: <UserMinus className="w-3.5 h-3.5" />, pro: true },
    { id: "duty",       label: "Duty Roster",        icon: <ClipboardList className="w-3.5 h-3.5" />, pro: true },
    { id: "engagement", label: "Engagement",         icon: <Activity className="w-3.5 h-3.5" />, pro: true },
    { id: "promotion",       label: "Promotions",     icon: <ChevronUp className="w-3.5 h-3.5" />, pro: true },
    { id: "conduct_tracker", label: "Conduct Tracker", icon: <ShieldAlert className="w-3.5 h-3.5" /> },
    { id: "accountability", label: "Accountability", icon: <Flame className="w-3.5 h-3.5" />, pro: true },
  ];

  return (
    <div className="flex gap-0 min-h-[600px]">
      {/* Mini vertical sidebar */}
      <div className="shrink-0 w-40 border-r border-border/60 pr-2 space-y-0.5 pt-1">
        {SUBS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSub(s.id)}
            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded text-left font-display font-bold uppercase tracking-widest text-[10px] transition-colors ${
              sub === s.id
                ? "bg-primary/15 text-primary border-l-2 border-primary pl-2.5"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
            }`}
          >
            {s.icon}
            <span className="truncate">{s.label}</span>
            {s.pro && <Crown className="ml-auto w-3 h-3 text-yellow-400 shrink-0" />}
          </button>
        ))}
      </div>

      {/* Content panel */}
      <div className="flex-1 pl-5 min-w-0">
        {sub === "roster"     && <RosterTab          group={group} onUpdated={onUpdated} showMsg={showMsg} />}
        {sub === "roles"      && <RolesTab           group={group} onUpdated={onUpdated} showMsg={showMsg} />}
        {sub === "ranks"      && <RanksTab           group={group} onUpdated={onUpdated} showMsg={showMsg} />}
        {sub === "coc"        && <ChainOfCommandTab  group={group} onUpdated={onUpdated} showMsg={showMsg} />}
        {sub === "loa"        && <LOATab             group={group} showMsg={showMsg} />}
        {sub === "reputation" && <ReputationTab      group={group} />}
        {sub === "doctrine"   && <DoctrineTab group={group} onSaved={onUpdated} setSaving={() => {}} saving={false} showMsg={showMsg} globalIsPro={globalIsPro} />}
        {sub === "loadouts"   && <LoadoutKitsTab     group={group} showMsg={showMsg} />}
        {sub === "servers"    && <GameServersTab     group={group} showMsg={showMsg} />}
        {sub === "inactivity" && <InactivityPurgeTab  group={group} globalIsPro={globalIsPro} showMsg={showMsg} />}
        {sub === "duty"       && <DutyRosterTab        group={group} globalIsPro={globalIsPro} showMsg={showMsg} />}
        {sub === "engagement" && <EngagementScoreTab   group={group} globalIsPro={globalIsPro} />}
        {sub === "promotion"  && <PromotionEngineTab   group={group} globalIsPro={globalIsPro} showMsg={showMsg} />}
        {sub === "conduct_tracker" && <ConductTrackerTab group={group} showMsg={showMsg} />}
        {sub === "accountability" && <AccountabilityTab group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
      </div>
    </div>
  );
}


// ─── Chain of Command Tab ─────────────────────────────────────────────────────

const DEFAULT_COC_POSITIONS = [
  { id: "co",       title: "Commanding Officer (CO)",           sort_order: 1 },
  { id: "xo",       title: "Executive Officer (XO)",           sort_order: 2 },
  { id: "adjutant", title: "Adjutant",                         sort_order: 3 },
  { id: "rsm",      title: "Regimental Sergeant Major (RSM)",  sort_order: 4 },
  { id: "cqms",     title: "CQMS / QM",                       sort_order: 5 },
  { id: "s2",       title: "Intelligence Officer (S2 / IO)",   sort_order: 6 },
  { id: "s3",       title: "Operations Officer (S3 / OC Ops)", sort_order: 7 },
  { id: "s4",       title: "Logistics Officer (S4)",           sort_order: 8 },
  { id: "sig",      title: "Signals Officer (Sigs)",           sort_order: 9 },
  { id: "med",      title: "Medical Officer (MO)",             sort_order: 10 },
];

interface CoCPosition {
  id: string;
  title: string;
  roster_id: string;
  callsign: string;
  sort_order: number;
}

function ChainOfCommandTab({ group, onUpdated, showMsg }: any) {
  const [positions, setPositions] = useState<CoCPosition[]>(() => {
    const saved: CoCPosition[] = Array.isArray(group.chain_of_command) ? group.chain_of_command : [];
    const map: Record<string, CoCPosition> = {};
    DEFAULT_COC_POSITIONS.forEach(d => { map[d.id] = { ...d, roster_id: "", callsign: "" }; });
    saved.forEach((s: CoCPosition) => { map[s.id] = s; });
    saved.forEach((s: CoCPosition) => { if (!map[s.id]) map[s.id] = s; });
    return Object.values(map).sort((a, b) => a.sort_order - b.sort_order);
  });
  const [saving, setSaving] = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const dragItem = useRef<number | null>(null);
  const dragOver = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const roster: any[] = group.roster ?? [];

  const updatePosition = (id: string, field: keyof CoCPosition, value: string) => {
    setPositions(prev => prev.map(p => {
      if (p.id !== id) return p;
      if (field === "roster_id") {
        const member = roster.find((r: any) => r.id === value);
        return { ...p, roster_id: value, callsign: member?.callsign ?? "" };
      }
      return { ...p, [field]: value };
    }));
  };

  const removePosition = (id: string) => {
    setPositions(prev => prev.filter(p => p.id !== id));
  };

  const addPosition = () => {
    if (!newTitle.trim()) return;
    const newId = `custom_${Date.now()}`;
    setPositions(prev => [...prev, { id: newId, title: newTitle.trim(), roster_id: "", callsign: "", sort_order: prev.length + 1 }]);
    setNewTitle("");
    setAddingNew(false);
  };

  const handleDragStart = (idx: number) => {
    dragItem.current = idx;
  };
  const handleDragEnter = (idx: number) => {
    dragOver.current = idx;
    setDragOverIdx(idx);
  };
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOver.current === null || dragItem.current === dragOver.current) {
      dragItem.current = null;
      dragOver.current = null;
      setDragOverIdx(null);
      return;
    }
    setPositions(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragItem.current!, 1);
      next.splice(dragOver.current!, 0, moved);
      return next.map((p, i) => ({ ...p, sort_order: i + 1 }));
    });
    dragItem.current = null;
    dragOver.current = null;
    setDragOverIdx(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await apiFetch(`/milsimGroups?path=${group.id}/info`, {
        method: "PATCH",
        body: JSON.stringify({ chain_of_command: positions }),
      });
      onUpdated(updated);
      showMsg("Chain of Command saved.", "success");
    } catch (e: any) {
      showMsg(e.message ?? "Failed to save.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-bold uppercase tracking-widest text-sm text-foreground">Chain of Command</h3>
          <p className="text-xs text-muted-foreground font-sans mt-1">Drag rows to reorder. Assign roster members to command positions. Titles are fully editable.</p>
        </div>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded font-display font-bold uppercase tracking-widest text-xs hover:bg-primary/90 transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </button>
      </div>

      <div className="space-y-2">
        {positions.map((pos, idx) => (
          <div
            key={pos.id}
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragEnter={() => handleDragEnter(idx)}
            onDragEnd={handleDragEnd}
            onDragOver={e => e.preventDefault()}
            className={[
              "bg-card border rounded p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 transition-all select-none",
              dragOverIdx === idx && dragItem.current !== idx
                ? "border-primary/70 bg-primary/5 scale-[1.01]"
                : "border-border"
            ].join(" ")}
          >
            {/* Drag handle */}
            <div className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground transition-colors shrink-0 pt-0.5" title="Drag to reorder">
              <svg width="12" height="20" viewBox="0 0 12 20" fill="currentColor">
                <circle cx="3" cy="4" r="1.5"/><circle cx="9" cy="4" r="1.5"/>
                <circle cx="3" cy="10" r="1.5"/><circle cx="9" cy="10" r="1.5"/>
                <circle cx="3" cy="16" r="1.5"/><circle cx="9" cy="16" r="1.5"/>
              </svg>
            </div>
            {/* Position number */}
            <span className="text-[10px] font-display font-bold text-muted-foreground/50 tracking-widest shrink-0 w-5 text-center">{idx + 1}</span>
            {/* Position title — editable */}
            <div className="flex-1 min-w-0">
              <input
                value={pos.title}
                onChange={e => updatePosition(pos.id, "title", e.target.value)}
                onMouseDown={e => e.stopPropagation()}
                className="w-full bg-background border border-border rounded px-3 py-2 text-sm font-display font-bold uppercase tracking-wider text-foreground focus:outline-none focus:border-primary transition-colors"
                placeholder="Position title…"
              />
            </div>
            {/* Roster member select */}
            <div className="w-full sm:w-56 shrink-0">
              <select
                value={pos.roster_id}
                onChange={e => updatePosition(pos.id, "roster_id", e.target.value)}
                onMouseDown={e => e.stopPropagation()}
                className="w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans text-foreground focus:outline-none focus:border-primary transition-colors"
              >
                <option value="">— Unassigned —</option>
                {roster.filter((r: any) => r.status === "Active" || r.status === "active").map((r: any) => (
                  <option key={r.id} value={r.id}>{r.callsign}</option>
                ))}
              </select>
            </div>
            {/* Callsign display */}
            {pos.callsign && (
              <span className="text-xs font-display font-bold text-primary tracking-wider shrink-0">{pos.callsign}</span>
            )}
            {/* Remove */}
            <button onClick={() => removePosition(pos.id)}
              className="p-1.5 text-muted-foreground hover:text-destructive transition-colors shrink-0 ml-auto sm:ml-0">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      {/* Add new position */}
      {addingNew ? (
        <div className="flex items-center gap-2">
          <input value={newTitle} onChange={e => setNewTitle(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addPosition()}
            autoFocus
            className="flex-1 bg-background border border-border rounded px-3 py-2 text-sm font-display font-bold uppercase tracking-wider text-foreground focus:outline-none focus:border-primary transition-colors"
            placeholder="New position title…" />
          <button onClick={addPosition} className="px-3 py-2 bg-primary text-primary-foreground rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors">Add</button>
          <button onClick={() => setAddingNew(false)} className="px-3 py-2 border border-border rounded text-xs font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
        </div>
      ) : (
        <button onClick={() => setAddingNew(true)}
          className="flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add Custom Position
        </button>
      )}

      {/* CoC display preview */}
      {positions.some(p => p.callsign) && (
        <div className="border border-border rounded p-5 bg-secondary/20">
          <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-4">Preview — How members appear across the platform</p>
          <div className="space-y-2">
            {positions.filter(p => p.callsign).map(pos => (
              <div key={pos.id} className="flex items-center gap-3">
                <span className="font-display font-bold text-sm text-foreground">{pos.callsign}</span>
                <span className="text-xs font-display font-bold text-primary uppercase tracking-wider px-2 py-0.5 border border-primary/30 rounded bg-primary/10">
                  {pos.title.split("(")[0].trim()} of {group.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function RolesTab({ group, onUpdated, showMsg }: any) {
  const [roles, setRoles] = useState<any[]>([]);
  const [name, setName] = useState(""); const [desc, setDesc] = useState("");
  const [adding, setAdding] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const loadRoles = () => {
    setLoadingRoles(true);
    apiFetch<any[]>(`/milsimGroups?path=${group.id}/roles`)
      .then(res => { if (Array.isArray(res)) setRoles([...res].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))); })
      .catch(() => {})
      .finally(() => setLoadingRoles(false));
  };

  React.useEffect(() => { loadRoles(); }, [group.id]);

  const dragItem = useRef<number | null>(null);
  const dragOver = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const add = async () => {
    if (!name.trim()) return;
    setAdding(true);
    try {
      await apiFetch(`/milsimGroups?path=${group.id}/roles`, { method: "POST", body: JSON.stringify({ name, description: desc || undefined, sortOrder: roles.length }) });
      setName(""); setDesc(""); showMsg(true, "Role added."); loadRoles();
    } catch (e: any) { showMsg(false, e.message); }
    finally { setAdding(false); }
  };

  const remove = async (id: string) => {
    try {
      await apiFetch(`/milsimGroups?path=${group.id}/roles/${id}`, { method: "DELETE" });
      setRoles(prev => prev.filter(r => r.id !== id)); showMsg(true, "Role removed.");
    } catch (e: any) { showMsg(false, e.message); }
  };

  const updateSlot = async (role: any, patch: Record<string, any>) => {
    setSaving(prev => ({ ...prev, [role.id]: true }));
    try {
      const updated = await apiFetch<any>(`/rankReorder?path=${group.id}/roles/${role.id}`, { method: "PATCH", body: JSON.stringify(patch) });
      setRoles(prev => prev.map(r => r.id === role.id ? { ...r, ...updated } : r));
      showMsg(true, "Saved.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setSaving(prev => ({ ...prev, [role.id]: false })); }
  };

  const handleDragStart = (idx: number) => { dragItem.current = idx; };
  const handleDragEnter = (idx: number) => { dragOver.current = idx; setDragOverIdx(idx); };
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOver.current === null || dragItem.current === dragOver.current) {
      dragItem.current = null; dragOver.current = null; setDragOverIdx(null); return;
    }
    setRoles(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragItem.current!, 1);
      next.splice(dragOver.current!, 0, moved);
      return next.map((r, i) => ({ ...r, sort_order: i }));
    });
    dragItem.current = null; dragOver.current = null; setDragOverIdx(null);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await apiFetch(`/rankReorder?path=${group.id}/roles/reorder`, { method: "PATCH", body: JSON.stringify({ order: roles.map((r, i) => ({ id: r.id, sort_order: i })) }) });
      showMsg(true, "Order saved.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setSavingOrder(false); }
  };

  const STATUS_CONFIG: Record<string, { label: string; color: string; dot: string }> = {
    open:    { label: "Open",    color: "text-green-400 border-green-500/40 bg-green-500/10",   dot: "bg-green-400" },
    limited: { label: "Limited", color: "text-yellow-400 border-yellow-500/40 bg-yellow-500/10", dot: "bg-yellow-400" },
    closed:  { label: "Closed",  color: "text-red-400 border-red-500/40 bg-red-500/10",         dot: "bg-red-400" },
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground font-sans">Drag to reorder. Click a role to set slot availability — shown publicly on your unit page.</p>
        </div>
        <button onClick={saveOrder} disabled={savingOrder}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded font-display font-bold uppercase tracking-widest text-xs hover:bg-primary/90 transition-colors disabled:opacity-60">
          {savingOrder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Order
        </button>
      </div>

      {loadingRoles && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}

      <div className="space-y-2">
        {!loadingRoles && roles.length === 0 && <p className="text-muted-foreground font-sans text-sm">No roles added yet.</p>}
        {roles.map((r, idx) => {
          const status = r.slot_status ?? "open";
          const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.open;
          const isExpanded = expandedId === r.id;
          const isPublic = r.publicly_visible !== false;
          return (
            <div key={r.id} className={["border rounded-lg transition-all select-none", dragOverIdx === idx && dragItem.current !== idx ? "border-primary/70 bg-primary/5" : "border-border bg-card"].join(" ")}>
              {/* Row header */}
              <div className="flex items-center gap-3 px-4 py-3"
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragEnter={() => handleDragEnter(idx)}
                onDragEnd={handleDragEnd}
                onDragOver={e => e.preventDefault()}>
                <div className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground transition-colors shrink-0">
                  <svg width="12" height="20" viewBox="0 0 12 20" fill="currentColor">
                    <circle cx="3" cy="4" r="1.5"/><circle cx="9" cy="4" r="1.5"/>
                    <circle cx="3" cy="10" r="1.5"/><circle cx="9" cy="10" r="1.5"/>
                    <circle cx="3" cy="16" r="1.5"/><circle cx="9" cy="16" r="1.5"/>
                  </svg>
                </div>
                <span className="text-[10px] font-display font-bold text-muted-foreground/50 tracking-widest w-5 text-center shrink-0">{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-display font-bold uppercase tracking-wider text-sm text-foreground">{r.name}</p>
                    {!isPublic && <span className="px-1.5 py-0.5 text-[9px] font-display font-bold uppercase tracking-wider rounded border border-border text-muted-foreground">Hidden</span>}
                  </div>
                  {r.description && <p className="text-xs text-muted-foreground font-sans truncate">{r.description}</p>}
                </div>
                {/* Status badge */}
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-display font-bold uppercase tracking-wider ${cfg.color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                  {cfg.label}
                  {r.slots_total != null && <span className="ml-0.5 opacity-70">({r.slots_filled ?? 0}/{r.slots_total})</span>}
                </span>
                <button onClick={() => setExpandedId(isExpanded ? null : r.id)} className="text-muted-foreground hover:text-foreground transition-colors p-1 shrink-0">
                  <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
                <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1 shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>

              {/* Expanded slot editor */}
              {isExpanded && (
                <div className="border-t border-border px-4 py-4 space-y-4 bg-secondary/20">
                  <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Slot Settings — visible on your public unit page</p>
                  <div className="grid grid-cols-3 gap-2">
                    {(['open','limited','closed'] as const).map(s => {
                      const c = STATUS_CONFIG[s];
                      return (
                        <button key={s} onClick={() => updateSlot(r, { slot_status: s })}
                          className={`flex items-center gap-2 px-3 py-2 rounded border font-display font-bold uppercase tracking-wider text-xs transition-all ${status === s ? c.color : 'border-border text-muted-foreground hover:border-primary/40'}`}>
                          <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                          {c.label}
                          {saving[r.id] && status === s && <Loader2 className="w-3 h-3 animate-spin ml-auto" />}
                        </button>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">Total Slots</label>
                      <input type="number" min="0" max="999"
                        defaultValue={r.slots_total ?? ""}
                        className="mf-input w-full text-sm"
                        placeholder="e.g. 4"
                        onBlur={e => updateSlot(r, { slots_total: e.target.value ? parseInt(e.target.value) : null })}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">Filled Slots</label>
                      <input type="number" min="0" max="999"
                        defaultValue={r.slots_filled ?? ""}
                        className="mf-input w-full text-sm"
                        placeholder="e.g. 2"
                        onBlur={e => updateSlot(r, { slots_filled: e.target.value ? parseInt(e.target.value) : null })}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-display font-bold uppercase tracking-wider">Show on Public Page</p>
                      <p className="text-[10px] text-muted-foreground">If off, this role is hidden from your unit listing</p>
                    </div>
                    <button onClick={() => updateSlot(r, { publicly_visible: !isPublic })}
                      className={`w-10 h-5 rounded-full transition-colors relative shrink-0 ${isPublic ? 'bg-primary' : 'bg-border'}`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${isPublic ? 'left-5' : 'left-0.5'}`} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-card border border-border rounded-lg p-5 space-y-3">
        <h3 className="font-display font-bold uppercase tracking-wider text-xs text-muted-foreground">Add Role / Position</h3>
        <input value={name} onChange={e => setName(e.target.value)} className="mf-input" placeholder="Infantry, Medic, Engineer, Sniper..." />
        <input value={desc} onChange={e => setDesc(e.target.value)} className="mf-input" placeholder="Short description (optional)" />
        <button onClick={add} disabled={adding || !name.trim()}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded clip-angled-sm transition-all disabled:opacity-50">
          {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add Role
        </button>
      </div>
    </div>
  );
}

function RanksTab({ group, onUpdated, showMsg }: any) {
  const [ranks, setRanks] = useState<Rank[]>([...(group.ranks || [])].sort((a, b) => (a.sort_order ?? b.tier ?? 0) - (b.sort_order ?? a.tier ?? 0)));
  const [name, setName] = useState(""); const [abbr, setAbbr] = useState(""); const [tier, setTier] = useState("0");
  const [rankClass, setRankClass] = useState("enlisted"); const [maxHolders, setMaxHolders] = useState(""); const [minRosterSize, setMinRosterSize] = useState("");
  const [adding, setAdding] = useState(false);
  const [editRankId, setEditRankId] = useState<string | null>(null);
  const [editRankData, setEditRankData] = useState<any>({});
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (r: any) => { setEditRankId(r.id); setEditRankData({ name: r.name, abbreviation: r.abbreviation ?? "", tier: r.tier ?? 0, rank_class: r.rank_class ?? "enlisted", max_holders: r.max_holders ?? 0, min_roster_size: r.min_roster_size ?? 0 }); };

  const saveRankEdit = async () => {
    if (!editRankId) return;
    setSavingEdit(true);
    try {
      const updated = await apiFetch(`/milsimGroups?path=${group.id}/ranks/${editRankId}`, {
        method: "PATCH",
        body: JSON.stringify({ name: editRankData.name, abbreviation: editRankData.abbreviation || undefined, tier: parseInt(editRankData.tier) || 0, rank_class: editRankData.rank_class, max_holders: editRankData.max_holders ? parseInt(editRankData.max_holders) : 0, min_roster_size: editRankData.min_roster_size ? parseInt(editRankData.min_roster_size) : 0 }),
      });
      const newRanks = ranks.map((r: any) => r.id === editRankId ? { ...r, ...updated } : r);
      setRanks(newRanks); onUpdated({ ...group, ranks: newRanks });
      setEditRankId(null); showMsg(true, "Rank updated.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setSavingEdit(false); }
  };
  const [savingOrder, setSavingOrder] = useState(false);
  const dragItem = useRef<number | null>(null);
  const dragOver = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const add = async () => {
    if (!name.trim()) return;
    setAdding(true);
    try {
      const rank = await apiFetch<Rank>(`/milsimGroups?path=${group.id}/ranks`, { method: "POST", body: JSON.stringify({ name, abbreviation: abbr || undefined, tier: parseInt(tier) || 0, rank_class: rankClass, max_holders: maxHolders ? parseInt(maxHolders) : 0, min_roster_size: minRosterSize ? parseInt(minRosterSize) : 0 }) });
      const updated = [...ranks, rank];
      setRanks(updated); onUpdated({ ...group, ranks: updated });
      setName(""); setAbbr(""); setTier("0"); setRankClass("enlisted"); setMaxHolders(""); setMinRosterSize(""); showMsg(true, "Rank added.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setAdding(false); }
  };

  const remove = async (id: string) => {
    try {
      await apiFetch(`/milsimGroups?path=${group.id}/ranks/${id}`, { method: "DELETE" });
      const updated = ranks.filter((r) => r.id !== id); setRanks(updated); onUpdated({ ...group, ranks: updated });
    } catch (e: any) { showMsg(false, e.message); }
  };

  const handleDragStart = (idx: number) => { dragItem.current = idx; };
  const handleDragEnter = (idx: number) => { dragOver.current = idx; setDragOverIdx(idx); };
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOver.current === null || dragItem.current === dragOver.current) {
      dragItem.current = null; dragOver.current = null; setDragOverIdx(null); return;
    }
    setRanks(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragItem.current!, 1);
      next.splice(dragOver.current!, 0, moved);
      return next.map((r, i) => ({ ...r, sort_order: i }));
    });
    dragItem.current = null; dragOver.current = null; setDragOverIdx(null);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await apiFetch(`/rankReorder?path=${group.id}/ranks/reorder`, {
        method: "PATCH",
        body: JSON.stringify({ order: ranks.map((r, i) => ({ id: r.id, sort_order: i })) }),
      });
      showMsg(true, "Rank order saved.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setSavingOrder(false); }
  };

  const uploadIcon = async (e: React.ChangeEvent<HTMLInputElement>, rankId: number | string) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    try {
      const iconUrl = await uploadFileToBase44(file);
      await apiFetch(`/rankReorder?path=${group.id}/ranks/${rankId}`, {
        method: "PATCH",
        body: JSON.stringify({ icon_url: iconUrl }),
      }).catch(() => null);
      setRanks(prev => prev.map(r => r.id === String(rankId) ? { ...r, icon_url: iconUrl } : r));
      onUpdated({ ...group, ranks: ranks.map(r => r.id === String(rankId) ? { ...r, icon_url: iconUrl } : r) });
      showMsg(true, "Rank icon updated.");
    } catch (e: any) { showMsg(false, "Icon upload failed: " + e.message); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground font-sans">Drag rows to reorder. Click save when done.</p>
        <button onClick={saveOrder} disabled={savingOrder}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded font-display font-bold uppercase tracking-widest text-xs hover:bg-primary/90 transition-colors disabled:opacity-60">
          {savingOrder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Order
        </button>
      </div>
      <div className="space-y-2">
        {ranks.length === 0 && <p className="text-muted-foreground font-sans text-sm">No ranks added yet.</p>}
        {ranks.map((r, idx) => (
          <React.Fragment key={r.id}><div
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragEnter={() => handleDragEnter(idx)}
            onDragEnd={handleDragEnd}
            onDragOver={e => e.preventDefault()}
            className={[
              "flex items-center gap-3 bg-card border rounded-lg px-4 py-3 transition-all select-none",
              dragOverIdx === idx && dragItem.current !== idx ? "border-primary/70 bg-primary/5 scale-[1.01]" : "border-border"
            ].join(" ")}>
            <div className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground transition-colors shrink-0">
              <svg width="12" height="20" viewBox="0 0 12 20" fill="currentColor">
                <circle cx="3" cy="4" r="1.5"/><circle cx="9" cy="4" r="1.5"/>
                <circle cx="3" cy="10" r="1.5"/><circle cx="9" cy="10" r="1.5"/>
                <circle cx="3" cy="16" r="1.5"/><circle cx="9" cy="16" r="1.5"/>
              </svg>
            </div>
            <span className="text-[10px] font-display font-bold text-muted-foreground/50 tracking-widest w-5 text-center shrink-0">{idx + 1}</span>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <label className="relative w-8 h-8 rounded bg-secondary border border-border flex items-center justify-center shrink-0 cursor-pointer group overflow-hidden" title="Upload rank icon">
                {r.icon_url
                  ? <img src={r.icon_url} alt={r.name} className="w-8 h-8 object-contain" />
                  : <span className="font-display font-black text-xs text-primary">{r.tier}</span>}
                <span className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Upload className="w-3.5 h-3.5 text-white" />
                </span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadIcon(e, r.id)} />
              </label>
              <div>
                <p className="font-display font-bold uppercase tracking-wider text-sm text-foreground">{r.name}</p>
                {r.abbreviation && <p className="text-xs text-muted-foreground font-mono">{r.abbreviation}</p>}
              </div>
            </div>
            <button onClick={() => openEdit(r)} className="text-muted-foreground hover:text-primary transition-colors p-1 shrink-0" title="Edit rank"><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1 shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
          {/* Inline Edit Panel */}
          {editRankId === r.id && (
            <div className="mt-2 ml-8 bg-muted/20 border border-border/60 rounded-lg p-4 space-y-3">
              <p className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground">Edit Rank</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Name</label>
                  <input value={editRankData.name ?? ""} onChange={e => setEditRankData((d: any) => ({...d, name: e.target.value}))} className="mf-input" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Abbreviation</label>
                  <input value={editRankData.abbreviation ?? ""} onChange={e => setEditRankData((d: any) => ({...d, abbreviation: e.target.value}))} className="mf-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Tier</label>
                  <input type="number" value={editRankData.tier ?? 0} onChange={e => setEditRankData((d: any) => ({...d, tier: e.target.value}))} className="mf-input" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Rank Class</label>
                  <select value={editRankData.rank_class ?? "enlisted"} onChange={e => setEditRankData((d: any) => ({...d, rank_class: e.target.value}))} className="mf-input text-xs">
                    <option value="enlisted">Enlisted (Pte / E1–E4)</option>
                    <option value="nco">JNCO (Cpl / E5)</option>
                    <option value="snco">SNCO (Sgt–WO / E6–E9)</option>
                    <option value="junior_officer">Junior Officer (2Lt–Lt) — unlocks at Platoon</option>
                    <option value="senior_officer">Senior Officer (Capt–Col) — unlocks at Company</option>
                    <option value="general">General (Brig+) — unlocks at Brigade</option>
                    <option value="staff">Staff / Specialist</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Max Holders <span className="opacity-50">(0 = unlimited)</span></label>
                  <input type="number" min="0" value={editRankData.max_holders ?? 0} onChange={e => setEditRankData((d: any) => ({...d, max_holders: e.target.value}))} className="mf-input" placeholder="e.g. 1 for CO" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Min Roster Size to Unlock</label>
                  <input type="number" min="0" value={editRankData.min_roster_size ?? 0} onChange={e => setEditRankData((d: any) => ({...d, min_roster_size: e.target.value}))} className="mf-input" placeholder="0 = always available" />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={saveRankEdit} disabled={savingEdit} className="px-4 py-1.5 bg-primary text-primary-foreground rounded text-xs font-bold font-display uppercase tracking-wider">
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
                <button onClick={() => setEditRankId(null)} className="px-4 py-1.5 border border-border rounded text-xs text-muted-foreground hover:text-foreground">
                  Cancel
                </button>
              </div>
            </div>
          )}
          </React.Fragment>
        ))}
      </div>
      <div className="bg-card border border-border rounded-lg p-5 space-y-3">
        <h3 className="font-display font-bold uppercase tracking-wider text-xs text-muted-foreground">Add Rank</h3>
        <input value={name} onChange={(e) => setName(e.target.value)} className="mf-input" placeholder="Private, Corporal, Sergeant..." />
        <div className="grid grid-cols-2 gap-3">
          <input value={abbr} onChange={(e) => setAbbr(e.target.value)} className="mf-input" placeholder="Abbreviation (e.g. SGT)" />
          <input value={tier} onChange={(e) => setTier(e.target.value)} type="number" className="mf-input" placeholder="Tier (higher = senior)" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground font-display uppercase tracking-wider block mb-1">Rank Class <span className="opacity-50">(Structure Engine)</span></label>
            <select value={rankClass} onChange={e => setRankClass(e.target.value)} className="mf-input text-xs">
              <option value="enlisted">Enlisted (Pte / E1–E4)</option>
              <option value="nco">JNCO (Cpl / E5)</option>
              <option value="snco">SNCO (Sgt–WO / E6–E9)</option>
              <option value="junior_officer">Junior Officer (2Lt–Lt) — unlocks at Platoon</option>
              <option value="senior_officer">Senior Officer (Capt–Col) — unlocks at Company</option>
              <option value="general">General (Brig+) — unlocks at Brigade</option>
              <option value="staff">Staff / Specialist</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground font-display uppercase tracking-wider block mb-1">Max Holders <span className="opacity-50">(0 = unlimited)</span></label>
            <input value={maxHolders} onChange={e => setMaxHolders(e.target.value)} type="number" min="0" className="mf-input" placeholder="e.g. 1 for CO, 4 for Sect Cmdr" />
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-display uppercase tracking-wider block mb-1">Min Roster Size to Unlock <span className="opacity-50">(0 = always)</span></label>
          <input value={minRosterSize} onChange={e => setMinRosterSize(e.target.value)} type="number" min="0" className="mf-input" placeholder="e.g. 36 to unlock at Company strength" />
        </div>
        <button onClick={add} disabled={adding || !name.trim()}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded clip-angled-sm transition-all disabled:opacity-50">
          {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add Rank
        </button>
      </div>
    </div>
  );
}

function RosterTab({ group, onUpdated, showMsg }: any) {
  const [roster, setRoster] = useState<RosterEntry[]>(group.roster ?? []);
  const [editEntry, setEditEntry] = useState<RosterEntry | null>(null);
  const [editData, setEditData] = useState<Partial<RosterEntry & { specInput: string }>>({});
  const [adding, setAdding] = useState(false);
  const [newCallsign, setNewCallsign] = useState("");
  const [saving, setSaving] = useState(false);

  // Build CoC lookup: roster_id → position title
  const cocByRosterId: Record<string, string> = React.useMemo(() => {
    const coc: any[] = Array.isArray(group.chain_of_command) ? group.chain_of_command : [];
    const map: Record<string, string> = {};
    coc.forEach((pos: any) => {
      if (pos.roster_id) {
        const short = pos.title.replace(/\(.*?\)/g, "").trim();
        map[pos.roster_id] = short;
      }
    });
    return map;
  }, [group.chain_of_command]);

  const STATUSES = ["Active", "Reserve", "AWOL", "MIA", "KIA", "Discharged"];

  const openEdit = (e: RosterEntry) => {
    setEditEntry(e);
    setEditData({ ...e, specInput: "" });
  };

  const saveEdit = async () => {
    if (!editEntry) return;
    // Structure Engine check — validate rank assignment before saving
    if (editData.rankId && editData.rankId !== editEntry.rankId) {
      const doctrine = group.structure_doctrine ?? "british";
      const lockRoles = group.structure_lock_roles !== false;
      const allRanks: any[] = group.ranks ?? [];
      const activeRoster = roster.filter((r: any) => !r.status || r.status === "active" || r.status === "Active");
      const toRank = allRanks.find((r: any) => r.id === editData.rankId);
      if (toRank && lockRoles) {
        const tier = getCurrentTier(doctrine, activeRoster.length);
        const check = checkPromotionAllowed(toRank, tier, activeRoster, allRanks, activeRoster.length);
        if (!check.allowed) {
          showMsg(false, `Promotion blocked: ${check.reason}`);
          setSaving(false);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const updated = await apiFetch<RosterEntry>(`/milsimGroups?path=${group.id}/roster/${editEntry.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          callsign: editData.callsign,
          rankId: editData.rankId || null,
          roleId: editData.roleId || null,
          notes: editData.notes || null,
          status: editData.status || "Active",
          specialisations: editData.specialisations ?? [],
          join_date: editData.join_date || null,
          ops_count: editData.ops_count ?? null,
        }),
      });
      const newRoster = roster.map(r => r.id === editEntry.id ? { ...updated, qualifications: (editEntry as any).qualifications ?? [], awards: (editEntry as any).awards ?? [] } : r);
      setRoster(newRoster); onUpdated({ ...group, roster: newRoster });
      setEditEntry(null); showMsg(true, "Operator record updated.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setSaving(false); }
  };

  const enlist = async () => {
    if (!newCallsign.trim()) return;
    setAdding(true);
    try {
      const entry = await apiFetch<RosterEntry>(`/milsimGroups?path=${group.id}/roster`, {
        method: "POST",
        body: JSON.stringify({ callsign: newCallsign, status: "Active" }),
      });
      const updated = [...roster, { ...entry, qualifications: [], awards: [] }];
      setRoster(updated); onUpdated({ ...group, roster: updated });
      setNewCallsign(""); showMsg(true, "Operator enlisted.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setAdding(false); }
  };

  // ── Discharge modal state ──
  const [dischargeTarget, setDischargeTarget] = useState<RosterEntry | null>(null);
  const [dischargeForm, setDischargeForm] = useState({ discharge_type: "Administrative", reason: "", effective_date: new Date().toISOString().slice(0,10), notes: "", reinstatement_eligible: true });
  const [discharging, setDischarging] = useState(false);
  const [showDischargeRecords, setShowDischargeRecords] = useState(false);
  const [dischargeRecords, setDischargeRecords] = useState<any[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);

  const openDischarge = (e: RosterEntry) => {
    setDischargeTarget(e);
    setDischargeForm({ discharge_type: "Administrative", reason: "", effective_date: new Date().toISOString().slice(0,10), notes: "", reinstatement_eligible: true });
  };

  const processDischarge = async () => {
    if (!dischargeTarget || !dischargeForm.reason.trim()) return;
    setDischarging(true);
    try {
      await apiFetch(`/milsimGroups?path=${group.id}/roster/discharge`, {
        method: "POST",
        body: JSON.stringify({ roster_id: dischargeTarget.id, ...dischargeForm }),
      });
      const updated = roster.map(r => r.id === dischargeTarget.id ? { ...r, status: "Discharged" } : r);
      setRoster(updated); onUpdated({ ...group, roster: updated });
      setDischargeTarget(null);
      showMsg(true, `${dischargeTarget.callsign} has been discharged.`);
    } catch (e: any) { showMsg(false, e.message); }
    finally { setDischarging(false); }
  };

  const loadDischargeRecords = async () => {
    setLoadingRecords(true);
    try {
      const records = await apiFetch<any[]>(`/milsimGroups?path=${group.id}/discharges`);
      setDischargeRecords(records ?? []);
      setShowDischargeRecords(true);
    } catch (e: any) { showMsg(false, e.message); }
    finally { setLoadingRecords(false); }
  };

  const addSpec = () => {
    const val = (editData.specInput ?? "").trim().toUpperCase();
    if (!val) return;
    setEditData(d => ({ ...d, specialisations: [...(d.specialisations ?? []), val], specInput: "" }));
  };

  const statusColour = (s: string) =>
    s === "Active"     ? "bg-green-500/15 text-green-400 border-green-500/30" :
    s === "Reserve"    ? "bg-blue-500/15 text-blue-400 border-blue-500/30" :
    s === "AWOL"       ? "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" :
    s === "MIA"        ? "bg-orange-500/15 text-orange-400 border-orange-500/30" :
    s === "KIA"        ? "bg-red-500/15 text-red-400 border-red-500/30" :
    s === "Discharged" ? "bg-secondary text-muted-foreground border-border" :
    "bg-green-500/15 text-green-400 border-green-500/30";

  const [rosterExporting, setRosterExporting] = useState(false);
  return (
    <div className="max-w-4xl space-y-6">
      {/* Roster list */}
      <div className="flex justify-end mb-2">
        <button onClick={async () => { setRosterExporting(true); try { const url = await exportRosterToSheets(roster, group.ranks ?? [], group.roles ?? [], group.name); window.open(url, "_blank"); } catch(err: any) { showMsg(false, "Sheets export: " + err.message); } finally { setRosterExporting(false); } }} disabled={rosterExporting || roster.length === 0} className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-600/20 hover:bg-green-600/30 border border-green-500/40 text-green-400 rounded text-xs font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50">
          {rosterExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>↗</span>} Export to Google Sheets
        </button>
      </div>
      <div className="space-y-2">
        {roster.length === 0 && <p className="text-muted-foreground font-sans text-sm p-4 bg-card border border-border rounded-lg">Roster is empty. Enlist your first operator below.</p>}
        {roster.map((e) => {
          const rank = e.rankId ? (group.ranks ?? []).find((r: any) => r.id === e.rankId) : null;
          const role = e.roleId ? (group.roles ?? []).find((r: any) => r.id === e.roleId) : null;
          return (
            <div key={e.id} className="flex items-center gap-3 bg-card border border-border rounded-lg px-4 py-3 hover:border-primary/20 transition-colors">
              <div className="w-8 h-8 rounded bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <span className="font-display font-black text-[10px] text-primary">{(e.callsign ?? "??").slice(0,2).toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold uppercase tracking-wider text-sm text-foreground">{e.callsign}</span>
                  <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${statusColour(e.status ?? "Active")}`}>
                    {e.status ?? "Active"}
                  </span>
                  {rank && <span className="text-[9px] text-primary font-display font-bold uppercase tracking-widest">{rank.name}</span>}
                  {role && <span className="text-[9px] text-muted-foreground font-display uppercase tracking-widest bg-secondary border border-border px-1.5 py-0.5 rounded">{role.name}</span>}
                  {cocByRosterId[e.id] && <span className="text-[9px] font-display font-bold uppercase tracking-widest text-amber-400 border border-amber-400/30 bg-amber-400/10 px-1.5 py-0.5 rounded">{cocByRosterId[e.id]}</span>}
                </div>
                {(e.specialisations ?? []).length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {(e.specialisations ?? []).map((s, i) => (
                      <span key={i} className="text-[8px] font-display font-bold uppercase tracking-widest bg-accent/10 border border-accent/25 text-accent px-1 py-0.5 rounded">{s}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => openEdit(e)} className="p-1.5 rounded text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" title="Edit Operator Record">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => openDischarge(e)} className="p-1.5 rounded text-muted-foreground hover:text-orange-400 hover:bg-orange-400/10 transition-colors" title="Discharge Operator">
                  <UserMinus2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Enlist form */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-3">
        <h3 className="font-display font-bold uppercase tracking-wider text-xs text-muted-foreground">Enlist Operator</h3>
        <div className="flex gap-3">
          <input value={newCallsign} onChange={(e) => setNewCallsign(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") enlist(); }}
            className="mf-input flex-1" placeholder="Callsign" />
          <button onClick={enlist} disabled={adding || !newCallsign.trim()}
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded clip-angled-sm transition-all disabled:opacity-50">
            {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Enlist
          </button>
        </div>
        <p className="text-xs text-muted-foreground font-sans">Use the edit button to set rank, role, status, specialisations, and more.</p>
      </div>

      {/* Discharge Records Toggle */}
      <div>
        <button onClick={loadDischargeRecords} disabled={loadingRecords}
          className="inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground border border-border hover:border-primary/30 rounded px-4 py-2 transition-colors">
          {loadingRecords ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
          Discharge Records
        </button>
      </div>

      {/* Discharge Records Panel */}
      {showDischargeRecords && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-border">
            <h3 className="font-display font-bold uppercase tracking-wider text-xs text-foreground flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-primary" /> Discharge Records</h3>
            <button onClick={() => setShowDischargeRecords(false)} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
          </div>
          {dischargeRecords.length === 0 ? (
            <p className="p-5 text-sm text-muted-foreground font-sans">No discharge records on file.</p>
          ) : (
            <div className="divide-y divide-border">
              {dischargeRecords.map((d: any) => (
                <div key={d.id} className="px-5 py-4 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold uppercase tracking-wider text-sm text-foreground">{d.callsign}</span>
                    <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                      d.discharge_type === "Honourable" ? "bg-green-500/15 text-green-400 border-green-500/30" :
                      d.discharge_type === "Dishonourable" ? "bg-red-500/15 text-red-400 border-red-500/30" :
                      d.discharge_type === "Medical" ? "bg-blue-500/15 text-blue-400 border-blue-500/30" :
                      d.discharge_type === "Voluntary" ? "bg-purple-500/15 text-purple-400 border-purple-500/30" :
                      "bg-secondary text-muted-foreground border-border"
                    }`}>{d.discharge_type}</span>
                    {d.final_rank && <span className="text-[9px] text-primary font-display font-bold uppercase tracking-widest">{d.final_rank}</span>}
                    {d.final_role && <span className="text-[9px] text-muted-foreground font-display uppercase tracking-widest bg-secondary border border-border px-1.5 py-0.5 rounded">{d.final_role}</span>}
                    {d.reinstatement_eligible === false && <span className="text-[9px] font-display font-bold uppercase tracking-widest text-red-400 border border-red-400/30 bg-red-400/10 px-1.5 py-0.5 rounded">No Reinstatement</span>}
                  </div>
                  <p className="text-xs text-foreground/80 font-sans">{d.reason}</p>
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground font-sans">
                    <span>Effective: {d.effective_date}</span>
                    <span>Ops served: {d.ops_served ?? 0}</span>
                    <span>Conducted by: {d.conducted_by_username}</span>
                  </div>
                  {d.notes && <p className="text-[11px] text-muted-foreground font-sans italic">{d.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Discharge Modal */}
      {dischargeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setDischargeTarget(null)}>
          <div className="bg-card border border-orange-500/30 rounded-xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="font-display font-black uppercase tracking-widest text-sm text-foreground flex items-center gap-2">
                  <UserMinus2 className="w-4 h-4 text-orange-400" /> Discharge Order
                </h2>
                <p className="text-xs text-muted-foreground font-sans mt-0.5">Operator: <span className="text-foreground font-bold">{dischargeTarget.callsign}</span></p>
              </div>
              <button onClick={() => setDischargeTarget(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Type */}
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Discharge Classification</label>
                <div className="grid grid-cols-3 gap-2">
                  {["Honourable", "Administrative", "Dishonourable", "Medical", "Voluntary"].map(t => (
                    <button key={t} onClick={() => setDischargeForm(f => ({...f, discharge_type: t, reinstatement_eligible: t !== "Dishonourable"}))}
                      className={`text-[10px] font-display font-bold uppercase tracking-widest px-3 py-2 rounded border transition-colors ${
                        dischargeForm.discharge_type === t
                          ? t === "Dishonourable" ? "bg-red-500/15 text-red-400 border-red-500/40"
                          : t === "Honourable" ? "bg-green-500/15 text-green-400 border-green-500/40"
                          : "bg-primary/15 text-primary border-primary/40"
                          : "bg-secondary border-border text-muted-foreground hover:border-primary/20"
                      }`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              {/* Reason */}
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Reason for Discharge <span className="text-destructive">*</span></label>
                <textarea value={dischargeForm.reason} onChange={e => setDischargeForm(f => ({...f, reason: e.target.value}))}
                  className="mf-input w-full min-h-[80px] resize-none" placeholder="State the reason for this discharge..." />
              </div>
              {/* Effective Date */}
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Effective Date</label>
                <input type="date" value={dischargeForm.effective_date} onChange={e => setDischargeForm(f => ({...f, effective_date: e.target.value}))} className="mf-input w-full" />
              </div>
              {/* Notes */}
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Additional Notes</label>
                <textarea value={dischargeForm.notes} onChange={e => setDischargeForm(f => ({...f, notes: e.target.value}))}
                  className="mf-input w-full min-h-[60px] resize-none" placeholder="Optional — classified notes, context, etc." />
              </div>
              {/* Reinstatement */}
              <div className="flex items-center gap-3">
                <input type="checkbox" id="reinstatement" checked={dischargeForm.reinstatement_eligible}
                  onChange={e => setDischargeForm(f => ({...f, reinstatement_eligible: e.target.checked}))}
                  className="w-4 h-4 accent-primary" />
                <label htmlFor="reinstatement" className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground cursor-pointer">
                  Eligible for Reinstatement
                </label>
              </div>
            </div>
            <div className="p-5 border-t border-border flex gap-3 justify-end">
              <button onClick={() => setDischargeTarget(null)}
                className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground border border-border rounded px-4 py-2 transition-colors">
                Cancel
              </button>
              <button onClick={processDischarge} disabled={discharging || !dischargeForm.reason.trim()}
                className="inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest bg-orange-500/20 text-orange-400 border border-orange-500/30 hover:bg-orange-500/30 rounded px-5 py-2 transition-colors disabled:opacity-50">
                {discharging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus2 className="w-3.5 h-3.5" />}
                Process Discharge
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setEditEntry(null)}>
          <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h2 className="font-display font-black uppercase tracking-widest text-sm text-foreground">Operator Record — {editEntry.callsign}</h2>
              <button onClick={() => setEditEntry(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">

              {/* Callsign */}
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Callsign</label>
                <input value={editData.callsign ?? ""} onChange={e => setEditData(d => ({...d, callsign: e.target.value}))} className="mf-input w-full" />
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Duty Status</label>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map(s => (
                    <button key={s} onClick={() => setEditData(d => ({...d, status: s}))}
                      className={`text-[10px] font-display font-bold uppercase tracking-widest px-3 py-1.5 rounded border transition-colors ${editData.status === s ? statusColour(s) : "bg-secondary border-border text-muted-foreground hover:border-primary/30"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rank + Role */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Rank</label>
                  <select value={editData.rankId ?? ""} onChange={e => setEditData(d => ({...d, rankId: e.target.value || null}))} className="mf-input w-full">
                    <option value="">No Rank</option>
                    {(() => {
                      const doctrine = group.structure_doctrine ?? "british";
                      const activeRoster = roster.filter((r: any) => !r.status || r.status === "active" || r.status === "Active");
                      const tier = getCurrentTier(doctrine, activeRoster.length);
                      return (group.ranks ?? []).map((r: any) => {
                        const locked = group.structure_lock_roles !== false && !isRankUnlocked(r, tier, activeRoster.length);
                        return <option key={r.id} value={r.id}>{locked ? "🔒 " : ""}{r.name}{locked ? " (locked)" : ""}</option>;
                      });
                    })()}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Role</label>
                  <select value={editData.roleId ?? ""} onChange={e => setEditData(d => ({...d, roleId: e.target.value || null}))} className="mf-input w-full">
                    <option value="">No Role</option>
                    {(group.roles ?? []).map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Specialisations */}
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Specialisations / MOS Tags</label>
                <div className="flex gap-2 mb-2">
                  <input value={editData.specInput ?? ""} onChange={e => setEditData(d => ({...d, specInput: e.target.value}))}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addSpec(); } }}
                    className="mf-input flex-1" placeholder="e.g. CQB, JTAC, Medic, Sniper, EOD..." />
                  <button onClick={addSpec} className="px-3 py-2 bg-secondary border border-border rounded text-xs font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(editData.specialisations ?? []).map((s, i) => (
                    <span key={i} className="text-[10px] font-display font-bold uppercase tracking-widest bg-accent/10 border border-accent/25 text-accent px-2 py-0.5 rounded flex items-center gap-1">
                      {s}
                      <button onClick={() => setEditData(d => ({...d, specialisations: (d.specialisations ?? []).filter((_, j) => j !== i)}))} className="hover:text-destructive ml-0.5">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Join date + Ops count */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Date Enlisted</label>
                  <input type="date" value={editData.join_date ?? ""} onChange={e => setEditData(d => ({...d, join_date: e.target.value || null}))} className="mf-input w-full" />
                </div>
                <div>
                  <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Ops Participated</label>
                  <input type="number" min={0} value={editData.ops_count ?? ""} onChange={e => setEditData(d => ({...d, ops_count: e.target.value ? parseInt(e.target.value) : null}))} className="mf-input w-full" placeholder="0" />
                </div>
              </div>

              {/* Notes / biog */}
              <div>
                <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Commander Notes / Bio</label>
                <textarea value={editData.notes ?? ""} onChange={e => setEditData(d => ({...d, notes: e.target.value}))}
                  className="mf-input w-full min-h-[80px] resize-y" placeholder="Conduct record, background, notable service..." />
              </div>

              <button onClick={saveEdit} disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-5 py-3 rounded clip-angled-sm transition-all disabled:opacity-50">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save Operator Record
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TYPE_DEFS = [
  { id: "medal", label: "Medal", Icon: Medal, desc: "Worn on uniform" },
  { id: "ribbon", label: "Ribbon", Icon: Star, desc: "Service ribbon bar" },
  { id: "qualification-patch", label: "Qual. Patch", Icon: GraduationCap, desc: "Skill or unit patch" },
] as const;


// ─── ORBAT Builder Tab (J5 Planning) ─────────────────────────────────────────
// ─── Fullscreen ORBAT (fixed overlay, group passed after load) ───────────────
function OrbatFullscreen({ group, setGroup, showMsg, onBack }: any) {
  const [orbatJson, setOrbatJson] = useState(group.orbat ?? "");
  const [saving, setSaving] = useState(false);

  const saveOrbat = async () => {
    setSaving(true);
    try {
      const updated = await apiFetch(`/milsimGroups?path=${group.id}/info`, {
        method: "PATCH",
        body: JSON.stringify({ orbat: orbatJson }),
      });
      setGroup(updated);
      showMsg(true, "ORBAT saved.");
    } catch (e: any) { showMsg(false, e.message ?? "Failed to save ORBAT"); }
    finally { setSaving(false); }
  };

  return (
    <div style={{position:"fixed",inset:0,zIndex:9999,background:"#0d0e11",display:"flex",flexDirection:"column",width:"100vw",height:"100vh"}}>
      <OrbatBuilder
        value={orbatJson}
        onChange={setOrbatJson}
        groupName={group.name}
        onBack={onBack}
        onSave={saveOrbat}
      />
    </div>
  );
}

function OrbatBuilderTab({ group, onUpdated, showMsg, globalIsPro }: any) {
  const [orbatJson, setOrbatJson] = useState(group.orbat ?? "");
  const [saving, setSaving] = useState(false);

  const saveOrbat = async () => {
    setSaving(true);
    try {
      const updated = await apiFetch(`/milsimGroups?path=${group.id}/info`, {
        method: "PATCH",
        body: JSON.stringify({ orbat: orbatJson }),
      });
      onUpdated(updated);
      showMsg(true, "ORBAT saved.");
    } catch (e: any) { showMsg(false, e.message ?? "Failed to save ORBAT"); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 mb-2 shrink-0">
        <GitBranch className="w-5 h-5 text-primary" />
        <h2 className="font-display font-black text-lg uppercase tracking-wider text-foreground">ORBAT Builder</h2>
        <span className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
          <Crown className="w-2.5 h-2.5" /> Pro
        </span>
      </div>
      <div className="flex-1 min-h-0">
        <OrbatProGate group={group} orbatJson={orbatJson} setOrbatJson={setOrbatJson} saveOrbat={saveOrbat} saving={saving} globalIsPro={globalIsPro} />
      </div>
    </div>
  );
}

// ─── Customisation Tab (Awards + Qualifications + ORBAT) ─────────────────────
function CustomisationTab({ group, showMsg, globalIsPro }: any) {
  const [sub, setSub] = useState<"awards" | "quals" | "orbat" | "tacplanner" | "featured">("awards");
  const [orbatJson, setOrbatJson] = useState(group.orbat ?? "");
  const [saving, setSaving] = useState(false);

  const saveOrbat = async () => {
    setSaving(true);
    try {
      const updated = await apiFetch(`/milsimGroups?path=${group.id}/info`, { method: "PATCH", body: JSON.stringify({ orbat: orbatJson }) });
      showMsg(true, "ORBAT saved.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setSaving(false); }
  };

  const SUB_TABS = [
    { id: "awards" as const, label: "Awards",         icon: Medal },
    { id: "quals"  as const, label: "Qualifications", icon: GraduationCap },
    { id: "orbat"  as const, label: "ORBAT Builder",  icon: GitBranch },
    { id: "tacplanner" as const, label: "Area of Operations Map",  icon: Map },
    { id: "featured"   as const, label: "Featured",       icon: Star },
  ];
  return (
    <div className="space-y-4">
      {/* Sub-tab pills */}
      <div className="grid grid-cols-5 gap-1.5 border-b border-border pb-3">
        {SUB_TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setSub(id)}
            className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded text-xs font-display font-bold uppercase tracking-wider transition-all border ${
              sub === id ? "bg-primary/15 border-primary/50 text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/60"
            }`}>
            <Icon className="w-3.5 h-3.5" />{label}
            {id === "awards" && !globalIsPro && <Crown className="w-3 h-3 text-yellow-400 ml-0.5" />}
            {id === "quals"  && !globalIsPro && <Crown className="w-3 h-3 text-yellow-400 ml-0.5" />}
          </button>
        ))}
      </div>
      {sub === "awards" && <TabErrorBoundary tabName="Awards"><AwardsTab group={group} showMsg={showMsg} globalIsPro={globalIsPro} /></TabErrorBoundary>}
      {sub === "quals"  && <TabErrorBoundary tabName="Quals">{globalIsPro ? <QualsTab group={group} showMsg={showMsg} /> : (
        <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
          <Crown className="w-10 h-10 text-yellow-400 opacity-60" />
          <h3 className="font-display font-black text-2xl uppercase tracking-wider text-foreground">Commander Pro Required</h3>
          <p className="text-muted-foreground font-sans text-sm max-w-sm">The Qualifications system — including custom qualification types and badge grants — is a Commander Pro feature.</p>
          <a href="/commander-pro" className="inline-flex items-center gap-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 text-yellow-300 font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded transition-all mt-2">
            <Crown className="w-3.5 h-3.5" /> Upgrade to Commander Pro
          </a>
        </div>
      )}</TabErrorBoundary>}
      {sub === "orbat"  && (
        <TabErrorBoundary tabName="ORBAT"><OrbatProGate group={group} orbatJson={orbatJson} setOrbatJson={setOrbatJson} saveOrbat={saveOrbat} saving={saving} globalIsPro={globalIsPro} /></TabErrorBoundary>
      )}
      {sub === "tacplanner" && (
        <TabErrorBoundary tabName="TacPlanner">
          <div style={{ height: "calc(100vh - 280px)", minHeight: 600 }}>
            <TacticalPlanner group={group} showMsg={showMsg} />
          </div>
        </TabErrorBoundary>
      )}
      {sub === "featured" && <TabErrorBoundary tabName="Featured"><FeaturedListingTab group={group} showMsg={showMsg} globalIsPro={globalIsPro} /></TabErrorBoundary>}
    </div>
  );
}

// ─── Image Upload Helper Components ─────────────────────────────────────────
// Upload via rankReorder backend (avoids CORS on direct Base44 API calls)
async function uploadImageToStorage(file: File): Promise<string | null> {
  const fd = new FormData();
  fd.append("file", file);
  const token = sessionStorage.getItem("tag_auth_token") ?? localStorage.getItem("tag_auth_token") ?? "";
  const res = await fetch("https://agent-tag-lead-developer-cff87ae4.base44.app/functions/rankReorder?path=upload", {
    method: "POST",
    body: fd,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? "Upload failed");
  }
  const data = await res.json();
  return data.url ?? data.file_url ?? null;
}

function ImageUploadField({ value, onChange, aspectHint, label, onPositionChange, position }: {
  value: string; onChange: (url: string) => void; aspectHint?: string; label?: string;
  onPositionChange?: (pos: { x: number; y: number }) => void;
  position?: { x: number; y: number };
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  // Drag-to-pan state (banner only)
  const [isDragging, setIsDragging] = useState(false);
  const [imgPos, setImgPos] = useState<{ x: number; y: number }>(position ?? { x: 50, y: 50 });
  const dragStart = React.useRef<{ mx: number; my: number; ix: number; iy: number } | null>(null);

  const isBanner = aspectHint === "banner";

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please select an image file."); return; }
    if (file.size > 70 * 1024 * 1024) { setError("Max file size is 70MB."); return; }
    setError(null);
    setUploading(true);
    try {
      const url = await uploadImageToStorage(file);
      if (url) { onChange(url); setImgPos({ x: 50, y: 50 }); }
      else setError("Upload returned no URL.");
    } catch (err: any) {
      setError(err.message ?? "Upload failed.");
    }
    setUploading(false);
    e.target.value = "";
  };

  // Track whether mouse moved enough to count as a drag (prevents click-after-drag opening file picker)
  const didDrag = React.useRef(false);

  // Mouse drag handlers for banner focal point
  const onMouseDown = (e: React.MouseEvent) => {
    if (!isBanner || !value || uploading) return;
    e.preventDefault();
    didDrag.current = false;
    setIsDragging(true);
    dragStart.current = { mx: e.clientX, my: e.clientY, ix: imgPos.x, iy: imgPos.y };
  };
  const onMouseMove = React.useCallback((e: MouseEvent) => {
    if (!isDragging || !dragStart.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dx = ((e.clientX - dragStart.current.mx) / rect.width) * 100;
    const dy = ((e.clientY - dragStart.current.my) / rect.height) * 100;
    // Only count as a drag if moved more than 3px
    if (Math.abs(e.clientX - dragStart.current.mx) > 3 || Math.abs(e.clientY - dragStart.current.my) > 3) {
      didDrag.current = true;
    }
    const nx = Math.max(0, Math.min(100, dragStart.current.ix - dx));
    const ny = Math.max(0, Math.min(100, dragStart.current.iy - dy));
    setImgPos({ x: nx, y: ny });
    onPositionChange?.({ x: nx, y: ny });
  }, [isDragging, onPositionChange]);
  const onMouseUp = React.useCallback(() => { setIsDragging(false); dragStart.current = null; }, []);

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    }
    return () => { window.removeEventListener("mousemove", onMouseMove); window.removeEventListener("mouseup", onMouseUp); };
  }, [isDragging, onMouseMove, onMouseUp]);

  // Touch support
  const onTouchStart = (e: React.TouchEvent) => {
    if (!isBanner || !value || uploading) return;
    const t = e.touches[0];
    didDrag.current = false;
    setIsDragging(true);
    dragStart.current = { mx: t.clientX, my: t.clientY, ix: imgPos.x, iy: imgPos.y };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !dragStart.current || !containerRef.current) return;
    const t = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const dx = ((t.clientX - dragStart.current.mx) / rect.width) * 100;
    const dy = ((t.clientY - dragStart.current.my) / rect.height) * 100;
    if (Math.abs(t.clientX - dragStart.current.mx) > 3 || Math.abs(t.clientY - dragStart.current.my) > 3) {
      didDrag.current = true;
    }
    const nx = Math.max(0, Math.min(100, dragStart.current.ix - dx));
    const ny = Math.max(0, Math.min(100, dragStart.current.iy - dy));
    setImgPos({ x: nx, y: ny });
    onPositionChange?.({ x: nx, y: ny });
  };

  return (
    <div className="space-y-2">
      <div
        ref={containerRef}
        onClick={() => { if (!didDrag.current && !uploading) inputRef.current?.click(); }}
        onMouseDown={isBanner && value ? onMouseDown : undefined}
        onTouchStart={isBanner && value ? onTouchStart : undefined}
        onTouchMove={isBanner && value ? onTouchMove : undefined}
        onTouchEnd={() => setIsDragging(false)}
        className={`relative flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg overflow-hidden transition-all
          ${isBanner ? "h-56" : "h-52"}
          ${isDragging ? "cursor-grabbing" : isBanner && value ? "cursor-grab" : "cursor-pointer"}
          ${uploading ? "border-primary/60 bg-primary/5" : value ? "border-green-500/40 bg-green-500/5" : "border-border hover:border-primary/40 bg-secondary/20"}`}>
        {value && !uploading && (
          <img
            src={value}
            alt="preview"
            draggable={false}
            className={`absolute inset-0 w-full h-full rounded-lg pointer-events-none select-none ${isBanner ? "object-cover" : "object-contain p-2 opacity-90"}`}
            style={isBanner ? { objectPosition: `${imgPos.x}% ${imgPos.y}%`, opacity: 0.8 } : {}}
          />
        )}
        {isBanner && value && !uploading && (
          <div className="absolute top-2 right-2 z-20 bg-black/60 rounded px-2 py-1 flex items-center gap-1.5 pointer-events-none select-none">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" /></svg>
            <p className="text-[9px] font-display uppercase tracking-wider text-white">Drag to reframe</p>
          </div>
        )}
        <div className={`relative z-10 flex flex-col items-center gap-1 ${value && !uploading ? "opacity-0 hover:opacity-100 transition-opacity" : ""}`}>
          {uploading ? (
            <><Loader2 className="w-5 h-5 animate-spin text-primary" /><p className="text-[10px] font-display uppercase tracking-wider text-primary">Uploading...</p></>
          ) : value ? (
            <div className="bg-black/70 rounded px-2 py-1 flex items-center gap-1.5"><Upload className="w-3 h-3 text-white" /><p className="text-[10px] font-display uppercase tracking-wider text-white">Click to replace</p></div>
          ) : (
            <><Upload className="w-5 h-5 text-muted-foreground" /><p className="text-[10px] font-display uppercase tracking-wider text-muted-foreground">Click to upload {label ?? "image"}</p><p className="text-[9px] text-muted-foreground/60">{isBanner ? "960×540px recommended" : "400×400px recommended"} · PNG/JPG/WEBP · Max 70MB</p></>
          )}
        </div>
      </div>
      {isBanner && value && (
        <p className="text-[9px] text-muted-foreground/60 font-sans">
          Focal point: {Math.round(imgPos.x)}% × {Math.round(imgPos.y)}% — drag the image to reframe. Saves when you submit the form.
        </p>
      )}
      {error && <p className="text-[10px] text-red-400 font-sans">{error}</p>}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
}

function LogoUploadField({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  return <ImageUploadField value={value} onChange={onChange} aspectHint="logo" label="logo" />;
}
function BannerUploadField({ value, onChange, onPositionChange, position }: {
  value: string; onChange: (url: string) => void;
  onPositionChange?: (pos: { x: number; y: number }) => void;
  position?: { x: number; y: number };
}) {
  return <ImageUploadField value={value} onChange={onChange} aspectHint="banner" label="banner" onPositionChange={onPositionChange} position={position} />;
}

function AwardsTab({ group, showMsg, globalIsPro }: any) {
  const [subView, setSubView] = useState<"library" | "issued">("library");
  const [defs, setDefs] = useState<any[]>([]);
  const [issued, setIssued] = useState<any[]>([]);
  const [roster, setRoster] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  // Ribbon picker state
  const [createMode, setCreateMode] = useState<"build" | "library">("build");
  const [ribbonConfig, setRibbonConfig] = useState<RibbonConfig>({ colors: ["#1a3a6b","#c8102e","#ffffff"], pattern: "thirds" });
  const [awardName, setAwardName] = useState("");
  const [pickerCountry, setPickerCountry] = useState<string>("");
  const [pickerBranch, setPickerBranch] = useState<string>("US Army");
  const [pickerSearch, setPickerSearch] = useState<string>("");
  const [pickerPage, setPickerPage] = useState<number>(0);
  const [selectedTemplate, setSelectedTemplate] = useState<RibbonTemplate | null>(null);
  const [pickerMods, setPickerMods] = useState<Record<string, string>>({}); // modifier selections for picker preview
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);

  // Issue state
  const [issuingDefId, setIssuingDefId] = useState<string | null>(null);
  const [issueRosterId, setIssueRosterId] = useState("");
  const [issueCitation, setIssueCitation] = useState("");
  const [issuing, setIssuing] = useState(false);

  // Library search / filter / pagination
  const [libSearch, setLibSearch] = useState("");
  const [libCountry, setLibCountry] = useState("all");
  const [libPage, setLibPage] = useState(1);
  const LIB_PER_PAGE = 20;

  // Derived filtered/paged defs
  const filteredDefs = React.useMemo(() => {
    const q = libSearch.toLowerCase();
    return defs.filter(d => {
      if (libCountry !== "all" && d.source_country !== libCountry) return false;
      if (q && !(d.name ?? "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [defs, libSearch, libCountry]);
  const libTotalPages = Math.ceil(filteredDefs.length / LIB_PER_PAGE);
  const pagedDefs = filteredDefs.slice((libPage - 1) * LIB_PER_PAGE, libPage * LIB_PER_PAGE);
  const libCountries = React.useMemo(() => {
    const s = new Set<string>();
    defs.forEach(d => { if (d.source_country) s.add(d.source_country); });
    return Array.from(s).sort();
  }, [defs]);
  const handleLibSearch = (v: string) => { setLibSearch(v); setLibPage(1); setIssuingDefId(null); };
  const handleLibCountry = (v: string) => { setLibCountry(v); setLibPage(1); setIssuingDefId(null); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [defsData, issuedData, rosterData] = await Promise.all([
        apiFetch<any>(`/milsimAwards?path=${group.id}/award-defs`).catch(() => []),
        apiFetch<any>(`/milsimAwards?path=${group.id}/awards`).catch(() => []),
        apiFetch<any>(`/milsimGroups?path=mine/own`).catch(() => ({ roster: [] })),
      ]);
      setDefs(Array.isArray(defsData) ? defsData : []);
      setIssued(Array.isArray(issuedData) ? issuedData : []);
      setRoster(rosterData?.roster ?? []);
    } catch { showMsg(false, "Failed to load awards."); }
    finally { setLoading(false); }
  }, [group.id]);

  useEffect(() => { load(); }, [load]);

  // Derive available branches based on selected country
  const availableBranches = pickerCountry
    ? (RIBBON_BRANCHES_BY_COUNTRY[pickerCountry] ?? [])
    : [];

  // Filter ribbon templates
  const filteredTemplates = RIBBON_TEMPLATES.filter(r => {
    if (pickerCountry && r.country !== pickerCountry) return false;
    if (pickerBranch && r.branch !== pickerBranch) return false;
    if (pickerSearch) {
      const q = pickerSearch.toLowerCase();
      if (!r.name.toLowerCase().includes(q) && !(r.sku ?? '').toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const RIBBONS_PER_PAGE = 48;
  const totalPages = Math.ceil(filteredTemplates.length / RIBBONS_PER_PAGE);
  const pagedTemplates = filteredTemplates.slice(pickerPage * RIBBONS_PER_PAGE, (pickerPage + 1) * RIBBONS_PER_PAGE);

  const resetForm = () => {
    setShowCreate(false);
    setSelectedTemplate(null);
    setPickerCountry(""); setPickerBranch("US Army"); setPickerSearch(""); setDesc(""); setPickerPage(0);
    setAwardName(""); setRibbonConfig({ colors: ["#1a3a6b","#c8102e","#ffffff"], pattern: "thirds" });
    setCreateMode("build"); setPickerMods({});
  };

  const createDef = async () => {
    if (!selectedTemplate) { showMsg(false, "Select a ribbon first."); return; }
    setCreating(true);
    try {
      // Resolve modifier URL if user selected a grade variant
      const modifiers = getRibbonModifiers(selectedTemplate!.url);
      let resolvedUrl = selectedTemplate!.url;
      let resolvedName = selectedTemplate!.name;
      for (const mod of modifiers) {
        if (mod.type === "select" && mod.options && pickerMods[mod.name]) {
          const opt = mod.options.find(o => o.value === pickerMods[mod.name]);
          if (opt?.url) resolvedUrl = opt.url;
          resolvedName = `${selectedTemplate!.name} - ${pickerMods[mod.name]}`;
        }
      }
      const payload = { name: resolvedName, description: desc || undefined, award_type: "ribbon", image_url: resolvedUrl, source_country: selectedTemplate!.country, source_branch: selectedTemplate!.branch, sort_order: defs.length };
      await apiFetch(`/milsimAwards?path=${group.id}/award-defs`, { method: "POST", body: JSON.stringify(payload) });
      resetForm();
      showMsg(true, "Award added to library.");
      load();
    } catch (e: any) { showMsg(false, e.message || "Failed to create award."); }
    finally { setCreating(false); }
  };

  const deleteDef = async (defId: string) => {
    try {
      await apiFetch(`/milsimAwards?path=${group.id}/award-defs/${defId}`, { method: "DELETE" });
      showMsg(true, "Award removed."); load();
    } catch (e: any) { showMsg(false, e.message); }
  };

  const issueAward = async (defId: string) => {
    if (!issueRosterId) { showMsg(false, "Select a roster member."); return; }
    setIssuing(true);
    try {
      await apiFetch(`/milsimAwards?path=${group.id}/awards`, {
        method: "POST",
        body: JSON.stringify({ rosterEntryId: issueRosterId, awardDefId: defId, citation: issueCitation || undefined }),
      });
      showMsg(true, "Award issued.");
      setIssuingDefId(null); setIssueRosterId(""); setIssueCitation("");
      load();
    } catch (e: any) { showMsg(false, e.message); }
    finally { setIssuing(false); }
  };

  const revokeAward = async (id: string) => {
    try {
      await apiFetch(`/milsimAwards?path=${group.id}/awards/${id}`, { method: "DELETE" });
      showMsg(true, "Award revoked."); load();
    } catch (e: any) { showMsg(false, e.message); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (!globalIsPro) return (
    <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-8 text-center space-y-3">
      <Crown className="w-10 h-10 mx-auto text-yellow-400 opacity-70" />
      <p className="font-display font-bold uppercase tracking-widest text-yellow-400">Commander Pro Required</p>
      <p className="text-sm text-muted-foreground font-sans max-w-sm mx-auto">
        The Awards Library — including custom medals, ribbons, and qualification badges — is a Commander Pro feature.
        Upgrade to create your own award definitions and issue decorations to your roster.
      </p>
      <a href="/commander-pro" className="inline-flex items-center gap-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 text-yellow-300 font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded transition-all mt-2">
        <Crown className="w-3.5 h-3.5" /> Upgrade to Commander Pro
      </a>
    </div>
  );

  return (
    <div className="max-w-4xl space-y-5">

      {/* Sub-nav */}
      <div className="flex gap-1 border-b border-border">
        {([
          { id: "library", label: "Award Library" },
          { id: "issued", label: `Issued (${issued.length})` },
        ] as const).map(v => (
          <button key={v.id} onClick={() => setSubView(v.id)}
            className={`px-4 py-2.5 font-display font-bold uppercase tracking-wider text-xs border-b-2 transition-colors ${subView === v.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {v.label}
          </button>
        ))}
      </div>

      {/* ── AWARD LIBRARY ── */}
      {subView === "library" && (
        <div className="space-y-4">

          {/* Existing award defs — search, filter, pagination */}
          {defs.length > 0 && (
            <div className="space-y-3">
              {/* Search + filter bar */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[160px]">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search award library..."
                    value={libSearch}
                    onChange={e => handleLibSearch(e.target.value)}
                    className="w-full text-xs bg-background border border-border rounded pl-6 pr-2 py-1.5 font-sans focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                {libCountries.length > 0 && (
                  <select
                    value={libCountry}
                    onChange={e => handleLibCountry(e.target.value)}
                    className="text-xs bg-background border border-border rounded px-2 py-1.5 font-sans focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="all">All Countries</option>
                    {libCountries.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}
                <span className="text-[10px] text-muted-foreground font-sans whitespace-nowrap">
                  {filteredDefs.length} of {defs.length} awards
                  {libTotalPages > 1 && ` — page ${libPage}/${libTotalPages}`}
                </span>
              </div>
              {filteredDefs.length === 0 && (
                <div className="text-center py-6 border border-dashed border-border rounded-lg text-muted-foreground text-xs font-sans">
                  No awards match your search
                </div>
              )}
              <div className="space-y-2">
              {pagedDefs.map((d: any) => {
                const isIssuing = issuingDefId === d.id;
                const imgUrl = d.image_url ?? d.image_path ?? null;
                return (
                  <div key={d.id} className="bg-card border border-border rounded-lg overflow-hidden">
                    <div className="flex items-center gap-4 p-4">
                      <div className="shrink-0 w-14 flex items-center justify-center">
                        {imgUrl
                          ? <img src={imgUrl} alt={d.name} className="h-7 w-20 object-cover rounded-sm" />
                          : <Medal className="w-6 h-6 text-muted-foreground opacity-40" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-display font-bold uppercase tracking-wider text-sm">{d.name}</p>
                        {(d.source_country || d.source_branch) && (
                          <p className="text-[10px] font-sans text-muted-foreground mt-0.5">
                            {d.source_country}{d.source_branch && ` — ${d.source_branch}`}
                          </p>
                        )}
                        {d.description && (
                          <p className="text-xs text-muted-foreground font-sans mt-0.5 line-clamp-1">{d.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => { setIssuingDefId(isIssuing ? null : d.id); setIssueRosterId(""); setIssueCitation(""); }}
                          className="text-xs font-display font-bold uppercase tracking-widest px-3 py-1.5 bg-accent/10 text-accent border border-accent/30 rounded hover:bg-accent/20 transition-colors">
                          Issue
                        </button>
                        <button onClick={() => deleteDef(d.id)}
                          className="text-xs text-red-400 hover:text-red-300 font-display uppercase tracking-widest px-2 py-1.5 rounded hover:bg-red-500/10 transition-colors">
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Issue panel */}
                    {isIssuing && (
                      <div className="border-t border-border p-4 bg-secondary/20 flex flex-wrap gap-3 items-end">
                        <div className="flex-1 min-w-[180px]">
                          <label className="block text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">Recipient</label>
                          <select value={issueRosterId} onChange={e => setIssueRosterId(e.target.value)}
                            className="w-full bg-background border border-border rounded px-2 py-1.5 text-sm font-sans text-foreground">
                            <option value="">Select member...</option>
                            {roster.filter(r => r.status !== "Kicked" && r.status !== "Resigned").map((r: any) => (
                              <option key={r.id} value={r.id}>{r.rank_name ? `${r.rank_name} ` : ""}{r.callsign}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex-1 min-w-[180px]">
                          <label className="block text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">Citation (optional)</label>
                          <input value={issueCitation} onChange={e => setIssueCitation(e.target.value)}
                            placeholder="For gallantry in action..."
                            className="w-full bg-background border border-border rounded px-2 py-1.5 text-sm font-sans text-foreground" />
                        </div>
                        <button onClick={() => issueAward(d.id)} disabled={issuing || !issueRosterId}
                          className="px-4 py-1.5 bg-accent text-accent-foreground rounded font-display text-xs uppercase tracking-widest disabled:opacity-50 hover:bg-accent/90 transition-colors flex items-center gap-1.5">
                          {issuing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Award className="w-3.5 h-3.5" />}
                          Confirm Issue
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
              {/* Pagination */}
              {libTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
                  <button
                    onClick={() => setLibPage(p => Math.max(1, p - 1))}
                    disabled={libPage === 1}
                    className="px-3 py-1 text-xs font-display uppercase tracking-widest border border-border rounded hover:bg-secondary disabled:opacity-40 transition-colors">
                    ← Prev
                  </button>
                  <div className="flex gap-1 flex-wrap justify-center">
                    {Array.from({ length: libTotalPages }, (_, i) => i + 1).map(pg => (
                      <button key={pg} onClick={() => setLibPage(pg)}
                        className={`w-7 h-7 text-xs font-display rounded transition-colors ${pg === libPage ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary text-muted-foreground"}`}>
                        {pg}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setLibPage(p => Math.min(libTotalPages, p + 1))}
                    disabled={libPage === libTotalPages}
                    className="px-3 py-1 text-xs font-display uppercase tracking-widest border border-border rounded hover:bg-secondary disabled:opacity-40 transition-colors">
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Add award button */}
          {!showCreate && (
            <button onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-3 border border-dashed border-border rounded-lg text-xs font-display uppercase tracking-widest text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors w-full justify-center">
              <Plus className="w-3.5 h-3.5" /> Add Award to Library
            </button>
          )}

          {/* ── RIBBON PICKER ── */}
          {showCreate && (
            <div className="border border-primary/30 rounded-lg bg-card overflow-hidden">
              <div className="bg-primary/10 border-b border-primary/20 px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="font-display font-black text-xs uppercase tracking-widest text-primary">Add Award from Ribbon Library</p>
                  <p className="text-[10px] font-sans text-muted-foreground mt-0.5">564 curated military ribbons — US, UK, Australia, Canada, NATO & 23 nations</p>
                </div>
                <button onClick={resetForm} className="text-muted-foreground hover:text-foreground transition-colors ml-4 shrink-0">
                  <XCircle className="w-4 h-4" />
                </button>
              </div>

              <div className="p-5 space-y-4">

                {/* Filters */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">Country</label>
                    <select value={pickerCountry}
                      onChange={e => { const v = e.target.value; setPickerCountry(v); const brs = RIBBON_BRANCHES_BY_COUNTRY[v] ?? []; setPickerBranch(brs.length === 1 ? brs[0] : ""); setSelectedTemplate(null); setPickerPage(0); }}
                      className="w-full bg-background border border-border rounded px-2 py-1.5 text-sm font-sans text-foreground">
                      <option value="">All countries</option>
                      {RIBBON_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">Branch / Force</label>
                    <select value={pickerBranch}
                      onChange={e => { setPickerBranch(e.target.value); setSelectedTemplate(null); setPickerPage(0); }}
                      disabled={!pickerCountry}
                      className="w-full bg-background border border-border rounded px-2 py-1.5 text-sm font-sans text-foreground disabled:opacity-40">
                      <option value="">All branches</option>
                      {availableBranches.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">Search</label>
                    <input value={pickerSearch}
                      onChange={e => { setPickerSearch(e.target.value); setSelectedTemplate(null); setPickerPage(0); }}
                      placeholder="e.g. Bronze Star, Victoria Cross..."
                      className="w-full bg-background border border-border rounded px-2 py-1.5 text-sm font-sans text-foreground" />
                  </div>
                </div>

                {/* Selected ribbon preview */}
                {selectedTemplate && (() => {
                  const modifiers = getRibbonModifiers(selectedTemplate.url);
                  const { activeImg, activeOverlay } = (() => {
                    let img = selectedTemplate.url;
                    let overlay: string | undefined;
                    for (const mod of modifiers) {
                      if (mod.type === "select" && mod.options) {
                        const val = pickerMods[mod.name] ?? mod.options[0].value;
                        const opt = mod.options.find(o => o.value === val);
                        if (opt?.url) img = opt.url;
                        if (opt?.overlayUrl) overlay = opt.overlayUrl;
                      }
                    }
                    return { activeImg: img, activeOverlay: overlay };
                  })();
                  return (
                    <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg space-y-3">
                      <div className="flex items-center gap-3">
                        {activeOverlay ? (
                          <div style={{ position: "relative", width: 96, height: 32, flexShrink: 0, borderRadius: 2, overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)" }}>
                            <img src={activeImg} alt={selectedTemplate.name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "fill" }} onError={(e: any) => { (e.target as HTMLImageElement).style.opacity = "0.2"; }} />
                            <img src={activeOverlay} alt="device" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
                          </div>
                        ) : (
                          <img src={activeImg} alt={selectedTemplate.name}
                            className="h-8 w-24 object-cover rounded-sm border border-white/10"
                            onError={(e: any) => { (e.target as HTMLImageElement).style.opacity = "0.2"; }} />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-display font-bold text-sm uppercase tracking-wider text-primary truncate">
                            {selectedTemplate.name}{pickerMods["class"] || pickerMods["branch"] ? ` — ${pickerMods["class"] || pickerMods["branch"]}` : ""}
                          </p>
                          <p className="text-[10px] text-muted-foreground font-sans">{selectedTemplate.country} — {selectedTemplate.branch}</p>
                        </div>
                        <button onClick={() => { setSelectedTemplate(null); setPickerMods({}); }} className="text-muted-foreground hover:text-foreground shrink-0">
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                      {modifiers.length > 0 && (
                        <div className="border-t border-primary/20 pt-2 space-y-2">
                          <p className="text-[9px] font-display font-bold uppercase tracking-widest text-primary">Select Grade / Variant</p>
                          {modifiers.map((mod) => (
                            <div key={mod.name}>
                              {mod.type === "select" && mod.options && (
                                <div className="flex items-center gap-2">
                                  <label className="text-[9px] text-muted-foreground font-sans uppercase tracking-wider w-20 shrink-0">{mod.label}</label>
                                  <select
                                    value={pickerMods[mod.name] ?? mod.options[0].value}
                                    onChange={e => setPickerMods(prev => ({ ...prev, [mod.name]: e.target.value }))}
                                    className="flex-1 text-[11px] bg-background border border-border rounded px-2 py-1 font-sans focus:outline-none focus:ring-1 focus:ring-primary/40">
                                    {mod.options.map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Ribbon grid */}
                {!selectedTemplate && (
                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="bg-secondary/40 border-b border-border px-3 py-2 flex items-center justify-between gap-2">
                      <p className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">
                        {filteredTemplates.length} ribbons {pickerSearch || pickerCountry ? "matching filters" : "available"}
                        {totalPages > 1 && <span className="ml-1 text-primary">— page {pickerPage + 1}/{totalPages}</span>}
                      </p>
                      <p className="text-[9px] font-sans text-muted-foreground/60 ml-auto">
                        <span className="inline-block w-2 h-2 rounded-full bg-primary/70 mr-1 align-middle" />
                        = has grade variants
                      </p>
                      {totalPages > 1 && (
                        <div className="flex items-center gap-1">
                          <button
                            disabled={pickerPage === 0}
                            onClick={() => setPickerPage(p => Math.max(0, p - 1))}
                            className="w-6 h-6 flex items-center justify-center rounded border border-border text-muted-foreground hover:text-foreground hover:border-primary/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs font-bold">
                            ‹
                          </button>
                          {Array.from({ length: totalPages }, (_, i) => (
                            <button key={i} onClick={() => setPickerPage(i)}
                              className={`w-6 h-6 flex items-center justify-center rounded border text-[9px] font-display font-bold transition-colors ${pickerPage === i ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"}`}>
                              {i + 1}
                            </button>
                          ))}
                          <button
                            disabled={pickerPage >= totalPages - 1}
                            onClick={() => setPickerPage(p => Math.min(totalPages - 1, p + 1))}
                            className="w-6 h-6 flex items-center justify-center rounded border border-border text-muted-foreground hover:text-foreground hover:border-primary/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs font-bold">
                            ›
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto p-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {pagedTemplates.map((r, i) => {
                        const tileMods = getRibbonModifiers(r.url);
                        return (
                          <button key={pickerPage * RIBBONS_PER_PAGE + i} onClick={() => { setSelectedTemplate(r); setPickerMods({}); }}
                            title={`${r.name} (${r.country})${tileMods.length > 0 ? ' — has grade variants' : ''}`}
                            className="relative flex flex-col items-center gap-1 p-1.5 rounded border border-transparent hover:border-primary/50 hover:bg-primary/5 transition-all group">
                            {tileMods.length > 0 && (
                              <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-primary/70" title="Has grade variants" />
                            )}
                            <img src={r.url} alt={r.name}
                              className="h-5 w-16 object-cover rounded-sm"
                              onError={(e: any) => { (e.target as HTMLImageElement).style.opacity = "0.15"; }} />
                            <p className="text-[8px] font-sans text-muted-foreground text-center leading-tight line-clamp-2 group-hover:text-foreground w-full">
                              {r.name.split(" (")[0]}
                            </p>
                          </button>
                        );
                      })}
                      {filteredTemplates.length === 0 && (
                        <div className="col-span-6 text-center py-8 text-xs text-muted-foreground font-sans">
                          No ribbons match — try clearing filters
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Optional description */}
                <div>
                  <label className="block text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1">Award Criteria / Description (optional)</label>
                  <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2}
                    placeholder="Awarded for acts of gallantry..."
                    className="w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans text-foreground resize-none" />
                </div>

                <div className="flex gap-3 pt-1">
                  <button onClick={resetForm}
                    className="px-4 py-2 border border-border rounded font-display text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">
                    Cancel
                  </button>
                  <button onClick={createDef} disabled={creating || !selectedTemplate}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded font-display text-xs uppercase tracking-widest hover:bg-primary/90 disabled:opacity-50 transition-colors">
                    {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    Add to Award Library
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ISSUED AWARDS ── */}
      {subView === "issued" && (
        <div className="space-y-3">
          {issued.length === 0 ? (
            <div className="text-center py-14 border border-dashed border-border rounded-lg text-muted-foreground">
              <Award className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-display text-sm uppercase tracking-widest">No awards issued yet</p>
            </div>
          ) : (
            issued.map((a: any) => (
              <div key={a.id} className="flex items-center gap-4 p-4 bg-card border border-border rounded-lg">
                <div className="shrink-0 w-14 flex justify-center">
                  {a.award_image_url
                    ? <img src={a.award_image_url} alt={a.award_name} className="h-7 w-20 object-cover rounded-sm" />
                    : <Medal className="w-6 h-6 text-muted-foreground opacity-40" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold uppercase tracking-wider text-sm">{a.award_name}</p>
                  <p className="text-xs text-muted-foreground font-sans">
                    To: <span className="text-foreground font-medium">{a.recipient_callsign}</span>
                    {a.reason && <> · <span className="italic">{a.reason}</span></>}
                  </p>
                </div>
                <button onClick={() => revokeAward(a.id)}
                  className="text-xs text-red-400 hover:text-red-300 font-display uppercase tracking-widest px-2 py-1.5 rounded hover:bg-red-500/10 transition-colors shrink-0">
                  Revoke
                </button>
              </div>
            ))
          )}
        </div>
      )}

    </div>
  );
}


function StreamTab({ group, onUpdated, showMsg }: any) {
  const [streamUrl, setStreamUrl] = useState(group.stream_url ?? "");
  const [isLive, setIsLive] = useState(group.is_live ?? false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch(`/milsimGroups?path=${group.id}/stream`, {
        method: "PATCH",
        body: JSON.stringify({ streamUrl: streamUrl || null, isLive }),
      });
      onUpdated({ ...group, stream_url: streamUrl || null, is_live: isLive });
      showMsg(true, isLive ? "Stream is now LIVE!" : "Stream settings saved.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setSaving(false); }
  };

  const getEmbedUrl = (url: string): string | null => {
    try {
      const u = new URL(url);
      if (u.hostname.includes("youtube.com") || u.hostname.includes("youtu.be")) {
        const vid = u.searchParams.get("v") || u.pathname.split("/").pop();
        return vid ? `https://www.youtube.com/embed/${vid}?autoplay=0` : null;
      }
      if (u.hostname.includes("twitch.tv")) {
        const channel = u.pathname.split("/").filter(Boolean)[0];
        return channel ? `https://player.twitch.tv/?channel=${channel}&parent=${window.location.hostname}` : null;
      }
    } catch { /* invalid url */ }
    return null;
  };

  const embedUrl = streamUrl ? getEmbedUrl(streamUrl) : null;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-card border border-border rounded-lg p-6 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <Radio className="w-5 h-5 text-primary" />
          <h3 className="font-display font-bold uppercase tracking-wider">Broadcast Control</h3>
        </div>

        <div>
          <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Stream URL</label>
          <input
            value={streamUrl}
            onChange={e => setStreamUrl(e.target.value)}
            className="mf-input w-full"
            placeholder="https://twitch.tv/yourchannel  or  https://youtube.com/watch?v=..."
          />
          <p className="text-xs text-muted-foreground mt-1">Supports YouTube and Twitch. Visible to anyone viewing your unit page.</p>
        </div>

        <div className="flex items-center justify-between bg-secondary/40 border border-border rounded-lg px-4 py-3">
          <div className="flex items-center gap-3">
            {isLive ? <Wifi className="w-5 h-5 text-red-400" /> : <WifiOff className="w-5 h-5 text-muted-foreground" />}
            <div>
              <p className="font-display font-bold uppercase tracking-wider text-sm">{isLive ? "Broadcasting LIVE" : "Off Air"}</p>
              <p className="text-xs text-muted-foreground">{isLive ? "Visitors can see your live stream" : "Toggle to go live"}</p>
            </div>
          </div>
          <button
            onClick={() => setIsLive(!isLive)}
            className={`relative w-12 h-6 rounded-full transition-colors ${isLive ? "bg-red-500" : "bg-secondary border border-border"}`}
          >
            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${isLive ? "left-7" : "left-1"}`} />
          </button>
        </div>

        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-sm px-6 py-2.5 rounded clip-angled-sm transition-all disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Stream Settings
        </button>
      </div>

      {embedUrl && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary/30 flex items-center gap-2">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="font-display font-bold uppercase tracking-wider text-xs">Preview</span>
          </div>
          <div className="aspect-video w-full">
            <iframe src={embedUrl} className="w-full h-full" allowFullScreen title="Stream Preview" />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Onboarding Tab (Selection Criteria + App Questions + Pipeline) ───────────
function OnboardingTab({ group, onUpdated, showMsg, globalIsPro }: any) {
  type OBSub = "criteria" | "questions" | "pipeline" | "opsearch" | "recruit" | "analytics" | "notifrules";
  const [sub, setSub] = useState<OBSub>("criteria");

  const SUBS: { id: OBSub; label: string; icon: React.ReactNode }[] = [
    { id: "criteria",  label: "Selection Criteria", icon: <Target className="w-3.5 h-3.5" /> },
    { id: "questions", label: "App Questions",       icon: <FileText className="w-3.5 h-3.5" /> },
    { id: "pipeline",  label: "Pipeline",            icon: <UserCheck className="w-3.5 h-3.5" /> },
    { id: "recruit",   label: "Recruitment",         icon: <Megaphone className="w-3.5 h-3.5" />, pro: true },
    { id: "opsearch",  label: "Operator Search",     icon: <Search className="w-3.5 h-3.5" />, pro: true },
    { id: "analytics", label: "Recruit Analytics",   icon: <BarChart3 className="w-3.5 h-3.5" />, pro: true },
    { id: "notifrules",label: "Alert Rules",          icon: <Bell className="w-3.5 h-3.5" />, pro: true },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {SUBS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSub(s.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded font-display font-bold uppercase tracking-widest text-xs border transition-colors ${
              sub === s.id
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-secondary border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
            }`}
          >
            {s.icon}
            {s.label}
            {(s as any).pro && <Crown className="ml-1 w-3 h-3 text-yellow-400 shrink-0" />}
          </button>
        ))}
      </div>

      {sub === "criteria"  && <SelectionCriteriaTab  group={group} onUpdated={onUpdated} showMsg={showMsg} />}
      {sub === "questions" && <QuestionsTab          group={group} onUpdated={onUpdated} showMsg={showMsg} />}
      {sub === "pipeline"  && <RecruitPipelineTab    group={group} showMsg={showMsg} />}
      {sub === "opsearch"  && <OperatorSearchTab         group={group} globalIsPro={globalIsPro} />}
      {sub === "recruit"   && <RecruitmentSchedulerTab group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
      {sub === "analytics" && <RecruitmentAnalyticsTab group={group} globalIsPro={globalIsPro} />}
      {sub === "notifrules"&& <CustomNotificationRulesTab group={group} globalIsPro={globalIsPro} showMsg={showMsg} />}
    </div>
  );
}

// ─── Selection Criteria Tab ───────────────────────────────────────────────────
const APP_POLICY_URL = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions/milsimApplications";

function SelectionCriteriaTab({ group, onUpdated, showMsg }: any) {
  const [criteria, setCriteria] = useState<string>(group.selection_criteria ?? "");
  const [saving, setSaving] = useState(false);

  // Application Policy state
  const [policy, setPolicy] = useState<any>(group.app_policy ?? {});
  const [policyLoaded, setPolicyLoaded] = useState(false);
  const [policySaving, setPolicySaving] = useState(false);
  const getToken = () => sessionStorage.getItem("tag_auth_token") ?? localStorage.getItem("tag_auth_token") ?? "";

  useEffect(() => {
    fetch(`${APP_POLICY_URL}?path=${encodeURIComponent(`/${group.id}/policy`)}`)
      .then(r => r.json())
      .then(p => { setPolicy(p ?? {}); setPolicyLoaded(true); })
      .catch(() => setPolicyLoaded(true));
  }, [group.id]);

  async function handleSavePolicy() {
    setPolicySaving(true);
    try {
      const res = await fetch(`${APP_POLICY_URL}?path=${encodeURIComponent(`/${group.id}/policy`)}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify(policy),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed");
      setPolicy(json.policy);
      showMsg(true, "Application policy saved");
    } catch (e: any) {
      showMsg(false, e.message ?? "Failed to save policy");
    } finally {
      setPolicySaving(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const updated = await apiFetch(`/milsimGroups?path=${group.id}/info`, {
        method: "PATCH",
        body: JSON.stringify({ selection_criteria: criteria }),
      });
      onUpdated(updated);
      showMsg(true, "Selection criteria saved");
    } catch {
      showMsg(false, "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display font-black text-lg uppercase tracking-wider text-foreground mb-1">Selection Criteria</h2>
            <p className="text-xs text-muted-foreground font-sans">
              Define the standards, requirements, and expectations applicants must meet to join your unit.
              This is displayed publicly on your unit's profile page.
            </p>
          </div>
          <div className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-widest text-primary border border-primary/30 bg-primary/10 px-2 py-1 rounded">
            <Globe className="w-3 h-3" /> Public
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Criteria (Markdown supported)</p>
          <textarea
            value={criteria}
            onChange={(e) => setCriteria(e.target.value)}
            rows={16}
            className="mf-input w-full resize-y font-mono text-sm"
            placeholder={`## Minimum Requirements
- Age 18+
- Mic required
- Available for scheduled ops

## What We Look For
- Teamwork and communication
- Ability to take orders under pressure
- Prior milsim experience preferred

## Selection Process
1. Submit application
2. Review by command
3. Trial op participation
4. Final acceptance or rejection`}
          />
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground font-sans">
            {criteria.length > 0 ? `${criteria.length} characters` : "No criteria set yet — applicants will see an empty page."}
          </p>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground font-display font-black uppercase tracking-widest text-xs px-5 py-2.5 rounded transition-all"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
            Save Criteria
          </button>
        </div>
      </div>

      {/* Preview */}
      {criteria.trim() && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-3">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Target className="w-3 h-3" /> Public Preview
          </p>
          <div className="prose prose-sm prose-invert max-w-none font-sans text-muted-foreground leading-relaxed whitespace-pre-wrap">
            {criteria}
          </div>
        </div>
      )}

      {/* ── Application Policy ── */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 bg-primary/10 border border-primary/30 rounded flex items-center justify-center shrink-0">
            <Shield className="w-3.5 h-3.5 text-primary" />
          </div>
          <div>
            <h2 className="font-display font-black text-base uppercase tracking-wider text-foreground">Application Policy</h2>
            <p className="text-xs text-muted-foreground font-sans">Set admission rules, score thresholds, and rejection cooldowns.</p>
          </div>
          <span className="ml-auto text-[9px] font-display font-bold uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 rounded flex items-center gap-1">
            <Crown className="w-2.5 h-2.5" /> Private
          </span>
        </div>

        {!policyLoaded ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-5">

            {/* Score threshold */}
            <div className="bg-secondary/30 border border-border rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Star className="w-3.5 h-3.5 text-yellow-400" />
                <span className="font-display font-bold uppercase tracking-widest text-xs text-foreground">Reputation Score Gate</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">Minimum Reputation Score (0–10)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number" min={0} max={10} step={0.1}
                      value={policy.min_reputation_score ?? ""}
                      onChange={e => setPolicy((p: any) => ({ ...p, min_reputation_score: e.target.value === "" ? null : parseFloat(e.target.value) }))}
                      placeholder="No minimum"
                      className="bg-secondary border border-border rounded px-3 py-1.5 text-sm font-sans text-foreground focus:outline-none focus:border-primary/50 w-32"
                    />
                    {policy.min_reputation_score !== null && policy.min_reputation_score !== undefined && (
                      <button onClick={() => setPolicy((p: any) => ({ ...p, min_reputation_score: null }))}
                        className="text-xs text-muted-foreground hover:text-destructive font-sans">Clear</button>
                    )}
                  </div>
                  <p className="text-[9px] font-sans text-muted-foreground">Leave blank to allow all applicants regardless of reputation.</p>
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <div
                      onClick={() => setPolicy((p: any) => ({ ...p, auto_reject_below_threshold: !p.auto_reject_below_threshold }))}
                      className={`w-9 h-5 rounded-full border transition-colors flex items-center px-0.5 cursor-pointer shrink-0 ${
                        policy.auto_reject_below_threshold ? "bg-red-500 border-red-400" : "bg-secondary border-border"
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white transition-transform ${policy.auto_reject_below_threshold ? "translate-x-4" : ""}`} />
                    </div>
                    <div>
                      <p className="text-xs font-display font-bold uppercase tracking-wider text-foreground">Auto-Reject Below Threshold</p>
                      <p className="text-[9px] font-sans text-muted-foreground">Applications that don't meet the minimum score are rejected instantly.</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Rejection cooldown */}
            <div className="bg-secondary/30 border border-border rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-blue-400" />
                <span className="font-display font-bold uppercase tracking-widest text-xs text-foreground">Rejection Cooldown</span>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">Reapplication Lockout Period (days)</label>
                <div className="flex items-center gap-3 flex-wrap">
                  {[0, 7, 14, 30, 60, 90, 180].map(d => (
                    <button key={d}
                      onClick={() => setPolicy((p: any) => ({ ...p, rejection_cooldown_days: d }))}
                      className={`px-3 py-1.5 rounded text-xs font-display font-bold uppercase tracking-widest border transition-colors ${
                        policy.rejection_cooldown_days === d
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-secondary border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
                      }`}
                    >{d === 0 ? "No Lockout" : `${d}d`}</button>
                  ))}
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-sans text-muted-foreground">Custom:</span>
                    <input
                      type="number" min={0} max={365}
                      value={[0,7,14,30,60,90,180].includes(policy.rejection_cooldown_days ?? 0) ? "" : (policy.rejection_cooldown_days ?? "")}
                      onChange={e => setPolicy((p: any) => ({ ...p, rejection_cooldown_days: e.target.value === "" ? 0 : parseInt(e.target.value) }))}
                      placeholder="days"
                      className="bg-secondary border border-border rounded px-2 py-1.5 text-sm font-sans text-foreground focus:outline-none focus:border-primary/50 w-20"
                    />
                  </div>
                </div>
                <p className="text-[9px] font-sans text-muted-foreground mt-1">
                  {(policy.rejection_cooldown_days ?? 0) === 0
                    ? "Rejected applicants can reapply immediately."
                    : `Rejected applicants must wait ${policy.rejection_cooldown_days} days before reapplying.`}
                </p>
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-secondary/30 border border-border rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2">
                <Bell className="w-3.5 h-3.5 text-green-400" />
                <span className="font-display font-bold uppercase tracking-widest text-xs text-foreground">Commander Notifications</span>
              </div>
              <div className="space-y-3">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <div
                    onClick={() => setPolicy((p: any) => ({ ...p, notify_owner_on_cooldown_expire: !p.notify_owner_on_cooldown_expire }))}
                    className={`w-9 h-5 rounded-full border transition-colors flex items-center px-0.5 cursor-pointer shrink-0 mt-0.5 ${
                      policy.notify_owner_on_cooldown_expire ? "bg-green-500 border-green-400" : "bg-secondary border-border"
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform ${policy.notify_owner_on_cooldown_expire ? "translate-x-4" : ""}`} />
                  </div>
                  <div>
                    <p className="text-xs font-display font-bold uppercase tracking-wider text-foreground">Notify when cooldown expires</p>
                    <p className="text-[9px] font-sans text-muted-foreground">You'll be alerted when a previously rejected operator becomes eligible to reapply.</p>
                  </div>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <div
                    onClick={() => setPolicy((p: any) => ({ ...p, notify_owner_on_score_threshold: !p.notify_owner_on_score_threshold }))}
                    className={`w-9 h-5 rounded-full border transition-colors flex items-center px-0.5 cursor-pointer shrink-0 mt-0.5 ${
                      policy.notify_owner_on_score_threshold ? "bg-green-500 border-green-400" : "bg-secondary border-border"
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white transition-transform ${policy.notify_owner_on_score_threshold ? "translate-x-4" : ""}`} />
                  </div>
                  <div>
                    <p className="text-xs font-display font-bold uppercase tracking-wider text-foreground">Notify when score crosses threshold</p>
                    <p className="text-[9px] font-sans text-muted-foreground">You'll be alerted when a rejected operator's reputation score rises above your minimum. Requires a minimum score to be set.</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Save button */}
            <div className="flex justify-end pt-2 border-t border-border">
              <button
                onClick={handleSavePolicy}
                disabled={policySaving}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground font-display font-black uppercase tracking-widest text-xs px-5 py-2.5 rounded transition-all"
              >
                {policySaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
                Save Policy
              </button>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}


function QuestionsTab({ group, onUpdated, showMsg }: any) {
  const [questions, setQuestions] = useState<AppQuestion[]>(group.questions ?? []);
  const [question, setQuestion] = useState(""); const [required, setRequired] = useState(true);
  const [adding, setAdding] = useState(false);

  const add = async () => {
    if (!question.trim()) return;
    setAdding(true);
    try {
      const q = await apiFetch<AppQuestion>(`/milsimGroups?path=${group.id}/questions`, { method: "POST", body: JSON.stringify({ question, sortOrder: questions.length, required }) });
      const updated = [...questions, q]; setQuestions(updated); onUpdated({ ...group, questions: updated });
      setQuestion(""); setRequired(true); showMsg(true, "Question added.");
    } catch (e: any) { showMsg(false, e.message); }
    finally { setAdding(false); }
  };

  const remove = async (id: string) => {
    try {
      await apiFetch(`/milsimGroups?path=${group.id}/questions/${id}`, { method: "DELETE" });
      const updated = questions.filter((q) => q.id !== id); setQuestions(updated); onUpdated({ ...group, questions: updated });
    } catch (e: any) { showMsg(false, e.message); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-muted-foreground font-sans">These questions are shown publicly on your group profile so prospective applicants know what to prepare.</p>
      <div className="space-y-2">
        {questions.length === 0 && <p className="text-muted-foreground font-sans text-sm">No questions added yet.</p>}
        {questions.map((q, i) => (
          <div key={q.id} className="flex items-start justify-between gap-3 bg-card border border-border rounded-lg px-4 py-3">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 shrink-0 rounded bg-primary/10 border border-primary/30 flex items-center justify-center font-display font-bold text-xs text-primary mt-0.5">{i + 1}</span>
              <div>
                <p className="font-sans text-sm text-foreground">{q.question}</p>
                {q.required && <span className="text-[10px] font-display font-bold uppercase text-accent">Required</span>}
              </div>
            </div>
            <button onClick={() => remove(q.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1 shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="bg-card border border-border rounded-lg p-5 space-y-3">
        <h3 className="font-display font-bold uppercase tracking-wider text-xs text-muted-foreground">Add Question</h3>
        <input value={question} onChange={e => setQuestion(e.target.value)} className="mf-input" placeholder="e.g. How long have you played Squad?" />
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={required} onChange={e => setRequired(e.target.checked)} className="accent-primary w-4 h-4" />
          Required question
        </label>
        <button onClick={add} disabled={adding || !question.trim()}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded clip-angled-sm transition-all disabled:opacity-50">
          {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add Question
        </button>
      </div>
    </div>
  );
}

// ─── Commendation Wall ────────────────────────────────────────────────────────
function CommendationsTab({ group }: any) {
  const [awards, setAwards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiFetch<any[]>(`/milsimGroups?path=${group.id}/awards`)
      .then(d => setAwards(Array.isArray(d) ? d : [])).catch(() => setAwards([])).finally(() => setLoading(false));
  }, [group.id]);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  return (
    <div className="max-w-3xl space-y-4">
      <p className="text-xs text-muted-foreground font-sans">A public record of all commendations bestowed within this unit.</p>
      {awards.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg text-muted-foreground">
          <Megaphone className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="font-display text-sm uppercase tracking-widest">No commendations on record</p>
        </div>
      ) : (
        <div className="space-y-3">
          {awards.map((a: any) => (
            <div key={a.id} className="flex items-center gap-4 bg-card border border-border rounded-lg px-5 py-4">
              <div className="w-11 h-11 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center text-accent shrink-0">
                {a.award_image_url ? (
                  <img src={a.award_image_url} alt={a.award_name} className="w-8 h-8 object-contain" />
                ) : (
                  <Medal className="w-5 h-5" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="font-display font-bold uppercase tracking-wider text-sm text-foreground">{a.award_name}</p>
                  <span className="text-xs text-muted-foreground">{a.created_date ? formatDistanceToNow(new Date(a.created_date), { addSuffix: true }) : ""}</span>
                </div>
                {a.recipient_callsign && <p className="text-xs text-primary font-display font-bold mt-0.5">Awarded to {a.recipient_callsign}</p>}
                {a.award_description && <p className="text-xs text-muted-foreground font-sans mt-1">{a.award_description}</p>}
                {a.reason && <p className="text-xs text-muted-foreground font-sans mt-0.5 italic">"{a.reason}"</p>}
                {a.awarded_by && <p className="text-xs text-muted-foreground mt-0.5">By {a.awarded_by}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Qualifications ───────────────────────────────────────────────────────────
function QualsTab({ group, showMsg }: any) {
  const [quals, setQuals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState(""); const [desc, setDesc] = useState(""); const [adding, setAdding] = useState(false);
  const [grantModal, setGrantModal] = useState<any | null>(null); const [grantRosterId, setGrantRosterId] = useState("");
  const load = () => { apiFetch<any[]>(`/milsimGroupsB?path=${group.id}/qualifications`).then(r => setQuals(Array.isArray(r) ? r : [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [group.id]);
  const add = async () => {
    if (!name.trim()) return; setAdding(true);
    try { await apiFetch(`/milsimGroupsB?path=${group.id}/qualifications`, { method: "POST", body: JSON.stringify({ name, description: desc || undefined }) }); setName(""); setDesc(""); showMsg(true, "Qualification added."); load(); }
    catch (e: any) { showMsg(false, e.message); } finally { setAdding(false); }
  };
  const remove = async (qid: number) => {
    try { await apiFetch(`/milsimGroupsB?path=${group.id}/qualifications/${qid}`, { method: "DELETE" }); showMsg(true, "Removed."); load(); }
    catch (e: any) { showMsg(false, e.message); }
  };
  const grant = async (qid: number) => {
    if (!grantRosterId) return;
    try { await apiFetch(`/milsimGroupsB?path=${group.id}/qualifications/${qid}/grant`, { method: "POST", body: JSON.stringify({ rosterEntryId: parseInt(grantRosterId) }) }); showMsg(true, "Granted."); setGrantModal(null); setGrantRosterId(""); load(); }
    catch (e: any) { showMsg(false, e.message); }
  };
  const revokeGrant = async (qid: number, grantId: number) => {
    try { await apiFetch(`/milsimGroupsB?path=${group.id}/qualifications/${qid}/grant/${grantId}`, { method: "DELETE" }); showMsg(true, "Revoked."); load(); }
    catch (e: any) { showMsg(false, e.message); }
  };
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  return (
    <div className="max-w-3xl space-y-6">
      {quals.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-border rounded-lg text-muted-foreground">
          <GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="font-display text-sm uppercase tracking-widest">No qualifications defined</p>
        </div>
      ) : (
        <div className="space-y-3">
          {quals.map((q: any) => (
            <div key={q.id} className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-5 py-3">
                <div className="flex items-center gap-3"><GraduationCap className="w-4 h-4 text-primary shrink-0" /><div><p className="font-display font-bold uppercase tracking-wider text-sm text-foreground">{q.name}</p>{q.description && <p className="text-xs text-muted-foreground">{q.description}</p>}<p className="text-xs text-muted-foreground">{q.grants?.length ?? 0} qualified</p></div></div>
                <div className="flex gap-2 shrink-0"><button onClick={() => setGrantModal(q)} className="text-xs font-display font-bold uppercase tracking-widest px-3 py-1.5 bg-primary/10 text-primary border border-primary/30 rounded hover:bg-primary/20 transition-colors">Grant</button><button onClick={() => remove(q.id)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button></div>
              </div>
              {q.grants?.length > 0 && (
                <div className="border-t border-border px-5 py-3 bg-secondary/20">
                  <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Qualified Personnel</p>
                  <div className="flex flex-wrap gap-2">
                    {q.grants.map((g: any) => (<div key={g.id} className="flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded border border-primary/20">{g.callsign}<button onClick={() => revokeGrant(q.id, g.id)} className="ml-1 hover:text-destructive transition-colors"><X className="w-3 h-3" /></button></div>))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {grantModal && (
        <div className="bg-card border border-primary/30 rounded-lg p-5 space-y-3">
          <h3 className="font-display font-bold uppercase tracking-wider text-sm">Grant: {grantModal.name}</h3>
          <select value={grantRosterId} onChange={e => setGrantRosterId(e.target.value)} className="mf-input"><option value="">Select member...</option>{(group.roster ?? []).map((r: RosterEntry) => <option key={r.id} value={r.id}>{r.callsign}</option>)}</select>
          <div className="flex gap-2"><button onClick={() => grant(grantModal.id)} disabled={!grantRosterId} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-5 py-2 rounded transition-all disabled:opacity-50"><Check className="w-3.5 h-3.5" /> Grant</button><button onClick={() => { setGrantModal(null); setGrantRosterId(""); }} className="px-4 py-2 border border-border text-muted-foreground rounded text-xs font-display uppercase hover:text-foreground">Cancel</button></div>
        </div>
      )}
      <div className="bg-card border border-border rounded-lg p-5 space-y-3">
        <h3 className="font-display font-bold uppercase tracking-wider text-xs text-muted-foreground">Add Qualification Type</h3>
        <input value={name} onChange={e => setName(e.target.value)} className="mf-input" placeholder="CQB Certified, Medic Qualified, JTAC..." />
        <input value={desc} onChange={e => setDesc(e.target.value)} className="mf-input" placeholder="Description (optional)" />
        <button onClick={add} disabled={adding || !name.trim()} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded clip-angled-sm transition-all disabled:opacity-50">{adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add</button>
      </div>
    </div>
  );
}


// ─── Operations Tab (Live Ops + AARs + Briefings merged) ─────────────────────

// ─── Recruitment Scheduler Tab ─────────────────────────────────────────────────
function RecruitmentSchedulerTab({ group, showMsg, globalIsPro }: any) {
  const { toast } = useToast();
  const [sched, setSched] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingVid, setUploadingVid] = useState(false);
  const [preview, setPreview] = useState(false);
  const imgRef = React.useRef<HTMLInputElement>(null);
  const vidRef = React.useRef<HTMLInputElement>(null);

  const GAME_TAGS = ["Arma 3", "Arma Reforger", "Squad", "Ready Or Not", "Escape From Tarkov", "Ground Branch", "DayZ", "Grey Zone Warfare", "Body Cam", "Operator", "Exfil", "Hell Let Loose", "Mil-Sim General"];
  const TIMEZONES = ["UTC", "Europe/London", "Europe/Berlin", "Europe/Paris", "America/New_York", "America/Chicago", "America/Los_Angeles", "Australia/Sydney", "Asia/Singapore"];

  const blankForm = {
    title: "", body: "", looking_for: "",
    image_url: "", video_url: "",
    discord_url: "", youtube_url: "", twitch_url: "", steam_url: "", website_url: "",
    tags: [] as string[], min_age: "", timezone: "",
    interval_hours: 48,
  };
  const [form, setForm] = useState<any>(blankForm);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch<any>(`/recruitmentSchedule?group_id=${group.id}`);
      setSched(res.schedule);
      if (res.schedule) {
        setForm({
          title: res.schedule.title ?? "",
          body: res.schedule.body ?? "",
          looking_for: res.schedule.looking_for ?? "",
          image_url: res.schedule.image_url ?? "",
          video_url: res.schedule.video_url ?? "",
          discord_url: res.schedule.discord_url ?? "",
          youtube_url: res.schedule.youtube_url ?? "",
          twitch_url: res.schedule.twitch_url ?? "",
          steam_url: res.schedule.steam_url ?? "",
          website_url: res.schedule.website_url ?? "",
          tags: res.schedule.tags ?? [],
          min_age: res.schedule.min_age ?? "",
          timezone: res.schedule.timezone ?? "",
          interval_hours: res.schedule.interval_hours ?? 48,
        });
      }
    } catch { /* no schedule */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [group.id]);

  const uploadFile = async (file: File, type: "image" | "video") => {
    const setter = type === "image" ? setUploadingImg : setUploadingVid;
    setter(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = sessionStorage.getItem("tag_auth_token") ?? localStorage.getItem("tag_auth_token") ?? "";
      const res = await fetch("https://agent-tag-lead-developer-cff87ae4.base44.app/functions/rankReorder?path=upload", {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).then(r => r.json());
      const url = res.url ?? res.file_url ?? "";
      if (!url) throw new Error("Upload returned no URL");
      setForm((f: any) => ({ ...f, [type === "image" ? "image_url" : "video_url"]: url }));
      toast({ title: `${type === "image" ? "Image" : "Video"} uploaded!` });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally { setter(false); }
  };

  const toggleTag = (tag: string) => {
    setForm((f: any) => ({
      ...f,
      tags: f.tags.includes(tag) ? f.tags.filter((t: string) => t !== tag) : [...f.tags, tag],
    }));
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast({ title: "Title and content are required.", variant: "destructive" }); return;
    }
    setSaving(true);
    try {
      const payload = {
        group_id: group.id,
        ...form,
        min_age: form.min_age ? Number(form.min_age) : null,
      };
      const res = await apiFetch<any>("/recruitmentSchedule", { method: "POST", body: JSON.stringify(payload) });
      setSched(res.schedule);
      setEditing(false);
      toast({ title: "Recruitment schedule saved!", description: `Posts every ${form.interval_hours}h automatically.` });
    } catch (e: any) {
      toast({ title: "Failed to save", description: e.message, variant: "destructive" });
    } finally { setSaving(false); }
  };

  const toggleStatus = async () => {
    if (!sched) return;
    const newStatus = sched.status === "active" ? "paused" : "active";
    try {
      const res = await apiFetch<any>(`/recruitmentSchedule?path=${sched.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      setSched(res.schedule);
      toast({ title: newStatus === "active" ? "Schedule resumed" : "Schedule paused" });
    } catch (e: any) {
      toast({ title: "Failed", description: e.message, variant: "destructive" });
    }
  };

  const deleteSched = async () => {
    if (!sched) return;
    setDeleting(true);
    try {
      await apiFetch<any>(`/recruitmentSchedule?path=${sched.id}`, { method: "DELETE" });
      setSched(null);
      setForm(blankForm);
      toast({ title: "Recruitment schedule deleted" });
    } catch (e: any) {
      toast({ title: "Failed", description: e.message, variant: "destructive" });
    } finally { setDeleting(false); }
  };

  const nextPost = sched?.last_posted_at
    ? new Date(new Date(sched.last_posted_at).getTime() + (sched.interval_hours ?? 48) * 3600000)
    : null;

  if (loading) return (
    <div className="flex items-center gap-2 text-muted-foreground text-sm font-sans py-8">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading recruitment schedule...
    </div>
  );

  if (!globalIsPro) return (
    <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
      <Crown className="w-10 h-10 text-yellow-400" />
      <p className="font-display font-black uppercase tracking-widest text-lg">Commander Pro Required</p>
      <p className="text-sm text-muted-foreground max-w-xs">Auto Recruitment Posts are a Pro feature. Upgrade to Commander Pro to automatically re-post your recruitment ad at set intervals.</p>
      <a href="/commander-pro" className="inline-flex items-center gap-2 bg-yellow-400 text-black font-display font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded transition-all hover:bg-yellow-300">
        <Crown className="w-3.5 h-3.5" /> Upgrade to Pro
      </a>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display font-bold uppercase tracking-widest text-sm text-foreground flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-amber-400" /> Auto Recruitment Posts
          </h3>
          <p className="text-xs text-muted-foreground font-sans mt-1">
            Automatically re-post your recruitment ad on the forum at a set interval. Commander Pro feature.
          </p>
        </div>
        {sched && !editing && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={toggleStatus}
              className={`text-xs px-3 py-1.5 rounded border font-display font-bold uppercase tracking-widest transition-colors ${
                sched.status === "active"
                  ? "border-amber-400/40 text-amber-400 hover:bg-amber-400/10"
                  : "border-green-400/40 text-green-400 hover:bg-green-400/10"
              }`}>
              {sched.status === "active" ? "⏸ Pause" : "▶ Resume"}
            </button>
            <button onClick={() => setEditing(true)}
              className="text-xs px-3 py-1.5 rounded border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 font-display font-bold uppercase tracking-widest transition-colors">
              Edit
            </button>
            <button onClick={deleteSched} disabled={deleting}
              className="text-xs px-3 py-1.5 rounded border border-destructive/40 text-destructive hover:bg-destructive/10 font-display font-bold uppercase tracking-widest transition-colors">
              {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Delete"}
            </button>
          </div>
        )}
      </div>

      {/* Status card */}
      {sched && !editing && (
        <div className={`border rounded p-4 space-y-3 ${sched.status === "active" ? "border-green-500/20 bg-green-500/5" : "border-amber-500/20 bg-amber-500/5"}`}>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${sched.status === "active" ? "bg-green-400 animate-pulse" : "bg-amber-400"}`} />
            <span className={`text-xs font-display font-bold uppercase tracking-widest ${sched.status === "active" ? "text-green-400" : "text-amber-400"}`}>
              {sched.status === "active" ? "Active — Auto-posting" : "Paused"}
            </span>
          </div>
          <div>
            <p className="text-sm font-display font-bold text-foreground">{sched.title}</p>
            <p className="text-xs text-muted-foreground font-sans mt-1 line-clamp-3">{sched.body}</p>
          </div>
          {/* Social links preview */}
          {(sched.discord_url || sched.youtube_url || sched.twitch_url || sched.steam_url || sched.website_url) && (
            <div className="flex flex-wrap gap-2 pt-1">
              {sched.discord_url && <a href={sched.discord_url} target="_blank" rel="noopener noreferrer" className="text-[10px] px-2 py-1 rounded bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 font-display font-bold uppercase tracking-wider hover:bg-indigo-500/20 transition-colors">Discord</a>}
              {sched.youtube_url && <a href={sched.youtube_url} target="_blank" rel="noopener noreferrer" className="text-[10px] px-2 py-1 rounded bg-red-500/10 border border-red-500/30 text-red-400 font-display font-bold uppercase tracking-wider hover:bg-red-500/20 transition-colors">YouTube</a>}
              {sched.twitch_url && <a href={sched.twitch_url} target="_blank" rel="noopener noreferrer" className="text-[10px] px-2 py-1 rounded bg-purple-500/10 border border-purple-500/30 text-purple-400 font-display font-bold uppercase tracking-wider hover:bg-purple-500/20 transition-colors">Twitch</a>}
              {sched.steam_url && <a href={sched.steam_url} target="_blank" rel="noopener noreferrer" className="text-[10px] px-2 py-1 rounded bg-sky-500/10 border border-sky-500/30 text-sky-400 font-display font-bold uppercase tracking-wider hover:bg-sky-500/20 transition-colors">Steam</a>}
              {sched.website_url && <a href={sched.website_url} target="_blank" rel="noopener noreferrer" className="text-[10px] px-2 py-1 rounded bg-secondary border border-border text-muted-foreground font-display font-bold uppercase tracking-wider hover:text-foreground transition-colors flex items-center gap-1"><Globe className="w-2.5 h-2.5" /> Website</a>}
            </div>
          )}
          {/* Tags */}
          {sched.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {sched.tags.map((t: string) => (
                <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary font-display font-bold uppercase tracking-wider">{t}</span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground font-sans pt-1 border-t border-border/40">
            <span>Every <strong className="text-foreground">{sched.interval_hours}h</strong></span>
            <span>Posted <strong className="text-foreground">{sched.post_count ?? 0}</strong> time{(sched.post_count ?? 0) !== 1 ? "s" : ""}</span>
            {sched.min_age && <span>Age <strong className="text-foreground">{sched.min_age}+</strong></span>}
            {sched.timezone && <span>TZ: <strong className="text-foreground">{sched.timezone}</strong></span>}
            {sched.last_posted_at && <span>Last: <strong className="text-foreground">{new Date(sched.last_posted_at).toLocaleDateString("en-GB")}</strong></span>}
            {nextPost && sched.status === "active" && (
              <span>Next: <strong className="text-foreground">{nextPost < new Date() ? "Due now" : nextPost.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</strong></span>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit form */}
      {(!sched || editing) && (
        <div className="space-y-5">
          {/* Preview toggle */}
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground">
              {sched ? "Edit Recruitment Post" : "Set Up Recruitment Post"}
            </h4>
            <button onClick={() => setPreview(p => !p)}
              className="text-xs px-3 py-1.5 rounded border border-border text-muted-foreground hover:text-foreground font-display font-bold uppercase tracking-widest transition-colors flex items-center gap-1.5">
              <Eye className="w-3 h-3" /> {preview ? "Hide Preview" : "Preview"}
            </button>
          </div>

          {/* Live preview */}
          {preview && (
            <div className="border border-primary/20 rounded p-4 bg-secondary/40 space-y-3">
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary">Forum Preview</p>
              <p className="text-sm font-display font-bold text-foreground">{form.title || "Your post title"}</p>
              <p className="text-xs font-sans text-muted-foreground whitespace-pre-wrap">{form.body || "Your post body will appear here..."}</p>
              {form.looking_for && (
                <div className="border-t border-border/40 pt-2">
                  <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Looking for</p>
                  <p className="text-xs font-sans text-muted-foreground whitespace-pre-wrap">{form.looking_for}</p>
                </div>
              )}
              {form.image_url && <img src={form.image_url} alt="preview" className="rounded max-h-40 object-cover" />}
              {form.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {form.tags.map((t: string) => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-primary font-display font-bold uppercase tracking-wider">{t}</span>)}
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                {form.discord_url && <span className="text-[10px] px-2 py-1 rounded bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 font-display font-bold uppercase tracking-wider">Discord</span>}
                {form.youtube_url && <span className="text-[10px] px-2 py-1 rounded bg-red-500/10 border border-red-500/30 text-red-400 font-display font-bold uppercase tracking-wider">YouTube</span>}
                {form.twitch_url && <span className="text-[10px] px-2 py-1 rounded bg-purple-500/10 border border-purple-500/30 text-purple-400 font-display font-bold uppercase tracking-wider">Twitch</span>}
                {form.steam_url && <span className="text-[10px] px-2 py-1 rounded bg-sky-500/10 border border-sky-500/30 text-sky-400 font-display font-bold uppercase tracking-wider">Steam</span>}
                {form.website_url && <span className="text-[10px] px-2 py-1 rounded bg-secondary border border-border text-muted-foreground font-display font-bold uppercase tracking-wider">Website</span>}
              </div>
            </div>
          )}

          <div className="border border-border rounded p-4 space-y-5">
            {/* Title */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground font-sans uppercase tracking-widest">Post Title *</label>
              <input value={form.title} onChange={e => setForm((f: any) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. [RECRUITING] E-Squadron — Elite Milsim Unit"
                className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60" />
            </div>

            {/* Body */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground font-sans uppercase tracking-widest">About Your Unit *</label>
              <textarea value={form.body} onChange={e => setForm((f: any) => ({ ...f, body: e.target.value }))}
                rows={5} maxLength={3000}
                placeholder="Describe your unit, what games you play, your play style, when you operate..."
                className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 resize-y" />
              <p className="text-[10px] text-muted-foreground text-right">{form.body.length}/3000</p>
            </div>

            {/* Looking for */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground font-sans uppercase tracking-widest">What We're Looking For</label>
              <textarea value={form.looking_for} onChange={e => setForm((f: any) => ({ ...f, looking_for: e.target.value }))}
                rows={3} maxLength={1000}
                placeholder="e.g. Dedicated players, strong comms discipline, 18+, available weekends..."
                className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 resize-y" />
              <p className="text-[10px] text-muted-foreground text-right">{(form.looking_for ?? "").length}/1000</p>
            </div>

            {/* Game tags */}
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground font-sans uppercase tracking-widest flex items-center gap-1.5"><Tag className="w-3 h-3" /> Games / Tags</label>
              <div className="flex flex-wrap gap-2">
                {GAME_TAGS.map(tag => (
                  <button key={tag} type="button" onClick={() => toggleTag(tag)}
                    className={`text-xs px-2.5 py-1 rounded-full border font-display font-bold uppercase tracking-wider transition-colors ${
                      form.tags?.includes(tag)
                        ? "bg-primary/15 border-primary/50 text-primary"
                        : "bg-secondary border-border text-muted-foreground hover:text-foreground hover:border-border/60"
                    }`}>
                    {form.tags?.includes(tag) && <Check className="w-2.5 h-2.5 inline mr-1" />}{tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Image upload */}
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground font-sans uppercase tracking-widest">Banner / Image</label>
              <div className="flex gap-2 items-center">
                <input value={form.image_url} onChange={e => setForm((f: any) => ({ ...f, image_url: e.target.value }))}
                  placeholder="Paste URL or upload below"
                  className="flex-1 bg-secondary border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60" />
                <button type="button" onClick={() => imgRef.current?.click()} disabled={uploadingImg}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-border text-muted-foreground hover:text-foreground font-display font-bold uppercase tracking-widest transition-colors shrink-0">
                  {uploadingImg ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload
                </button>
                <input ref={imgRef} type="file" accept="image/*" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f, "image"); }} />
              </div>
              {form.image_url && <img src={form.image_url} alt="preview" className="rounded max-h-28 object-cover border border-border" />}
            </div>

            {/* Video upload */}
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground font-sans uppercase tracking-widest">Promo Video (optional)</label>
              <div className="flex gap-2 items-center">
                <input value={form.video_url} onChange={e => setForm((f: any) => ({ ...f, video_url: e.target.value }))}
                  placeholder="Paste YouTube/video URL or upload"
                  className="flex-1 bg-secondary border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60" />
                <button type="button" onClick={() => vidRef.current?.click()} disabled={uploadingVid}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-border text-muted-foreground hover:text-foreground font-display font-bold uppercase tracking-widest transition-colors shrink-0">
                  {uploadingVid ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload
                </button>
                <input ref={vidRef} type="file" accept="video/*" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f, "video"); }} />
              </div>
              {form.video_url && !form.video_url.includes("youtube") && !form.video_url.includes("youtu.be") && (
                <video src={form.video_url} controls className="rounded max-h-28 border border-border w-full" />
              )}
            </div>

            {/* Social links */}
            <div className="space-y-3">
              <label className="text-xs text-muted-foreground font-sans uppercase tracking-widest flex items-center gap-1.5"><Link2 className="w-3 h-3" /> Social & Community Links</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[10px] text-indigo-400 font-display font-bold uppercase tracking-widest">Discord Invite</label>
                  <input value={form.discord_url} onChange={e => setForm((f: any) => ({ ...f, discord_url: e.target.value }))}
                    placeholder="https://discord.gg/..."
                    className="w-full bg-secondary border border-indigo-500/20 rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-indigo-500/60" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-red-400 font-display font-bold uppercase tracking-widest">YouTube Channel</label>
                  <input value={form.youtube_url} onChange={e => setForm((f: any) => ({ ...f, youtube_url: e.target.value }))}
                    placeholder="https://youtube.com/..."
                    className="w-full bg-secondary border border-red-500/20 rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-red-500/60" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-purple-400 font-display font-bold uppercase tracking-widest">Twitch</label>
                  <input value={form.twitch_url} onChange={e => setForm((f: any) => ({ ...f, twitch_url: e.target.value }))}
                    placeholder="https://twitch.tv/..."
                    className="w-full bg-secondary border border-purple-500/20 rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-purple-500/60" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-sky-400 font-display font-bold uppercase tracking-widest">Steam Group</label>
                  <input value={form.steam_url} onChange={e => setForm((f: any) => ({ ...f, steam_url: e.target.value }))}
                    placeholder="https://steamcommunity.com/groups/..."
                    className="w-full bg-secondary border border-sky-500/20 rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-sky-500/60" />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <label className="text-[10px] text-muted-foreground font-display font-bold uppercase tracking-widest flex items-center gap-1"><Globe className="w-2.5 h-2.5" /> Website</label>
                  <input value={form.website_url} onChange={e => setForm((f: any) => ({ ...f, website_url: e.target.value }))}
                    placeholder="https://yourunit.com"
                    className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60" />
                </div>
              </div>
            </div>

            {/* Requirements */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground font-sans uppercase tracking-widest">Min. Age</label>
                <input type="number" value={form.min_age} onChange={e => setForm((f: any) => ({ ...f, min_age: e.target.value }))}
                  min={16} max={99} placeholder="e.g. 18"
                  className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground font-sans uppercase tracking-widest">Timezone</label>
                <select value={form.timezone} onChange={e => setForm((f: any) => ({ ...f, timezone: e.target.value }))}
                  className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm font-sans text-foreground focus:outline-none focus:border-primary/60">
                  <option value="">Any / Not specified</option>
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
            </div>

            {/* Interval */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground font-sans uppercase tracking-widest">Repost Interval</label>
              <select value={form.interval_hours} onChange={e => setForm((f: any) => ({ ...f, interval_hours: Number(e.target.value) }))}
                className="bg-secondary border border-border rounded px-3 py-2 text-sm font-sans text-foreground focus:outline-none focus:border-primary/60">
                <option value={24}>Every 24 hours</option>
                <option value={48}>Every 48 hours (default)</option>
                <option value={72}>Every 72 hours</option>
                <option value={168}>Every week</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              <button onClick={save} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded bg-primary text-primary-foreground font-display font-bold uppercase tracking-widest text-xs hover:opacity-90 transition-opacity">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {sched ? "Save Changes" : "Activate Schedule"}
              </button>
              {editing && (
                <button onClick={() => setEditing(false)}
                  className="text-xs px-3 py-2 rounded border border-border text-muted-foreground hover:text-foreground font-display font-bold uppercase tracking-widest transition-colors">
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!sched && !loading && (
        <div className="text-center py-4 text-muted-foreground text-xs font-sans">
          <p className="font-display text-sm uppercase tracking-widest">No recruitment schedule set</p>
          <p className="mt-1">Fill in the form above to start auto-posting.</p>
        </div>
      )}
    </div>
  );
}


// ─── Event Hub Tab (Events + Activity Calendar + Campaigns merged) ────────────
function EventHubTab({ group, showMsg, globalIsPro }: any) {
  type EHSub = "events" | "calendar" | "campaigns" | "planning" | "comms";
  const [sub, setSub] = useState<EHSub>("events");

  const SUBS: { id: EHSub; label: string; icon: React.ReactNode; pro?: boolean }[] = [
    { id: "events",    label: "Operations",    icon: <Siren className="w-3.5 h-3.5" /> },
    { id: "calendar",  label: "Calendar",      icon: <CalendarDays className="w-3.5 h-3.5" />, pro: true },
    { id: "campaigns", label: "Campaigns",     icon: <Zap className="w-3.5 h-3.5" />, pro: true },
    { id: "planning",  label: "Planning Room", icon: <Layers className="w-3.5 h-3.5" />, pro: true },
    { id: "comms",     label: "Comms Plan",    icon: <Antenna className="w-3.5 h-3.5" />, pro: true },
  ];

  return (
    <div className="flex gap-0 min-h-[600px]">
      {/* Mini vertical sidebar */}
      <div className="shrink-0 w-40 border-r border-border/60 pr-2 space-y-0.5 pt-1">
        {SUBS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSub(s.id)}
            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded text-left font-display font-bold uppercase tracking-widest text-[10px] transition-colors ${
              sub === s.id
                ? "bg-primary/15 text-primary border-l-2 border-primary pl-2.5"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
            }`}
          >
            {s.icon}
            <span className="truncate">{s.label}</span>
            {s.pro && <Crown className="ml-auto w-3 h-3 text-yellow-400 shrink-0" />}
          </button>
        ))}
      </div>

      {/* Content panel */}
      <div className="flex-1 pl-5 min-w-0">
        {sub === "events"    && <EventsTab    group={group} showMsg={showMsg} />}
        {sub === "calendar"  && <ActivityCalendarTab group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
        {sub === "campaigns" && <CampaignsTab group={group} globalIsPro={globalIsPro} />}
        {sub === "planning"  && <OpPlanningRoomTab group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
        {sub === "comms"     && <CommsPlanTab group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
      </div>
    </div>
  );
}


function EventsTab({ group, showMsg }: any) {
  const [sub, setSub] = useState<"reports">("reports");
  const [reportSub, setReportSub] = useState<"ops" | "aars" | "warnos" | "lace" | "sitrep" | "conduct" | "medevac" | "intel">("ops");

  const SUB_TABS = [
    { id: "reports" as const, label: "Reports",  icon: ClipboardList },
  ];

  const REPORT_SUBS = [
    { id: "ops"     as const, label: "Operations", icon: Siren },
    { id: "aars"    as const, label: "AARs",       icon: ClipboardList },
    { id: "warnos"  as const, label: "WARNOs",     icon: AlertTriangle },
    { id: "lace"    as const, label: "LACE",       icon: Radio },
    { id: "sitrep"  as const, label: "SITREPs",    icon: Target },
    { id: "conduct" as const, label: "Conduct",    icon: UserMinus2 },
    { id: "medevac" as const, label: "MEDEVAC",    icon: Radio },
    { id: "intel"         as const, label: "Op Intel",       icon: BrainCircuit },
  ];

  return (
    <div className="flex gap-0 min-h-[600px]">
      <div className="w-44 shrink-0 border-r border-border flex flex-col gap-0.5 pr-2 pt-1">
        {SUB_TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setSub(id)}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded text-xs font-display font-bold uppercase tracking-wider transition-all text-left w-full ${
              sub === id
                ? "bg-primary/15 border border-primary/50 text-primary"
                : "border border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/60"
            }`}>
            <Icon className="w-3.5 h-3.5 shrink-0" />{label}
          </button>
        ))}
      </div>
      <div className="flex-1 min-w-0 pl-5">
        {sub === "reports" && (
          <div>
            {/* Report type sub-tabs */}
            <div className="flex gap-1.5 mb-5 flex-wrap">
              {REPORT_SUBS.map(({ id, label, icon: Icon }) => (
                <button key={id} onClick={() => setReportSub(id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-[10px] font-display font-bold uppercase tracking-widest transition-all ${
                    reportSub === id
                      ? "bg-primary/15 text-primary border-primary/40"
                      : "bg-secondary border-border text-muted-foreground hover:text-foreground hover:border-border/80"
                  }`}>
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>
            {reportSub === "ops"     && <OpsTab    group={group} showMsg={showMsg} onNavTo={(t: string) => setReportSub(t as any)} />}
            {reportSub === "aars"   && <AARsTab   group={group} showMsg={showMsg} />}
            {reportSub === "warnos" && <WarnosTab group={group} showMsg={showMsg} />}
            {reportSub === "lace"   && <LaceTab   group={group} showMsg={showMsg} />}
            {reportSub === "sitrep" && <SitrepTab group={group} showMsg={showMsg} />}
            {reportSub === "conduct" && <ConductReportTab group={group} showMsg={showMsg} />}
            {reportSub === "medevac" && <MedevacTab group={group} showMsg={showMsg} />}
            {reportSub === "intel"        && <OpIntelTab group={group} showMsg={showMsg} />}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Ops Calendar Tab ────────────────────────────────────────────────────────
function OpsTab({ group, showMsg, onNavTo }: any) {
  const { user } = useAuth();
  const [ops, setOps] = useState<any[]>([]);
  const [aars, setAars] = useState<any[]>([]);
  const [briefings, setBriefings] = useState<any[]>([]);
  const [warnos, setWarnos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedOp, setExpandedOp] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editOpId, setEditOpId] = useState<string | null>(null);
  const [linkTarget, setLinkTarget] = useState<{ opId: string; type: "aar" } | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [opsExporting, setOpsExporting] = useState(false);

  const emptyForm = { name: "", description: "", game: "", event_type: "Op" as const, scheduled_at: "", end_date: "", status: "Planned" as const, location: "", max_slots: "" };
  const [form, setForm] = useState<any>(emptyForm);

  const GAMES = ["Arma 3","Arma Reforger","Squad","Ready Or Not","Escape From Tarkov","Ground Branch","DayZ","Grey Zone Warfare","Body Cam","Bellum","Exfil","Other"];
  const EVENT_TYPES = [
    { value: "Op",       label: "Operation", color: "text-red-400 border-red-400/40 bg-red-400/10" },
    { value: "Training", label: "Training",  color: "text-blue-400 border-blue-400/40 bg-blue-400/10" },
    { value: "Meeting",  label: "Meeting",   color: "text-yellow-400 border-yellow-400/40 bg-yellow-400/10" },
    { value: "Social",   label: "Social",    color: "text-purple-400 border-purple-400/40 bg-purple-400/10" },
    { value: "Admin",    label: "Admin",     color: "text-muted-foreground border-border bg-secondary/40" },
  ];
  const STATUS_COLOR: Record<string, string> = {
    Active:    "text-red-400 bg-red-500/10 border-red-500/30",
    Planned:   "text-amber-400 bg-amber-500/10 border-amber-500/30",
    Confirmed: "text-green-400 bg-green-500/10 border-green-500/30",
    Completed: "text-muted-foreground bg-secondary border-border",
    Cancelled: "text-muted-foreground bg-secondary/40 border-border",
  };
  const typeStyle = (t: string) => EVENT_TYPES.find(x => x.value === t)?.color ?? "text-muted-foreground border-border bg-secondary";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [opsData, aarsData, briefsData, warnosData] = await Promise.all([
        apiFetch(`/activityCalendar?path=list&group_id=${group.id}`),
        apiFetch(`/milsimAars?path=list&group_id=${group.id}`),
        apiFetch(`/milsimBriefings?path=list&group_id=${group.id}`),
        apiFetch(`/milsimGroupsB?path=${group.id}/warnos`).catch(() => []),
      ]);
      setOps(opsData.events ?? []);
      setAars(aarsData.aars ?? []);
      setBriefings(briefsData.briefings ?? []);
      setWarnos(Array.isArray(warnosData) ? warnosData : []);
    } catch { }
    setLoading(false);
  }, [group.id]);

  useEffect(() => { load(); }, [load]);

  const saveOp = async () => {
    if (!form.name) { showMsg(false, "Op name required"); return; }
    setSaving(true);
    try {
      if (editOpId) {
        await apiFetch("/activityCalendar?path=update", { method: "POST", body: JSON.stringify({ id: editOpId, title: form.name, ...form }) });
        showMsg(true, "Op updated");
      } else {
        await apiFetch("/activityCalendar?path=create", { method: "POST", body: JSON.stringify({ group_id: group.id, title: form.name, created_by: user?.username, ...form }) });
        showMsg(true, "Op scheduled");
      }
      setShowForm(false); setEditOpId(null); setForm(emptyForm); load();
    } catch (e: any) { showMsg(false, e.message); }
    setSaving(false);
  };

  const deleteOp = async (id: string) => {
    if (!confirm("Delete this op?")) return;
    await apiFetch("/activityCalendar?path=delete", { method: "POST", body: JSON.stringify({ id }) });
    showMsg(true, "Deleted"); load();
  };

  const setOpStatus = async (id: string, status: string) => {
    await apiFetch("/activityCalendar?path=update", { method: "POST", body: JSON.stringify({ id, status }) });
    load();
  };

  const linkDoc = async (docId: string) => {
    if (!linkTarget) return;
    try {
      await apiFetch("/milsimAars?path=link-op", { method: "POST", body: JSON.stringify({ aar_id: docId, op_id: linkTarget.opId }) });
      showMsg(true, "Linked"); setLinkTarget(null); load();
    } catch (e: any) { showMsg(false, e.message); }
  };

  const unlinkDoc = async (docId: string) => {
    try {
      await apiFetch("/milsimAars?path=link-op", { method: "POST", body: JSON.stringify({ aar_id: docId, op_id: null }) });
      load();
    } catch {}
  };

  const opAars      = (id: string) => aars.filter((a: any) => a.op_id === id);
  const opBriefings = (id: string) => briefings.filter((b: any) => b.op_id === id);
  const opWarnos    = (id: string) => warnos.filter((w: any) => w.op_id === id);
  const unlinkedAars = aars.filter((a: any) => !a.op_id);

  const sortedOps = [...ops]
    .filter(op => !typeFilter || op.event_type === typeFilter)
    .sort((a, b) => new Date(b.scheduled_at ?? b.created_date).getTime() - new Date(a.scheduled_at ?? a.created_date).getTime());

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      {/* Attach AAR modal */}
      {linkTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md space-y-4">
            <h3 className="font-display font-bold text-sm uppercase tracking-widest">Attach AAR</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {unlinkedAars.length === 0
                ? <p className="text-xs text-muted-foreground font-sans">No unlinked AARs available.</p>
                : unlinkedAars.map((doc: any) => (
                    <button key={doc.id} onClick={() => linkDoc(doc.id)} className="w-full text-left px-4 py-3 border border-border rounded-lg hover:bg-secondary/40 transition-colors">
                      <p className="font-display font-bold text-sm">{doc.title ?? doc.op_name}</p>
                      <p className="text-xs text-muted-foreground font-sans mt-0.5">{doc.author_username ?? ""}</p>
                    </button>
                  ))}
            </div>
            <button onClick={() => setLinkTarget(null)} className="px-4 py-1.5 border border-border rounded font-display text-xs uppercase tracking-widest">Cancel</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <CalendarDays className="w-5 h-5 text-primary" />
            <h2 className="font-display font-bold text-lg uppercase tracking-widest">Ops Calendar</h2>
          </div>
          <p className="text-xs text-muted-foreground font-sans">Scheduled operations, training events, and unit activities.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => { setOpsExporting(true); try { const url = await exportOpsToSheets(ops, group.name); window.open(url, "_blank"); } catch(err: any) { showMsg(false, "Sheets export: " + err.message); } finally { setOpsExporting(false); } }}
            disabled={opsExporting || ops.length === 0}
            className="flex items-center gap-2 px-3 py-2 bg-green-600/20 hover:bg-green-600/30 border border-green-500/40 text-green-400 font-display font-bold uppercase tracking-wider text-xs rounded transition-all disabled:opacity-50"
          >
            {opsExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>↗</span>} Export Sheets
          </button>
          <button
            onClick={() => { setShowForm(v => !v); setEditOpId(null); setForm(emptyForm); }}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs rounded transition-all"
          >
            <Plus className="w-3.5 h-3.5" /> Schedule Op
          </button>
        </div>
      </div>

      {/* Type filter pills */}
      <div className="flex flex-wrap gap-2">
        {EVENT_TYPES.map(t => (
          <button key={t.value}
            onClick={() => setTypeFilter(typeFilter === t.value ? null : t.value)}
            className={`text-[10px] px-2.5 py-1 rounded border font-display font-bold uppercase tracking-widest transition-all ${
              typeFilter === t.value ? t.color + " ring-1 ring-current" : t.color + " opacity-50 hover:opacity-80"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {/* Create / Edit form */}
      {(showForm || editOpId) && (
        <div className="bg-card border border-primary/40 rounded-lg p-5 space-y-4">
          <h3 className="font-display font-bold uppercase tracking-widest text-primary flex items-center gap-2 text-sm">
            <Tag className="w-4 h-4" />{editOpId ? "Edit Operation" : "New Operation"}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="mf-label">Title *</label>
              <input value={form.name} onChange={e => setForm((f:any) => ({...f, name: e.target.value}))} placeholder="Op Ironsides" className="mf-input w-full" />
            </div>
            <div>
              <label className="mf-label">Type</label>
              <select value={form.event_type} onChange={e => setForm((f:any) => ({...f, event_type: e.target.value}))} className="mf-input w-full">
                {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mf-label">Game</label>
              <select value={form.game} onChange={e => setForm((f:any) => ({...f, game: e.target.value}))} className="mf-input w-full">
                <option value="">Select game...</option>
                {GAMES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="mf-label">Start Date &amp; Time (UTC) *</label>
              <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm((f:any) => ({...f, scheduled_at: e.target.value}))} className="mf-input w-full" />
            </div>
            <div>
              <label className="mf-label">End Date &amp; Time (UTC)</label>
              <input type="datetime-local" value={form.end_date} onChange={e => setForm((f:any) => ({...f, end_date: e.target.value}))} className="mf-input w-full" />
            </div>
            <div>
              <label className="mf-label">Location / Server</label>
              <input value={form.location ?? ""} onChange={e => setForm((f:any) => ({...f, location: e.target.value}))} placeholder="Discord Stage / Server name..." className="mf-input w-full" />
            </div>
            <div>
              <label className="mf-label">Max Slots</label>
              <input type="number" value={form.max_slots ?? ""} onChange={e => setForm((f:any) => ({...f, max_slots: e.target.value}))} placeholder="24" className="mf-input w-full" />
            </div>
            <div className="sm:col-span-2">
              <label className="mf-label">Briefing / Description</label>
              <textarea value={form.description} onChange={e => setForm((f:any) => ({...f, description: e.target.value}))} rows={3} placeholder="Mission objectives, notes..." className="mf-input w-full resize-none" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={saveOp} disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded font-display text-xs uppercase tracking-widest hover:bg-primary/90 disabled:opacity-60">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {editOpId ? "Save" : "Schedule"}
            </button>
            <button onClick={() => { setShowForm(false); setEditOpId(null); setForm(emptyForm); }}
              className="px-5 py-2 border border-border rounded font-display text-xs uppercase tracking-widest hover:bg-secondary transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Op cards */}
      {sortedOps.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground font-sans text-sm border border-dashed border-border rounded-lg">
          <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p>{typeFilter ? `No ${typeFilter} ops scheduled.` : "No ops scheduled yet."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedOps.map((op: any) => {
            const linkedAars = opAars(op.id);
            const linkedWarnos = opWarnos(op.id);
            const linkedBriefs = opBriefings(op.id);
            const isExpanded = expandedOp === op.id;
            const isActive = op.status === "Active";
            const tStyle = typeStyle(op.event_type ?? "Op");
            return (
              <div key={op.id} className={`bg-card border rounded-lg overflow-hidden transition-all hover:border-primary/40 ${isActive ? "border-red-500/40 bg-red-500/5" : "border-border"}`}>
                {/* Card header */}
                <button onClick={() => setExpandedOp(isExpanded ? null : op.id)}
                  className="w-full flex items-start justify-between gap-3 px-5 py-4 hover:bg-secondary/10 transition-colors text-left">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      {isActive && (
                        <span className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-widest text-red-400 px-2 py-0.5 bg-red-500/20 border border-red-500/30 rounded animate-pulse">
                          <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />LIVE
                        </span>
                      )}
                      <span className={`text-[10px] px-2 py-0.5 rounded border font-display font-bold uppercase tracking-widest ${STATUS_COLOR[op.status] ?? "text-muted-foreground border-border"}`}>{op.status}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded border font-display font-bold uppercase tracking-widest ${tStyle}`}>{op.event_type ?? "Op"}</span>
                      <span className="font-display font-bold text-sm text-foreground">{op.name ?? op.title}</span>
                      {op.game && <span className="flex items-center gap-1 text-[10px] bg-secondary border border-border text-muted-foreground px-2 py-0.5 rounded font-display uppercase tracking-widest"><Gamepad2 className="w-2.5 h-2.5" />{op.game}</span>}
                    </div>
                    {op.description && <p className="text-xs text-muted-foreground font-sans line-clamp-1">{op.description}</p>}
                    <div className="flex flex-wrap gap-4 mt-1.5 text-[11px] text-muted-foreground font-sans">
                      {op.scheduled_at && (
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-primary" />
                          {new Date(op.scheduled_at).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"})}
                          {" "}{new Date(op.scheduled_at).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})} UTC
                        </span>
                      )}
                      {op.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-primary" />{op.location}</span>}
                      {op.max_slots && <span className="flex items-center gap-1"><Users className="w-3 h-3 text-primary" />{op.max_slots} slots</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 mt-0.5">
                    {linkedAars.length > 0   && <span className="flex items-center gap-1 text-[10px] text-green-400"><ClipboardList className="w-3 h-3" />{linkedAars.length}</span>}
                    {linkedWarnos.length > 0  && <span className="flex items-center gap-1 text-[10px] text-orange-400"><AlertTriangle className="w-3 h-3" />{linkedWarnos.length}</span>}
                    {linkedBriefs.length > 0  && <span className="flex items-center gap-1 text-[10px] text-blue-400"><FileText className="w-3 h-3" />{linkedBriefs.length}</span>}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </div>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-border px-5 py-4 space-y-4 bg-secondary/10">
                    {/* Status controls */}
                    <div className="flex flex-wrap gap-2">
                      {["Planned","Confirmed","Active","Completed","Cancelled"].filter(s => s !== op.status).map(s => (
                        <button key={s} onClick={() => setOpStatus(op.id, s)}
                          className="text-[10px] font-display font-bold uppercase tracking-widest px-2 py-1 border border-border rounded hover:bg-secondary transition-colors">→ {s}</button>
                      ))}
                      <button onClick={() => { setEditOpId(op.id); setForm({ name: op.name??op.title??"", description: op.description??"", game: op.game??"", event_type: op.event_type??"Op", scheduled_at: op.scheduled_at?.slice(0,16)??"", end_date: op.end_date??"", status: op.status??"Planned", location: op.location??"", max_slots: op.max_slots?.toString()??"" }); setShowForm(false); }}
                        className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-widest px-2 py-1 border border-border text-muted-foreground rounded hover:text-primary transition-colors">
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      <button onClick={() => deleteOp(op.id)}
                        className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-widest px-2 py-1 border border-red-500/30 text-red-400 rounded hover:bg-red-500/10 transition-colors">
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>

                    {/* WARNOs */}
                    <div>
                      <p className="text-[10px] font-display font-bold uppercase tracking-widest text-orange-400 flex items-center gap-1.5 mb-2"><AlertTriangle className="w-3 h-3" /> Warning Orders</p>
                      {linkedWarnos.length === 0
                        ? <p className="text-xs text-muted-foreground font-sans italic">No WARNOs issued — create one in the WARNOs tab.</p>
                        : <div className="space-y-1.5">{linkedWarnos.map((w: any) => (
                            <button key={w.id} onClick={() => onNavTo?.("warnos")}
                              className="w-full flex items-center justify-between px-3 py-2 bg-orange-500/5 border border-orange-500/20 rounded hover:bg-orange-500/10 transition-colors text-left">
                              <span className="text-xs font-display font-bold">{w.title}</span>
                              <span className="flex items-center gap-1 text-[10px] text-orange-400 font-display uppercase">{w.status}<ChevronRight className="w-3 h-3" /></span>
                            </button>
                          ))}</div>}
                    </div>

                    {/* Briefings */}
                    <div>
                      <p className="text-[10px] font-display font-bold uppercase tracking-widest text-blue-400 flex items-center gap-1.5 mb-2"><FileText className="w-3 h-3" /> Op Briefings</p>
                      {linkedBriefs.length === 0
                        ? <p className="text-xs text-muted-foreground font-sans italic">No briefings attached — create one in Op Intel.</p>
                        : <div className="space-y-1.5">{linkedBriefs.map((b: any) => (
                            <button key={b.id} onClick={() => onNavTo?.("intel")}
                              className="w-full flex items-center justify-between px-3 py-2 bg-blue-500/5 border border-blue-500/20 rounded hover:bg-blue-500/10 transition-colors text-left">
                              <span className="text-xs font-display font-bold">{b.title}</span>
                              <span className="flex items-center gap-1 text-[10px] text-blue-400 font-mono uppercase">{b.classification}<ChevronRight className="w-3 h-3" /></span>
                            </button>
                          ))}</div>}
                    </div>

                    {/* AARs */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] font-display font-bold uppercase tracking-widest text-green-400 flex items-center gap-1.5"><ClipboardList className="w-3 h-3" /> After Action Reports</p>
                        <button onClick={() => setLinkTarget({ opId: op.id, type: "aar" })}
                          className="flex items-center gap-1 text-[10px] font-display font-bold uppercase text-muted-foreground border border-border rounded px-2 py-0.5 hover:text-primary transition-colors">
                          <Plus className="w-3 h-3" /> Attach
                        </button>
                      </div>
                      {linkedAars.length === 0
                        ? <p className="text-xs text-muted-foreground font-sans italic">No AARs filed yet.</p>
                        : <div className="space-y-1.5">{linkedAars.map((a: any) => (
                            <div key={a.id} className="flex items-center justify-between px-3 py-2 bg-green-500/5 border border-green-500/20 rounded">
                              <button onClick={() => onNavTo?.("aars")} className="flex-1 text-left hover:opacity-80 transition-opacity">
                                <span className="text-xs font-display font-bold">{a.title ?? a.op_name}</span>
                                {a.outcome && <span className="ml-2 text-[10px] text-muted-foreground">{a.outcome}</span>}
                              </button>
                              <button onClick={() => unlinkDoc(a.id)} className="p-1 text-muted-foreground hover:text-destructive transition-colors"><X className="w-3 h-3" /></button>
                            </div>
                          ))}</div>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── AARs ─────────────────────────────────────────────────────────────────────
function AARField({ label, value }: { label: string; value: string }) {
  return <div><p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">{label}</p><p className="text-sm text-foreground font-sans whitespace-pre-wrap">{value}</p></div>;
}


/* ─── WARNOs Tab ─────────────────────────────────────────────────────────── */
function WarnosTab({ group, showMsg }: any) {
  const emptyForm = {
    title: "", op_date: "", op_id: "", status: "draft",
    situation_ground: "", situation_enemy: "", situation_friendly: "",
    mission: "",
    timings_hh: "", timings_nmb: "", timings_other: "",
    o_group_time: "", o_group_loc: "", o_group_other: "",
    css: "",
    acknowledge_1_sect: "", acknowledge_2_sect: "", acknowledge_3_sect: "", acknowledge_4_sect: "",
    acknowledge_pl_sgt: "", acknowledge_atts_1: "", acknowledge_atts_2: "", acknowledge_atts_3: "",
  };
  const [warnos, setWarnos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<any>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = () => {
    apiFetch<any[]>(`/milsimGroupsB?path=${group.id}/warnos`)
      .then(d => setWarnos(d ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  const [ops, setOps] = useState<any[]>([]);
  useEffect(() => {
    apiFetch<any>(`/milsimGroupsB?path=${group.id}/ops`).then((d: any) => setOps((Array.isArray(d) ? d : (d?.events ?? [])).filter((o:any) => ["Active","Confirmed","Planned","Completed"].includes(o.status)))).catch(() => {});
  }, [group.id]);

  useEffect(() => { load(); }, [group.id]);

  const setF = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f: any) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await apiFetch(`/milsimGroupsB?path=${group.id}/warnos/${editId}`, { method: "PATCH", body: JSON.stringify(form) });
        showMsg(true, "WARNO updated.");
      } else {
        await apiFetch(`/milsimGroupsB?path=${group.id}/warnos`, { method: "POST", body: JSON.stringify(form) });
        showMsg(true, "WARNO created.");
      }
      setCreating(false); setEditId(null); setForm(emptyForm); load();
    } catch (e: any) { showMsg(false, e.message); } finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    try {
      await apiFetch(`/milsimGroupsB?path=${group.id}/warnos/${id}`, { method: "DELETE" });
      showMsg(true, "WARNO deleted."); load();
    } catch (e: any) { showMsg(false, e.message); }
  };

  const startEdit = (w: any) => {
    setEditId(w.id);
    setForm({
      title: w.title ?? "", op_date: w.op_date ?? "", status: w.status ?? "draft",
      situation_ground: w.situation_ground ?? "", situation_enemy: w.situation_enemy ?? "", situation_friendly: w.situation_friendly ?? "",
      mission: w.mission ?? "",
      timings_hh: w.timings_hh ?? "", timings_nmb: w.timings_nmb ?? "", timings_other: w.timings_other ?? "",
      o_group_time: w.o_group_time ?? "", o_group_loc: w.o_group_loc ?? "", o_group_other: w.o_group_other ?? "",
      css: w.css ?? "",
      acknowledge_1_sect: w.acknowledge_1_sect ?? "", acknowledge_2_sect: w.acknowledge_2_sect ?? "",
      acknowledge_3_sect: w.acknowledge_3_sect ?? "", acknowledge_4_sect: w.acknowledge_4_sect ?? "",
      acknowledge_pl_sgt: w.acknowledge_pl_sgt ?? "",
      acknowledge_atts_1: w.acknowledge_atts_1 ?? "", acknowledge_atts_2: w.acknowledge_atts_2 ?? "", acknowledge_atts_3: w.acknowledge_atts_3 ?? "",
    });
    setCreating(true);
  };

  const STATUS_STYLE: Record<string, string> = {
    draft:    "text-muted-foreground bg-secondary border-border",
    issued:   "text-orange-400 bg-orange-500/10 border-orange-500/30",
    archived: "text-muted-foreground bg-secondary/40 border-border",
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground font-sans">Warning Orders — issued before an operation to give early notice of upcoming tasks.</p>
        {!creating && !editId && (
          <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-4 py-2 rounded transition-all">
            <Plus className="w-3.5 h-3.5" /> New WARNO
          </button>
        )}
      </div>

      {/* Create / Edit Form */}
      {(creating || editId) && (
        <div className="bg-card border border-primary/30 rounded-lg overflow-hidden">
          {/* WARNO header bar */}
          <div className="bg-primary/10 border-b border-primary/20 px-6 py-3 flex items-center gap-3">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
            <span className="font-display font-black text-sm uppercase tracking-widest text-foreground">Warning Order</span>
          </div>

          <div className="p-6 space-y-5">
            {/* Title + Status + Date row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><label className="mf-label">Title *</label><input value={form.title} onChange={setF("title")} className="mf-input" placeholder="Op IRON FIST — WARNO 1" /></div>
              <div><label className="mf-label">Op Date</label><input type="datetime-local" value={form.op_date} onChange={setF("op_date")} className="mf-input" /></div>
            </div>
            <div><label className="mf-label">Linked Op</label><select value={form.op_id ?? ""} onChange={setF("op_id")} className="mf-input"><option value="">— No op —</option>{ops.map((o:any) => <option key={o.id} value={o.id}>{o.title ?? o.name}</option>)}</select></div>
            <div className="w-40"><label className="mf-label">Status</label>
              <select value={form.status} onChange={setF("status")} className="mf-input">
                <option value="draft">Draft</option>
                <option value="issued">Issued</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Section 1 — Situation */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-secondary/60 px-4 py-2 border-b border-border"><p className="text-[10px] font-display font-black uppercase tracking-widest text-foreground">1. Situation</p></div>
              <div className="divide-y divide-border">
                <div className="px-4 py-3"><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">Ground</label><textarea rows={3} value={form.situation_ground} onChange={setF("situation_ground")} className="mf-input resize-none" placeholder="Terrain, weather, visibility..." /></div>
                <div className="px-4 py-3"><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">Enemy</label><textarea rows={3} value={form.situation_enemy} onChange={setF("situation_enemy")} className="mf-input resize-none" placeholder="Enemy strength, disposition, likely COA..." /></div>
                <div className="px-4 py-3"><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">Friendly</label><textarea rows={3} value={form.situation_friendly} onChange={setF("situation_friendly")} className="mf-input resize-none" placeholder="Higher unit intentions, adjacent units..." /></div>
              </div>
            </div>

            {/* Section 2 — Mission */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-secondary/60 px-4 py-2 border-b border-border"><p className="text-[10px] font-display font-black uppercase tracking-widest text-foreground">2. Mission</p></div>
              <div className="px-4 py-3"><textarea rows={3} value={form.mission} onChange={setF("mission")} className="mf-input resize-none" placeholder="Who, what, where, when, why..." /></div>
            </div>

            {/* Section 3 — Timings */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-secondary/60 px-4 py-2 border-b border-border"><p className="text-[10px] font-display font-black uppercase tracking-widest text-foreground">3. Timings</p></div>
              <div className="px-4 py-3 grid grid-cols-3 gap-3">
                <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">H Hr</label><input value={form.timings_hh} onChange={setF("timings_hh")} className="mf-input" placeholder="e.g. 1800Z" /></div>
                <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">NMB</label><input value={form.timings_nmb} onChange={setF("timings_nmb")} className="mf-input" placeholder="e.g. 30 mins" /></div>
                <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">Other</label><input value={form.timings_other} onChange={setF("timings_other")} className="mf-input" /></div>
              </div>
            </div>

            {/* Section 4 — O Group */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-secondary/60 px-4 py-2 border-b border-border"><p className="text-[10px] font-display font-black uppercase tracking-widest text-foreground">4. O Group</p></div>
              <div className="px-4 py-3 grid grid-cols-3 gap-3">
                <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">Time</label><input value={form.o_group_time} onChange={setF("o_group_time")} className="mf-input" placeholder="e.g. 1700Z" /></div>
                <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">Loc</label><input value={form.o_group_loc} onChange={setF("o_group_loc")} className="mf-input" placeholder="e.g. Grid 123456" /></div>
                <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">Other</label><input value={form.o_group_other} onChange={setF("o_group_other")} className="mf-input" /></div>
              </div>
            </div>

            {/* Section 5 — CSS */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-secondary/60 px-4 py-2 border-b border-border"><p className="text-[10px] font-display font-black uppercase tracking-widest text-foreground">5. CSS (Combat Service Support)</p></div>
              <div className="px-4 py-3"><textarea rows={3} value={form.css} onChange={setF("css")} className="mf-input resize-none" placeholder="Ammo, medical, transport, resupply..." /></div>
            </div>

            {/* Section 6 — Acknowledge */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-secondary/60 px-4 py-2 border-b border-border"><p className="text-[10px] font-display font-black uppercase tracking-widest text-foreground">6. Acknowledge (Radio or Sign)</p></div>
              <div className="px-4 py-3 space-y-3">
                <div className="grid grid-cols-4 gap-2">
                  <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">1 Sect</label><input value={form.acknowledge_1_sect} onChange={setF("acknowledge_1_sect")} className="mf-input text-xs" /></div>
                  <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">2 Sect</label><input value={form.acknowledge_2_sect} onChange={setF("acknowledge_2_sect")} className="mf-input text-xs" /></div>
                  <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">3 Sect</label><input value={form.acknowledge_3_sect} onChange={setF("acknowledge_3_sect")} className="mf-input text-xs" /></div>
                  <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">4 Sect</label><input value={form.acknowledge_4_sect} onChange={setF("acknowledge_4_sect")} className="mf-input text-xs" /></div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">Pl Sgt</label><input value={form.acknowledge_pl_sgt} onChange={setF("acknowledge_pl_sgt")} className="mf-input text-xs" /></div>
                  <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">Atts 1</label><input value={form.acknowledge_atts_1} onChange={setF("acknowledge_atts_1")} className="mf-input text-xs" /></div>
                  <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">Atts 2</label><input value={form.acknowledge_atts_2} onChange={setF("acknowledge_atts_2")} className="mf-input text-xs" /></div>
                  <div><label className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground block mb-1">Atts 3</label><input value={form.acknowledge_atts_3} onChange={setF("acknowledge_atts_3")} className="mf-input text-xs" /></div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={submit} disabled={saving || !form.title.trim()}
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded transition-all disabled:opacity-50">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {editId ? "Update WARNO" : "Issue WARNO"}
              </button>
              <button onClick={() => { setCreating(false); setEditId(null); setForm(emptyForm); }}
                className="px-4 py-2 border border-border text-muted-foreground rounded text-xs font-display uppercase hover:text-foreground">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {warnos.length === 0 && !creating ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg text-muted-foreground">
          <AlertTriangle className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-display text-sm uppercase tracking-widest">No warning orders issued</p>
        </div>
      ) : (
        <div className="space-y-3">
          {warnos.map((w: any) => (
            <div key={w.id} className="bg-card border border-border rounded-lg overflow-hidden">
              <button onClick={() => setExpandedId(expandedId === w.id ? null : w.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/30 transition-colors">
                <AlertTriangle className="w-4 h-4 text-orange-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-sm uppercase tracking-wider text-foreground truncate">{w.title}</p>
                  {w.op_date && <p className="text-xs text-muted-foreground font-sans mt-0.5">{new Date(w.op_date).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}</p>}
                </div>
                <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${STATUS_STYLE[w.status] ?? STATUS_STYLE.draft}`}>{w.status}</span>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform shrink-0 ${expandedId === w.id ? "rotate-180" : ""}`} />
              </button>

              {expandedId === w.id && (
                <div className="border-t border-border px-4 py-4 space-y-4">
                  {/* Situation */}
                  <div className="space-y-2">
                    <p className="text-[10px] font-display font-black uppercase tracking-widest text-primary border-b border-border pb-1">1. Situation</p>
                    {w.situation_ground && <WField label="Ground" value={w.situation_ground} />}
                    {w.situation_enemy && <WField label="Enemy" value={w.situation_enemy} />}
                    {w.situation_friendly && <WField label="Friendly" value={w.situation_friendly} />}
                  </div>
                  {w.mission && <div><p className="text-[10px] font-display font-black uppercase tracking-widest text-primary border-b border-border pb-1 mb-2">2. Mission</p><p className="text-sm text-foreground font-sans whitespace-pre-wrap">{w.mission}</p></div>}
                  {(w.timings_hh || w.timings_nmb || w.timings_other) && (
                    <div><p className="text-[10px] font-display font-black uppercase tracking-widest text-primary border-b border-border pb-1 mb-2">3. Timings</p>
                      <div className="flex gap-6 text-sm font-sans">
                        {w.timings_hh && <span><span className="text-muted-foreground text-xs">H Hr: </span>{w.timings_hh}</span>}
                        {w.timings_nmb && <span><span className="text-muted-foreground text-xs">NMB: </span>{w.timings_nmb}</span>}
                        {w.timings_other && <span>{w.timings_other}</span>}
                      </div>
                    </div>
                  )}
                  {(w.o_group_time || w.o_group_loc || w.o_group_other) && (
                    <div><p className="text-[10px] font-display font-black uppercase tracking-widest text-primary border-b border-border pb-1 mb-2">4. O Group</p>
                      <div className="flex gap-6 text-sm font-sans">
                        {w.o_group_time && <span><span className="text-muted-foreground text-xs">Time: </span>{w.o_group_time}</span>}
                        {w.o_group_loc && <span><span className="text-muted-foreground text-xs">Loc: </span>{w.o_group_loc}</span>}
                        {w.o_group_other && <span>{w.o_group_other}</span>}
                      </div>
                    </div>
                  )}
                  {w.css && <div><p className="text-[10px] font-display font-black uppercase tracking-widest text-primary border-b border-border pb-1 mb-2">5. CSS</p><p className="text-sm text-foreground font-sans whitespace-pre-wrap">{w.css}</p></div>}
                  {(w.acknowledge_1_sect || w.acknowledge_2_sect || w.acknowledge_pl_sgt) && (
                    <div><p className="text-[10px] font-display font-black uppercase tracking-widest text-primary border-b border-border pb-1 mb-2">6. Acknowledge</p>
                      <div className="grid grid-cols-4 gap-2 text-xs font-sans">
                        {w.acknowledge_1_sect && <span><span className="text-muted-foreground">1 Sect: </span>{w.acknowledge_1_sect}</span>}
                        {w.acknowledge_2_sect && <span><span className="text-muted-foreground">2 Sect: </span>{w.acknowledge_2_sect}</span>}
                        {w.acknowledge_3_sect && <span><span className="text-muted-foreground">3 Sect: </span>{w.acknowledge_3_sect}</span>}
                        {w.acknowledge_4_sect && <span><span className="text-muted-foreground">4 Sect: </span>{w.acknowledge_4_sect}</span>}
                        {w.acknowledge_pl_sgt && <span><span className="text-muted-foreground">Pl Sgt: </span>{w.acknowledge_pl_sgt}</span>}
                        {w.acknowledge_atts_1 && <span><span className="text-muted-foreground">Atts 1: </span>{w.acknowledge_atts_1}</span>}
                        {w.acknowledge_atts_2 && <span><span className="text-muted-foreground">Atts 2: </span>{w.acknowledge_atts_2}</span>}
                        {w.acknowledge_atts_3 && <span><span className="text-muted-foreground">Atts 3: </span>{w.acknowledge_atts_3}</span>}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2 border-t border-border">
                    <button onClick={() => startEdit(w)} className="inline-flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground border border-border rounded px-3 py-1.5 transition-colors"><Pencil className="w-3 h-3" /> Edit</button>
                    <button
              onClick={() => archiveToIntel(group, user, null, {
                title: `WARNO — ${w.title || "—"} (${new Date(w.op_date || w.created_date).toLocaleDateString()})`,
                doc_type: "WARNO",
                classification: "RESTRICTED",
                content: `Title: ${w.title || "—"}
Op Date: ${w.op_date || "—"}
Mission: ${w.mission || "—"}
Situation (Ground): ${w.situation_ground || "—"}
Enemy: ${w.situation_enemy || "—"}
Friendly: ${w.situation_friendly || "—"}
CSS: ${w.css || "—"}`,
                tags: ["warno"],
              }, showMsg)}
              className="inline-flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wider text-orange-400/70 hover:text-orange-400 border border-border hover:border-orange-500/40 rounded px-3 py-1.5 transition-colors"
            ><Archive className="w-3 h-3" /> Archive to Intel</button>
            <button onClick={() => remove(w.id)} className="inline-flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wider text-destructive/70 hover:text-destructive border border-border hover:border-destructive/40 rounded px-3 py-1.5 transition-colors"><Trash2 className="w-3 h-3" /> Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WField({ label, value }: { label: string; value: string }) {
  return <div><p className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground mb-0.5">{label}</p><p className="text-sm text-foreground font-sans whitespace-pre-wrap pl-2 border-l border-border">{value}</p></div>;
}

function AARsTab({ group, showMsg }: any) {
  const [aars, setAars] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const emptyForm = { op_name: "", op_date: "", op_id: "", summary: "", outcome: "INCOMPLETE", objectives_hit: 0, objectives_missed: 0, casualties: "NONE", casualties_note: "", commendations: "", recommendations: "", classification: "unclassified" };
  const [form, setForm] = useState<any>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [aarExportingId, setAarExportingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [debriefingId, setDebriefingId] = useState<string | null>(null);
  const [debriefs, setDebriefs] = useState<Record<string, string>>({});
  const load = () => { apiFetch<any[]>(`/milsimGroupsB?path=${group.id}/aars`).then(setAars).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [group.id]);
  const [ops, setOps] = useState<any[]>([]);
  useEffect(() => {
    apiFetch<any>(`/milsimGroupsB?path=${group.id}/ops`).then((d: any) => setOps((Array.isArray(d) ? d : (d?.events ?? [])).filter((o:any) => ["Active","Confirmed","Planned","Completed"].includes(o.status)))).catch(() => {});
  }, [group.id]);

  const submit = async () => {
    if (!form.op_name.trim()) return; setSaving(true);
    try {
      if (editId) {
        await apiFetch(`/milsimGroupsB?path=${group.id}/aars/${editId}`, { method: "PATCH", body: JSON.stringify(form) });
        showMsg(true, "AAR updated.");
      } else {
        // After Action Score: auto-calculated 0-100
      const _totalObjs = (form.objectives_hit ?? 0) + (form.objectives_missed ?? 0);
      const _objPct    = _totalObjs > 0 ? Math.round(((form.objectives_hit ?? 0) / _totalObjs) * 100) : 50;
      const _casScore  = form.casualties === "NONE" ? 30 : form.casualties === "LOW" ? 22 : form.casualties === "MODERATE" ? 12 : 0;
      const _outcomeScore = form.outcome === "VICTORY" ? 40 : form.outcome === "PARTIAL VICTORY" ? 28 : form.outcome === "DRAW" ? 20 : form.outcome === "DEFEAT" ? 8 : 10;
      const _detailScore  = ((form.summary?.trim().length ?? 0) > 80 ? 15 : 8) + ((form.commendations?.trim().length ?? 0) > 10 ? 10 : 4) + ((form.recommendations?.trim().length ?? 0) > 10 ? 5 : 0);
      const _aarScore = Math.min(100, Math.round(_outcomeScore + _casScore * (_objPct / 100) + _detailScore));
      const formWithScore = { ...form, ai_score: _aarScore };
      const newAAR = await apiFetch<any>(`/milsimGroupsB?path=${group.id}/aars`, { method: "POST", body: JSON.stringify(formWithScore) });
        const aarSummary = `Outcome:${form.outcome} ObjsHit:${form.objectives_hit} ObjsMissed:${form.objectives_missed} Casualties:${form.casualties}${form.casualties_note ? " — " + form.casualties_note : ""}`;
        apiFetch(`/opIntel?path=notify&group_id=${group.id}`, { method: "POST", body: JSON.stringify({ report_type: "AAR", report_id: newAAR?.id ?? newAAR?.aar?.id ?? "", filer_callsign: "S6 ACTUAL", op_name: form.op_name || "Unknown Op", summary: aarSummary }) }).catch(() => {});
        showMsg(true, "AAR filed — eyewitness notifications sent.");
      }
      setCreating(false); setEditId(null); setForm(emptyForm); load();
    } catch (e: any) { showMsg(false, e.message); } finally { setSaving(false); }
  };
  const remove = async (id: string) => {
    try { await apiFetch(`/milsimGroupsB?path=${group.id}/aars/${id}`, { method: "DELETE" }); showMsg(true, "AAR deleted."); load(); }
    catch (e: any) { showMsg(false, e.message); }
  };
  const CL: Record<string, string> = { "unclassified": "text-green-400 bg-green-500/10 border-green-500/30", "confidential": "text-blue-400 bg-blue-500/10 border-blue-500/30", "classified": "text-yellow-400 bg-yellow-500/10 border-yellow-500/30", "top-secret": "text-red-400 bg-red-500/10 border-red-500/30" };
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  const setF = (k: string) => (e: any) => setForm((f: any) => ({...f, [k]: e.target.value}));
  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground font-sans">After Action Reports — filed post-op.</p>
        {!creating && !editId && <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-4 py-2 rounded transition-all"><Plus className="w-3.5 h-3.5" /> File AAR</button>}
      </div>
      {(creating || editId !== null) && (
        <div className="bg-card border border-primary/30 rounded-lg p-6 space-y-4">
          <h3 className="font-display font-bold uppercase tracking-widest text-sm">{editId ? "Edit AAR" : "New AAR"}</h3>
          <div className="grid grid-cols-2 gap-3"><div><label className="mf-label">Op Name *</label><input value={form.op_name} onChange={setF("op_name")} className="mf-input" placeholder="Operation Iron Fist" /></div><div><label className="mf-label">Op Date</label><input type="date" value={form.op_date} onChange={setF("op_date")} className="mf-input" /></div></div>
          <div><label className="mf-label">Linked Op</label><select value={form.op_id ?? ""} onChange={setF("op_id")} className="mf-input"><option value="">— No op —</option>{ops.map((o:any) => <option key={o.id} value={o.id}>{o.title ?? o.name}</option>)}</select></div>
          <div><label className="mf-label">Classification</label><select value={form.classification} onChange={setF("classification")} className="mf-input">{["unclassified","confidential","classified","top-secret"].map(c => <option key={c} value={c}>{c.replace("-"," ").toUpperCase()}</option>)}</select></div>
          <div><label className="mf-label">Summary</label><textarea rows={3} value={form.summary} onChange={setF("summary")} className="mf-input resize-none" placeholder="Overall mission summary..." /></div>
          {/* Outcome selector */}
          <div>
            <label className="mf-label">Op Outcome</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {(["VICTORY","PARTIAL VICTORY","DRAW","DEFEAT","INCOMPLETE"] as const).map(o => {
                const oc: Record<string, string> = { VICTORY: "text-green-400 border-green-500/50 bg-green-500/10", "PARTIAL VICTORY": "text-lime-400 border-lime-500/50 bg-lime-500/10", DRAW: "text-yellow-400 border-yellow-500/50 bg-yellow-500/10", DEFEAT: "text-red-400 border-red-500/50 bg-red-500/10", INCOMPLETE: "text-muted-foreground border-border bg-secondary/40" };
                return (
                  <button key={o} type="button" onClick={() => setForm((f: any) => ({...f, outcome: o}))}
                    className={`px-3 py-1.5 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${form.outcome === o ? oc[o] : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
                    {o}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Objectives — numbered inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mf-label">Objectives Hit <span className="text-muted-foreground font-normal">(count)</span></label>
              <input type="number" min={0} max={99} value={form.objectives_hit} onChange={e => setForm((f: any) => ({...f, objectives_hit: parseInt(e.target.value) || 0}))} className="mf-input w-full" placeholder="0" />
            </div>
            <div>
              <label className="mf-label">Objectives Missed <span className="text-muted-foreground font-normal">(count)</span></label>
              <input type="number" min={0} max={99} value={form.objectives_missed} onChange={e => setForm((f: any) => ({...f, objectives_missed: parseInt(e.target.value) || 0}))} className="mf-input w-full" placeholder="0" />
            </div>
          </div>

          {/* Casualties — standardised enum */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mf-label">Casualty Level</label>
              <div className="flex gap-2 mt-1">
                {(["NONE","LOW","MODERATE","HEAVY"] as const).map(c => {
                  const cc: Record<string, string> = { NONE: "text-green-400 border-green-500/50 bg-green-500/10", LOW: "text-yellow-400 border-yellow-500/50 bg-yellow-500/10", MODERATE: "text-orange-400 border-orange-500/50 bg-orange-500/10", HEAVY: "text-red-400 border-red-500/50 bg-red-500/10" };
                  return (
                    <button key={c} type="button" onClick={() => setForm((f: any) => ({...f, casualties: c}))}
                      className={`flex-1 py-1.5 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${form.casualties === c ? cc[c] : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="mf-label">Casualty Notes <span className="text-muted-foreground font-normal">(optional)</span></label>
              <input value={form.casualties_note ?? ""} onChange={setF("casualties_note")} className="mf-input w-full" placeholder="e.g. 1 WIA extracted, 2 KIA confirmed" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3"><div><label className="mf-label">Commendations</label><textarea rows={2} value={form.commendations} onChange={setF("commendations")} className="mf-input resize-none" /></div><div><label className="mf-label">Recommendations</label><textarea rows={2} value={form.recommendations} onChange={setF("recommendations")} className="mf-input resize-none" /></div></div>
          <div className="flex gap-2">
            <button onClick={submit} disabled={saving || !form.op_name.trim()} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded transition-all disabled:opacity-50">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {editId ? "Update" : "File AAR"}</button>
            <button onClick={() => { setCreating(false); setEditId(null); setForm(emptyForm); }} className="px-4 py-2 border border-border text-muted-foreground rounded text-xs font-display uppercase hover:text-foreground">Cancel</button>
          </div>
        </div>
      )}
      {aars.length === 0 && !creating ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg text-muted-foreground"><ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="font-display text-sm uppercase tracking-widest">No AARs filed</p></div>
      ) : (
        <div className="space-y-3">
          {aars.map((a: any) => (
            <div key={a.id} className="bg-card border border-border rounded-lg overflow-hidden">
              <button onClick={() => setExpandedId(expandedId === a.id ? null : a.id)} className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-secondary/20 transition-colors text-left">
                <div className="flex items-center gap-3 flex-wrap"><span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${CL[a.classification] ?? ""}`}>{(a.classification ?? "unclassified").replace("-"," ")}</span><span className="font-display font-bold text-sm text-foreground">{a.op_name}</span>{a.op_date && <span className="text-xs text-muted-foreground">{format(new Date(a.op_date + "T00:00:00"), "MMM dd, yyyy")}</span>}{a.ai_score != null && <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${a.ai_score >= 75 ? "text-green-400 bg-green-500/10 border-green-500/30" : a.ai_score >= 45 ? "text-amber-400 bg-amber-500/10 border-amber-500/30" : "text-red-400 bg-red-500/10 border-red-500/30"}`}>Score: {a.ai_score}</span>}</div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={async e => { e.stopPropagation(); setAarExportingId(a.id); try { const url = await generateAARDoc(a, group.name); window.open(url, "_blank"); } catch(err: any) { showMsg(false, "Doc export: " + err.message); } finally { setAarExportingId(null); } }} title="Export AAR to Google Doc" className="p-1.5 text-muted-foreground hover:text-blue-400 transition-colors">{aarExportingId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[10px] font-bold">DOC</span>}</button>
                  <button onClick={e => { e.stopPropagation(); setEditId(a.id); setForm({ op_name: a.op_name, op_date: a.op_date?.split("T")[0] ?? "", summary: a.summary ?? a.content ?? "", outcome: a.outcome ?? "INCOMPLETE", objectives_hit: a.objectives_hit ?? 0, objectives_missed: a.objectives_missed ?? 0, casualties: a.casualties ?? "NONE", casualties_note: a.casualties_note ?? "", commendations: a.commendations ?? "", recommendations: a.recommendations ?? "", classification: a.classification ?? "unclassified" }); }} className="p-1.5 text-muted-foreground hover:text-primary transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                  <button
                    onClick={e => { e.stopPropagation(); archiveToIntel(group, user, null, {
                      title: `AAR — ${a.op_name || "Op"} (${a.outcome || "—"}) ${a.op_date ? new Date(a.op_date + "T00:00:00").toLocaleDateString() : ""}`,
                      doc_type: "AAR",
                      classification: a.classification === "top-secret" ? "TOP SECRET" : a.classification === "classified" ? "SECRET" : a.classification === "confidential" ? "RESTRICTED" : "UNCLASSIFIED",
                      content: `Op: ${a.op_name || "—"}
Date: ${a.op_date || "—"}
Outcome: ${a.outcome || "—"}
Objectives Hit: ${a.objectives_hit ?? 0} / Missed: ${a.objectives_missed ?? 0}
Casualties: ${a.casualties || "—"} ${a.casualties_note ? "— " + a.casualties_note : ""}

Summary:
${a.summary ?? a.content ?? "—"}

Lessons Learned: ${a.recommendations || "—"}`,
                      tags: ["aar", (a.op_name || "").toLowerCase(), (a.outcome || "").toLowerCase()].filter(Boolean),
                    }, showMsg); }}
                    title="Archive to J2 Intel"
                    className="p-1.5 text-muted-foreground hover:text-orange-400 transition-colors"
                  ><Archive className="w-3.5 h-3.5" /></button>
                  <button onClick={e => { e.stopPropagation(); remove(a.id); }} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  {expandedId === a.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </div>
              </button>
              {expandedId === a.id && (
                <div className="border-t border-border p-5 space-y-4 bg-secondary/10">
                  {(a.summary ?? a.content) && <AARField label="Summary" value={a.summary ?? a.content} />}
                  {(a.objectives_hit !== undefined || a.objectives_missed !== undefined) && (
                    <div className="flex gap-4">
                      <div className="text-xs"><span className="font-display font-bold uppercase tracking-wider text-green-400">Objs Hit: </span><span className="font-mono text-foreground">{a.objectives_hit ?? 0}</span></div>
                      <div className="text-xs"><span className="font-display font-bold uppercase tracking-wider text-red-400">Objs Missed: </span><span className="font-mono text-foreground">{a.objectives_missed ?? 0}</span></div>
                      {((a.objectives_hit ?? 0) + (a.objectives_missed ?? 0)) > 0 && <div className="text-xs"><span className="font-display font-bold uppercase tracking-wider text-muted-foreground">Success: </span><span className="font-mono text-foreground">{Math.round(((a.objectives_hit ?? 0) / ((a.objectives_hit ?? 0) + (a.objectives_missed ?? 0))) * 100)}%</span></div>}
                    </div>
                  )}
                  {a.casualties && <div className="flex items-center gap-2"><span className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground">Casualties:</span><span className={`text-xs font-display font-bold uppercase px-2 py-0.5 rounded border ${a.casualties === "HEAVY" ? "text-red-400 bg-red-500/10 border-red-500/30" : a.casualties === "MODERATE" ? "text-orange-400 bg-orange-500/10 border-orange-500/30" : a.casualties === "LOW" ? "text-yellow-400 bg-yellow-500/10 border-yellow-500/30" : "text-green-400 bg-green-500/10 border-green-500/30"}`}>{a.casualties}</span>{a.casualties_note && <span className="text-xs text-muted-foreground font-sans">{a.casualties_note}</span>}</div>}
                  {a.commendations && <AARField label="Commendations" value={a.commendations} />}
                  {a.recommendations && <AARField label="Recommendations" value={a.recommendations} />}
                  {debriefs[a.id] && (
                    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-2">
                      <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary flex items-center gap-1.5"><Zap className="w-3 h-3" /> AI Op Debrief</p>
                      <p className="text-xs text-foreground font-sans leading-relaxed whitespace-pre-wrap">{debriefs[a.id]}</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-border">
                    <p className="text-xs text-muted-foreground">Filed by {a.created_by} · {formatDistanceToNow(new Date(a.created_date ?? a.created_at ?? Date.now()), { addSuffix: true })}</p>
                    <div className="flex items-center gap-2">
                      {(a.eyewitness_count ?? 0) > 0 && <span className="text-[10px] font-display font-bold uppercase tracking-widest text-primary px-2 py-0.5 bg-primary/10 border border-primary/30 rounded">{a.eyewitness_count} EYEWITNESS{(a.eyewitness_count ?? 0) > 1 ? "ES" : ""}</span>}
                      <button
                        onClick={async () => {
                          setDebriefingId(a.id);
                          try {
                            const prompt = `You are a military operations analyst. Analyse this After Action Report and produce a structured debrief.

Op: ${a.op_name}
Outcome: ${a.outcome}
Objectives Hit: ${a.objectives_hit ?? 0}, Missed: ${a.objectives_missed ?? 0}
Casualties: ${a.casualties}${a.casualties_note ? " — " + a.casualties_note : ""}
Summary: ${a.summary ?? a.content ?? "Not provided"}
Commendations: ${a.commendations ?? "None"}
Recommendations: ${a.recommendations ?? "None"}

Produce a concise debrief covering:
1. EXECUTION ASSESSMENT — what went right and what went wrong
2. KEY FAILURE POINTS — specific tactical or command breakdowns
3. TRAINING PRIORITIES — top 2-3 areas the unit should drill before next op
4. COMMANDER'S NOTES — one paragraph overall assessment

Keep it tight, military in tone, no fluff.`;
                            const res = await fetch("https://api.base44.com/api/apps/69bf52c997cae5d4cff87ae4/ai/complete", {
                              method: "POST",
                              headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("tag_auth_token") ?? ""}` },
                              body: JSON.stringify({ prompt, max_tokens: 600 })
                            });
                            const d = await res.json();
                            const text = d.completion ?? d.text ?? d.result ?? "No response generated.";
                            setDebriefs(prev => ({ ...prev, [a.id]: text }));
                          } catch { } finally { setDebriefingId(null); }
                        }}
                        disabled={debriefingId === a.id}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-yellow-500/40 bg-yellow-500/10 text-yellow-400 text-[10px] font-display font-bold uppercase tracking-widest hover:bg-yellow-500/20 transition-colors disabled:opacity-50">
                        {debriefingId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                        {debriefingId === a.id ? "Analysing..." : debriefs[a.id] ? "Re-Debrief" : "AI Debrief"}
                      </button>
                      <button onClick={() => apiFetch("/opIntel?path=confirm", { method: "POST", body: JSON.stringify({ report_type: "AAR", report_id: a.id, note: "" }) }).then(() => { showMsg("Eyewitness confirmation recorded", "success"); load(); }).catch((e: any) => showMsg(e.message, "error"))}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-primary/40 bg-primary/10 text-primary text-[10px] font-display font-bold uppercase tracking-widest hover:bg-primary/20 transition-colors">
                        <Eye className="w-3 h-3" /> Confirm Eyewitness
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Briefings ────────────────────────────────────────────────────────────────

/* ─── LACE Report Tab ─────────────────────────────────────────────────────── */
/* ─── LACE Report Tab — NATO Standard ───────────────────────────────────────
   LACE: Liquid · Ammo · Casualty · Equipment
   Colour codes: GREEN (good — shown for visibility), ORANGE (low/degraded), RED (out/critical)
   DTG format, FROM/TO callsigns, slate-card layout
──────────────────────────────────────────────────────────────────────────── */
function LaceTab({ group, showMsg }: { group: any; showMsg: (m: string, t?: "success" | "error") => void }) {
  const { user } = useAuth();
  const [reports, setReports] = useState<any[]>([]);
  const [ops, setOps] = useState<any[]>([]);
  const [roster, setRoster] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const now = new Date();
  const dtgNow = `${String(now.getUTCDate()).padStart(2,'0')}${String(now.getUTCHours()).padStart(2,'0')}${String(now.getUTCMinutes()).padStart(2,'0')}Z ${['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'][now.getUTCMonth()]} ${now.getUTCFullYear()}`;

  const emptyForm = {
    from_callsign: "", to_callsign: "", op_id: "", op_name: "",
    report_time: dtgNow,
    liquid: "GREEN", liquid_note: "",
    ammo: "GREEN", ammo_note: "",
    casualty: "GREEN", casualty_note: "",
    equipment: "GREEN", equipment_note: "",
  };
  const [form, setForm] = useState<any>(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [d, opsData, rosterData] = await Promise.all([
        apiFetch(`/milsimLace?path=list&group_id=${group.id}`),
        apiFetch(`/activityCalendar?path=list&group_id=${group.id}`),
        apiFetch(`/milsimGroups?path=mine/own`),
      ]);
      setReports(d.lace_reports ?? []);
      setOps((opsData.events ?? []).filter((o: any) => ["Active","Confirmed","Planned"].includes(o.status)));
      setRoster((rosterData.roster ?? []).filter((m:any) => m.status === "Active"));
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [group.id]);

  const save = async () => {
    if (!form.from_callsign) { showMsg("FROM callsign required", "error"); return; }
    setSaving(true);
    try {
      const payload = {
        group_id: group.id, reported_by: user?.username ?? "Unknown",
        callsign: form.from_callsign,
        report_time: form.report_time,
        op_id: form.op_id, op_name: form.op_name,
        liquid: form.liquid, liquid_note: form.liquid_note,
        ammo: form.ammo, ammo_note: form.ammo_note,
        casualty: form.casualty, casualty_note: form.casualty_note,
        equipment: form.equipment, equipment_note: form.equipment_note,
      };
      if (payload.op_id) { const op = ops.find((o:any) => o.id === payload.op_id); if (op) payload.op_name = op.title ?? op.name ?? ""; }
      const newLace = await apiFetch<any>("/milsimLace?path=create", { method: "POST", body: JSON.stringify(payload) });
      // Dispatch eyewitness notifications to all active roster members
      const laceSummary = `L:${payload.liquid} A:${payload.ammo} C:${payload.casualty} E:${payload.equipment}${payload.casualty_note ? " — " + payload.casualty_note : ""}`;
      apiFetch(`/opIntel?path=notify&group_id=${group.id}`, { method: "POST", body: JSON.stringify({ report_type: "LACE", report_id: newLace?.lace_report?.id ?? "", filer_callsign: payload.callsign, op_name: payload.op_name || "Unknown Op", summary: laceSummary }) }).catch(() => {});
      showMsg("LACE filed — eyewitness notifications sent", "success"); setShowForm(false); setForm({ ...emptyForm, report_time: dtgNow }); load();
    } catch (e: any) { showMsg(e.message, "error"); }
    setSaving(false);
  };

  const STATUS: Record<string, { color: string; bg: string; border: string; dot: string; desc: string }> = {
    GREEN:  { color: "text-green-400",  bg: "bg-green-500/10",  border: "border-green-500/40",  dot: "bg-green-400",  desc: "All Good / Fully Operational" },
    ORANGE: { color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/40", dot: "bg-orange-400", desc: "Low / Degraded — able to continue" },
    RED:    { color: "text-red-400",    bg: "bg-red-500/10",    border: "border-red-500/40",    dot: "bg-red-400",    desc: "Out / Critical — resupply required" },
  };

  const FIELDS: { key: "liquid"|"ammo"|"casualty"|"equipment"; letter: string; label: string; sublabel: string; noteKey: "liquid_note"|"ammo_note"|"casualty_note"|"equipment_note"; notePlaceholder: string }[] = [
    { key: "liquid",    letter: "L", label: "LIQUID",    sublabel: "Water / hydration supply",          noteKey: "liquid_note",    notePlaceholder: "e.g. 2x water bottles remaining per man" },
    { key: "ammo",      letter: "A", label: "AMMO",      sublabel: "Ammunition across all weapons",     noteKey: "ammo_note",      notePlaceholder: "e.g. Orange 5.56, Red AT4" },
    { key: "casualty",  letter: "C", label: "CASUALTY",  sublabel: "Friendly casualty level",           noteKey: "casualty_note",  notePlaceholder: "e.g. 2 WIA walking, 1 KIA extracted" },
    { key: "equipment", letter: "E", label: "EQUIPMENT", sublabel: "Mission-critical gear status",       noteKey: "equipment_note", notePlaceholder: "e.g. Orange bandages, Red NVG" },
  ];

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-display font-bold text-lg uppercase tracking-widest">LACE Report</h2>
            <span className="text-xs font-display font-bold uppercase tracking-widest px-2 py-0.5 bg-primary/10 border border-primary/30 text-primary rounded">NATO Standard</span>
          </div>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Liquid · Ammo · Casualty · Equipment — section status to higher command.</p>
        </div>
        <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-2 px-3 py-1.5 bg-primary/20 border border-primary/40 text-primary rounded font-display text-xs uppercase tracking-widest hover:bg-primary/30 transition-colors">
          <Plus className="w-3.5 h-3.5" /> File LACE
        </button>
      </div>

      {/* Colour Legend */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {Object.entries(STATUS).map(([k, s]) => (
          <div key={k} className={`flex items-start gap-2.5 p-2.5 rounded border ${s.bg} ${s.border}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${s.dot} mt-0.5 shrink-0`} />
            <div><p className={`font-display font-black text-xs uppercase tracking-widest ${s.color}`}>{k}</p><p className="text-xs text-muted-foreground font-sans">{s.desc}</p></div>
          </div>
        ))}
      </div>

      {/* Form — styled as slate card */}
      {showForm && (
        <div className="border border-primary/40 rounded-lg overflow-hidden">
          {/* Slate card header */}
          <div className="bg-primary/15 border-b border-primary/30 px-5 py-3 flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="font-display font-black text-sm uppercase tracking-widest text-primary">LACE REPORT — SLATE CARD</p>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">Complete all fields. Report to next higher element.</p>
            </div>
            <span className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground border border-border px-2 py-1 rounded">UNCLASSIFIED</span>
          </div>

          <div className="p-5 space-y-5 bg-card">
            {/* Header fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-4 border-b border-border">
              <MField label="DTG (Date-Time Group)">
                <input value={form.report_time} onChange={e => setForm((f:any) => ({...f, report_time: e.target.value}))} placeholder={dtgNow} className="mf-input w-full font-mono text-xs" />
              </MField>
              <MField label="FROM (Callsign) *">
                <select value={form.from_callsign} onChange={e => setForm((f:any) => ({...f, from_callsign: e.target.value}))} className="mf-input w-full">
                  <option value="">— Select callsign —</option>
                  {roster.map((m:any) => <option key={m.id} value={m.callsign}>{m.callsign}{m.rank_name ? ` (${m.rank_name})` : ""}</option>)}
                </select>
              </MField>
              <MField label="TO (Higher Element)">
                <input value={form.to_callsign} onChange={e => setForm((f:any) => ({...f, to_callsign: e.target.value}))} placeholder="e.g. PLATOON ACTUAL / Coy HQ" className="mf-input w-full" />
              </MField>
            </div>
            <MField label="Linked Op (Optional)">
              <select value={form.op_id} onChange={e => setForm((f:any) => ({...f, op_id: e.target.value}))} className="mf-input w-full">
                <option value="">— No op —</option>
                {ops.map((o:any) => <option key={o.id} value={o.id}>{o.title ?? o.name}</option>)}
              </select>
            </MField>

            {/* LACE fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FIELDS.map(f => (
                <div key={f.key} className="border border-border rounded-lg overflow-hidden">
                  <div className="bg-secondary/40 px-4 py-2 border-b border-border flex items-center gap-2">
                    <span className={`w-7 h-7 rounded flex items-center justify-center font-display font-black text-sm ${STATUS[form[f.key]]?.bg} ${STATUS[form[f.key]]?.color} border ${STATUS[form[f.key]]?.border}`}>{f.letter}</span>
                    <div>
                      <p className="font-display font-black text-xs uppercase tracking-widest">{f.label}</p>
                      <p className="text-xs text-muted-foreground font-sans">{f.sublabel}</p>
                    </div>
                  </div>
                  <div className="p-3 space-y-2.5">
                    <div className="flex gap-2">
                      {(f.key === "casualty" ? ["NONE","LOW","MODERATE","HEAVY"] as const : ["GREEN","ORANGE","RED"] as const).map((status: string) => {
                        const CAS_CFG: Record<string, { color: string; bg: string; border: string }> = { NONE: { color: "text-green-400", bg: "bg-green-500/10", border: "border-green-500/40" }, LOW: { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/40" }, MODERATE: { color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/40" }, HEAVY: { color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/40" } };
                        const cfg = f.key === "casualty" ? CAS_CFG[status] : STATUS[status as "GREEN"|"ORANGE"|"RED"];
                        const active = form[f.key] === status;
                        return (
                          <button key={status} onClick={() => setForm((prev:any) => ({...prev, [f.key]: status}))}
                            className={`flex-1 py-1.5 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 ${active ? `${cfg.bg} ${cfg.border} ${cfg.color} ring-1 ring-inset ${cfg.border}` : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
                            <span className={`w-2 h-2 rounded-full ${active ? cfg.dot : "bg-muted-foreground/30"}`} />{status}
                          </button>
                        );
                      })}
                    </div>
                    <input value={form[f.noteKey]} onChange={e => setForm((prev:any) => ({...prev, [f.noteKey]: e.target.value}))} placeholder={f.notePlaceholder} className="mf-input w-full text-xs font-sans" />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2 pt-2 border-t border-border">
              <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-1.5 bg-primary text-primary-foreground rounded font-display text-xs uppercase tracking-widest hover:bg-primary/90">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Transmit Report
              </button>
              <button onClick={() => { setShowForm(false); setForm({...emptyForm, report_time: dtgNow}); }} className="px-4 py-1.5 border border-border rounded font-display text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Records */}
      {reports.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg"><Radio className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm font-sans">No LACE reports filed.</p></div>
      ) : (
        <div className="space-y-3">
          {reports.map((r:any) => {
            const vals = [r.liquid, r.ammo, r.casualty, r.equipment];
            const hasRed = vals.some(v => v === "RED");
            const hasOrange = vals.some(v => v === "ORANGE");
            const bCls = hasRed ? "border-red-500/50" : hasOrange ? "border-orange-500/40" : "border-green-500/30";
            return (
              <div key={r.id} className={`border ${bCls} rounded-lg overflow-hidden bg-card`}>
                <div className="bg-secondary/30 border-b border-border px-4 py-2 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <Radio className="w-4 h-4 text-primary shrink-0" />
                    <div>
                      <span className="font-display font-black text-sm uppercase tracking-widest">{r.callsign}</span>
                      {r.op_name && <span className="ml-2 text-xs text-muted-foreground font-sans">/ {r.op_name}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground font-mono">{r.report_time || new Date(r.created_date).toUTCString().slice(5,22)}</span>
                    <span className="text-xs text-muted-foreground font-sans">{r.reported_by}</span>
                    <button onClick={async () => { if(!confirm("Delete?")) return; await apiFetch("/milsimLace?path=delete",{method:"POST",body:JSON.stringify({id:r.id})}); load(); }} className="text-muted-foreground hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-4">
                  {FIELDS.map((f,i) => {
                    const val:string = (r[f.key] ?? "GREEN").toUpperCase();
                    const cfg = STATUS[val] ?? STATUS["GREEN"];
                    return (
                      <div key={f.key} className={`p-3 space-y-1 ${i < 3 ? "border-r border-border" : ""}`}>
                        <div className="flex items-center gap-1.5">
                          <span className={`w-6 h-6 rounded flex items-center justify-center font-display font-black text-xs ${cfg.bg} ${cfg.color} border ${cfg.border}`}>{f.letter}</span>
                          <span className="text-xs text-muted-foreground font-display font-bold uppercase tracking-wider">{f.label}</span>
                        </div>
                        <div className={`flex items-center gap-1.5 font-display font-black text-xs uppercase tracking-widest ${cfg.color}`}>
                          <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />{val}
                        </div>
                        {r[f.noteKey] && <p className="text-xs text-muted-foreground font-sans">{r[f.noteKey]}</p>}
                      </div>
                    );
                  })}
                </div>
                {/* Eyewitness confirmation row */}
                <div className="flex items-center justify-between px-4 py-2 border-t border-border bg-secondary/10">
                  <span className="text-[10px] text-muted-foreground font-display uppercase tracking-widest">
                    {(r.eyewitness_count ?? 0) > 0 ? `${r.eyewitness_count} eyewitness${(r.eyewitness_count ?? 0) > 1 ? "es" : ""} confirmed` : "Awaiting eyewitness confirmation"}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => apiFetch("/opIntel?path=confirm", { method: "POST", body: JSON.stringify({ report_type: "LACE", report_id: r.id }) }).then(() => { showMsg("Eyewitness confirmation recorded", "success"); load(); }).catch((e: any) => showMsg(e.message, "error"))}
                      className="flex items-center gap-1 px-2 py-0.5 rounded border border-primary/40 bg-primary/10 text-primary text-[10px] font-display font-bold uppercase tracking-widest hover:bg-primary/20 transition-colors">
                      <Eye className="w-3 h-3" /> Confirm
                    </button>
                    <button
                      onClick={() => archiveToIntel(group, user, roster?.[0] ?? null, {
                        title: `LACE — ${r.callsign} / ${r.op_name || "Op"} (${r.report_time || new Date(r.created_date).toLocaleDateString()})`,
                        doc_type: "LACE REPORT",
                        classification: "RESTRICTED",
                        content: `Callsign: ${r.callsign}
Op: ${r.op_name || "—"}
Time: ${r.report_time || "—"}
Liquid: ${r.liquid || "—"} ${r.liquid_note ? "— " + r.liquid_note : ""}
Ammo: ${r.ammo || "—"} ${r.ammo_note ? "— " + r.ammo_note : ""}
Casualty: ${r.casualty || "—"} ${r.casualty_note ? "— " + r.casualty_note : ""}
Equipment: ${r.equipment || "—"} ${r.equipment_note ? "— " + r.equipment_note : ""}`,
                        tags: ["lace", (r.op_name || "").toLowerCase()].filter(Boolean),
                      }, showMsg)}
                      className="flex items-center gap-1 px-2 py-0.5 rounded border border-orange-500/30 text-orange-400 text-[10px] font-display font-bold uppercase tracking-widest hover:bg-orange-500/10 transition-colors"
                    >
                      <Archive className="w-3 h-3" /> Archive
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── SITREP Tab — NATO Standard ─────────────────────────────────────────────
   NATO SITREP: DTG · FROM · TO · LOCATION · ACTIVITY · REMARKS
   Compact slate card — answers "where are you?" and "what are you doing?"
──────────────────────────────────────────────────────────────────────────── */
/* ─── SITREP Tab — NATO Slate Card (ABCDE format) ────────────────────────────
   A = When  B = Where  C = What it is  D = What is it doing  E = What are you doing about it
   Gold/amber table layout matching real slate card
──────────────────────────────────────────────────────────────────────────── */
function SitrepTab({ group, showMsg }: { group: any; showMsg: (m: string, t?: "success" | "error") => void }) {
  const { user } = useAuth();
  const [reports, setReports] = useState<any[]>([]);
  const [ops, setOps] = useState<any[]>([]);
  const [roster, setRoster] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const now = new Date();
  const dtgNow = `${String(now.getUTCDate()).padStart(2,'0')}${String(now.getUTCHours()).padStart(2,'0')}${String(now.getUTCMinutes()).padStart(2,'0')}Z ${['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'][now.getUTCMonth()]} ${now.getUTCFullYear()}`;

  const emptyForm = {
    from_callsign: "", to_callsign: "", op_id: "", op_name: "",
    report_time: dtgNow,
    // ABCDE
    field_a: "",  // When (DTG of event/sighting)
    field_b: "",  // Where (Grid/Location)
    field_c: "",  // What it is (Description of subject)
    field_d: "",  // What is it doing (Activity)
    field_e: "",  // What are you doing about it (Own action)
    // Supplementary
    enemy_contact: "NONE",
    friendly_casualties: "NONE", casualty_notes: "",
    mission_status: "ON TRACK",
  };
  const [form, setForm] = useState<any>(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [d, opsData, rosterData] = await Promise.all([
        apiFetch(`/milsimSitrep?path=list&group_id=${group.id}`),
        apiFetch(`/activityCalendar?path=list&group_id=${group.id}`),
        apiFetch(`/milsimGroups?path=mine/own`),
      ]);
      setReports(d.sitreps ?? []);
      setOps((opsData.events ?? []).filter((o:any) => ["Active","Confirmed","Planned"].includes(o.status)));
      setRoster((rosterData.roster ?? []).filter((m:any) => m.status === "Active"));
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [group.id]);

  const save = async () => {
    if (!form.from_callsign) { showMsg("FROM callsign required", "error"); return; }
    if (!form.field_b) { showMsg("Where (B — Location) is mandatory", "error"); return; }
    setSaving(true);
    try {
      const payload: any = {
        group_id: group.id, reported_by: user?.username ?? "Unknown",
        callsign: form.from_callsign, report_time: form.report_time,
        op_id: form.op_id, op_name: form.op_name,
        // Map ABCDE to existing entity fields
        position: form.field_b,
        activity: form.field_d,
        enemy_contact: form.enemy_contact,
        friendly_casualties: form.friendly_casualties, casualty_notes: form.casualty_notes,
        mission_status: form.mission_status,
        next_action: form.field_e,
        additional_info: JSON.stringify({
          a: form.field_a,
          c: form.field_c,
          b: form.field_b,
          d: form.field_d,
          e: form.field_e,
        }),
      };
      if (payload.op_id) { const op = ops.find((o:any) => o.id === payload.op_id); if (op) payload.op_name = op.title ?? op.name ?? ""; }
      const newSitrep = await apiFetch<any>("/milsimSitrep?path=create", { method: "POST", body: JSON.stringify(payload) });
      const sitrepSummary = `Contact:${payload.enemy_contact} Mission:${payload.mission_status} Cas:${payload.friendly_casualties}${payload.next_action ? " — " + payload.next_action.slice(0, 120) : ""}`;
      apiFetch(`/opIntel?path=notify&group_id=${group.id}`, { method: "POST", body: JSON.stringify({ report_type: "SITREP", report_id: newSitrep?.sitrep?.id ?? "", filer_callsign: payload.callsign, op_name: payload.op_name || "Unknown Op", summary: sitrepSummary }) }).catch(() => {});
      showMsg("SITREP transmitted — eyewitness notifications sent", "success"); setShowForm(false); setForm({...emptyForm, report_time: dtgNow}); load();
    } catch (e: any) { showMsg(e.message, "error"); }
    setSaving(false);
  };

  const CONTACT: Record<string, { color: string; bg: string; border: string }> = {
    NONE:      { color: "text-green-400",  bg: "bg-green-500/10",  border: "border-green-500/40" },
    POSSIBLE:  { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/40" },
    CONFIRMED: { color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/40" },
    ENGAGED:   { color: "text-red-400",    bg: "bg-red-500/10",    border: "border-red-500/40" },
  };
  const MISSION: Record<string, { color: string; bg: string; border: string }> = {
    "ON TRACK":    { color: "text-green-400",  bg: "bg-green-500/10",  border: "border-green-500/40" },
    "DELAYED":     { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/40" },
    "COMPROMISED": { color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/40" },
    "ABORT":       { color: "text-red-400",    bg: "bg-red-500/10",    border: "border-red-500/40" },
  };
  const CAS_COLOR: Record<string, string> = { NONE:"text-green-400", LOW:"text-yellow-400", MODERATE:"text-orange-400", HEAVY:"text-red-400" };

  // The 5 ABCDE rows
  const ROWS: { letter: string; label: string; sublabel: string; field: string; placeholder: string; rows?: number }[] = [
    { letter: "A", label: "When?",                    sublabel: "DTG of the event / sighting / activity",     field: "field_a", placeholder: `e.g. ${dtgNow}` },
    { letter: "B", label: "Where?",                   sublabel: "Grid reference or location descriptor",       field: "field_b", placeholder: "e.g. Grid 4523, Compound 6-6, Treeline NE of OBJ" },
    { letter: "C", label: "What is it?",              sublabel: "Description — type, size, composition",       field: "field_c", placeholder: "e.g. Enemy infantry section, ~8 personnel, light weapons" },
    { letter: "D", label: "What is it doing?",        sublabel: "Observed activity or direction of movement",  field: "field_d", placeholder: "e.g. Patrolling south along Route AMBER, no apparent awareness" },
    { letter: "E", label: "What are you doing about it?", sublabel: "Own element's action or intent",         field: "field_e", placeholder: "e.g. Breaking contact, requesting fire support, holding position", rows: 2 },
  ];

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-display font-bold text-lg uppercase tracking-widest">SITREP</h2>
            <span className="text-xs font-display font-bold uppercase tracking-widest px-2 py-0.5 bg-amber-500/15 border border-amber-500/40 text-amber-400 rounded">NATO Slate Card</span>
          </div>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Situation Report — A=When · B=Where · C=What is it · D=What is it doing · E=What are you doing about it</p>
        </div>
        <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-2 px-3 py-1.5 bg-primary/20 border border-primary/40 text-primary rounded font-display text-xs uppercase tracking-widest hover:bg-primary/30 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Send SITREP
        </button>
      </div>

      {showForm && (
        <div className="border border-amber-500/50 rounded-lg overflow-hidden">
          {/* Slate card header — amber to match real card */}
          <div className="bg-amber-500/20 border-b border-amber-500/40 px-5 py-3 flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="font-display font-black text-sm uppercase tracking-widest text-amber-400">SITREP — SLATE CARD</p>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">AUTHORITY: NATO Standard · 4IB 2 Signals Regiment Format</p>
            </div>
            <span className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground border border-border px-2 py-1 rounded">UNCLASSIFIED</span>
          </div>

          <div className="bg-card">
            {/* FROM / TO / DTG header row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border-b border-amber-500/20">
              <MField label="DTG (Report Time)">
                <input value={form.report_time} onChange={e => setForm((f:any) => ({...f, report_time: e.target.value}))} placeholder={dtgNow} className="mf-input w-full font-mono text-xs" />
              </MField>
              <MField label="FROM (Callsign) *">
                <select value={form.from_callsign} onChange={e => setForm((f:any) => ({...f, from_callsign: e.target.value}))} className="mf-input w-full">
                  <option value="">— Select callsign —</option>
                  {roster.map((m:any) => <option key={m.id} value={m.callsign}>{m.callsign}{m.rank_name ? ` (${m.rank_name})` : ""}</option>)}
                </select>
              </MField>
              <MField label="TO (Higher Element)">
                <input value={form.to_callsign} onChange={e => setForm((f:any) => ({...f, to_callsign: e.target.value}))} placeholder="e.g. PLATOON ACTUAL" className="mf-input w-full" />
              </MField>
            </div>
            <div className="px-4 pb-4 border-b border-amber-500/20">
              <MField label="Linked Op (Optional)">
                <select value={form.op_id} onChange={e => setForm((f:any) => ({...f, op_id: e.target.value}))} className="mf-input w-full">
                  <option value="">— No op —</option>
                  {ops.map((o:any) => <option key={o.id} value={o.id}>{o.title ?? o.name}</option>)}
                </select>
              </MField>
            </div>

            {/* ABCDE table — styled to match the amber slate card */}
            <div className="border-b border-amber-500/20">
              {ROWS.map((row, i) => (
                <div key={row.letter} className={`flex border-b border-amber-500/20 last:border-0 ${i % 2 === 0 ? "" : ""}`}>
                  {/* Letter column */}
                  <div className="w-12 shrink-0 flex items-center justify-center bg-amber-500/15 border-r border-amber-500/30">
                    <span className="font-display font-black text-lg text-amber-400">{row.letter}</span>
                  </div>
                  {/* Label column */}
                  <div className="w-44 shrink-0 flex flex-col justify-center px-3 py-3 bg-amber-500/10 border-r border-amber-500/30">
                    <p className="font-display font-black text-xs uppercase tracking-widest text-amber-300">{row.label}</p>
                    <p className="text-xs text-muted-foreground font-sans mt-0.5 leading-tight">{row.sublabel}</p>
                  </div>
                  {/* Input column */}
                  <div className="flex-1 p-2.5">
                    {row.rows ? (
                      <textarea
                        value={form[row.field]}
                        onChange={e => setForm((f:any) => ({...f, [row.field]: e.target.value}))}
                        rows={row.rows}
                        placeholder={row.placeholder}
                        className="mf-input w-full resize-none text-sm"
                      />
                    ) : (
                      <input
                        value={form[row.field]}
                        onChange={e => setForm((f:any) => ({...f, [row.field]: e.target.value}))}
                        placeholder={row.placeholder}
                        className="mf-input w-full text-sm"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Supplementary status strip */}
            <div className="p-4 border-t border-amber-500/20">
              <p className="font-display font-bold text-xs uppercase tracking-widest text-muted-foreground mb-3">— Supplementary Status (Optional) —</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Enemy Contact */}
                <div className="border border-border rounded-lg p-3 space-y-2">
                  <p className="font-display font-bold text-xs uppercase tracking-widest">Enemy Contact</p>
                  <div className="flex flex-wrap gap-1">
                    {(["NONE","POSSIBLE","CONFIRMED","ENGAGED"] as const).map(v => {
                      const cfg = CONTACT[v];
                      return <button key={v} onClick={() => setForm((f:any) => ({...f, enemy_contact: v}))}
                        className={`px-2 py-1 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${form.enemy_contact === v ? `${cfg.bg} ${cfg.border} ${cfg.color}` : "border-border text-muted-foreground hover:bg-secondary/60"}`}>{v}</button>;
                    })}
                  </div>
                </div>
                {/* Casualties */}
                <div className="border border-border rounded-lg p-3 space-y-2">
                  <p className="font-display font-bold text-xs uppercase tracking-widest">Own Casualties</p>
                  <div className="flex flex-wrap gap-1">
                    {(["NONE","LOW","MODERATE","HEAVY"] as const).map(v => (
                      <button key={v} onClick={() => setForm((f:any) => ({...f, friendly_casualties: v}))}
                        className={`px-2 py-1 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${form.friendly_casualties === v ? `bg-secondary/80 border-primary/50 ${CAS_COLOR[v]}` : "border-border text-muted-foreground hover:bg-secondary/60"}`}>{v}</button>
                    ))}
                  </div>
                  {form.friendly_casualties !== "NONE" && (
                    <input value={form.casualty_notes} onChange={e => setForm((f:any) => ({...f, casualty_notes: e.target.value}))} placeholder="e.g. 5 up, 1 WIA walking" className="mf-input w-full text-xs" />
                  )}
                </div>
                {/* Mission */}
                <div className="border border-border rounded-lg p-3 space-y-2">
                  <p className="font-display font-bold text-xs uppercase tracking-widest">Mission Status</p>
                  <div className="flex flex-wrap gap-1">
                    {(["ON TRACK","DELAYED","COMPROMISED","ABORT"] as const).map(v => {
                      const cfg = MISSION[v];
                      return <button key={v} onClick={() => setForm((f:any) => ({...f, mission_status: v}))}
                        className={`px-2 py-1 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${form.mission_status === v ? `${cfg.bg} ${cfg.border} ${cfg.color}` : "border-border text-muted-foreground hover:bg-secondary/60"}`}>{v}</button>;
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 p-4 pt-0">
              <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-1.5 bg-primary text-primary-foreground rounded font-display text-xs uppercase tracking-widest hover:bg-primary/90">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Transmit SITREP
              </button>
              <button onClick={() => { setShowForm(false); setForm({...emptyForm, report_time: dtgNow}); }} className="px-4 py-1.5 border border-border rounded font-display text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Records */}
      {reports.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg">
          <Target className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm font-sans">No SITREPs on record.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r:any) => {
            // Parse ABCDE from additional_info if stored as JSON
            let abcde: any = {};
            try { if (r.additional_info && r.additional_info.startsWith('{')) abcde = JSON.parse(r.additional_info); } catch {}
            const fieldA = abcde.a ?? r.report_time ?? "";
            const fieldB = abcde.b ?? r.position ?? "";
            const fieldC = abcde.c ?? "";
            const fieldD = abcde.d ?? r.activity ?? "";
            const fieldE = abcde.e ?? r.next_action ?? "";
            const ec = CONTACT[(r.enemy_contact ?? "NONE").toUpperCase()] ?? CONTACT["NONE"];
            const ms = MISSION[(r.mission_status ?? "ON TRACK").toUpperCase()] ?? MISSION["ON TRACK"];
            const isHot = (r.enemy_contact ?? "").toUpperCase() === "ENGAGED" || (r.mission_status ?? "").toUpperCase() === "ABORT";
            return (
              <div key={r.id} className={`border ${isHot ? "border-red-500/50" : "border-amber-500/30"} rounded-lg overflow-hidden bg-card`}>
                {/* Card header */}
                <div className="bg-amber-500/10 border-b border-amber-500/25 px-4 py-2 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <Target className="w-4 h-4 text-amber-400 shrink-0" />
                    <span className="font-display font-black text-sm uppercase tracking-widest">{r.callsign}</span>
                    {r.op_name && <span className="text-xs text-muted-foreground font-sans">/ {r.op_name}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground font-mono">{r.report_time || ""}</span>
                    <span className="text-xs text-muted-foreground font-sans">{r.reported_by}</span>
                    <button onClick={async () => { if(!confirm("Delete?")) return; await apiFetch("/milsimSitrep?path=delete",{method:"POST",body:JSON.stringify({id:r.id})}); load(); }} className="text-muted-foreground hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {/* ABCDE table in record */}
                {[
                  { letter:"A", label:"When",          val: fieldA },
                  { letter:"B", label:"Where",          val: fieldB },
                  { letter:"C", label:"What is it",     val: fieldC },
                  { letter:"D", label:"What doing",     val: fieldD },
                  { letter:"E", label:"Own action",     val: fieldE },
                ].filter(row => row.val).map((row, i, arr) => (
                  <div key={row.letter} className={`flex ${i < arr.length - 1 ? "border-b border-amber-500/15" : ""}`}>
                    <div className="w-8 shrink-0 flex items-center justify-center bg-amber-500/10 border-r border-amber-500/20">
                      <span className="font-display font-black text-xs text-amber-400">{row.letter}</span>
                    </div>
                    <div className="w-28 shrink-0 flex items-center px-2 py-2 bg-amber-500/5 border-r border-amber-500/15">
                      <span className="font-display font-bold text-xs uppercase tracking-widest text-amber-300/70">{row.label}</span>
                    </div>
                    <div className="flex-1 px-3 py-2">
                      <p className="text-sm font-sans">{row.val}</p>
                    </div>
                  </div>
                ))}
                {/* Status strip */}
                {(r.enemy_contact !== "NONE" || r.mission_status !== "ON TRACK" || r.friendly_casualties !== "NONE") && (
                  <div className="grid grid-cols-3 text-xs border-t border-amber-500/20">
                    <div className={`p-2 border-r border-border ${ec.bg}`}>
                      <p className="text-muted-foreground font-display font-bold uppercase tracking-wider text-xs mb-0.5">Enemy</p>
                      <p className={`font-display font-black uppercase ${ec.color}`}>{r.enemy_contact ?? "NONE"}</p>
                    </div>
                    <div className={`p-2 border-r border-border ${ms.bg}`}>
                      <p className="text-muted-foreground font-display font-bold uppercase tracking-wider text-xs mb-0.5">Mission</p>
                      <p className={`font-display font-black uppercase ${ms.color}`}>{r.mission_status ?? "ON TRACK"}</p>
                    </div>
                    <div className="p-2">
                      <p className="text-muted-foreground font-display font-bold uppercase tracking-wider text-xs mb-0.5">Casualties</p>
                      <p className={`font-display font-black uppercase ${CAS_COLOR[(r.friendly_casualties ?? "NONE").toUpperCase()] ?? "text-green-400"}`}>{r.friendly_casualties ?? "NONE"}</p>
                    </div>
                  </div>
                )}
                {/* Eyewitness confirmation row */}
                <div className="flex items-center justify-between px-4 py-2 border-t border-amber-500/20 bg-amber-500/5">
                  <span className="text-[10px] text-muted-foreground font-display uppercase tracking-widest">
                    {(r.eyewitness_count ?? 0) > 0 ? `${r.eyewitness_count} eyewitness${(r.eyewitness_count ?? 0) > 1 ? "es" : ""} confirmed` : "Awaiting eyewitness confirmation"}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => apiFetch("/opIntel?path=confirm", { method: "POST", body: JSON.stringify({ report_type: "SITREP", report_id: r.id }) }).then(() => { showMsg("Eyewitness confirmation recorded", "success"); load(); }).catch((e: any) => showMsg(e.message, "error"))}
                      className="flex items-center gap-1 px-2 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-400 text-[10px] font-display font-bold uppercase tracking-widest hover:bg-amber-500/20 transition-colors">
                      <Eye className="w-3 h-3" /> Confirm
                    </button>
                    <button
                      onClick={() => archiveToIntel(group, user, null, {
                        title: `SITREP — ${r.callsign} / ${r.op_name || "Op"} (${r.report_time || new Date(r.created_date).toLocaleDateString()})`,
                        doc_type: "SITREP",
                        classification: r.enemy_contact === "YES" ? "SECRET" : "RESTRICTED",
                        content: `Callsign: ${r.callsign}
Op: ${r.op_name || "—"}
Time: ${r.report_time || "—"}
Position: ${r.position || "—"}
Activity: ${r.activity || "—"}
Enemy Contact: ${r.enemy_contact || "—"} ${r.enemy_notes ? "— " + r.enemy_notes : ""}
Casualties: ${r.friendly_casualties || "—"} ${r.casualty_notes ? "— " + r.casualty_notes : ""}
Mission Status: ${r.mission_status || "—"}
Next Action: ${r.next_action || "—"}
Additional: ${r.additional_info || "—"}`,
                        tags: ["sitrep", (r.op_name || "").toLowerCase()].filter(Boolean),
                      }, showMsg)}
                      className="flex items-center gap-1 px-2 py-0.5 rounded border border-orange-500/30 text-orange-400 text-[10px] font-display font-bold uppercase tracking-widest hover:bg-orange-500/10 transition-colors"
                    >
                      <Archive className="w-3 h-3" /> Archive
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── MEDEVAC Tab — NATO 9-Line Request ───────────────────────────────────────
   Exact fields from the 4IB 2 Signals Regiment MEDEVAC card:
   1 Callsign & Freq  2 HLS Grid  3 Patients/Precedence  4 Special Equipment
   5 Patient Type     6 HLS Security  7 HLS Marking  8 Nationality/Status  9 HLS Terrain
──────────────────────────────────────────────────────────────────────────── */
function MedevacTab({ group, showMsg }: { group: any; showMsg: (m: string, t?: "success" | "error") => void }) {
  const { user } = useAuth();
  const [requests, setRequests] = useState<any[]>([]);
  const [ops, setOps] = useState<any[]>([]);
  const [roster, setRoster] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const now = new Date();
  const dtgNow = `${String(now.getUTCDate()).padStart(2,'0')}${String(now.getUTCHours()).padStart(2,'0')}${String(now.getUTCMinutes()).padStart(2,'0')}Z ${['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'][now.getUTCMonth()]} ${now.getUTCFullYear()}`;

  const emptyForm = {
    callsign: "", unit: "", dtg: dtgNow, op_id: "", op_name: "",
    // Line 1
    l1_callsign_freq: "",
    // Line 2
    l2_hls_grid: "",
    // Line 3 — patients by precedence
    l3_p1: 0, l3_p2: 0, l3_p3: 0,
    // Line 4 — special equipment
    l4_equipment: "A" as "A"|"B"|"C"|"D"|"E",
    l4_other: "",
    // Line 5 — patient type
    l5_stretcher: 0, l5_walking: 0, l5_escort: 0, l5_other: "",
    // Line 6 — HLS security
    l6_security: "N" as "N"|"P"|"E"|"X",
    // Line 7 — HLS marking
    l7_marking: "C" as "A"|"B"|"C"|"D"|"E",
    l7_smoke_colour: "",
    // Line 8 — nationality/status (checkboxes)
    l8_a: 0, l8_b: 0, l8_c: 0, l8_d: 0, l8_e: 0, l8_f: 0, l8_g: 0, l8_h: 0,
    // Line 9
    l9_terrain: "",
  };
  const [form, setForm] = useState<any>(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [d, opsData, rosterData] = await Promise.all([
        apiFetch(`/milsimSitrep?path=medevac_list&group_id=${group.id}`),
        apiFetch(`/activityCalendar?path=list&group_id=${group.id}`),
        apiFetch(`/milsimGroups?path=mine/own`),
      ]);
      setRequests(d.medevacs ?? []);
      setOps((opsData.events ?? []).filter((o:any) => ["Active","Confirmed","Planned"].includes(o.status)));
      setRoster((rosterData.roster ?? []).filter((m:any) => m.status === "Active"));
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [group.id]);

  const save = async () => {
    if (!form.callsign) { showMsg("Callsign required", "error"); return; }
    if (!form.l1_callsign_freq) { showMsg("Line 1 (Callsign & Freq) required", "error"); return; }
    if (!form.l2_hls_grid) { showMsg("Line 2 (HLS Grid) required", "error"); return; }
    setSaving(true);
    try {
      const payload: any = {
        group_id: group.id, reported_by: user?.username ?? "Unknown",
        callsign: form.callsign, report_time: form.dtg,
        op_id: form.op_id, op_name: form.op_name,
        // Store as structured JSON in additional_info
        additional_info: JSON.stringify({
          type: "MEDEVAC_9LINE",
          unit: form.unit, dtg: form.dtg,
          l1: form.l1_callsign_freq,
          l2: form.l2_hls_grid,
          l3: { p1: form.l3_p1, p2: form.l3_p2, p3: form.l3_p3 },
          l4: { code: form.l4_equipment, other: form.l4_other },
          l5: { s: form.l5_stretcher, w: form.l5_walking, e: form.l5_escort, o: form.l5_other },
          l6: form.l6_security,
          l7: { code: form.l7_marking, colour: form.l7_smoke_colour },
          l8: { a:form.l8_a, b:form.l8_b, c:form.l8_c, d:form.l8_d, e:form.l8_e, f:form.l8_f, g:form.l8_g, h:form.l8_h },
          l9: form.l9_terrain,
        }),
        // Summaries for list view
        position: form.l2_hls_grid,
        activity: `MEDEVAC — ${(form.l3_p1||0)+(form.l3_p2||0)+(form.l3_p3||0)} cas, P1:${form.l3_p1||0} P2:${form.l3_p2||0} P3:${form.l3_p3||0}`,
        enemy_contact: form.l6_security === "X" ? "CONFIRMED" : form.l6_security === "E" ? "POSSIBLE" : "NONE",
        friendly_casualties: ((form.l3_p1||0)+(form.l3_p2||0)+(form.l3_p3||0)) > 0 ? "SIGNIFICANT" : "NONE",
        mission_status: "ON TRACK",
      };
      if (payload.op_id) { const op = ops.find((o:any) => o.id === payload.op_id); if (op) payload.op_name = op.title ?? op.name ?? ""; }
      await apiFetch("/milsimSitrep?path=create", { method: "POST", body: JSON.stringify(payload) });
      showMsg("MEDEVAC 9-Line transmitted", "success"); setShowForm(false); setForm({...emptyForm, dtg: dtgNow}); load();
    } catch (e: any) { showMsg(e.message, "error"); }
    setSaving(false);
  };

  const L4_OPTS = [
    { code: "A", label: "A — NONE" },
    { code: "B", label: "B — HOIST" },
    { code: "C", label: "C — EXTRACTION EQUIP" },
    { code: "D", label: "D — VENTILATOR" },
    { code: "E", label: "E — OTHER" },
  ];
  const L6_OPTS = [
    { code: "N", label: "N — NO ENEMY",           color: "text-green-400",  bg: "bg-green-500/10",  border: "border-green-500/40" },
    { code: "P", label: "P — POSSIBLE ENEMY",     color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/40" },
    { code: "E", label: "E — ENEMY IN AREA",      color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/40" },
    { code: "X", label: "X — ARMED ESCORT REQ'D", color: "text-red-400",    bg: "bg-red-500/10",    border: "border-red-500/40" },
  ];
  const L7_OPTS = [
    { code: "A", label: "A — PANELS" },
    { code: "B", label: "B — PYRO" },
    { code: "C", label: "C — SMOKE (colour?)" },
    { code: "D", label: "D — NONE" },
    { code: "E", label: "E — OTHER" },
  ];
  const L8_CATS = [
    { code: "A", label: "A — UK / NATO Military" },
    { code: "B", label: "B — UK / NATO Civilian" },
    { code: "C", label: "C — Non-UK / NATO Military" },
    { code: "D", label: "D — Non-UK / NATO Civilian" },
    { code: "E", label: "E — Detainees / PW" },
    { code: "F", label: "F — Embedded Interpreter" },
    { code: "G", label: "G — Civ Cas caused by FF" },
    { code: "H", label: "H — Child" },
  ];

  // Row renderer for the pink table style
  const MRow = ({ lineNum, title, children, subrow }: { lineNum: number | string; title: string; children: React.ReactNode; subrow?: React.ReactNode }) => (
    <div className={`border-b border-pink-500/25 last:border-0`}>
      <div className="flex min-h-[3.25rem]">
        <div className="w-10 shrink-0 flex items-center justify-center bg-pink-500/10 border-r border-pink-500/25">
          <span className="font-display font-black text-sm text-pink-400">{lineNum}</span>
        </div>
        <div className="w-52 shrink-0 flex items-center px-3 py-2.5 bg-pink-500/5 border-r border-pink-500/20">
          <p className="font-display font-black text-xs uppercase tracking-widest text-pink-300">{title}</p>
        </div>
        <div className="flex-1 p-2.5 flex items-center">{children}</div>
      </div>
      {subrow && <div className="border-t border-pink-500/20 bg-secondary/20 px-4 py-2">{subrow}</div>}
    </div>
  );

  const NumInput = ({ field, label }: { field: string; label: string }) => (
    <div className="flex flex-col items-center gap-1">
      <p className="text-xs text-muted-foreground font-display font-bold uppercase tracking-wider">{label}</p>
      <input type="number" min={0} max={99} value={form[field]} onChange={e => setForm((f:any) => ({...f, [field]: parseInt(e.target.value)||0}))} className="mf-input w-16 text-center font-mono font-bold" />
    </div>
  );

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-display font-bold text-lg uppercase tracking-widest">MEDEVAC</h2>
            <span className="text-xs font-display font-bold uppercase tracking-widest px-2 py-0.5 bg-pink-500/15 border border-pink-500/40 text-pink-400 rounded">NATO 9-Line</span>
          </div>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Medical Evacuation Request — 9-Line format. AUTHORITY: 4IB 2 Signals Regiment.</p>
        </div>
        <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-2 px-3 py-1.5 bg-pink-500/20 border border-pink-500/40 text-pink-400 rounded font-display text-xs uppercase tracking-widest hover:bg-pink-500/30 transition-colors">
          <Plus className="w-3.5 h-3.5" /> File 9-Line
        </button>
      </div>

      {showForm && (
        <div className="border border-pink-500/50 rounded-lg overflow-hidden">
          {/* Header — pink to match real card */}
          <div className="bg-pink-500/20 border-b border-pink-500/40 px-5 py-3 flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="font-display font-black text-sm uppercase tracking-widest text-pink-400">MEDEVAC — 9-LINE REQUEST</p>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">Complete all 9 lines before transmitting. Priority 1 = within 60 mins.</p>
            </div>
            <span className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground border border-border px-2 py-1 rounded">URGENT</span>
          </div>
          <div className="bg-card">
            {/* Admin / header block */}
            <div className="p-4 border-b border-pink-500/20 grid grid-cols-2 md:grid-cols-4 gap-4">
              <MField label="DTG">
                <input value={form.dtg} onChange={e => setForm((f:any) => ({...f, dtg: e.target.value}))} placeholder={dtgNow} className="mf-input w-full font-mono text-xs" />
              </MField>
              <MField label="Reporting Callsign *">
                <select value={form.callsign} onChange={e => setForm((f:any) => ({...f, callsign: e.target.value}))} className="mf-input w-full">
                  <option value="">— Select callsign —</option>
                  {roster.map((m:any) => <option key={m.id} value={m.callsign}>{m.callsign}{m.rank_name ? ` (${m.rank_name})` : ""}</option>)}
                </select>
              </MField>
              <MField label="Unit Name">
                <input value={form.unit} onChange={e => setForm((f:any) => ({...f, unit: e.target.value}))} placeholder="e.g. 2 SECTION" className="mf-input w-full" />
              </MField>
              <MField label="Linked Op">
                <select value={form.op_id} onChange={e => setForm((f:any) => ({...f, op_id: e.target.value}))} className="mf-input w-full">
                  <option value="">— No op —</option>
                  {ops.map((o:any) => <option key={o.id} value={o.id}>{o.title ?? o.name}</option>)}
                </select>
              </MField>
            </div>

            {/* 9-Line table */}
            <div>
              {/* Line 1 */}
              <MRow lineNum={1} title="Callsign & Freq">
                <input value={form.l1_callsign_freq} onChange={e => setForm((f:any) => ({...f, l1_callsign_freq: e.target.value}))} placeholder="e.g. SUNRAY ACTUAL, Freq 46.50" className="mf-input w-full text-sm" />
              </MRow>

              {/* Line 2 */}
              <MRow lineNum={2} title="Location (Grid of HLS)">
                <input value={form.l2_hls_grid} onChange={e => setForm((f:any) => ({...f, l2_hls_grid: e.target.value}))} placeholder="8 or 10 digit grid of HLS" className="mf-input w-full text-sm font-mono" />
              </MRow>

              {/* Line 3 */}
              <MRow lineNum={3} title="No. of Patients / Precedence"
                subrow={
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground font-display font-bold uppercase tracking-wider">
                    <span className="text-red-400">P1 — within 60 mins</span>
                    <span className="text-orange-400">P2 — within 4 hrs</span>
                    <span className="text-yellow-400">P3 — within 24 hrs</span>
                  </div>
                }>
                <div className="flex gap-4 flex-wrap">
                  <NumInput field="l3_p1" label="P1 (Urgent)" />
                  <NumInput field="l3_p2" label="P2 (Priority)" />
                  <NumInput field="l3_p3" label="P3 (Routine)" />
                </div>
              </MRow>

              {/* Line 4 */}
              <MRow lineNum={4} title="Special Equipment Required"
                subrow={
                  <div className="flex flex-wrap gap-2">
                    {L4_OPTS.map(o => (
                      <button key={o.code} onClick={() => setForm((f:any) => ({...f, l4_equipment: o.code}))}
                        className={`px-2.5 py-1 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${form.l4_equipment === o.code ? "bg-pink-500/20 border-pink-500/50 text-pink-400" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>{o.label}</button>
                    ))}
                    {form.l4_equipment === "E" && <input value={form.l4_other} onChange={e => setForm((f:any) => ({...f, l4_other: e.target.value}))} placeholder="Specify other equipment..." className="mf-input text-xs" />}
                  </div>
                }>
                <span className={`font-display font-black text-sm text-pink-400`}>{L4_OPTS.find(o => o.code === form.l4_equipment)?.label ?? "—"}</span>
              </MRow>

              {/* Line 5 */}
              <MRow lineNum={5} title="No. of Patients / Type"
                subrow={
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground font-display font-bold uppercase tracking-wider">
                    <span>S — Stretcher</span><span>W — Walking</span><span>E — Escort</span><span>O — Other (specify)</span>
                  </div>
                }>
                <div className="flex gap-4 flex-wrap items-end">
                  <NumInput field="l5_stretcher" label="S (Stretcher)" />
                  <NumInput field="l5_walking" label="W (Walking)" />
                  <NumInput field="l5_escort" label="E (Escort)" />
                  <div className="flex flex-col gap-1">
                    <p className="text-xs text-muted-foreground font-display font-bold uppercase tracking-wider">O (Other)</p>
                    <input value={form.l5_other} onChange={e => setForm((f:any) => ({...f, l5_other: e.target.value}))} placeholder="Details..." className="mf-input w-32 text-xs" />
                  </div>
                </div>
              </MRow>

              {/* Line 6 */}
              <MRow lineNum={6} title="Security at HLS"
                subrow={
                  <div className="flex flex-wrap gap-2">
                    {L6_OPTS.map(o => (
                      <button key={o.code} onClick={() => setForm((f:any) => ({...f, l6_security: o.code}))}
                        className={`px-2.5 py-1 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${form.l6_security === o.code ? `${o.bg} ${o.border} ${o.color}` : "border-border text-muted-foreground hover:bg-secondary/60"}`}>{o.label}</button>
                    ))}
                  </div>
                }>
                {(() => { const o = L6_OPTS.find(x => x.code === form.l6_security); return o ? <span className={`font-display font-black text-sm ${o.color}`}>{o.label}</span> : null; })()}
              </MRow>

              {/* Line 7 */}
              <MRow lineNum={7} title="HLS Marking Method"
                subrow={
                  <div className="flex flex-wrap gap-2 items-center">
                    {L7_OPTS.map(o => (
                      <button key={o.code} onClick={() => setForm((f:any) => ({...f, l7_marking: o.code}))}
                        className={`px-2.5 py-1 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${form.l7_marking === o.code ? "bg-pink-500/20 border-pink-500/50 text-pink-400" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>{o.label}</button>
                    ))}
                    {form.l7_marking === "C" && <input defaultValue={form.l7_smoke_colour} onBlur={e => setForm((f:any) => ({...f, l7_smoke_colour: e.target.value}))} onKeyDown={e => { if(e.key==="Enter") e.currentTarget.blur(); }} placeholder="Smoke colour..." className="mf-input text-xs w-36" key={`smoke-${form.l7_marking}`} />}
                  </div>
                }>
                <span className="font-display font-black text-sm text-pink-400">{L7_OPTS.find(o => o.code === form.l7_marking)?.label ?? "—"}{form.l7_smoke_colour && ` — ${form.l7_smoke_colour}`}</span>
              </MRow>

              {/* Line 8 */}
              <MRow lineNum={8} title="Patients by Nationality / Status"
                subrow={
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {L8_CATS.map(cat => (
                      <div key={cat.code} className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground font-display font-bold uppercase tracking-wider w-4">{cat.code}</p>
                        <input type="number" min={0} max={99} value={form[`l8_${cat.code.toLowerCase()}`]} onChange={e => setForm((f:any) => ({...f, [`l8_${cat.code.toLowerCase()}`]: parseInt(e.target.value)||0}))} className="mf-input w-14 text-center font-mono text-xs font-bold" />
                        <p className="text-xs text-muted-foreground font-sans">{cat.label.slice(4)}</p>
                      </div>
                    ))}
                  </div>
                }>
                <div className="flex gap-3 flex-wrap text-xs font-mono font-bold">
                  {L8_CATS.map(cat => form[`l8_${cat.code.toLowerCase()}`] > 0 ? <span key={cat.code} className="text-pink-400">{cat.code}: {form[`l8_${cat.code.toLowerCase()}`]}</span> : null)}
                </div>
              </MRow>

              {/* Line 9 */}
              <MRow lineNum={9} title="HLS Terrain / Obstacles">
                <input value={form.l9_terrain} onChange={e => setForm((f:any) => ({...f, l9_terrain: e.target.value}))} placeholder="e.g. Flat open ground, power lines to north, soft soil, no obstacles" className="mf-input w-full text-sm" />
              </MRow>
            </div>

            <div className="flex gap-2 p-4 border-t border-pink-500/20">
              <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-1.5 bg-pink-500/80 hover:bg-pink-500 text-white rounded font-display text-xs uppercase tracking-widest transition-colors">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Transmit 9-Line
              </button>
              <button onClick={() => { setShowForm(false); setForm({...emptyForm, dtg: dtgNow}); }} className="px-4 py-1.5 border border-border rounded font-display text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Records */}
      {requests.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg">
          <Radio className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm font-sans">No MEDEVAC requests on record.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r:any) => {
            let d: any = {};
            try { if (r.additional_info) d = JSON.parse(r.additional_info); } catch {}
            if (d.type !== "MEDEVAC_9LINE") return null;
            const totalCas = (d.l3?.p1||0) + (d.l3?.p2||0) + (d.l3?.p3||0);
            const secCfg = L6_OPTS.find(o => o.code === d.l6) ?? L6_OPTS[0];
            return (
              <div key={r.id} className="border border-pink-500/40 rounded-lg overflow-hidden bg-card">
                <div className="bg-pink-500/10 border-b border-pink-500/25 px-4 py-2 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-display font-black text-pink-400 border border-pink-500/40 px-2 py-0.5 rounded">MEDEVAC</span>
                    <span className="font-display font-black text-sm uppercase tracking-widest">{r.callsign}</span>
                    {r.op_name && <span className="text-xs text-muted-foreground font-sans">/ {r.op_name}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground font-mono">{r.report_time || ""}</span>
                    <span className={`font-display font-black text-xs uppercase ${(d.l3?.p1||0) > 0 ? "text-red-400" : (d.l3?.p2||0) > 0 ? "text-orange-400" : "text-yellow-400"}`}>{totalCas} CASUALTIES</span>
                    <button
                      onClick={() => archiveToIntel(group, user, null, {
                        title: `MEDEVAC — ${r.callsign} / ${r.op_name || "Op"} (${r.report_time || new Date(r.created_date).toLocaleDateString()})`,
                        doc_type: "MEDEVAC REPORT",
                        classification: "RESTRICTED",
                        content: `Callsign: ${r.callsign}
Op: ${r.op_name || "—"}
Time: ${r.report_time || "—"}
HLS Grid: ${d.l2 || "—"}
Casualties: P1=${d.l3?.p1||0} P2=${d.l3?.p2||0} P3=${d.l3?.p3||0}
Security: ${d.l6 || "—"}
Terrain: ${d.l9 || "—"}`,
                        tags: ["medevac", (r.op_name || "").toLowerCase()].filter(Boolean),
                      }, showMsg)}
                      className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-orange-500/30 text-orange-400 font-display font-bold uppercase tracking-widest hover:bg-orange-500/10 transition-colors"
                    >
                      <Archive className="w-3 h-3" /> Archive
                    </button>
                    <button onClick={async () => { if(!confirm("Delete?")) return; await apiFetch("/milsimSitrep?path=delete",{method:"POST",body:JSON.stringify({id:r.id})}); load(); }} className="text-muted-foreground hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {/* Summary grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-0 text-xs">
                  <div className="p-3 border-r border-border">
                    <p className="text-muted-foreground font-display font-bold uppercase tracking-wider mb-1">HLS Grid</p>
                    <p className="font-mono font-bold">{d.l2 || "—"}</p>
                  </div>
                  <div className="p-3 border-r border-border">
                    <p className="text-muted-foreground font-display font-bold uppercase tracking-wider mb-1">Casualties</p>
                    <p className="font-display font-bold">
                      {(d.l3?.p1||0) > 0 && <span className="text-red-400 mr-1.5">P1:{d.l3.p1}</span>}
                      {(d.l3?.p2||0) > 0 && <span className="text-orange-400 mr-1.5">P2:{d.l3.p2}</span>}
                      {(d.l3?.p3||0) > 0 && <span className="text-yellow-400">P3:{d.l3.p3}</span>}
                      {totalCas === 0 && "—"}
                    </p>
                  </div>
                  <div className={`p-3 border-r border-border ${secCfg.bg}`}>
                    <p className="text-muted-foreground font-display font-bold uppercase tracking-wider mb-1">HLS Security</p>
                    <p className={`font-display font-bold uppercase ${secCfg.color}`}>{secCfg.label}</p>
                  </div>
                  <div className="p-3">
                    <p className="text-muted-foreground font-display font-bold uppercase tracking-wider mb-1">Marking</p>
                    <p className="font-display font-bold">{L7_OPTS.find(o => o.code === d.l7?.code)?.label ?? "—"}{d.l7?.colour && ` — ${d.l7.colour}`}</p>
                  </div>
                </div>
                {(d.l9 || d.l1) && (
                  <div className="px-3 pb-2 text-xs border-t border-border">
                    {d.l1 && <p className="text-muted-foreground font-sans mt-1.5"><span className="font-display font-bold text-foreground">Line 1: </span>{d.l1}</p>}
                    {d.l9 && <p className="text-muted-foreground font-sans mt-0.5"><span className="font-display font-bold text-foreground">Terrain: </span>{d.l9}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



// ─── Op Intel Tab ────────────────────────────────────────────────────────────
function OpIntelTab({ group, showMsg }: { group: any; showMsg: (m: string, t?: "success" | "error") => void }) {
  const [intel, setIntel] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLatest = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await apiFetch<any>(`/opIntel?path=latest&group_id=${group.id}`);
      setIntel(d);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  };

  const runAnalysis = async () => {
    setAnalysing(true);
    setError(null);
    try {
      const d = await apiFetch<any>(`/opIntel?path=analyse&group_id=${group.id}`);
      setIntel(d);
      showMsg("AI Op Intel analysis complete", "success");
    } catch (e: any) { setError(e.message); showMsg(e.message, "error"); }
    setAnalysing(false);
  };

  useEffect(() => { loadLatest(); }, [group.id]);

  const GRADE_CFG: Record<string, { color: string; bg: string; border: string; label: string }> = {
    ALPHA:   { color: "text-green-300",  bg: "bg-green-500/10",  border: "border-green-500/40",  label: "ALPHA — Elite Operational Capability" },
    BRAVO:   { color: "text-blue-300",   bg: "bg-blue-500/10",   border: "border-blue-500/40",   label: "BRAVO — Proficient Combat Unit" },
    CHARLIE: { color: "text-yellow-300", bg: "bg-yellow-500/10", border: "border-yellow-500/40", label: "CHARLIE — Developing Capability" },
    DELTA:   { color: "text-red-300",    bg: "bg-red-500/10",    border: "border-red-500/40",    label: "DELTA — Limited / Insufficient Data" },
  };

  const OUTCOME_COLORS: Record<string, string> = {
    VICTORY: "bg-green-500", "PARTIAL VICTORY": "bg-lime-500", DRAW: "bg-yellow-500",
    DEFEAT: "bg-red-500", INCOMPLETE: "bg-secondary",
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const stats     = intel?.stats ?? null;
  const narrative = intel?.ai_narrative ?? null;
  const grade     = narrative?.intel_grade ?? "DELTA";
  const gradeCfg  = GRADE_CFG[grade] ?? GRADE_CFG.DELTA;

  const hasData = stats && (stats.total_aars > 0 || stats.total_lace > 0 || stats.total_sitreps > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-display font-bold text-lg uppercase tracking-widest">Op Intel</h2>
            <span className="text-xs font-display font-bold uppercase tracking-widest px-2 py-0.5 bg-primary/10 border border-primary/30 text-primary rounded">AI-Powered</span>
          </div>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">GPT-4o-mini analysis across all AARs, LACE reports, and SITREPs. Calculates win rate, casualty trends, and mission success patterns.</p>
        </div>
        <button onClick={runAnalysis} disabled={analysing}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary/20 border border-primary/40 text-primary rounded font-display text-xs uppercase tracking-widest hover:bg-primary/30 transition-colors disabled:opacity-50">
          {analysing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BrainCircuit className="w-3.5 h-3.5" />}
          {analysing ? "Analysing..." : "Run AI Analysis"}
        </button>
      </div>

      {error && <div className="p-3 rounded border border-red-500/40 bg-red-500/10 text-red-400 text-xs font-sans">{error}</div>}

      {!hasData && !analysing && (
        <div className="text-center py-16 border border-dashed border-border rounded-lg text-muted-foreground">
          <BrainCircuit className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-display text-sm uppercase tracking-widest">No Op Data</p>
          <p className="text-xs mt-2 font-sans max-w-xs mx-auto">File AARs, LACE reports, or SITREPs to generate Op Intel. The AI needs at least 1 AAR to produce a meaningful analysis.</p>
        </div>
      )}

      {hasData && (
        <>
          {/* Data counts */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "AARs", value: stats.total_aars, icon: ClipboardList, color: "text-primary" },
              { label: "LACE Reports", value: stats.total_lace, icon: Radio, color: "text-blue-400" },
              { label: "SITREPs", value: stats.total_sitreps, icon: Target, color: "text-amber-400" },
            ].map(s => (
              <div key={s.label} className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
                <s.icon className={`w-5 h-5 ${s.color} shrink-0`} />
                <div>
                  <p className={`font-display font-black text-2xl ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-widest font-display">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Intel grade banner */}
          {narrative && (
            <div className={`p-4 rounded-lg border ${gradeCfg.bg} ${gradeCfg.border} flex items-center gap-4`}>
              <div className={`w-16 h-16 rounded-lg flex items-center justify-center font-display font-black text-2xl ${gradeCfg.color} border ${gradeCfg.border} ${gradeCfg.bg} shrink-0`}>{grade}</div>
              <div>
                <p className={`font-display font-black text-sm uppercase tracking-widest ${gradeCfg.color}`}>{gradeCfg.label}</p>
                <p className="text-xs font-sans text-foreground/80 mt-1 leading-relaxed">{narrative.summary}</p>
              </div>
            </div>
          )}

          {/* Key metrics row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Win Rate",          value: stats.win_rate !== null ? `${stats.win_rate}%` : "—",  color: stats.win_rate !== null ? (stats.win_rate >= 60 ? "text-green-400" : stats.win_rate >= 40 ? "text-yellow-400" : "text-red-400") : "text-muted-foreground", icon: Trophy },
              { label: "Obj Success Rate",  value: stats.total_aars > 0 ? `${stats.objective_success_rate}%` : "—", color: stats.objective_success_rate >= 70 ? "text-green-400" : stats.objective_success_rate >= 40 ? "text-yellow-400" : "text-red-400", icon: Crosshair },
              { label: "Mission On-Track",  value: stats.total_sitreps > 0 ? `${stats.on_track_rate}%` : "—", color: stats.on_track_rate >= 70 ? "text-green-400" : stats.on_track_rate >= 40 ? "text-yellow-400" : "text-red-400", icon: TrendingUp },
              { label: "Casualty Pressure", value: `${stats.casualty_pressure_index}/100`, color: stats.casualty_pressure_index <= 20 ? "text-green-400" : stats.casualty_pressure_index <= 50 ? "text-yellow-400" : "text-red-400", icon: TrendingDown },
            ].map(m => (
              <div key={m.label} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <m.icon className="w-3.5 h-3.5 text-muted-foreground" />
                  <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">{m.label}</p>
                </div>
                <p className={`font-display font-black text-2xl ${m.color}`}>{m.value}</p>
              </div>
            ))}
          </div>

          {/* Combat effectiveness score bar */}
          <div className="bg-card border border-border rounded-lg p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Combat Effectiveness Score</span>
              <span className={`font-display font-black text-sm ${stats.combat_effectiveness_score >= 70 ? "text-green-400" : stats.combat_effectiveness_score >= 40 ? "text-yellow-400" : "text-red-400"}`}>{stats.combat_effectiveness_score} / 100</span>
            </div>
            <div className="h-3 rounded-full bg-secondary overflow-hidden">
              <div className={`h-full rounded-full transition-all ${stats.combat_effectiveness_score >= 70 ? "bg-green-500" : stats.combat_effectiveness_score >= 40 ? "bg-yellow-500" : "bg-red-500"}`}
                style={{ width: `${stats.combat_effectiveness_score}%` }} />
            </div>
            <p className="text-[10px] text-muted-foreground font-sans">Win rate (40%) + Objective success (30%) + Mission on-track (20%) + Low casualties (10%)</p>
          </div>

          {/* Outcome breakdown */}
          {stats.total_aars > 0 && (
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-3">AAR Outcome Breakdown</p>
              <div className="space-y-2">
                {Object.entries(stats.outcome_breakdown ?? {}).map(([outcome, count]: [string, any]) => (
                  <div key={outcome} className="flex items-center gap-3">
                    <span className="w-28 text-xs font-display font-bold uppercase tracking-wider text-foreground shrink-0">{outcome}</span>
                    <div className="flex-1 h-5 rounded bg-secondary overflow-hidden">
                      <div className={`h-full rounded ${OUTCOME_COLORS[outcome] ?? "bg-secondary/80"}`} style={{ width: `${Math.round((count / stats.total_aars) * 100)}%` }} />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground w-8 text-right shrink-0">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ammo stress & casualty split */}
          {stats.total_lace > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-card border border-border rounded-lg p-4">
                <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-3">Ammo Status (LACE)</p>
                {Object.entries(stats.lace_ammo_breakdown ?? {}).map(([s, c]: [string, any]) => {
                  const bc = s === "RED" ? "bg-red-500" : s === "ORANGE" ? "bg-orange-500" : "bg-green-500";
                  return (
                    <div key={s} className="flex items-center gap-2 mb-1.5">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${bc}`} />
                      <span className="text-xs font-display font-bold uppercase w-16 shrink-0">{s}</span>
                      <div className="flex-1 h-4 rounded bg-secondary overflow-hidden"><div className={`h-full rounded ${bc}`} style={{ width: `${Math.round((c / stats.total_lace) * 100)}%` }} /></div>
                      <span className="text-xs font-mono text-muted-foreground w-5 text-right shrink-0">{c}</span>
                    </div>
                  );
                })}
                <p className="text-[10px] text-muted-foreground mt-2 font-sans">Critical ammo rate (Orange + Red): <span className={`font-bold ${stats.critical_ammo_rate >= 60 ? "text-red-400" : stats.critical_ammo_rate >= 30 ? "text-orange-400" : "text-green-400"}`}>{stats.critical_ammo_rate}%</span></p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-3">Casualty Distribution (LACE)</p>
                {(["NONE","LOW","MODERATE","HEAVY"] as const).map(c => {
                  const val = stats.lace_casualty_breakdown?.[c] ?? 0;
                  const bc = c === "HEAVY" ? "bg-red-500" : c === "MODERATE" ? "bg-orange-500" : c === "LOW" ? "bg-yellow-500" : "bg-green-500";
                  return (
                    <div key={c} className="flex items-center gap-2 mb-1.5">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${bc}`} />
                      <span className="text-xs font-display font-bold uppercase w-20 shrink-0">{c}</span>
                      <div className="flex-1 h-4 rounded bg-secondary overflow-hidden"><div className={`h-full rounded ${bc}`} style={{ width: `${Math.round((val / stats.total_lace) * 100)}%` }} /></div>
                      <span className="text-xs font-mono text-muted-foreground w-5 text-right shrink-0">{val}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI narrative breakdown */}
          {narrative && (narrative.strengths?.length > 0 || narrative.weaknesses?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {narrative.strengths?.length > 0 && (
                <div className="bg-card border border-green-500/30 rounded-lg p-4">
                  <p className="text-[10px] font-display font-bold uppercase tracking-widest text-green-400 mb-3">Strengths</p>
                  <ul className="space-y-2">
                    {narrative.strengths.map((s: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-xs font-sans"><span className="text-green-400 shrink-0 mt-0.5">▸</span>{s}</li>
                    ))}
                  </ul>
                </div>
              )}
              {narrative.weaknesses?.length > 0 && (
                <div className="bg-card border border-red-500/30 rounded-lg p-4">
                  <p className="text-[10px] font-display font-bold uppercase tracking-widest text-red-400 mb-3">Weaknesses</p>
                  <ul className="space-y-2">
                    {narrative.weaknesses.map((w: string, i: number) => (
                      <li key={i} className="flex items-start gap-2 text-xs font-sans"><span className="text-red-400 shrink-0 mt-0.5">▸</span>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* AI recommendations */}
          {narrative?.recommendations?.length > 0 && (
            <div className="bg-card border border-primary/30 rounded-lg p-4">
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary mb-3">AI Recommendations</p>
              <ul className="space-y-2">
                {narrative.recommendations.map((r: string, i: number) => (
                  <li key={i} className="flex items-start gap-2 text-xs font-sans">
                    <span className="font-display font-black text-primary shrink-0 mt-0.5">{i+1}.</span>{r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Threat assessment */}
          {narrative?.threat_assessment && (
            <div className="flex items-start gap-3 p-3 rounded border border-orange-500/30 bg-orange-500/5">
              <FlaskConical className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-[10px] font-display font-bold uppercase tracking-widest text-orange-400 mb-1">Threat Assessment</p>
                <p className="text-xs font-sans text-foreground/80">{narrative.threat_assessment}</p>
              </div>
            </div>
          )}

          {intel?.generated_at && (
            <p className="text-[10px] text-muted-foreground text-right font-sans">Last analysed: {new Date(intel.generated_at).toLocaleString()}</p>
          )}
        </>
      )}
    </div>
  );
}

function ConductReportTab({ group, showMsg }: { group: any; showMsg: (m: string, t?: "success" | "error") => void }) {
  const { user } = useAuth();
  const [reports, setReports] = useState<any[]>([]);
  const [ops, setOps] = useState<any[]>([]);
  const [roster, setRoster] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const now = new Date();
  const dtgNow = `${String(now.getUTCDate()).padStart(2,'0')}${String(now.getUTCHours()).padStart(2,'0')}${String(now.getUTCMinutes()).padStart(2,'0')}Z ${['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'][now.getUTCMonth()]} ${now.getUTCFullYear()}`;

  const emptyForm = {
    subject_callsign: "", report_date: dtgNow, filed_by_rank: "",
    op_id: "", op_name: "",
    category: "Conduct", severity: "Minor",
    description: "", evidence: "",
    outcome: "No Action", outcome_notes: "", reviewed_by: "",
  };
  const [form, setForm] = useState<any>(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const [d, opsData, rosterData] = await Promise.all([
        apiFetch(`/milsimConductReport?path=list&group_id=${group.id}`),
        apiFetch(`/activityCalendar?path=list&group_id=${group.id}`),
        apiFetch(`/milsimGroups?path=mine/own`),
      ]);
      setReports(d.reports ?? []);
      setOps((opsData.events ?? []).filter((o: any) => ["Active","Confirmed","Planned","Completed"].includes(o.status)));
      setRoster((rosterData.roster ?? []).filter((m:any) => m.status === "Active"));
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, [group.id]);

  const save = async () => {
    if (!form.subject_callsign) { showMsg("Subject callsign required", "error"); return; }
    if (!form.description) { showMsg("Description of incident required", "error"); return; }
    setSaving(true);
    try {
      const crPayload: any = { ...form, group_id: group.id, filed_by: (form.filed_by_rank ? form.filed_by_rank + " " : "") + (user?.username ?? "Unknown") };
      if (crPayload.op_id) { const op = ops.find((o:any) => o.id === crPayload.op_id); if (op) crPayload.op_name = op.title ?? op.name ?? ""; }
      await apiFetch("/milsimConductReport?path=create", { method: "POST", body: JSON.stringify(crPayload) });
      showMsg("Admin report filed", "success"); setShowForm(false); setForm({...emptyForm, report_date: dtgNow}); load();
    } catch (e: any) { showMsg(e.message, "error"); }
    setSaving(false);
  };

  const SEV: Record<string, { color: string; bg: string; border: string }> = {
    Info:     { color: "text-blue-400",   bg: "bg-blue-500/10",   border: "border-blue-500/30" },
    Minor:    { color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/30" },
    Major:    { color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/30" },
    Critical: { color: "text-red-400",    bg: "bg-red-500/10",    border: "border-red-500/30" },
  };
  const CAT_COLOR: Record<string, string> = {
    Absence:"text-yellow-400", Conduct:"text-orange-400", Insubordination:"text-red-400",
    Negligence:"text-orange-400", Commendation:"text-green-400", Admin:"text-muted-foreground",
  };
  const OUTCOME_COLOR: Record<string, string> = {
    "No Action":"text-muted-foreground", "Verbal Warning":"text-yellow-400", "Written Warning":"text-orange-400",
    "Strike":"text-red-400", "Demotion":"text-red-400", "Removal":"text-red-500", "Commendation":"text-green-400",
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-display font-bold text-lg uppercase tracking-widest">Admin / Conduct Reports</h2>
            <span className="text-xs font-display font-bold uppercase tracking-widest px-2 py-0.5 bg-primary/10 border border-primary/30 text-primary rounded">Military Admin</span>
          </div>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Disciplinary notices, commendations, absences, admin actions. Filed by chain of command.</p>
        </div>
        <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-2 px-3 py-1.5 bg-primary/20 border border-primary/40 text-primary rounded font-display text-xs uppercase tracking-widest hover:bg-primary/30 transition-colors">
          <Plus className="w-3.5 h-3.5" /> File Report
        </button>
      </div>

      {showForm && (
        <div className="border border-primary/40 rounded-lg overflow-hidden">
          <div className="bg-primary/15 border-b border-primary/30 px-5 py-3 flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="font-display font-black text-sm uppercase tracking-widest text-primary">ADMIN / CONDUCT REPORT</p>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">To be raised by chain of command only. Filed reports are permanent record.</p>
            </div>
            <span className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground border border-border px-2 py-1 rounded">RESTRICTED</span>
          </div>
          <div className="p-5 space-y-5 bg-card">
            {/* Header */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-4 border-b border-border">
              <MField label="DTG (Date-Time Group)">
                <input value={form.report_date} onChange={e => setForm((f:any) => ({...f, report_date: e.target.value}))} placeholder={dtgNow} className="mf-input w-full font-mono text-xs" />
              </MField>
              <MField label="Subject (Individual) *">
                <select value={form.subject_callsign} onChange={e => setForm((f:any) => ({...f, subject_callsign: e.target.value}))} className="mf-input w-full">
                  <option value="">— Select soldier —</option>
                  {roster.map((m:any) => <option key={m.id} value={m.callsign}>{m.callsign}{m.rank_name ? ` (${m.rank_name})` : ""}</option>)}
                </select>
              </MField>
              <MField label="Reviewing Authority (Commander / NCO)">
                <select value={form.reviewed_by} onChange={e => setForm((f:any) => ({...f, reviewed_by: e.target.value}))} className="mf-input w-full">
                  <option value="">— Select authority —</option>
                  {roster.map((m:any) => <option key={m.id} value={m.callsign}>{m.callsign}{m.rank_name ? ` (${m.rank_name})` : ""}</option>)}
                </select>
              </MField>
            </div>

            {/* Linked Op */}
            <MField label="Linked Op (Optional)">
              <select value={form.op_id} onChange={e => setForm((f:any) => ({...f, op_id: e.target.value}))} className="mf-input w-full">
                <option value="">— No op —</option>
                {ops.map((o:any) => <option key={o.id} value={o.id}>{o.title ?? o.name}</option>)}
              </select>
            </MField>

            {/* Category + Severity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-border rounded-lg p-3 space-y-2">
                <p className="font-display font-bold text-xs uppercase tracking-widest">Report Category</p>
                <div className="flex flex-wrap gap-1.5">
                  {(["Absence","Conduct","Insubordination","Negligence","Commendation","Admin"] as const).map(cat => (
                    <button key={cat} onClick={() => setForm((f:any) => ({...f, category: cat}))}
                      className={`px-2.5 py-1 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${form.category === cat ? `bg-secondary/80 border-primary/50 ${CAT_COLOR[cat]}` : "border-border text-muted-foreground hover:bg-secondary/60"}`}>{cat}</button>
                  ))}
                </div>
              </div>
              <div className="border border-border rounded-lg p-3 space-y-2">
                <p className="font-display font-bold text-xs uppercase tracking-widest">Severity</p>
                <div className="flex gap-1.5 flex-wrap">
                  {(["Info","Minor","Major","Critical"] as const).map(sev => {
                    const cfg = SEV[sev];
                    return <button key={sev} onClick={() => setForm((f:any) => ({...f, severity: sev}))}
                      className={`px-3 py-1.5 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${form.severity === sev ? `${cfg.bg} ${cfg.border} ${cfg.color}` : "border-border text-muted-foreground hover:bg-secondary/60"}`}>{sev}</button>;
                  })}
                </div>
              </div>
            </div>

            {/* Incident detail */}
            <MField label="Description of Incident / Conduct *">
              <textarea value={form.description} onChange={e => setForm((f:any) => ({...f, description: e.target.value}))} rows={4} placeholder="Detailed account of the incident, conduct, absence, or commendation..." className="mf-input w-full resize-none" />
            </MField>
            <MField label="Evidence / Supporting References">
              <textarea value={form.evidence} onChange={e => setForm((f:any) => ({...f, evidence: e.target.value}))} rows={2} placeholder="Witness callsigns, timestamps, screenshots, prior reports..." className="mf-input w-full resize-none" />
            </MField>

            {/* Outcome */}
            <div className="border border-border rounded-lg p-3 space-y-2">
              <p className="font-display font-bold text-xs uppercase tracking-widest">Outcome / Action Taken</p>
              <div className="flex flex-wrap gap-1.5">
                {(["No Action","Verbal Warning","Written Warning","Strike","Demotion","Removal","Commendation"] as const).map(o => (
                  <button key={o} onClick={() => setForm((f:any) => ({...f, outcome: o}))}
                    className={`px-2.5 py-1 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${form.outcome === o ? `bg-secondary/80 border-primary/50 ${OUTCOME_COLOR[o]}` : "border-border text-muted-foreground hover:bg-secondary/60"}`}>{o}</button>
                ))}
              </div>
            </div>
            <MField label="Outcome Notes / Instructions">
              <textarea value={form.outcome_notes} onChange={e => setForm((f:any) => ({...f, outcome_notes: e.target.value}))} rows={2} placeholder="Terms of warning, demotion details, commendation citation..." className="mf-input w-full resize-none" />
            </MField>

            <div className="flex gap-2 pt-2 border-t border-border">
              <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-1.5 bg-primary text-primary-foreground rounded font-display text-xs uppercase tracking-widest hover:bg-primary/90">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} File Report
              </button>
              <button onClick={() => { setShowForm(false); setForm({...emptyForm, report_date: dtgNow}); }} className="px-4 py-1.5 border border-border rounded font-display text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {reports.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg"><UserMinus2 className="w-8 h-8 mx-auto mb-2 opacity-30" /><p className="text-sm font-sans">No admin or conduct reports on record.</p></div>
      ) : (
        <div className="space-y-3">
          {reports.map((r:any) => {
            const sev = SEV[r.severity] ?? SEV["Minor"];
            const isComm = r.category === "Commendation";
            return (
              <div key={r.id} className={`border ${isComm ? "border-green-500/30" : sev.border} rounded-lg overflow-hidden bg-card`}>
                <div className={`border-b border-border px-4 py-2 flex items-center justify-between flex-wrap gap-2 ${isComm ? "bg-green-500/5" : "bg-secondary/30"}`}>
                  <div className="flex items-center gap-3">
                    <UserMinus2 className={`w-4 h-4 shrink-0 ${isComm ? "text-green-400" : "text-primary"}`} />
                    <span className="font-display font-black text-sm uppercase tracking-widest">{r.subject_callsign}</span>
                    {r.op_name && <span className="text-xs text-muted-foreground font-sans">/ {r.op_name}</span>}
                    <span className={`text-xs font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${sev.bg} ${sev.border} ${sev.color}`}>{r.severity}</span>
                    <span className={`text-xs font-display font-bold uppercase tracking-widest ${CAT_COLOR[r.category] ?? "text-muted-foreground"}`}>{r.category}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-display font-bold uppercase tracking-widest ${OUTCOME_COLOR[r.outcome] ?? "text-muted-foreground"}`}>{r.outcome}</span>
                    <span className="text-xs text-muted-foreground font-mono">{r.report_date || ""}</span>
                    <span className="text-xs text-muted-foreground font-sans">{r.filed_by}</span>
                    <button
                      onClick={() => archiveToIntel(group, user, null, {
                        title: `CONDUCT — ${r.subject_callsign} (${r.category || "—"}) ${new Date(r.report_date || r.created_date).toLocaleDateString()}`,
                        doc_type: "CONDUCT REPORT",
                        classification: "RESTRICTED",
                        content: `Subject: ${r.subject_callsign}
Category: ${r.category || "—"}
Severity: ${r.severity || "—"}
Date: ${r.report_date || "—"}
Description: ${r.description || "—"}
Evidence: ${r.evidence || "—"}
Outcome: ${r.outcome || "—"}
${r.outcome_notes ? "Notes: " + r.outcome_notes : ""}`,
                        tags: ["conduct", (r.category || "").toLowerCase()].filter(Boolean),
                      }, showMsg)}
                      className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-orange-500/30 text-orange-400 font-display font-bold uppercase tracking-widest hover:bg-orange-500/10 transition-colors"
                    >
                      <Archive className="w-3 h-3" /> Archive
                    </button>
                    <button onClick={async () => { if(!confirm("Delete?")) return; await apiFetch("/milsimConductReport?path=delete",{method:"POST",body:JSON.stringify({id:r.id})}); load(); }} className="text-muted-foreground hover:text-red-400 transition-colors"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <div className="p-3 space-y-1">
                  <p className="text-sm font-sans">{r.description}</p>
                  {r.evidence && <p className="text-xs text-muted-foreground font-sans border-t border-border pt-2"><span className="font-display font-bold uppercase tracking-wider text-foreground">Evidence: </span>{r.evidence}</p>}
                  {r.outcome_notes && <p className="text-xs text-muted-foreground font-sans"><span className="font-display font-bold uppercase tracking-wider text-foreground">Action: </span>{r.outcome_notes}</p>}
                  {r.reviewed_by && <p className="text-xs text-muted-foreground font-sans text-right">Reviewed by: <span className="text-foreground font-bold">{r.reviewed_by}</span></p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function BriefingsTab({ group, showMsg }: any) {
  const [briefings, setBriefings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const emptyForm = { title: "", op_date: "", op_id: "", ao: "", objectives: "", comms_plan: "", comms_nets: "[]", roe: "", additional_notes: "", status: "draft" };
  const [form, setForm] = useState<any>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [exportingId, setExportingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const load = () => { apiFetch<any[]>(`/milsimGroupsB?path=${group.id}/briefings`).then(setBriefings).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [group.id]);
  const [ops, setOps] = useState<any[]>([]);
  useEffect(() => {
    apiFetch<any>(`/milsimGroupsB?path=${group.id}/ops`).then((d: any) => setOps((Array.isArray(d) ? d : (d?.events ?? [])).filter((o:any) => ["Active","Confirmed","Planned","Completed"].includes(o.status)))).catch(() => {});
  }, [group.id]);

  const submit = async () => {
    if (!form.title.trim()) return; setSaving(true);
    try {
      // Pack comms_nets into comms_plan as JSON so no schema change needed
      const payload = { ...form, comms_plan: JSON.stringify({ nets: (() => { try { return JSON.parse(form.comms_nets || "[]"); } catch { return []; } })(), legacy: form.comms_plan }) };
      if (editId) { await apiFetch(`/milsimGroupsB?path=${group.id}/briefings/${editId}`, { method: "PATCH", body: JSON.stringify(payload) }); showMsg(true, "Briefing updated."); }
      else { await apiFetch(`/milsimGroupsB?path=${group.id}/briefings`, { method: "POST", body: JSON.stringify(payload) }); showMsg(true, "Briefing created."); }
      setCreating(false); setEditId(null); setForm(emptyForm); load();
    } catch (e: any) { showMsg(false, e.message); } finally { setSaving(false); }
  };
  const remove = async (id: string) => {
    try { await apiFetch(`/milsimGroupsB?path=${group.id}/briefings/${id}`, { method: "DELETE" }); showMsg(true, "Deleted."); load(); }
    catch (e: any) { showMsg(false, e.message); }
  };
  const SC: Record<string, string> = { draft: "text-muted-foreground bg-secondary border-border", published: "text-primary bg-primary/10 border-primary/30", archived: "text-muted-foreground bg-secondary/40 border-border" };
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  const setF = (k: string) => (e: any) => setForm((f: any) => ({...f, [k]: e.target.value}));
  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground font-sans">Op briefings — distributed to members before an operation.</p>
        {!creating && !editId && <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-4 py-2 rounded transition-all"><Plus className="w-3.5 h-3.5" /> New Briefing</button>}
      </div>
      {(creating || editId !== null) && (
        <div className="bg-card border border-primary/30 rounded-lg p-6 space-y-4">
          <h3 className="font-display font-bold uppercase tracking-widest text-sm">{editId ? "Edit Briefing" : "New Briefing"}</h3>
          <div className="grid grid-cols-2 gap-3"><div><label className="mf-label">Title *</label><input value={form.title} onChange={setF("title")} className="mf-input" placeholder="Operation Iron Fist — OPORD" /></div><div><label className="mf-label">Op Date / Time</label><input type="datetime-local" value={form.op_date} onChange={setF("op_date")} className="mf-input" /></div></div>
          <div><label className="mf-label">Linked Op</label><select value={form.op_id ?? ""} onChange={setF("op_id")} className="mf-input"><option value="">— No op —</option>{ops.map((o:any) => <option key={o.id} value={o.id}>{o.title ?? o.name}</option>)}</select></div>
          <div><label className="mf-label">Status</label><select value={form.status} onChange={setF("status")} className="mf-input"><option value="draft">Draft</option><option value="published">Published (visible to all members)</option><option value="archived">Archived</option></select></div>
          <div><label className="mf-label">Area of Operations (AO)</label><input value={form.ao} onChange={setF("ao")} className="mf-input" placeholder="Grid reference, map name..." /></div>
          <div><label className="mf-label">Objectives</label><textarea rows={4} value={form.objectives} onChange={setF("objectives")} className="mf-input resize-none" placeholder="1. Secure FOB Alpha&#10;2. Eliminate HVT Bravo..." /></div>
          {/* Structured Comms Plan */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="mf-label mb-0">Comms Plan — Nets</label>
              <button type="button" onClick={() => {
                try { const nets = JSON.parse(form.comms_nets || "[]"); nets.push({ net: "", primary: "", secondary: "", callsigns: "", codeword: "" }); setForm((f: any) => ({ ...f, comms_nets: JSON.stringify(nets) })); } catch {}
              }} className="text-[10px] font-display font-bold uppercase tracking-wider text-primary hover:text-primary/80 flex items-center gap-1">
                <span>+</span> Add Net
              </button>
            </div>
            {(() => {
              let nets: any[] = [];
              try { nets = JSON.parse(form.comms_nets || "[]"); } catch {}
              if (nets.length === 0) return (
                <div className="border border-dashed border-border rounded p-3 text-center text-xs text-muted-foreground font-sans">No nets added — click Add Net above</div>
              );
              return (
                <div className="space-y-2">
                  {nets.map((net: any, i: number) => (
                    <div key={i} className="border border-border rounded p-3 bg-secondary/20 space-y-2">
                      <div className="flex items-center gap-2">
                        <input value={net.net} onChange={e => { const n=[...nets]; n[i]={...n[i],net:e.target.value}; setForm((f:any)=>({...f,comms_nets:JSON.stringify(n)})); }} className="mf-input flex-1 text-xs" placeholder="Net name (e.g. CMD Net, Ops Net)" />
                        <button type="button" onClick={() => { const n=nets.filter((_:any,j:number)=>j!==i); setForm((f:any)=>({...f,comms_nets:JSON.stringify(n)})); }} className="text-destructive hover:text-destructive/80 p-1"><X className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={net.primary} onChange={e => { const n=[...nets]; n[i]={...n[i],primary:e.target.value}; setForm((f:any)=>({...f,comms_nets:JSON.stringify(n)})); }} className="mf-input text-xs" placeholder="Primary freq / channel" />
                        <input value={net.secondary} onChange={e => { const n=[...nets]; n[i]={...n[i],secondary:e.target.value}; setForm((f:any)=>({...f,comms_nets:JSON.stringify(n)})); }} className="mf-input text-xs" placeholder="Secondary / backup" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={net.callsigns} onChange={e => { const n=[...nets]; n[i]={...n[i],callsigns:e.target.value}; setForm((f:any)=>({...f,comms_nets:JSON.stringify(n)})); }} className="mf-input text-xs" placeholder="Callsigns on this net" />
                        <input value={net.codeword} onChange={e => { const n=[...nets]; n[i]={...n[i],codeword:e.target.value}; setForm((f:any)=>({...f,comms_nets:JSON.stringify(n)})); }} className="mf-input text-xs" placeholder="Codeword / password" />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
          <div><label className="mf-label">ROE</label><textarea rows={3} value={form.roe} onChange={setF("roe")} className="mf-input resize-none" placeholder="Weapons free in AO..." /></div>
          <div><label className="mf-label">Additional Notes</label><textarea rows={3} value={form.additional_notes} onChange={setF("additional_notes")} className="mf-input resize-none" /></div>
          <div className="flex gap-2">
            <button onClick={submit} disabled={saving || !form.title.trim()} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded transition-all disabled:opacity-50">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {editId ? "Update" : "Create"}</button>
            <button onClick={() => { setCreating(false); setEditId(null); setForm(emptyForm); }} className="px-4 py-2 border border-border text-muted-foreground rounded text-xs font-display uppercase hover:text-foreground">Cancel</button>
          </div>
        </div>
      )}
      {briefings.length === 0 && !creating ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg text-muted-foreground"><MapPin className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="font-display text-sm uppercase tracking-widest">No briefings created</p></div>
      ) : (
        <div className="space-y-3">
          {briefings.map((b: any) => (
            <div key={b.id} className="bg-card border border-border rounded-lg overflow-hidden">
              <button onClick={() => setExpandedId(expandedId === b.id ? null : b.id)} className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-secondary/20 transition-colors text-left">
                <div className="flex items-center gap-3 flex-wrap"><span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${SC[b.status] ?? ""}`}>{b.status}</span><span className="font-display font-bold text-sm text-foreground">{b.title}</span>{b.op_date && <span className="text-xs text-muted-foreground">{format(new Date(b.op_date), "MMM dd, yyyy HH:mm")}</span>}</div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={async e => { e.stopPropagation(); setExportingId(b.id); try { const url = await generateBriefingSlides(b, group.name); window.open(url, "_blank"); } catch(err: any) { showMsg(false, "Slides: " + err.message); } finally { setExportingId(null); } }} title="Export to Google Slides" className="p-1.5 text-muted-foreground hover:text-green-400 transition-colors" disabled={exportingId === b.id}>{exportingId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[10px] font-bold">PPT</span>}</button>
                  <button onClick={async e => { e.stopPropagation(); setExportingId(b.id * -1); try { const url = await generateBriefingDoc(b, group.name); window.open(url, "_blank"); } catch(err: any) { showMsg(false, "Doc: " + err.message); } finally { setExportingId(null); } }} title="Export to Google Docs" className="p-1.5 text-muted-foreground hover:text-blue-400 transition-colors" disabled={exportingId === b.id * -1}>{exportingId === b.id * -1 ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="text-[10px] font-bold">DOC</span>}</button>
                  <button onClick={e => { e.stopPropagation(); setEditId(b.id); setForm({ title: b.title, op_date: b.op_date ? b.op_date.slice(0,16) : "", ao: b.ao ?? "", objectives: b.objectives ?? "", comms_plan: "", comms_nets: (() => { try { const p = JSON.parse(b.comms_plan ?? "{}"); return JSON.stringify(p.nets ?? []); } catch { return "[]"; } })(), roe: b.roe ?? "", additional_notes: b.additional_notes ?? "", status: b.status }); }} className="p-1.5 text-muted-foreground hover:text-primary transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={e => { e.stopPropagation(); remove(b.id); }} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  {expandedId === b.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </div>
              </button>
              {expandedId === b.id && (
                <div className="border-t border-border p-5 space-y-4 bg-secondary/10">
                  {b.ao && <AARField label="Area of Operations" value={b.ao} />}
                  {b.objectives && <AARField label="Objectives" value={b.objectives} />}
                  {b.comms_plan && (() => {
                    try {
                      const p = JSON.parse(b.comms_plan);
                      const nets = p.nets ?? [];
                      if (nets.length === 0) return null;
                      return (
                        <div>
                          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Comms Plan</p>
                          <div className="space-y-2">
                            {nets.map((n: any, i: number) => (
                              <div key={i} className="bg-secondary/30 border border-border rounded p-2 text-xs font-sans space-y-1">
                                <p className="font-bold text-foreground font-display uppercase tracking-wider">{n.net || `Net ${i+1}`}</p>
                                {n.primary && <p className="text-muted-foreground">Primary: <span className="text-foreground">{n.primary}</span></p>}
                                {n.secondary && <p className="text-muted-foreground">Secondary: <span className="text-foreground">{n.secondary}</span></p>}
                                {n.callsigns && <p className="text-muted-foreground">Callsigns: <span className="text-foreground">{n.callsigns}</span></p>}
                                {n.codeword && <p className="text-muted-foreground">Codeword: <span className="text-foreground font-mono">{n.codeword}</span></p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    } catch { return b.comms_plan ? <AARField label="Comms Plan" value={b.comms_plan} /> : null; }
                  })()}
                  {b.roe && <AARField label="ROE" value={b.roe} />}
                  {b.additional_notes && <AARField label="Additional Notes" value={b.additional_notes} />}
                  <p className="text-xs text-muted-foreground font-sans">By {b.created_by} · {formatDistanceToNow(new Date(b.created_date ?? b.created_at ?? Date.now()), { addSuffix: true })}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Org Chart ────────────────────────────────────────────────────────────────
function OrgChartTab({ group }: any) {
  const rankById = Object.fromEntries(((group.ranks ?? []) as Rank[]).map((r: Rank) => [r.id, r]));
  const roleById = Object.fromEntries(((group.roles ?? []) as Role[]).map((r: Role) => [r.id, r]));
  const sorted = [...((group.roster ?? []) as RosterEntry[])].sort((a, b) => {
    const ra = a.rankId ? (rankById[a.rankId]?.tier ?? 0) : 0;
    const rb = b.rankId ? (rankById[b.rankId]?.tier ?? 0) : 0;
    return rb - ra;
  });
  const byTier = sorted.reduce<Record<number, RosterEntry[]>>((acc, entry) => {
    const tier = entry.rankId ? (rankById[entry.rankId]?.tier ?? -1) : -1;
    if (!acc[tier]) acc[tier] = [];
    acc[tier].push(entry);
    return acc;
  }, {});
  const tiers = Object.keys(byTier).map(Number).sort((a, b) => b - a);
  if (sorted.length === 0) return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg text-muted-foreground"><GitBranch className="w-10 h-10 mx-auto mb-3 opacity-30" /><p className="font-display text-sm uppercase tracking-widest">Roster is empty</p></div>
  );
  return (
    <div className="max-w-4xl space-y-6">
      <p className="text-xs text-muted-foreground font-sans">Chain of command — organized by rank tier (highest first).</p>
      <div className="space-y-6">
        {tiers.map(tier => (
          <div key={tier} className="space-y-2">
            <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground border-b border-border pb-1">{tier >= 0 ? `Tier ${tier} — ${rankById[byTier[tier][0]?.rankId ?? -1]?.name ?? ""}` : "No Rank"}</p>
            <div className="flex flex-wrap gap-3">
              {byTier[tier].map(entry => (
                <div key={entry.id} className="bg-card border border-border rounded-lg px-4 py-3 min-w-[120px] text-center">
                  <div className="w-9 h-9 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center mx-auto mb-2"><span className="text-primary font-display font-black text-sm">{entry.callsign.charAt(0)}</span></div>
                  <p className="font-display font-bold uppercase tracking-widest text-xs text-foreground">{entry.callsign}</p>
                  {entry.rankId && <p className="text-xs text-primary mt-0.5">{rankById[entry.rankId]?.abbreviation ?? rankById[entry.rankId]?.name}</p>}
                  {entry.roleId && <p className="text-xs text-muted-foreground">{roleById[entry.roleId]?.name}</p>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Readiness ────────────────────────────────────────────────────────────────
function ReadinessTab({ group }: any) {
  const [readiness, setReadiness] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [combatRecord, setCombatRecord] = useState<any | null>(null);

  useEffect(() => {
    apiFetch<any>(`/api/stats/readiness/${group.id}`)
      .then(data => { setReadiness(normaliseReadiness(data)); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [group.id]);

  useEffect(() => {
    // GroupCombatRecord comes from jointOpsData (not statsReadiness)
    apiFetch<any>(`/jointOpsData?type=records`)
      .then((data: any) => {
        const records = data?.records ?? data ?? [];
        const match = Array.isArray(records) ? records.find((r: any) => r.group_id === group.id) : null;
        if (match) setCombatRecord(match);
      })
      .catch(() => {/* combat record optional */});
  }, [group.id]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (error || !readiness) return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg text-muted-foreground">
      <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
      <p className="font-display text-sm uppercase tracking-widest">Readiness data unavailable</p>
      <p className="text-xs mt-2">Add roster members and log operations to generate readiness data.</p>
    </div>
  );

  const sc = readiness.status_color === "green" ? "text-green-400" : readiness.status_color === "amber" ? "text-yellow-400" : "text-red-400";
  const bc = readiness.status_color === "green" ? "bg-green-500" : readiness.status_color === "amber" ? "bg-yellow-500" : "bg-red-500";

  const TIER_META: Record<string, { label: string; colour: string; bg: string; border: string; badge: string; desc: string }> = {
    "SOF":         { label: "Special Operations Forces",               colour: "text-purple-400",    bg: "bg-purple-600/10",   border: "border-purple-500/40",   badge: "bg-purple-600/20 text-purple-300 border-purple-500/40",    desc: "The highest attainable designation. Elite multi-domain doctrine, near-perfect AAR discipline, and an exceptional operational record. This unit operates at the tip of the spear." },
    "SOC":         { label: "Special Operations Capable",               colour: "text-blue-400",       bg: "bg-blue-500/10",     border: "border-blue-400/40",     badge: "bg-blue-500/20 text-blue-300 border-blue-400/50",          desc: "Extensive op record, elite AAR discipline, and comprehensive multi-type training doctrine. Operates at special operations capable standard." },
    "STRATEGIC":   { label: "Strategically Capable",                   colour: "text-green-400",     bg: "bg-green-500/10",    border: "border-green-500/40",    badge: "bg-green-500/20 text-green-300 border-green-400/50",       desc: "Proven unit with strong operational output, solid reputation, and well-documented training resources across multiple doctrine types." },
    "OPERATIONAL": { label: "Operationally Capable",                   colour: "text-emerald-400",   bg: "bg-emerald-600/10",  border: "border-emerald-600/40",  badge: "bg-emerald-600/20 text-emerald-400 border-emerald-500/40", desc: "Active unit with a consistent operational record and growing doctrine framework. Capable of executing standard mission types." },
    "TACTICAL":    { label: "Tactically Capable",                      colour: "text-yellow-400",    bg: "bg-yellow-400/10",   border: "border-yellow-400/40",   badge: "bg-yellow-400/20 text-yellow-300 border-yellow-400/50",    desc: "Building op history and operator experience. Some training doctrine in place. Unit is progressing toward operational readiness." },
    "LIMITED":     { label: "Limited Capability",                      colour: "text-amber-400",     bg: "bg-amber-500/10",    border: "border-amber-500/40",    badge: "bg-amber-500/20 text-amber-400 border-amber-500/40",       desc: "Minimal operational record and insufficient training documentation to meet baseline capability standards." },
    "POOR":        { label: "Poor Capability",                         colour: "text-red-400",       bg: "bg-red-500/10",      border: "border-red-500/40",      badge: "bg-red-500/20 text-red-400 border-red-500/40",             desc: "No established operational record, no doctrine, and no verified activity. Unit has not demonstrated any measurable capability." },
  };

  const tier = readiness.op_capability_tier ?? "POOR";
  const tm = TIER_META[tier] ?? TIER_META["POOR"];

  // Score breakdown for transparency
  const sb = readiness.score_breakdown ?? {};
  const scoreBreakdown = [
    { label: "Manpower",            max: 40, earned: sb.manpower ?? 0,         note: `${readiness.verified_total ?? readiness.total} verified members` },
    { label: "Member Activity",     max: 25, earned: sb.activity ?? 0,         note: `${readiness.active_this_month}/${readiness.total} active (30d)` },
    { label: "Operations History",  max: 35, earned: sb.ops_history ?? 0,      note: `${readiness.valid_ops ?? readiness.total_ops ?? 0} verified ops` },
    { label: "Op Recency",          max: 20, earned: sb.op_recency ?? 0,       note: readiness.days_since_last_op != null ? `Last op ${readiness.days_since_last_op}d ago` : "No ops" },
    { label: "AAR Discipline",      max: 25, earned: sb.aar_discipline ?? 0,   note: `${readiness.completed_ops ?? 0} AARs for ${readiness.valid_ops ?? readiness.total_ops ?? 0} ops` },
    { label: "Training Doctrine",   max: 60, earned: sb.training_doctrine ?? 0,note: `Knowledge factor ${readiness.training?.knowledge_factor ?? 0}/100` },
    { label: "Discord Linked",      max: 10, earned: sb.discord ?? 0,          note: readiness.has_discord ? "Linked" : "Not linked" },
    { label: "Page Maintenance",    max: 10, earned: sb.page_maintenance ?? 0, note: readiness.days_since_page_update != null ? `Updated ${readiness.days_since_page_update}d ago` : "Never updated" },
    { label: "Reputation / Reviews",max: 15, earned: sb.reputation ?? 0,       note: `${readiness.review_count} review${readiness.review_count !== 1 ? "s" : ""}, avg ${readiness.avg_rep_score || "—"}` },
    { label: "Combat Intel",         max: 25, earned: sb.combat_intel ?? 0,      note: sb.combat_intel > 0 ? `${sb.combat_intel}/25 — win rate & objective data` : "File 3+ AARs with outcomes to score" },
    { label: "Game Breadth",         max: 15, earned: sb.game_breadth ?? 0,      note: "Min-strength capability across all games listed" },
    { label: "Doctrine Bonus",       max: 20, earned: sb.doctrine_bonus ?? 0,    note: sb.doctrine_bonus > 0 ? "Full doctrine set — SOP + TTP + ROE + Drill (depth ≥70)" : "Requires SOP + TTP + ROE + Drill uploaded with avg depth ≥70" },
    { label: "Joint Ops",            max: 20, earned: sb.joint_ops ?? 0,          note: sb.joint_ops > 0 ? `${sb.joint_ops}/20 — inter-unit ops, tier & win rate` : "Complete Joint Ops to score" },
    { label: "Accountability",        max: 20, earned: Math.max(0, sb.accountability_delta ?? 0),
      note: sb.accountability_delta == null ? "No accountability data" :
            sb.accountability_delta > 0 ? `Net +${sb.accountability_delta}pts — fitness reviews, training reviews active` :
            sb.accountability_delta < 0 ? `Net ${sb.accountability_delta}pts — conduct reports / PIOs / LOA rate / unfit roles` :
            "No net impact — no issues and no bonuses" },
  ];

  return (
    <div className="max-w-2xl space-y-5">

      {/* ── Readiness header ──────────────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-display font-bold uppercase tracking-widest">Unit Readiness</h3>
          <div className="flex items-center gap-3">
            <span className={`font-display font-black text-xl uppercase ${sc}`}>{(readiness.status ?? "unknown").toUpperCase()}</span>
            <span className={`text-xs font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded border ${tm.badge}`}>
              ⊕ {tier}
            </span>
          </div>
        </div>
        <div className="space-y-1">
          <div className="flex justify-between text-xs font-display font-bold uppercase tracking-widest text-muted-foreground">
            <span>Composite Readiness Score</span><span>{readiness.readiness_pct}%</span>
          </div>
          <div className="h-3 bg-secondary rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${bc}`} style={{ width: `${readiness.readiness_pct}%` }} />
          </div>
          <p className={`text-right text-xs font-display font-bold ${sc}`}>{readiness.readiness_pct}% composite readiness</p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 pt-2 border-t border-border text-center">
          {[
            { label: "Total", value: readiness.total, col: "" },
            { label: "Active 7d", value: readiness.active_this_week, col: "text-green-400" },
            { label: "Active 30d", value: readiness.active_this_month, col: "text-blue-400" },
            { label: "Ops Logged", value: readiness.total_ops ?? 0, col: "text-primary" },
            { label: "Win Rate", value: readiness.win_rate !== null && readiness.win_rate !== undefined ? `${readiness.win_rate}%` : "—",
              col: readiness.win_rate !== null && readiness.win_rate !== undefined ? (readiness.win_rate >= 60 ? "text-green-400" : readiness.win_rate >= 35 ? "text-yellow-400" : "text-red-400") : "text-muted-foreground" },
          ].map(s => (
            <div key={s.label}>
              <p className={`text-xl font-display font-bold ${s.col || "text-foreground"}`}>{s.value}</p>
              <p className="text-[10px] text-muted-foreground font-display uppercase tracking-widest">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Readiness Flags ───────────────────────────────────────────────── */}
      {readiness.flags && readiness.flags.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Readiness Flags</p>
          {readiness.flags.map((flag: any) => (
            <div key={flag.code} className={`rounded-lg border px-4 py-3 flex gap-3 ${
              flag.severity === "red"  ? "border-red-500/40 bg-red-500/5" :
              flag.severity === "amber" ? "border-yellow-500/30 bg-yellow-500/5" :
              "border-blue-500/30 bg-blue-500/5"
            }`}>
              <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                flag.severity === "red" ? "bg-red-500" :
                flag.severity === "amber" ? "bg-yellow-400" :
                "bg-blue-400"
              }`} />
              <div>
                <p className={`font-display font-bold uppercase tracking-widest text-xs ${
                  flag.severity === "red" ? "text-red-400" :
                  flag.severity === "amber" ? "text-yellow-400" :
                  "text-blue-400"
                }`}>
                  {flag.label}
                </p>
                <p className="text-xs text-muted-foreground font-sans mt-0.5 leading-relaxed">{flag.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Score Breakdown ───────────────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-3">
        <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Score Breakdown — {readiness.readiness_pct}% Composite Readiness</p>
        <div className="space-y-2">
          {scoreBreakdown.map(row => (
            <div key={row.label} className="flex items-center gap-3 text-xs">
              <span className="w-40 shrink-0 font-display font-bold uppercase tracking-widest text-muted-foreground text-[10px]">{row.label}</span>
              <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${Math.min(100, (row.earned / row.max) * 100)}%`,
                    background: row.earned === 0 ? '#ef4444' : row.earned >= row.max * 0.75 ? '#22c55e' : row.earned >= row.max * 0.4 ? '#eab308' : '#f97316' }} />
              </div>
              <span className="text-[10px] font-display shrink-0 w-14 text-right" style={{ color: row.earned === 0 ? '#ef4444' : row.earned >= row.max * 0.75 ? '#22c55e' : row.earned >= row.max * 0.4 ? '#eab308' : '#f97316' }}>{Math.round((row.earned / row.max) * 100)}%</span>
              <span className="text-[10px] text-muted-foreground font-sans shrink-0 hidden sm:block">{row.note}</span>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground font-sans pt-1 border-t border-border/50">
          All scores shown as % of max. GREEN ≥68% · AMBER 41–67% · RED &lt;41%. Units below minimum game strength are forced Red regardless of score. Combat Intel unlocks after filing 3+ AARs with outcomes. Doctrine Bonus (+20pts) requires SOP + TTP + ROE + Drill at avg depth ≥70. Accountability applies conduct, PIO, LOA rate, and role fitness as live scoring modifiers — penalties drag the score, bonuses push it up.
        </p>

      {/* ── Accountability Breakdown ───────────────────────────────────────── */}
      {readiness.accountability && (() => {
        const acc = readiness.accountability;
        const netPositive = acc.net_accountability_delta > 0;
        const netNegative = acc.net_accountability_delta < 0;
        const netColour = netPositive ? "text-green-400" : netNegative ? "text-red-400" : "text-muted-foreground";
        const netBorder = netPositive ? "border-green-500/30 bg-green-500/5" : netNegative ? "border-red-500/30 bg-red-500/5" : "border-border";
        const items = [
          {
            label: "Conduct Reports",
            icon: "⚑",
            status: acc.open_conduct_count === 0 ? "green" : acc.critical_conduct_count > 0 ? "red" : "amber",
            value: acc.open_conduct_count === 0 ? "Clear" : `${acc.open_conduct_count} open`,
            detail: acc.open_conduct_count === 0
              ? "No unresolved conduct reports on file."
              : `${acc.open_conduct_count} unresolved report${acc.open_conduct_count !== 1 ? "s" : ""}${acc.critical_conduct_count > 0 ? ` (${acc.critical_conduct_count} critical)` : ""}. Resolve to recover points.`,
            delta: acc.conduct_penalty > 0 ? `−${acc.conduct_penalty}pts` : null,
            deltaColour: "text-red-400",
            link: "File & resolve reports in J3 → Reports → Conduct Reports.",
          },
          {
            label: "PIOs",
            icon: "📋",
            status: acc.breached_pio_count > 0 ? "red" : acc.open_pio_count > 0 ? "amber" : "green",
            value: acc.breached_pio_count > 0 ? `${acc.breached_pio_count} breached` : acc.open_pio_count > 0 ? `${acc.open_pio_count} open` : "Clear",
            detail: acc.breached_pio_count > 0
              ? `${acc.breached_pio_count} PIO${acc.breached_pio_count !== 1 ? "s" : ""} breached — deadline passed without resolution. Serious leadership accountability failure.`
              : acc.open_pio_count > 0
                ? `${acc.open_pio_count} active PIO${acc.open_pio_count !== 1 ? "s" : ""} — monitor progress to avoid breach penalty.`
                : "No open or breached PIOs.",
            delta: acc.pio_penalty > 0 ? `−${acc.pio_penalty}pts` : null,
            deltaColour: "text-red-400",
            link: "Manage PIOs in J8 → Human Resources → PIOs.",
          },
          {
            label: "Role Fitness",
            icon: "🎖",
            status: acc.unfit_role_count > 0 ? "amber" : acc.fitness_bonus > 0 ? "green" : "neutral",
            value: acc.unfit_role_count > 0 ? `${acc.unfit_role_count} unfit` : acc.fitness_bonus > 0 ? `+${acc.fitness_bonus}pts bonus` : "Not reviewed",
            detail: acc.unfit_role_count > 0
              ? `${acc.unfit_role_count} role${acc.unfit_role_count !== 1 ? "s" : ""} rated UNFIT. Personnel in unfit positions degrade capability.`
              : acc.fitness_bonus > 0
                ? `${acc.fit_role_count} role${acc.fit_role_count !== 1 ? "s" : ""} cleared as FIT. Active command oversight rewarded.`
                : "Conduct Role Fitness Reviews to earn bonus points or identify issues.",
            delta: acc.fitness_penalty > 0 ? `−${acc.fitness_penalty}pts` : acc.fitness_bonus > 0 ? `+${acc.fitness_bonus}pts` : null,
            deltaColour: acc.fitness_bonus > 0 ? "text-green-400" : "text-red-400",
            link: "Run fitness reviews in J8 → Human Resources → Role Fitness.",
          },
          {
            label: "LOA Rate",
            icon: "🏥",
            status: acc.loa_rate_pct >= 35 ? "red" : acc.loa_rate_pct >= 20 ? "amber" : "green",
            value: `${acc.loa_rate_pct}% on leave`,
            detail: acc.loa_rate_pct === 0
              ? "No members currently on approved leave — full strength available."
              : acc.loa_rate_pct < 10
                ? `${acc.active_loa_count} member${acc.active_loa_count !== 1 ? "s" : ""} on approved leave — within acceptable range.`
                : `${acc.active_loa_count} member${acc.active_loa_count !== 1 ? "s" : ""} (${acc.loa_rate_pct}%) on leave — reducing effective strength below threshold.`,
            delta: acc.loa_penalty > 0 ? `−${acc.loa_penalty}pts` : null,
            deltaColour: "text-red-400",
            link: "Manage LOAs in J1 → Personnel → Leave of Absence.",
          },
          {
            label: "Training Reviews",
            icon: "📊",
            status: acc.training_review_count === 0 ? "amber" : acc.training_review_bonus >= 6 ? "green" : "amber",
            value: acc.training_review_count === 0 ? "None filed" : `${acc.training_review_count} filed${acc.avg_training_score !== null ? ` · avg ${acc.avg_training_score}/100` : ""}`,
            detail: acc.training_review_count === 0
              ? "No training reviews filed. Formally reviewing exercises demonstrates doctrine-driven culture and earns bonus points."
              : `${acc.training_review_count} formal review${acc.training_review_count !== 1 ? "s" : ""} on file${acc.avg_training_score !== null ? `, average AI score ${acc.avg_training_score}/100` : ""}.`,
            delta: acc.training_review_bonus > 0 ? `+${acc.training_review_bonus}pts` : null,
            deltaColour: "text-green-400",
            link: "File training reviews after exercises in J7 → Development → Training Reviews.",
          },
        ];
        return (
          <div className="bg-card border border-border rounded-lg p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">
                Accountability — Live Scoring Modifiers
              </p>
              <span className={`text-xs font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded border ${netBorder} ${netColour}`}>
                {acc.net_accountability_delta > 0 ? `+${acc.net_accountability_delta}` : acc.net_accountability_delta}pts net
              </span>
            </div>
            <div className="space-y-3">
              {items.map(item => (
                <div key={item.label} className={`rounded-lg border px-4 py-3 space-y-1 ${
                  item.status === "red" ? "border-red-500/40 bg-red-500/5" :
                  item.status === "amber" ? "border-yellow-500/30 bg-yellow-500/5" :
                  item.status === "green" ? "border-green-500/30 bg-green-500/5" :
                  "border-border"
                }`}>
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{item.icon}</span>
                      <span className={`font-display font-bold uppercase tracking-widest text-xs ${
                        item.status === "red" ? "text-red-400" :
                        item.status === "amber" ? "text-yellow-400" :
                        item.status === "green" ? "text-green-400" :
                        "text-muted-foreground"
                      }`}>{item.label}</span>
                      <span className={`text-[10px] font-display font-bold uppercase tracking-widest ${
                        item.status === "red" ? "text-red-300" :
                        item.status === "amber" ? "text-yellow-300" :
                        item.status === "green" ? "text-green-300" :
                        "text-muted-foreground"
                      }`}>— {item.value}</span>
                    </div>
                    {item.delta && (
                      <span className={`text-[10px] font-display font-bold ${item.deltaColour}`}>{item.delta}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground font-sans leading-relaxed">{item.detail}</p>
                  <p className="text-[10px] text-muted-foreground/60 font-sans italic">{item.link}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground font-sans pt-1 border-t border-border/50">
              Accountability modifiers are live — resolving conduct reports, closing PIOs, and filing training reviews updates your score immediately. Penalties cap at −20pts (conduct), −10pts (PIOs), −10pts (fitness), −10pts (LOA). Bonuses cap at +10pts per system.
            </p>
          </div>
        );
      })()}
      </div>

      {/* ── Operational Capability Tier ───────────────────────────────────── */}
      <div className={`rounded-lg border p-5 space-y-3 ${tm.bg} ${tm.border}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Operational Capability Tier</p>
          <span className={`text-xs font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded border ${tm.badge}`}>
            ⊕ {tier} — {tm.label}
          </span>
        </div>
        <p className={`text-sm font-sans leading-relaxed ${tm.colour}`}>{tm.desc}</p>
        <div className="space-y-1">
          <div className="flex justify-between text-[10px] font-display uppercase tracking-widest text-muted-foreground">
            <span>Op Capability Score</span><span>{readiness.op_cap_score ?? 0} / 100</span>
          </div>
          <div className="h-2 bg-secondary/60 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${tm.badge.includes("blue") ? "bg-blue-500" : tm.badge.includes("yellow") ? "bg-yellow-400" : tm.badge.includes("slate") ? "bg-slate-400" : tm.badge.includes("orange") ? "bg-orange-500" : "bg-muted-foreground"}`}
              style={{ width: `${Math.min(100, readiness.op_cap_score ?? 0)}%` }} />
          </div>
        </div>
        {/* Tier ladder */}
        <div className="grid grid-cols-7 gap-1 pt-2 border-t border-border/40">
          {(["POOR","LIMITED","TACTICAL","OPERATIONAL","STRATEGIC","SOC","SOF"] as const).map(t => {
            const m = TIER_META[t];
            const active = t === tier;
            return (
              <div key={t} className={`rounded p-1.5 text-center transition-all ${active ? `${m.bg} ${m.border} border` : "opacity-30"}`}>
                <p className={`text-[9px] font-display font-bold uppercase tracking-widest ${active ? m.colour : "text-muted-foreground"}`}>{t}</p>
                <p className={`text-[8px] font-sans mt-0.5 ${active ? m.colour : "text-muted-foreground"} hidden sm:block`}>{m.label}</p>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-muted-foreground font-sans">
          Tier is calculated from: ops logged (30pts) · troop experience (25pts) · roster size (15pts) · AAR culture (10pts) · training doctrine (20pts) · joint ops record (0–15pts).
          <br/>POOR → LIMITED → TACTICAL → OPERATIONAL → STRATEGIC → SOC → SOF. Improve by filing AARs with outcomes, uploading training docs, running joint ops, and maintaining active accountability systems.
        </p>
      </div>

      {/* ── Joint Ops Combat Record ─────────────────────────────────────── */}
      {(() => {
        const cr = combatRecord;

        // Canonical tier system — matches JointOps.tsx exactly
        type JTier = "SOF" | "SOC" | "STRATEGIC" | "OPERATIONAL" | "TACTICAL" | "LIMITED" | "POOR";
        const JTIER_META: Record<JTier, { label: string; color: string; bg: string; threshold: string }> = {
          SOF:         { label: "Special Operations Forces",  color: "text-purple-300",  bg: "bg-purple-900/20 border-purple-500/30",   threshold: "1200+ pts" },
          SOC:         { label: "Special Operations Capable", color: "text-blue-300",    bg: "bg-blue-900/20 border-blue-400/30",       threshold: "900–1199 pts" },
          STRATEGIC:   { label: "Strategically Capable",      color: "text-green-300",   bg: "bg-green-900/20 border-green-400/30",     threshold: "650–899 pts" },
          OPERATIONAL: { label: "Operationally Capable",      color: "text-emerald-400", bg: "bg-emerald-900/20 border-emerald-500/30", threshold: "400–649 pts" },
          TACTICAL:    { label: "Tactically Capable",         color: "text-yellow-400",  bg: "bg-yellow-900/20 border-yellow-400/30",   threshold: "200–399 pts" },
          LIMITED:     { label: "Limited Capability",         color: "text-amber-400",   bg: "bg-amber-900/20 border-amber-500/30",     threshold: "75–199 pts" },
          POOR:        { label: "Poor Capability",            color: "text-red-400",     bg: "bg-red-900/20 border-red-500/30",         threshold: "0–74 pts" },
        };
        const JTIER_ORDER: JTier[] = ["SOF","SOC","STRATEGIC","OPERATIONAL","TACTICAL","LIMITED","POOR"];

        function getTierFromPoints(pts: number): JTier {
          if (pts >= 1200) return "SOF";
          if (pts >= 900)  return "SOC";
          if (pts >= 650)  return "STRATEGIC";
          if (pts >= 400)  return "OPERATIONAL";
          if (pts >= 200)  return "TACTICAL";
          if (pts >= 75)   return "LIMITED";
          return "POOR";
        }

        // cr.tier is already stored as a string like "OPERATIONAL" — use it directly, fall back to points
        const rawTier = cr?.tier as JTier | undefined;
        const currentTier: JTier | null = cr
          ? (JTIER_META[rawTier as JTier] ? rawTier! : getTierFromPoints(cr.total_points ?? 0))
          : null;
        const tierMeta = currentTier ? JTIER_META[currentTier] : null;
        const jointPts = readiness.score_breakdown?.joint_ops ?? 0;

        return (
          <div className="bg-card border border-border rounded-lg p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <Swords className="w-3.5 h-3.5" /> Joint Operations Record
              </p>
              {currentTier && tierMeta && (
                <span className={`text-xs font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded border ${tierMeta.bg} ${tierMeta.color}`}>
                  {currentTier}
                </span>
              )}
            </div>

            {cr ? (
              <>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: "Wins",       value: cr.wins ?? 0,       col: "text-green-400" },
                    { label: "Draws",      value: cr.draws ?? 0,      col: "text-yellow-400" },
                    { label: "Losses",     value: cr.losses ?? 0,     col: "text-red-400" },
                    { label: "Ops Played", value: cr.ops_played ?? 0, col: "text-foreground" },
                  ].map(s => (
                    <div key={s.label} className="bg-secondary/40 rounded p-2.5 text-center">
                      <p className={`font-display font-black text-xl ${s.col}`}>{s.value}</p>
                      <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs font-sans">
                  <span className="text-muted-foreground">Total Points</span>
                  <span className="font-display font-bold text-primary">{cr.total_points ?? 0} pts</span>
                </div>
                {cr.win_rate !== null && cr.win_rate !== undefined && (
                  <div className="flex items-center justify-between text-xs font-sans">
                    <span className="text-muted-foreground">Win Rate</span>
                    <span className={`font-display font-bold ${cr.win_rate >= 60 ? "text-green-400" : cr.win_rate >= 35 ? "text-yellow-400" : "text-red-400"}`}>{cr.win_rate}%</span>
                  </div>
                )}
              </>
            ) : (
              <p className="text-xs text-muted-foreground font-sans">No joint operations on record. Issue a challenge via the Joint Ops board to build your combat record.</p>
            )}

            {/* Impact on capability score */}
            <div className="pt-2 border-t border-border/50">
              <div className="flex items-center justify-between text-[10px] font-display uppercase tracking-widest text-muted-foreground mb-1.5">
                <span>Capability Score Contribution</span><span>{jointPts} / 15 pts</span>
              </div>
              <div className="h-1.5 bg-secondary/60 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-purple-500 transition-all" style={{ width: `${Math.min(100, (jointPts / 15) * 100)}%` }} />
              </div>
              <p className="text-[10px] text-muted-foreground font-sans mt-1.5">
                Joint op results directly affect your Operational Capability Score. Win rate, total ops, and tier contribute up to 15pts. Build your record to climb the leaderboard.
              </p>
            </div>

            {/* Tier ladder */}
            <div className="pt-3 border-t border-border/50">
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                <Star className="w-3 h-3 text-yellow-400" /> Tier Thresholds
              </p>
              <div className="grid grid-cols-7 gap-1">
                {JTIER_ORDER.map(t => {
                  const m = JTIER_META[t];
                  const active = t === currentTier;
                  return (
                    <div key={t} className={`rounded p-1.5 text-center border transition-all ${active ? `${m.bg} border-opacity-100` : "border-border/30 opacity-40"}`}>
                      <p className={`text-[8px] font-display font-bold uppercase tracking-wide leading-tight ${active ? m.color : "text-muted-foreground"}`}>{t}</p>
                      <p className="text-[7px] font-sans text-muted-foreground mt-0.5 leading-tight">{m.threshold}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Rep stats ─────────────────────────────────────────────────────── */}
      <div className="bg-card border border-border rounded-lg p-5 grid grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Avg Rep Score</p>
          <p className="text-2xl font-display font-bold text-foreground">{readiness.avg_rep_score || "—"}</p>
          <p className="text-[10px] text-muted-foreground">{readiness.review_count} review{readiness.review_count !== 1 ? "s" : ""}</p>
        </div>
        <div>
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Avg Experience</p>
          <p className="text-2xl font-display font-bold text-foreground">{readiness.avg_experience > 0 ? `${readiness.avg_experience}/10` : "—"}</p>
          <p className="text-[10px] text-muted-foreground">from troop ratings</p>
        </div>
      </div>

      {/* ── Training Knowledge Assessment ─────────────────────────────────── */}
      {readiness.training && readiness.training.knowledge_grade !== 'none' && (
        <div className={`border rounded-lg p-5 space-y-2 ${
          readiness.training.knowledge_grade === 'elite'      ? 'border-orange-400/40 bg-orange-400/5' :
          readiness.training.knowledge_grade === 'expert'     ? 'border-yellow-500/40 bg-yellow-500/5' :
          readiness.training.knowledge_grade === 'proficient' ? 'border-purple-500/40 bg-purple-500/5' :
          readiness.training.knowledge_grade === 'developing' ? 'border-blue-400/40 bg-blue-400/5' :
          'border-orange-500/30 bg-orange-500/5'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="font-display font-bold uppercase tracking-widest text-xs flex items-center gap-2">
              <Brain className="w-4 h-4" /> Training Knowledge
            </span>
            <span className={`text-xs font-display font-bold px-2 py-0.5 rounded border ${
              readiness.training.knowledge_grade === 'elite'      ? 'text-orange-300 border-orange-400/40' :
              readiness.training.knowledge_grade === 'expert'     ? 'text-yellow-400 border-yellow-500/40' :
              readiness.training.knowledge_grade === 'proficient' ? 'text-purple-400 border-purple-500/40' :
              readiness.training.knowledge_grade === 'developing' ? 'text-blue-300 border-blue-400/40' :
              'text-orange-500 border-orange-600/40'
            }`}>{(() => { const g = readiness.training.knowledge_grade ?? 'none'; return g.charAt(0).toUpperCase() + g.slice(1); })()}</span>
          </div>
          <p className="text-xs text-muted-foreground font-sans leading-relaxed">{readiness.training.knowledge_detail ?? `${readiness.training.doc_count ?? 0} qualifying docs at avg depth ${readiness.training.avg_depth_score ?? 0}/100`}</p>
          <div className="flex gap-3 text-xs text-muted-foreground font-sans pt-1 border-t border-border/50">
            <span>{readiness.training.doc_count ?? 0} docs · Knowledge factor: {readiness.training.knowledge_factor ?? 0}/100</span>
          </div>
        </div>
      )}
      {readiness.training && readiness.training.knowledge_grade === 'none' && (
        <div className="border border-dashed border-orange-500/30 bg-orange-500/5 rounded-lg p-4 text-xs text-orange-400 font-sans flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>No training documents filed. Upload SOPs, TTPs, and drills in the <strong>Training Docs</strong> tab to improve your capability tier score.</span>
        </div>
      )}

    </div>
  );
}

// ─── Restored Tabs ───
function ReputationTab({ group, globalIsPro }: any) {
  const [roster, setRoster] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [repData, setRepData] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isProUnit, setIsProUnit] = useState(false);
  const [form, setForm] = useState({ activity: 7, attitude: 7, experience: 5, discipline: 7, overall_vote: "commend", blacklisted: false, blacklist_reason: "", notes: "" });
  const [saved, setSaved] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    Promise.all([
      apiFetch<any>(`/milsimGroups?path=${group.id}/full`),
      apiFetch<any>(`/getProStatus?group_id=${group.id}`).catch(() => ({ is_pro: false })),
    ]).then(([g, proStatus]) => {
      const ranks: any[] = g.ranks ?? [];
      const enriched = (g.roster ?? []).map((m: any) => ({
        ...m,
        userId: m.userId ?? m.user_id ?? null,
        rankName: m.rankName ?? m.rank_name ?? ranks.find((r: any) => r.id === (m.rank_id ?? m.rankId))?.name ?? null,
      }));
      setRoster(enriched);
      setIsProUnit(!!proStatus?.is_pro);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [group.id]);

  const loadRep = async (member: any) => {
    setSelected(member);
    if (repData[member.userId]) return;
    try {
      const data = await apiFetch<any>(`/reputation?path=${member.userId}`);
      setRepData(prev => ({ ...prev, [member.userId]: data }));
    } catch {}
  };

  const submitReview = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await apiFetch(`/reputation?path=${selected.userId}`, {
        method: "POST",
        body: JSON.stringify({
          ...form,
          group_id: group.id,
          group_name: group.name,
          blacklist_reason: form.blacklisted ? form.blacklist_reason : undefined,
        }),
      });
      // Refresh rep
      const updated = await apiFetch<any>(`/reputation?path=${selected.userId}`);
      setRepData(prev => ({ ...prev, [selected.userId]: updated }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      toast({ title: "Review submitted", description: `Service file for ${selected.callsign} updated.` });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  const GRADE_COLORS: Record<string, string> = {
    ELITE: "#fbbf24", TRUSTED: "#4ade80", STANDARD: "#60a5fa",
    CAUTION: "#fb923c", "HIGH RISK": "#f87171", BLACKLISTED: "#ef4444", UNRATED: "#6b7280",
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <h3 className="font-display font-bold text-lg uppercase tracking-widest">Member Service Files</h3>
        <p className="text-sm text-muted-foreground font-sans mt-1">
          As a unit commander, you can rate your members' performance. These scores are <strong>public</strong> and visible to all commanders across the registry — they help filter out unit hoppers and inactive operators.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Member list */}
        <div className="space-y-2">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-3">Roster ({roster.length})</p>
          {roster.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg text-muted-foreground">
              <p className="text-sm font-display uppercase tracking-widest">No roster members</p>
            </div>
          ) : (
            roster.map((m: any) => {
              const rep = repData[m.userId]?.score ?? null;
              const isSelected = selected?.id === m.id;
              return (
                <button key={m.id} onClick={() => loadRep(m)}
                  className={`w-full text-left flex items-center justify-between p-3 rounded-lg border transition-all ${
                    isSelected ? "border-primary/50 bg-primary/5" : "border-border hover:border-primary/30 bg-card"
                  }`}>
                  <div>
                    <p className="font-display font-bold uppercase tracking-wider text-sm text-foreground">{m.callsign}</p>
                    {m.rankName && <p className="text-xs text-muted-foreground">{m.rankName}</p>}
                  </div>
                  {rep ? (
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-black" style={{ color: GRADE_COLORS[rep.grade] ?? "#6b7280" }}>{rep.overall}</span>
                      <span className="text-[9px] font-bold uppercase" style={{ color: GRADE_COLORS[rep.grade] ?? "#6b7280" }}>{rep.grade}</span>
                    </div>
                  ) : (
                    <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Unrated</span>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Rating panel */}
        <div>
          {!selected ? (
            <div className="flex flex-col items-center justify-center h-64 border border-dashed border-border rounded-lg text-muted-foreground text-sm text-center px-6">
              <Star className="w-8 h-8 mb-3 opacity-30" />
              <p className="font-display font-bold uppercase tracking-widest">Select a member to view or submit their service assessment</p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-display font-black uppercase tracking-widest text-lg">{selected.callsign}</p>
                  {repData[selected.userId]?.score && (
                    <p className="text-xs font-bold uppercase" style={{ color: GRADE_COLORS[repData[selected.userId].score.grade] }}>
                      {repData[selected.userId].score.grade} · {repData[selected.userId].score.overall}/100
                    </p>
                  )}
                </div>
                {repData[selected.userId]?.score?.blacklisted && (
                  <div className="flex items-center gap-1 text-xs font-bold text-red-400 bg-red-400/10 border border-red-400/30 px-2 py-1 rounded">
                    ⚠ BLACKLISTED
                  </div>
                )}
              </div>

              {/* Existing reviews summary — Pro: full detail | Free: summary only */}
              {repData[selected.userId] && (() => {
                const rd = repData[selected.userId];
                const isPro = isProUnit;
                const reviewCount = rd.reviews?.length ?? 0;
                const commends = rd.score?.commends ?? 0;
                const flags = rd.score?.flags ?? 0;
                return isPro ? (
                  <div className="space-y-2">
                    <div className="text-xs text-muted-foreground font-sans">
                      {reviewCount} review(s) on record — {commends} commend(s), {flags} flag(s)
                    </div>
                    {rd.reviews && rd.reviews.length > 0 && (
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {rd.reviews.map((rev: any, i: number) => (
                          <div key={i} className="text-[10px] bg-secondary/40 border border-border rounded px-2.5 py-1.5">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="font-display font-bold uppercase tracking-widest text-foreground">{rev.group_name ?? "Unknown Unit"}</span>
                              <span className={`font-bold uppercase ${rev.overall_vote === "commend" ? "text-green-400" : rev.overall_vote === "flag" ? "text-red-400" : "text-muted-foreground"}`}>
                                {rev.overall_vote === "commend" ? "✓ Commend" : rev.overall_vote === "flag" ? "⚑ Flag" : "— Neutral"}
                              </span>
                            </div>
                            <div className="flex gap-3 text-muted-foreground">
                              <span>Activity: {rev.activity}/10</span>
                              <span>Attitude: {rev.attitude}/10</span>
                              <span>Experience: {rev.experience}/10</span>
                              <span>Discipline: {rev.discipline}/10</span>
                            </div>
                            {rev.notes && <p className="mt-0.5 text-muted-foreground italic">"{rev.notes}"</p>}
                          </div>
                        ))}
                      </div>
                    )}
                    {rd.reviews && rd.reviews.length > 0 && (
                      <button
                        onClick={() => {
                          const rows = [["Unit","Vote","Activity","Attitude","Experience","Discipline","Notes"]];
                          rd.reviews.forEach((rev: any) => rows.push([rev.group_name ?? "", rev.overall_vote, rev.activity, rev.attitude, rev.experience, rev.discipline, rev.notes ?? ""]));
                          const csv = rows.map(r => r.map((v: any) => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
                          const blob = new Blob([csv], { type: "text/csv" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a"); a.href = url; a.download = `${selected.callsign ?? "operator"}-reputation.csv`; a.click();
                          URL.revokeObjectURL(url);
                        }}
                        className="text-[10px] font-display font-bold uppercase tracking-widest text-primary border border-primary/30 bg-primary/5 hover:bg-primary/10 px-2.5 py-1 rounded transition-all flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" /> Export CSV
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground font-sans">
                      {reviewCount} review(s) on record — {commends} commend(s), {flags} flag(s)
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-yellow-400 border border-yellow-500/30 bg-yellow-500/5 rounded px-2.5 py-1.5">
                      <Crown className="w-3 h-3 shrink-0" />
                      <span className="font-sans">Full review history + export available with <a href="/commander-pro" className="underline font-bold">Commander Pro</a></span>
                    </div>
                  </div>
                );
              })()}

              <div className="border-t border-border pt-4 space-y-3">
                <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Your Assessment</p>

                {/* Score sliders */}
                {[
                  { key: "activity", label: "Activity", hint: "How active are they in ops and events?" },
                  { key: "attitude", label: "Attitude", hint: "Conduct, teamwork, professionalism." },
                  { key: "experience", label: "Experience", hint: "Tactical knowledge and skill level." },
                  { key: "discipline", label: "Discipline", hint: "Follows orders, SOP compliance." },
                ].map(({ key, label, hint }) => (
                  <div key={key} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-xs font-display font-bold uppercase tracking-widest">{label}</label>
                        <p className="text-[9px] text-muted-foreground">{hint}</p>
                      </div>
                      <span className="text-sm font-bold font-mono text-foreground w-6 text-right">
                        {(form as any)[key]}
                      </span>
                    </div>
                    <input type="range" min={1} max={10} value={(form as any)[key]}
                      onChange={e => setForm(f => ({ ...f, [key]: parseInt(e.target.value) }))}
                      className="w-full accent-primary" />
                    <div className="flex justify-between text-[8px] text-muted-foreground"><span>1</span><span>10</span></div>
                  </div>
                ))}

                {/* Overall vote */}
                <div>
                  <label className="text-xs font-display font-bold uppercase tracking-widest mb-2 block">Overall Vote</label>
                  <div className="flex gap-2">
                    {[
                      { v: "commend", label: "✓ Commend", cls: "text-green-400 border-green-500/40 bg-green-500/10" },
                      { v: "neutral", label: "— Neutral", cls: "text-slate-400 border-border bg-secondary" },
                      { v: "flag",    label: "⚑ Flag",    cls: "text-red-400 border-red-500/40 bg-red-500/10" },
                    ].map(({ v, label, cls }) => (
                      <button key={v} type="button" onClick={() => setForm(f => ({ ...f, overall_vote: v }))}
                        className={`flex-1 py-2 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${
                          form.overall_vote === v ? cls : "border-border text-muted-foreground"
                        }`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-xs font-display font-bold uppercase tracking-widest mb-1 block">Notes (optional)</label>
                  <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Additional context or observations..."
                    className="mf-input resize-none text-xs" />
                </div>

                {/* Blacklist toggle */}
                <div className="border border-red-500/20 rounded-lg p-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.blacklisted} onChange={e => setForm(f => ({ ...f, blacklisted: e.target.checked }))}
                      className="accent-red-500" />
                    <span className="text-xs font-display font-bold uppercase tracking-widest text-red-400">Blacklist this operator</span>
                  </label>
                  {form.blacklisted && (
                    <input value={form.blacklist_reason} onChange={e => setForm(f => ({ ...f, blacklist_reason: e.target.value }))}
                      placeholder="Reason for blacklist (visible publicly)..."
                      className="mf-input text-xs" />
                  )}
                </div>

                <button onClick={submitReview} disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-sm py-2.5 rounded clip-angled-sm transition-all disabled:opacity-60">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? "✓ Saved" : <><Star className="w-4 h-4" /> Submit Assessment</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Training Docs Tab ────────────────────────────────────────────────────────
const DOC_TYPES = ["SOP", "TTP", "Field Manual", "Drill", "Reference", "Rules of Engagement", "WARNO", "OPORD", "FRAGO", "Other"] as const;

function TrainingDocsTab({ group, showMsg, globalIsPro }: any) {
  const { user } = useAuth();
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isProUnit, setIsProUnit] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [assessment, setAssessment] = useState<any | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    title: "", description: "", doc_type: "SOP" as typeof DOC_TYPES[number],
    last_reviewed_at: new Date().toISOString().split("T")[0],
  });

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const proStatus = (globalIsPro !== null && globalIsPro !== undefined)
        ? { is_pro: globalIsPro }
        : await apiFetch<any>(`/getProStatus?group_id=${group.id}`).catch(() => ({ is_pro: false }));
      const raw = await apiFetch<any>(`/api/training-docs/${group.id}`);
      console.log("[TrainingDocs] raw response:", typeof raw, Array.isArray(raw) ? raw.length : JSON.stringify(raw)?.slice(0,200));
      const docArray = Array.isArray(raw) ? raw : (raw?.docs ?? []);
      setDocs(docArray);
      setIsProUnit(!!proStatus?.is_pro);
    } catch (e: any) {
      console.error("[TrainingDocs] loadDocs failed:", e?.message ?? e);
      setDocs([]);
    } finally { setLoading(false); }
  }, [group.id]);

  const loadAssessment = useCallback(async () => {
    try {
      const r = await apiFetch<any>(`/api/stats/readiness/${group.id}`);
      if (r?.training) setAssessment(r.training);
    } catch {}
  }, [group.id]);

  useEffect(() => { loadDocs(); loadAssessment(); }, [loadDocs, loadAssessment]);

  const uploadDoc = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file || !form.title.trim()) {
      showMsg(false, "Title and file are required."); return;
    }
    const allowed = ["application/pdf", "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain", "text/markdown"];
    if (!allowed.includes(file.type)) {
      showMsg(false, "Only PDF, DOCX, DOC, TXT, or MD files are supported."); return;
    }
    if (file.size > 20 * 1024 * 1024) {
      showMsg(false, "File must be under 20MB."); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("group_id", group.id);
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("doc_type", form.doc_type);
      fd.append("last_reviewed_at", form.last_reviewed_at ? new Date(form.last_reviewed_at).toISOString() : new Date().toISOString());
      fd.append("uploaded_by", user?.id ?? "");
      fd.append("uploaded_by_username", (user as any)?.username ?? "");
      const result = await apiFetch<any>("/api/training-docs/upload", { method: "POST", body: fd, isFormData: true });
      if (result?.record?.id || result?.id) {
        const newDoc = result?.record ?? result;
        setDocs(prev => [newDoc, ...prev]);
        setForm({ title: "", description: "", doc_type: "SOP", last_reviewed_at: new Date().toISOString().split("T")[0] });
        if (fileInputRef.current) fileInputRef.current.value = "";
        setShowForm(false);
        showMsg(true, "Training document uploaded.");
        loadAssessment();
      } else if (result?.pro_required) {
        showMsg(false, "You've reached the 5-document limit for free units. Upgrade to Commander Pro for unlimited uploads.");
      } else { showMsg(false, "Upload failed — try again."); }
    } catch (e: any) {
      if (e?.pro_required || e?.message?.includes("Commander Pro")) {
        showMsg(false, "You've reached the 5-document limit. Upgrade to Commander Pro for unlimited uploads.");
      } else {
        showMsg(false, e.message ?? "Upload failed.");
      }
    } finally { setUploading(false); }
  };

  const deleteDoc = async (id: string) => {
    if (!confirm("Remove this training document?")) return;
    try {
      await apiFetch(`/api/training-docs/${group.id}/${id}`, { method: "DELETE" });
      setDocs(prev => prev.filter(d => d.id !== id));
      showMsg(true, "Document removed.");
      loadAssessment();
    } catch { showMsg(false, "Failed to remove document."); }
  };

  const markReviewed = async (doc: any) => {
    try {
      const updated = await apiFetch<any>(`/api/training-docs/${group.id}/${doc.id}`, {
        method: "PATCH", body: JSON.stringify({ mark_reviewed: true }),
      });
      setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, ...updated } : d));
      showMsg(true, "Marked as reviewed.");
      loadAssessment();
    } catch { showMsg(false, "Failed to update."); }
  };

  const gradeColor: Record<string, string> = {
    elite:      "text-orange-300 border-orange-400/40 bg-orange-400/10",
    expert:     "text-yellow-400 border-yellow-500/40 bg-yellow-500/10",
    proficient: "text-purple-400 border-purple-500/40 bg-purple-500/10",
    developing: "text-blue-300 border-blue-400/40 bg-blue-400/10",
    minimal:    "text-green-400 border-green-500/40 bg-green-500/10",
    none:       "text-red-400 border-red-500/40 bg-red-500/10",
  };
  const gradeIcon: Record<string, string> = {
    elite: "⬡⬡", expert: "⬡", proficient: "★", developing: "◆", minimal: "▲", none: "●",
  };

  const nowMs = Date.now();
  const isStale = (doc: any) => {
    const ref = doc.last_reviewed_at ?? doc.updated_date ?? doc.created_date;
    if (!ref) return true;
    return (nowMs - new Date(ref).getTime()) > 180 * 86_400_000;
  };

  const docTypeColor: Record<string, string> = {
    "SOP":                  "text-green-400 border-green-500/30 bg-green-500/10",
    "TTP":                  "text-blue-400 border-blue-500/30 bg-blue-500/10",
    "Field Manual":         "text-cyan-400 border-cyan-500/30 bg-cyan-500/10",
    "Drill":                "text-yellow-400 border-yellow-500/30 bg-yellow-500/10",
    "WARNO":                "text-orange-400 border-orange-500/30 bg-orange-500/10",
    "OPORD":                "text-orange-400 border-orange-500/30 bg-orange-500/10",
    "FRAGO":                "text-orange-300 border-orange-400/30 bg-orange-400/10",
    "Reference": "text-purple-400 border-purple-500/30 bg-purple-500/10",
    "Rules of Engagement": "text-red-400 border-red-500/30 bg-red-500/10",
    "Other": "text-muted-foreground border-border bg-secondary/40",
  };

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Assessment Banner */}
      {assessment && (
        <div className={`border rounded-lg p-5 space-y-2 ${gradeColor[assessment.knowledge_grade] ?? gradeColor.none}`}>
          <div className="flex items-center gap-3 flex-wrap">
            <Brain className="w-5 h-5 shrink-0" />
            <span className="font-display font-black uppercase tracking-widest text-sm">
              {gradeIcon[assessment.knowledge_grade ?? 'none']} {(() => { const g = assessment.knowledge_grade ?? 'none'; return assessment.knowledge_label ?? (g.charAt(0).toUpperCase() + g.slice(1)); })()}
            </span>
            <span className="ml-auto font-display font-bold text-xs opacity-70">
              Knowledge Factor: {assessment.knowledge_factor}/100
            </span>
          </div>
          <p className="text-xs font-sans leading-relaxed opacity-85">{assessment.knowledge_detail ?? `${assessment.doc_count ?? 0} qualifying docs at avg depth ${assessment.avg_depth_score ?? 0}/100`}</p>
          <div className="flex flex-wrap gap-3 pt-1 text-xs font-sans opacity-70">
            <span>{assessment.doc_count} document{assessment.doc_count !== 1 ? "s" : ""}</span>
            <span>·</span>
            <span>{assessment.doc_count ?? 0} docs total</span>
            <span>·</span>
            <span>Avg depth: {assessment.avg_depth_score}/100</span>
            {/* outdated_count not in API — removed CONF-005 */}
          </div>
          {/* Coverage badges */}
          <div className="flex flex-wrap gap-2 pt-1">
            {[
              { label: "SOPs", has: assessment.has_sop },
              { label: "TTPs", has: assessment.has_ttp },
              { label: "Rules of Engagement", has: assessment.has_roe },
              { label: "Drills", has: assessment.has_drill },
              { label: "Field Manual", has: assessment.has_fm },
            ].map(b => (
              <span key={b.label} className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${b.has ? "border-green-500/40 text-green-400 bg-green-500/10" : "border-border text-muted-foreground bg-secondary/30 opacity-50"}`}>
                {b.has ? "✓" : "✗"} {b.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold uppercase tracking-widest">Training Documents</h3>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Upload SOPs, TTPs, drills, and references. Depth and recency directly influence your unit's capability tier.</p>
        </div>
        <button onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs rounded clip-angled-sm transition-all">
          <Plus className="w-3.5 h-3.5" /> Upload Doc
        </button>
      </div>

      {/* Upload Form */}
      {showForm && (
        <div
          className="bg-card border border-primary/30 rounded-lg p-5 space-y-4">
          <h4 className="font-display font-bold uppercase tracking-widest text-sm text-primary">New Training Document</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Document Title *</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Section Attack Procedure SOP" className="mf-input w-full" />
            </div>
            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Document Type</label>
              <select value={form.doc_type} onChange={e => setForm(f => ({ ...f, doc_type: e.target.value as any }))} className="mf-input w-full">
                {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Effective / Reviewed Date</label>
              <input type="date" value={form.last_reviewed_at} onChange={e => setForm(f => ({ ...f, last_reviewed_at: e.target.value }))} className="mf-input w-full" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Description (optional)</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2} placeholder="Brief summary of what this document covers..." className="mf-input w-full resize-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">File * (PDF, DOCX, DOC, TXT, MD — max 20MB)</label>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 border border-border hover:border-primary/50 text-muted-foreground hover:text-foreground font-display font-bold uppercase tracking-wider text-xs rounded transition-all">
                  <Upload className="w-3.5 h-3.5" /> Choose File
                </button>
                <span className="text-xs text-muted-foreground font-sans" id="file-label">No file selected</span>
              </div>
              <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.txt,.md"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) { const lbl = document.getElementById('file-label'); if (lbl) lbl.textContent = f.name; } }} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowForm(false)}
              className="px-4 py-2 border border-border hover:border-destructive/50 text-muted-foreground hover:text-destructive font-display font-bold uppercase tracking-wider text-xs rounded transition-all">
              Cancel
            </button>
            {!isProUnit && docs.length >= 5 && (
              <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5 text-xs font-sans text-yellow-400 mb-2">
                <span className="text-lg">⚠️</span>
                <span>
                  <strong className="font-display font-bold uppercase">Doc limit reached (5/5).</strong>{" "}
                  Free units can store up to 5 training documents.{" "}
                  <a href="/commander-pro" className="underline font-bold hover:text-yellow-300">Upgrade to Commander Pro</a> for unlimited uploads.
                </span>
              </div>
            )}
            <button onClick={uploadDoc} disabled={uploading || (!isProUnit && docs.length >= 5)}
              className="flex items-center gap-2 px-5 py-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs rounded clip-angled-sm transition-all disabled:opacity-50">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      )}

      {/* Docs List */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
      ) : docs.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-border rounded-lg text-muted-foreground">
          <Brain className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-display text-sm uppercase tracking-widest">No Training Documents</p>
          <p className="text-xs mt-2 font-sans">Upload your SOPs, TTPs, and drill references to build your knowledge baseline.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {docs.map(doc => {
            const stale = isStale(doc);
            return (
              <div key={doc.id} className={`bg-card border rounded-lg p-4 flex items-start gap-4 transition-colors ${stale ? "border-orange-500/30" : "border-border"}`}>
                <div className="shrink-0 mt-0.5">
                  <FileCheck className={`w-5 h-5 ${stale ? "text-orange-400" : "text-primary"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-display font-bold text-sm">{doc.title}</span>
                    <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${docTypeColor[doc.doc_type] ?? docTypeColor.Other}`}>
                      {doc.doc_type}
                    </span>
                    {doc.quality_flag === 'amber' && (
                      <span className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border border-yellow-500/40 text-yellow-400 bg-yellow-500/10" title={doc.ai_summary ?? 'Thin content — review recommended'}>
                        <AlertTriangle className="w-2.5 h-2.5" /> Thin Content
                      </span>
                    )}
                    {doc.quality_flag === 'red' && (
                      <span className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border border-red-500/40 text-red-400 bg-red-500/10" title={doc.ai_summary ?? 'Suspected low-quality or invalid content'}>
                        <AlertTriangle className="w-2.5 h-2.5" /> Flagged
                      </span>
                    )}
                    {stale && (
                      <span className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border border-orange-500/40 text-orange-400 bg-orange-500/10">
                        <AlertTriangle className="w-2.5 h-2.5" /> Outdated
                      </span>
                    )}
                  </div>
                  {(doc.quality_flag === 'amber' || doc.quality_flag === 'red') && doc.ai_summary && (
                    <p className="text-[10px] font-sans mt-1 mb-1 italic text-muted-foreground opacity-70">⚠ {doc.ai_summary}</p>
                  )}
                  {doc.description && <p className="text-xs text-muted-foreground font-sans mb-2 line-clamp-2">{doc.description}</p>}
                  <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground font-sans">
                    {doc.page_count && <span>{doc.page_count} pages</span>}
                    {doc.file_name && <span>{doc.file_name}</span>}
                    {doc.file_size_bytes && <span>{(doc.file_size_bytes / 1024).toFixed(0)} KB</span>}
                    {doc.depth_score && <span>Depth: {doc.depth_score}/100</span>}
                    {doc.ai_score != null && <span>AI Score: {doc.ai_score}/100</span>}
                    <span>Reviewed: {doc.last_reviewed_at ? format(new Date(doc.last_reviewed_at), "dd MMM yyyy") : "—"}</span>
                    <span>By: {doc.uploaded_by_username ?? "—"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {doc.file_url && (
                    <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 text-muted-foreground hover:text-primary transition-colors" title="View document">
                      <Eye className="w-4 h-4" />
                    </a>
                  )}
                  {stale && (
                    <button onClick={() => markReviewed(doc)}
                      className="p-1.5 text-muted-foreground hover:text-green-400 transition-colors" title="Mark as reviewed today">
                      <FileCheck className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => deleteDoc(doc.id)}
                    className="p-1.5 text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LOA MANAGER TAB
// ─────────────────────────────────────────────────────────────────────────────
const LOA_REASONS = [
  "Personal", "Medical", "Work / Career", "Family", "Travel",
  "Education", "Mental Health", "Technical Issues", "Military Service", "Other"
] as const;

function LOATab({ group, showMsg }: { group: any; showMsg: (m: string, t?: "success"|"error") => void }) {
  const { user } = useAuth();
  const [loas, setLoas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [extTarget, setExtTarget] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    roster_id: "", callsign: "", reason_category: "Personal" as typeof LOA_REASONS[number],
    reason_detail: "", start_date: new Date().toISOString().split("T")[0],
    end_date: "", notes: ""
  });
  const [extForm, setExtForm] = useState({ extension_requested_until: "", extension_reason: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/loa?path=list&group_id=${group.id}`);
      setLoas(data.loas ?? []);
    } catch { setLoas([]); }
    setLoading(false);
  }, [group.id]);

  useEffect(() => { load(); }, [load]);

  const isCommander = group.roster?.some((r: any) =>
    r.user_id === user?.id && (r.role === "commander" || r.notes?.includes("commander") || group.owner_id === user?.id)
  ) || group.owner_id === user?.id;

  const rosterOptions = group.roster ?? [];

  const grantLOA = async () => {
    if (!form.roster_id || !form.end_date) { showMsg("Select member and end date", "error"); return; }
    setSaving(true);
    try {
      await apiFetch("/loa?path=grant", { method: "POST", body: JSON.stringify({
        group_id: group.id, ...form,
        granted_by: user?.id, granted_by_username: user?.username
      }) });
      showMsg("LOA granted ✓", "success");
      setShowForm(false);
      setForm({ roster_id: "", callsign: "", reason_category: "Personal", reason_detail: "", start_date: new Date().toISOString().split("T")[0], end_date: "", notes: "" });
      load();
    } catch (e: any) { showMsg(e.message, "error"); }
    setSaving(false);
  };

  const revoke = async (id: string) => {
    if (!confirm("Revoke this LOA?")) return;
    try {
      await apiFetch("/loa?path=revoke", { method: "POST", body: JSON.stringify({ loa_id: id }) });
      showMsg("LOA revoked", "success"); load();
    } catch (e: any) { showMsg(e.message, "error"); }
  };

  const approveExt = async (loa: any, approve: boolean) => {
    try {
      await apiFetch("/loa?path=review-extension", { method: "POST", body: JSON.stringify({
        loa_id: loa.id, approve, reviewed_by: user?.username
      }) });
      showMsg(approve ? "Extension approved ✓" : "Extension denied", "success"); load();
    } catch (e: any) { showMsg(e.message, "error"); }
  };

  const requestExt = async () => {
    if (!extForm.extension_requested_until || !extForm.extension_reason) { showMsg("Fill all fields", "error"); return; }
    try {
      await apiFetch("/loa?path=request-extension", { method: "POST", body: JSON.stringify({
        loa_id: extTarget.id, ...extForm
      }) });
      showMsg("Extension request submitted", "success"); setExtTarget(null); load();
    } catch (e: any) { showMsg(e.message, "error"); }
  };

  const statusColor: Record<string, string> = {
    Active: "text-green-400 border-green-500/30 bg-green-500/10",
    Expired: "text-muted-foreground border-border bg-secondary/30",
    Revoked: "text-red-400 border-red-500/30 bg-red-500/10",
    "Extension Requested": "text-amber-400 border-amber-500/30 bg-amber-500/10",
  };

  const myLOA = loas.find(l => l.user_id === user?.id && l.status === "Active");

  // ── Smart LOA Alerts (pure client — no automations, no credits) ──────────────
  const now = new Date();

  // 1. LOAs expiring within 48h
  const expiringSoon = loas.filter(l => {
    if (l.status !== "Active") return false;
    const end = new Date(l.end_date);
    const diffHours = (end.getTime() - now.getTime()) / (1000 * 60 * 60);
    return diffHours >= 0 && diffHours <= 48;
  });

  // 2. Overdue active LOAs (end date passed, still marked active)
  const overdueActive = loas.filter(l => {
    if (l.status !== "Active") return false;
    return new Date(l.end_date) < now;
  });

  // 3. Pre-op manning alerts — ops in the next 14 days vs active LOAs
  const upcomingOps = (group.ops ?? []).filter((op: any) => {
    if (!op.scheduled_at) return false;
    const diff = (new Date(op.scheduled_at).getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 14;
  });

  const totalActive = (group.roster ?? []).filter((r: any) => r.status === "active" || !r.status).length;

  type OpAlert = { op: any; onLoa: string[]; pct: number; severity: "critical" | "warning" };
  const opAlerts: OpAlert[] = upcomingOps.map((op: any) => {
    const opDate = new Date(op.scheduled_at);
    // Members on approved LOA that overlaps this op date
    const onLoa = loas
      .filter((l: any) => {
        if (l.status !== "Active") return false;
        const start = new Date(l.start_date);
        const end = new Date(l.end_date);
        return opDate >= start && opDate <= end;
      })
      .map((l: any) => l.callsign || "Unknown");
    const pct = totalActive > 0 ? Math.round((onLoa.length / totalActive) * 100) : 0;
    const severity: "critical" | "warning" = pct >= 40 ? "critical" : "warning";
    return { op, onLoa, pct, severity };
  }).filter((a: OpAlert) => a.onLoa.length > 0 && a.pct >= 20); // only flag if ≥20% on LOA

  return (
    <div className="space-y-6">
      {/* ── Smart LOA Alerts ─────────────────────────────────────────── */}
      {(opAlerts.length > 0 || expiringSoon.length > 0 || overdueActive.length > 0) && (
        <div className="space-y-2">

          {/* Pre-op manning alerts */}
          {opAlerts.map(({ op, onLoa, pct, severity }) => (
            <div key={op.id ?? op.name} className={`flex items-start gap-3 px-4 py-3 rounded-lg border ${severity === "critical" ? "border-red-500/50 bg-red-500/10" : "border-amber-500/40 bg-amber-500/8"}`}>
              <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${severity === "critical" ? "text-red-400" : "text-amber-400"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-xs font-display font-black uppercase tracking-wider ${severity === "critical" ? "text-red-400" : "text-amber-400"}`}>
                    {severity === "critical" ? "⚠ UNDERMANNED" : "Manning Warning"} — {op.name}
                  </p>
                  <span className={`text-[9px] font-display font-black uppercase tracking-widest px-1.5 py-0.5 rounded border ${severity === "critical" ? "border-red-500/40 bg-red-500/15 text-red-400" : "border-amber-500/30 bg-amber-500/10 text-amber-400"}`}>
                    {pct}% on LOA
                  </span>
                </div>
                <p className="text-[10px] font-sans text-muted-foreground mt-0.5">
                  {new Date(op.scheduled_at).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} ·{" "}
                  {onLoa.length} of {totalActive} active members on LOA:{" "}
                  <span className="text-foreground">{onLoa.slice(0, 4).join(", ")}{onLoa.length > 4 ? ` +${onLoa.length - 4} more` : ""}</span>
                </p>
              </div>
            </div>
          ))}

          {/* Overdue LOA — end date passed, still active */}
          {overdueActive.map(l => (
            <div key={l.id} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-red-500/40 bg-red-500/10">
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-display font-bold uppercase tracking-wider text-red-400">LOA Overdue — {l.callsign}</p>
                <p className="text-[10px] font-sans text-muted-foreground">Ended {new Date(l.end_date).toLocaleDateString("en-GB")} · Still marked active. Revoke or extend manually.</p>
              </div>
            </div>
          ))}

          {/* Expiring within 48h */}
          {expiringSoon.map(l => {
            const hoursLeft = Math.round((new Date(l.end_date).getTime() - now.getTime()) / (1000 * 60 * 60));
            return (
              <div key={l.id} className="flex items-center gap-3 px-4 py-3 rounded-lg border border-amber-500/40 bg-amber-500/10">
                <Bell className="w-4 h-4 text-amber-400 shrink-0 animate-pulse" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-display font-bold uppercase tracking-wider text-amber-400">LOA Expiring Soon — {l.callsign}</p>
                  <p className="text-[10px] font-sans text-muted-foreground">Expires in ~{hoursLeft}h on {new Date(l.end_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · {l.reason_category}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-lg uppercase tracking-widest">Leave of Absence Manager</h2>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Active LOAs freeze reputation decay and activity tracking for the duration.</p>
        </div>
        {isCommander && (
          <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-2 px-3 py-1.5 bg-primary/20 border border-primary/40 text-primary rounded font-display text-xs uppercase tracking-widest hover:bg-primary/30 transition-colors">
            <PlaneTakeoff className="w-3.5 h-3.5" /> Grant LOA
          </button>
        )}
      </div>

      {/* Grant Form */}
      {showForm && isCommander && (
        <div className="border border-primary/30 rounded-lg p-4 bg-primary/5 space-y-4">
          <h3 className="font-display font-bold text-sm uppercase tracking-widest text-primary">New LOA</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MField label="Member">
              <select value={form.roster_id} onChange={e => {
                const r = rosterOptions.find((x: any) => x.id === e.target.value);
                setForm(f => ({ ...f, roster_id: e.target.value, callsign: r?.callsign ?? "" }));
              }} className="mf-input w-full">
                <option value="">— Select member —</option>
                {rosterOptions.map((r: any) => (
                  <option key={r.id} value={r.id}>{r.callsign}</option>
                ))}
              </select>
            </MField>
            <MField label="Reason Category">
              <select value={form.reason_category} onChange={e => setForm(f => ({ ...f, reason_category: e.target.value as any }))} className="mf-input w-full">
                {LOA_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </MField>
            <MField label="Start Date">
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="mf-input w-full" />
            </MField>
            <MField label="End Date">
              <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="mf-input w-full" />
            </MField>
            <MField label="Details (optional)">
              <input type="text" value={form.reason_detail} onChange={e => setForm(f => ({ ...f, reason_detail: e.target.value }))} placeholder="Brief context..." className="mf-input w-full" />
            </MField>
            <MField label="Commander Notes (internal)">
              <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Internal notes..." className="mf-input w-full" />
            </MField>
          </div>
          <div className="flex gap-2">
            <button onClick={grantLOA} disabled={saving} className="flex items-center gap-2 px-4 py-1.5 bg-primary text-primary-foreground rounded font-display text-xs uppercase tracking-widest hover:bg-primary/90 transition-colors">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Confirm LOA
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-1.5 border border-border text-muted-foreground rounded font-display text-xs uppercase tracking-widest hover:bg-secondary transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Extension request modal for member */}
      {extTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md space-y-4">
            <h3 className="font-display font-bold text-sm uppercase tracking-widest">Request LOA Extension</h3>
            <p className="text-xs text-muted-foreground font-sans">Current end: <span className="text-foreground font-bold">{extTarget.end_date}</span></p>
            <MField label="New End Date">
              <input type="date" value={extForm.extension_requested_until} onChange={e => setExtForm(f => ({ ...f, extension_requested_until: e.target.value }))} className="mf-input w-full" />
            </MField>
            <MField label="Reason for Extension">
              <textarea value={extForm.extension_reason} onChange={e => setExtForm(f => ({ ...f, extension_reason: e.target.value }))} rows={3} placeholder="Explain why you need more time..." className="mf-input w-full resize-none" />
            </MField>
            <div className="flex gap-2">
              <button onClick={requestExt} className="flex items-center gap-2 px-4 py-1.5 bg-primary text-primary-foreground rounded font-display text-xs uppercase tracking-widest hover:bg-primary/90">
                <RefreshCw className="w-3.5 h-3.5" /> Submit Request
              </button>
              <button onClick={() => setExtTarget(null)} className="px-4 py-1.5 border border-border rounded font-display text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* My active LOA banner */}
      {myLOA && (
        <div className="border border-blue-500/30 bg-blue-500/10 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="font-display font-bold text-sm text-blue-400 uppercase tracking-widest">You are currently on LOA</p>
            <p className="text-xs text-muted-foreground font-sans mt-0.5">Until {myLOA.end_date} · {myLOA.reason_category} · Granted by {myLOA.granted_by_username}</p>
          </div>
          {myLOA.extension_status !== "Pending" && (
            <button onClick={() => { setExtTarget(myLOA); setExtForm({ extension_requested_until: "", extension_reason: "" }); }}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-blue-500/40 text-blue-400 rounded font-display text-xs uppercase tracking-widest hover:bg-blue-500/20 transition-colors">
              <RefreshCw className="w-3 h-3" /> Request Extension
            </button>
          )}
          {myLOA.extension_status === "Pending" && (
            <span className="text-xs font-display font-bold text-amber-400 uppercase tracking-widest border border-amber-500/30 bg-amber-500/10 px-2 py-1 rounded">Extension Pending</span>
          )}
        </div>
      )}

      {/* LOA list */}
      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm font-sans"><Loader2 className="w-4 h-4 animate-spin" /> Loading LOAs...</div>
      ) : loas.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground font-sans text-sm border border-dashed border-border rounded-lg">
          <PlaneTakeoff className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p>No active or historical LOAs.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {loas.map(loa => {
            const isPending = loa.extension_status === "Pending";
            const daysLeft = Math.ceil((new Date(loa.end_date).getTime() - Date.now()) / 86400000);
            return (
              <div key={loa.id} className="border border-border rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="font-display font-bold text-sm">{loa.callsign}</span>
                    <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${statusColor[loa.status] ?? "text-muted-foreground border-border"}`}>{loa.status}</span>
                    <span className="text-xs text-muted-foreground font-sans">{loa.reason_category}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {loa.status === "Active" && (
                      <span className={`text-xs font-sans ${daysLeft <= 3 ? "text-red-400" : "text-muted-foreground"}`}>
                        {daysLeft > 0 ? `${daysLeft}d remaining` : "Expires today"}
                      </span>
                    )}
                    {isPending && isCommander && (
                      <>
                        <button onClick={() => approveExt(loa, true)} className="flex items-center gap-1 px-2 py-1 text-xs font-display font-bold uppercase bg-green-500/10 border border-green-500/30 text-green-400 rounded hover:bg-green-500/20 transition-colors">
                          <Check className="w-3 h-3" /> Approve Ext.
                        </button>
                        <button onClick={() => approveExt(loa, false)} className="flex items-center gap-1 px-2 py-1 text-xs font-display font-bold uppercase bg-red-500/10 border border-red-500/30 text-red-400 rounded hover:bg-red-500/20 transition-colors">
                          <X className="w-3 h-3" /> Deny
                        </button>
                      </>
                    )}
                    {isCommander && loa.status === "Active" && (
                      <button onClick={() => revoke(loa.id)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors" title="Revoke LOA">
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground font-sans flex-wrap">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {loa.start_date} → {loa.end_date}</span>
                  <span>Granted by <span className="text-foreground">{loa.granted_by_username}</span></span>
                  {loa.reason_detail && <span>"{loa.reason_detail}"</span>}
                </div>
                {isPending && (
                  <div className="text-xs font-sans bg-amber-500/10 border border-amber-500/20 rounded p-2 flex items-center gap-2">
                    <RefreshCw className="w-3 h-3 text-amber-400 flex-shrink-0" />
                    <span><span className="text-amber-400 font-bold">Extension requested</span> until {loa.extension_requested_until} — "{loa.extension_reason}"</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVITY CALENDAR TAB
// ─────────────────────────────────────────────────────────────────────────────
// ─── Activity Calendar ────────────────────────────────────────────────────────

const EV_STYLE: Record<string, string> = {
  Op:       "bg-red-500/15 border-red-500/40 text-red-300",
  Training: "bg-yellow-500/15 border-yellow-500/40 text-yellow-300",
  Meeting:  "bg-purple-500/15 border-purple-500/40 text-purple-300",
  Social:   "bg-green-500/15 border-green-500/40 text-green-300",
  Admin:    "bg-secondary border-border text-muted-foreground",
  Other:    "bg-secondary border-border text-muted-foreground",
  LOA:      "bg-blue-500/10 border-blue-500/30 text-blue-400",
  AAR:      "bg-green-500/10 border-green-500/30 text-green-400",
  Campaign: "bg-yellow-500/10 border-yellow-500/30 text-yellow-300",
  JointOp:  "bg-purple-500/10 border-purple-500/30 text-purple-300",
};

function ActivityCalendarTab({ group, showMsg, globalIsPro }: { group: any; showMsg: (m: string, t?: "success"|"error") => void; globalIsPro?: boolean | null }) {
  const { user } = useAuth();
  const now = new Date();
  const [viewYear, setViewYear]   = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [events, setEvents]   = useState<any[]>([]);
  const [loas, setLoas]       = useState<any[]>([]);
  const [aars, setAars]       = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [jointOps, setJointOps]   = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isProCal, setIsProCal] = useState<boolean | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [showForm, setShowForm]   = useState(false);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", event_type: "Op" as typeof EVENT_TYPES[number],
    scheduled_at: "", end_date: "", description: "", game: "", status: "Planned"
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Pro check
      const proData = (globalIsPro !== undefined && globalIsPro !== null)
        ? { is_pro: globalIsPro }
        : await fetch(`${PRO_STATUS_URL_MANAGE}?group_id=${group.id}`).then(r => r.json()).catch(() => ({ is_pro: false }));
      setIsProCal(proData.is_pro);
      if (!proData.is_pro) { setLoading(false); return; }

      const [opsData, loaData, aarData, campRes, jointOpsRes] = await Promise.all([
        apiFetch(`/activityCalendar?path=list&group_id=${group.id}`),
        apiFetch(`/loa?path=list&group_id=${group.id}`),
        apiFetch(`/milsimAars?path=list&group_id=${group.id}`),
        fetch(`${CAMPAIGNS_URL}?path=list&group_id=${group.id}`).then(r => r.json()).catch(() => []),
        apiFetch(`/jointOpsData?type=ops`).catch(() => ({ ops: [] })),
      ]);
      setEvents(opsData.events ?? []);
      setLoas(loaData.loas ?? []);
      setAars(aarData.aars ?? []);
      setCampaigns(Array.isArray(campRes) ? campRes : []);
      const allJointOps = Array.isArray(jointOpsRes?.ops) ? jointOpsRes.ops : [];
      setJointOps(allJointOps.filter((j: any) => j.group_a_id === group.id || j.group_b_id === group.id));
    } catch { setEvents([]); setLoas([]); setAars([]); setCampaigns([]); setJointOps([]); }
    setLoading(false);
  }, [group.id]);

  useEffect(() => { load(); }, [load]);

  // ── Pro gate ──────────────────────────────────────────────────────────────
  if (isProCal === null) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  if (!isProCal) return (
    <div className="flex flex-col items-center justify-center py-20 text-center max-w-md mx-auto gap-6">
      <div className="w-16 h-16 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center justify-center">
        <Crown className="w-8 h-8 text-yellow-400" />
      </div>
      <div>
        <h3 className="font-display font-black text-2xl uppercase tracking-wider text-foreground mb-2">Commander Pro Required</h3>
        <p className="text-muted-foreground font-sans leading-relaxed">
          The Activity Calendar is a Pro feature. Upgrade to unlock full campaign-linked scheduling, LOA tracking, AAR timelines, and monthly readiness views.
        </p>
      </div>
      <a href="/commander-pro"
        className="inline-flex items-center gap-3 bg-yellow-500 hover:bg-yellow-400 text-black font-display font-black uppercase tracking-widest text-sm px-8 py-3 rounded transition-all shadow-[0_0_20px_hsla(48,96%,53%,0.3)]">
        <Crown className="w-4 h-4" /> Upgrade to Pro
      </a>
    </div>
  );

  const save = async () => {
    if (!form.title || !form.scheduled_at) { showMsg("Title and date required", "error"); return; }
    setSaving(true);
    try {
      if (editTarget) {
        await apiFetch("/activityCalendar?path=update", { method: "POST", body: JSON.stringify({ id: editTarget.id, ...form }) });
        showMsg("Event updated", "success");
      } else {
        await apiFetch("/activityCalendar?path=create", { method: "POST", body: JSON.stringify({ group_id: group.id, created_by: user?.username, ...form }) });
        showMsg("Event scheduled", "success");
      }
      setShowForm(false); setEditTarget(null); load();
    } catch (e: any) { showMsg(e.message, "error"); }
    setSaving(false);
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this event?")) return;
    await apiFetch("/activityCalendar?path=delete", { method: "POST", body: JSON.stringify({ id }) });
    showMsg("Deleted", "success"); load();
  };

  const openEdit = (ev: any) => {
    setEditTarget(ev);
    setForm({ title: ev.title ?? ev.name ?? "", event_type: ev.event_type ?? "Op", scheduled_at: ev.scheduled_at ? ev.scheduled_at.slice(0,16) : "", end_date: ev.end_date ?? "", description: ev.description ?? "", game: ev.game ?? "", status: ev.status ?? "Planned" });
    setShowForm(true);
    setSelectedDay(null);
  };

  const firstDay    = new Date(viewYear, viewMonth, 1);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const startOffset = firstDay.getDay();
  const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAY_NAMES   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  // ── helpers ──────────────────────────────────────────────────────────────────
  const dayDate = (day: number) => new Date(viewYear, viewMonth, day);

  const opsForDay = (day: number) => events.filter(e => {
    if (!e.scheduled_at) return false;
    const d = new Date(e.scheduled_at);
    return d.getFullYear() === viewYear && d.getMonth() === viewMonth && d.getDate() === day;
  });

  const loasForDay = (day: number) => {
    const date = dayDate(day);
    return loas.filter(l => {
      if (!l.start_date || !l.end_date) return false;
      const start = new Date(l.start_date); const end = new Date(l.end_date);
      // include all statuses so even pending LOAs show
      return date >= start && date <= end;
    });
  };

  // AARs land on the op's scheduled_at date OR their own created_date
  const aarsForDay = (day: number) => aars.filter(a => {
    // try to match to a linked op's date first
    if (a.op_id) {
      const linkedOp = events.find(e => e.id === a.op_id);
      if (linkedOp?.scheduled_at) {
        const d = new Date(linkedOp.scheduled_at);
        return d.getFullYear() === viewYear && d.getMonth() === viewMonth && d.getDate() === day;
      }
    }
    // fallback: use created_date
    const d = new Date(a.created_date ?? a.created_at ?? "");
    return d.getFullYear() === viewYear && d.getMonth() === viewMonth && d.getDate() === day;
  });

  const campaignsForDay = (day: number) => {
    const date = dayDate(day);
    return campaigns.filter(camp => {
      if (!camp.start_date || !camp.end_date) return false;
      const start = new Date(camp.start_date);
      const end = new Date(camp.end_date);
      return date >= start && date <= end && camp.status !== "Archived";
    });
  };

  const jointOpsForDay = (day: number) => {
    return jointOps.filter(j => {
      const d = j.scheduled_at ? new Date(j.scheduled_at) : null;
      if (!d) return false;
      return d.getFullYear() === viewYear && d.getMonth() === viewMonth && d.getDate() === day;
    });
  };

  const allItemsForDay = (day: number) => [
    ...opsForDay(day).map(e => ({ kind: e.event_type ?? "Op", label: e.title ?? e.name, raw: e })),
    ...loasForDay(day).map(l => ({ kind: "LOA", label: `LOA: ${l.callsign}`, raw: l })),
    ...aarsForDay(day).map(a => ({ kind: "AAR", label: `AAR: ${a.title ?? a.op_name}`, raw: a })),
    ...campaignsForDay(day).map(camp => ({ kind: "Campaign", label: `Campaign: ${camp.name}`, raw: camp })),
    ...jointOpsForDay(day).map(j => ({ kind: "JointOp", label: `Joint Op vs ${j.group_b_name ?? j.group_a_name ?? "Unknown"}`, raw: j })),
  ];

  const upcomingOps = events
    .filter(e => new Date(e.scheduled_at) >= now)
    .sort((a,b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime())
    .slice(0, 8);

  // ── day detail panel ─────────────────────────────────────────────────────────
  const renderDayDetail = () => {
    if (selectedDay === null) return null;
    const items = allItemsForDay(selectedDay);
    const dayOps = opsForDay(selectedDay);
    const dayLoas = loasForDay(selectedDay);
    const dayAars = aarsForDay(selectedDay);
    const dayCamps = campaignsForDay(selectedDay);
    return (
      <div className="border border-border rounded-lg bg-card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-sm uppercase tracking-widest">
            {selectedDay} {MONTH_NAMES[viewMonth]} {viewYear}
          </h3>
          <button onClick={() => setSelectedDay(null)} className="p-1 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        {items.length === 0 && <p className="text-xs text-muted-foreground font-sans">Nothing logged for this day.</p>}
        {dayOps.length > 0 && (
          <div>
            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Operations / Events</p>
            <div className="space-y-2">
              {dayOps.map(ev => {
                const aarCount = aars.filter(a => a.op_id === ev.id).length;
                return (
                  <div key={ev.id} className={`rounded-lg border p-3 ${EV_STYLE[ev.event_type] ?? EV_STYLE.Other}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-display font-bold text-sm">{ev.title ?? ev.name}</p>
                        {ev.game && <p className="text-[10px] font-sans opacity-70 mt-0.5">{ev.game}</p>}
                        {ev.description && <p className="text-xs font-sans mt-1 opacity-80">{ev.description}</p>}
                        {aarCount > 0 && <p className="text-[10px] font-display font-bold uppercase tracking-widest text-green-400 mt-1">✓ {aarCount} AAR{aarCount > 1 ? "s" : ""} filed</p>}
                        {aarCount === 0 && ev.status === "Completed" && <p className="text-[10px] font-display font-bold uppercase tracking-widest text-amber-400 mt-1">⚠ No AAR filed</p>}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => openEdit(ev)} className="p-1 opacity-60 hover:opacity-100 transition-opacity"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => remove(ev.id)} className="p-1 opacity-60 hover:opacity-100 text-red-400 transition-opacity"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                        ev.status === "Active" ? "border-red-500/40 text-red-400" :
                        ev.status === "Completed" ? "border-border text-muted-foreground" :
                        ev.status === "Confirmed" ? "border-green-500/40 text-green-400" :
                        "border-amber-500/40 text-amber-400"}`}>{ev.status}</span>
                      {ev.scheduled_at && <span className="text-[9px] font-sans opacity-60">{new Date(ev.scheduled_at).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {dayAars.length > 0 && (
          <div>
            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-green-400 mb-2">After Action Reports</p>
            <div className="space-y-1.5">
              {dayAars.map(a => (
                <div key={a.id} className="rounded border border-green-500/20 bg-green-500/5 p-2.5">
                  <p className="font-display font-bold text-xs text-green-300">{a.title ?? a.op_name}</p>
                  {a.outcome && <p className="text-[10px] font-sans text-muted-foreground mt-0.5">{a.outcome}</p>}
                  {a.lessons_learned && <p className="text-[10px] font-sans text-muted-foreground mt-0.5 line-clamp-2">Lessons: {a.lessons_learned}</p>}
                  <p className="text-[9px] text-muted-foreground font-sans mt-1">by {a.author_username ?? "—"}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {dayLoas.length > 0 && (
          <div>
            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-blue-400 mb-2">Leave of Absence</p>
            <div className="space-y-1.5">
              {dayLoas.map(l => (
                <div key={l.id} className="rounded border border-blue-500/20 bg-blue-500/5 p-2.5 flex items-center justify-between">
                  <div>
                    <p className="font-display font-bold text-xs text-blue-300">{l.callsign}</p>
                    <p className="text-[10px] font-sans text-muted-foreground">{l.reason_category}{l.reason_detail ? ` — ${l.reason_detail}` : ""}</p>
                  </div>
                  <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                    l.status === "Active" ? "border-green-500/40 text-green-400" :
                    l.status === "Extension Requested" ? "border-amber-500/40 text-amber-400" :
                    "border-border text-muted-foreground"}`}>{l.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {(() => {
          const dayJoints = selectedDay !== null ? jointOpsForDay(selectedDay) : [];
          if (dayJoints.length === 0) return null;
          return (
            <div>
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-purple-400 mb-2 flex items-center gap-1">⚔ Joint Operations</p>
              <div className="space-y-1.5">
                {dayJoints.map((j: any) => {
                  const opponent = j.group_a_id === group.id ? j.group_b_name : j.group_a_name;
                  const myTag    = j.group_a_id === group.id ? j.group_a_tag  : j.group_b_tag;
                  const oppTag   = j.group_a_id === group.id ? j.group_b_tag  : j.group_a_tag;
                  return (
                    <div key={j.id} className="rounded border border-purple-500/20 bg-purple-500/5 p-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="font-display font-bold text-xs text-purple-300">
                            [{myTag ?? "—"}] vs [{oppTag ?? "—"}] {opponent ?? "Unknown"}
                          </p>
                          {j.game && <p className="text-[10px] font-sans text-muted-foreground mt-0.5">{j.game} — {j.op_type ?? "Op"}</p>}
                          {j.outcome && (
                            <p className={`text-[10px] font-display font-bold uppercase tracking-widest mt-1 ${j.outcome === "Win" ? "text-green-400" : j.outcome === "Loss" ? "text-red-400" : "text-yellow-400"}`}>
                              {j.outcome}
                            </p>
                          )}
                        </div>
                        <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                          j.status === "Completed" ? "border-border text-muted-foreground" :
                          j.status === "Scheduled" ? "border-green-500/40 text-green-400" :
                          "border-amber-500/40 text-amber-400"}`}>{j.status ?? "—"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
        {(() => {
          const dayCamps = selectedDay !== null ? campaignsForDay(selectedDay) : [];
          if (dayCamps.length === 0) return null;
          return (
            <div>
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-yellow-400 mb-2 flex items-center gap-1"><Zap className="w-3 h-3" /> Active Campaigns</p>
              <div className="space-y-1.5">
                {dayCamps.map(camp => (
                  <div key={camp.id} className="rounded border border-yellow-500/20 bg-yellow-500/5 p-3 space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-display font-bold text-xs text-yellow-300">{camp.name}</p>
                      <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                        camp.status === "Active" ? "border-green-500/40 text-green-400" :
                        camp.status === "Planning" ? "border-yellow-500/40 text-yellow-400" :
                        camp.status === "Completed" ? "border-border text-muted-foreground" :
                        "border-border text-muted-foreground"
                      }`}>{camp.status}</span>
                    </div>
                    {camp.objective && <p className="text-[10px] font-sans text-muted-foreground line-clamp-2">{camp.objective}</p>}
                    {camp.tags && camp.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {camp.tags.map((t: string) => <span key={t} className="text-[8px] font-display uppercase tracking-widest px-1 py-0.5 bg-secondary border border-border rounded">{t}</span>)}
                      </div>
                    )}
                    {camp.start_date && camp.end_date && (
                      <p className="text-[9px] font-sans text-muted-foreground opacity-70">
                        {new Date(camp.start_date).toLocaleDateString("en-GB")} – {new Date(camp.end_date).toLocaleDateString("en-GB")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-lg uppercase tracking-widest">Activity Calendar</h2>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Full ops timeline — ops, training, LOAs, and AARs on their correct dates. Click any day to review.</p>
        </div>
        <button onClick={() => { setShowForm(v => !v); setEditTarget(null); setSelectedDay(null); setForm({ title:"", event_type:"Op", scheduled_at:"", end_date:"", description:"", game:"", status:"Planned" }); }}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary/20 border border-primary/40 text-primary rounded font-display text-xs uppercase tracking-widest hover:bg-primary/30 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Schedule Event
        </button>
      </div>

      {showForm && (
        <div className="border border-primary/30 rounded-lg p-4 bg-primary/5 space-y-4">
          <h3 className="font-display font-bold text-sm uppercase tracking-widest text-primary">{editTarget ? "Edit Event" : "New Event"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MField label="Title"><input value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} placeholder="e.g. Sunday Op — Grid 742" className="mf-input w-full" /></MField>
            <MField label="Type"><select value={form.event_type} onChange={e => setForm(f=>({...f,event_type:e.target.value as any}))} className="mf-input w-full">{EVENT_TYPES.map(t=><option key={t} value={t}>{t}</option>)}</select></MField>
            <MField label="Date / Time"><input type="datetime-local" value={form.scheduled_at} onChange={e => setForm(f=>({...f,scheduled_at:e.target.value}))} className="mf-input w-full" /></MField>
            <MField label="End Date (optional)"><input type="date" value={form.end_date} onChange={e => setForm(f=>({...f,end_date:e.target.value}))} className="mf-input w-full" /></MField>
            <MField label="Game"><input value={form.game} onChange={e => setForm(f=>({...f,game:e.target.value}))} placeholder="e.g. Arma 3" className="mf-input w-full" /></MField>
            <MField label="Status"><select value={form.status} onChange={e => setForm(f=>({...f,status:e.target.value}))} className="mf-input w-full">{["Planned","Confirmed","Active","Completed","Cancelled"].map(s=><option key={s} value={s}>{s}</option>)}</select></MField>
            <div className="md:col-span-2"><MField label="Description"><textarea value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} rows={2} className="mf-input w-full resize-none" /></MField></div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-1.5 bg-primary text-primary-foreground rounded font-display text-xs uppercase tracking-widest hover:bg-primary/90">{saving?<Loader2 className="w-3.5 h-3.5 animate-spin"/>:<Save className="w-3.5 h-3.5"/>} {editTarget?"Save Changes":"Schedule"}</button>
            <button onClick={() => { setShowForm(false); setEditTarget(null); }} className="px-4 py-1.5 border border-border rounded font-display text-xs uppercase tracking-widest">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm font-sans"><Loader2 className="w-4 h-4 animate-spin" /> Loading calendar...</div>
      ) : (
        <div className="space-y-4">
          {/* Month navigation */}
          <div className="flex items-center gap-4 justify-between">
            <button onClick={() => { const d=new Date(viewYear,viewMonth-1); setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); setSelectedDay(null); }} className="p-1.5 border border-border rounded hover:bg-secondary transition-colors"><ChevronDown className="w-4 h-4 rotate-90" /></button>
            <span className="font-display font-bold text-sm uppercase tracking-widest">{MONTH_NAMES[viewMonth]} {viewYear}</span>
            <button onClick={() => { const d=new Date(viewYear,viewMonth+1); setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); setSelectedDay(null); }} className="p-1.5 border border-border rounded hover:bg-secondary transition-colors"><ChevronDown className="w-4 h-4 -rotate-90" /></button>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 text-[9px] font-display font-bold uppercase tracking-widest">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-400/60" />Op</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-yellow-400/60" />Training</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-green-400/60" />AAR</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-blue-300/60 border border-blue-400/40" />LOA</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-yellow-400/60" />Campaign</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-purple-400/60" />Joint Op</span>
            <span className="flex items-center gap-1 text-muted-foreground">Click any day for full review</span>
          </div>

          {/* Calendar grid */}
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="grid grid-cols-7 border-b border-border">
              {DAY_NAMES.map(d => <div key={d} className="text-center text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground py-2">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: startOffset }).map((_,i) => <div key={`pad-${i}`} className="border-b border-r border-border/40 min-h-[72px] bg-secondary/20" />)}
              {Array.from({ length: daysInMonth }).map((_,i) => {
                const day = i + 1;
                const items = allItemsForDay(day);
                const isToday = viewYear === now.getFullYear() && viewMonth === now.getMonth() && day === now.getDate();
                const isSelected = selectedDay === day;
                const hasMissedAAR = opsForDay(day).some(e => e.status === "Completed" && !aars.find(a => a.op_id === e.id));
                return (
                  <div key={day} onClick={() => setSelectedDay(isSelected ? null : day)}
                    className={`border-b border-r border-border/40 min-h-[72px] p-1.5 cursor-pointer transition-colors
                      ${isToday ? "bg-primary/5" : ""}
                      ${isSelected ? "ring-1 ring-inset ring-primary/60 bg-primary/10" : "hover:bg-secondary/20"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-display font-bold ${isToday ? "text-primary" : "text-muted-foreground"}`}>{day}</span>
                      {hasMissedAAR && <span title="Completed op with no AAR" className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
                    </div>
                    <div className="space-y-0.5">
                      {items.slice(0, 3).map((item, idx) => (
                        <div key={idx} className={`text-[8px] font-display font-bold uppercase tracking-wide px-1 py-0.5 rounded border truncate ${EV_STYLE[item.kind] ?? EV_STYLE.Other}`}>
                          {item.label}
                        </div>
                      ))}
                      {items.length > 3 && <div className="text-[8px] text-muted-foreground font-sans pl-0.5">+{items.length - 3}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Day detail panel */}
          {renderDayDetail()}

          {/* Upcoming */}
          {upcomingOps.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-display font-bold text-xs uppercase tracking-widest text-muted-foreground">Upcoming</h3>
              {upcomingOps.map(ev => (
                <div key={ev.id} className="flex items-center justify-between gap-4 border border-border rounded-lg px-4 py-3 hover:bg-secondary/10 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border flex-shrink-0 ${EV_STYLE[ev.event_type] ?? EV_STYLE.Other}`}>{ev.event_type}</span>
                    <span className="font-display font-bold text-sm truncate">{ev.title ?? ev.name}</span>
                    {ev.game && <span className="text-xs text-muted-foreground font-sans hidden md:block">{ev.game}</span>}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-muted-foreground font-sans">{new Date(ev.scheduled_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>
                    <button onClick={() => openEdit(ev)} className="p-1.5 text-muted-foreground hover:text-primary transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(ev.id)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Analytics (Pro) ──────────────────────────────────────────────────────────

function MiniBar({ value, max, color = "bg-primary" }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-display font-bold text-muted-foreground w-6 text-right">{value}</span>
    </div>
  );
}

function SparkLine({ data, color = "#22d3ee" }: { data: number[]; color?: string }) {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const w = 120, h = 40, pad = 4;
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1 || 1)) * (w - pad * 2);
    const y = h - pad - ((v / max) * (h - pad * 2));
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="opacity-80">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {data.map((v, i) => {
        const x = pad + (i / (data.length - 1 || 1)) * (w - pad * 2);
        const y = h - pad - ((v / max) * (h - pad * 2));
        return <circle key={i} cx={x} cy={y} r="2.5" fill={color} />;
      })}
    </svg>
  );
}

function StatCard({ label, value, sub, trend, sparkData, color }: { label: string; value: string | number; sub?: string; trend?: string; sparkData?: number[]; color?: string }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5 flex flex-col gap-2">
      <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
      <div className="flex items-end justify-between gap-2">
        <p className={`font-display font-black text-3xl ${color || "text-foreground"}`}>{value}</p>
        {sparkData && <SparkLine data={sparkData} color={color === "text-yellow-400" ? "#facc15" : color === "text-green-400" ? "#4ade80" : "#22d3ee"} />}
      </div>
      {sub && <p className="text-xs font-sans text-muted-foreground">{sub}</p>}
      {trend && <p className="text-xs font-sans text-primary">{trend}</p>}
    </div>
  );
}

function AnalyticsTab({ group, globalIsPro }: any) {
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState<boolean | null>(null);

  useEffect(() => {
    const proPromise = (globalIsPro !== undefined && globalIsPro !== null)
      ? Promise.resolve({ is_pro: globalIsPro })
      : fetch(`${PRO_STATUS_URL_MANAGE}?group_id=${group.id}`).then(r => r.json()).catch(() => ({ is_pro: false }));
    proPromise
      .then(s => {
        setIsPro(s.is_pro);
        if (s.is_pro) {
          return fetch(`${ANALYTICS_URL}?group_id=${group.id}`)
            .then(r => r.json())
            .then(setData)
            .catch(() => {});
        }
      })
      .catch(() => setIsPro(false))
      .finally(() => setLoading(false));
  }, [group.id, globalIsPro]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground font-sans">Loading analytics...</p>
    </div>
  );

  // Pro gate
  if (!isPro) return (
    <div className="flex flex-col items-center justify-center py-20 text-center max-w-md mx-auto gap-6">
      <div className="w-16 h-16 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center justify-center">
        <Crown className="w-8 h-8 text-yellow-400" />
      </div>
      <div>
        <h3 className="font-display font-black text-2xl uppercase tracking-wider text-foreground mb-2">Commander Pro Required</h3>
        <p className="text-muted-foreground font-sans leading-relaxed">
          Analytics is a Pro feature. Upgrade your unit to unlock full attendance tracking, ops frequency charts, roster growth, duty status breakdown, and more.
        </p>
      </div>
      <a href="/commander-pro"
        className="inline-flex items-center gap-3 bg-yellow-500 hover:bg-yellow-400 text-black font-display font-black uppercase tracking-widest text-sm px-8 py-3 rounded transition-all shadow-[0_0_20px_hsla(48,96%,53%,0.3)]"
      >
        <Crown className="w-4 h-4" /> Upgrade to Pro — £10/mo
      </a>
      <p className="text-xs text-muted-foreground font-sans">Cancel anytime. Instant activation.</p>
    </div>
  );

  if (!data) return (
    <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
      <AlertCircle className="w-8 h-8 text-destructive" />
      <p className="text-muted-foreground font-sans">Failed to load analytics data.</p>
    </div>
  );

  const { summary = {} as any, charts = {}, top_operators = [], top_awards = [] } = data ?? {};

  // Format month labels
  const monthLabels = Object.keys(charts?.ops_per_month ?? {}).map(k => {
    const [y, m] = k.split("-");
    return new Date(parseInt(y), parseInt(m) - 1).toLocaleString("default", { month: "short" });
  });
  const opsData = Object.values(charts?.ops_per_month ?? {}) as number[];
  const attendData = Object.values(charts?.attendance_per_month ?? {}) as number[];
  const joinData = Object.values(charts?.join_per_month ?? {}) as number[];

  // Outcome colors
  const outcomeColor: Record<string, string> = { victory: "bg-green-500", defeat: "bg-red-500", draw: "bg-yellow-500", incomplete: "bg-secondary" };
  const totalOutcomes = Object.values(charts?.aar_outcomes ?? {}).reduce((a: any, b: any) => a + b, 0) as number;

  return (
    <div className="space-y-8 max-w-5xl">

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-yellow-500/10 border border-yellow-500/30 rounded flex items-center justify-center">
          <BarChart3 className="w-4 h-4 text-yellow-400" />
        </div>
        <div>
          <h2 className="font-display font-bold uppercase tracking-wider text-foreground">Unit Analytics</h2>
          <p className="text-xs font-sans text-muted-foreground">Live data — all time unless noted</p>
        </div>
        <span className="ml-auto text-[10px] font-display font-bold uppercase tracking-widest px-2 py-1 rounded border bg-yellow-500/10 text-yellow-400 border-yellow-500/30 flex items-center gap-1">
          <Crown className="w-3 h-3" /> Pro
        </span>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Active Roster" value={summary.active_roster} sub={`${summary.total_roster} total enrolled`} sparkData={joinData} color="text-foreground" />
        <StatCard label="Ops Completed" value={summary.completed_ops} sub={`${summary.scheduled_ops} upcoming`} sparkData={opsData} color="text-primary" />
        <StatCard label="Avg Attendance" value={summary.avg_attendance} sub="per op (from AARs)" color="text-yellow-400" />
        <StatCard label="Op Win Rate" value={`${summary.op_win_rate}%`} sub={`${summary.total_aars} AARs filed`} color={(summary.op_win_rate ?? 0) >= 60 ? "text-green-400" : (summary.op_win_rate ?? 0) >= 40 ? "text-yellow-400" : "text-red-400"} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Awards" value={summary.total_awards} color="text-yellow-400" />
        <StatCard label="Active LOAs" value={summary.active_loas} color={(summary.active_loas ?? 0) > 3 ? "text-yellow-400" : "text-muted-foreground"} />
        <StatCard label="Briefings Filed" value={summary.total_aars} color="text-foreground" />
        <StatCard label="Scheduled Ops" value={summary.scheduled_ops} color="text-primary" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Ops per month */}
        <div className="bg-card border border-border rounded-lg p-5">
          <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-4">Ops Completed — Last 6 Months</p>
          <div className="flex items-end gap-2 h-24">
            {opsData.map((v, i) => {
              const maxV = Math.max(...opsData, 1);
              const pct = (v / maxV) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] text-muted-foreground font-display font-bold">{v}</span>
                  <div className="w-full bg-primary/80 rounded-t transition-all" style={{ height: `${Math.max(pct * 0.7, v > 0 ? 4 : 2)}px` }} />
                  <span className="text-[9px] text-muted-foreground">{monthLabels[i]}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Roster growth */}
        <div className="bg-card border border-border rounded-lg p-5">
          <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-4">New Recruits — Last 6 Months</p>
          <div className="flex items-end gap-2 h-24">
            {joinData.map((v, i) => {
              const maxV = Math.max(...joinData, 1);
              const pct = (v / maxV) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[10px] text-muted-foreground font-display font-bold">{v}</span>
                  <div className="w-full bg-green-500/70 rounded-t transition-all" style={{ height: `${Math.max(pct * 0.7, v > 0 ? 4 : 2)}px` }} />
                  <span className="text-[9px] text-muted-foreground">{monthLabels[i]}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* Roster status */}
        <div className="bg-card border border-border rounded-lg p-5">
          <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-4">Roster by Status</p>
          <div className="space-y-2.5">
            {Object.entries(charts.roster_by_status).map(([status, count]) => (
              <div key={status}>
                <div className="flex justify-between text-xs font-sans mb-1">
                  <span className="text-foreground capitalize">{status}</span>
                  <span className="text-muted-foreground">{count as number}</span>
                </div>
                <MiniBar value={count as number} max={summary.total_roster} color="bg-primary" />
              </div>
            ))}
          </div>
        </div>

        {/* AAR outcomes */}
        <div className="bg-card border border-border rounded-lg p-5">
          <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-4">Op Outcomes</p>
          {totalOutcomes === 0 ? (
            <p className="text-sm text-muted-foreground font-sans py-4 text-center">No AARs filed yet</p>
          ) : (
            <div className="space-y-2.5">
              {Object.entries(charts.aar_outcomes).map(([outcome, count]) => (
                count as number > 0 ? (
                  <div key={outcome}>
                    <div className="flex justify-between text-xs font-sans mb-1">
                      <span className="text-foreground capitalize">{outcome}</span>
                      <span className="text-muted-foreground">{count as number} ({Math.round(((count as number) / totalOutcomes) * 100)}%)</span>
                    </div>
                    <MiniBar value={count as number} max={totalOutcomes} color={outcomeColor[outcome] || "bg-secondary"} />
                  </div>
                ) : null
              ))}
            </div>
          )}
        </div>

        {/* Top operators */}
        <div className="bg-card border border-border rounded-lg p-5">
          <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-1.5">
            <Trophy className="w-3 h-3 text-yellow-400" /> Top Operators
          </p>
          {top_operators.length === 0 ? (
            <p className="text-sm text-muted-foreground font-sans py-4 text-center">No op data yet</p>
          ) : (
            <div className="space-y-2">
              {top_operators.map((op: any, i: number) => (
                <div key={op.callsign} className="flex items-center gap-3">
                  <span className={`text-xs font-display font-black w-4 ${i === 0 ? "text-yellow-400" : i === 1 ? "text-zinc-400" : i === 2 ? "text-orange-500" : "text-muted-foreground"}`}>
                    {i + 1}
                  </span>
                  <span className="flex-1 text-sm font-sans text-foreground truncate">{op.callsign}</span>
                  <span className="text-xs font-display font-bold text-muted-foreground">{op.ops_count} ops</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top awards */}
      {top_awards.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-5">
          <p className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-4">Most Issued Awards</p>
          <div className="flex flex-wrap gap-3">
            {top_awards.map((a: any) => (
              <div key={a.name} className="flex items-center gap-2 bg-secondary/50 border border-border rounded px-3 py-2">
                <Star className="w-3.5 h-3.5 text-yellow-400" />
                <span className="text-sm font-sans text-foreground">{a.name}</span>
                <span className="text-xs font-display font-bold text-muted-foreground">×{a.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Campaigns (Pro) ──────────────────────────────────────────────────────────

const OUTCOME_OPTIONS = ["Victory", "Defeat", "Draw", "Ongoing", "Abandoned"];
const STATUS_OPTIONS = ["active", "completed", "archived"];

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-500/15 text-green-400 border-green-500/30",
  completed: "bg-primary/15 text-primary border-primary/30",
  archived: "bg-secondary text-muted-foreground border-border",
};

const CAMPAIGN_OUTCOME_COLORS: Record<string, string> = {
  Victory: "text-green-400",
  Defeat: "text-red-400",
  Draw: "text-yellow-400",
  Ongoing: "text-primary",
  Abandoned: "text-muted-foreground",
};

function CampaignCard({ campaign, onEdit, onDelete }: { campaign: any; onEdit: () => void; onDelete: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const winColor = campaign.win_rate !== null
    ? campaign.win_rate >= 60 ? "text-green-400" : campaign.win_rate >= 40 ? "text-yellow-400" : "text-red-400"
    : "text-muted-foreground";

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden hover:border-primary/30 transition-colors">
      {/* Banner */}
      {campaign.banner_url && (
        <div className="h-24 w-full overflow-hidden">
          <img src={campaign.banner_url} alt={campaign.name} className="w-full h-full object-cover opacity-70" />
        </div>
      )}

      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-display font-black uppercase tracking-wider text-foreground text-base truncate">{campaign.name}</h3>
              <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${STATUS_COLORS[campaign.status] || STATUS_COLORS.active}`}>
                {campaign.status}
              </span>
              {campaign.outcome && (
                <span className={`text-[10px] font-display font-bold uppercase tracking-widest ${CAMPAIGN_OUTCOME_COLORS[campaign.outcome] || ""}`}>
                  {campaign.outcome}
                </span>
              )}
            </div>
            {campaign.objective && (
              <p className="text-xs text-muted-foreground font-sans line-clamp-1">{campaign.objective}</p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={onEdit} className="p-1.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={onDelete} className="p-1.5 hover:bg-destructive/10 rounded text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-3 py-3 border-y border-border mb-3">
          <div className="text-center">
            <p className="font-display font-black text-lg text-foreground">{campaign.ops_count}</p>
            <p className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">Ops</p>
          </div>
          <div className="text-center">
            <p className="font-display font-black text-lg text-foreground">{campaign.aars_count}</p>
            <p className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">AARs</p>
          </div>
          <div className="text-center">
            <p className="font-display font-black text-lg text-foreground">{campaign.avg_attendance || "—"}</p>
            <p className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">Avg Att.</p>
          </div>
          <div className="text-center">
            <p className={`font-display font-black text-lg ${winColor}`}>{campaign.win_rate !== null ? `${campaign.win_rate}%` : "—"}</p>
            <p className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">Win Rate</p>
          </div>
        </div>

        {/* Dates */}
        <div className="flex items-center gap-4 text-xs font-sans text-muted-foreground mb-3">
          {campaign.start_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(campaign.start_date).toLocaleDateString()}</span>}
          {campaign.end_date && <span>→ {new Date(campaign.end_date).toLocaleDateString()}</span>}
          {campaign.tags?.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {campaign.tags.map((t: string) => (
                <span key={t} className="bg-secondary px-1.5 py-0.5 rounded text-[10px] text-muted-foreground">{t}</span>
              ))}
            </div>
          )}
        </div>

        {/* Expandable ops list */}
        {campaign.ops?.length > 0 && (
          <div>
            <button onClick={() => setExpanded(!expanded)} className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {campaign.ops_count} Linked Op{campaign.ops_count !== 1 ? "s" : ""}
            </button>
            {expanded && (
              <div className="mt-2 space-y-1">
                {campaign.ops.map((op: any) => (
                  <div key={op.id} className="flex items-center gap-2 text-xs font-sans text-muted-foreground py-1 border-b border-border/50 last:border-0">
                    <Target className="w-3 h-3 text-primary shrink-0" />
                    <span className="flex-1 truncate text-foreground">{op.name}</span>
                    <span className="capitalize text-muted-foreground">{op.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Outcome note */}
        {campaign.outcome_note && (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-xs font-sans text-muted-foreground italic">"{campaign.outcome_note}"</p>
          </div>
        )}
      </div>
    </div>
  );
}

function CampaignModal({ group, campaign, availableOps, onSave, onClose }: {
  group: any; campaign: any | null; availableOps: any[]; onSave: (data: any) => void; onClose: () => void;
}) {
  const [form, setForm] = useState({
    name: campaign?.name || "",
    description: campaign?.description || "",
    objective: campaign?.objective || "",
    banner_url: campaign?.banner_url || "",
    status: campaign?.status || "active",
    start_date: campaign?.start_date || "",
    end_date: campaign?.end_date || "",
    outcome: campaign?.outcome || "",
    outcome_note: campaign?.outcome_note || "",
    tags: campaign?.tags?.join(", ") || "",
    op_ids: campaign?.op_ids || [],
  });
  const [saving, setSaving] = useState(false);

  const toggleOp = (opId: string) => {
    setForm(f => ({
      ...f,
      op_ids: f.op_ids.includes(opId) ? f.op_ids.filter((id: string) => id !== opId) : [...f.op_ids, opId],
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags ? form.tags.split(",").map((t: string) => t.trim()).filter(Boolean) : [],
      group_id: group.id,
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h2 className="font-display font-black uppercase tracking-wider text-foreground">
            {campaign ? "Edit Campaign" : "New Campaign"}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Campaign Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="mf-input w-full" placeholder="Operation Thunderstorm" />
          </div>

          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Objective</label>
            <input value={form.objective} onChange={e => setForm(f => ({ ...f, objective: e.target.value }))}
              className="mf-input w-full" placeholder="Secure the northern corridor..." />
          </div>

          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="mf-input w-full min-h-[80px] resize-y" placeholder="Full campaign brief..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="mf-input w-full">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Outcome</label>
              <select value={form.outcome} onChange={e => setForm(f => ({ ...f, outcome: e.target.value }))}
                className="mf-input w-full">
                <option value="">— Not set —</option>
                {OUTCOME_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Start Date</label>
              <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
                className="mf-input w-full" />
            </div>
            <div>
              <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">End Date</label>
              <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                className="mf-input w-full" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Outcome Note</label>
            <input value={form.outcome_note} onChange={e => setForm(f => ({ ...f, outcome_note: e.target.value }))}
              className="mf-input w-full" placeholder="A decisive victory achieved through..." />
          </div>

          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Banner Image URL</label>
            <input value={form.banner_url} onChange={e => setForm(f => ({ ...f, banner_url: e.target.value }))}
              className="mf-input w-full" placeholder="https://..." />
          </div>

          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Tags (comma separated)</label>
            <input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
              className="mf-input w-full" placeholder="arma3, combined-arms, summer-2025" />
          </div>

          {/* Link ops */}
          <div>
            <label className="block text-xs font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">
              <Link2 className="w-3 h-3 inline mr-1" /> Link Ops ({form.op_ids.length} selected)
            </label>
            <div className="max-h-48 overflow-y-auto border border-border rounded-lg divide-y divide-border">
              {availableOps.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground font-sans text-center">No ops found — create some in the Live Ops tab first</p>
              ) : availableOps.map((op: any) => (
                <label key={op.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/50 cursor-pointer">
                  <input type="checkbox" checked={form.op_ids.includes(op.id)} onChange={() => toggleOp(op.id)}
                    className="rounded" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-sans text-foreground truncate">{op.name}</p>
                    <p className="text-[10px] text-muted-foreground capitalize">{op.status} · {op.event_type || op.game || ""}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-5 py-2 border border-border rounded text-sm font-display font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
          <button onClick={handleSubmit} disabled={saving || !form.name.trim()}
            className="inline-flex items-center gap-2 px-6 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-display font-black uppercase tracking-wider text-sm rounded transition-all disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {campaign ? "Save Changes" : "Create Campaign"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CampaignsTab({ group, globalIsPro }: any) {
  const { toast } = useToast();
  const [isPro, setIsPro] = useState<boolean | null>(null);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [availableOps, setAvailableOps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ open: boolean; campaign: any | null }>({ open: false, campaign: null });

  const load = async () => {
    setLoading(true);
    try {
      // Check pro
      const proData = (globalIsPro !== undefined && globalIsPro !== null)
        ? { is_pro: globalIsPro }
        : await fetch(`${PRO_STATUS_URL_MANAGE}?group_id=${group.id}`).then(r => r.json()).catch(() => ({ is_pro: false }));
      setIsPro(proData.is_pro);
      if (!proData.is_pro) { setLoading(false); return; }

      // Load campaigns + ops in parallel
      const [campRes, opsRes] = await Promise.all([
        fetch(`${CAMPAIGNS_URL}?path=list&group_id=${group.id}`),
        fetch(`${CAMPAIGNS_URL}?path=ops&group_id=${group.id}`),
      ]);
      if (campRes.ok) setCampaigns(await campRes.json());
      if (opsRes.ok) setAvailableOps(await opsRes.json());
    } catch { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [group.id]);

  const handleSave = async (data: any) => {
    try {
      const isEdit = !!modal.campaign;
      const res = await fetch(
        isEdit
          ? `${CAMPAIGNS_URL}?path=update&id=${modal.campaign.id}`
          : `${CAMPAIGNS_URL}?path=create`,
        { method: isEdit ? "PATCH" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }
      );
      if (!res.ok) throw new Error((await res.json()).error || "Save failed");
      toast({ title: isEdit ? "Campaign updated" : "Campaign created" });
      setModal({ open: false, campaign: null });
      load();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleDelete = async (campaign: any) => {
    if (!confirm(`Delete "${campaign.name}"? This cannot be undone.`)) return;
    try {
      await fetch(`${CAMPAIGNS_URL}?path=delete&id=${campaign.id}`, { method: "DELETE" });
      toast({ title: "Campaign deleted" });
      load();
    } catch {
      toast({ title: "Delete failed", variant: "destructive" });
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (!isPro) return (
    <div className="flex flex-col items-center justify-center py-20 text-center max-w-md mx-auto gap-6">
      <div className="w-16 h-16 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center justify-center">
        <Crown className="w-8 h-8 text-yellow-400" />
      </div>
      <div>
        <h3 className="font-display font-black text-2xl uppercase tracking-wider text-foreground mb-2">Commander Pro Required</h3>
        <p className="text-muted-foreground font-sans leading-relaxed">
          Campaigns let you group ops into named series with full progression tracking, win rates, attendance analytics, outcome notes and banners. Upgrade to unlock.
        </p>
      </div>
      <a href="/commander-pro"
        className="inline-flex items-center gap-3 bg-yellow-500 hover:bg-yellow-400 text-black font-display font-black uppercase tracking-widest text-sm px-8 py-3 rounded transition-all shadow-[0_0_20px_hsla(48,96%,53%,0.3)]"
      >
        <Crown className="w-4 h-4" /> Upgrade to Pro — £10/mo
      </a>
    </div>
  );

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-yellow-500/10 border border-yellow-500/30 rounded flex items-center justify-center">
            <Zap className="w-4 h-4 text-yellow-400" />
          </div>
          <div>
            <h2 className="font-display font-bold uppercase tracking-wider text-foreground">Campaigns</h2>
            <p className="text-xs font-sans text-muted-foreground">{campaigns.length} campaign{campaigns.length !== 1 ? "s" : ""} · link ops into series and track progression</p>
          </div>
          <span className="text-[10px] font-display font-bold uppercase tracking-widest px-2 py-1 rounded border bg-yellow-500/10 text-yellow-400 border-yellow-500/30 flex items-center gap-1">
            <Crown className="w-3 h-3" /> Pro
          </span>
        </div>
        <button onClick={() => setModal({ open: true, campaign: null })}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/80 text-black font-display font-black uppercase tracking-wider text-xs px-5 py-2.5 rounded transition-all">
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {/* Summary stats */}
      {campaigns.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total Campaigns", value: campaigns.length },
            { label: "Active", value: campaigns.filter(c => c.status === "active").length, color: "text-green-400" },
            { label: "Total Ops Linked", value: campaigns.reduce((a, c) => a + c.ops_count, 0), color: "text-primary" },
            { label: "Avg Win Rate", value: (() => { const wrs = campaigns.filter(c => c.win_rate !== null); return wrs.length ? `${Math.round(wrs.reduce((a, c) => a + c.win_rate, 0) / wrs.length)}%` : "—"; })(), color: "text-yellow-400" },
          ].map(s => (
            <div key={s.label} className="bg-card border border-border rounded-lg p-4 text-center">
              <p className={`font-display font-black text-2xl ${s.color || "text-foreground"}`}>{s.value}</p>
              <p className="text-[10px] font-display uppercase tracking-widest text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Campaign cards */}
      {campaigns.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center gap-4">
          <Flag className="w-12 h-12 text-muted-foreground/30" />
          <div>
            <p className="font-display font-bold uppercase tracking-wider text-foreground mb-1">No Campaigns Yet</p>
            <p className="text-sm text-muted-foreground font-sans">Group your ops into named campaigns. Track win rates, attendance, and progression across op series.</p>
          </div>
          <button onClick={() => setModal({ open: true, campaign: null })}
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary/80 text-black font-display font-black uppercase tracking-wider text-sm px-6 py-2.5 rounded transition-all">
            <Plus className="w-4 h-4" /> Create First Campaign
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {campaigns.map(c => (
            <CampaignCard key={c.id} campaign={c}
              onEdit={() => setModal({ open: true, campaign: c })}
              onDelete={() => handleDelete(c)} />
          ))}
        </div>
      )}

      {modal.open && (
        <CampaignModal
          group={group}
          campaign={modal.campaign}
          availableOps={availableOps}
          onSave={handleSave}
          onClose={() => setModal({ open: false, campaign: null })}
        />
      )}
    </div>
  );
}

// ─── Recruit Pipeline ───────────────────────────────────────────────────────────

const PIPELINE_COLUMNS = [
  { id: "pending", label: "Applied", color: "border-yellow-500/40 bg-yellow-500/5", dot: "bg-yellow-400" },
  { id: "reviewing", label: "Reviewing", color: "border-blue-500/40 bg-blue-500/5", dot: "bg-blue-400" },
  { id: "interview", label: "Interview", color: "border-purple-500/40 bg-purple-500/5", dot: "bg-purple-400" },
  { id: "approved", label: "Accepted", color: "border-green-500/40 bg-green-500/5", dot: "bg-green-400" },
  { id: "rejected", label: "Rejected", color: "border-red-500/40 bg-red-500/5", dot: "bg-red-400" },
];

function RecruitPipelineTab({ group, showMsg }: any) {
  const [apps, setApps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any | null>(null);
  const [moving, setMoving] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [cooldownOverride, setCooldownOverride] = useState<number | null>(null);
  const [groupPolicy, setGroupPolicy] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  useEffect(() => { loadApps(); }, [group.id]);

  const getToken = () => sessionStorage.getItem("tag_auth_token") ?? localStorage.getItem("tag_auth_token") ?? "";

  useEffect(() => {
    fetch(`${PIPELINE_URL}?path=${encodeURIComponent(`/${group.id}/policy`)}`)
      .then(r => r.json()).then(p => setGroupPolicy(p ?? {})).catch(() => {});
  }, [group.id]);

  const loadApps = () => {
    const token = getToken();
    fetch(`${PIPELINE_URL}?path=${encodeURIComponent(`/${group.id}/applications`)}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data && data.error) { console.error("Pipeline fetch error:", data.error); setLoading(false); return; }
        setApps(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((e) => { console.error("Pipeline fetch failed:", e); setLoading(false); });
  };

  const moveApp = async (app: any, newStatus: string, note?: string) => {
    setMoving(true);
    const token = getToken();
    try {
      const res = await fetch(`${PIPELINE_URL}?path=${encodeURIComponent(`/${group.id}/applications/${app.id}`)}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          status: newStatus,
          reviewNote: note ?? reviewNote,
          ...(newStatus === "rejected" && cooldownOverride !== null ? { cooldown_days: cooldownOverride } : {}),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { showMsg(false, json.error ?? "Failed to update status"); setMoving(false); return; }
      const updated = { ...app, status: newStatus, review_note: note ?? reviewNote };
      setApps(prev => prev.map(a => a.id === app.id ? updated : a));
      if (selected?.id === app.id) setSelected(updated);
      showMsg(true, `${app.applicant_username} moved to ${PIPELINE_COLUMNS.find(c => c.id === newStatus)?.label ?? newStatus}`);
      setReviewNote("");
    } catch (e) { showMsg(false, "Failed to update status"); }
    setMoving(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;



  const byStatus = (status: string) => apps.filter(a => a.status === status);
  const total = apps.length;
  const pending = byStatus("pending").length;

  const filteredApps = apps.filter(a => {
    const matchSearch = !searchQuery || a.applicant_username?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = filterStatus === "all" || a.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const conversionRate = total > 0 ? Math.round((byStatus("approved").length / total) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-yellow-500/10 border border-yellow-500/30 rounded flex items-center justify-center">
            <UserCheck className="w-4 h-4 text-yellow-400" />
          </div>
          <div>
            <h2 className="font-display font-bold uppercase tracking-wider text-foreground">Recruitment Pipeline</h2>
            <p className="text-xs font-sans text-muted-foreground">{total} applicant{total !== 1 ? "s" : ""} · {conversionRate}% acceptance rate{pending > 0 ? ` · ${pending} awaiting review` : ""}</p>
          </div>
          <span className="text-[10px] font-display font-bold uppercase tracking-widest px-2 py-1 rounded border bg-yellow-500/10 text-yellow-400 border-yellow-500/30 flex items-center gap-1">
            <Crown className="w-3 h-3" /> Pro
          </span>
        </div>
        {pending > 0 && (
          <span className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-wider text-yellow-400 bg-yellow-500/10 border border-yellow-500/30 px-3 py-1.5 rounded animate-pulse">
            <Bell className="w-3 h-3" /> {pending} new
          </span>
        )}
      </div>

      {/* Search + filter bar */}
      {total > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <input
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search applicants..."
            className="bg-secondary border border-border rounded px-3 py-1.5 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 w-48"
          />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="bg-secondary border border-border rounded px-3 py-1.5 text-sm font-sans text-foreground focus:outline-none focus:border-primary/50">
            <option value="all">All Stages</option>
            {PIPELINE_COLUMNS.map(c => <option key={c.id} value={c.id}>{c.label} ({byStatus(c.id).length})</option>)}
          </select>
        </div>
      )}

      {total === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center gap-4">
          <UserCheck className="w-12 h-12 text-muted-foreground/30" />
          <div>
            <p className="font-display font-bold uppercase tracking-wider text-foreground mb-1">No Applications Yet</p>
            <p className="text-sm text-muted-foreground font-sans">Applicants will appear here when they apply to join your unit.</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
          {PIPELINE_COLUMNS.map(col => {
            const colApps = filteredApps.filter(a => a.status === col.id);
            return (
              <div key={col.id} className={`rounded-lg border p-3 ${col.color} min-h-[200px]`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-2 h-2 rounded-full ${col.dot}`} />
                  <span className="font-display font-bold uppercase tracking-wider text-xs text-foreground">{col.label}</span>
                  <span className="ml-auto text-xs font-display font-bold text-muted-foreground">{byStatus(col.id).length}</span>
                </div>
                <div className="space-y-2">
                  {colApps.map(app => {
                    const rep = app.service_dossier?.reputation;
                    const isBlacklisted = rep?.blacklisted === true;
                    const grade = rep?.grade;
                    const score = rep?.overall_score;
                    const flagCount = Array.isArray(rep?.reviews_from_units)
                      ? rep.reviews_from_units.filter((r: any) => r.overall_vote === "flag").length
                      : 0;
                    const GRADE_CLR: Record<string,string> = {
                      ELITE:"text-yellow-400", TRUSTED:"text-green-400", STANDARD:"text-blue-400",
                      CAUTION:"text-orange-400", "HIGH RISK":"text-red-400", UNRATED:"text-muted-foreground",
                    };
                    return (
                      <button key={app.id} onClick={() => { setSelected(app); setReviewNote(app.review_note ?? ""); }}
                        className={`w-full text-left bg-card rounded p-3 hover:border-primary/50 transition-all group border ${isBlacklisted ? "border-red-500/50 bg-red-500/5" : "border-border"}`}>
                        <div className="flex items-start justify-between gap-1">
                          <p className="font-display font-bold text-sm text-foreground leading-tight">{app.applicant_username}</p>
                          {isBlacklisted && (
                            <span className="shrink-0 text-[8px] font-display font-bold uppercase tracking-widest text-red-400 border border-red-500/40 bg-red-500/10 px-1.5 py-0.5 rounded">⚠ BL</span>
                          )}
                          {!isBlacklisted && grade && grade !== "UNRATED" && (
                            <span className={`shrink-0 text-[8px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                              grade === "ELITE" ? "text-yellow-400 border-yellow-500/30 bg-yellow-500/10" :
                              grade === "TRUSTED" ? "text-green-400 border-green-500/30 bg-green-500/10" :
                              grade === "CAUTION" ? "text-orange-400 border-orange-500/30 bg-orange-500/10" :
                              grade === "HIGH RISK" ? "text-red-400 border-red-500/30 bg-red-500/10" :
                              "text-muted-foreground border-border"
                            }`}>{score?.toFixed(1) ?? grade}</span>
                          )}
                        </div>
                        <p className="text-[10px] font-sans text-muted-foreground mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {app.created_date ? new Date(app.created_date).toLocaleDateString("en-GB") : "—"}
                        </p>
                        {flagCount > 0 && (
                          <p className="text-[10px] font-sans text-orange-400 mt-1 flex items-center gap-1">
                            <ShieldAlert className="w-3 h-3" /> {flagCount} flag{flagCount !== 1 ? "s" : ""} from other units
                          </p>
                        )}
                        {app.review_note && <p className="text-[10px] font-sans text-muted-foreground mt-1 truncate italic">"{app.review_note}"</p>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Applicant detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSelected(null)}>
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 space-y-5" onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display font-black text-xl uppercase tracking-wider text-foreground">{selected.applicant_username}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[9px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
                    PIPELINE_COLUMNS.find(c => c.id === selected.status)?.color ?? "border-border"
                  } ${PIPELINE_COLUMNS.find(c => c.id === selected.status)?.dot.replace("bg-","text-") ?? "text-muted-foreground"}`}>
                    {PIPELINE_COLUMNS.find(c => c.id === selected.status)?.label ?? selected.status}
                  </span>
                  <p className="text-xs font-sans text-muted-foreground">Applied {selected.created_date ? new Date(selected.created_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—"}</p>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>

            {/* Answers */}
            {selected.answers && (Array.isArray(selected.answers) ? selected.answers.length > 0 : Object.keys(selected.answers).length > 0) && (
              <div className="space-y-3">
                <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Application Answers</p>
                {Array.isArray(selected.answers)
                  ? selected.answers.map((item: any, i: number) => (
                    <div key={i} className="bg-secondary/30 rounded p-3 border border-border">
                      <p className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground mb-1">{item.question}</p>
                      <p className="text-sm font-sans text-foreground leading-relaxed">{item.answer || <span className="italic text-muted-foreground">No answer provided</span>}</p>
                    </div>
                  ))
                  : Object.entries(selected.answers).map(([q, a]: any) => (
                    <div key={q} className="bg-secondary/30 rounded p-3 border border-border">
                      <p className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground mb-1">{q}</p>
                      <p className="text-sm font-sans text-foreground leading-relaxed">{a}</p>
                    </div>
                  ))
                }
              </div>
            )}


            {/* ── Service Dossier ──────────────────────────────────────────── */}
            {selected.service_dossier && (() => {
              const d = selected.service_dossier as any;

              const GRADE_COLOR: Record<string,string> = {
                ELITE:"#fbbf24", TRUSTED:"#4ade80", STANDARD:"#60a5fa",
                CAUTION:"#fb923c", "HIGH RISK":"#f87171", UNRATED:"#6b7280",
              };
              const TYPE_CLR: Record<string,string> = {
                Honourable:    "bg-green-500/10 text-green-400 border-green-500/30",
                Dishonourable: "bg-red-500/10   text-red-400   border-red-500/30",
                Medical:       "bg-blue-500/10  text-blue-400  border-blue-500/30",
                Voluntary:     "bg-purple-500/10 text-purple-400 border-purple-500/30",
                Administrative:"bg-secondary    text-muted-foreground border-border",
              };
              const LOA_CLR: Record<string,string> = {
                Pending:"text-amber-400 border-amber-500/30 bg-amber-500/10",
                Approved:"text-green-400 border-green-500/30 bg-green-500/10",
                Active:"text-primary border-primary/30 bg-primary/10",
                Denied:"text-destructive border-destructive/30 bg-destructive/10",
                Expired:"text-muted-foreground border-border",
              };
              const fmt = (iso: string | null) =>
                iso ? new Date(iso).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}) : "—";

              return (
                <div className="space-y-5 border-t border-border pt-5">

                  {/* ── Dossier Header ── */}
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="font-display font-black text-sm uppercase tracking-widest text-primary">Operator Service Dossier</span>
                      {d.operator?.is_verified && (
                        <span className="text-[9px] font-display font-bold uppercase tracking-widest text-primary border border-primary/30 bg-primary/10 px-1.5 py-0.5 rounded">✓ Verified</span>
                      )}
                    </div>
                    <span className="text-[9px] font-display uppercase tracking-widest text-muted-foreground border border-border px-2 py-0.5 rounded">
                      Snapshot · {d.compiled_at ? new Date(d.compiled_at).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "—"}
                    </span>
                  </div>

                  {/* ── Summary Stats ── */}
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      {label:"Units Served", value:d.total_units_served ?? 0},
                      {label:"Total Ops",    value:d.total_ops ?? 0},
                      {label:"Awards",       value:d.total_awards ?? 0},
                      {label:"Qualifications",value:d.total_qualifications ?? 0},
                    ].map(s => (
                      <div key={s.label} className="bg-secondary/40 border border-border rounded p-3 text-center">
                        <p className="text-2xl font-display font-black text-primary">{s.value}</p>
                        <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* ── Operator Info ── */}
                  {d.operator && (
                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center gap-2">
                        <Shield className="w-3.5 h-3.5 text-primary" />
                        <span className="font-display font-bold uppercase tracking-widest text-xs">Operator Profile</span>
                      </div>
                      <div className="px-5 py-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {[
                          {label:"Username",    value:d.operator.username},
                          {label:"Nationality", value:d.operator.nationality},
                          {label:"Discord",     value:d.operator.discord_tag},
                        ].filter(f=>f.value).map(f=>(
                          <div key={f.label} className="bg-secondary/40 border border-border rounded p-2.5">
                            <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">{f.label}</p>
                            <p className="text-xs font-display font-bold text-foreground">{f.value}</p>
                          </div>
                        ))}
                        {d.operator.bio && (
                          <div className="col-span-full">
                            <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mb-1">Bio</p>
                            <p className="text-xs font-sans text-foreground/80 leading-relaxed">{d.operator.bio}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── Reputation ── */}
                  {d.reputation ? (
                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center gap-2 flex-wrap">
                        <Star className="w-3.5 h-3.5 text-primary" />
                        <span className="font-display font-bold uppercase tracking-widest text-xs">Operator Reputation</span>
                        {d.reputation.blacklisted && (
                          <span className="ml-auto text-[9px] font-bold uppercase tracking-widest text-red-400 border border-red-500/30 bg-red-500/10 px-2 py-0.5 rounded">⚠ Blacklisted</span>
                        )}
                      </div>
                      <div className="px-5 py-4 space-y-4">
                        <div className="flex items-center gap-6">
                          <div className="text-center shrink-0">
                            <p className="text-5xl font-black" style={{color: GRADE_COLOR[d.reputation.grade] ?? "#6b7280"}}>
                              {d.reputation.overall_score?.toFixed(1) ?? "—"}
                            </p>
                            <p className="text-[10px] font-bold uppercase tracking-widest mt-0.5" style={{color: GRADE_COLOR[d.reputation.grade] ?? "#6b7280"}}>
                              {d.reputation.grade}
                            </p>
                            <p className="text-[9px] text-muted-foreground font-sans mt-1">{d.reputation.review_count} review{d.reputation.review_count !== 1 ? "s" : ""}</p>
                          </div>
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            {[
                              {label:"Activity",   value:d.reputation.activity},
                              {label:"Attitude",   value:d.reputation.attitude},
                              {label:"Experience", value:d.reputation.experience},
                              {label:"Discipline", value:d.reputation.discipline},
                            ].map(s => (
                              <div key={s.label}>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-muted-foreground font-display uppercase tracking-wider">{s.label}</span>
                                  <span className="font-bold text-foreground">{s.value?.toFixed(1) ?? "—"}</span>
                                </div>
                                <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                                  <div className="h-full bg-primary rounded-full" style={{width:`${((s.value??0)/10)*100}%`}} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        {Array.isArray(d.reputation.reviews_from_units) && d.reputation.reviews_from_units.length > 0 && (
                          <div className="pt-3 border-t border-border space-y-2">
                            <p className="text-[9px] font-display font-bold uppercase tracking-widest text-muted-foreground">Reviews from Commanders</p>
                            {d.reputation.reviews_from_units.map((r:any, i:number) => (
                              <div key={i} className="bg-secondary/30 border border-border rounded-lg p-3 flex items-start justify-between gap-3">
                                <div>
                                  <p className="text-xs font-display font-bold uppercase tracking-widest text-foreground">{r.group_name ?? "Anonymous Unit"}</p>
                                  <p className="text-[9px] font-sans text-muted-foreground mt-0.5">by {r.reviewer_username ?? "—"}{r.date ? ` · ${fmt(r.date)}` : ""}</p>
                                  {r.notes && <p className="text-xs font-sans text-muted-foreground/80 italic mt-1">"{r.notes}"</p>}
                                </div>
                                <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border shrink-0 ${
                                  r.overall_vote==="commend" ? "text-green-400 border-green-500/30" :
                                  r.overall_vote==="caution" ? "text-amber-400 border-amber-500/30" :
                                  "text-muted-foreground border-border"
                                }`}>{r.overall_vote}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-secondary/20 border border-dashed border-border rounded-lg px-5 py-4 text-center">
                      <p className="text-xs font-sans text-muted-foreground">No reputation reviews on record</p>
                    </div>
                  )}

                  {/* ── Per-Unit Service Records ── */}
                  {Array.isArray(d.unit_records) && d.unit_records.length === 0 && (
                    <div className="text-center py-8 border border-dashed border-border rounded-lg">
                      <p className="text-sm font-display uppercase tracking-widest text-muted-foreground">No prior service — first-time recruit</p>
                    </div>
                  )}

                  {Array.isArray(d.unit_records) && d.unit_records.map((u:any, ui:number) => (
                    <div key={ui} className="bg-card border border-border rounded-xl overflow-hidden space-y-0">

                      {/* Unit header */}
                      <div className="px-5 py-3 bg-secondary/50 border-b border-border flex items-center gap-2 flex-wrap">
                        <Shield className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span className="font-display font-black text-sm uppercase tracking-widest text-foreground">{u.unit?.name ?? "Unknown Unit"}</span>
                        {u.unit?.branch && <span className="text-[9px] border border-border px-1.5 py-0.5 rounded font-display uppercase tracking-widest text-muted-foreground">{u.unit.branch}</span>}
                        {u.unit?.country && <span className="text-[9px] text-muted-foreground font-sans">{u.unit.country}</span>}
                        {u.unit?.is_verified && <span className="ml-auto text-[9px] font-display font-bold uppercase tracking-widest text-primary border border-primary/30 bg-primary/10 px-1.5 py-0.5 rounded">✓ Verified</span>}
                      </div>

                      <div className="px-5 py-4 space-y-5">

                        {/* — Rank & Assignment card — */}
                        <div className="bg-card border border-border rounded-xl overflow-hidden">
                          <div className="px-4 py-2.5 border-b border-border bg-secondary/30 flex items-center gap-2">
                            <Shield className="w-3 h-3 text-primary" />
                            <span className="font-display font-bold uppercase tracking-widest text-[10px]">Rank &amp; Assignment</span>
                            <span className="ml-auto text-[8px] font-display uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5 rounded">Read Only</span>
                          </div>
                          <div className="px-4 py-3">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {[
                                {label:"Callsign", value:u.service?.callsign},
                                {label:"Rank",     value:u.service?.rank_abbr ? `${u.service.rank_abbr} — ${u.service.rank_name}` : u.service?.rank_name},
                                {label:"Role",     value:u.service?.role_name},
                                {label:"Ops",      value:u.service?.ops_count != null ? String(u.service.ops_count) : null},
                              ].filter(f=>f.value).map(f=>(
                                <div key={f.label} className="bg-secondary/40 border border-border rounded p-2">
                                  <p className="text-[8px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">{f.label}</p>
                                  <p className="text-xs font-display font-bold text-foreground">{f.value}</p>
                                </div>
                              ))}
                            </div>
                            {u.service?.join_date && (
                              <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mt-2">Enlisted: {fmt(u.service.join_date)}</p>
                            )}
                            {Array.isArray(u.service?.specialisations) && u.service.specialisations.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {u.service.specialisations.map((s:string,i:number)=>(
                                  <span key={i} className="text-[9px] font-display uppercase tracking-wider bg-primary/10 text-primary border border-primary/20 px-1.5 py-0.5 rounded">{s}</span>
                                ))}
                              </div>
                            )}
                            {u.service?.notes_on_file && (
                              <p className="text-xs font-sans text-muted-foreground italic mt-2 pt-2 border-t border-border/40">Commander note: "{u.service.notes_on_file}"</p>
                            )}
                          </div>
                        </div>

                        {/* — Discharge Paperwork — */}
                        {u.discharge && (
                          <div className="bg-card border border-border rounded-xl overflow-hidden">
                            <div className={`px-4 py-2.5 border-b flex items-center justify-between flex-wrap gap-2 ${
                              u.discharge.type==="Dishonourable" ? "border-red-500/20 bg-red-500/5" :
                              u.discharge.type==="Honourable"    ? "border-green-500/20 bg-green-500/5" :
                              "border-border bg-secondary/30"
                            }`}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <UserMinus2 className="w-3 h-3 text-muted-foreground" />
                                <span className="font-display font-bold uppercase tracking-widest text-[10px]">Discharge Paperwork</span>
                                <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${TYPE_CLR[u.discharge.type] ?? TYPE_CLR.Administrative}`}>
                                  {u.discharge.type} Discharge
                                </span>
                                {u.discharge.reinstatement_eligible === false && (
                                  <span className="text-[9px] font-bold uppercase tracking-widest text-red-400 border border-red-400/30 bg-red-400/10 px-1.5 py-0.5 rounded">No Reinstatement</span>
                                )}
                              </div>
                              <span className="text-[9px] text-muted-foreground font-sans">{fmt(u.discharge.effective_date)}</span>
                            </div>
                            <div className="px-4 py-3 space-y-3">
                              {u.discharge.reason && (
                                <div>
                                  <p className="text-[9px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Reason</p>
                                  <p className="text-sm font-sans text-foreground/90 leading-relaxed">{u.discharge.reason}</p>
                                </div>
                              )}
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {[
                                  {label:"Final Rank",  value:u.discharge.final_rank},
                                  {label:"Final Role",  value:u.discharge.final_role},
                                  {label:"Ops Served",  value:u.discharge.ops_served != null ? String(u.discharge.ops_served) : null},
                                  {label:"Conducted By",value:u.discharge.conducted_by},
                                ].filter(f=>f.value).map(f=>(
                                  <div key={f.label} className="bg-secondary/40 border border-border rounded p-2">
                                    <p className="text-[8px] font-display uppercase tracking-widest text-muted-foreground mb-0.5">{f.label}</p>
                                    <p className="text-xs font-display font-bold text-foreground">{f.value}</p>
                                  </div>
                                ))}
                              </div>
                              {u.discharge.notes && (
                                <p className="text-xs font-sans text-muted-foreground italic border-l-2 border-border pl-2">"{u.discharge.notes}"</p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* — LOA Records — */}
                        {Array.isArray(u.loas) && u.loas.length > 0 && (
                          <div className="bg-card border border-border rounded-xl overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-border bg-secondary/30 flex items-center gap-2">
                              <Calendar className="w-3 h-3 text-primary" />
                              <span className="font-display font-bold uppercase tracking-widest text-[10px]">Leave of Absence Records</span>
                              <span className="ml-auto text-[9px] font-display font-bold text-muted-foreground">{u.loas.length} record{u.loas.length!==1?"s":""}</span>
                            </div>
                            <div className="px-4 py-3 space-y-2">
                              {u.loas.map((l:any, li:number) => (
                                <div key={li} className="bg-card border border-border rounded-lg p-3 flex items-start justify-between gap-3">
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                      <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${LOA_CLR[l.status] ?? "text-muted-foreground border-border"}`}>{l.status}</span>
                                      <span className="font-display font-bold text-xs">{l.reason_category}</span>
                                    </div>
                                    <p className="text-[10px] font-sans text-muted-foreground">
                                      {fmt(l.start_date)} → {fmt(l.end_date)}
                                      {l.reason_detail && <span> · {l.reason_detail}</span>}
                                    </p>
                                    {l.granted_by && <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mt-0.5">Granted by: {l.granted_by}</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* — Awards — */}
                        {Array.isArray(u.awards) && u.awards.length > 0 && (
                          <div className="bg-card border border-border rounded-xl overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-border bg-secondary/30 flex items-center gap-2">
                              <Award className="w-3 h-3 text-primary" />
                              <span className="font-display font-bold uppercase tracking-widest text-[10px]">Awards &amp; Decorations</span>
                              <span className="ml-auto text-[9px] font-display font-bold text-muted-foreground">{u.awards.length} award{u.awards.length!==1?"s":""}</span>
                            </div>
                            <div className="px-4 py-3 space-y-2">
                              {u.awards.map((a:any, ai:number) => (
                                <div key={ai} className="bg-secondary/30 border border-border rounded-lg p-3 flex items-center gap-3">
                                  {a.image_url
                                    ? <img src={a.image_url} alt={a.name} className="w-10 h-10 object-contain rounded shrink-0 border border-border bg-secondary/40" />
                                    : <div className="w-10 h-10 rounded border border-border bg-secondary/40 flex items-center justify-center shrink-0"><Award className="w-5 h-5 text-muted-foreground/30" /></div>
                                  }
                                  <div className="min-w-0">
                                    <p className="font-display font-bold text-xs uppercase tracking-widest text-foreground">{a.name}</p>
                                    {a.reason && <p className="text-[10px] font-sans text-muted-foreground italic mt-0.5">{a.reason}</p>}
                                    {a.awarded_by && <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mt-0.5">Awarded by: {a.awarded_by}</p>}
                                    {a.date && <p className="text-[9px] font-sans text-muted-foreground">{fmt(a.date)}</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* — Qualifications — */}
                        {Array.isArray(u.qualifications) && u.qualifications.length > 0 && (
                          <div className="bg-card border border-border rounded-xl overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-border bg-secondary/30 flex items-center gap-2">
                              <GraduationCap className="w-3 h-3 text-primary" />
                              <span className="font-display font-bold uppercase tracking-widest text-[10px]">Qualifications</span>
                              <span className="ml-auto text-[9px] font-display font-bold text-muted-foreground">{u.qualifications.length}</span>
                            </div>
                            <div className="px-4 py-3 flex flex-wrap gap-2">
                              {u.qualifications.map((q:any, qi:number) => (
                                <div key={qi} className="bg-primary/10 border border-primary/20 rounded-lg px-3 py-2">
                                  <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary">{q.name}</p>
                                  {q.granted_by && <p className="text-[9px] text-muted-foreground font-display uppercase tracking-widest">by {q.granted_by}</p>}
                                  {q.date && <p className="text-[9px] text-muted-foreground font-sans">{fmt(q.date)}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* — AARs Authored — */}
                        {Array.isArray(u.aars_authored) && u.aars_authored.length > 0 && (
                          <div className="bg-card border border-border rounded-xl overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-border bg-secondary/30 flex items-center gap-2">
                              <ClipboardList className="w-3 h-3 text-primary" />
                              <span className="font-display font-bold uppercase tracking-widest text-[10px]">After Action Reports Authored</span>
                              <span className="ml-auto text-[9px] font-display font-bold text-muted-foreground">{u.aars_authored.length}</span>
                            </div>
                            <div className="px-4 py-3 space-y-1.5">
                              {u.aars_authored.map((a:any, ai:number) => (
                                <div key={ai} className="bg-secondary/20 border border-border rounded p-2.5 flex items-center justify-between gap-3">
                                  <div>
                                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-foreground">{a.op_name ?? "Unknown Op"}</p>
                                    {a.title && <p className="text-[9px] font-sans text-muted-foreground">{a.title}</p>}
                                  </div>
                                  <div className="text-right shrink-0">
                                    {a.outcome && <span className={`text-[9px] font-display font-bold uppercase tracking-widest ${a.outcome==="victory"?"text-green-400":a.outcome==="defeat"?"text-red-400":"text-muted-foreground"}`}>{a.outcome}</span>}
                                    {a.op_date && <p className="text-[9px] font-sans text-muted-foreground">{fmt(a.op_date)}</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ── ACCOUNTABILITY RECORDS ──────────────────────── */}

                        {/* — Conduct Reports — */}
                        {Array.isArray(u.conduct_reports) && u.conduct_reports.length > 0 && (
                          <div className="bg-card border border-red-500/20 rounded-xl overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-red-500/20 bg-red-500/5 flex items-center gap-2">
                              <ShieldAlert className="w-3 h-3 text-red-400" />
                              <span className="font-display font-bold uppercase tracking-widest text-[10px] text-red-400">Conduct Reports</span>
                              <span className="ml-auto text-[9px] font-display font-bold text-red-400">{u.conduct_reports.length} on file</span>
                            </div>
                            <div className="px-4 py-3 space-y-2">
                              {u.conduct_reports.map((cr: any, ci: number) => (
                                <div key={ci} className="bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                                  <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
                                    <div className="flex items-center gap-1.5">
                                      <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                                        cr.severity === "critical" ? "text-red-400 border-red-400/40 bg-red-400/10" :
                                        cr.severity === "high"     ? "text-orange-400 border-orange-400/40 bg-orange-400/10" :
                                        cr.severity === "medium"   ? "text-amber-400 border-amber-400/40 bg-amber-400/10" :
                                        "text-muted-foreground border-border"
                                      }`}>{cr.severity ?? "minor"}</span>
                                      <span className="text-[10px] font-display font-bold uppercase tracking-widest text-foreground">{cr.category}</span>
                                    </div>
                                    {cr.report_date && <span className="text-[9px] text-muted-foreground font-sans">{fmt(cr.report_date)}</span>}
                                  </div>
                                  <p className="text-xs font-sans text-foreground/80 leading-relaxed">{cr.description}</p>
                                  {cr.outcome && (
                                    <div className="mt-1.5 pt-1.5 border-t border-border/40">
                                      <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground">Outcome: <span className="text-foreground font-bold">{cr.outcome}</span></p>
                                      {cr.outcome_notes && <p className="text-[9px] font-sans text-muted-foreground italic mt-0.5">"{cr.outcome_notes}"</p>}
                                    </div>
                                  )}
                                  {cr.reviewed_by && <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mt-1">Filed by: {cr.reviewed_by}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* — Role Fitness Reviews — */}
                        {Array.isArray(u.role_fitness_reviews) && u.role_fitness_reviews.length > 0 && (
                          <div className="bg-card border border-amber-500/20 rounded-xl overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-amber-500/20 bg-amber-500/5 flex items-center gap-2">
                              <Target className="w-3 h-3 text-amber-400" />
                              <span className="font-display font-bold uppercase tracking-widest text-[10px] text-amber-400">Role Fitness Reviews</span>
                              <span className="ml-auto text-[9px] font-display font-bold text-amber-400">{u.role_fitness_reviews.length} review{u.role_fitness_reviews.length !== 1 ? "s" : ""}</span>
                            </div>
                            <div className="px-4 py-3 space-y-2">
                              {u.role_fitness_reviews.map((rfr: any, ri: number) => (
                                <div key={ri} className="bg-secondary/20 border border-border rounded-lg p-3">
                                  <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                                    <div className="flex items-center gap-2">
                                      <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                                        rfr.verdict === "fit"           ? "text-green-400 border-green-500/30 bg-green-500/10" :
                                        rfr.verdict === "unfit"         ? "text-red-400 border-red-500/30 bg-red-500/10" :
                                        rfr.verdict === "conditional"   ? "text-amber-400 border-amber-500/30 bg-amber-500/10" :
                                        "text-muted-foreground border-border"
                                      }`}>{rfr.verdict ?? "—"}</span>
                                      <span className="text-[10px] font-display font-bold text-foreground">{rfr.role_name}</span>
                                    </div>
                                    {rfr.review_date && <span className="text-[9px] text-muted-foreground font-sans">{fmt(rfr.review_date)}</span>}
                                  </div>
                                  {rfr.notes && <p className="text-xs font-sans text-foreground/80 leading-relaxed">{rfr.notes}</p>}
                                  {rfr.reviewer_callsign && <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mt-1">Reviewed by: {rfr.reviewer_callsign}</p>}
                                  {rfr.acknowledged_at && <p className="text-[9px] text-green-400 mt-1 font-display uppercase tracking-widest">✓ Acknowledged {fmt(rfr.acknowledged_at)}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* — Performance Improvement Orders — */}
                        {Array.isArray(u.pios) && u.pios.length > 0 && (
                          <div className="bg-card border border-orange-500/20 rounded-xl overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-orange-500/20 bg-orange-500/5 flex items-center gap-2">
                              <AlertCircle className="w-3 h-3 text-orange-400" />
                              <span className="font-display font-bold uppercase tracking-widest text-[10px] text-orange-400">Performance Improvement Orders</span>
                              <span className="ml-auto text-[9px] font-display font-bold text-orange-400">{u.pios.length} issued</span>
                            </div>
                            <div className="px-4 py-3 space-y-2">
                              {u.pios.map((pio: any, pi: number) => (
                                <div key={pi} className="bg-secondary/20 border border-border rounded-lg p-3">
                                  <div className="flex items-center justify-between gap-2 flex-wrap mb-1.5">
                                    <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                                      pio.status === "open"     ? "text-orange-400 border-orange-400/30 bg-orange-400/10" :
                                      pio.status === "closed"   ? "text-green-400 border-green-500/30 bg-green-500/10" :
                                      pio.status === "breached" ? "text-red-400 border-red-500/30 bg-red-500/10" :
                                      "text-muted-foreground border-border"
                                    }`}>{pio.status ?? "open"}</span>
                                    {pio.deadline && <span className="text-[9px] text-muted-foreground font-sans">Deadline: {fmt(pio.deadline)}</span>}
                                  </div>
                                  <p className="text-xs font-sans text-foreground/80 leading-relaxed">{pio.reason}</p>
                                  {Array.isArray(pio.tasks) && pio.tasks.length > 0 && (
                                    <ul className="mt-2 space-y-0.5">
                                      {pio.tasks.map((t: string, ti: number) => (
                                        <li key={ti} className="flex items-start gap-1.5 text-[10px] font-sans text-muted-foreground">
                                          <span className="text-primary mt-0.5">•</span>{t}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {pio.outcome_note && (
                                    <p className="text-[9px] font-sans text-muted-foreground italic mt-2 pt-1.5 border-t border-border/40">Outcome: "{pio.outcome_note}"</p>
                                  )}
                                  {pio.issued_by_callsign && <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground mt-1">Issued by: {pio.issued_by_callsign}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* — Promotion Flags — */}
                        {Array.isArray(u.promotion_flags) && u.promotion_flags.length > 0 && (
                          <div className="bg-card border border-green-500/20 rounded-xl overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-green-500/20 bg-green-500/5 flex items-center gap-2">
                              <TrendingUp className="w-3 h-3 text-green-400" />
                              <span className="font-display font-bold uppercase tracking-widest text-[10px] text-green-400">Promotion Flags</span>
                              <span className="ml-auto text-[9px] font-display font-bold text-green-400">{u.promotion_flags.length}</span>
                            </div>
                            <div className="px-4 py-3 space-y-2">
                              {u.promotion_flags.map((pf: any, pfi: number) => (
                                <div key={pfi} className="bg-secondary/20 border border-border rounded-lg p-3 flex items-center justify-between gap-3">
                                  <div>
                                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-foreground">{pf.from_rank_name} → {pf.to_rank_name}</p>
                                    <p className="text-[9px] font-sans text-muted-foreground mt-0.5">{pf.ops_at_flag} ops · {pf.tenure_days_at_flag} days served</p>
                                  </div>
                                  <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                                    pf.status === "approved" ? "text-green-400 border-green-500/30 bg-green-500/10" :
                                    pf.status === "denied"   ? "text-red-400 border-red-500/30 bg-red-500/10" :
                                    pf.status === "pending"  ? "text-amber-400 border-amber-500/30 bg-amber-500/10" :
                                    "text-muted-foreground border-border"
                                  }`}>{pf.status ?? "pending"}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  ))}

                </div>
              );
            })()}

            {/* Review note */}
            <div className="space-y-2">
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Internal Review Note</p>
              <textarea
                value={reviewNote}
                onChange={e => setReviewNote(e.target.value)}
                placeholder="Add internal notes about this applicant (not visible to them)..."
                rows={3}
                className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 resize-none"
              />
            </div>

            {/* Rejection cooldown override */}
            {selected.status !== "rejected" && (
              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-3 h-3 text-red-400" />
                  <span className="text-[10px] font-display font-bold uppercase tracking-widest text-red-400">Rejection Cooldown Override</span>
                  {groupPolicy?.rejection_cooldown_days > 0 && (
                    <span className="ml-auto text-[9px] font-sans text-muted-foreground">Policy default: {groupPolicy.rejection_cooldown_days}d</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {[0, 7, 14, 30, 60, 90].map(d => (
                    <button key={d}
                      onClick={() => setCooldownOverride(cooldownOverride === d ? null : d)}
                      className={`px-2.5 py-1 rounded text-[10px] font-display font-bold uppercase tracking-widest border transition-colors ${
                        cooldownOverride === d
                          ? "bg-red-500 text-white border-red-400"
                          : "bg-secondary border-border text-muted-foreground hover:border-red-400/40"
                      }`}
                    >{d === 0 ? "No lockout" : `${d}d`}</button>
                  ))}
                </div>
                <p className="text-[9px] font-sans text-muted-foreground">
                  {cooldownOverride === null
                    ? `Using policy default${groupPolicy?.rejection_cooldown_days > 0 ? ` (${groupPolicy.rejection_cooldown_days} days)` : " (no lockout)"}.`
                    : cooldownOverride === 0
                      ? "Override: no reapplication lockout."
                      : `Override: ${cooldownOverride}-day reapplication lockout.`}
                </p>
              </div>
            )}

            {/* Stage actions */}
            <div className="space-y-2 pt-2 border-t border-border">
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Move to Stage</p>
              <div className="flex flex-wrap gap-2">
                {PIPELINE_COLUMNS.filter(c => c.id !== selected.status).map(col => (
                  <button key={col.id} disabled={moving} onClick={() => { moveApp(selected, col.id); if (col.id !== "rejected") setCooldownOverride(null); }}
                    className={`text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 rounded border transition-all disabled:opacity-50 ${col.color} ${col.dot.replace("bg-","text-")}`}>
                    → {col.label}
                  </button>
                ))}
              </div>
              {reviewNote !== (selected.review_note ?? "") && (
                <button disabled={moving} onClick={() => moveApp(selected, selected.status, reviewNote)}
                  className="text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 rounded border border-primary/50 text-primary bg-primary/10 hover:bg-primary/20 transition-all disabled:opacity-50">
                  Save Note Only
                </button>
              )}
              {/* Rejected applicant — show cooldown status */}
              {selected.status === "rejected" && selected.rejection_cooldown_until && (
                <div className="flex items-center gap-2 text-[9px] font-sans text-muted-foreground bg-secondary/40 border border-border rounded p-2">
                  <Clock className="w-3 h-3 shrink-0 text-red-400" />
                  Locked out until {new Date(selected.rejection_cooldown_until).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                  {new Date(selected.rejection_cooldown_until) <= new Date() && (
                    <span className="ml-1 text-green-400 font-bold">— Eligible now ✓</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Unit Legacy ──────────────────────────────────────────────────────────────
function UnitLegacyTab({ group, globalIsPro }: any) {
  const [ops, setOps] = useState<any[]>([]);
  const [aars, setAars] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState<boolean | null>(null);
  const token = localStorage.getItem("tag_auth_token") ?? "";

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      apiFetch<any[]>(`/milsimGroupsB?path=${group.id}/ops`).catch(() => []),
      apiFetch<any[]>(`/milsimGroupsB?path=${group.id}/aars`).catch(() => []),
      fetch(`${CAMPAIGNS_URL}?path=list&group_id=${group.id}`, { headers }).then(r => r.json()).catch(() => []),
      (globalIsPro !== null && globalIsPro !== undefined) ? Promise.resolve({ is_pro: globalIsPro }) : fetch(`${_PRO_STATUS_URL_MANAGE}?group_id=${group.id}`).then(r => r.json()).catch(() => ({ is_pro: false })),
    ]).then(([o, a, c, proStatus]) => {
      setOps(Array.isArray(o) ? o : []);
      setAars(Array.isArray(a) ? a : []);
      setCampaigns(Array.isArray(c) ? c : []);
      setIsPro(!!proStatus?.is_pro);
      setLoading(false);
    });
  }, [group.id]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (!isPro) return (
    <div className="flex flex-col items-center justify-center py-20 text-center gap-4 max-w-md mx-auto">
      <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
        <Archive className="w-6 h-6 text-primary" />
      </div>
      <div>
        <h3 className="font-display font-black uppercase tracking-widest text-lg text-foreground">Unit Legacy</h3>
        <p className="text-sm font-sans text-muted-foreground mt-2">
          The full timeline of your unit's history — ops, campaigns, AARs, and ribbons — is a <strong className="text-foreground">Commander Pro</strong> feature.
        </p>
      </div>
      <a href="/commander-pro"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded border border-primary/40 bg-primary/10 text-primary text-xs font-display font-bold uppercase tracking-widest hover:bg-primary/20 transition-all">
        <Zap className="w-3.5 h-3.5" />
        Upgrade to Commander Pro
      </a>
    </div>
  );

  type LegacyEntryType = "op" | "aar" | "campaign";
  type LegacyEntry = {
    date: string; type: LegacyEntryType; id: string;
    title: string; game?: string; eventType?: string; status?: string;
    outcome?: string; author?: string; participants?: number;
    campaignId?: string; opCount?: number;
  };

  const allEntries: LegacyEntry[] = [
    ...ops.filter(o => o.scheduled_at).map(o => ({
      date: o.scheduled_at, type: "op" as const, id: o.id,
      title: o.name, game: o.game, eventType: o.event_type, status: o.status,
      campaignId: (campaigns.find((c: any) => (c.op_ids || []).includes(o.id)) || {}).id,
    })),
    ...aars.filter(a => a.op_date || a.created_date).map(a => ({
      date: a.op_date || a.created_date, type: "aar" as const, id: a.id,
      title: a.title || a.op_name || "AAR",
      outcome: a.outcome, author: a.author_username,
      participants: Array.isArray(a.participants) ? a.participants.length : undefined,
    })),
    ...campaigns.filter(c => c.start_date).map(c => ({
      date: c.start_date, type: "campaign" as const, id: c.id,
      title: c.name, outcome: c.outcome, status: c.status,
      opCount: (c.op_ids || []).length,
    })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const legacyTypeStyle: Record<LegacyEntryType, { dot: string; card: string; label: string; icon: typeof Archive }> = {
    op:       { dot: "bg-primary border-primary/60",        card: "border-primary/20 bg-primary/5",       label: "text-primary",    icon: Siren        },
    aar:      { dot: "bg-green-500 border-green-400/60",    card: "border-green-500/20 bg-green-500/5",   label: "text-green-400",  icon: ClipboardList },
    campaign: { dot: "bg-yellow-400 border-yellow-300/60",  card: "border-yellow-500/20 bg-yellow-500/5", label: "text-yellow-400", icon: Zap           },
  };

  const legacyByYear: Record<number, LegacyEntry[]> = {};
  allEntries.forEach(e => {
    const y = new Date(e.date).getFullYear();
    if (!legacyByYear[y]) legacyByYear[y] = [];
    legacyByYear[y].push(e);
  });
  const legacyYears = Object.keys(legacyByYear).map(Number).sort((a, b) => b - a);

  const firstOpDate = ops.length > 0 ? ops.reduce((e: string | null, o) => (!e || new Date(o.scheduled_at) < new Date(e)) ? o.scheduled_at : e, null) : null;
  const victories = campaigns.filter(c => c.outcome === "victory").length;
  const yearsActive = firstOpDate ? new Date().getFullYear() - new Date(firstOpDate).getFullYear() + 1 : 0;

  const legacyOutcomeBadge = (outcome?: string) => {
    if (!outcome) return null;
    const map: Record<string, string> = { victory: "bg-green-500/20 text-green-400 border-green-500/30", defeat: "bg-red-500/20 text-red-400 border-red-500/30", draw: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" };
    return map[outcome] ? <span className={`text-[9px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${map[outcome]}`}>{outcome}</span> : null;
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-secondary border border-border rounded flex items-center justify-center">
          <Archive className="w-4 h-4 text-foreground" />
        </div>
        <div>
          <h2 className="font-display font-bold uppercase tracking-wider text-foreground">Unit Legacy</h2>
          <p className="text-xs font-sans text-muted-foreground">The permanent record of {group.name}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total Ops",    value: ops.length,         color: "text-primary"    },
          { label: "AARs Filed",   value: aars.length,        color: "text-green-400"  },
          { label: "Campaigns",    value: campaigns.length,   color: "text-yellow-400" },
          { label: "Victories",    value: victories,          color: "text-green-400"  },
          { label: "Years Active", value: yearsActive || "—", color: "text-foreground" },
        ].map(s => (
          <div key={s.label} className="bg-card border border-border rounded-lg p-3 text-center">
            <p className={`font-display font-black text-2xl ${s.color}`}>{s.value}</p>
            <p className="text-[10px] font-display uppercase tracking-widest text-muted-foreground mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {campaigns.length > 0 && (
        <div>
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-3">Campaign Ribbons</p>
          <div className="flex flex-wrap gap-2">
            {campaigns.map((c: any) => {
              const outcome = c.outcome || "incomplete";
              const col = outcome === "victory" ? "from-green-600 to-green-800 border-green-500/40" :
                          outcome === "defeat" ? "from-red-700 to-red-900 border-red-500/40" :
                          outcome === "draw" ? "from-yellow-600 to-yellow-800 border-yellow-500/40" :
                          "from-zinc-700 to-zinc-900 border-zinc-500/40";
              return (
                <div key={c.id} className={`bg-gradient-to-b ${col} border rounded px-3 py-1.5 text-center min-w-[70px]`}>
                  <p className="text-[10px] font-display font-black uppercase tracking-wider text-white leading-tight">{c.name}</p>
                  <p className="text-[8px] font-sans text-white/60 mt-0.5">{(outcome ?? "").toUpperCase()}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {allEntries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
          <Archive className="w-10 h-10 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground font-sans">No ops, AARs, or campaigns logged yet.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {legacyYears.map(year => (
            <div key={year}>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1 bg-border" />
                <span className="font-display font-black text-xs uppercase tracking-widest text-muted-foreground px-3 py-1 bg-secondary border border-border rounded">{year}</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <div className="relative pl-6 border-l-2 border-border space-y-3">
                {legacyByYear[year].map((e, i) => {
                  const s = legacyTypeStyle[e.type];
                  const Icon = s.icon;
                  const dateStr = new Date(e.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
                  return (
                    <div key={i} className="relative">
                      <div className={`absolute -left-[29px] w-4 h-4 rounded-full border-2 ${s.dot} flex items-center justify-center`}>
                        <Icon className="w-2 h-2 text-background" />
                      </div>
                      <div className={`rounded-lg border p-3 ml-1 ${s.card}`}>
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`text-[9px] font-display font-bold uppercase tracking-widest shrink-0 ${s.label}`}>
                              {e.type === "op" ? "OP" : e.type === "aar" ? "AAR" : "CAMPAIGN"}
                            </span>
                            <p className="font-display font-bold text-sm text-foreground truncate">{e.title}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {legacyOutcomeBadge(e.outcome)}
                            <span className="text-[10px] font-sans text-muted-foreground">{dateStr}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          {e.game && <span className="text-[10px] font-sans text-muted-foreground">{e.game}</span>}
                          {e.eventType && <span className="text-[10px] font-sans text-muted-foreground">· {e.eventType}</span>}
                          {e.author && <span className="text-[10px] font-sans text-muted-foreground">By {e.author}</span>}
                          {e.participants !== undefined && <span className="text-[10px] font-sans text-muted-foreground">· {e.participants} participants</span>}
                          {e.opCount !== undefined && <span className="text-[10px] font-sans text-muted-foreground">· {e.opCount} ops</span>}
                          {e.campaignId && <span className="text-[9px] font-display uppercase tracking-wider text-yellow-400/70">· {(campaigns.find((c: any) => c.id === e.campaignId) || {}).name}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Developer / API & Webhooks Tab ──────────────────────────────────────────

function DeveloperTab({ group, showMsg, globalIsPro }: { group: any; showMsg: (m: string, t?: "success"|"error") => void; globalIsPro?: boolean | null }) {
  const [keys, setKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPro, setIsPro] = useState<boolean | null>(null);
  const [generating, setGenerating] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState("Default");
  const [freshKey, setFreshKey] = useState<{ raw_key: string; webhook_secret: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const token = localStorage.getItem("tag_auth_token") ?? "";

  const copy = (val: string, id: string) => {
    navigator.clipboard.writeText(val);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  useEffect(() => {
    const proPromise = (globalIsPro !== undefined && globalIsPro !== null)
      ? Promise.resolve({ is_pro: globalIsPro })
      : fetch(`${PRO_STATUS_URL_MANAGE}?group_id=${group.id}`).then(r => r.json()).catch(() => ({ is_pro: false }));
    Promise.all([
      proPromise,
      fetch(`${API_KEYS_URL}?path=%2Fkeys&group_id=${group.id}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []),
    ]).then(([proStatus, keyList]) => {
      setIsPro(!!proStatus?.is_pro);
      setKeys(Array.isArray(keyList) ? keyList : []);
      setLoading(false);
    });
  }, [group.id, globalIsPro]);

  const generateKey = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_KEYS_URL}?path=%2Fgenerate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ group_id: group.id, label: newKeyLabel || "Default" }),
      });
      const data = await res.json();
      if (data.error) { showMsg(data.error, "error"); setGenerating(false); return; }
      setFreshKey({ raw_key: data.raw_key, webhook_secret: data.webhook_secret });
      setKeys(prev => [...prev, { id: Date.now(), key_prefix: data.prefix, label: data.label, is_active: true, last_used_at: null, created_date: new Date().toISOString() }]);
      setNewKeyLabel("Default");
    } catch { showMsg("Failed to generate key", "error"); }
    setGenerating(false);
  };

  const revokeKey = async (keyId: string, label: string) => {
    if (!confirm(`Revoke key "${label}"? Any integrations using it will stop working immediately.`)) return;
    try {
      await fetch(`${API_KEYS_URL}?path=%2Frevoke`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ key_id: keyId }),
      });
      setKeys(prev => prev.map(k => k.id === keyId ? { ...k, is_active: false } : k));
      showMsg(`Key "${label}" revoked`, "success");
    } catch { showMsg("Failed to revoke key", "error"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (!isPro) return (
    <div className="flex flex-col items-center justify-center py-20 text-center max-w-md mx-auto gap-6">
      <div className="w-16 h-16 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center justify-center">
        <Crown className="w-8 h-8 text-yellow-400" />
      </div>
      <div>
        <h3 className="font-display font-black text-2xl uppercase tracking-wider text-foreground mb-2">Commander Pro Required</h3>
        <p className="text-muted-foreground font-sans leading-relaxed">
          API access and webhook integration are Pro features. Connect your unit's Discord bot, sync rosters to external tools, or build custom dashboards.
        </p>
      </div>
      <a href="/commander-pro"
        className="inline-flex items-center gap-3 bg-yellow-500 hover:bg-yellow-400 text-black font-display font-black uppercase tracking-widest text-sm px-8 py-3 rounded transition-all shadow-[0_0_20px_hsla(48,96%,53%,0.3)]">
        <Crown className="w-4 h-4" /> Upgrade to Pro — £10/mo
      </a>
    </div>
  );

  const BASE_URL = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions";

  const endpoints = [
    { method: "GET",  path: "/milsimGroups?path=%2F{group_id}%2Ffull",        label: "Get full group data (roster, roles, ranks)" },
    { method: "GET",  path: "/milsimGroupsB?path={group_id}/ops",         label: "List all ops for group" },
    { method: "GET",  path: "/milsimGroupsB?path={group_id}/aars",        label: "List all AARs" },
    { method: "POST", path: "/milsimApiKeys?path=%2Fverify",                  label: "Verify an API key" },
    { method: "GET",  path: "/milsimApplications?path=%2F{group_id}%2Fapplications", label: "List applications (pipeline)" },
  ];

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-yellow-500/10 border border-yellow-500/30 rounded flex items-center justify-center">
          <GitBranch className="w-4 h-4 text-yellow-400" />
        </div>
        <div>
          <h2 className="font-display font-bold uppercase tracking-wider text-foreground">API & Webhooks</h2>
          <p className="text-xs font-sans text-muted-foreground">Connect external tools, Discord bots, and custom dashboards to your unit.</p>
        </div>
        <span className="text-[10px] font-display font-bold uppercase tracking-widest px-2 py-1 rounded border bg-yellow-500/10 text-yellow-400 border-yellow-500/30 flex items-center gap-1">
          <Crown className="w-3 h-3" /> Pro
        </span>
      </div>

      {/* Fresh key reveal — shown once after generation */}
      {freshKey && (
        <div className="border-2 border-yellow-500/60 bg-yellow-500/5 rounded-xl p-5 space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-display font-bold uppercase tracking-wider text-yellow-400">Save these now — they won't be shown again</p>
              <p className="text-xs text-muted-foreground font-sans mt-0.5">Store them securely. Once you close this, neither value can be recovered.</p>
            </div>
          </div>
          <div className="space-y-3">
            {[{ label: "API Key", val: freshKey.raw_key, id: "apikey" }, { label: "Webhook Secret", val: freshKey.webhook_secret, id: "secret" }].map(item => (
              <div key={item.id}>
                <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">{item.label}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-black/40 border border-border rounded px-3 py-2 text-xs font-mono text-green-400 truncate">{item.val}</code>
                  <button onClick={() => copy(item.val, item.id)}
                    className="shrink-0 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider border border-border rounded hover:border-primary/50 text-muted-foreground hover:text-foreground transition-all">
                    {copied === item.id ? "✓ Copied" : "Copy"}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => setFreshKey(null)} className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground">
            I've saved these — dismiss
          </button>
        </div>
      )}

      {/* Active keys */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">API Keys ({keys.filter(k => k.is_active).length}/3 active)</p>
        </div>
        {keys.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-8 text-center">
            <GitBranch className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm font-display uppercase tracking-widest text-muted-foreground">No API keys yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {keys.map(k => (
              <div key={k.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${k.is_active ? "border-border bg-card" : "border-border/40 bg-secondary/20 opacity-50"}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-sm text-foreground">{k.label}</span>
                    <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${k.is_active ? "text-green-400 border-green-500/30 bg-green-500/10" : "text-muted-foreground border-border"}`}>
                      {k.is_active ? "Active" : "Revoked"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <code className="text-[10px] font-mono text-muted-foreground">{k.key_prefix}</code>
                    {k.last_used_at && <span className="text-[10px] text-muted-foreground">Last used {new Date(k.last_used_at).toLocaleDateString("en-GB")}</span>}
                    {!k.last_used_at && <span className="text-[10px] text-muted-foreground">Never used</span>}
                    <span className="text-[10px] text-muted-foreground">Created {new Date(k.created_date).toLocaleDateString("en-GB")}</span>
                  </div>
                </div>
                {k.is_active && (
                  <button onClick={() => revokeKey(k.id, k.label)}
                    className="text-[10px] font-display font-bold uppercase tracking-wider text-red-400 border border-red-500/30 bg-red-500/5 hover:bg-red-500/10 px-2.5 py-1 rounded transition-all">
                    Revoke
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Generate new key */}
        {keys.filter(k => k.is_active).length < 3 && (
          <div className="flex items-center gap-2 pt-1">
            <input value={newKeyLabel} onChange={e => setNewKeyLabel(e.target.value)}
              placeholder="Key label (e.g. Discord Bot)"
              className="bg-secondary border border-border rounded px-3 py-1.5 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 flex-1 max-w-xs"
            />
            <button onClick={generateKey} disabled={generating}
              className="flex items-center gap-2 px-4 py-1.5 bg-primary/20 border border-primary/40 text-primary rounded font-display text-xs uppercase tracking-widest hover:bg-primary/30 transition-colors disabled:opacity-50">
              {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Generate Key
            </button>
          </div>
        )}
      </div>

      {/* API Reference */}
      <div className="space-y-3">
        <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">API Reference</p>
        <div className="bg-secondary/30 border border-border rounded-lg p-4 space-y-1.5">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Authentication</p>
          <code className="block text-xs font-mono text-green-400 bg-black/30 rounded px-3 py-2">Authorization: Bearer YOUR_API_KEY</code>
        </div>
        <div className="space-y-2">
          {endpoints.map((ep, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-card border border-border rounded-lg">
              <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded shrink-0 mt-0.5 ${ep.method === "GET" ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" : "bg-green-500/20 text-green-400 border border-green-500/30"}`}>
                {ep.method}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-sans text-muted-foreground">{ep.label}</p>
                <code className="text-[10px] font-mono text-foreground/70 truncate block">{BASE_URL}{ep.path}</code>
              </div>
              <button onClick={() => copy(`${BASE_URL}${ep.path}`, `ep${i}`)}
                className="text-[10px] text-muted-foreground hover:text-foreground shrink-0">
                {copied === `ep${i}` ? "✓" : "Copy"}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Webhook Endpoints ─────────────────────────────────────────────── */}
      <WebhookEndpointsSection group={group} showMsg={showMsg} token={token} />
    </div>
  );
}

// ─── All supported events ────────────────────────────────────────────────────
const ALL_WEBHOOK_EVENTS = [
  { id: "application.submitted", label: "Application Submitted", cat: "Applications" },
  { id: "application.approved",  label: "Application Approved",  cat: "Applications" },
  { id: "application.rejected",  label: "Application Rejected",  cat: "Applications" },
  { id: "roster.member_joined",  label: "Member Joined Roster",  cat: "Roster" },
  { id: "roster.member_left",    label: "Member Left Roster",    cat: "Roster" },
  { id: "roster.rank_changed",   label: "Rank Changed",          cat: "Roster" },
  { id: "roster.status_changed", label: "Member Status Changed", cat: "Roster" },
  { id: "op.created",            label: "Op Created",            cat: "Ops" },
  { id: "op.status_changed",     label: "Op Status Changed",     cat: "Ops" },
  { id: "op.completed",          label: "Op Completed",          cat: "Ops" },
  { id: "aar.posted",            label: "AAR Posted",            cat: "AARs" },
  { id: "loa.submitted",         label: "LOA Submitted",         cat: "LOA" },
  { id: "loa.approved",          label: "LOA Approved",          cat: "LOA" },
  { id: "loa.expired",           label: "LOA Expired",           cat: "LOA" },
  { id: "award.granted",         label: "Award Granted",         cat: "Awards" },
  { id: "briefing.published",    label: "Briefing Published",    cat: "Briefings" },
];

const WEBHOOKS_BASE = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions/webhooks";

function WebhookEndpointsSection({ group, showMsg, token }: { group: any; showMsg: (m: string, t?: "success"|"error") => void; token: string }) {
  const [endpoints, setEndpoints] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newEvents, setNewEvents] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [freshSecret, setFreshSecret] = useState<{ id: string; secret: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const copy = (val: string, id: string) => {
    navigator.clipboard.writeText(val);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const load = () => {
    fetch(`${WEBHOOKS_BASE}?path=%2Fendpoints&group_id=${group.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setEndpoints(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [group.id]);

  const addEndpoint = async () => {
    if (!newUrl.trim()) { showMsg("URL required", "error"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${WEBHOOKS_BASE}?path=%2Fendpoints`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ group_id: group.id, label: newLabel || "My Endpoint", webhook_url: newUrl, events: newEvents }),
      });
      const data = await res.json();
      if (data.error) { showMsg(data.error, "error"); setSaving(false); return; }
      setFreshSecret({ id: data.id, secret: data.secret });
      setShowAdd(false);
      setNewUrl(""); setNewLabel(""); setNewEvents([]);
      load();
    } catch { showMsg("Failed to add endpoint", "error"); }
    setSaving(false);
  };

  const removeEndpoint = async (id: string, label: string) => {
    if (!confirm(`Remove webhook "${label}"?`)) return;
    await fetch(`${WEBHOOKS_BASE}?path=%2Fendpoints%2F${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    setEndpoints(prev => prev.filter(e => e.id !== id));
    showMsg("Webhook removed", "success");
  };

  const testEndpoint = async (id: string) => {
    setTesting(id);
    try {
      const res = await fetch(`${WEBHOOKS_BASE}?path=%2Ftest`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ endpoint_id: id }),
      });
      const data = await res.json();
      if (data.ok) showMsg(`Test ping delivered (HTTP ${data.status})`, "success");
      else showMsg(`Test failed: ${data.error || `HTTP ${data.status}`}`, "error");
      load();
    } catch { showMsg("Test request failed", "error"); }
    setTesting(null);
  };

  const toggleEvent = (ev: string) => {
    setNewEvents(prev => prev.includes(ev) ? prev.filter(e => e !== ev) : [...prev, ev]);
  };

  const catGroups = ALL_WEBHOOK_EVENTS.reduce((acc, ev) => {
    (acc[ev.cat] = acc[ev.cat] || []).push(ev);
    return acc;
  }, {} as Record<string, typeof ALL_WEBHOOK_EVENTS>);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">
          Webhook Endpoints ({endpoints.filter(e => e.is_active).length}/5)
        </p>
        {!showAdd && endpoints.filter(e => e.is_active).length < 5 && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-wider text-primary border border-primary/30 bg-primary/10 hover:bg-primary/20 px-2.5 py-1 rounded transition-all">
            <Plus className="w-3 h-3" /> Add Endpoint
          </button>
        )}
      </div>

      {/* Fresh secret reveal */}
      {freshSecret && (
        <div className="border-2 border-yellow-500/60 bg-yellow-500/5 rounded-xl p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
            <p className="text-xs font-display font-bold uppercase tracking-wider text-yellow-400">Save your signing secret — shown once only</p>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-black/40 border border-border rounded px-3 py-2 text-xs font-mono text-green-400 truncate">{freshSecret.secret}</code>
            <button onClick={() => copy(freshSecret.secret, "whsec")}
              className="shrink-0 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider border border-border rounded hover:border-primary/50 text-muted-foreground hover:text-foreground transition-all">
              {copied === "whsec" ? "✓ Copied" : "Copy"}
            </button>
          </div>
          <button onClick={() => setFreshSecret(null)} className="text-[10px] font-display uppercase tracking-wider text-muted-foreground hover:text-foreground">I've saved it — dismiss</button>
        </div>
      )}

      {/* Add form */}
      {showAdd && (
        <div className="border border-primary/30 bg-primary/5 rounded-xl p-4 space-y-4">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary">New Webhook Endpoint</p>

          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">Label</label>
              <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="e.g. Discord Ops Bot"
                className="w-full bg-secondary border border-border rounded px-3 py-1.5 text-sm font-sans text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50" />
            </div>
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">Endpoint URL *</label>
              <input value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="https://your-server.com/webhook"
                className="w-full bg-secondary border border-border rounded px-3 py-1.5 text-sm font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50" />
            </div>

            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-2">
                Events to subscribe ({newEvents.length === 0 ? "all events" : `${newEvents.length} selected`})
              </label>
              <div className="space-y-3">
                {Object.entries(catGroups).map(([cat, evs]) => (
                  <div key={cat}>
                    <p className="text-[9px] font-display font-bold uppercase tracking-widest text-muted-foreground/60 mb-1.5">{cat}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {evs.map(ev => (
                        <button key={ev.id} onClick={() => toggleEvent(ev.id)}
                          className={`text-[10px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded border transition-all ${newEvents.includes(ev.id) ? "border-primary/60 bg-primary/20 text-primary" : "border-border bg-secondary text-muted-foreground hover:border-border/80"}`}>
                          {ev.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[9px] text-muted-foreground/60 mt-2">Leave none selected to receive all events.</p>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button onClick={addEndpoint} disabled={saving}
              className="flex items-center gap-2 px-4 py-1.5 bg-primary/20 border border-primary/40 text-primary rounded font-display text-xs uppercase tracking-widest hover:bg-primary/30 transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
              Register Endpoint
            </button>
            <button onClick={() => { setShowAdd(false); setNewUrl(""); setNewLabel(""); setNewEvents([]); }}
              className="px-4 py-1.5 text-xs font-display uppercase tracking-widest text-muted-foreground border border-border rounded hover:text-foreground transition-all">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Endpoint list */}
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : endpoints.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-6 text-center">
          <p className="text-xs font-display uppercase tracking-widest text-muted-foreground">No webhook endpoints yet</p>
          <p className="text-[10px] text-muted-foreground/60 mt-1">Add one above to start receiving real-time events.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {endpoints.map(ep => {
            const failRate = ep.delivery_count > 0 ? Math.round((ep.failure_count / ep.delivery_count) * 100) : 0;
            const healthColor = failRate === 0 ? "text-green-400 border-green-500/30 bg-green-500/10"
              : failRate < 20 ? "text-yellow-400 border-yellow-500/30 bg-yellow-500/10"
              : "text-red-400 border-red-500/30 bg-red-500/10";
            return (
              <div key={ep.id} className="border border-border bg-card rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-bold text-sm text-foreground">{ep.label || "Endpoint"}</span>
                      <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${ep.is_active ? healthColor : "text-muted-foreground border-border"}`}>
                        {ep.is_active ? (failRate === 0 ? "Healthy" : `${failRate}% fail`) : "Inactive"}
                      </span>
                    </div>
                    <code className="text-[10px] font-mono text-muted-foreground truncate block mt-0.5">{ep.url}</code>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => testEndpoint(ep.id)} disabled={testing === ep.id}
                      className="text-[10px] font-display font-bold uppercase tracking-wider text-muted-foreground border border-border px-2 py-1 rounded hover:text-foreground hover:border-border/80 transition-all disabled:opacity-50">
                      {testing === ep.id ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Test"}
                    </button>
                    <button onClick={() => removeEndpoint(ep.id, ep.label)}
                      className="text-[10px] font-display font-bold uppercase tracking-wider text-red-400 border border-red-500/30 bg-red-500/5 hover:bg-red-500/10 px-2 py-1 rounded transition-all">
                      Remove
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
                  <span>{ep.delivery_count} deliveries</span>
                  <span>{ep.failure_count} failures</span>
                  {ep.last_triggered_at && <span>Last fired {new Date(ep.last_triggered_at).toLocaleDateString("en-GB")}</span>}
                  {ep.last_status_code && <span className={ep.last_status_code >= 200 && ep.last_status_code < 300 ? "text-green-400" : "text-red-400"}>HTTP {ep.last_status_code}</span>}
                </div>
                {ep.events && ep.events.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {ep.events.map((ev: string) => (
                      <span key={ev} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-secondary border border-border text-muted-foreground">{ev}</span>
                    ))}
                  </div>
                )}
                {(!ep.events || ep.events.length === 0) && (
                  <p className="text-[9px] text-muted-foreground/60">Subscribed to all events</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Signing doc */}
      <div className="bg-secondary/30 border border-border rounded-lg p-4 space-y-2 text-xs font-sans text-muted-foreground leading-relaxed">
        <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-2">Webhook Payload Format</p>
        <pre className="text-[10px] font-mono text-foreground/70 bg-black/30 rounded p-3 overflow-x-auto">{`{
  "event": "op.created",
  "timestamp": "2026-03-25T22:00:00Z",
  "group_id": "abc123",
  "data": { ... event-specific fields ... }
}`}</pre>
        <p>Verify the <code className="text-foreground bg-black/30 px-1 rounded">X-TAG-Signature</code> header: compute <code className="text-foreground bg-black/30 px-1 rounded">HMAC-SHA256(body, signing_secret)</code> — compare to <code className="text-foreground bg-black/30 px-1 rounded">sha256=...</code>.</p>
      </div>
    </div>
  );
}
// cache-bust 1774705069

// ─── Loadout Kits Tab ──────────────────────────────────────────────────────────
const LOADOUT_FUNCTIONS_BASE = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions";
const KIT_CATEGORIES = ["Uniform", "Vest / Carrier", "Primary Weapon", "Secondary Weapon", "Ammunition", "Optics", "Communications", "Medical", "Equipment", "Rations", "Other"];

function LoadoutKitsTab({ group, showMsg }: any) {
  const [kits, setKits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("tag_auth_token") ?? "";
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    fetch(`${LOADOUT_FUNCTIONS_BASE}/loadoutKits?group_id=${group.id}`, { headers })
      .then(r => r.json()).then(d => { setKits(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, [group.id]);

  const saveKit = async (data: any) => {
    setSaving(true);
    try {
      const isNew = !data.id;
      if (data.id) {
        await fetch(`${LOADOUT_FUNCTIONS_BASE}/loadoutKits?path=/${data.id}`, { method: "PUT", headers, body: JSON.stringify(data) });
        showMsg("Kit updated");
      } else {
        await fetch(`${LOADOUT_FUNCTIONS_BASE}/loadoutKits`, { method: "POST", headers, body: JSON.stringify({ ...data, group_id: group.id, is_active: true }) });
        showMsg("Kit created");
      }

      // Notify roster members whose role matches any of the kit's role_tags
      const roleTags: string[] = data.role_tags ?? [];
      if (roleTags.length > 0) {
        try {
          const rosterRes = await apiFetch<any[]>(`/milsimRosters?group_id=${group.id}`);
          const roster = Array.isArray(rosterRes) ? rosterRes : [];
          const rolesRes = await apiFetch<any[]>(`/milsimGroups?path=${group.id}/roles`);
          const roles = Array.isArray(rolesRes) ? rolesRes : [];
          const matched = roster.filter((r: any) => {
            if (!r.role_id) return false;
            const role = roles.find((rl: any) => rl.id === r.role_id || String(rl.id) === String(r.role_id));
            return role && roleTags.some(tag => tag.toLowerCase() === role.name.toLowerCase());
          });
          await Promise.all(matched.map((r: any) =>
            apiFetch("/notifications", {
              method: "POST",
              body: JSON.stringify({
                user_id: r.user_id,
                type: "loadout_kit",
                title: isNew ? "New Loadout Kit Assigned" : "Loadout Kit Updated",
                body: `${data.name} has been ${isNew ? "posted" : "updated"} for your role in ${group.name}.`,
                link: "/member-hq",
                is_read: false
              })
            }).catch(() => {})
          ));
        } catch {}
      }

      setEditing(null); setCreating(false); load();
    } catch { showMsg("Save failed", "error"); }
    finally { setSaving(false); }
  };

  const deleteKit = async (id: string) => {
    if (!confirm("Delete this loadout kit?")) return;
    await fetch(`${LOADOUT_FUNCTIONS_BASE}/loadoutKits?path=/${id}`, { method: "DELETE", headers });
    showMsg("Kit deleted"); load();
  };

  if (editing || creating) {
    return <KitEditor kit={editing} onSave={saveKit} onCancel={() => { setEditing(null); setCreating(false); }} saving={saving} group={group} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest text-base text-foreground">Loadout Kits</h3>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Role-specific shared kit lists visible to all members</p>
        </div>
        <button onClick={() => setCreating(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-primary/90 transition-all">
          <Plus className="w-3.5 h-3.5" /> New Kit
        </button>
      </div>

      {loading && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}

      {!loading && kits.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3 border border-dashed border-border rounded-lg">
          <Package className="w-10 h-10 text-muted-foreground/30" />
          <p className="text-sm font-display font-bold uppercase tracking-wider text-muted-foreground">No loadout kits yet</p>
          <p className="text-xs text-muted-foreground/60 font-sans">Create kits for each role your unit fields</p>
        </div>
      )}

      <div className="space-y-2">
        {kits.map((kit: any) => (
          <div key={kit.id} className="border border-border rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 bg-secondary/30 cursor-pointer" onClick={() => setExpanded(expanded === kit.id ? null : kit.id)}>
              <Package className="w-4 h-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold uppercase tracking-widest text-xs text-foreground">{kit.name}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(kit.role_tags ?? []).map((t: string) => (
                    <span key={t} className="text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-primary/10 border border-primary/20 text-primary">{t}</span>
                  ))}
                  {kit.game && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm bg-secondary border border-border text-muted-foreground">{kit.game}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] text-muted-foreground font-mono">{(kit.items ?? []).length} items</span>
                <button onClick={e => { e.stopPropagation(); setEditing(kit); }} className="p-1.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors">
                  <Pencil className="w-3 h-3" />
                </button>
                <button onClick={e => { e.stopPropagation(); deleteKit(kit.id); }} className="p-1.5 hover:bg-red-500/10 rounded text-muted-foreground hover:text-red-400 transition-colors">
                  <Trash2 className="w-3 h-3" />
                </button>
                {expanded === kit.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </div>
            </div>
            {expanded === kit.id && (
              <div className="px-4 pb-4 pt-3 space-y-3">
                {kit.description && <p className="text-xs font-sans text-muted-foreground italic border-l-2 border-primary/30 pl-3">{kit.description}</p>}
                {KIT_CATEGORIES.filter(cat => (kit.items ?? []).some((i: any) => i.category === cat)).map(cat => (
                  <div key={cat}>
                    <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1.5">{cat}</p>
                    <div className="space-y-1">
                      {(kit.items ?? []).filter((i: any) => i.category === cat).map((item: any, idx: number) => (
                        <div key={idx} className="flex items-start gap-3 px-3 py-2 rounded bg-secondary/40 border border-border/50">
                          {item.mandatory
                            ? <CheckSquare className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                            : <Square className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0 mt-0.5" />
                          }
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-sans text-foreground">{item.name}</span>
                            {item.quantity && <span className="ml-2 text-[10px] font-mono text-muted-foreground">×{item.quantity}</span>}
                            {item.notes && <p className="text-[10px] text-muted-foreground/70 mt-0.5">{item.notes}</p>}
                          </div>
                          {item.mandatory && <span className="text-[9px] font-display uppercase tracking-widest text-primary/70 shrink-0">Required</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {(kit.items ?? []).length === 0 && <p className="text-xs text-muted-foreground/50 font-sans italic">No items added yet</p>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function KitEditor({ kit, onSave, onCancel, saving, group }: any) {
  const [name, setName] = useState(kit?.name ?? "");
  const [description, setDescription] = useState(kit?.description ?? "");
  const [game, setGame] = useState(kit?.game ?? "");
  const [roleTags, setRoleTags] = useState<string[]>(kit?.role_tags ?? []);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>(kit?.items ?? []);
  const [newItem, setNewItem] = useState({ category: "Uniform", name: "", quantity: "", notes: "", mandatory: false });

  useEffect(() => {
    // Roles are already part of the group object — no separate fetch needed
    const roles = Array.isArray(group?.roles) ? group.roles : [];
    setAvailableRoles(roles);
  }, [group?.roles]);

  const addItem = () => {
    if (!newItem.name.trim()) return;
    setItems(prev => [...prev, { ...newItem }]);
    setNewItem({ category: "Uniform", name: "", quantity: "", notes: "", mandatory: false });
  };

  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));

  const addRole = (roleName: string) => {
    if (roleName && !roleTags.includes(roleName)) setRoleTags(prev => [...prev, roleName]);
  };

  const submit = () => {
    if (!name.trim()) return;
    onSave({ ...(kit ?? {}), name, description, game, role_tags: roleTags, items });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="p-1.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h3 className="font-display font-black uppercase tracking-widest text-base text-foreground">{kit ? "Edit Kit" : "New Loadout Kit"}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Kit Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Rifleman Standard Load" className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Game</label>
          <select value={game} onChange={e => setGame(e.target.value)} className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground focus:outline-none focus:border-primary">
            <option value="">All games</option>
            {["Arma 3","Arma Reforger","Squad","Ready Or Not","Escape From Tarkov","Ground Branch","DayZ","Grey Zone Warfare","Body Cam","Operator","Exfil","Hell Let Loose"].map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Description / Notes</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="When this loadout is used, rules, context..." className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary resize-none" />
      </div>

      <div className="space-y-2">
        <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Roles / Tags</label>
        <div className="flex gap-2">
          <select
            onChange={e => { if (e.target.value) { addRole(e.target.value); e.target.value = ""; } }}
            defaultValue=""
            className="flex-1 px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground focus:outline-none focus:border-primary"
          >
            <option value="" disabled>{availableRoles.length === 0 ? "No roles defined yet" : "Select a role to add..."}</option>
            {availableRoles.filter(r => !roleTags.includes(r.name)).map(r => (
              <option key={r.id} value={r.name}>{r.name}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {roleTags.map(t => (
            <span key={t} className="flex items-center gap-1.5 px-2 py-1 rounded bg-primary/10 border border-primary/20 text-primary text-[10px] font-display font-bold uppercase tracking-widest">
              {t}
              <button onClick={() => setRoleTags(prev => prev.filter(x => x !== t))} className="hover:text-red-400 transition-colors"><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Kit Items</label>
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-secondary/50 text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">
            <span className="col-span-3">Category</span>
            <span className="col-span-4">Item</span>
            <span className="col-span-2">Qty</span>
            <span className="col-span-2">Req?</span>
            <span className="col-span-1"></span>
          </div>
          {items.map((item: any, idx: number) => (
            <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-border/50 text-xs items-center">
              <span className="col-span-3 text-muted-foreground text-[10px] truncate">{item.category}</span>
              <span className="col-span-4 text-foreground truncate">{item.name}</span>
              <span className="col-span-2 text-muted-foreground font-mono">{item.quantity || "—"}</span>
              <span className="col-span-2">{item.mandatory ? <span className="text-[9px] text-primary font-display font-bold uppercase tracking-widest">Yes</span> : <span className="text-[9px] text-muted-foreground/50">No</span>}</span>
              <button onClick={() => removeItem(idx)} className="col-span-1 text-muted-foreground hover:text-red-400 transition-colors flex justify-center"><X className="w-3 h-3" /></button>
            </div>
          ))}
          <div className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-border bg-secondary/20 items-center">
            <select value={newItem.category} onChange={e => setNewItem(p => ({ ...p, category: e.target.value }))} className="col-span-3 px-2 py-1.5 text-xs bg-secondary border border-border rounded text-foreground focus:outline-none focus:border-primary">
              {KIT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
            <input value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} placeholder="Item name" className="col-span-4 px-2 py-1.5 text-xs bg-secondary border border-border rounded text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary" />
            <input value={newItem.quantity} onChange={e => setNewItem(p => ({ ...p, quantity: e.target.value }))} placeholder="Qty" className="col-span-2 px-2 py-1.5 text-xs bg-secondary border border-border rounded text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary" />
            <label className="col-span-2 flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={newItem.mandatory} onChange={e => setNewItem(p => ({ ...p, mandatory: e.target.checked }))} className="accent-primary" />
              <span className="text-[10px] text-muted-foreground">Req</span>
            </label>
            <button onClick={addItem} className="col-span-1 flex justify-center p-1.5 bg-primary/10 hover:bg-primary/20 rounded text-primary transition-colors"><Plus className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button onClick={submit} disabled={saving || !name.trim()} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-primary/90 transition-all disabled:opacity-50">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {kit ? "Save Changes" : "Create Kit"}
        </button>
        <button onClick={onCancel} className="px-5 py-2.5 border border-border rounded text-xs font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-secondary transition-all">Cancel</button>
      </div>
    </div>
  );
}

// ─── Game Servers Tab ──────────────────────────────────────────────────────────
const GAME_SERVERS_BASE = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions";

function GameServersTab({ group, showMsg }: any) {
  const [servers, setServers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const token = localStorage.getItem("tag_auth_token") ?? "";
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    fetch(`${GAME_SERVERS_BASE}/gameServers?group_id=${group.id}`, { headers })
      .then(r => r.json()).then(d => { setServers(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, [group.id]);

  const saveServer = async (data: any) => {
    setSaving(true);
    try {
      if (data.id) {
        await fetch(`${GAME_SERVERS_BASE}/gameServers?path=/${data.id}`, { method: "PUT", headers, body: JSON.stringify(data) });
        showMsg("Server updated");
      } else {
        await fetch(`${GAME_SERVERS_BASE}/gameServers`, { method: "POST", headers, body: JSON.stringify({ ...data, group_id: group.id }) });
        showMsg("Server added");
      }
      setEditing(null); setCreating(false); load();
    } catch { showMsg("Save failed", "error"); }
    finally { setSaving(false); }
  };

  const deleteServer = async (id: string) => {
    if (!confirm("Remove this server?")) return;
    await fetch(`${GAME_SERVERS_BASE}/gameServers?path=/${id}`, { method: "DELETE", headers });
    showMsg("Server removed"); load();
  };

  const toggleReveal = (id: string) => {
    setRevealed(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const copyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => showMsg(`${label} copied`));
  };

  if (editing || creating) {
    return <ServerEditor server={editing} onSave={saveServer} onCancel={() => { setEditing(null); setCreating(false); }} saving={saving} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest text-base text-foreground">Game Servers</h3>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Server addresses and passwords visible to all members</p>
        </div>
        <button onClick={() => setCreating(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-primary/90 transition-all">
          <Plus className="w-3.5 h-3.5" /> Add Server
        </button>
      </div>

      {loading && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}

      {!loading && servers.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3 border border-dashed border-border rounded-lg">
          <Server className="w-10 h-10 text-muted-foreground/30" />
          <p className="text-sm font-display font-bold uppercase tracking-wider text-muted-foreground">No servers added</p>
          <p className="text-xs text-muted-foreground/60 font-sans">Add your game servers so members always have connection details</p>
        </div>
      )}

      <div className="space-y-3">
        {servers.map((srv: any) => {
          const isRevealed = revealed.has(srv.id);
          return (
            <div key={srv.id} className={`border rounded-lg overflow-hidden ${srv.is_primary ? "border-primary/40 bg-primary/3" : "border-border"}`}>
              <div className="flex items-center gap-3 px-4 py-3 bg-secondary/20">
                <div className={`w-2 h-2 rounded-full shrink-0 ${srv.is_active !== false ? "bg-green-400 animate-pulse" : "bg-red-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-display font-bold uppercase tracking-widest text-xs text-foreground">{srv.label}</p>
                    {srv.is_primary && <span className="text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/15 border border-primary/30 text-primary">Primary</span>}
                  </div>
                  {srv.game && <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{srv.game}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setEditing(srv)} className="p-1.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors"><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => deleteServer(srv.id)} className="p-1.5 hover:bg-red-500/10 rounded text-muted-foreground hover:text-red-400 transition-colors"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>

              <div className="px-4 py-3 space-y-2 border-t border-border/50">
                {srv.server_address && (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Address</p>
                      <p className="text-xs font-mono text-foreground mt-0.5">{srv.server_address}{srv.port ? `:${srv.port}` : ""}</p>
                    </div>
                    <button onClick={() => copyText(`${srv.server_address}${srv.port ? `:${srv.port}` : ""}`, "Address")} className="p-1.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                )}

                {srv.password && (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Password</p>
                      <p className="text-xs font-mono text-foreground mt-0.5 tracking-widest">{isRevealed ? srv.password : "••••••••"}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggleReveal(srv.id)} className="p-1.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors">
                        {isRevealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => copyText(srv.password, "Password")} className="p-1.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors"><Copy className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                )}

                {srv.modset_url && (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Modset / Preset</p>
                      <a href={srv.modset_url} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-primary hover:underline mt-0.5 block truncate max-w-[220px]">{srv.modset_url}</a>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  </div>
                )}

                {srv.notes && (
                  <div className="pt-1 border-t border-border/40">
                    <p className="text-xs font-sans text-muted-foreground italic">{srv.notes}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ServerEditor({ server, onSave, onCancel, saving }: any) {
  const [label, setLabel] = useState(server?.label ?? "");
  const [game, setGame] = useState(server?.game ?? "");
  const [address, setAddress] = useState(server?.server_address ?? "");
  const [port, setPort] = useState(server?.port ?? "");
  const [password, setPassword] = useState(server?.password ?? "");
  const [modsetUrl, setModsetUrl] = useState(server?.modset_url ?? "");
  const [notes, setNotes] = useState(server?.notes ?? "");
  const [isPrimary, setIsPrimary] = useState(server?.is_primary ?? false);
  const [isActive, setIsActive] = useState(server?.is_active !== false);

  const submit = () => {
    if (!label.trim() || !address.trim()) return;
    onSave({ ...(server ?? {}), label, game, server_address: address, port, password, modset_url: modsetUrl, notes, is_primary: isPrimary, is_active: isActive });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="p-1.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-colors"><ChevronLeft className="w-4 h-4" /></button>
        <h3 className="font-display font-black uppercase tracking-widest text-base text-foreground">{server ? "Edit Server" : "Add Game Server"}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Server Label *</label>
          <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Main Op Server, Training Server" className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Game</label>
          <select value={game} onChange={e => setGame(e.target.value)} className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground focus:outline-none focus:border-primary">
            <option value="">Select game...</option>
            {["Arma 3","Arma Reforger","Squad","Ready Or Not","Escape From Tarkov","Ground Branch","DayZ","Grey Zone Warfare","Body Cam","Operator","Exfil","Hell Let Loose"].map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Server Address / IP *</label>
          <input value={address} onChange={e => setAddress(e.target.value)} placeholder="e.g. 123.456.789.0 or play.myunit.com" className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Port</label>
          <input value={port} onChange={e => setPort(e.target.value)} placeholder="e.g. 2302" className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Lock className="w-3 h-3" /> Password</label>
          <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Server password (if any)" className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Modset / Preset URL</label>
          <input value={modsetUrl} onChange={e => setModsetUrl(e.target.value)} placeholder="Steam workshop collection, preset file link..." className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary" />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Additional info — modset instructions, schedule, etc." className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary resize-none" />
      </div>

      <div className="flex items-center gap-5">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} className="accent-primary" />
          <span className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground">Primary Server</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="accent-primary" />
          <span className="text-xs font-display font-bold uppercase tracking-widest text-muted-foreground">Online / Active</span>
        </label>
      </div>

      <div className="flex gap-3 pt-2">
        <button onClick={submit} disabled={saving || !label.trim() || !address.trim()} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-primary/90 transition-all disabled:opacity-50">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {server ? "Save Changes" : "Add Server"}
        </button>
        <button onClick={onCancel} className="px-5 py-2.5 border border-border rounded text-xs font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-secondary transition-all">Cancel</button>
      </div>
    </div>
  );
}

// ─── HQ Permissions Tab ──────────────────────────────────────────────────────
const HQ_TABS_CONFIG: { id: string; label: string; description: string }[] = [
  { id: "onboarding",  label: "Onboarding",           description: "Unit info, SOPs, org chart, training docs" },
  { id: "troops",      label: "Troop Management",      description: "Roster, roles, ranks, CoC, LOA, service files" },
  { id: "eventhub",    label: "Operational Management",description: "Operations, AARs, WARNOs, campaigns, calendar" },
  { id: "recognition", label: "Customisation",         description: "Awards, qualifications, ribbon builder" },
  { id: "legacy",      label: "Unit Legacy",           description: "Unit history and milestone tracker" },
  { id: "developer",   label: "API & Webhooks",        description: "Developer API keys and webhook endpoints" },
  { id: "readiness",   label: "Readiness",             description: "Member readiness and LOA overview" },
  { id: "analytics",   label: "Analytics",             description: "Activity charts and unit stats" },
  { id: "stream",      label: "Stream",                description: "Streaming integration and live status" },
];

type PermLevel = "none" | "view" | "manage";

interface PermRow {
  roleId: string;
  roleName: string;
  permissions: Record<string, PermLevel>;
}

interface MemberPermRow {
  rosterId: string;
  callsign: string;
  roleId: string | null;
  permissions: Record<string, PermLevel>;
}

function PermissionsTab({ group, showMsg }: { group: GroupDetail; showMsg: (ok: boolean | string, t?: string) => void }) {
  const [activeSection, setActiveSection] = useState<"roles" | "members">("roles");
  const [rolePerms, setRolePerms] = useState<PermRow[]>([]);
  const [memberPerms, setMemberPerms] = useState<MemberPermRow[]>([]);
  const [saving, setSaving] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Build role rows from group data
    const rows: PermRow[] = (group.roles ?? []).map((r: any) => ({
      roleId: r.id,
      roleName: r.name,
      permissions: r.permissions ?? {},
    }));
    setRolePerms(rows);

    // Build member rows
    const mRows: MemberPermRow[] = (group.roster ?? [])
      .filter((r: any) => r.status === "Active" || r.status === "active")
      .map((r: any) => ({
        rosterId: r.id,
        callsign: r.callsign ?? "Unknown",
        roleId: r.role_id ?? r.roleId ?? null,
        permissions: r.hq_permissions ?? {},
      }));
    setMemberPerms(mRows);

    // All headers collapsed by default
    const allIds = new Set<string>([
      ...rows.map(r => r.roleId),
      ...mRows.map(m => m.rosterId),
    ]);
    setCollapsed(allIds);
  }, [group]);

  const setRolePerm = (roleId: string, tabId: string, level: PermLevel) => {
    setRolePerms(prev => prev.map(r => r.roleId === roleId
      ? { ...r, permissions: { ...r.permissions, [tabId]: level } }
      : r));
  };

  const setMemberPerm = (rosterId: string, tabId: string, level: PermLevel) => {
    setMemberPerms(prev => prev.map(m => m.rosterId === rosterId
      ? { ...m, permissions: { ...m.permissions, [tabId]: level } }
      : m));
  };

  const toggleCollapsed = (id: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const saveRolePerms = async (roleId: string) => {
    setSaving(roleId);
    try {
      const row = rolePerms.find(r => r.roleId === roleId);
      if (!row) return;
      await apiFetch(`/milsimGroups?path=${group.id}/roles/permissions`, {
        method: "PATCH",
        body: JSON.stringify({ role_id: roleId, permissions: row.permissions }),
      });
      showMsg(true, "Role permissions saved");
    } catch {
      showMsg(false, "Failed to save");
    } finally {
      setSaving(null);
    }
  };

  const saveMemberPerms = async (rosterId: string) => {
    setSaving(rosterId);
    try {
      const row = memberPerms.find(m => m.rosterId === rosterId);
      if (!row) return;
      await apiFetch(`/milsimGroups?path=${group.id}/roster/permissions`, {
        method: "PATCH",
        body: JSON.stringify({ roster_id: rosterId, permissions: row.permissions }),
      });
      showMsg(true, "Member permissions saved");
    } catch {
      showMsg(false, "Failed to save");
    } finally {
      setSaving(null);
    }
  };

  const LEVEL_COLORS: Record<PermLevel, string> = {
    none:   "bg-secondary text-muted-foreground border-border",
    view:   "bg-blue-500/20 text-blue-400 border-blue-500/40",
    manage: "bg-primary/20 text-primary border-primary/40",
  };

  const PermToggle = ({ value, onChange }: { value: PermLevel; onChange: (v: PermLevel) => void }) => {
    const cycle: PermLevel[] = ["none", "view", "manage"];
    const next = cycle[(cycle.indexOf(value) + 1) % cycle.length];
    return (
      <button
        onClick={() => onChange(next)}
        className={`px-2.5 py-1 rounded border text-xs font-display font-bold uppercase tracking-widest transition-all ${LEVEL_COLORS[value]}`}
      >
        {value === "none" ? "No Access" : value === "view" ? "View" : "Manage"}
      </button>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-display font-bold uppercase tracking-widest text-foreground">HQ Permissions</h2>
        <p className="text-xs text-muted-foreground font-sans mt-1">
          Control which roles and members can access each section of Unit HQ. Works like Discord permissions — role permissions apply to all members with that role, member overrides take precedence.
        </p>
      </div>

      {/* Section toggle */}
      <div className="flex gap-2">
        {(["roles", "members"] as const).map(s => (
          <button key={s} onClick={() => setActiveSection(s)}
            className={`px-4 py-2 rounded text-xs font-display font-bold uppercase tracking-widest border transition-all ${activeSection === s ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}>
            {s === "roles" ? "By Role" : "Member Overrides"}
          </button>
        ))}
      </div>

      {/* Role permissions */}
      {activeSection === "roles" && (
        <div className="space-y-4">
          {rolePerms.length === 0 && (
            <div className="p-6 border border-border rounded-lg bg-card text-center text-muted-foreground text-sm font-sans">
              No roles defined yet. Create roles in Troop Management first.
            </div>
          )}
          {rolePerms.map(row => {
            const isCollapsed = collapsed.has(row.roleId);
            return (
              <div key={row.roleId} className="border border-border rounded-lg bg-card overflow-hidden">
                <div className="flex items-center justify-between p-3 bg-secondary/50 border-b border-border cursor-pointer select-none" onClick={() => toggleCollapsed(row.roleId)}>
                  <div className="flex items-center gap-2">
                    <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                    <span className="font-display font-bold uppercase tracking-widest text-sm text-foreground">{row.roleName}</span>
                    {isCollapsed && (
                      <span className="text-[9px] font-display uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5 rounded ml-1">
                        {Object.values(row.permissions).filter(v => v !== "none" && v).length} / {HQ_TABS_CONFIG.length} sections
                      </span>
                    )}
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); saveRolePerms(row.roleId); }}
                    disabled={saving === row.roleId}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-primary/90 transition-all disabled:opacity-50"
                  >
                    {saving === row.roleId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    Save
                  </button>
                </div>
                {!isCollapsed && (
                  <div className="divide-y divide-border">
                    {HQ_TABS_CONFIG.map(tab => (
                      <div key={tab.id} className="flex items-center justify-between px-4 py-2.5">
                        <div>
                          <p className="text-xs font-display font-bold uppercase tracking-wider text-foreground">{tab.label}</p>
                          <p className="text-xs text-muted-foreground font-sans">{tab.description}</p>
                        </div>
                        <PermToggle
                          value={(row.permissions[tab.id] as PermLevel) ?? "none"}
                          onChange={v => setRolePerm(row.roleId, tab.id, v)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Member overrides */}
      {activeSection === "members" && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground font-sans">
            Member overrides take priority over role permissions. Use sparingly — for individual exceptions only.
          </p>
          {memberPerms.length === 0 && (
            <div className="p-6 border border-border rounded-lg bg-card text-center text-muted-foreground text-sm font-sans">
              No active roster members found.
            </div>
          )}
          {memberPerms.map(row => {
            const isCollapsed = collapsed.has(row.rosterId);
            return (
              <div key={row.rosterId} className="border border-border rounded-lg bg-card overflow-hidden">
                <div className="flex items-center justify-between p-3 bg-secondary/50 border-b border-border cursor-pointer select-none" onClick={() => toggleCollapsed(row.rosterId)}>
                  <div className="flex items-center gap-2">
                    <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                    <span className="font-display font-bold uppercase tracking-widest text-sm text-foreground">{row.callsign}</span>
                    {row.roleId && (() => {
                      const role = rolePerms.find(r => r.roleId === row.roleId);
                      return role ? <span className="text-xs text-muted-foreground font-sans">({role.roleName})</span> : null;
                    })()}
                    {isCollapsed && (
                      <span className="text-[9px] font-display uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5 rounded ml-1">
                        {Object.values(row.permissions).filter(v => v !== "none" && v).length} / {HQ_TABS_CONFIG.length} overrides
                      </span>
                    )}
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); saveMemberPerms(row.rosterId); }}
                    disabled={saving === row.rosterId}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-primary/90 transition-all disabled:opacity-50"
                  >
                    {saving === row.rosterId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    Save
                  </button>
                </div>
                {!isCollapsed && (
                  <div className="divide-y divide-border">
                    {HQ_TABS_CONFIG.map(tab => (
                      <div key={tab.id} className="flex items-center justify-between px-4 py-2.5">
                        <div>
                          <p className="text-xs font-display font-bold uppercase tracking-wider text-foreground">{tab.label}</p>
                          <p className="text-xs text-muted-foreground font-sans">{tab.description}</p>
                        </div>
                        <PermToggle
                          value={(row.permissions[tab.id] as PermLevel) ?? "none"}
                          onChange={v => setMemberPerm(row.rosterId, tab.id, v)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Duty Roster Tab (pure client — no automations, no backend functions) ──────
const DUTY_ROLES = ["Section Commander", "2IC", "Medic", "Support Gunner", "Rifleman", "Sniper", "Engineer", "Radio Operator", "Driver", "Pilot", "Custom"] as const;

type DutyEntry = {
  id?: string;
  group_id: string;
  title: string;
  description: string;
  duty_date: string;
  assignments: { roster_id: string; callsign: string; duty_role: string }[];
  created_by: string;
};

function DutyRosterTab({ group, globalIsPro, showMsg }: any) {
  const { user } = useAuth();
  const { toast } = useToast();

  const [rosters, setRosters] = useState<DutyEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<"list" | "create" | "edit">("list");
  const [editing, setEditing] = useState<DutyEntry | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dutyDate, setDutyDate] = useState("");
  const [assignments, setAssignments] = useState<{ roster_id: string; callsign: string; duty_role: string }[]>([]);

  const rosterMembers: any[] = (group.roster ?? []).filter((r: any) => r.status === "active" || !r.status);

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<any>(`/dutyRoster?path=list&group_id=${group.id}`);
      setRosters(Array.isArray(data) ? data : data?.rosters ?? []);
    } catch {
      // Fall back — try reading directly from entity API pattern
      setRosters([]);
    }
    setLoading(false);
  }, [group.id]);

  useEffect(() => { load(); }, [load]);

  // ── Conflict detection ────────────────────────────────────────────────────
  const getConflicts = (date: string, assignList: typeof assignments, excludeId?: string): string[] => {
    if (!date) return [];
    const targetDate = date.slice(0, 10);
    const conflicts: string[] = [];
    for (const existing of rosters) {
      if (excludeId && existing.id === excludeId) continue;
      if (!existing.duty_date) continue;
      if (existing.duty_date.slice(0, 10) !== targetDate) continue;
      for (const a of assignList) {
        if (existing.assignments?.some((ea: any) => ea.roster_id === a.roster_id)) {
          conflicts.push(`${a.callsign} already assigned on ${new Date(existing.duty_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} in "${existing.title}"`);
        }
      }
    }
    return conflicts;
  };

  // ── LOA cross-check ───────────────────────────────────────────────────────
  const getLoaConflicts = (date: string, assignList: typeof assignments): string[] => {
    if (!date || !group.loas) return [];
    const dt = new Date(date);
    return assignList
      .filter(a => (group.loas ?? []).some((l: any) =>
        l.roster_id === a.roster_id &&
        l.status === "Active" &&
        dt >= new Date(l.start_date) &&
        dt <= new Date(l.end_date)
      ))
      .map(a => `${a.callsign} is on approved LOA on this date`);
  };

  const conflicts = getConflicts(dutyDate, assignments, editing?.id);
  const loaConflicts = getLoaConflicts(dutyDate, assignments);
  const allWarnings = [...conflicts, ...loaConflicts];

  // ── Upcoming ops cross-check ──────────────────────────────────────────────
  const upcomingOp = dutyDate
    ? (group.ops ?? []).find((op: any) => {
        if (!op.scheduled_at) return false;
        const diff = Math.abs(new Date(op.scheduled_at).getTime() - new Date(dutyDate).getTime());
        return diff < 24 * 60 * 60 * 1000; // same day
      })
    : null;

  // ── Assignment helpers ────────────────────────────────────────────────────
  const addSlot = () => setAssignments(prev => [...prev, { roster_id: "", callsign: "", duty_role: "Rifleman" }]);

  const updateSlot = (i: number, field: string, val: string) => {
    setAssignments(prev => prev.map((s, idx) => {
      if (idx !== i) return s;
      if (field === "roster_id") {
        const member = rosterMembers.find((m: any) => m.id === val);
        return { ...s, roster_id: val, callsign: member?.callsign ?? val };
      }
      return { ...s, [field]: val };
    }));
  };

  const removeSlot = (i: number) => setAssignments(prev => prev.filter((_, idx) => idx !== i));

  // ── Save ──────────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditing(null);
    setTitle(""); setDescription(""); setDutyDate(""); setAssignments([]);
    setView("create");
  };

  const openEdit = (r: DutyEntry) => {
    setEditing(r);
    setTitle(r.title); setDescription(r.description ?? "");
    setDutyDate(r.duty_date ? r.duty_date.slice(0, 10) : "");
    setAssignments(r.assignments ?? []);
    setView("edit");
  };

  const save = async () => {
    if (!title.trim() || !dutyDate) { showMsg("Title and date required", "error"); return; }
    setSaving(true);
    const payload: any = {
      group_id: group.id,
      title: title.trim(),
      description: description.trim(),
      duty_date: dutyDate,
      assignments,
      created_by: user?.username ?? user?.id ?? "",
    };
    try {
      if (editing?.id) {
        await apiFetch(`/dutyRoster?path=${editing.id}`, { method: "PUT", body: JSON.stringify(payload) });
        toast({ title: "Duty roster updated ✓" });
      } else {
        await apiFetch("/dutyRoster?path=create", { method: "POST", body: JSON.stringify(payload) });
        toast({ title: "Duty roster created ✓" });
      }
      await load();
      setView("list");
    } catch (e: any) {
      showMsg(e.message ?? "Save failed", "error");
    }
    setSaving(false);
  };

  const del = async (id: string) => {
    if (!confirm("Delete this duty roster?")) return;
    try {
      await apiFetch(`/dutyRoster?path=${id}`, { method: "DELETE" });
      toast({ title: "Deleted" });
      load();
    } catch (e: any) { showMsg(e.message, "error"); }
  };

  // ── Rotation suggestion ───────────────────────────────────────────────────
  const suggestRotation = () => {
    // Count how many times each member has been assigned across all rosters
    const tally: Record<string, number> = {};
    for (const r of rosters) {
      for (const a of (r.assignments ?? [])) {
        tally[a.roster_id] = (tally[a.roster_id] ?? 0) + 1;
      }
    }
    // Pick least-used members first, skip those on LOA on the chosen date
    const loaIds = new Set(
      (group.loas ?? [])
        .filter((l: any) => {
          if (l.status !== "Active" || !dutyDate) return false;
          const dt = new Date(dutyDate);
          return dt >= new Date(l.start_date) && dt <= new Date(l.end_date);
        })
        .map((l: any) => l.roster_id)
    );
    const sorted = [...rosterMembers]
      .filter((m: any) => !loaIds.has(m.id))
      .sort((a, b) => (tally[a.id] ?? 0) - (tally[b.id] ?? 0));
    const suggested = sorted.slice(0, Math.min(4, sorted.length)).map((m: any, i: number) => ({
      roster_id: m.id,
      callsign: m.callsign,
      duty_role: DUTY_ROLES[i % (DUTY_ROLES.length - 1)],
    }));
    setAssignments(suggested);
    toast({ title: `Suggested ${suggested.length} members based on rotation fairness` });
  };

  // ── Pro gate ──────────────────────────────────────────────────────────────
  if (!globalIsPro) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Crown className="w-10 h-10 text-yellow-400 mb-4" />
        <h3 className="font-display font-black text-xl uppercase tracking-wider text-foreground mb-2">Commander Pro Required</h3>
        <p className="text-sm text-muted-foreground font-sans mb-6 max-w-sm">
          Duty Roster Scheduling gives you a full rotation planner with conflict detection, LOA cross-checks, and op alignment.
        </p>
        <a href="/commander-pro" className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 font-display font-bold uppercase tracking-wider text-xs rounded hover:bg-yellow-500/20 transition-all">
          <Crown className="w-3.5 h-3.5" /> Upgrade to Pro
        </a>
      </div>
    );
  }

  // ── Create / Edit form ────────────────────────────────────────────────────
  if (view === "create" || view === "edit") {
    return (
      <div className="space-y-5 max-w-2xl">
        <div className="flex items-center gap-3">
          <button onClick={() => setView("list")} className="text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h3 className="font-display font-black text-base uppercase tracking-wider text-foreground">
            {view === "edit" ? "Edit Duty Roster" : "New Duty Roster"}
          </h3>
        </div>

        {/* Title + date */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-[10px] font-display font-black uppercase tracking-widest text-muted-foreground">Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="Alpha Section Duty 12 Apr"
              className="w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-display font-black uppercase tracking-widest text-muted-foreground">Duty Date *</label>
            <input type="date" value={dutyDate} onChange={e => setDutyDate(e.target.value)}
              className="w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-[10px] font-display font-black uppercase tracking-widest text-muted-foreground">Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)}
            rows={2} placeholder="Optional briefing notes or context..."
            className="w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
        </div>

        {/* Op alignment notice */}
        {upcomingOp && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-primary/30 bg-primary/5">
            <Swords className="w-3.5 h-3.5 text-primary shrink-0" />
            <p className="text-[11px] font-sans text-primary">
              Op <strong>{upcomingOp.name}</strong> is scheduled for this date — this duty roster will automatically align with it.
            </p>
          </div>
        )}

        {/* Conflict warnings */}
        {allWarnings.length > 0 && (
          <div className="space-y-1.5">
            {allWarnings.map((w, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-amber-500/40 bg-amber-500/8">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <p className="text-[11px] font-sans text-amber-300">{w}</p>
              </div>
            ))}
          </div>
        )}

        {/* Assignments */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-[10px] font-display font-black uppercase tracking-widest text-muted-foreground">
              Assignments ({assignments.length})
            </label>
            <div className="flex items-center gap-2">
              {dutyDate && rosterMembers.length > 0 && (
                <button onClick={suggestRotation}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 border border-primary/40 text-primary font-display font-bold uppercase tracking-widest text-[9px] rounded hover:bg-primary/10 transition-all">
                  <RefreshCw className="w-3 h-3" /> Suggest Rotation
                </button>
              )}
              <button onClick={addSlot}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-primary/10 border border-primary/30 text-primary font-display font-bold uppercase tracking-widest text-[9px] rounded hover:bg-primary/20 transition-all">
                <Plus className="w-3 h-3" /> Add Slot
              </button>
            </div>
          </div>

          {assignments.length === 0 && (
            <div className="flex flex-col items-center py-8 text-center border border-dashed border-border rounded-lg">
              <Users className="w-6 h-6 text-muted-foreground/30 mb-2" />
              <p className="text-xs text-muted-foreground font-sans">No assignments yet. Add slots manually or use Suggest Rotation.</p>
            </div>
          )}

          {assignments.map((slot, i) => {
            const isOnLoa = dutyDate && (group.loas ?? []).some((l: any) =>
              l.roster_id === slot.roster_id && l.status === "Active" &&
              new Date(dutyDate) >= new Date(l.start_date) && new Date(dutyDate) <= new Date(l.end_date)
            );
            const isDupe = assignments.filter((s, j) => j !== i && s.roster_id && s.roster_id === slot.roster_id).length > 0;
            return (
              <div key={i} className={`flex items-center gap-2 p-2.5 rounded-lg border ${isOnLoa ? "border-amber-500/40 bg-amber-500/5" : isDupe ? "border-red-500/40 bg-red-500/5" : "border-border bg-secondary/20"}`}>
                <span className="text-[10px] font-display font-black text-muted-foreground w-5 text-center">{i + 1}</span>

                <select value={slot.roster_id} onChange={e => updateSlot(i, "roster_id", e.target.value)}
                  className="flex-1 bg-background border border-border rounded px-2 py-1.5 text-xs font-sans text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                  <option value="">— Select member —</option>
                  {rosterMembers.map((m: any) => (
                    <option key={m.id} value={m.id}>{m.callsign}</option>
                  ))}
                </select>

                <select value={slot.duty_role} onChange={e => updateSlot(i, "duty_role", e.target.value)}
                  className="w-36 bg-background border border-border rounded px-2 py-1.5 text-xs font-sans text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                  {DUTY_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>

                {isOnLoa && <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" title="On LOA this date" />}
                {isDupe && <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0" title="Duplicate assignment" />}

                <button onClick={() => removeSlot(i)} className="text-muted-foreground hover:text-destructive transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Save */}
        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <button onClick={() => setView("list")} className="px-4 py-2 border border-border text-muted-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:bg-secondary/60 transition-all">
            Cancel
          </button>
          <button onClick={save} disabled={saving || !title.trim() || !dutyDate}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:bg-primary/90 transition-all disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? "Saving..." : view === "edit" ? "Save Changes" : "Create Roster"}
          </button>
          {allWarnings.length > 0 && (
            <span className="text-[10px] font-sans text-amber-400 ml-auto">{allWarnings.length} conflict{allWarnings.length !== 1 ? "s" : ""} detected</span>
          )}
        </div>
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  const now = new Date();
  const upcoming = rosters.filter(r => r.duty_date && new Date(r.duty_date) >= now).sort((a, b) => new Date(a.duty_date).getTime() - new Date(b.duty_date).getTime());
  const past = rosters.filter(r => r.duty_date && new Date(r.duty_date) < now).sort((a, b) => new Date(b.duty_date).getTime() - new Date(a.duty_date).getTime());

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ClipboardList className="w-4 h-4 text-primary" />
            <h3 className="font-display font-black text-base uppercase tracking-wider text-foreground">Duty Roster</h3>
            <span className="flex items-center gap-0.5 text-[8px] font-display font-black uppercase tracking-widest px-1.5 py-0.5 rounded border bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
              <Crown className="w-2.5 h-2.5" /> Pro
            </span>
          </div>
          <p className="text-xs text-muted-foreground font-sans">
            Plan assignments, detect conflicts, and cross-check against LOAs and scheduled ops.
          </p>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:bg-primary/90 transition-all shrink-0">
          <Plus className="w-3.5 h-3.5" /> New Roster
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && rosters.length === 0 && (
        <div className="flex flex-col items-center py-14 text-center border border-dashed border-border rounded-lg">
          <ClipboardList className="w-8 h-8 text-muted-foreground/30 mb-3" />
          <p className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground">No duty rosters yet</p>
          <p className="text-xs text-muted-foreground/60 font-sans mt-1 max-w-xs">Create your first rotation plan. The system will flag conflicts and LOA overlaps automatically.</p>
        </div>
      )}

      {!loading && upcoming.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-display font-black uppercase tracking-widest text-muted-foreground">Upcoming</p>
          {upcoming.map(r => <DutyRosterCard key={r.id} roster={r} now={now} onEdit={openEdit} onDelete={del} group={group} />)}
        </div>
      )}

      {!loading && past.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-display font-black uppercase tracking-widest text-muted-foreground mt-4">Past</p>
          {past.map(r => <DutyRosterCard key={r.id} roster={r} now={now} onEdit={openEdit} onDelete={del} group={group} isPast />)}
        </div>
      )}
    </div>
  );
}

function DutyRosterCard({ roster, now, onEdit, onDelete, group, isPast }: {
  roster: DutyEntry; now: Date;
  onEdit: (r: DutyEntry) => void;
  onDelete: (id: string) => void;
  group: any;
  isPast?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const daysUntil = Math.ceil((new Date(roster.duty_date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  // Count assignments on LOA
  const loaConflictCount = (roster.assignments ?? []).filter((a: any) =>
    (group.loas ?? []).some((l: any) =>
      l.roster_id === a.roster_id && l.status === "Active" &&
      new Date(roster.duty_date) >= new Date(l.start_date) &&
      new Date(roster.duty_date) <= new Date(l.end_date)
    )
  ).length;

  return (
    <div className={`rounded-lg border ${isPast ? "border-border/50 bg-secondary/10 opacity-70" : "border-border bg-secondary/20"} overflow-hidden`}>
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-black text-xs uppercase tracking-wider text-foreground truncate">{roster.title}</span>
            {loaConflictCount > 0 && (
              <span className="flex items-center gap-0.5 text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-400">
                <AlertTriangle className="w-2.5 h-2.5" /> {loaConflictCount} LOA conflict{loaConflictCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          <p className="text-[10px] font-sans text-muted-foreground mt-0.5">
            {new Date(roster.duty_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" })}
            {!isPast && daysUntil >= 0 && <span className="ml-2 text-primary font-bold">in {daysUntil}d</span>}
            {roster.assignments?.length > 0 && <span className="ml-2">· {roster.assignments.length} assigned</span>}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!isPast && (
            <button onClick={e => { e.stopPropagation(); onEdit(roster); }}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-colors">
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={e => { e.stopPropagation(); onDelete(roster.id!); }}
            className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-secondary rounded transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border/50 px-4 py-3 space-y-2">
          {roster.description && (
            <p className="text-xs font-sans text-muted-foreground italic">{roster.description}</p>
          )}
          {(!roster.assignments || roster.assignments.length === 0) ? (
            <p className="text-xs text-muted-foreground font-sans">No assignments recorded.</p>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              {roster.assignments.map((a: any, i: number) => {
                const onLoa = (group.loas ?? []).some((l: any) =>
                  l.roster_id === a.roster_id && l.status === "Active" &&
                  new Date(roster.duty_date) >= new Date(l.start_date) &&
                  new Date(roster.duty_date) <= new Date(l.end_date)
                );
                return (
                  <div key={i} className={`flex items-center gap-2 px-2.5 py-1.5 rounded border text-xs font-sans ${onLoa ? "border-amber-500/30 bg-amber-500/5 text-amber-300" : "border-border/50 bg-background/40 text-foreground"}`}>
                    <UserCheck className="w-3 h-3 shrink-0 text-muted-foreground" />
                    <span className="font-bold">{a.callsign}</span>
                    <span className="text-muted-foreground truncate">— {a.duty_role}</span>
                    {onLoa && <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0 ml-auto" title="On LOA" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Inactivity Purge Tab (pure client-side — no backend function, no credits) ──
function InactivityPurgeTab({ group, globalIsPro, showMsg }: any) {
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState<{ inactive: any[]; total_active: number; scanned_at: string } | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [action, setAction] = React.useState<"inactive" | "discharged" | "awol">("inactive");
  const [applying, setApplying] = React.useState(false);
  const { toast } = useToast();

  const scan = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

      // Pull all active roster members — confirmed working path
      const rosterRaw = await apiFetch<any>(`/milsimGroups?path=${group.id}/full`).catch(() => ({}));
      const roster = Array.isArray(rosterRaw?.roster) ? rosterRaw.roster : [];

      // Active LOAs — use loa?path=list (CONF-017 fixed)
      const loas = await apiFetch<any>(`/loa?path=list&group_id=${group.id}`).then(normaliseLoas).catch(() => []);
      const loaRosterIds = new Set((Array.isArray(loas) ? loas : []).filter((l: any) => l.status === "approved").map((l: any) => l.roster_id));

      // Recent AAR participation — use confirmed working path
      const aars = await apiFetch<any[]>(`/milsimGroupsB?path=${group.id}/aars`).catch(() => []);
      const recentParticipants = new Set<string>();
      for (const aar of (Array.isArray(aars) ? aars : [])) {
        const aDate = aar.op_date || aar.created_date;
        if (aDate && new Date(aDate) >= thirtyDaysAgo) {
          if (Array.isArray(aar.participants)) {
            for (const p of aar.participants) {
              const id = typeof p === "string" ? p : p?.roster_id;
              if (id) recentParticipants.add(id);
            }
          }
          if (aar.author_id) recentParticipants.add(aar.author_id);
        }
      }

      const inactive = roster.filter((m: any) => {
        if (!m.id) return false;
        if (m.status && m.status.toLowerCase() !== "active") return false;
        if (loaRosterIds.has(m.id)) return false;
        if (recentParticipants.has(m.id) || recentParticipants.has(m.user_id)) return false;
        const ref = m.join_date || m.created_date;
        if (ref && new Date(ref) >= thirtyDaysAgo) return false;
        return true;
      });

      setResult({ inactive, total_active: roster.filter((m: any) => !m.status || m.status.toLowerCase() === "active").length, scanned_at: new Date().toISOString() });
    } catch (e: any) {
      toast({ title: "Scan failed", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id: string) => setSelected(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const selectAll = () => {
    if (!result) return;
    setSelected(selected.size === result.inactive.length ? new Set() : new Set(result.inactive.map((m: any) => m.id)));
  };

  const applyBulk = async () => {
    if (!selected.size) return;
    setApplying(true);
    try {
      for (const id of Array.from(selected)) {
        await apiFetch(`/milsimGroups?path=${group.id}/roster/${id}`, { method: "PATCH", body: JSON.stringify({
          status: action,
          notes: `Marked ${action} via Inactivity Purge on ${new Date().toLocaleDateString("en-GB")}`,
        }) });
      }
      toast({ title: `${selected.size} member${selected.size !== 1 ? "s" : ""} marked as ${action}` });
      await scan();
    } catch (e: any) {
      toast({ title: "Update failed", description: e.message, variant: "destructive" });
    } finally {
      setApplying(false);
    }
  };

  if (!globalIsPro) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Crown className="w-10 h-10 text-yellow-400 mb-4" />
        <h3 className="font-display font-black text-xl uppercase tracking-wider text-foreground mb-2">Commander Pro Required</h3>
        <p className="text-sm text-muted-foreground font-sans mb-6 max-w-sm">
          Inactivity Purge scans your roster for members with no op activity in the last 30 days.
        </p>
        <a href="/commander-pro" className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 font-display font-bold uppercase tracking-wider text-xs rounded hover:bg-yellow-500/20 transition-all">
          <Crown className="w-3.5 h-3.5" /> Upgrade to Pro
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <UserMinus className="w-4 h-4 text-primary" />
            <h3 className="font-display font-black text-base uppercase tracking-wider text-foreground">Inactivity Purge</h3>
            <span className="flex items-center gap-0.5 text-[8px] font-display font-black uppercase tracking-widest px-1.5 py-0.5 rounded border bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
              <Crown className="w-2.5 h-2.5" /> Pro
            </span>
          </div>
          <p className="text-xs text-muted-foreground font-sans">
            Flags active members with no recorded ops in 30 days and no active LOA. Members who joined under 30 days ago are excluded.
          </p>
        </div>
        <button onClick={scan} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:bg-primary/90 transition-all disabled:opacity-50">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
          {loading ? "Scanning..." : "Run Scan"}
        </button>
      </div>

      {result && (
        <>
          <div className="flex items-center gap-4 p-3 rounded-lg border border-border bg-secondary/30 text-xs font-sans">
            <span className="text-muted-foreground">Scanned <strong className="text-foreground">{result.total_active}</strong> active members</span>
            <span className="text-muted-foreground">·</span>
            <span className={result.inactive.length > 0 ? "text-destructive font-bold" : "text-green-400 font-bold"}>
              {result.inactive.length} inactive
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">{new Date(result.scanned_at).toLocaleString("en-GB")}</span>
          </div>

          {result.inactive.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <CheckCircle2 className="w-8 h-8 text-green-400 mb-3" />
              <p className="font-display font-bold text-sm uppercase tracking-wider text-foreground">All clear</p>
              <p className="text-xs text-muted-foreground font-sans mt-1">No inactive members detected.</p>
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-2.5 bg-secondary/50 border-b border-border">
                  <input type="checkbox" checked={selected.size === result.inactive.length && result.inactive.length > 0} onChange={selectAll} className="rounded border-border" />
                  <span className="text-[10px] font-display font-black uppercase tracking-widest text-muted-foreground flex-1">Callsign</span>
                  <span className="text-[10px] font-display font-black uppercase tracking-widest text-muted-foreground w-16 text-center">Ops</span>
                  <span className="text-[10px] font-display font-black uppercase tracking-widest text-muted-foreground w-24 text-right">Joined</span>
                </div>
                {result.inactive.map((m: any) => (
                  <div key={m.id} className={`flex items-center gap-3 px-4 py-3 border-b border-border/50 last:border-0 transition-colors ${selected.has(m.id) ? "bg-destructive/5" : "hover:bg-secondary/30"}`}>
                    <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} className="rounded border-border" />
                    <span className="font-display font-bold text-xs uppercase tracking-wider text-foreground flex-1">{m.callsign}</span>
                    <span className="text-xs text-muted-foreground font-sans w-16 text-center">{m.ops_count || 0}</span>
                    <span className="text-xs text-muted-foreground font-sans w-24 text-right">
                      {m.join_date ? new Date(m.join_date).toLocaleDateString("en-GB") : "—"}
                    </span>
                  </div>
                ))}
              </div>

              {selected.size > 0 && (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-destructive/30 bg-destructive/5">
                  <span className="text-xs font-display font-bold uppercase tracking-wider text-destructive">{selected.size} selected</span>
                  <span className="text-muted-foreground text-xs">→</span>
                  <select value={action} onChange={e => setAction(e.target.value as any)}
                    className="bg-background border border-border text-xs font-display font-bold uppercase tracking-wider rounded px-2 py-1.5 text-foreground">
                    <option value="inactive">Mark as Inactive</option>
                    <option value="discharged">Mark as Discharged</option>
                    <option value="awol">Mark as AWOL</option>
                  </select>
                  <button onClick={applyBulk} disabled={applying}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-destructive text-destructive-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:bg-destructive/90 transition-all disabled:opacity-50 ml-auto">
                    {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserMinus className="w-3 h-3" />}
                    {applying ? "Applying..." : "Apply to Selected"}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {!result && !loading && (
        <div className="flex flex-col items-center py-14 text-center">
          <UserMinus className="w-8 h-8 text-muted-foreground/30 mb-3" />
          <p className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground">Run a scan to check your roster</p>
          <p className="text-xs text-muted-foreground/60 font-sans mt-1 max-w-xs">Checks op participation and active LOAs. Members on LOA are automatically excluded.</p>
        </div>
      )}
    </div>
  );
}

// ─── Member Engagement Score Tab ──────────────────────────────────────────────
// Pure client-side. Reads existing ops/AARs/LOAs via apiFetch. Zero backend credits.
function EngagementScoreTab({ group, globalIsPro }: any) {
  const [scores, setScores] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [sortBy, setSortBy] = React.useState<"score"|"callsign"|"ops"|"aars"|"trend">("score");
  const [sortDir, setSortDir] = React.useState<"desc"|"asc">("desc");
  const [filter, setFilter] = React.useState<"all"|"green"|"amber"|"red">("all");
  const [scannedAt, setScannedAt] = React.useState<string | null>(null);

  const roster: any[] = React.useMemo(() => (group.roster ?? []).filter((r: any) => r.status === "active" || !r.status), [group]);

  const runScan = async () => {
    if (!globalIsPro) return;
    setLoading(true);
    try {
      // Fetch AARs, LOAs, and AppUser last_active_at data in parallel
      const [_aarsRaw2, _loasRaw2, appUsers] = await Promise.all([
        apiFetch<any>(`/milsimAars?path=list&group_id=${group.id}`).then(normaliseAars).catch(() => []),
        apiFetch<any>(`/loa?path=list&group_id=${group.id}`).then(normaliseLoas).catch(() => []),
        apiFetch<any[]>(`/api/users?roster=true`).catch(() => []),
      ]);
      const aars: any[] = _aarsRaw2 as any[];
      const loas: any[] = _loasRaw2 as any[];

      const aarsByRoster: Record<string, number> = {};
      (Array.isArray(aars) ? aars : []).forEach((a: any) => {
        (a.participants ?? []).forEach((pid: string) => {
          aarsByRoster[pid] = (aarsByRoster[pid] ?? 0) + 1;
        });
        if (a.author_id) aarsByRoster[a.author_id] = (aarsByRoster[a.author_id] ?? 0) + 1;
      });
      const loasByUser: Record<string, number> = {};
      (Array.isArray(loas) ? loas : []).forEach((l: any) => {
        if (l.status === "approved" && l.roster_id) loasByUser[l.roster_id] = (loasByUser[l.roster_id] ?? 0) + 1;
      });
      // Map user_id → last_active_at timestamp
      const lastActiveByUser: Record<string, number> = {};
      (Array.isArray(appUsers) ? appUsers : []).forEach((u: any) => {
        if (u.id && u.last_active_at) {
          lastActiveByUser[u.id] = new Date(u.last_active_at).getTime();
        }
      });

      const now = Date.now();
      const THIRTY_DAYS = 30 * 86400000;
      const computed = roster.map((m: any) => {
        const ops = m.ops_count ?? 0;
        const aarCount = aarsByRoster[m.id] ?? aarsByRoster[m.user_id] ?? 0;
        const loaCount = loasByUser[m.id] ?? 0;
        const joinDays = m.join_date ? Math.max(1, Math.floor((now - new Date(m.join_date).getTime()) / 86400000)) : 90;
        const tenureMonths = joinDays / 30;

        // Recency — platform login activity takes priority over ops_count proxy
        const lastActive = lastActiveByUser[m.user_id] ?? 0;
        const daysSinceActive = lastActive > 0 ? Math.floor((now - lastActive) / 86400000) : 999;
        // Full 10pts if active in last 7 days, 7pts within 30 days, 3pts within 60, 0 if ghost
        const recencyScore = daysSinceActive <= 7 ? 10 : daysSinceActive <= 30 ? 7 : daysSinceActive <= 60 ? 3 : ops > 0 ? 2 : 0;

        // Score components (out of 100)
        // Op cadence: ops per month, cap at 4/mo = full marks (35pts)
        const opsPerMonth = ops / Math.max(tenureMonths, 1);
        const opScore = Math.min(35, Math.round((opsPerMonth / 4) * 35));
        // AAR participation (25pts)
        const aarScore = Math.min(25, Math.round((aarCount / Math.max(ops, 1)) * 25));
        // Tenure bonus (20pts) — longer member = more trust
        const tenureScore = Math.min(20, Math.round((tenureMonths / 12) * 20));
        // LOA compliance (10pts) — using LOA system properly = good discipline
        const loaScore = loaCount > 0 ? 10 : ops > 0 ? 5 : 0;

        const total = opScore + aarScore + tenureScore + loaScore + recencyScore;
        const tier = total >= 70 ? "green" : total >= 40 ? "amber" : "red";
        const tierLabel = total >= 70 ? "Active" : total >= 40 ? "At Risk" : "Inactive";
        const _lastSeenDays = daysSinceActive < 999 ? daysSinceActive : null;

        return { ...m, _ops: ops, _aars: aarCount, _loas: loaCount, _score: total, _tier: tier, _tierLabel: tierLabel, _opScore: opScore, _aarScore: aarScore, _tenureScore: tenureScore, _recencyScore: recencyScore, _lastSeenDays };
      });
      setScores(computed);
      setScannedAt(new Date().toLocaleTimeString("en-GB"));
    } finally { setLoading(false); }
  };

  const sorted = React.useMemo(() => {
    const filtered = filter === "all" ? scores : scores.filter(s => s._tier === filter);
    return [...filtered].sort((a, b) => {
      const field: Record<string, any> = { score: "_score", callsign: "callsign", ops: "_ops", aars: "_aars" };
      const key = field[sortBy] ?? "_score";
      const av = typeof a[key] === "string" ? a[key].toLowerCase() : a[key];
      const bv = typeof b[key] === "string" ? b[key].toLowerCase() : b[key];
      return sortDir === "desc" ? (bv > av ? 1 : -1) : (av > bv ? 1 : -1);
    });
  }, [scores, sortBy, sortDir, filter]);

  const toggleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const tierCounts = React.useMemo(() => ({
    green: scores.filter(s => s._tier === "green").length,
    amber: scores.filter(s => s._tier === "amber").length,
    red:   scores.filter(s => s._tier === "red").length,
  }), [scores]);

  if (!globalIsPro) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <Crown className="w-10 h-10 text-yellow-400/50" />
      <p className="font-display font-black uppercase tracking-widest text-base text-muted-foreground">Commander Pro Required</p>
      <p className="text-sm text-muted-foreground/60 font-sans max-w-xs">Member Engagement Scoring is a Pro feature. Upgrade to identify at-risk operators before they go ghost.</p>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest text-base text-foreground flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" /> Member Engagement Score
          </h3>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Scores every active roster member across ops, AARs, tenure and LOA discipline.</p>
        </div>
        <button onClick={runScan} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:bg-primary/90 transition-all disabled:opacity-50 shrink-0">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5" />}
          {loading ? "Scanning..." : scannedAt ? "Re-scan" : "Run Scan"}
        </button>
      </div>

      {scannedAt && scores.length > 0 && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Active Operators", count: tierCounts.green, color: "text-green-400", bg: "bg-green-500/10 border-green-500/20", tier: "green" as const },
              { label: "At Risk",          count: tierCounts.amber, color: "text-amber-400",  bg: "bg-amber-500/10 border-amber-500/20",  tier: "amber" as const },
              { label: "Ghost / Inactive", count: tierCounts.red,   color: "text-red-400",    bg: "bg-red-500/10 border-red-500/20",      tier: "red" as const },
            ].map(c => (
              <button key={c.tier} onClick={() => setFilter(f => f === c.tier ? "all" : c.tier)}
                className={`p-3 rounded-lg border cursor-pointer transition-all text-left ${c.bg} ${filter === c.tier ? "ring-1 ring-primary" : ""}`}>
                <p className={`font-display font-black text-2xl ${c.color}`}>{c.count}</p>
                <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mt-0.5">{c.label}</p>
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-secondary/50 text-[9px] font-display font-bold uppercase tracking-widest text-muted-foreground">
              {[
                { col: "callsign" as const, label: "Callsign", span: "col-span-3" },
                { col: "score"    as const, label: "Score",    span: "col-span-2" },
                { col: "ops"      as const, label: "Ops",      span: "col-span-1" },
                { col: "aars"     as const, label: "AARs",     span: "col-span-1" },
              ].map(h => (
                <button key={h.col} onClick={() => toggleSort(h.col)} className={`${h.span} flex items-center gap-1 hover:text-foreground transition-colors`}>
                  {h.label}
                  {sortBy === h.col && <span>{sortDir === "desc" ? "↓" : "↑"}</span>}
                </button>
              ))}
              <span className="col-span-2 text-[9px]">Breakdown</span>
              <span className="col-span-3 text-[9px]">Status</span>
            </div>

            {sorted.length === 0 && (
              <div className="py-10 text-center text-sm text-muted-foreground font-sans">No members match this filter.</div>
            )}

            {sorted.map((m: any) => {
              const barW = Math.round((m._score / 100) * 100);
              const barColor = m._tier === "green" ? "bg-green-500" : m._tier === "amber" ? "bg-amber-500" : "bg-red-500";
              const badgeStyle = m._tier === "green"
                ? "text-green-400 bg-green-500/10 border-green-500/30"
                : m._tier === "amber"
                ? "text-amber-400 bg-amber-500/10 border-amber-500/30"
                : "text-red-400 bg-red-500/10 border-red-500/30";
              return (
                <div key={m.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-border/50 items-center hover:bg-secondary/20 transition-colors">
                  <span className="col-span-3 font-display font-bold text-xs uppercase tracking-wider text-foreground truncate">{m.callsign}</span>
                  <div className="col-span-2 flex items-center gap-2">
                    <span className={`font-display font-black text-sm ${m._tier === "green" ? "text-green-400" : m._tier === "amber" ? "text-amber-400" : "text-red-400"}`}>{m._score}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${barW}%` }} />
                    </div>
                  </div>
                  <span className="col-span-1 text-xs text-muted-foreground font-mono text-center">{m._ops}</span>
                  <span className="col-span-1 text-xs text-muted-foreground font-mono text-center">{m._aars}</span>
                  <div className="col-span-2 flex items-center gap-1 text-[9px] text-muted-foreground/70 font-mono">
                    <span title="Ops score">{m._opScore}+</span>
                    <span title="AAR score">{m._aarScore}+</span>
                    <span title="Tenure">{m._tenureScore}</span>
                  </div>
                  <div className="col-span-3 flex items-center justify-between">
                    <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${badgeStyle}`}>{m._tierLabel}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-muted-foreground/50 font-sans">Last scanned at {scannedAt} · Score: Ops cadence (35) + AAR participation (25) + Tenure (20) + LOA discipline (10) + Platform recency (10)</p>
        </>
      )}

      {!scannedAt && !loading && (
        <div className="flex flex-col items-center py-16 text-center gap-3 border border-dashed border-border rounded-lg">
          <Activity className="w-8 h-8 text-muted-foreground/30" />
          <p className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground">No scan run yet</p>
          <p className="text-xs text-muted-foreground/60 font-sans max-w-xs">Hit Run Scan to score all {roster.length} active operators. Identifies who's engaged, who's at risk, and who's gone ghost.</p>
        </div>
      )}
    </div>
  );
}

// ─── Automated Promotion Engine ───────────────────────────────────────────────
function PromotionEngineTab({ group, globalIsPro, showMsg }: any) {
  const [rules, setRules] = React.useState<any[]>([]);
  const [flags, setFlags] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [scanning, setScanning] = React.useState(false);
  const [showRuleForm, setShowRuleForm] = React.useState(false);
  const [editRule, setEditRule] = React.useState<any | null>(null);
  const [savingRule, setSavingRule] = React.useState(false);
  const [view, setView] = React.useState<"rules"|"flags">("flags");

  const ranks: any[] = group.ranks ?? [];
  const roster: any[] = (group.roster ?? []).filter((r: any) => r.status === "active" || !r.status);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, f] = await Promise.all([
        apiFetch<any[]>(`/api/promotion-rules?entity=rules&group_id=${group.id}`).catch(() => []),
        apiFetch<any[]>(`/api/promotion-flags?entity=flags&group_id=${group.id}&status=pending`).catch(() => []),
      ]);
      setRules(Array.isArray(r) ? r : []);
      setFlags(Array.isArray(f) ? f : []);
    } finally { setLoading(false); }
  };

  React.useEffect(() => { if (globalIsPro) loadAll(); }, [group.id, globalIsPro]);

  const runScan = async () => {
    setScanning(true);
    try {
      const conducts: any[] = await apiFetch<any>(`/milsimConductReport?path=list&group_id=${group.id}`).then(normaliseConductReports).catch(() => []);
      const activeConduct = new Set((Array.isArray(conducts) ? conducts : [])
        .filter((c: any) => c.outcome === "warning" || c.outcome === "pending")
        .map((c: any) => c.subject_roster_id));
      const existingFlags = await apiFetch<any[]>(`/api/promotion-flags?entity=flags&group_id=${group.id}`).catch(() => []);
      const alreadyFlagged = new Set((Array.isArray(existingFlags) ? existingFlags : [])
        .filter((f: any) => f.status === "pending" || f.status === "approved")
        .map((f: any) => `${f.roster_id}_${f.rule_id}`));
      let newFlags = 0;
      const now = Date.now();
      for (const rule of rules.filter(r => r.is_active)) {
        for (const member of roster) {
          if (member.rank_id !== rule.from_rank_id) continue;
          const ops = member.ops_count ?? 0;
          const tenureDays = member.join_date ? Math.floor((now - new Date(member.join_date).getTime()) / 86400000) : 0;
          if (ops < (rule.min_ops ?? 0)) continue;
          if (tenureDays < (rule.min_tenure_days ?? 0)) continue;
          if (rule.require_no_active_conduct && activeConduct.has(member.id)) continue;
          const key = `${member.id}_${rule.id}`;
          if (alreadyFlagged.has(key)) continue;
          await apiFetch("/api/promotion-flags?entity=flags", { method: "POST", body: JSON.stringify({
            group_id: group.id, roster_id: member.id, callsign: member.callsign,
            rule_id: rule.id, from_rank_id: rule.from_rank_id, to_rank_id: rule.to_rank_id,
            from_rank_name: rule.from_rank_name, to_rank_name: rule.to_rank_name,
            ops_at_flag: ops, tenure_days_at_flag: tenureDays, status: "pending"
          }) }).catch(() => {});
          newFlags++;
        }
      }
      showMsg(newFlags > 0 ? `${newFlags} member(s) flagged for promotion` : "No new promotions flagged");
      loadAll();
    } finally { setScanning(false); }
  };

  const reviewFlag = async (flag: any, action: "approved"|"dismissed") => {
    await apiFetch(`/api/promotion-flags?entity=flags&id=${flag.id}`, { method: "PUT", body: JSON.stringify({ ...flag, status: action }) }).catch(() => {});
    if (action === "approved") {
      const member = roster.find((r: any) => r.id === flag.roster_id);
      if (member) {
        await apiFetch(`/milsimRosters?id=${member.id}`, { method: "PUT", body: JSON.stringify({ ...member, rank_id: flag.to_rank_id }) }).catch(() => {});
        await apiFetch("/notifications", { method: "POST", body: JSON.stringify({
          user_id: member.user_id, type: "promotion", is_read: false,
          title: "Congratulations — You've Been Promoted",
          body: `You have been promoted from ${flag.from_rank_name} to ${flag.to_rank_name} in ${group.name}.`,
          link: "/member-hq"
        }) }).catch(() => {});
      }
      showMsg(`${flag.callsign} promoted to ${flag.to_rank_name}`);
    } else { showMsg("Flag dismissed"); }
    loadAll();
  };

  const deleteRule = async (id: string) => {
    if (!confirm("Delete this rule?")) return;
    await apiFetch(`/api/promotion-rules?entity=rules&id=${id}`, { method: "DELETE" }).catch(() => {});
    loadAll();
  };

  if (!globalIsPro) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <Crown className="w-10 h-10 text-yellow-400/50" />
      <p className="font-display font-black uppercase tracking-widest text-base text-muted-foreground">Commander Pro Required</p>
      <p className="text-sm text-muted-foreground/60 font-sans max-w-xs">Automated Promotion Engine is a Pro feature.</p>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest text-base text-foreground flex items-center gap-2">
            <ChevronUp className="w-4 h-4 text-primary" /> Automated Promotion Engine
          </h3>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Set promotion criteria. System flags eligible members automatically — you approve or dismiss.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => { setEditRule(null); setShowRuleForm(true); }}
            className="flex items-center gap-1.5 px-3 py-2 bg-secondary border border-border text-muted-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:text-foreground transition-all">
            <Plus className="w-3.5 h-3.5" /> Rule
          </button>
          <button onClick={runScan} disabled={scanning || rules.filter(r => r.is_active).length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:bg-primary/90 transition-all disabled:opacity-50">
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            {scanning ? "Scanning..." : "Run Scan"}
          </button>
        </div>
      </div>

      {/* View toggle */}
      <div className="flex gap-2">
        {([["flags","Pending Flags"], ["rules","Rules"]] as const).map(([id, label]) => (
          <button key={id} onClick={() => setView(id)}
            className={`px-3 py-1.5 text-xs font-display font-bold uppercase tracking-widest rounded border transition-colors ${view === id ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}>
            {label}{id === "flags" && flags.length > 0 && <span className="ml-1.5 bg-primary/20 text-primary px-1.5 py-0.5 rounded-full text-[9px]">{flags.length}</span>}
          </button>
        ))}
      </div>

      {/* Pending Flags */}
      {view === "flags" && (
        <div className="space-y-2">
          {loading && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
          {!loading && flags.length === 0 && (
            <div className="flex flex-col items-center py-14 text-center gap-3 border border-dashed border-border rounded-lg">
              <ChevronUp className="w-8 h-8 text-muted-foreground/30" />
              <p className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground">No pending promotions</p>
              <p className="text-xs text-muted-foreground/60 font-sans">Create rules and run a scan to flag eligible members.</p>
            </div>
          )}
          {flags.map((f: any) => (
            <div key={f.id} className="flex items-center gap-3 p-4 border border-border rounded-lg bg-secondary/20 hover:bg-secondary/40 transition-colors">
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-xs uppercase tracking-wider text-foreground">{f.callsign}</p>
                <p className="text-[10px] text-muted-foreground font-sans mt-0.5">
                  <span className="text-muted-foreground/60">{f.from_rank_name}</span>
                  <span className="mx-1.5 text-primary">→</span>
                  <span className="text-primary font-bold">{f.to_rank_name}</span>
                  <span className="mx-2 text-muted-foreground/40">·</span>
                  {f.ops_at_flag} ops · {Math.round(f.tenure_days_at_flag / 30)} months
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => reviewFlag(f, "approved")}
                  className="flex items-center gap-1 px-3 py-1.5 bg-green-500/10 border border-green-500/30 text-green-400 font-display font-bold uppercase tracking-widest text-[10px] rounded hover:bg-green-500/20 transition-colors">
                  <Check className="w-3 h-3" /> Approve
                </button>
                <button onClick={() => reviewFlag(f, "dismissed")}
                  className="flex items-center gap-1 px-3 py-1.5 bg-secondary border border-border text-muted-foreground font-display font-bold uppercase tracking-widest text-[10px] rounded hover:text-foreground transition-colors">
                  <X className="w-3 h-3" /> Dismiss
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rules list */}
      {view === "rules" && (
        <div className="space-y-2">
          {rules.length === 0 && !loading && (
            <div className="flex flex-col items-center py-14 text-center gap-3 border border-dashed border-border rounded-lg">
              <p className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground">No promotion rules yet</p>
              <p className="text-xs text-muted-foreground/60 font-sans">Click "+ Rule" to define your first promotion criteria.</p>
            </div>
          )}
          {rules.map((r: any) => (
            <div key={r.id} className="flex items-center gap-3 p-4 border border-border rounded-lg">
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-xs uppercase tracking-wider text-foreground">{r.from_rank_name} → {r.to_rank_name}</p>
                <p className="text-[10px] text-muted-foreground font-sans mt-0.5">
                  {r.min_ops > 0 && `${r.min_ops} ops`}
                  {r.min_tenure_days > 0 && ` · ${Math.round(r.min_tenure_days/30)}mo tenure`}
                  {r.require_aar && " · AAR filed"}
                  {r.require_no_active_conduct && " · Clean conduct"}
                </p>
              </div>
              <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${r.is_active ? "text-green-400 bg-green-500/10 border-green-500/30" : "text-muted-foreground bg-secondary border-border"}`}>{r.is_active ? "Active" : "Paused"}</span>
              <button onClick={() => { setEditRule(r); setShowRuleForm(true); }} className="text-muted-foreground hover:text-foreground transition-colors p-1"><Edit3 className="w-3.5 h-3.5" /></button>
              <button onClick={() => deleteRule(r.id)} className="text-muted-foreground hover:text-red-400 transition-colors p-1"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      {/* Rule form modal */}
      {showRuleForm && (
        <PromotionRuleForm ranks={ranks} rule={editRule} groupId={group.id}
          onSave={async (data: any) => {
            setSavingRule(true);
            try {
              if (data.id) await apiFetch(`/api/promotion-rules?entity=rules&id=${data.id}`, { method: "PUT", body: JSON.stringify(data) });
              else await apiFetch("/api/promotion-rules?entity=rules", { method: "POST", body: JSON.stringify({ ...data, group_id: group.id }) });
              showMsg(data.id ? "Rule updated" : "Rule created");
              setShowRuleForm(false); setEditRule(null); loadAll();
            } finally { setSavingRule(false); }
          }}
          onClose={() => { setShowRuleForm(false); setEditRule(null); }}
          saving={savingRule} />
      )}
    </div>
  );
}

function PromotionRuleForm({ ranks, rule, groupId, onSave, onClose, saving }: any) {
  const [fromRankId, setFromRankId] = React.useState(rule?.from_rank_id ?? "");
  const [toRankId, setToRankId]     = React.useState(rule?.to_rank_id ?? "");
  const [minOps, setMinOps]         = React.useState(rule?.min_ops ?? 0);
  const [minMonths, setMinMonths]   = React.useState(rule ? Math.round((rule.min_tenure_days ?? 0) / 30) : 0);
  const [requireAar, setRequireAar] = React.useState(rule?.require_aar ?? false);
  const [requireClean, setRequireClean] = React.useState(rule?.require_no_active_conduct ?? true);
  const [isActive, setIsActive]     = React.useState(rule?.is_active ?? true);
  const fromRank = ranks.find((r: any) => r.id === fromRankId);
  const toRank   = ranks.find((r: any) => r.id === toRankId);
  const submit = () => {
    if (!fromRankId || !toRankId || fromRankId === toRankId) return;
    onSave({ ...(rule ?? {}), from_rank_id: fromRankId, to_rank_id: toRankId, from_rank_name: fromRank?.name ?? "", to_rank_name: toRank?.name ?? "", min_ops: minOps, min_tenure_days: minMonths * 30, require_aar: requireAar, require_no_active_conduct: requireClean, is_active: isActive });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black uppercase tracking-widest text-sm text-foreground">{rule ? "Edit Rule" : "New Promotion Rule"}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">From Rank</label>
            <select value={fromRankId} onChange={e => setFromRankId(e.target.value)} className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground focus:outline-none focus:border-primary">
              <option value="">Select rank</option>
              {ranks.map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">To Rank</label>
            <select value={toRankId} onChange={e => setToRankId(e.target.value)} className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground focus:outline-none focus:border-primary">
              <option value="">Select rank</option>
              {ranks.filter((r: any) => r.id !== fromRankId).map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Min Ops</label>
            <input type="number" min={0} value={minOps} onChange={e => setMinOps(+e.target.value)} className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground focus:outline-none focus:border-primary" />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Min Months</label>
            <input type="number" min={0} value={minMonths} onChange={e => setMinMonths(+e.target.value)} className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded text-foreground focus:outline-none focus:border-primary" />
          </div>
        </div>
        <div className="space-y-2">
          {[
            { label: "Require AAR filed", val: requireAar, set: setRequireAar },
            { label: "Require clean conduct record", val: requireClean, set: setRequireClean },
            { label: "Rule is active", val: isActive, set: setIsActive },
          ].map(item => (
            <label key={item.label} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={item.val} onChange={e => item.set(e.target.checked)} className="rounded border-border" />
              <span className="text-xs font-sans text-foreground">{item.label}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={submit} disabled={saving || !fromRankId || !toRankId || fromRankId === toRankId}
            className="flex-1 py-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:bg-primary/90 disabled:opacity-50 transition-all">
            {saving ? "Saving..." : rule ? "Update Rule" : "Create Rule"}
          </button>
          <button onClick={onClose} className="px-4 py-2 bg-secondary border border-border text-muted-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:text-foreground transition-all">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Unit Health Dashboard ─────────────────────────────────────────────────────
// Pure client-side — reads existing entities, zero backend credits.
function UnitHealthDashboard({ group, globalIsPro }: any) {
  const [data, setData] = React.useState<any | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [refreshedAt, setRefreshedAt] = React.useState<string | null>(null);

  const roster: any[] = group.roster ?? [];
  const activeRoster = roster.filter((r: any) => r.status === "active" || !r.status);

  const load = async () => {
    setLoading(true);
    try {
      const [_aarsR, _opsR, _loasR, _appsR, _conductsR] = await Promise.all([
        apiFetch<any>(`/milsimAars?path=list&group_id=${group.id}`).then(normaliseAars).catch(() => []),
        apiFetch<any>(`/milsimOps?group_id=${group.id}`).then(normaliseOps).catch(() => []),
        apiFetch<any>(`/loa?path=list&group_id=${group.id}`).then(normaliseLoas).catch(() => []),
        apiFetch<any>(`/milsimApplications?path=${group.id}/applications`).then(normaliseApplications).catch(() => []),
        apiFetch<any>(`/milsimConductReport?path=list&group_id=${group.id}`).then(normaliseConductReports).catch(() => []),
      ]);

      const aarList: any[] = _aarsR as any[];
      const opsList: any[] = _opsR as any[];
      const loaList: any[] = _loasR as any[];
      const appList: any[] = _appsR as any[];
      const conductList: any[] = _conductsR as any[];

      const now = Date.now();
      const day30 = now - 30 * 86400000;
      const day60 = now - 60 * 86400000;

      // Roster fill & retention
      const totalRoster = roster.length;
      const activeCount = activeRoster.length;
      const inactiveCount = roster.filter((r: any) => r.status === "inactive" || r.status === "awol").length;
      const joinedLast30 = roster.filter((r: any) => r.join_date && new Date(r.join_date).getTime() > day30).length;
      const retentionPct = totalRoster > 0 ? Math.round((activeCount / totalRoster) * 100) : 0;

      // Op tempo
      const opsLast30 = opsList.filter((o: any) => o.scheduled_at && new Date(o.scheduled_at).getTime() > day30).length;
      const opsLast60 = opsList.filter((o: any) => o.scheduled_at && new Date(o.scheduled_at).getTime() > day60 && new Date(o.scheduled_at).getTime() <= day30).length;
      const opTrend = opsLast30 >= opsLast60 ? "up" : "down";

      // AAR discipline
      const aarsLast30 = aarList.filter((a: any) => new Date(a.created_date ?? a.created_at ?? 0).getTime() > day30).length;
      const aarRate = opsLast30 > 0 ? Math.round((aarsLast30 / opsLast30) * 100) : 0;

      // LOA load
      const activeLoas = loaList.filter((l: any) => l.status === "approved" && (!l.end_date || new Date(l.end_date).getTime() > now)).length;
      const loaLoad = activeCount > 0 ? Math.round((activeLoas / activeCount) * 100) : 0;

      // Recruitment pipeline
      const pendingApps = appList.filter((a: any) => a.status === "pending" || a.status === "reviewing").length;

      // Conduct
      const openConduct = conductList.filter((c: any) => c.outcome === "pending" || c.outcome === "warning").length;

      // Overall health score (0-100)
      const retScore   = Math.min(25, Math.round((retentionPct / 100) * 25));
      const opScore    = Math.min(25, opsLast30 >= 2 ? 25 : Math.round((opsLast30 / 2) * 25));
      const aarScore   = Math.min(25, Math.round((Math.min(aarRate, 100) / 100) * 25));
      const conductScore = Math.max(0, 25 - openConduct * 5);
      const overallScore = retScore + opScore + aarScore + conductScore;
      const healthLabel = overallScore >= 80 ? "Combat Ready" : overallScore >= 55 ? "Degraded" : "Critical";
      const healthColor = overallScore >= 80 ? "text-green-400" : overallScore >= 55 ? "text-amber-400" : "text-red-400";
      const healthBg    = overallScore >= 80 ? "bg-green-500/10 border-green-500/30" : overallScore >= 55 ? "bg-amber-500/10 border-amber-500/30" : "bg-red-500/10 border-red-500/30";

      setData({ totalRoster, activeCount, inactiveCount, joinedLast30, retentionPct, opsLast30, opsLast60, opTrend, aarsLast30, aarRate, activeLoas, loaLoad, pendingApps, openConduct, overallScore, healthLabel, healthColor, healthBg, retScore, opScore, aarScore, conductScore });
      setRefreshedAt(new Date().toLocaleTimeString("en-GB"));
    } finally { setLoading(false); }
  };

  React.useEffect(() => { if (globalIsPro) load(); }, [group.id]);

  if (!globalIsPro) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <Crown className="w-10 h-10 text-yellow-400/50" />
      <p className="font-display font-black uppercase tracking-widest text-base text-muted-foreground">Commander Pro Required</p>
      <p className="text-sm text-muted-foreground/60 font-sans max-w-xs">Unit Health Dashboard is a Pro feature.</p>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest text-base text-foreground flex items-center gap-2">
            <Heart className="w-4 h-4 text-primary" /> Unit Health Dashboard
          </h3>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">CO morning brief. Live snapshot across all unit metrics.</p>
        </div>
        <button onClick={load} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:bg-primary/90 transition-all disabled:opacity-50 shrink-0">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />}
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {loading && !data && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}

      {data && (
        <>
          {/* Overall health banner */}
          <div className={`rounded-xl border p-5 flex items-center gap-6 ${data.healthBg}`}>
            <div className="flex-1">
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Overall Unit Status</p>
              <p className={`font-display font-black text-3xl mt-1 ${data.healthColor}`}>{data.overallScore}<span className="text-lg text-muted-foreground">/100</span></p>
              <p className={`text-xs font-display font-bold uppercase tracking-widest mt-1 ${data.healthColor}`}>{data.healthLabel}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-right">
              {[
                { label: "Retention",    val: data.retScore,     max: 25 },
                { label: "Op Tempo",     val: data.opScore,      max: 25 },
                { label: "AAR Discipline",val: data.aarScore,    max: 25 },
                { label: "Conduct",      val: data.conductScore, max: 25 },
              ].map(s => (
                <div key={s.label}>
                  <p className="text-[9px] font-display font-bold uppercase tracking-widest text-muted-foreground">{s.label}</p>
                  <p className="font-display font-bold text-sm text-foreground">{s.val}<span className="text-muted-foreground text-[10px]">/{s.max}</span></p>
                </div>
              ))}
            </div>
          </div>

          {/* Metric tiles */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { label: "Active Operators",  value: data.activeCount,    sub: `of ${data.totalRoster} total`,  icon: <Users className="w-4 h-4" />,      color: "text-foreground" },
              { label: "Retention Rate",    value: `${data.retentionPct}%`, sub: `${data.inactiveCount} inactive`, icon: <TrendingUp className="w-4 h-4" />, color: data.retentionPct >= 75 ? "text-green-400" : data.retentionPct >= 50 ? "text-amber-400" : "text-red-400" },
              { label: "Ops Last 30 Days",  value: data.opsLast30,      sub: data.opTrend === "up" ? "↑ vs prev 30d" : "↓ vs prev 30d", icon: <Siren className="w-4 h-4" />, color: data.opTrend === "up" ? "text-green-400" : "text-amber-400" },
              { label: "AAR Rate",          value: `${data.aarRate}%`,  sub: `${data.aarsLast30} filed / ${data.opsLast30} ops`, icon: <ClipboardList className="w-4 h-4" />, color: data.aarRate >= 80 ? "text-green-400" : data.aarRate >= 50 ? "text-amber-400" : "text-red-400" },
              { label: "On LOA",            value: data.activeLoas,     sub: `${data.loaLoad}% of active roster`, icon: <PlaneTakeoff className="w-4 h-4" />, color: data.loaLoad > 30 ? "text-amber-400" : "text-foreground" },
              { label: "Open Conduct",      value: data.openConduct,    sub: data.openConduct === 0 ? "All clear" : "Needs review", icon: <ShieldCheck className="w-4 h-4" />, color: data.openConduct === 0 ? "text-green-400" : "text-red-400" },
              { label: "Pending Recruits",  value: data.pendingApps,    sub: "In pipeline",                   icon: <UserPlus className="w-4 h-4" />,   color: "text-foreground" },
              { label: "New Joins (30d)",   value: data.joinedLast30,   sub: "Joined last 30 days",           icon: <Star className="w-4 h-4" />,       color: data.joinedLast30 > 0 ? "text-green-400" : "text-muted-foreground" },
            ].map(tile => (
              <div key={tile.label} className="p-4 rounded-lg border border-border bg-secondary/20 space-y-1">
                <div className="flex items-center gap-1.5 text-muted-foreground">{tile.icon}<p className="text-[9px] font-display font-bold uppercase tracking-widest">{tile.label}</p></div>
                <p className={`font-display font-black text-2xl ${tile.color}`}>{tile.value}</p>
                <p className="text-[10px] text-muted-foreground font-sans">{tile.sub}</p>
              </div>
            ))}
          </div>

          <p className="text-[10px] text-muted-foreground/50 font-sans">Last refreshed {refreshedAt}</p>
        </>
      )}

      {!data && !loading && (
        <div className="flex flex-col items-center py-16 text-center gap-3 border border-dashed border-border rounded-lg">
          <Heart className="w-8 h-8 text-muted-foreground/30" />
          <p className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground">Hit Refresh to load your unit health snapshot</p>
        </div>
      )}
    </div>
  );
}

// ─── Featured Listing Tab ──────────────────────────────────────────────────────
function FeaturedListingTab({ group, showMsg, globalIsPro, onGroupUpdate }: any) {
  const { token } = useAuth() as any;
  const [requesting, setRequesting] = React.useState(false);
  const isFeatured = group.status === "featured";
  const isPendingReview = group.featured_requested && !isFeatured;
  const isPro = globalIsPro;

  const requestFeature = async () => {
    if (!isPro) return;
    setRequesting(true);
    try {
      // 1. Mark the group as having requested featured
      await apiFetch(`/api/milsim-groups?path=update&id=${group.id}`, {
        method: "PATCH",
        body: JSON.stringify({ featured_requested: true }),
      }).catch(() => {});

      // 2. Notify all platform admins via the admin function
      await apiFetch("/admin?path=notify-admins", {
        method: "POST",
        body: JSON.stringify({
          type: "featured_request",
          title: "Featured Listing Request",
          body: `${group.name} has requested a Featured listing on the registry.`,
          link: `/portal/admin?tab=featured`,
          group_id: group.id,
          group_name: group.name,
        }),
      }).catch(() => {});

      showMsg(true, "Featured request sent to TAG admin — we'll review within 24h.");
      if (onGroupUpdate) onGroupUpdate({ ...group, featured_requested: true });
    } finally { setRequesting(false); }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h3 className="font-display font-black uppercase tracking-widest text-base text-foreground flex items-center gap-2">
          <Star className="w-4 h-4 text-yellow-400" /> Featured Registry Listing
        </h3>
        <p className="text-xs text-muted-foreground font-sans mt-0.5">Featured units appear at the top of the registry with a gold badge — maximum recruitment visibility.</p>
      </div>

      {isFeatured ? (
        <div className="p-5 rounded-xl border border-yellow-500/30 bg-yellow-500/5 space-y-3">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
            <p className="font-display font-black uppercase tracking-widest text-sm text-yellow-400">Your unit is Featured</p>
          </div>
          <p className="text-xs text-muted-foreground font-sans">You appear at the top of the TAG Registry with the Featured badge. Members browsing the registry see your unit first.</p>
        </div>
      ) : isPendingReview ? (
        <div className="p-5 rounded-xl border border-amber-500/30 bg-amber-500/5 space-y-3">
          <div className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 text-amber-400" />
            <p className="font-display font-black uppercase tracking-widest text-sm text-amber-400">Request Under Review</p>
          </div>
          <p className="text-xs text-muted-foreground font-sans">Your featured listing request has been submitted and is being reviewed by TAG admin. We'll notify you within 24h.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3">
            {[
              { icon: <TrendingUp className="w-4 h-4 text-primary" />, title: "Top of Registry",    desc: "Your unit card appears above all standard listings in browse and search results." },
              { icon: <Star className="w-4 h-4 text-yellow-400" />,    title: "Featured Badge",     desc: "Gold 'FEATURED' badge on your unit card — instantly recognisable." },
              { icon: <Users className="w-4 h-4 text-green-400" />,   title: "Recruitment Boost",  desc: "Featured units see 3-5x more application traffic on average." },
            ].map(f => (
              <div key={f.title} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-secondary/20">
                <div className="mt-0.5">{f.icon}</div>
                <div><p className="text-xs font-display font-bold uppercase tracking-widest text-foreground">{f.title}</p><p className="text-xs text-muted-foreground font-sans mt-0.5">{f.desc}</p></div>
              </div>
            ))}
          </div>

          {!isPro ? (
            <div className="p-4 rounded-lg border border-yellow-500/20 bg-yellow-500/5 text-center">
              <Crown className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <p className="text-xs font-display font-bold uppercase tracking-widest text-yellow-400">Commander Pro Required</p>
              <p className="text-xs text-muted-foreground font-sans mt-1">Upgrade to Commander Pro to request a Featured listing.</p>
            </div>
          ) : (
            <button onClick={requestFeature} disabled={requesting}
              className="w-full flex items-center justify-center gap-2 py-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 font-display font-bold uppercase tracking-widest text-xs rounded hover:bg-yellow-500/20 transition-all disabled:opacity-50">
              {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
              {requesting ? "Sending Request..." : "Request Featured Listing"}
            </button>
          )}
          <p className="text-[10px] text-muted-foreground/50 font-sans text-center">Featured status is reviewed and approved by TAG admin. Pro subscription required.</p>
        </div>
      )}
    </div>
  );
}

// ─── Recruitment Analytics Tab ────────────────────────────────────────────────
// Reads MilsimApplication entity — pure client-side, zero backend credits.

function OperatorSearchTab({ group, globalIsPro }: any) {
  const APPROVED_GAMES = ['Arma 3','Arma Reforger','Squad','Ready Or Not','Escape From Tarkov','Ground Branch','DayZ','Grey Zone Warfare','Body Cam','Operator','Exfil','Hell Let Loose'];
  const SME_TAGS_OPTIONS = ['Squad Leader','Platoon Commander','Company Commander','Sniper','Breacher','Medic','Combat Controller','Forward Observer','Drone Operator','Machine Gunner','Logistics','Intelligence Analyst','Signals','Artillery','Sapper/Engineer','Pilot','Vehicle Commander','Anti-Tank','JTAC','Instructor','CBRN','EOD'];

  const [operators, setOperators] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [filterGame, setFilterGame] = React.useState("");
  const [filterSme, setFilterSme] = React.useState("");
  const [filterAvail, setFilterAvail] = React.useState("");
  const [filterSearch, setFilterSearch] = React.useState("");
  const [sentContacts, setSentContacts] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    setLoading(true);
    apiFetch<any[]>("/users?path=operator-search")
      .then(res => setOperators(Array.isArray(res) ? res : []))
      .catch(() => setOperators([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = React.useMemo(() => {
    return operators.filter(op => {
      if (filterGame && !(op.preferred_games ?? []).includes(filterGame)) return false;
      if (filterSme && !(op.sme_tags ?? []).includes(filterSme)) return false;
      if (filterAvail && op.availability !== filterAvail) return false;
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        const matchName = (op.username ?? "").toLowerCase().includes(q);
        const matchSme = (op.sme_tags ?? []).some((t: string) => t.toLowerCase().includes(q));
        if (!matchName && !matchSme) return false;
      }
      return true;
    });
  }, [operators, filterGame, filterSme, filterAvail, filterSearch]);

  const sendContact = async (op: any) => {
    try {
      await apiFetch("/messages?path=send", {
        method: "POST",
        body: JSON.stringify({
          recipient_id: op.id,
          recipient_username: op.username,
          subject: `Recruitment Enquiry from ${group.name}`,
          body: `Hello ${op.username},\n\nYou have been identified as a potential match for ${group.name}. We are looking for operators with your expertise.\n\nIf you are interested in learning more about us, please check our unit page or reply to this message.\n\nBest regards,\n${group.name} Command`,
        }),
      });
      setSentContacts(prev => new Set([...prev, op.id]));
    } catch {}
  };

  if (!globalIsPro) {
    return (
      <div className="text-center py-16 space-y-4">
        <Crown className="w-12 h-12 text-yellow-400 mx-auto" />
        <p className="font-display font-bold uppercase tracking-widest text-lg">Commander Pro Required</p>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">Operator Search lets you browse and contact operators who are actively looking for a unit. Upgrade to Commander Pro to unlock this feature.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-display font-bold uppercase tracking-widest text-sm">Operator Search</h3>
        <p className="text-xs text-muted-foreground mt-1">Browse operators who are actively seeking a unit. Filter by game, SME tag, or availability and send a direct recruitment message.</p>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <input
          value={filterSearch}
          onChange={e => setFilterSearch(e.target.value)}
          className="mf-input col-span-2 md:col-span-1"
          placeholder="Search callsign or SME..."
        />
        <select value={filterGame} onChange={e => setFilterGame(e.target.value)} className="mf-input">
          <option value="">All Games</option>
          {APPROVED_GAMES.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={filterSme} onChange={e => setFilterSme(e.target.value)} className="mf-input">
          <option value="">All SME Tags</option>
          {SME_TAGS_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterAvail} onChange={e => setFilterAvail(e.target.value)} className="mf-input">
          <option value="">Any Availability</option>
          <option value="Weekends">Weekends</option>
          <option value="Evenings">Evenings</option>
          <option value="Flexible">Flexible</option>
          <option value="Full-time">Full-time</option>
        </select>
      </div>

      {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-display font-bold uppercase tracking-widest text-sm">No Operators Found</p>
          <p className="text-xs mt-1">Try adjusting your filters or check back later as more operators register.</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(op => (
            <div key={op.id} className="bg-card border border-border rounded-lg p-4 space-y-3 hover:border-primary/40 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  {op.avatar_url ? (
                    <img src={op.avatar_url} className="w-10 h-10 rounded-full object-cover border border-border" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                      <span className="text-sm font-display font-bold text-primary">{(op.username ?? "?")[0]?.toUpperCase() ?? "?"}</span>
                    </div>
                  )}
                  <div>
                    <p className="font-display font-bold uppercase tracking-wider text-sm">{op.username}</p>
                    <p className="text-xs text-muted-foreground">{op.nationality ?? "Unknown nationality"}</p>
                  </div>
                </div>
                <button
                  onClick={() => sendContact(op)}
                  disabled={sentContacts.has(op.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-display font-bold uppercase tracking-wider transition-all ${
                    sentContacts.has(op.id)
                      ? "bg-green-600/20 border border-green-500/40 text-green-400"
                      : "bg-primary hover:bg-primary/90 text-primary-foreground"
                  }`}
                >
                  {sentContacts.has(op.id) ? <><CheckCircle2 className="w-3 h-3" /> Contacted</> : <><Send className="w-3 h-3" /> Contact</>}
                </button>
              </div>

              <div className="space-y-1.5">
                {op.preferred_games?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {op.preferred_games.map((g: string) => (
                      <span key={g} className="px-1.5 py-0.5 rounded text-[10px] font-display font-bold uppercase tracking-wider bg-blue-500/10 border border-blue-500/30 text-blue-400">{g}</span>
                    ))}
                  </div>
                )}
                {op.sme_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {op.sme_tags.map((t: string) => (
                      <span key={t} className="px-1.5 py-0.5 rounded text-[10px] font-display font-bold uppercase tracking-wider bg-primary/10 border border-primary/30 text-primary">{t}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                  {op.years_experience != null && <span><span className="text-foreground font-bold">{op.years_experience}y</span> experience</span>}
                  {op.availability && <span>🕐 {op.availability}</span>}
                  {op.discord_tag && <span>💬 {op.discord_tag}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center">
        Showing <span className="text-foreground font-bold">{filtered.length}</span> of <span className="text-foreground font-bold">{operators.length}</span> available operators
      </p>
    </div>
  );
}

function RecruitmentAnalyticsTab({ group, globalIsPro }: any) {
  const [apps, setApps] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await apiFetch<any[]>(`/milsimApplications?path=${group.id}/applications`).catch(() => []);
      setApps(Array.isArray(d) ? d : []);
      setLoaded(true);
    } finally { setLoading(false); }
  };

  React.useEffect(() => { if (globalIsPro) load(); }, [group.id]);

  const stats = React.useMemo(() => {
    if (!apps.length) return null;
    const total = apps.length;
    const approved = apps.filter(a => a.status === "approved").length;
    const rejected = apps.filter(a => a.status === "rejected").length;
    const pending  = apps.filter(a => a.status === "pending" || a.status === "reviewing").length;
    const interview = apps.filter(a => a.status === "interview").length;
    const conversionRate = total > 0 ? Math.round((approved / total) * 100) : 0;

    // Avg time to decision (approved or rejected)
    const decided = apps.filter(a => (a.status === "approved" || a.status === "rejected") && a.created_date && a.updated_date);
    const avgDays = decided.length > 0
      ? Math.round(decided.reduce((sum: number, a: any) => sum + (new Date(a.updated_date).getTime() - new Date(a.created_date).getTime()) / 86400000, 0) / decided.length)
      : null;

    // Monthly trend (last 6 months)
    const now = new Date();
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      return { label: d.toLocaleString("en-GB", { month: "short" }), year: d.getFullYear(), month: d.getMonth() };
    });
    const trend = months.map(m => ({
      label: m.label,
      count: apps.filter(a => {
        const d = new Date(a.created_date ?? 0);
        return d.getFullYear() === m.year && d.getMonth() === m.month;
      }).length
    }));
    const maxTrend = Math.max(...trend.map(t => t.count), 1);

    // Rejection reasons (from review_note — simple top words)
    const rejNotes = apps.filter(a => a.status === "rejected" && a.review_note).map((a: any) => a.review_note.toLowerCase());

    return { total, approved, rejected, pending, interview, conversionRate, avgDays, trend, maxTrend, rejNotes };
  }, [apps]);

  if (!globalIsPro) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <Crown className="w-10 h-10 text-yellow-400/50" />
      <p className="font-display font-black uppercase tracking-widest text-base text-muted-foreground">Commander Pro Required</p>
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest text-base text-foreground flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" /> Recruitment Analytics
          </h3>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Pipeline performance, conversion rates, and trends.</p>
        </div>
        <button onClick={load} disabled={loading} className="flex items-center gap-2 px-3 py-2 bg-secondary border border-border text-muted-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:text-foreground transition-all disabled:opacity-50">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Activity className="w-3.5 h-3.5" />} Refresh
        </button>
      </div>

      {loading && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}

      {!loading && loaded && !stats && (
        <div className="flex flex-col items-center py-14 gap-3 border border-dashed border-border rounded-lg text-center">
          <BarChart3 className="w-8 h-8 text-muted-foreground/30" />
          <p className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground">No applications yet</p>
        </div>
      )}

      {stats && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Total Applicants",  value: stats.total,           color: "text-foreground" },
              { label: "Conversion Rate",   value: `${stats.conversionRate}%`, color: stats.conversionRate >= 50 ? "text-green-400" : stats.conversionRate >= 25 ? "text-amber-400" : "text-red-400" },
              { label: "Avg Decision Time", value: stats.avgDays !== null ? `${stats.avgDays}d` : "—", color: "text-foreground" },
              { label: "In Pipeline",       value: stats.pending + stats.interview, color: "text-primary" },
            ].map(k => (
              <div key={k.label} className="p-4 rounded-lg border border-border bg-secondary/20">
                <p className="text-[9px] font-display font-bold uppercase tracking-widest text-muted-foreground">{k.label}</p>
                <p className={`font-display font-black text-2xl mt-1 ${k.color}`}>{k.value}</p>
              </div>
            ))}
          </div>

          {/* Status breakdown */}
          <div className="space-y-2">
            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Status Breakdown</p>
            {[
              { label: "Approved",   count: stats.approved,  color: "bg-green-500", text: "text-green-400" },
              { label: "Rejected",   count: stats.rejected,  color: "bg-red-500",   text: "text-red-400" },
              { label: "Pending",    count: stats.pending,   color: "bg-amber-500", text: "text-amber-400" },
              { label: "Interview",  count: stats.interview, color: "bg-blue-500",  text: "text-blue-400" },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-3">
                <span className={`w-20 text-[10px] font-display font-bold uppercase tracking-widest ${s.text}`}>{s.label}</span>
                <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                  <div className={`h-full rounded-full ${s.color}`} style={{ width: `${stats.total > 0 ? (s.count / stats.total) * 100 : 0}%` }} />
                </div>
                <span className="text-xs font-mono text-muted-foreground w-6 text-right">{s.count}</span>
              </div>
            ))}
          </div>

          {/* 6-month trend bars */}
          <div className="space-y-2">
            <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Applications (6-Month Trend)</p>
            <div className="flex items-end gap-1.5 h-20">
              {stats.trend.map((t: any) => (
                <div key={t.label} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full bg-primary/20 rounded-t relative" style={{ height: `${(t.count / stats.maxTrend) * 100}%`, minHeight: t.count > 0 ? 4 : 0 }}>
                    {t.count > 0 && <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] font-mono text-primary">{t.count}</span>}
                  </div>
                  <span className="text-[9px] text-muted-foreground font-display font-bold uppercase">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Custom Notification Rules Tab ────────────────────────────────────────────
// Stored in SiteSetting entity keyed per group. Zero new entities needed.
function CustomNotificationRulesTab({ group, globalIsPro, showMsg }: any) {
  type NRule = { id: string; trigger: string; threshold: number; label: string; active: boolean };
  const TRIGGER_OPTS = [
    { id: "ops_missed_streak",  label: "Member misses N ops in a row",       unit: "ops"    },
    { id: "roster_below",       label: "Active roster drops below N members", unit: "members"},
    { id: "aar_rate_below",     label: "AAR rate drops below N%",             unit: "%"      },
    { id: "loa_load_above",     label: "LOA load exceeds N% of roster",       unit: "%"      },
    { id: "pending_apps_above", label: "Pending applications exceed N",       unit: "apps"   },
  ];

  const [rules, setRules] = React.useState<NRule[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const settingKey = `notif_rules_${group.id}`;

  const load = async () => {
    setLoading(true);
    try {
      const d = await apiFetch<any[]>(`/siteSettings?key=${settingKey}`).catch(() => []);
      const match = Array.isArray(d) ? d.find((s: any) => s.key === settingKey) : null;
      if (match?.value) {
        try { setRules(JSON.parse(match.value)); } catch {}
      } else {
        // Default rules
        setRules([
          { id: "ops_missed_streak",  trigger: "ops_missed_streak",  threshold: 3, label: "Member misses 3 ops in a row",       active: true  },
          { id: "roster_below",       trigger: "roster_below",       threshold: 5, label: "Active roster drops below 5 members", active: true  },
          { id: "aar_rate_below",     trigger: "aar_rate_below",     threshold: 50, label: "AAR rate drops below 50%",            active: false },
        ]);
      }
    } finally { setLoading(false); }
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      const existing = await apiFetch<any[]>(`/siteSettings?key=${settingKey}`).catch(() => []);
      const match = Array.isArray(existing) ? existing.find((s: any) => s.key === settingKey) : null;
      if (match) {
        await apiFetch(`/siteSettings?id=${match.id}`, { method: "PUT", body: JSON.stringify({ ...match, value: JSON.stringify(rules) }) });
      } else {
        await apiFetch("/siteSettings", { method: "POST", body: JSON.stringify({ key: settingKey, value: JSON.stringify(rules), description: `Notification rules for group ${group.id}` }) });
      }
      showMsg(true, "Notification rules saved.");
    } finally { setSaving(false); }
  };

  const addRule = () => {
    const opt = TRIGGER_OPTS[0];
    setRules(prev => [...prev, { id: crypto.randomUUID(), trigger: opt.id, threshold: 3, label: opt.label, active: true }]);
  };

  const updateRule = (id: string, field: keyof NRule, value: any) => {
    setRules(prev => prev.map(r => {
      if (r.id !== id) return r;
      if (field === "trigger") {
        const opt = TRIGGER_OPTS.find(o => o.id === value);
        return { ...r, trigger: value, label: opt?.label ?? value };
      }
      return { ...r, [field]: value };
    }));
  };

  React.useEffect(() => { if (globalIsPro) load(); }, [group.id]);

  if (!globalIsPro) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <Crown className="w-10 h-10 text-yellow-400/50" />
      <p className="font-display font-black uppercase tracking-widest text-base text-muted-foreground">Commander Pro Required</p>
    </div>
  );

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest text-base text-foreground flex items-center gap-2">
            <Bell className="w-4 h-4 text-primary" /> Custom Notification Rules
          </h3>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Set tripwires. When a condition is met, your HQ officers get notified automatically.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={addRule} className="flex items-center gap-1.5 px-3 py-2 bg-secondary border border-border text-muted-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:text-foreground transition-all">
            <Plus className="w-3.5 h-3.5" /> Add Rule
          </button>
          <button onClick={saveRules} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:bg-primary/90 transition-all disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </button>
        </div>
      </div>

      {loading && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}

      <div className="space-y-3">
        {rules.map(rule => {
          const opt = TRIGGER_OPTS.find(o => o.id === rule.trigger);
          return (
            <div key={rule.id} className={`p-4 rounded-lg border transition-colors ${rule.active ? "border-border bg-secondary/20" : "border-border/40 bg-secondary/5 opacity-60"}`}>
              <div className="flex items-start gap-3">
                <button onClick={() => updateRule(rule.id, "active", !rule.active)} className="mt-0.5 shrink-0">
                  <div className={`w-8 h-4 rounded-full transition-colors relative ${rule.active ? "bg-primary" : "bg-secondary border border-border"}`}>
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${rule.active ? "left-4" : "left-0.5"}`} />
                  </div>
                </button>
                <div className="flex-1 space-y-2">
                  <select value={rule.trigger} onChange={e => updateRule(rule.id, "trigger", e.target.value)}
                    className="w-full px-3 py-1.5 text-xs bg-secondary border border-border rounded text-foreground focus:outline-none focus:border-primary font-sans">
                    {TRIGGER_OPTS.map(o => <option key={o.id} value={o.id}>{o.label.replace("N", rule.threshold.toString())}</option>)}
                  </select>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground font-sans">Threshold:</span>
                    <input type="number" min={1} value={rule.threshold} onChange={e => updateRule(rule.id, "threshold", +e.target.value)}
                      className="w-20 px-2 py-1 text-xs bg-secondary border border-border rounded text-foreground focus:outline-none focus:border-primary font-mono" />
                    <span className="text-[10px] text-muted-foreground font-sans">{opt?.unit ?? ""}</span>
                  </div>
                </div>
                <button onClick={() => setRules(prev => prev.filter(r => r.id !== rule.id))} className="text-muted-foreground hover:text-red-400 transition-colors p-1 shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
        {rules.length === 0 && (
          <div className="flex flex-col items-center py-14 gap-3 border border-dashed border-border rounded-lg text-center">
            <Bell className="w-8 h-8 text-muted-foreground/30" />
            <p className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground">No rules configured</p>
            <p className="text-xs text-muted-foreground/60 font-sans">Add rules to get notified when key thresholds are hit.</p>
          </div>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground/50 font-sans">Rules are evaluated daily by the platform automation. Notifications go to all HQ officers.</p>
    </div>
  );
}

// ─── Op Planning Room ────────────────────────────────────────────────────────

// ── Field schema per planning item ──────────────────────────────────────────
interface PlanField { key: string; label: string; type: "text"|"textarea"|"select"|"radio"; options?: string[]; placeholder?: string; }
interface PlanItem  { id: string; title: string; fields: PlanField[]; }
interface PlanPhase { id: string; label: string; items: PlanItem[]; }

const PLAN_PHASES: PlanPhase[] = [
  {
    id: "planning", label: "PLANNING",
    items: [
      { id: "mission_type", title: "Mission Type", fields: [
        { key: "type", label: "Mission Type", type: "select", options: ["Assault","Raid","Ambush","Recce","Cordon & Search","Deliberate Attack","Hasty Attack","Defensive","Convoy","CASEVAC Extract","Training Ex"] },
        { key: "intent", label: "Commander's Intent", type: "textarea", placeholder: "End state and purpose of the operation..." },
        { key: "endex", label: "ENDEX Conditions", type: "textarea", placeholder: "Conditions under which the op ends (objective secured, time limit, casualty threshold...)" },
      ]},
      { id: "elements", title: "Element Leaders & Slots", fields: [
        { key: "assault_lead", label: "Assault Element Lead", type: "text", placeholder: "Callsign" },
        { key: "support_lead", label: "Support Element Lead", type: "text", placeholder: "Callsign or N/A" },
        { key: "cmd_lead", label: "Command Element / CO", type: "text", placeholder: "Callsign" },
        { key: "max_slots", label: "Max Slots", type: "text", placeholder: "e.g. 12" },
        { key: "slot_notes", label: "Slot Restrictions / Notes", type: "textarea", placeholder: "e.g. 2x medic mandatory, no more than 1 AR per section..." },
      ]},
      { id: "warno", title: "WARNO Issued", fields: [
        { key: "warno_issued", label: "WARNO Issued?", type: "radio", options: ["Yes","No","Pending"] },
        { key: "warno_brief", label: "Key Points Covered", type: "textarea", placeholder: "Area of Ops, enemy situation, mission outline, timings..." },
      ]},
    ],
  },
  {
    id: "admin_log", label: "ADMIN / LOG",
    items: [
      { id: "server_modset", title: "Server & Modset", fields: [
        { key: "server", label: "Server Address / Name", type: "text", placeholder: "e.g. TAG-OPS-01 | 127.0.0.1:2302" },
        { key: "modset", label: "Modset / Preset Name", type: "text", placeholder: "e.g. TAG Core Modset v3" },
        { key: "modset_url", label: "Modset URL / Workshop Link", type: "text", placeholder: "https://..." },
        { key: "server_pw", label: "Server Password (if any)", type: "text", placeholder: "Leave blank if open" },
      ]},
      { id: "kit", title: "Kit & Loadout Restrictions", fields: [
        { key: "uniform", label: "Uniform / Camo Pattern", type: "text", placeholder: "e.g. MTP, Multicam, OD Green" },
        { key: "kit_restrictions", label: "Restricted Equipment", type: "textarea", placeholder: "e.g. No thermals, AT limited to 2 per section, NVG mandatory..." },
        { key: "vehicle_alloc", label: "Vehicle Allocation", type: "textarea", placeholder: "e.g. 2x MRAP (assault), 1x logistics truck, foot patrol only for recce element..." },
      ]},
      { id: "timings", title: "Timings & Time Hack", fields: [
        { key: "muster_time", label: "Muster / Join Time", type: "text", placeholder: "e.g. 19:45 GMT" },
        { key: "briefing_time", label: "Briefing Time", type: "text", placeholder: "e.g. 20:00 GMT" },
        { key: "h_hour", label: "H-Hour (Mission Start)", type: "text", placeholder: "e.g. 20:30 GMT" },
        { key: "time_hack_method", label: "Time Hack Method", type: "select", options: ["In-game clock","Real time","Server time","CO callout"] },
      ]},
    ],
  },
  {
    id: "rehearsal", label: "REHEARSAL",
    items: [
      { id: "comms_test", title: "Comms Test", fields: [
        { key: "comms_test_done", label: "Comms Test Status", type: "radio", options: ["Complete","In Progress","Not Yet"] },
        { key: "primary_net", label: "Primary Net / Channel", type: "text", placeholder: "e.g. TS Channel: TAG-OPS / In-game CH1" },
        { key: "backup_net", label: "Backup Net / Channel", type: "text", placeholder: "e.g. Discord VC: OPS-STAGING" },
        { key: "radio_silence", label: "Radio Silence State", type: "select", options: ["No restriction","Minimize chatter","Radio silence until H-Hour","As ordered"] },
      ]},
      { id: "task_org", title: "Task Organisation Walkthrough", fields: [
        { key: "task_org", label: "Task Organisation", type: "textarea", placeholder: "e.g.\nCMD: Sunray, Sunray Minor\nASSAULT: Alpha (6 pax) — OBJ BLUE\nSUPPORT: Bravo (4 pax) — Overwatch Grid 123456\nLOG: 1x MRAP staged at ERV..." },
        { key: "rehearsal_method", label: "Rehearsal Method", type: "select", options: ["Map brief only","Terrain model","In-game walkthrough","Battlespace simulation","No formal rehearsal"] },
      ]},
      { id: "actions_on", title: "Actions On", fields: [
        { key: "ao_contact", label: "Action On — Unexpected Contact", type: "textarea", placeholder: "e.g. Break contact bound-by-bound, rally at RV1, CO makes call to abort or continue..." },
        { key: "ao_kia", label: "Action On — KIA / Incapacitation of Leader", title: "", type: "textarea", placeholder: "e.g. Next in CoC assumes command, CASEVAC initiated, continue mission unless CO orders otherwise..." },
        { key: "ao_abort", label: "Action On — Mission Abort", type: "textarea", placeholder: "e.g. Codeword TABASCO, exfil via Route BRAVO, RV at ERV 2..." },
        { key: "ao_compromise", label: "Action On — Compromise / Spotted", type: "textarea", placeholder: "e.g. Immediate assault if within 50m, otherwise break contact and reassess..." },
      ]},
    ],
  },
  {
    id: "infil", label: "INFIL",
    items: [
      { id: "infil_method", title: "Infiltration Method", fields: [
        { key: "method", label: "Method of Infil", type: "select", options: ["Foot Patrol","Vehicle (road)","Vehicle (off-road / cross-country)","Helicopter Insert","Boat / Waterborne","HALO / Paradrop","Mixed (specify below)"] },
        { key: "noise_state", label: "Noise & Light Discipline", type: "select", options: ["Covert — full noise/light discipline","Semi-covert — minimize signatures","Overt — no restrictions","Rapid infil — speed over stealth"] },
        { key: "vehicle_detail", label: "Vehicle Detail (if applicable)", type: "textarea", placeholder: "Vehicle types, number, loading plan, driver callsigns, staging area..." },
        { key: "air_detail", label: "Air Asset Detail (if applicable)", type: "textarea", placeholder: "Helo type, LZ grid, approach direction, door gunner restrictions, abort criteria..." },
      ]},
      { id: "infil_route", title: "Route & RV Points", fields: [
        { key: "start_point", label: "Start Point / Staging Area", type: "text", placeholder: "Grid ref or landmark" },
        { key: "route", label: "Infil Route", type: "textarea", placeholder: "Route description, waypoints, grid refs, key terrain features to pass..." },
        { key: "rv1", label: "RV1 (Primary Rally Point)", type: "text", placeholder: "Grid ref or in-game landmark" },
        { key: "rv2", label: "RV2 / ERV (Emergency RV)", type: "text", placeholder: "Grid ref or in-game landmark" },
        { key: "roe_infil", label: "ROE During Infil", type: "select", options: ["No engagement — evade all contact","Engage only if fired upon","Engage at will if compromised","As ordered by CO"] },
        { key: "formation", label: "Formation / Movement Technique", type: "select", options: ["File","Extended line","Wedge","Staggered column","Bounding overwatch","Travelling overwatch","Vehicle convoy"] },
      ]},
    ],
  },
  {
    id: "action_obj", label: "ACTION ON OBJ",
    items: [
      { id: "assault_plan", title: "Assault Plan", fields: [
        { key: "assault_element", label: "Assault Element", type: "text", placeholder: "Callsigns assigned to assault" },
        { key: "support_element", label: "Support / Overwatch Element", type: "text", placeholder: "Callsigns assigned to fire support / overwatch" },
        { key: "breach_method", label: "Breach / Entry Method", type: "select", options: ["Soft breach (stealth entry)","Hard breach (explosive/force)","Vehicle breach","No breach — open approach","Multiple entry points"] },
        { key: "assault_notes", label: "Assault Notes / Scheme of Manoeuvre", type: "textarea", placeholder: "Axis of advance, phase lines, consolidation on OBJ, exploitation tasks..." },
      ]},
      { id: "casevac", title: "CASEVAC Plan", fields: [
        { key: "casevac_priority", label: "CASEVAC Priority", type: "select", options: ["Urgent (immediate threat to life)","Priority (serious but stable)","Routine (minor — can wait)","As required"] },
        { key: "ccp", label: "Casualty Collection Point (CCP)", type: "text", placeholder: "Grid ref or in-game landmark" },
        { key: "medevac_callsign", label: "MEDEVAC Callsign / Asset", type: "text", placeholder: "e.g. DUSTOFF 1, or 'no dedicated MEDEVAC — buddy carry'" },
        { key: "nine_line", label: "9-Line MEDEVAC Template", type: "textarea", placeholder: "Line 1: Grid of pick-up site\nLine 2: Radio freq & callsign\nLine 3: No. of patients by precedence\nLine 4: Special equipment\nLine 5: No. of patients (ambulatory / litter)\nLine 6: Security at site\nLine 7: Method of marking\nLine 8: Patient nationality / status\nLine 9: NBC contamination" },
        { key: "casevac_abort", label: "CASEVAC Abort Criteria", type: "textarea", placeholder: "e.g. If LZ hot and asset cannot land — buddy carry to ERV, request exfil..." },
      ]},
      { id: "lace_obj", title: "LACE on Clearance", fields: [
        { key: "lace_timing", label: "When to Submit LACE", type: "select", options: ["On clearance of OBJ","On consolidation","On exfil start","As ordered by CO"] },
        { key: "lace_to", label: "Report LACE to", type: "text", placeholder: "Callsign / net" },
      ]},
    ],
  },
  {
    id: "exfil", label: "EXFIL",
    items: [
      { id: "exfil_plan", title: "Exfiltration Plan", fields: [
        { key: "exfil_method", label: "Method of Exfil", type: "select", options: ["Foot Patrol","Vehicle (road)","Vehicle (off-road)","Helicopter Extract","Boat / Waterborne","Mixed (specify below)"] },
        { key: "exfil_route", label: "Exfil Route", type: "textarea", placeholder: "Route description, waypoints, ERV locations..." },
        { key: "noise_state_exfil", label: "Noise State During Exfil", type: "select", options: ["Covert","Semi-covert","Rapid — speed priority","As ordered"] },
        { key: "exfil_trigger", label: "Exfil Trigger / Order Word", type: "text", placeholder: "e.g. Codeword EAGLE, or CO order" },
        { key: "split_plan", label: "Action If Split / Element Separated", type: "textarea", placeholder: "e.g. Rally at ERV 2, attempt comms on backup net, wait 10 mins then exfil independently..." },
      ]},
    ],
  },
  {
    id: "consolidation", label: "CONSOLIDATION",
    items: [
      { id: "admin_consolidation", title: "Post-Op Admin", fields: [
        { key: "headcount", label: "Headcount Confirmed?", type: "radio", options: ["Yes","No","Partial"] },
        { key: "lace_final", label: "Final LACE Submitted?", type: "radio", options: ["Yes","No","Pending"] },
        { key: "debrief_method", label: "Debrief Method", type: "select", options: ["Hot debrief (immediate, in-server)","Written AAR only","Voice debrief (Discord/TS)","Full structured debrief","No debrief planned"] },
        { key: "aar_submitted", label: "AAR Submitted?", type: "radio", options: ["Yes","No","Pending"] },
        { key: "commendations", label: "Commendations / Notable Performances", type: "textarea", placeholder: "Callsigns and reason for commendation — submit formal awards via HQ..." },
        { key: "lessons", label: "Key Lessons Learned", type: "textarea", placeholder: "Tactical, logistical, comms, or personnel issues to address for next op..." },
      ]},
    ],
  },
  {
    id: "timeline", label: "TIMELINE",
    items: [
      { id: "__timeline__", title: "Mission Timeline", fields: [
        { key: "__timeline_data__", label: "Timeline", type: "text", placeholder: "" },
      ]},
    ],
  },
];

// ── Chat message type ─────────────────────────────────────────────────────────
interface PlanChatMsg { id: string; callsign: string; text: string; ts: number; }

function OpPlanningRoomTab({ group, showMsg, globalIsPro }: { group: any; showMsg: (m: string, t?: "success"|"error") => void; globalIsPro?: boolean }) {
  const { user } = useAuth();

  // Linked op
  const [ops, setOps] = React.useState<any[]>([]);
  const [selectedOpId, setSelectedOpId] = React.useState<string>("");

  // Phase / item state
  const [activePhase, setActivePhase] = React.useState(0);
  const [expandedItem, setExpandedItem] = React.useState<string | null>(null);

  // Field values keyed by "phaseId::itemId::fieldKey"
  const [fields, setFields] = React.useState<Record<string, string>>({});
  const [saved, setSaved] = React.useState(false);

  const [published, setPublished] = React.useState(false);

  // Mission timeline entries (stored separately, not in fields{})
  const [timelineEvents, setTimelineEvents] = React.useState<{id:string; label:string; offset_min:number; element:string; type:string}[]>([
    { id:"t1", label:"Muster / Join Server", offset_min:-60, element:"All", type:"admin" },
    { id:"t2", label:"Orders Group", offset_min:-30, element:"All", type:"admin" },
    { id:"t3", label:"Comms Test & Time Hack", offset_min:-10, element:"All", type:"admin" },
    { id:"t4", label:"H-Hour — Mission Start", offset_min:0, element:"All", type:"h_hour" },
  ]);

  // Chat
  const [chat, setChat] = React.useState<PlanChatMsg[]>([]);
  const [chatInput, setChatInput] = React.useState("");
  const chatRef = React.useRef<HTMLDivElement>(null);

  // Completion tracking
  const totalFields = PLAN_PHASES.flatMap(p => p.items.flatMap(i => i.fields)).length;
  const filledFields = Object.values(fields).filter(v => v && v.trim()).length;
  const pct = Math.round((filledFields / totalFields) * 100);

  // Roster for callsign picker
  const [roster, setRoster] = React.useState<any[]>([]);
  React.useEffect(() => {
    if (!group?.id) return;
    apiFetch<any[]>(`/milsimRosters?group_id=${group.id}`)
      .then((r: any) => setRoster(Array.isArray(r) ? r.filter((m:any) => m.status === "Active" || m.status === "active") : []))
      .catch(() => {});
  }, [group?.id]);

  // Load ops for selector
  React.useEffect(() => {
    if (!group?.id) return;
    apiFetch<any[]>(`/milsimOps?path=${group.id}/list`).then((res) => {
      const list = Array.isArray(res) ? res : [];
      setOps(list.sort((a: any, b: any) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime()));
    }).catch(() => {});
  }, [group?.id]);

  // Load saved plan when op selected
  React.useEffect(() => {
    if (!selectedOpId) { setFields({}); setChat([]); return; }
    apiFetch<any>(`/milsimOps?path=${selectedOpId}/detail`).then((op: any) => {
      try {
        const plan = op?.description ? JSON.parse(op.description) : {};
        if (plan.__planFields) setFields(plan.__planFields);
        if (plan.__planChat) setChat(plan.__planChat);
        if (plan.__timeline) setTimelineEvents(plan.__timeline);
      } catch { setFields({}); setChat([]); }
    }).catch(() => {});
  }, [selectedOpId]);

  React.useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [chat]);

  const setField = (phaseId: string, itemId: string, key: string, value: string) => {
    setFields(p => ({ ...p, [`${phaseId}::${itemId}::${key}`]: value }));
  };

  const handleSave = async () => {
    if (!selectedOpId) { showMsg("Select an op first to save the plan.", "error"); return; }
    try {
      const op = await apiFetch<any>(`/milsimOps?path=${selectedOpId}/detail`);
      let base: any = {};
      try { base = op?.description ? JSON.parse(op.description) : {}; } catch {}
      base.__planFields = fields;
      base.__planChat = chat;
      base.__timeline = timelineEvents;
      await apiFetch(`/milsimOps?path=${selectedOpId}/update`, { method: "PATCH", body: JSON.stringify({ description: JSON.stringify(base) }) });
      setSaved(true);
      showMsg("Planning Room saved.", "success");
      setTimeout(() => setSaved(false), 3000);
    } catch { showMsg("Save failed.", "error"); }
  };

  const handlePublish = async () => {
    if (!selectedOpId) { showMsg("Select an op first.", "error"); return; }
    await handleSave();
    try {
      const op = ops.find((o: any) => o.id === selectedOpId);
      const opName = op?.name ?? "Op";
      // Notify all active roster members
      const rosterList: any[] = await apiFetch<any>(`/milsimGroups?path=${group.id}/roster`).then((r: any) => Array.isArray(r) ? r : r?.members ?? []).catch(() => []);
      const active = rosterList.filter((m: any) => m.status === "Active" || m.status === "active");
      await Promise.all(active.map((m: any) =>
        apiFetch("/notifications?path=create", {
          method: "POST",
          body: JSON.stringify({
            user_id: m.user_id,
            type: "planning_room",
            title: "Op Planning Room Published",
            body: `Planning document for ${opName} has been published by your HQ.`,
            link: `/portal/milsim/${group.id}/ops`,
          }),
        }).catch(() => {})
      ));
      setPublished(true);
      showMsg(`Planning room published — ${active.length} members notified.`, "success");
      setTimeout(() => setPublished(false), 3000);
    } catch { showMsg("Publish failed.", "error"); }
  };

  const sendChat = () => {
    const text = chatInput.trim();
    if (!text) return;
    const roster = group?.__roster?.find?.((r: any) => r.user_id === user?.id);
    const cs = roster?.callsign ?? user?.username ?? "Unknown";
    const msg: PlanChatMsg = { id: `${Date.now()}`, callsign: cs, text, ts: Date.now() };
    setChat(p => [...p, msg]);
    setChatInput("");
  };

  if (!globalIsPro) return (
    <div className="flex flex-col items-center justify-center py-20 text-center max-w-md mx-auto gap-6">
      <div className="w-16 h-16 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center justify-center">
        <Crown className="w-8 h-8 text-yellow-400" />
      </div>
      <div>
        <h3 className="font-display font-black text-2xl uppercase tracking-wider text-foreground mb-2">Commander Pro Required</h3>
        <p className="text-muted-foreground font-sans leading-relaxed">Op Planning Room is a Commander Pro feature. Upgrade to unlock phase-based operation planning.</p>
      </div>
      <a href="/commander-pro" className="inline-flex items-center gap-3 bg-yellow-500 hover:bg-yellow-400 text-black font-display font-black uppercase tracking-widest text-sm px-8 py-3 rounded transition-all shadow-[0_0_20px_hsla(48,96%,53%,0.3)]">
        <Crown className="w-5 h-5" /> Upgrade to Pro
      </a>
    </div>
  );

  // Timeline helpers
  const addTimelineEvent = () => setTimelineEvents(p => [...p, { id: `t${Date.now()}`, label: "", offset_min: 0, element: "All", type: "event" }]);
  const updateTL = (id: string, f: string, v: any) => setTimelineEvents(p => p.map(e => e.id === id ? { ...e, [f]: v } : e));
  const removeTL = (id: string) => setTimelineEvents(p => p.filter(e => e.id !== id));
  const sortedTL = [...timelineEvents].sort((a, b) => a.offset_min - b.offset_min);

  const formatOffset = (min: number) => {
    if (min === 0) return "H-Hour";
    const abs = Math.abs(min);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    const parts = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    return `${min < 0 ? "H-" : "H+"}${parts.join(" ")}`;
  };

  const TL_TYPES = ["admin","infil","action","exfil","h_hour","comms","casevac","other"];
  const TL_TYPE_COLOR: Record<string, string> = {
    admin: "text-muted-foreground border-border", infil: "text-blue-400 border-blue-500/40",
    action: "text-red-400 border-red-500/40", exfil: "text-amber-400 border-amber-500/40",
    h_hour: "text-primary border-primary/50", comms: "text-cyan-400 border-cyan-500/40",
    casevac: "text-green-400 border-green-500/40", other: "text-muted-foreground border-border",
  };

  const phase = PLAN_PHASES[activePhase];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-base uppercase tracking-widest text-foreground flex items-center gap-2">
            <Layers className="w-4 h-4 text-primary" /> Op Planning Room
          </h3>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Full operational planning document — phase by phase</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSave} className="flex items-center gap-2 px-3 py-1.5 bg-secondary border border-border text-foreground rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-secondary/80 transition-all">
            <Save className="w-3.5 h-3.5" /> {saved ? "Saved!" : "Save Plan"}
          </button>
          <button onClick={handlePublish} disabled={!selectedOpId} className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-green-500 transition-all disabled:opacity-40">
            <Send className="w-3.5 h-3.5" /> {published ? "Published!" : "Publish & Notify"}
          </button>
        </div>
      </div>

      {/* Op Selector */}
      <div className="p-4 rounded-lg border border-border bg-secondary/10 space-y-3">
        <div className="space-y-1">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Linked Operation</label>
          <select value={selectedOpId} onChange={e => setSelectedOpId(e.target.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded text-sm font-sans text-foreground focus:outline-none focus:border-primary">
            <option value="">— Select an op to plan —</option>
            {ops.map((op: any) => (
              <option key={op.id} value={op.id}>{op.name} {op.scheduled_at ? `(${new Date(op.scheduled_at).toLocaleDateString("en-GB")})` : ""}</option>
            ))}
          </select>
        </div>
        {/* Progress */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Plan Completion</span>
            <span className={`text-xs font-display font-bold ${pct >= 80 ? "text-green-400" : pct >= 40 ? "text-amber-400" : "text-muted-foreground"}`}>{pct}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
            <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {/* Phase tabs */}
      <div className="flex flex-wrap gap-1">
        {PLAN_PHASES.map((ph, i) => {
          const phFilled = ph.items.flatMap(it => it.fields.map(f => ({ ...f, _itId: it.id }))).filter(f => fields[`${ph.id}::${f._itId ?? ""}::${f.key}`]?.trim()).length;
          const phTotal  = ph.items.flatMap(it => it.fields).length;
          const complete = phFilled > 0 && phFilled === phTotal;
          return (
            <button key={ph.id} onClick={() => { setActivePhase(i); setExpandedItem(null); }}
              className={`px-2.5 py-1 rounded text-[10px] font-display font-bold uppercase tracking-wider transition-all border ${i === activePhase ? "bg-primary text-primary-foreground border-primary" : complete ? "bg-green-500/10 text-green-400 border-green-500/30" : "bg-secondary text-muted-foreground border-border hover:border-primary/50"}`}>
              {i + 1}. {ph.label}
            </button>
          );
        })}
      </div>

      {/* Active Phase Items */}
      <div className="space-y-2">
        {phase.items.map((item) => {
          // ── Custom renderer: Mission Timeline ──────────────────────────────
          if (item.id === "__timeline__") return (
            <div key="__timeline__" className="space-y-4 p-4 rounded-lg border border-border bg-secondary/10">
              <div className="flex items-center justify-between">
                <h4 className="font-display font-bold text-sm uppercase tracking-widest text-foreground flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" /> Mission Timeline
                </h4>
                <button onClick={addTimelineEvent} className="flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-widest text-primary hover:underline">
                  <Plus className="w-3 h-3" /> Add Event
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground font-sans">All times relative to H-Hour (0). Use negative values for pre-mission (e.g. -30 = H-30).</p>

              {/* Timeline visual track */}
              <div className="relative border-l-2 border-primary/30 ml-4 space-y-0.5 py-1">
                {sortedTL.map((ev) => (
                  <div key={ev.id} className="relative ml-4 group">
                    <div className={`absolute -left-[21px] top-2.5 w-3 h-3 rounded-full border-2 bg-background ${ev.type === "h_hour" ? "border-primary w-4 h-4 -left-[23px]" : "border-muted-foreground"}`} />
                    <div className="p-2.5 rounded-lg border border-border bg-background hover:border-primary/30 transition-colors">
                      <div className="flex items-start gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 shrink-0">
                          <input type="number" value={ev.offset_min}
                            onChange={e => updateTL(ev.id, "offset_min", parseInt(e.target.value) || 0)}
                            className="w-20 px-2 py-1 bg-secondary border border-border rounded text-xs font-mono text-foreground focus:outline-none focus:border-primary text-center"
                            placeholder="0" />
                          <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${TL_TYPE_COLOR[ev.type] ?? TL_TYPE_COLOR.other} bg-secondary/50 shrink-0`}>
                            {formatOffset(ev.offset_min)}
                          </span>
                        </div>
                        <input value={ev.label} onChange={e => updateTL(ev.id, "label", e.target.value)}
                          placeholder="Event description"
                          className="flex-1 min-w-[140px] px-2 py-1 bg-secondary border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary" />
                        <input value={ev.element} onChange={e => updateTL(ev.id, "element", e.target.value)}
                          placeholder="Element (All / Alpha / etc)"
                          className="w-32 px-2 py-1 bg-secondary border border-border rounded text-xs font-mono text-foreground focus:outline-none focus:border-primary" />
                        <select value={ev.type} onChange={e => updateTL(ev.id, "type", e.target.value)}
                          className="px-2 py-1 bg-secondary border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary">
                          {TL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <button onClick={() => removeTL(ev.id)} className="text-muted-foreground hover:text-red-400 transition-colors p-1 opacity-0 group-hover:opacity-100">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-[10px] text-muted-foreground/50 font-sans">Timeline is saved with the plan and exported in chronological order.</p>
            </div>
          );

          const isOpen = expandedItem === item.id;
          const filled = item.fields.filter(f => fields[`${phase.id}::${item.id}::${f.key}`]?.trim()).length;
          return (
            <div key={item.id} className={`rounded-lg border transition-all ${isOpen ? "border-primary/60 bg-secondary/20" : "border-border bg-secondary/10 hover:border-border/80"}`}>
              <button onClick={() => setExpandedItem(isOpen ? null : item.id)}
                className="w-full flex items-center justify-between px-4 py-3 text-left">
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 text-[9px] font-display font-bold ${filled === item.fields.length ? "bg-green-500/20 border-green-500/50 text-green-400" : filled > 0 ? "bg-amber-500/20 border-amber-500/50 text-amber-400" : "border-border text-muted-foreground"}`}>
                    {filled === item.fields.length ? <Check className="w-2.5 h-2.5" /> : filled > 0 ? filled : ""}
                  </div>
                  <span className="text-xs font-display font-bold uppercase tracking-widest text-foreground">{item.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground font-mono">{filled}/{item.fields.length}</span>
                  {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                </div>
              </button>
              {isOpen && (
                <div className="px-4 pb-4 space-y-4 border-t border-border pt-4">
                  {item.fields.map(f => {
                    const fkey = `${phase.id}::${item.id}::${f.key}`;
                    const val  = fields[fkey] ?? "";

                    // ── Roster-linked task org element builder ──────────────
                    if (f.key === "task_org") {
                      let elements: {element:string;task:string;callsigns:string[]}[] = [];
                      try { elements = JSON.parse(val || "[]"); } catch {}
                      const saveEls = (els: typeof elements) => setField(phase.id, item.id, f.key, JSON.stringify(els));
                      return (
                        <div key={f.key} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Task Organisation</label>
                            <button type="button" onClick={() => saveEls([...elements, { element: "", task: "", callsigns: [] }])}
                              className="text-[10px] font-display font-bold uppercase tracking-wider text-primary hover:text-primary/80 flex items-center gap-1">+ Add Element</button>
                          </div>
                          {elements.length === 0 && <div className="border border-dashed border-border rounded p-3 text-center text-xs text-muted-foreground font-sans">No elements added yet</div>}
                          <div className="space-y-2">
                            {elements.map((el, ei) => (
                              <div key={ei} className="border border-border rounded p-3 bg-secondary/20 space-y-2">
                                <div className="flex items-center gap-2">
                                  <input value={el.element} onChange={e => { const n=[...elements]; n[ei]={...n[ei],element:e.target.value}; saveEls(n); }}
                                    className="w-full px-2 py-1.5 bg-background border border-border rounded text-xs font-bold text-foreground focus:outline-none focus:border-primary"
                                    placeholder="Element (e.g. ASSAULT, CMD, SUPPORT, LOG)" />
                                  <button type="button" onClick={() => saveEls(elements.filter((_,j)=>j!==ei))} className="text-destructive hover:text-destructive/80 shrink-0"><X className="w-3.5 h-3.5" /></button>
                                </div>
                                <input value={el.task} onChange={e => { const n=[...elements]; n[ei]={...n[ei],task:e.target.value}; saveEls(n); }}
                                  className="w-full px-2 py-1.5 bg-background border border-border rounded text-xs text-foreground focus:outline-none focus:border-primary"
                                  placeholder="Task (e.g. Assault OBJ BLUE, Overwatch Grid 123456)" />
                                <div>
                                  <p className="text-[10px] text-muted-foreground font-sans mb-1.5">Assigned callsigns</p>
                                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                                    {el.callsigns.map((cs, ci) => (
                                      <span key={ci} className="inline-flex items-center gap-1 bg-primary/10 border border-primary/30 text-primary text-[10px] font-mono px-2 py-0.5 rounded">
                                        {cs}
                                        <button type="button" onClick={() => { const n=[...elements]; n[ei]={...n[ei],callsigns:el.callsigns.filter((_,k)=>k!==ci)}; saveEls(n); }}><X className="w-2.5 h-2.5" /></button>
                                      </span>
                                    ))}
                                  </div>
                                  <select defaultValue="" onChange={e => { if(!e.target.value || e.target.value==="__manual__") return; const cs=e.target.value; const n=[...elements]; if(!n[ei].callsigns.includes(cs)) n[ei]={...n[ei],callsigns:[...el.callsigns,cs]}; saveEls(n); (e.target as HTMLSelectElement).value=""; }}
                                    className="w-full px-2 py-1.5 bg-background border border-border rounded text-xs text-foreground focus:outline-none focus:border-primary">
                                    <option value="">+ Add from roster...</option>
                                    {roster.map((m:any) => <option key={m.id} value={m.callsign}>{m.callsign}{m.rank_name ? ` — ${m.rank_name}` : ""}</option>)}
                                  </select>
                                  <input onKeyDown={e => { if(e.key==="Enter"){ const v=(e.target as HTMLInputElement).value.trim(); if(v){ const n=[...elements]; if(!n[ei].callsigns.includes(v)) n[ei]={...n[ei],callsigns:[...el.callsigns,v]}; saveEls(n); (e.target as HTMLInputElement).value=""; } e.preventDefault(); }}}
                                    className="w-full px-2 py-1.5 bg-background border border-border rounded text-xs text-foreground focus:outline-none focus:border-primary mt-1"
                                    placeholder="Or type callsign + Enter to add manually" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={f.key} className="space-y-1">
                        <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">{f.label}</label>
                        {f.type === "textarea" && (
                          <textarea value={val} onChange={e => setField(phase.id, item.id, f.key, e.target.value)}
                            placeholder={f.placeholder}
                            className="w-full px-3 py-2 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary resize-none h-24" />
                        )}
                        {f.type === "text" && (
                          <input value={val} onChange={e => setField(phase.id, item.id, f.key, e.target.value)}
                            placeholder={f.placeholder}
                            className="w-full px-3 py-2 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary" />
                        )}
                        {f.type === "select" && (
                          <select value={val} onChange={e => setField(phase.id, item.id, f.key, e.target.value)}
                            className="w-full px-3 py-2 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary">
                            <option value="">— Select —</option>
                            {f.options?.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        )}
                        {f.type === "radio" && (
                          <div className="flex gap-3 flex-wrap">
                            {f.options?.map(o => (
                              <button key={o} onClick={() => setField(phase.id, item.id, f.key, o)}
                                className={`px-3 py-1 rounded text-[10px] font-display font-bold uppercase tracking-widest border transition-all ${val === o ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                                {o}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Planning Chat */}
      <div className="rounded-lg border border-border bg-secondary/10 overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5 text-primary" />
          <span className="text-[10px] font-display font-bold uppercase tracking-widest text-foreground">Planning Discussion</span>
          <span className="ml-auto text-[10px] text-muted-foreground">{chat.length} messages</span>
        </div>
        <div ref={chatRef} className="h-48 overflow-y-auto p-3 space-y-2">
          {chat.length === 0 && (
            <p className="text-xs text-muted-foreground/50 font-sans text-center py-8">No planning messages yet — use this to coordinate with your team before the op.</p>
          )}
          {chat.map(msg => (
            <div key={msg.id} className="flex gap-2.5 items-start">
              <div className="w-6 h-6 rounded bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <span className="text-[8px] font-display font-bold text-primary">{(msg.callsign ?? "??").slice(0,2).toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] font-display font-bold uppercase tracking-widest text-primary">{msg.callsign}</span>
                  <span className="text-[9px] text-muted-foreground/50 font-mono">{new Date(msg.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</span>
                </div>
                <p className="text-xs font-sans text-foreground/90 break-words">{msg.text}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="px-3 pb-3 pt-2 border-t border-border flex gap-2">
          <input value={chatInput} onChange={e => setChatInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); }}}
            placeholder="Add a planning note or question..."
            className="flex-1 px-3 py-2 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary" />
          <button onClick={sendChat} className="px-3 py-2 bg-primary text-primary-foreground rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-primary/90 transition-all">
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Comms Plan Generator ────────────────────────────────────────────────────
const FREQ_BANDS = ["HF","VHF","UHF","SATCOM","Digital"];
interface NetEntry { id: string; name: string; callsign: string; freq: string; alt_freq: string; contingency_freq: string; emergency_freq: string; band: string; primary: boolean; notes: string; }

function CommsPlanTab({ group, showMsg, globalIsPro }: { group: any; showMsg: (m: string, t?: "success"|"error") => void; globalIsPro?: boolean }) {
  const [opName, setOpName] = React.useState("");
  const [ops, setOps] = React.useState<any[]>([]);
  const [selectedOpId, setSelectedOpId] = React.useState<string>("");
  const [nets, setNets] = React.useState<NetEntry[]>([
    { id: "n1", name: "Command Net", callsign: "SUNRAY", freq: "", alt_freq: "", contingency_freq: "", emergency_freq: "", band: "VHF", primary: true, notes: "" },
    { id: "n2", name: "Assault Net", callsign: "SUNRAY MINOR", freq: "", alt_freq: "", contingency_freq: "", emergency_freq: "", band: "VHF", primary: false, notes: "" },
    { id: "n3", name: "Log Net", callsign: "ECHELON", freq: "", alt_freq: "", contingency_freq: "", emergency_freq: "", band: "VHF", primary: false, notes: "" },
  ]);
  // Auth — sign/countersign pairs (different from codewords)
  const [authPairs, setAuthPairs] = React.useState<{id:string; challenge:string; response:string; valid_from:string; valid_to:string}[]>([
    { id:"a1", challenge:"IRON", response:"DUKE", valid_from:"H-Hour", valid_to:"H+6" },
  ]);
  // EMCON — emissions control schedule
  const [emcon, setEmcon] = React.useState<{id:string; window:string; state:string; nets_affected:string}[]>([]);
  // OPSEC notes — editable, appended to export
  const [opsecNotes, setOpsecNotes] = React.useState("Maintain radio silence unless tactical necessity.\nSITREP interval: every 15 mins or on contact.\nMEDEVAC: 9-Line to SUNRAY.\nNo grid references in clear.");
  const [codewords, setCodewords] = React.useState<{id:string; word:string; meaning:string}[]>([
    { id:"c1", word:"CHECKMATE", meaning:"Mission complete — consolidate and report" },
    { id:"c2", word:"YANKEE",    meaning:"CASEVAC required — request extraction" },
    { id:"c3", word:"TABASCO",   meaning:"Break contact — rally at ERV" },
  ]);
  const [exported, setExported] = React.useState(false);
  const [commsPublished, setCommsPublished] = React.useState(false);
  const [commsSaved, setCommsSaved] = React.useState(false);

  React.useEffect(() => {
    if (!group?.id) return;
    apiFetch<any[]>(`/milsimOps?path=${group.id}/list`).then((res) => {
      const list = Array.isArray(res) ? res : [];
      setOps(list.sort((a: any, b: any) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime()));
    }).catch(() => {});
  }, [group?.id]);

  // Load saved comms plan when op changes
  React.useEffect(() => {
    if (!selectedOpId) return;
    apiFetch<any>(`/milsimBriefings?path=list&group_id=${group.id}`).then((res: any) => {
      const list: any[] = normaliseBriefings(res);
      const existing = list.find((b: any) => b.op_id === selectedOpId && b.title?.startsWith("COMMS PLAN:"));
      if (existing) {
        try {
          const data = JSON.parse(existing.content ?? "{}");
          if (data.nets) setNets(data.nets);
          if (data.codewords) setCodewords(data.codewords);
          if (data.opName) setOpName(data.opName);
          if (data.authPairs) setAuthPairs(data.authPairs);
          if (data.emcon) setEmcon(data.emcon);
          if (data.opsecNotes !== undefined) setOpsecNotes(data.opsecNotes);
        } catch {}
      }
    }).catch(() => {});
  }, [selectedOpId, group?.id]);

  const addNet = () => setNets(p => [...p, { id: `n${Date.now()}`, name: "", callsign: "", freq: "", alt_freq: "", contingency_freq: "", emergency_freq: "", band: "VHF", primary: false, notes: "" }]);
  const addAuth = () => setAuthPairs(p => [...p, { id: `a${Date.now()}`, challenge: "", response: "", valid_from: "", valid_to: "" }]);
  const updateAuth = (id: string, f: string, v: string) => setAuthPairs(p => p.map(a => a.id === id ? { ...a, [f]: v } : a));
  const removeAuth = (id: string) => setAuthPairs(p => p.filter(a => a.id !== id));
  const addEmcon = () => setEmcon(p => [...p, { id: `e${Date.now()}`, window: "", state: "EMCON ALPHA", nets_affected: "All" }]);
  const updateEmcon = (id: string, f: string, v: string) => setEmcon(p => p.map(e => e.id === id ? { ...e, [f]: v } : e));
  const removeEmcon = (id: string) => setEmcon(p => p.filter(e => e.id !== id));
  const updateNet = (id: string, f: keyof NetEntry, v: any) => setNets(p => p.map(n => n.id === id ? { ...n, [f]: v } : n));
  const removeNet = (id: string) => setNets(p => p.filter(n => n.id !== id));

  const addCW = () => setCodewords(p => [...p, { id: `c${Date.now()}`, word: "", meaning: "" }]);
  const updateCW = (id: string, f: string, v: string) => setCodewords(p => p.map(c => c.id === id ? { ...c, [f]: v } : c));
  const removeCW = (id: string) => setCodewords(p => p.filter(c => c.id !== id));

  if (!globalIsPro) return (
    <div className="flex flex-col items-center justify-center py-20 text-center max-w-md mx-auto gap-6">
      <div className="w-16 h-16 bg-yellow-500/10 border border-yellow-500/30 rounded-xl flex items-center justify-center">
        <Crown className="w-8 h-8 text-yellow-400" />
      </div>
      <div>
        <h3 className="font-display font-black text-2xl uppercase tracking-wider text-foreground mb-2">Commander Pro Required</h3>
        <p className="text-muted-foreground font-sans leading-relaxed">Comms Plan Generator is a Commander Pro feature. Upgrade to unlock NATO-format signals planning and export.</p>
      </div>
      <a href="/commander-pro" className="inline-flex items-center gap-3 bg-yellow-500 hover:bg-yellow-400 text-black font-display font-black uppercase tracking-widest text-sm px-8 py-3 rounded transition-all shadow-[0_0_20px_hsla(48,96%,53%,0.3)]">
        <Crown className="w-5 h-5" /> Upgrade to Pro
      </a>
    </div>
  );

  const handleSaveComms = async () => {
    if (!selectedOpId) { showMsg("Select an op to save the comms plan.", "error"); return; }
    try {
      const op = ops.find((o: any) => o.id === selectedOpId);
      const opLabel = op?.name ?? opName ?? "OP";
      const planContent = JSON.stringify({ opName, nets, codewords, authPairs, emcon, opsecNotes });
      // Upsert: check for existing comms plan briefing for this op
      const res: any = await apiFetch(`/milsimBriefings?path=list&group_id=${group.id}`).catch(() => ({ briefings: [] }));
      const list: any[] = normaliseBriefings(res);
      const existing = list.find((b: any) => b.op_id === selectedOpId && b.title?.startsWith("COMMS PLAN:"));
      if (existing) {
        await apiFetch(`/milsimBriefings?path=update&id=${existing.id}`, { method: "PATCH", body: JSON.stringify({ content: planContent }) });
      } else {
        await apiFetch(`/milsimBriefings?path=create`, {
          method: "POST",
          body: JSON.stringify({
            group_id: group.id,
            op_id: selectedOpId,
            title: `COMMS PLAN: ${opLabel}`,
            content: planContent,
            classification: "RESTRICTED",
            created_by: "system",
            status: "draft",
          }),
        });
      }
      setCommsSaved(true);
      showMsg("Comms plan saved to op.", "success");
      setTimeout(() => setCommsSaved(false), 3000);
    } catch { showMsg("Save failed.", "error"); }
  };

  const handlePublishComms = async () => {
    await handleSaveComms();
    if (!selectedOpId) return;
    try {
      const op = ops.find((o: any) => o.id === selectedOpId);
      const opLabel = op?.name ?? opName ?? "Op";
      const rosterList: any[] = await apiFetch<any>(`/milsimGroups?path=${group.id}/roster`).then((r: any) => Array.isArray(r) ? r : r?.members ?? []).catch(() => []);
      const active = rosterList.filter((m: any) => m.status === "Active" || m.status === "active");
      await Promise.all(active.map((m: any) =>
        apiFetch("/notifications?path=create", {
          method: "POST",
          body: JSON.stringify({
            user_id: m.user_id,
            type: "comms_plan",
            title: "Comms Plan Published",
            body: `Comms plan for ${opLabel} has been published by your HQ.`,
            link: `/portal/milsim/${group.id}/ops`,
          }),
        }).catch(() => {})
      ));
      setCommsPublished(true);
      showMsg(`Comms plan published — ${active.length} members notified.`, "success");
      setTimeout(() => setCommsPublished(false), 3000);
    } catch { showMsg("Publish failed.", "error"); }
  };

    const handleExport = () => {
    const lines = [
      `COMMUNICATIONS PLAN`,
      `OP: ${opName || "UNSET"}`,
      `UNIT: ${group?.name ?? ""}`,
      `DATE: ${new Date().toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" })}`,
      ``,
      `─── RADIO NETS (PACE) ───────────────────────`,
      ...nets.map(n => [
        `[${n.primary ? "PRIMARY" : "ALT"}] ${n.name}  CS: ${n.callsign}  BAND: ${n.band}`,
        `  P: ${n.freq || "—"}  A: ${n.alt_freq || "—"}  C: ${n.contingency_freq || "—"}  E: ${n.emergency_freq || "—"}`,
        n.notes ? `  NOTE: ${n.notes}` : null,
      ].filter(Boolean).join("\n")),
      ``,
      `─── AUTHENTICATION ──────────────────────────`,
      ...(authPairs.length > 0
        ? authPairs.map(a => `CHALLENGE: ${a.challenge.padEnd(16)} RESPONSE: ${a.response.padEnd(16)} VALID: ${a.valid_from}${a.valid_to ? "–"+a.valid_to : ""}`)
        : ["No auth pairs set"]),
      ``,
      `─── CODEWORDS ───────────────────────────────`,
      ...codewords.map(c => `${c.word.padEnd(20)} → ${c.meaning}`),
      ...(emcon.length > 0 ? [
        ``,
        `─── EMCON SCHEDULE ──────────────────────────`,
        ...emcon.map(e => `${e.window.padEnd(20)} STATE: ${e.state}  NETS: ${e.nets_affected}`),
      ] : []),
      ``,
      `─── OPSEC / COMMS DISCIPLINE ────────────────`,
      ...opsecNotes.split("\n").map(l => `- ${l}`),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Commsplan_${opName || "OP"}.txt`; a.click();
    URL.revokeObjectURL(url);
    setExported(true);
    showMsg("Comms plan exported.", "success");
    setTimeout(() => setExported(false), 2500);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-base uppercase tracking-widest text-foreground flex items-center gap-2">
            <Antenna className="w-4 h-4 text-primary" /> Comms Plan Generator
          </h3>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Build your NATO-format signals plan per operation</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} className="flex items-center gap-2 px-3 py-1.5 bg-secondary border border-border text-foreground rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-secondary/80 transition-all">
            <Download className="w-3.5 h-3.5" /> {exported ? "Exported!" : "Export .txt"}
          </button>
          <button onClick={handleSaveComms} disabled={!selectedOpId} className="flex items-center gap-2 px-3 py-1.5 bg-secondary border border-border text-foreground rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-secondary/80 transition-all disabled:opacity-40">
            <Save className="w-3.5 h-3.5" /> {commsSaved ? "Saved!" : "Save"}
          </button>
          <button onClick={handlePublishComms} disabled={!selectedOpId} className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded text-xs font-display font-bold uppercase tracking-widest hover:bg-green-500 transition-all disabled:opacity-40">
            <Send className="w-3.5 h-3.5" /> {commsPublished ? "Published!" : "Publish & Notify"}
          </button>
        </div>
      </div>

      {/* Op selector */}
      <div className="p-4 rounded-lg border border-border bg-secondary/10 space-y-3">
        <div className="space-y-1">
          <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Linked Operation</label>
          <select value={selectedOpId} onChange={e => setSelectedOpId(e.target.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded text-sm font-sans text-foreground focus:outline-none focus:border-primary">
            <option value="">— Select an op —</option>
            {ops.map((op: any) => (
              <option key={op.id} value={op.id}>{op.name} {op.scheduled_at ? `(${new Date(op.scheduled_at).toLocaleDateString("en-GB")})` : ""}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">Op Name / Label</label>
        <input value={opName} onChange={e => setOpName(e.target.value)} placeholder="e.g. OPERATION IRON CROW"
          className="w-full px-3 py-2 bg-background border border-border rounded text-sm font-sans text-foreground focus:outline-none focus:border-primary" />
      </div>

      {/* Radio Nets */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-display font-bold text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Radio className="w-3 h-3" /> Radio Nets</h4>
          <button onClick={addNet} className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider text-primary hover:underline">
            <Plus className="w-3 h-3" /> Add Net
          </button>
        </div>
        {nets.map(net => (
          <div key={net.id} className={`p-3 rounded-lg border bg-secondary/10 space-y-2 ${net.primary ? "border-primary/40" : "border-border"}`}>
            <div className="flex items-center gap-2">
              <button onClick={() => updateNet(net.id, "primary", !net.primary)}
                className={`text-[9px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border transition-all shrink-0 ${net.primary ? "bg-primary/10 text-primary border-primary/30" : "bg-secondary text-muted-foreground border-border hover:border-primary/40"}`}>
                {net.primary ? "PRIMARY" : "ALT"}
              </button>
              <input value={net.name} onChange={e => updateNet(net.id, "name", e.target.value)} placeholder="Net name"
                className="flex-1 px-2 py-1 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary" />
              <button onClick={() => removeNet(net.id)} className="text-muted-foreground hover:text-red-400 transition-colors p-1">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[9px] text-muted-foreground font-sans">Callsign</label>
                <input value={net.callsign} onChange={e => updateNet(net.id, "callsign", e.target.value)} placeholder="SUNRAY"
                  className="w-full px-2 py-1 bg-background border border-border rounded text-xs font-mono text-foreground focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="text-[9px] text-muted-foreground font-sans">Band</label>
                <select value={net.band} onChange={e => updateNet(net.id, "band", e.target.value)}
                  className="w-full px-2 py-1 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary">
                  {FREQ_BANDS.map(b => <option key={b}>{b}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <span className="text-[9px] text-muted-foreground font-sans uppercase tracking-widest font-bold pb-1.5">PACE</span>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {[["P", "freq", "Primary freq"], ["A", "alt_freq", "Alternate"], ["C", "contingency_freq", "Contingency"], ["E", "emergency_freq", "Emergency"]].map(([lbl, field, ph]) => (
                <div key={field}>
                  <label className="text-[9px] text-muted-foreground font-sans">{lbl}</label>
                  <input value={(net as any)[field]} onChange={e => updateNet(net.id, field as keyof NetEntry, e.target.value)} placeholder={ph}
                    className={`w-full px-2 py-1 bg-background border rounded text-xs font-mono text-foreground focus:outline-none focus:border-primary ${lbl === "P" ? "border-primary/40" : "border-border"}`} />
                </div>
              ))}
            </div>
            <input value={net.notes} onChange={e => updateNet(net.id, "notes", e.target.value)} placeholder="Notes (optional)"
              className="w-full px-2 py-1 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary" />
          </div>
        ))}
      </div>

      {/* Codewords */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-display font-bold text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Flag className="w-3 h-3" /> Codewords</h4>
          <button onClick={addCW} className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider text-primary hover:underline">
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        {codewords.map(cw => (
          <div key={cw.id} className="flex items-center gap-2">
            <input value={cw.word} onChange={e => updateCW(cw.id, "word", e.target.value)} placeholder="CODEWORD"
              className="w-36 px-2 py-1 bg-background border border-border rounded text-xs font-mono text-foreground focus:outline-none focus:border-primary uppercase" />
            <span className="text-muted-foreground text-xs">→</span>
            <input value={cw.meaning} onChange={e => updateCW(cw.id, "meaning", e.target.value)} placeholder="Meaning"
              className="flex-1 px-2 py-1 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary" />
            <button onClick={() => removeCW(cw.id)} className="text-muted-foreground hover:text-red-400 transition-colors p-1">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Authentication — Sign/Countersign */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-display font-bold text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> Authentication — Challenge / Response</h4>
          <button onClick={addAuth} className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider text-primary hover:underline"><Plus className="w-3 h-3" /> Add Pair</button>
        </div>
        {authPairs.length === 0 && <div className="border border-dashed border-border rounded p-2.5 text-center text-xs text-muted-foreground font-sans">No auth pairs — add IFF challenge/response pairs</div>}
        {authPairs.map(a => (
          <div key={a.id} className="grid grid-cols-[1fr_1fr_auto_auto_auto] gap-2 items-end">
            <div>
              <label className="text-[9px] text-muted-foreground font-sans">Challenge</label>
              <input value={a.challenge} onChange={e => updateAuth(a.id, "challenge", e.target.value.toUpperCase())} placeholder="IRON"
                className="w-full px-2 py-1 bg-background border border-border rounded text-xs font-mono text-foreground focus:outline-none focus:border-primary uppercase" />
            </div>
            <div>
              <label className="text-[9px] text-muted-foreground font-sans">Response</label>
              <input value={a.response} onChange={e => updateAuth(a.id, "response", e.target.value.toUpperCase())} placeholder="DUKE"
                className="w-full px-2 py-1 bg-background border border-border rounded text-xs font-mono text-foreground focus:outline-none focus:border-primary uppercase" />
            </div>
            <div>
              <label className="text-[9px] text-muted-foreground font-sans">Valid from</label>
              <input value={a.valid_from} onChange={e => updateAuth(a.id, "valid_from", e.target.value)} placeholder="H-Hour"
                className="w-full px-2 py-1 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-[9px] text-muted-foreground font-sans">Valid to</label>
              <input value={a.valid_to} onChange={e => updateAuth(a.id, "valid_to", e.target.value)} placeholder="H+6"
                className="w-full px-2 py-1 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary" />
            </div>
            <button onClick={() => removeAuth(a.id)} className="text-muted-foreground hover:text-red-400 transition-colors p-1 mb-0.5"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>

      {/* EMCON Schedule */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-display font-bold text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><RadioTower className="w-3 h-3" /> EMCON Schedule</h4>
          <button onClick={addEmcon} className="flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider text-primary hover:underline"><Plus className="w-3 h-3" /> Add Window</button>
        </div>
        {emcon.length === 0 && <div className="border border-dashed border-border rounded p-2.5 text-center text-xs text-muted-foreground font-sans">No EMCON windows — optional emissions control schedule</div>}
        {emcon.map(e => (
          <div key={e.id} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-end">
            <div>
              <label className="text-[9px] text-muted-foreground font-sans">Time window</label>
              <input value={e.window} onChange={ev => updateEmcon(e.id, "window", ev.target.value)} placeholder="H-2 to H-Hour"
                className="w-full px-2 py-1 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-[9px] text-muted-foreground font-sans">State</label>
              <select value={e.state} onChange={ev => updateEmcon(e.id, "state", ev.target.value)}
                className="w-full px-2 py-1 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary">
                {["EMCON ALPHA","EMCON BRAVO","EMCON CHARLIE","Radio Silence","Minimize"].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] text-muted-foreground font-sans">Nets affected</label>
              <input value={e.nets_affected} onChange={ev => updateEmcon(e.id, "nets_affected", ev.target.value)} placeholder="All / Command Net"
                className="w-full px-2 py-1 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary" />
            </div>
            <button onClick={() => removeEmcon(e.id)} className="text-muted-foreground hover:text-red-400 transition-colors p-1 mb-0.5"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>

      {/* Editable OPSEC Notes */}
      <div className="space-y-2">
        <h4 className="font-display font-bold text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Lock className="w-3 h-3" /> OPSEC / Comms Discipline</h4>
        <textarea value={opsecNotes} onChange={e => setOpsecNotes(e.target.value)} rows={4}
          className="w-full px-3 py-2 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary resize-y" />
        <p className="text-[9px] text-muted-foreground/60 font-sans">These notes are appended verbatim to the exported comms plan.</p>
      </div>
    </div>
  );
}

// ─── Conduct Tracker ─────────────────────────────────────────────────────────
function ConductTrackerTab({ group, showMsg }: { group: any; showMsg: (m: string, t?: "success"|"error") => void }) {
  const [reports, setReports] = React.useState<any[]>([]);
  const [roster, setRoster] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [filterCallsign, setFilterCallsign] = React.useState("");
  const [filterCat, setFilterCat] = React.useState("all");

  React.useEffect(() => {
    if (!group?.id) return;
    Promise.all([
      apiFetch<any>(`/milsimGroups?path=${group.id}/conduct-reports`).catch(() => []),
      apiFetch<any>(`/milsimGroups?path=${group.id}/roster`).catch(() => []),
    ]).then(([r, ros]) => {
      setReports(normaliseConductReports(r));
      setRoster(Array.isArray(ros) ? ros : ros?.members ?? []);
      setLoading(false);
    });
  }, [group?.id]);

  const cats = ["all", ...Array.from(new Set(reports.map((r:any) => r.category).filter(Boolean)))];
  const filtered = reports.filter((r:any) => {
    if (filterCat !== "all" && r.category !== filterCat) return false;
    if (filterCallsign && !r.subject_callsign?.toLowerCase().includes(filterCallsign.toLowerCase())) return false;
    return true;
  });

  // Build per-member summary
  const memberStats: Record<string, {callsign:string; count:number; severe:number; open:number; lastDate:string}> = {};
  reports.forEach((r:any) => {
    const cs = r.subject_callsign ?? "Unknown";
    if (!memberStats[cs]) memberStats[cs] = { callsign: cs, count: 0, severe: 0, open: 0, lastDate: r.report_date ?? r.created_date ?? "" };
    memberStats[cs].count++;
    if (r.severity === "HIGH" || r.severity === "CRITICAL") memberStats[cs].severe++;
    if (!r.outcome || r.outcome === "PENDING") memberStats[cs].open++;
    if ((r.report_date ?? r.created_date ?? "") > memberStats[cs].lastDate) memberStats[cs].lastDate = r.report_date ?? r.created_date ?? "";
  });
  const sortedMembers = Object.values(memberStats).sort((a:any,b:any) => b.count - a.count).slice(0, 10);

  const SEV_COLOR: Record<string,string> = { LOW:"text-green-400 bg-green-500/10 border-green-500/30", MEDIUM:"text-amber-400 bg-amber-500/10 border-amber-500/30", HIGH:"text-red-400 bg-red-500/10 border-red-500/30", CRITICAL:"text-red-600 bg-red-600/10 border-red-600/30" };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-display font-bold text-base uppercase tracking-widest text-foreground flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-primary" /> Conduct Tracker
        </h3>
        <p className="text-xs text-muted-foreground font-sans mt-0.5">Pattern analysis across all filed conduct reports</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Total Reports", val: reports.length, icon: <ClipboardList className="w-4 h-4" />, color: "text-foreground" },
              { label: "Open / Pending", val: reports.filter((r:any) => !r.outcome || r.outcome === "PENDING").length, icon: <Clock className="w-4 h-4" />, color: "text-amber-400" },
              { label: "High/Critical", val: reports.filter((r:any) => r.severity === "HIGH" || r.severity === "CRITICAL").length, icon: <AlertOctagon className="w-4 h-4" />, color: "text-red-400" },
              { label: "Members Flagged", val: Object.keys(memberStats).length, icon: <UserX className="w-4 h-4" />, color: "text-orange-400" },
            ].map(t => (
              <div key={t.label} className="p-3 rounded-lg border border-border bg-secondary/10 text-center space-y-1">
                <div className={`flex justify-center ${t.color}`}>{t.icon}</div>
                <p className={`font-display font-bold text-xl ${t.color}`}>{t.val}</p>
                <p className="text-[9px] font-display uppercase tracking-widest text-muted-foreground">{t.label}</p>
              </div>
            ))}
          </div>

          {/* Top offenders */}
          {sortedMembers.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-display font-bold text-xs uppercase tracking-widest text-muted-foreground">Most Reports — Top Members</h4>
              <div className="space-y-1">
                {sortedMembers.map(m => (
                  <div key={m.callsign} className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-secondary/5 hover:bg-secondary/20 transition-colors">
                    <UserX className={`w-3.5 h-3.5 shrink-0 ${m.severe > 0 ? "text-red-400" : "text-muted-foreground"}`} />
                    <span className="font-mono text-xs font-bold text-foreground flex-1">{m.callsign}</span>
                    <span className="text-[10px] text-muted-foreground font-sans">{m.count} report{m.count !== 1 ? "s" : ""}</span>
                    {m.severe > 0 && <span className="text-[10px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border text-red-400 bg-red-500/10 border-red-500/30">{m.severe} HIGH</span>}
                    {m.open > 0 && <span className="text-[10px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border text-amber-400 bg-amber-500/10 border-amber-500/30">{m.open} OPEN</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <input value={filterCallsign} onChange={e => setFilterCallsign(e.target.value)} placeholder="Filter by callsign…"
              className="px-3 py-1.5 text-xs bg-background border border-border rounded font-sans text-foreground focus:outline-none focus:border-primary" />
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
              className="px-3 py-1.5 text-xs bg-background border border-border rounded font-sans text-foreground focus:outline-none focus:border-primary">
              {cats.map(c => <option key={c} value={c}>{c === "all" ? "All Categories" : c}</option>)}
            </select>
          </div>

          {/* Report list */}
          <div className="space-y-2">
            {filtered.length === 0 && (
              <div className="flex flex-col items-center py-14 gap-3 border border-dashed border-border rounded-lg">
                <ShieldAlert className="w-8 h-8 text-muted-foreground/30" />
                <p className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground">{reports.length === 0 ? "No conduct reports filed" : "No results"}</p>
              </div>
            )}
            {filtered.map((r:any) => (
              <div key={r.id} className="p-3 rounded-lg border border-border bg-secondary/10 space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${SEV_COLOR[r.severity] ?? SEV_COLOR.LOW}`}>{r.severity ?? "LOW"}</span>
                  <span className="font-mono text-xs font-bold text-foreground">{r.subject_callsign ?? "Unknown"}</span>
                  <span className="text-[10px] text-muted-foreground font-sans">{r.category ?? "General"}</span>
                  <span className="ml-auto text-[10px] text-muted-foreground font-mono">{r.report_date ?? (r.created_date ? r.created_date.slice(0,10) : "")}</span>
                </div>
                <p className="text-xs font-sans text-muted-foreground line-clamp-2">{r.description}</p>
                {r.outcome && r.outcome !== "PENDING" && (
                  <span className="text-[10px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border text-green-400 bg-green-500/10 border-green-500/30">{r.outcome}</span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Accountability Tab ───────────────────────────────────────────────────────
function AccountabilityTab({ group, showMsg, globalIsPro }: any) {
  type ASub = "overview" | "expectations" | "fitness" | "pio";
  const [sub, setSub] = useState<ASub>("overview");

  const SUBS: { id: ASub; label: string; icon: React.ReactNode }[] = [
    { id: "overview",      label: "Role Overview",    icon: <Eye className="w-3.5 h-3.5" /> },
    { id: "expectations",  label: "Expectations",     icon: <BarChart3 className="w-3.5 h-3.5" /> },
    { id: "fitness",       label: "Fitness Reviews",  icon: <ClipboardCheck className="w-3.5 h-3.5" /> },
    { id: "pio",           label: "PIOs",             icon: <AlertCircle className="w-3.5 h-3.5" /> },
  ];

  if (!globalIsPro) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
      <Flame className="w-10 h-10 text-yellow-400 opacity-60" />
      <p className="font-display font-bold uppercase tracking-widest text-sm text-foreground">Commander Pro Required</p>
      <p className="text-xs text-muted-foreground max-w-xs">Role Accountability is a Pro feature. Upgrade to access fitness reviews, PIOs, and automatic role lapsing.</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-1.5 flex-wrap border-b border-border pb-3">
        {SUBS.map(s => (
          <button key={s.id} onClick={() => setSub(s.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-[10px] font-display font-bold uppercase tracking-widest transition-all ${sub === s.id ? "bg-primary/15 text-primary border-primary/40" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}>
            {s.icon}{s.label}
          </button>
        ))}
      </div>
      {sub === "overview"     && <RoleOverviewPanel     group={group} showMsg={showMsg} />}
      {sub === "expectations" && <RoleExpectationsPanel group={group} showMsg={showMsg} />}
      {sub === "fitness"      && <RoleFitnessPanel      group={group} showMsg={showMsg} />}
      {sub === "pio"          && <PIOPanel              group={group} showMsg={showMsg} />}
    </div>
  );
}

// ── Role Overview — performance stats per member, auto-lapse badges ───────────
function RoleOverviewPanel({ group, showMsg }: any) {
  const [ops, setOps] = useState<any[]>([]);
  const [aars, setAars] = useState<any[]>([]);
  const [trainings, setTrainings] = useState<any[]>([]);
  const [expectations, setExpectations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const roster: any[] = (group.roster ?? []).filter((r: any) => r.status === "Active" || r.status === "active");

  useEffect(() => {
    Promise.all([
      apiFetch<any[]>(`/milsimOps?group_id=${group.id}`).catch(() => []),
      apiFetch<any>(`/milsimAars?path=list&group_id=${group.id}`).then(normaliseAars).catch(() => []),
      apiFetch<any[]>(`/milsimTrainingReviews?group_id=${group.id}`).catch(() => []),
      apiFetch<any[]>(`/roleExpectations?group_id=${group.id}`).catch(() => []),
    ]).then(([o, a, t, e]) => {
      setOps(Array.isArray(o) ? o : []);
      setAars(Array.isArray(a) ? a : []);
      setTrainings(Array.isArray(t) ? t : []);
      setExpectations(Array.isArray(e) ? e : []);
      setLoading(false);
    });
  }, [group.id]);

  const now = Date.now();
  const day30 = now - 30 * 86400000;

  const getExpForRole = (roleId: any) =>
    expectations.find((e: any) => String(e.role_id) === String(roleId));

  const getMemberStats = (member: any) => {
    const rid = member.id;
    const opsLast30 = ops.filter((o: any) => {
      const parts: any[] = Array.isArray(o.participants) ? o.participants : [];
      return new Date(o.scheduled_at ?? 0).getTime() > day30 && parts.some((p: any) => p === rid || p?.id === rid || p?.roster_id === rid);
    }).length;
    const aarsLast30 = aars.filter((a: any) =>
      new Date(a.created_date ?? 0).getTime() > day30 && (a.author_id === member.user_id || (Array.isArray(a.participants) && a.participants.some((p: any) => p === rid || p?.id === rid || p?.roster_id === rid)))
    ).length;
    const trainingLast30 = trainings.filter((t: any) =>
      new Date(t.created_date ?? 0).getTime() > day30 && (t.conducted_by === rid || t.conducted_by === member.user_id)
    ).length;
    return { opsLast30, aarsLast30, trainingLast30 };
  };

  const getRoleLabel = (member: any) =>
    member.roleId ? (group.roles ?? []).find((r: any) => r.id === member.roleId)?.name ?? "—" : "—";

  const getRankLabel = (member: any) =>
    member.rankId ? (group.ranks ?? []).find((r: any) => r.id === member.rankId)?.name ?? "—" : "—";

  const getLapseStatus = (member: any, stats: ReturnType<typeof getMemberStats>) => {
    const exp = getExpForRole(member.roleId);
    if (!exp) return null;
    const issues: string[] = [];
    if (exp.min_ops_per_30d && stats.opsLast30 < exp.min_ops_per_30d) issues.push(`${stats.opsLast30}/${exp.min_ops_per_30d} ops`);
    if (exp.min_aars_per_30d && stats.aarsLast30 < exp.min_aars_per_30d) issues.push(`${stats.aarsLast30}/${exp.min_aars_per_30d} AARs`);
    if (exp.min_training_per_30d && stats.trainingLast30 < exp.min_training_per_30d) issues.push(`${stats.trainingLast30}/${exp.min_training_per_30d} training`);
    if (issues.length === 0) return { status: "green", issues: [] };
    if (issues.length >= 2) return { status: "red", issues };
    return { status: "amber", issues };
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground text-xs py-8"><Loader2 className="w-4 h-4 animate-spin" />Loading role data...</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">
          Role Performance — Last 30 Days
        </p>
        <p className="text-[10px] text-muted-foreground">
          Set expectations in the <span className="text-primary cursor-pointer">Expectations</span> tab to enable lapse detection
        </p>
      </div>
      {roster.length === 0 && <p className="text-muted-foreground text-xs">No active roster members.</p>}
      <div className="space-y-2">
        {roster.map((member: any) => {
          const stats = getMemberStats(member);
          const lapse = getLapseStatus(member, stats);
          const role = getRoleLabel(member);
          const rank = getRankLabel(member);
          const lapseColour =
            !lapse         ? "border-border bg-secondary/10" :
            lapse.status === "green" ? "border-green-500/30 bg-green-500/5" :
            lapse.status === "amber" ? "border-amber-500/40 bg-amber-500/5" :
                                       "border-red-500/40 bg-red-500/5";
          const badge =
            !lapse         ? null :
            lapse.status === "green" ? <span className="px-2 py-0.5 rounded text-[9px] font-display font-bold uppercase tracking-widest bg-green-500/15 text-green-400 border border-green-500/30">Fit</span> :
            lapse.status === "amber" ? <span className="px-2 py-0.5 rounded text-[9px] font-display font-bold uppercase tracking-widest bg-amber-500/15 text-amber-400 border border-amber-500/30">⚠ Underperforming</span> :
                                       <span className="px-2 py-0.5 rounded text-[9px] font-display font-bold uppercase tracking-widest bg-red-500/15 text-red-400 border border-red-500/30 animate-pulse">● Role Lapsed</span>;
          return (
            <div key={member.id} className={`rounded-lg border px-4 py-3 transition-all ${lapseColour}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                    <span className="font-display font-black text-[10px] text-primary">{member.callsign?.slice(0,2).toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="text-xs font-display font-bold text-foreground">{member.callsign}</p>
                    <p className="text-[10px] text-muted-foreground">{rank} · <span className={lapse && lapse.status !== "green" ? "text-amber-400" : "text-muted-foreground"}>{role}</span></p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-[10px] font-mono">
                  <span className="text-muted-foreground">Ops: <span className="text-foreground font-bold">{stats.opsLast30}</span></span>
                  <span className="text-muted-foreground">AARs: <span className="text-foreground font-bold">{stats.aarsLast30}</span></span>
                  <span className="text-muted-foreground">Training: <span className="text-foreground font-bold">{stats.trainingLast30}</span></span>
                  {badge}
                </div>
              </div>
              {lapse && lapse.issues.length > 0 && (
                <p className="text-[10px] text-amber-400 mt-2 ml-11">⚡ Below threshold: {lapse.issues.join(" · ")}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Role Expectations Config ───────────────────────────────────────────────────
function RoleExpectationsPanel({ group, showMsg }: any) {
  const [expectations, setExpectations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<any>({});

  const load = () => {
    setLoading(true);
    apiFetch<any[]>(`/roleExpectations?group_id=${group.id}`).then(res => {
      setExpectations(Array.isArray(res) ? res : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [group.id]);

  const roles: any[] = group.roles ?? [];
  const coveredRoleIds = new Set(expectations.map((e: any) => String(e.role_id)));

  const openEdit = (exp: any) => {
    setEditId(exp.id);
    setForm({ ...exp });
  };

  const createNew = (role: any) => {
    setEditId("new");
    setForm({ group_id: group.id, role_id: role.id, role_name: role.name, min_ops_per_30d: 2, min_training_per_30d: 0, min_aars_per_30d: 0, min_apps_reviewed_per_30d: 0, lapse_after_days: 30, is_leadership: false, is_instructor: false, is_admin: false });
  };

  const save = async () => {
    if (!form.role_id) return;
    setSaving(editId);
    try {
      if (editId === "new") {
        await apiFetch(`/roleExpectations`, { method: "POST", body: JSON.stringify(form) });
      } else {
        await apiFetch(`/roleExpectations/${editId}`, { method: "PATCH", body: JSON.stringify(form) });
      }
      showMsg("Expectations saved.", "success");
      setEditId(null);
      load();
    } catch (e: any) { showMsg(e.message, "error"); }
    finally { setSaving(null); }
  };

  const del = async (id: string) => {
    await apiFetch(`/roleExpectations/${id}`, { method: "DELETE" }).catch(() => {});
    load();
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground text-xs py-8"><Loader2 className="w-4 h-4 animate-spin" />Loading...</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-[10px] text-muted-foreground font-sans">Define what each role is expected to produce per month. Members below threshold will be flagged automatically.</p>
      {roles.length === 0 && <p className="text-muted-foreground text-xs">No roles defined yet. Create roles in the Roles tab first.</p>}

      {/* Existing expectations */}
      {expectations.map((exp: any) => (
        <div key={exp.id} className="rounded-lg border border-border bg-secondary/10 px-4 py-3">
          {editId === exp.id ? (
            <ExpectationForm form={form} setForm={setForm} onSave={save} onCancel={() => setEditId(null)} saving={saving === exp.id} />
          ) : (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs font-display font-bold text-foreground uppercase tracking-widest">{exp.role_name}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {exp.min_ops_per_30d > 0 && `${exp.min_ops_per_30d} ops/mo · `}
                  {exp.min_training_per_30d > 0 && `${exp.min_training_per_30d} training/mo · `}
                  {exp.min_aars_per_30d > 0 && `${exp.min_aars_per_30d} AARs/mo · `}
                  Lapse after {exp.lapse_after_days ?? 30}d
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(exp)} className="px-2.5 py-1 rounded border border-border text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground">Edit</button>
                <button onClick={() => del(exp.id)} className="px-2.5 py-1 rounded border border-red-500/30 text-[10px] font-display font-bold uppercase tracking-widest text-red-400 hover:bg-red-500/10">Remove</button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Uncovered roles */}
      {roles.filter((r: any) => !coveredRoleIds.has(String(r.id))).map((role: any) => (
        <div key={role.id} className="rounded-lg border border-dashed border-border/50 px-4 py-3 flex items-center justify-between">
          {editId === "new" && form.role_id === role.id ? (
            <ExpectationForm form={form} setForm={setForm} onSave={save} onCancel={() => setEditId(null)} saving={saving === "new"} />
          ) : (
            <>
              <p className="text-xs text-muted-foreground font-display uppercase tracking-widest">{role.name} <span className="text-[9px] normal-case">(no expectations set)</span></p>
              <button onClick={() => createNew(role)} className="px-2.5 py-1 rounded border border-primary/40 text-[10px] font-display font-bold uppercase tracking-widest text-primary hover:bg-primary/10">Set Expectations</button>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function ExpectationForm({ form, setForm, onSave, onCancel, saving }: any) {
  const f = (key: string, label: string, min = 0) => (
    <div className="flex items-center gap-2">
      <label className="text-[10px] font-display uppercase tracking-widest text-muted-foreground w-40 shrink-0">{label}</label>
      <input type="number" min={min} value={form[key] ?? 0} onChange={e => setForm((p: any) => ({ ...p, [key]: Number(e.target.value) }))}
        className="w-20 px-2 py-1 bg-background border border-border rounded text-xs font-mono text-foreground focus:outline-none focus:border-primary" />
    </div>
  );
  return (
    <div className="space-y-2 w-full">
      <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary mb-2">{form.role_name}</p>
      {f("min_ops_per_30d", "Min ops / 30 days")}
      {f("min_training_per_30d", "Min training / 30 days")}
      {f("min_aars_per_30d", "Min AARs / 30 days")}
      {f("lapse_after_days", "Lapse flag after (days)", 1)}
      <div className="flex gap-2 mt-3">
        <button onClick={onSave} disabled={saving} className="px-3 py-1 rounded bg-primary text-primary-foreground text-[10px] font-display font-bold uppercase tracking-widest hover:bg-primary/80 disabled:opacity-50">
          {saving ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Save"}
        </button>
        <button onClick={onCancel} className="px-3 py-1 rounded border border-border text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground">Cancel</button>
      </div>
    </div>
  );
}

// ── Role Fitness Reviews ───────────────────────────────────────────────────────
function RoleFitnessPanel({ group, showMsg }: any) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<any>({ roster_id: "", verdict: "pass", notes: "", review_date: new Date().toISOString().slice(0,10) });
  const [saving, setSaving] = useState(false);

  const myRosterEntry = (group.roster ?? []).find((r: any) => r.user_id === user?.id);

  const load = () => {
    apiFetch<any[]>(`/roleFitnessReviews?group_id=${group.id}`).then(res => {
      setReviews(Array.isArray(res) ? res : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, [group.id]);

  const submit = async () => {
    const target = (group.roster ?? []).find((r: any) => r.id === form.roster_id);
    if (!target) return;
    setSaving(true);
    try {
      await apiFetch(`/roleFitnessReviews`, { method: "POST", body: JSON.stringify({
        group_id: group.id,
        roster_id: form.roster_id,
        callsign: target.callsign,
        role_name: (group.roles ?? []).find((r: any) => r.id === target.roleId)?.name ?? "—",
        reviewer_id: myRosterEntry?.id ?? user?.id,
        reviewer_callsign: myRosterEntry?.callsign ?? user?.username ?? "CO",
        verdict: form.verdict,
        notes: form.notes,
        review_date: form.review_date,
      })});
      showMsg("Review filed.", "success");
      setShowForm(false);
      load();
    } catch (e: any) { showMsg(e.message, "error"); }
    finally { setSaving(false); }
  };

  const verdictBadge = (v: string) =>
    v === "pass" ? <span className="px-2 py-0.5 rounded text-[9px] font-display font-bold uppercase tracking-widest bg-green-500/15 text-green-400 border border-green-500/30">Pass</span> :
    v === "needs_improvement" ? <span className="px-2 py-0.5 rounded text-[9px] font-display font-bold uppercase tracking-widest bg-amber-500/15 text-amber-400 border border-amber-500/30">Needs Improvement</span> :
    <span className="px-2 py-0.5 rounded text-[9px] font-display font-bold uppercase tracking-widest bg-red-500/15 text-red-400 border border-red-500/30">Unfit</span>;

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground text-xs py-8"><Loader2 className="w-4 h-4 animate-spin" />Loading...</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-muted-foreground font-sans">Formal role fitness reviews are logged permanently to the operator\'s service record.</p>
        <button onClick={() => setShowForm(v => !v)} className="px-3 py-1.5 rounded border border-primary/40 text-[10px] font-display font-bold uppercase tracking-widest text-primary hover:bg-primary/10">
          + New Review
        </button>
      </div>

      {showForm && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-4 space-y-3">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary">File Role Fitness Review</p>
          <div className="space-y-2">
            <label className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">Operator</label>
            <select value={form.roster_id} onChange={e => setForm((p: any) => ({ ...p, roster_id: e.target.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary">
              <option value="">— Select operator —</option>
              {(group.roster ?? []).filter((r: any) => r.status === "Active" || r.status === "active").map((r: any) => (
                <option key={r.id} value={r.id}>{r.callsign}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">Verdict</label>
            <div className="flex gap-2">
              {(["pass","needs_improvement","unfit"] as const).map(v => (
                <button key={v} onClick={() => setForm((p: any) => ({ ...p, verdict: v }))}
                  className={`px-3 py-1 rounded border text-[10px] font-display font-bold uppercase tracking-widest transition-all ${form.verdict === v ? "bg-primary/15 text-primary border-primary/40" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {v === "pass" ? "Pass" : v === "needs_improvement" ? "Needs Improvement" : "Unfit"}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">Notes / Justification</label>
            <textarea value={form.notes} onChange={e => setForm((p: any) => ({ ...p, notes: e.target.value }))} rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary resize-none"
              placeholder="Describe performance against role expectations..." />
          </div>
          <div className="flex gap-2">
            <button onClick={submit} disabled={saving || !form.roster_id} className="px-3 py-1.5 rounded bg-primary text-primary-foreground text-[10px] font-display font-bold uppercase tracking-widest hover:bg-primary/80 disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "File Review"}
            </button>
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 rounded border border-border text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
        </div>
      )}

      {reviews.length === 0 && !showForm && <p className="text-muted-foreground text-xs py-4">No reviews filed yet.</p>}

      <div className="space-y-2">
        {reviews.sort((a: any, b: any) => new Date(b.created_date ?? 0).getTime() - new Date(a.created_date ?? 0).getTime()).map((rev: any) => (
          <div key={rev.id} className="rounded-lg border border-border bg-secondary/10 px-4 py-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-display font-bold text-foreground">{rev.callsign}</p>
                  <span className="text-[10px] text-muted-foreground">· {rev.role_name}</span>
                  {verdictBadge(rev.verdict)}
                </div>
                {rev.notes && <p className="text-[10px] text-muted-foreground font-sans mt-1 max-w-lg">{rev.notes}</p>}
              </div>
              <div className="text-right shrink-0">
                <p className="text-[10px] text-muted-foreground">By {rev.reviewer_callsign}</p>
                <p className="text-[10px] text-muted-foreground">{rev.review_date}</p>
                {rev.acknowledged_at && <p className="text-[10px] text-green-400 mt-0.5">✓ Acknowledged</p>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Performance Improvement Orders ────────────────────────────────────────────
function PIOPanel({ group, showMsg }: any) {
  const { user } = useAuth();
  const [pios, setPios] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any>({ roster_id: "", reason: "", deadline: "", tasks: [{ id: "1", description: "", completed: false }] });

  const myRosterEntry = (group.roster ?? []).find((r: any) => r.user_id === user?.id);

  const load = () => {
    apiFetch<any[]>(`/performanceImprovementOrders?group_id=${group.id}`).then(res => {
      setPios(Array.isArray(res) ? res : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, [group.id]);

  const addTask = () => setForm((p: any) => ({ ...p, tasks: [...p.tasks, { id: String(Date.now()), description: "", completed: false }] }));
  const setTask = (id: string, val: string) => setForm((p: any) => ({ ...p, tasks: p.tasks.map((t: any) => t.id === id ? { ...t, description: val } : t) }));
  const removeTask = (id: string) => setForm((p: any) => ({ ...p, tasks: p.tasks.filter((t: any) => t.id !== id) }));

  const submit = async () => {
    const target = (group.roster ?? []).find((r: any) => r.id === form.roster_id);
    if (!target || !form.reason.trim() || !form.deadline) return;
    setSaving(true);
    try {
      await apiFetch(`/performanceImprovementOrders`, { method: "POST", body: JSON.stringify({
        group_id: group.id,
        roster_id: form.roster_id,
        callsign: target.callsign,
        issued_by: myRosterEntry?.id ?? user?.id,
        issued_by_callsign: myRosterEntry?.callsign ?? user?.username ?? "CO",
        reason: form.reason,
        tasks: form.tasks.filter((t: any) => t.description.trim()),
        deadline: form.deadline,
        status: "active",
      })});
      showMsg("PIO issued.", "success");
      setShowForm(false);
      setForm({ roster_id: "", reason: "", deadline: "", tasks: [{ id: "1", description: "", completed: false }] });
      load();
    } catch (e: any) { showMsg(e.message, "error"); }
    finally { setSaving(false); }
  };

  const close = async (id: string, status: "completed" | "failed" | "withdrawn", note: string) => {
    await apiFetch(`/performanceImprovementOrders/${id}`, { method: "PATCH", body: JSON.stringify({ status, outcome_note: note, closed_at: new Date().toISOString(), closed_by: myRosterEntry?.callsign ?? user?.username ?? "CO" }) });
    showMsg(`PIO marked ${status}.`, "success");
    load();
  };

  const statusBadge = (s: string) =>
    s === "active"    ? <span className="px-2 py-0.5 rounded text-[9px] font-display font-bold uppercase tracking-widest bg-amber-500/15 text-amber-400 border border-amber-500/30">Active</span> :
    s === "completed" ? <span className="px-2 py-0.5 rounded text-[9px] font-display font-bold uppercase tracking-widest bg-green-500/15 text-green-400 border border-green-500/30">Completed</span> :
    s === "failed"    ? <span className="px-2 py-0.5 rounded text-[9px] font-display font-bold uppercase tracking-widest bg-red-500/15 text-red-400 border border-red-500/30">Failed</span> :
                        <span className="px-2 py-0.5 rounded text-[9px] font-display font-bold uppercase tracking-widest bg-secondary text-muted-foreground border border-border">Withdrawn</span>;

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground text-xs py-8"><Loader2 className="w-4 h-4 animate-spin" />Loading...</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-muted-foreground font-sans">PIOs are formal improvement orders with tasks and a deadline. Logged permanently to the service record.</p>
        <button onClick={() => setShowForm(v => !v)} className="px-3 py-1.5 rounded border border-primary/40 text-[10px] font-display font-bold uppercase tracking-widest text-primary hover:bg-primary/10">
          + Issue PIO
        </button>
      </div>

      {showForm && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-4 space-y-3">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary">Issue Performance Improvement Order</p>
          <div className="space-y-1">
            <label className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">Operator</label>
            <select value={form.roster_id} onChange={e => setForm((p: any) => ({ ...p, roster_id: e.target.value }))}
              className="w-full px-3 py-2 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary">
              <option value="">— Select operator —</option>
              {(group.roster ?? []).filter((r: any) => r.status === "Active" || r.status === "active").map((r: any) => (
                <option key={r.id} value={r.id}>{r.callsign}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">Reason</label>
            <textarea value={form.reason} onChange={e => setForm((p: any) => ({ ...p, reason: e.target.value }))} rows={2}
              className="w-full px-3 py-2 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary resize-none"
              placeholder="What performance issue is this addressing?" />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">Deadline</label>
            <input type="date" value={form.deadline} onChange={e => setForm((p: any) => ({ ...p, deadline: e.target.value }))}
              className="px-3 py-2 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary" />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">Required Tasks</label>
            {form.tasks.map((t: any) => (
              <div key={t.id} className="flex gap-2 items-center">
                <input value={t.description} onChange={e => setTask(t.id, e.target.value)}
                  className="flex-1 px-3 py-1.5 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary"
                  placeholder="e.g. Lead 2 ops before deadline" />
                <button onClick={() => removeTask(t.id)} className="text-muted-foreground hover:text-red-400 text-xs">✕</button>
              </div>
            ))}
            <button onClick={addTask} className="text-[10px] font-display uppercase tracking-widest text-primary hover:text-primary/80">+ Add task</button>
          </div>
          <div className="flex gap-2">
            <button onClick={submit} disabled={saving || !form.roster_id || !form.reason.trim() || !form.deadline} className="px-3 py-1.5 rounded bg-primary text-primary-foreground text-[10px] font-display font-bold uppercase tracking-widest hover:bg-primary/80 disabled:opacity-50">
              {saving ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Issue PIO"}
            </button>
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 rounded border border-border text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
        </div>
      )}

      {pios.length === 0 && !showForm && <p className="text-muted-foreground text-xs py-4">No PIOs issued yet.</p>}

      <div className="space-y-3">
        {pios.sort((a: any, b: any) => new Date(b.created_date ?? 0).getTime() - new Date(a.created_date ?? 0).getTime()).map((pio: any) => (
          <PIOCard key={pio.id} pio={pio} onClose={close} statusBadge={statusBadge} />
        ))}
      </div>
    </div>
  );
}

function PIOCard({ pio, onClose, statusBadge }: any) {
  const [expanded, setExpanded] = useState(false);
  const [closeNote, setCloseNote] = useState("");
  const overdue = pio.status === "active" && pio.deadline && new Date(pio.deadline).getTime() < Date.now();

  return (
    <div className={`rounded-lg border bg-secondary/10 overflow-hidden ${overdue ? "border-red-500/40" : "border-border"}`}>
      <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap cursor-pointer" onClick={() => setExpanded(v => !v)}>
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-display font-bold text-foreground">{pio.callsign}</p>
              {statusBadge(pio.status)}
              {overdue && <span className="px-2 py-0.5 rounded text-[9px] font-display font-bold uppercase tracking-widest bg-red-500/15 text-red-400 border border-red-500/30 animate-pulse">Overdue</span>}
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">Issued by {pio.issued_by_callsign} · Deadline: {pio.deadline ?? "—"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="text-[10px]">{(pio.tasks ?? []).filter((t: any) => t.completed).length}/{(pio.tasks ?? []).length} tasks</span>
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-border pt-3 space-y-3">
          <p className="text-xs text-foreground font-sans">{pio.reason}</p>
          <div className="space-y-1.5">
            {(pio.tasks ?? []).map((t: any) => (
              <div key={t.id} className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${t.completed ? "bg-green-500/20 border-green-500/50 text-green-400" : "border-border"}`}>
                  {t.completed && <Check className="w-2.5 h-2.5" />}
                </span>
                <span className={t.completed ? "line-through text-muted-foreground/50" : ""}>{t.description}</span>
              </div>
            ))}
          </div>
          {pio.status === "active" && (
            <div className="space-y-2 pt-2 border-t border-border">
              <input value={closeNote} onChange={e => setCloseNote(e.target.value)}
                className="w-full px-3 py-1.5 bg-background border border-border rounded text-xs font-sans text-foreground focus:outline-none focus:border-primary"
                placeholder="Outcome note (optional)..." />
              <div className="flex gap-2">
                <button onClick={() => onClose(pio.id, "completed", closeNote)} className="px-2.5 py-1 rounded bg-green-500/20 border border-green-500/40 text-green-400 text-[10px] font-display font-bold uppercase tracking-widest hover:bg-green-500/30">Mark Complete</button>
                <button onClick={() => onClose(pio.id, "failed", closeNote)} className="px-2.5 py-1 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-display font-bold uppercase tracking-widest hover:bg-red-500/20">Mark Failed</button>
                <button onClick={() => onClose(pio.id, "withdrawn", closeNote)} className="px-2.5 py-1 rounded border border-border text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground">Withdraw</button>
              </div>
            </div>
          )}
          {pio.outcome_note && <p className="text-[10px] text-muted-foreground italic">"{pio.outcome_note}" — {pio.closed_by}</p>}
        </div>
      )}
    </div>
  );
}

// ─── J2 Intelligence Room ─────────────────────────────────────────────────────
function S2IntelligenceRoom({ group, showMsg, globalIsPro }: any) {
  type S2Sub = "dashboard" | "salute" | "intel_docs" | "threat_profiles";
  const [sub, setSub] = React.useState<S2Sub>("dashboard");

  const SUBS: { id: S2Sub; label: string; icon: React.ReactNode }[] = [
    { id: "dashboard",       label: "Intelligence Overview",      icon: <Eye className="w-3.5 h-3.5" /> },
    // SALUTE Reports moved to J3 Reports tab
    { id: "intel_docs",      label: "Intel Documents",   icon: <FileText className="w-3.5 h-3.5" /> },
    { id: "threat_profiles", label: "Threat Profiles",   icon: <ShieldAlert className="w-3.5 h-3.5" /> },
  ];

  if (!globalIsPro) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
        <Crown className="w-10 h-10 text-yellow-400" />
        <p className="text-lg font-display font-bold uppercase tracking-widest">Commander Pro Required</p>
        <p className="text-sm text-muted-foreground max-w-sm">The Intelligence Room is a Commander Pro feature. Upgrade to unlock SALUTE reporting, intel documents, and threat profiles.</p>
      </div>
    );
  }

  return (
    <div className="flex gap-0 min-h-[600px]">
      <div className="shrink-0 w-44 border-r border-border/60 pr-2 space-y-0.5 pt-1">
        {SUBS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSub(s.id)}
            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded text-left font-display font-bold uppercase tracking-widest text-[10px] transition-colors ${
              sub === s.id
                ? "bg-primary/15 text-primary border-l-2 border-primary pl-2.5"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
            }`}
          >
            {s.icon}
            <span className="truncate">{s.label}</span>
          </button>
        ))}
      </div>
      <div className="flex-1 pl-5 min-w-0">
        {sub === "dashboard"       && <S2DashboardPanel group={group} showMsg={showMsg} onNav={setSub} />}
        {/* SALUTE Reports moved to J3 Reports */}
        {sub === "intel_docs"      && <IntelDocumentsPanel group={group} showMsg={showMsg} />}
        {sub === "threat_profiles" && <ThreatProfilesPanel group={group} showMsg={showMsg} />}
      </div>
    </div>
  );
}

// ─── S2 Dashboard Panel ───────────────────────────────────────────────────────
function S2DashboardPanel({ group, showMsg, onNav }: any) {
  const [salutes, setSalutes]   = React.useState<any[]>([]);
  const [docs, setDocs]         = React.useState<any[]>([]);
  const [threats, setThreats]   = React.useState<any[]>([]);
  const [sitreps, setSitreps]   = React.useState<any[]>([]);
  const [aars, setAars]         = React.useState<any[]>([]);
  const [laces, setLaces]       = React.useState<any[]>([]);
  const [loading, setLoading]   = React.useState(true);

  React.useEffect(() => {
    if (!group?.id) return;
    Promise.all([
      apiFetch(`/milsimGroupsB?path=salute-reports&group_id=${group.id}`).catch(() => []),
      apiFetch(`/milsimGroupsB?path=intel-docs&group_id=${group.id}`).catch(() => []),
      apiFetch(`/milsimGroupsB?path=threat-profiles&group_id=${group.id}`).catch(() => []),
      apiFetch(`/milsimSitrep?path=list&group_id=${group.id}`).then(normaliseSitreps).catch(() => []),
      apiFetch(`/milsimAars?path=list&group_id=${group.id}`).then(normaliseAars).catch(() => []),
      apiFetch(`/milsimLace?path=list&group_id=${group.id}`).then(normaliseLaceReports).catch(() => []),
    ]).then(([s, d, t, sit, aar, lac]) => {
      setSalutes(Array.isArray(s) ? s : (s as any)?.items || []);
      setDocs(Array.isArray(d) ? d : (d as any)?.items || []);
      setThreats(Array.isArray(t) ? t : (t as any)?.items || []);
      setSitreps(Array.isArray(sit) ? sit : (sit as any)?.items || []);
      setAars(Array.isArray(aar) ? aar : (aar as any)?.items || []);
      setLaces(Array.isArray(lac) ? lac : (lac as any)?.items || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [group?.id]);

  const threatCounts = React.useMemo(() => {
    const counts: Record<string, number> = { HOSTILE: 0, UNKNOWN: 0, NEUTRAL: 0, FRIENDLY: 0 };
    threats.forEach(t => { if (counts[t.classification] !== undefined) counts[t.classification]++; });
    return counts;
  }, [threats]);

  const criticalSalutes  = salutes.filter(s => s.threat_level === "CRITICAL" || s.threat_level === "HIGH");
  const enemyContactSits = sitreps.filter(s => s.enemy_contact === "yes" || s.enemy_contact === "possible");
  const allReportDates   = [
    ...salutes.map(r => r.created_date),
    ...sitreps.map(r => r.created_date),
    ...laces.map(r => r.created_date),
  ].sort().reverse();
  const daysSinceLast = allReportDates.length > 0
    ? Math.floor((Date.now() - new Date(allReportDates[0]).getTime()) / 86400000)
    : null;

  const THREAT_COLOR: Record<string, string> = {
    LOW: "text-green-400", MEDIUM: "text-yellow-400", HIGH: "text-orange-400", CRITICAL: "text-red-500"
  };
  const CLASS_COLOR: Record<string, string> = {
    FRIENDLY: "text-blue-400", NEUTRAL: "text-gray-400", UNKNOWN: "text-yellow-400", HOSTILE: "text-red-500"
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Eye className="w-5 h-5 text-primary shrink-0" />
        <h2 className="font-display font-black uppercase tracking-widest text-lg">Intelligence Overview</h2>
        {daysSinceLast !== null && (
          <span className={`ml-auto text-xs font-display font-bold uppercase tracking-widest px-2 py-1 rounded border ${daysSinceLast > 7 ? "border-red-500/40 text-red-400 bg-red-500/10" : "border-green-500/40 text-green-400 bg-green-500/10"}`}>
            Last report: {daysSinceLast === 0 ? "Today" : `${daysSinceLast}d ago`}
          </span>
        )}
      </div>

      {/* ── Stat strip — 6 tiles ── */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {[
          { label: "SALUTE",     value: salutes.length,                     icon: <Crosshair className="w-3.5 h-3.5" />,   color: "border-primary/30 bg-primary/5",        sub: `${criticalSalutes.length} HIGH+` },
          { label: "SITREPs",    value: sitreps.length,                     icon: <Target className="w-3.5 h-3.5" />,      color: "border-amber-500/30 bg-amber-500/5",    sub: `${enemyContactSits.length} contact` },
          { label: "LACE",       value: laces.length,                       icon: <Zap className="w-3.5 h-3.5" />,         color: "border-yellow-500/30 bg-yellow-500/5",  sub: `${laces.filter((l:any) => l.casualty !== "none" && l.casualty != null).length} cas reported` },
          { label: "AARs",       value: aars.length,                        icon: <FileText className="w-3.5 h-3.5" />,    color: "border-blue-500/30 bg-blue-500/5",      sub: `${aars.filter((a:any) => a.outcome === "victory").length} victories` },
          { label: "Intel Docs", value: docs.filter((d:any) => d.is_current !== false).length, icon: <BookOpen className="w-3.5 h-3.5" />, color: "border-purple-500/30 bg-purple-500/5", sub: `${docs.length} archived` },
          { label: "Threats",    value: threatCounts.HOSTILE + threatCounts.UNKNOWN, icon: <ShieldAlert className="w-3.5 h-3.5" />, color: "border-red-500/30 bg-red-500/5", sub: `${threatCounts.HOSTILE} hostile` },
        ].map(stat => (
          <div key={stat.label} className={`rounded-lg border p-3 ${stat.color}`}>
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">{stat.icon}<span className="text-[9px] font-display font-bold uppercase tracking-widest">{stat.label}</span></div>
            <div className="text-xl font-black font-display">{stat.value}</div>
            <div className="text-[9px] text-muted-foreground mt-0.5">{stat.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Contact & Activity row ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* SALUTE — recent */}
        <div className="rounded-lg border border-border/60 bg-card/40 p-4 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Crosshair className="w-3 h-3" /> Recent SALUTE Reports</span>
            <button onClick={() => onNav("salute" as any)} className="text-[10px] text-primary hover:underline font-display font-bold uppercase tracking-widest">All →</button>
          </div>
          {salutes.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3 text-center">No reports filed</p>
          ) : salutes.slice(0, 4).map((r: any) => (
            <div key={r.id} className="flex items-start gap-2.5 py-1.5 border-t border-border/40 first:border-0">
              <span className={`text-[9px] font-display font-black uppercase tracking-widest mt-0.5 px-1.5 py-0.5 rounded border shrink-0 ${r.threat_level === "CRITICAL" ? "border-red-500/50 bg-red-500/10 text-red-400" : r.threat_level === "HIGH" ? "border-orange-500/50 bg-orange-500/10 text-orange-400" : r.threat_level === "MEDIUM" ? "border-yellow-500/50 bg-yellow-500/10 text-yellow-400" : "border-green-500/30 bg-green-500/5 text-green-400"}`}>{r.threat_level}</span>
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{r.activity || "No activity noted"}</p>
                <p className="text-[9px] text-muted-foreground">{r.location} · {r.filed_by_callsign}</p>
              </div>
            </div>
          ))}
        </div>

        {/* SITREP — recent */}
        <div className="rounded-lg border border-border/60 bg-card/40 p-4 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Target className="w-3 h-3" /> Recent SITREPs</span>
            <span className="text-[9px] text-muted-foreground font-display uppercase tracking-widest">J3 → Reports</span>
          </div>
          {sitreps.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3 text-center">No SITREPs filed</p>
          ) : sitreps.slice(0, 4).map((r: any) => (
            <div key={r.id} className="flex items-start gap-2.5 py-1.5 border-t border-border/40 first:border-0">
              <span className={`text-[9px] font-display font-black uppercase tracking-widest mt-0.5 px-1.5 py-0.5 rounded border shrink-0 ${r.enemy_contact === "yes" ? "border-red-500/50 bg-red-500/10 text-red-400" : r.enemy_contact === "possible" ? "border-yellow-500/50 bg-yellow-500/10 text-yellow-400" : "border-green-500/30 bg-green-500/5 text-green-400"}`}>{r.enemy_contact === "yes" ? "CONTACT" : r.enemy_contact === "possible" ? "POSSIBL" : "NO CNT"}</span>
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{r.callsign} — {r.op_name || "Op"}</p>
                <p className="text-[9px] text-muted-foreground truncate">{r.activity || r.mission_status || "—"}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── LACE & AARs row ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* LACE — recent */}
        <div className="rounded-lg border border-border/60 bg-card/40 p-4 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Zap className="w-3 h-3" /> Recent LACE Reports</span>
            <span className="text-[9px] text-muted-foreground font-display uppercase tracking-widest">J3 → Reports</span>
          </div>
          {laces.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3 text-center">No LACE reports filed</p>
          ) : laces.slice(0, 4).map((r: any) => (
            <div key={r.id} className="flex items-center gap-2.5 py-1.5 border-t border-border/40 first:border-0">
              <div className="flex gap-1 shrink-0">
                <span className={`text-[8px] font-display font-bold uppercase px-1 py-0.5 rounded border ${r.liquid === "low" || r.liquid === "critical" ? "border-blue-500/50 bg-blue-500/10 text-blue-400" : "border-border/30 text-muted-foreground"}`}>L</span>
                <span className={`text-[8px] font-display font-bold uppercase px-1 py-0.5 rounded border ${r.ammo === "low" || r.ammo === "critical" ? "border-yellow-500/50 bg-yellow-500/10 text-yellow-400" : "border-border/30 text-muted-foreground"}`}>A</span>
                <span className={`text-[8px] font-display font-bold uppercase px-1 py-0.5 rounded border ${r.casualty !== "none" && r.casualty != null ? "border-red-500/50 bg-red-500/10 text-red-400" : "border-border/30 text-muted-foreground"}`}>C</span>
                <span className={`text-[8px] font-display font-bold uppercase px-1 py-0.5 rounded border ${r.equipment === "degraded" || r.equipment === "critical" ? "border-orange-500/50 bg-orange-500/10 text-orange-400" : "border-border/30 text-muted-foreground"}`}>E</span>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{r.callsign} — {r.op_name || "Op"}</p>
                <p className="text-[9px] text-muted-foreground">{new Date(r.report_time || r.created_date).toLocaleDateString()}</p>
              </div>
            </div>
          ))}
        </div>

        {/* AARs — recent */}
        <div className="rounded-lg border border-border/60 bg-card/40 p-4 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><FileText className="w-3 h-3" /> Recent AARs</span>
            <span className="text-[9px] text-muted-foreground font-display uppercase tracking-widest">J3 → Reports</span>
          </div>
          {aars.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3 text-center">No AARs filed</p>
          ) : aars.slice(0, 4).map((r: any) => (
            <div key={r.id} className="flex items-start gap-2.5 py-1.5 border-t border-border/40 first:border-0">
              <span className={`text-[9px] font-display font-black uppercase tracking-widest mt-0.5 px-1.5 py-0.5 rounded border shrink-0 ${r.outcome === "victory" ? "border-green-500/50 bg-green-500/10 text-green-400" : r.outcome === "defeat" ? "border-red-500/50 bg-red-500/10 text-red-400" : "border-yellow-500/50 bg-yellow-500/10 text-yellow-400"}`}>{r.outcome?.toUpperCase() ?? "—"}</span>
              <div className="min-w-0">
                <p className="text-xs font-medium truncate">{r.title || r.op_name}</p>
                <p className="text-[9px] text-muted-foreground">{r.author_username} · {new Date(r.op_date || r.created_date).toLocaleDateString()}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Threat profiles + Intel Docs row ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* Threat Profiles */}
        <div className="rounded-lg border border-border/60 bg-card/40 p-4 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><ShieldAlert className="w-3 h-3" /> Known Units</span>
            <button onClick={() => onNav("threat_profiles")} className="text-[10px] text-primary hover:underline font-display font-bold uppercase tracking-widest">Manage →</button>
          </div>
          {threats.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3 text-center">No threat profiles filed</p>
          ) : threats.slice(0, 5).map((t: any) => (
            <div key={t.id} className="flex items-center gap-2.5 py-1.5 border-t border-border/40 first:border-0">
              <span className={`text-[9px] font-display font-black uppercase tracking-widest ${CLASS_COLOR[t.classification] || "text-muted-foreground"}`}>{t.classification}</span>
              <span className="text-xs font-medium flex-1 truncate">{t.target_group_name} {t.target_group_tag ? `[${t.target_group_tag}]` : ""}</span>
              <span className={`text-[9px] font-display font-bold uppercase ${THREAT_COLOR[t.threat_level] || "text-muted-foreground"}`}>{t.threat_level}</span>
            </div>
          ))}
        </div>

        {/* Intel Docs */}
        <div className="rounded-lg border border-border/60 bg-card/40 p-4 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><BookOpen className="w-3 h-3" /> Intelligence Archive</span>
            <button onClick={() => onNav("intel_docs")} className="text-[10px] text-primary hover:underline font-display font-bold uppercase tracking-widest">View →</button>
          </div>
          {docs.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3 text-center">No documents archived</p>
          ) : docs.slice(0, 5).map((d: any) => (
            <div key={d.id} className="flex items-start gap-2.5 py-1.5 border-t border-border/40 first:border-0">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium truncate">{d.title}</p>
                <div className="flex gap-2 mt-0.5">
                  <span className={`text-[9px] font-display font-bold uppercase ${d.classification === "TOP SECRET" ? "text-red-400" : d.classification === "SECRET" ? "text-orange-400" : d.classification === "RESTRICTED" ? "text-yellow-400" : "text-green-400"}`}>{d.classification}</span>
                  <span className="text-[9px] text-muted-foreground">· {d.doc_type}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── SALUTE Reports Panel ─────────────────────────────────────────────────────
function SaluteReportsPanel({ group, showMsg }: any) {
  const [reports, setReports] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [filterThreat, setFilterThreat] = React.useState("ALL");
  const { user } = useAuth();
  const [roster, setRoster] = React.useState<any>(null);

  const emptyForm = { size: "", activity: "", location: "", unit: "", time_observed: "", equipment: "", threat_level: "MEDIUM", game: "", map: "", notes: "" };
  const [form, setForm] = React.useState({ ...emptyForm });
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!group?.id) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/milsimGroupsB?path=salute-reports&group_id=${group.id}`);
      setReports(Array.isArray(res) ? res : res?.items || []);
    } catch {}
    setLoading(false);
  }, [group?.id]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    if (!group?.id || !user?.id) return;
    apiFetch(`/milsimRosters?group_id=${group.id}&user_id=${user.id}`).then((r: any) => {
      const items = Array.isArray(r) ? r : r?.items || [];
      if (items[0]) setRoster(items[0]);
    }).catch(() => {});
  }, [group?.id, user?.id]);

  const handleSave = async () => {
    if (!form.activity || !form.location) { showMsg("Activity and Location are required"); return; }
    setSaving(true);
    try {
      await apiFetch("/milsimGroupsB?path=salute-reports/create", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          group_id: group.id,
          filed_by: user?.id,
          filed_by_callsign: roster?.callsign || user?.username || "Unknown",
          report_time: new Date().toISOString(),
        }),
      });
      showMsg("SALUTE report filed");
      setShowForm(false);
      setForm({ ...emptyForm });
      load();
    } catch { showMsg("Failed to file report", "error"); }
    setSaving(false);
  };

  const filtered = filterThreat === "ALL" ? reports : reports.filter(r => r.threat_level === filterThreat);

  const THREAT_OPTS = ["ALL", "CRITICAL", "HIGH", "MEDIUM", "LOW"];
  const THREAT_COLOR: Record<string, string> = {
    CRITICAL: "text-red-400 border-red-500/50 bg-red-500/10",
    HIGH: "text-orange-400 border-orange-500/50 bg-orange-500/10",
    MEDIUM: "text-yellow-400 border-yellow-500/50 bg-yellow-500/10",
    LOW: "text-green-400 border-green-500/30 bg-green-500/5",
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest">SALUTE Reports</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Size · Activity · Location · Unit · Time · Equipment</p>
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <select value={filterThreat} onChange={e => setFilterThreat(e.target.value)} className="text-xs bg-secondary border border-border/60 rounded px-2 py-1.5 font-display uppercase tracking-widest">
            {THREAT_OPTS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1.5 text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded font-display font-bold uppercase tracking-widest hover:opacity-90">
            <Plus className="w-3.5 h-3.5" /> File Report
          </button>
        </div>
      </div>

      {showForm && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-5 space-y-4">
          <p className="text-xs font-display font-bold uppercase tracking-widest text-primary">New SALUTE Report</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: "size", label: "Size (e.g. 4-man patrol)" },
              { key: "activity", label: "Activity *" },
              { key: "location", label: "Location / Grid *" },
              { key: "unit", label: "Unit / Callsign" },
              { key: "time_observed", label: "Time Observed" },
              { key: "equipment", label: "Equipment" },
              { key: "game", label: "Game" },
              { key: "map", label: "Map / AO" },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">{f.label}</label>
                <input value={(form as any)[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm" />
              </div>
            ))}
            <div>
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Threat Level</label>
              <select value={form.threat_level} onChange={e => setForm(p => ({ ...p, threat_level: e.target.value }))} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm">
                {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Additional Notes</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm resize-none" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground text-xs px-4 py-2 rounded font-display font-bold uppercase tracking-widest hover:opacity-90 disabled:opacity-50">
              {saving ? "Filing..." : "File Report"}
            </button>
            <button onClick={() => setShowForm(false)} className="text-xs px-4 py-2 rounded border border-border/60 font-display font-bold uppercase tracking-widest hover:bg-secondary">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Crosshair className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No reports found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <div key={r.id} className="rounded-lg border border-border/60 bg-card/40 p-4">
              <div className="flex items-start gap-3 flex-wrap">
                <span className={`text-[10px] font-display font-black uppercase tracking-widest px-2 py-1 rounded border shrink-0 ${THREAT_COLOR[r.threat_level] || "text-muted-foreground border-border/40"}`}>{r.threat_level}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{r.activity || "—"}</span>
                    <span className="text-xs text-muted-foreground">@ {r.location || "—"}</span>
                    {r.game && <span className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">{r.game}{r.map ? ` · ${r.map}` : ""}</span>}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                    {r.size && <span><span className="font-bold text-foreground/70">S:</span> {r.size}</span>}
                    {r.unit && <span><span className="font-bold text-foreground/70">U:</span> {r.unit}</span>}
                    {r.time_observed && <span><span className="font-bold text-foreground/70">T:</span> {r.time_observed}</span>}
                    {r.equipment && <span><span className="font-bold text-foreground/70">E:</span> {r.equipment}</span>}
                  </div>
                  {r.notes && <p className="text-xs text-muted-foreground mt-2 border-t border-border/30 pt-2">{r.notes}</p>}
                </div>
                <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                  <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground">{r.filed_by_callsign}</p>
                  <p className="text-[10px] text-muted-foreground">{r.report_time ? new Date(r.report_time).toLocaleDateString() : ""}</p>
                  <button
                    onClick={() => archiveToIntel(group, user, roster, {
                      title: `SALUTE — ${r.activity || "Contact"} @ ${r.location || "Unknown"} (${r.threat_level})`,
                      doc_type: "SALUTE REPORT",
                      classification: r.threat_level === "CRITICAL" || r.threat_level === "HIGH" ? "SECRET" : "RESTRICTED",
                      content: `Threat: ${r.threat_level}
Size: ${r.size || "—"}
Activity: ${r.activity || "—"}
Location: ${r.location || "—"}
Unit: ${r.unit || "—"}
Time Observed: ${r.time_observed || "—"}
Equipment: ${r.equipment || "—"}
Game: ${r.game || "—"} | Map: ${r.map || "—"}
Notes: ${r.notes || "—"}
Filed by: ${r.filed_by_callsign}`,
                      tags: ["salute", r.threat_level?.toLowerCase()].filter(Boolean),
                    }, showMsg)}
                    className="text-[10px] flex items-center gap-1 px-2 py-1 rounded border border-primary/30 text-primary hover:bg-primary/10 font-display font-bold uppercase tracking-widest transition-colors"
                  >
                    <Archive className="w-3 h-3" /> Archive to Intel
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Intel Documents Panel ─────────────────────────────────────────────────────
function IntelDocumentsPanel({ group, showMsg }: any) {
  const [docs, setDocs] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [viewing, setViewing] = React.useState<any>(null);
  const [filterClass, setFilterClass] = React.useState("ALL");
  const [filterDocType, setFilterDocType] = React.useState("ALL");
  const { user } = useAuth();
  const [roster, setRoster] = React.useState<any>(null);

  const emptyForm = { title: "", classification: "RESTRICTED", doc_type: "INTELLIGENCE SUMMARY", content: "", tags: "", is_current: true };
  const ALL_DOC_TYPES = [
    "INTELLIGENCE SUMMARY",
    "AAR",
    "WARNO",
    "LACE REPORT",
    "SITREP",
    "SALUTE REPORT",
    "CONDUCT REPORT",
    "MEDEVAC REPORT",
    "THREAT ASSESSMENT",
    "OPERATIONAL ORDER",
    "OTHER",
  ];
  const [form, setForm] = React.useState({ ...emptyForm });
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!group?.id) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/milsimGroupsB?path=intel-docs&group_id=${group.id}`);
      setDocs(Array.isArray(res) ? res : res?.items || []);
    } catch {}
    setLoading(false);
  }, [group?.id]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    if (!group?.id || !user?.id) return;
    apiFetch(`/milsimRosters?group_id=${group.id}&user_id=${user.id}`).then((r: any) => {
      const items = Array.isArray(r) ? r : r?.items || [];
      if (items[0]) setRoster(items[0]);
    }).catch(() => {});
  }, [group?.id, user?.id]);

  const handleSave = async () => {
    if (!form.title) { showMsg("Title is required"); return; }
    setSaving(true);
    try {
      await apiFetch("/milsimGroupsB?path=intel-docs/create", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          tags: form.tags ? form.tags.split(",").map((t: string) => t.trim()).filter(Boolean) : [],
          group_id: group.id,
          authored_by: user?.id,
          authored_by_callsign: roster?.callsign || user?.username || "Unknown",
        }),
      });
      showMsg("Document filed");
      setShowForm(false);
      setForm({ ...emptyForm });
      load();
    } catch { showMsg("Failed to save document", "error"); }
    setSaving(false);
  };

  const CLASS_COLOR: Record<string, string> = {
    "TOP SECRET": "text-red-400 border-red-500/50 bg-red-500/10",
    "SECRET": "text-orange-400 border-orange-500/50 bg-orange-500/10",
    "RESTRICTED": "text-yellow-400 border-yellow-500/50 bg-yellow-500/10",
    "UNCLASSIFIED": "text-green-400 border-green-500/30 bg-green-500/5",
  };

  const filtered = docs
    .filter(d => filterClass === "ALL" || d.classification === filterClass)
    .filter(d => filterDocType === "ALL" || d.doc_type === filterDocType);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest">Intel Documents</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Threat assessments, area studies, INTSUM, ORBAT, SIGINT</p>
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <select value={filterDocType} onChange={e => setFilterDocType(e.target.value)} className="text-xs bg-secondary border border-border/60 rounded px-2 py-1.5 font-display uppercase tracking-widest">
            <option value="ALL">All Types</option>
            {ALL_DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterClass} onChange={e => setFilterClass(e.target.value)} className="text-xs bg-secondary border border-border/60 rounded px-2 py-1.5 font-display uppercase tracking-widest">
            {["ALL", "TOP SECRET", "SECRET", "RESTRICTED", "UNCLASSIFIED"].map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1.5 text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded font-display font-bold uppercase tracking-widest hover:opacity-90">
            <Plus className="w-3.5 h-3.5" /> New Document
          </button>
        </div>
      </div>

      {showForm && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-5 space-y-4">
          <p className="text-xs font-display font-bold uppercase tracking-widest text-primary">New Intel Document</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Title *</label>
              <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Classification</label>
              <select value={form.classification} onChange={e => setForm(p => ({ ...p, classification: e.target.value }))} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm">
                {["UNCLASSIFIED", "RESTRICTED", "SECRET", "TOP SECRET"].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Document Type</label>
              <select value={form.doc_type} onChange={e => setForm(p => ({ ...p, doc_type: e.target.value }))} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm">
                {ALL_DOC_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Content</label>
              <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} rows={6} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm resize-none font-mono text-xs" placeholder="Classified intelligence content..." />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Tags (comma-separated)</label>
              <input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm" placeholder="e.g. insurgent, grid-443, arma3" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground text-xs px-4 py-2 rounded font-display font-bold uppercase tracking-widest hover:opacity-90 disabled:opacity-50">
              {saving ? "Saving..." : "File Document"}
            </button>
            <button onClick={() => setShowForm(false)} className="text-xs px-4 py-2 rounded border border-border/60 font-display font-bold uppercase tracking-widest hover:bg-secondary">Cancel</button>
          </div>
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setViewing(null)}>
          <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <span className={`text-[10px] font-display font-black uppercase tracking-widest px-2 py-1 rounded border ${CLASS_COLOR[viewing.classification] || ""}`}>{viewing.classification}</span>
                <h3 className="font-display font-black uppercase tracking-widest text-lg mt-2">{viewing.title}</h3>
                <p className="text-xs text-muted-foreground">{viewing.doc_type} · {viewing.authored_by_callsign} · {viewing.created_date ? new Date(viewing.created_date).toLocaleDateString() : ""}</p>
              </div>
              <button onClick={() => setViewing(null)} className="text-muted-foreground hover:text-foreground">✕</button>
            </div>
            {viewing.tags?.length > 0 && (
              <div className="flex gap-1 flex-wrap">{viewing.tags.map((t: string) => <span key={t} className="text-[10px] bg-secondary px-2 py-0.5 rounded">{t}</span>)}</div>
            )}
            <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap font-mono text-xs text-foreground/80 bg-secondary/30 rounded p-4">{viewing.content || "No content."}</div>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <FileText className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No documents in archive</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(d => (
            <button key={d.id} onClick={() => setViewing(d)} className="text-left rounded-lg border border-border/60 bg-card/40 hover:bg-card/80 p-4 transition-colors">
              <div className="flex items-start gap-3">
                <FileText className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{d.title}</p>
                  <div className="flex gap-2 mt-1 items-center flex-wrap">
                    <span className={`text-[10px] font-display font-black uppercase tracking-widest px-1.5 py-0.5 rounded border ${CLASS_COLOR[d.classification] || "text-muted-foreground border-border/40"}`}>{d.classification}</span>
                    <span className="text-[10px] text-muted-foreground">{d.doc_type}</span>
                  </div>
                  {d.tags?.length > 0 && (
                    <div className="flex gap-1 flex-wrap mt-1.5">{d.tags.slice(0, 3).map((t: string) => <span key={t} className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">{t}</span>)}</div>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-1">{d.authored_by_callsign} · {d.created_date ? new Date(d.created_date).toLocaleDateString() : ""}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Threat Profiles Panel ─────────────────────────────────────────────────────
function ThreatProfilesPanel({ group, showMsg }: any) {
  const [profiles, setProfiles] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [filterClass, setFilterClass] = React.useState("ALL");
  const { user } = useAuth();
  const [roster, setRoster] = React.useState<any>(null);

  const emptyForm = { target_group_name: "", target_group_tag: "", classification: "UNKNOWN", threat_level: "MEDIUM", known_games: "", known_tactics: "", known_equipment: "", engagement_history: "", analyst_notes: "" };
  const [form, setForm] = React.useState({ ...emptyForm });
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!group?.id) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/milsimGroupsB?path=threat-profiles&group_id=${group.id}`);
      setProfiles(Array.isArray(res) ? res : res?.items || []);
    } catch {}
    setLoading(false);
  }, [group?.id]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    if (!group?.id || !user?.id) return;
    apiFetch(`/milsimRosters?group_id=${group.id}&user_id=${user.id}`).then((r: any) => {
      const items = Array.isArray(r) ? r : r?.items || [];
      if (items[0]) setRoster(items[0]);
    }).catch(() => {});
  }, [group?.id, user?.id]);

  const handleSave = async () => {
    if (!form.target_group_name) { showMsg("Target group name is required"); return; }
    setSaving(true);
    try {
      await apiFetch("/milsimGroupsB?path=threat-profiles/create", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          known_games: form.known_games ? form.known_games.split(",").map((g: string) => g.trim()).filter(Boolean) : [],
          group_id: group.id,
          created_by: user?.id,
          created_by_callsign: roster?.callsign || user?.username || "Unknown",
          last_contact_date: new Date().toISOString(),
        }),
      });
      showMsg("Threat profile created");
      setShowForm(false);
      setForm({ ...emptyForm });
      load();
    } catch { showMsg("Failed to create profile", "error"); }
    setSaving(false);
  };

  const CLASS_COLOR: Record<string, string> = {
    FRIENDLY: "text-blue-400 border-blue-500/40 bg-blue-500/10",
    NEUTRAL: "text-gray-400 border-gray-500/40 bg-gray-500/10",
    UNKNOWN: "text-yellow-400 border-yellow-500/40 bg-yellow-500/10",
    HOSTILE: "text-red-400 border-red-500/40 bg-red-500/10",
  };
  const THREAT_DOT: Record<string, string> = { LOW: "bg-green-400", MEDIUM: "bg-yellow-400", HIGH: "bg-orange-400", CRITICAL: "bg-red-500" };

  const filtered = filterClass === "ALL" ? profiles : profiles.filter(p => p.classification === filterClass);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest">Threat Profiles</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Track known units — hostile, neutral, friendly, or unknown</p>
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <select value={filterClass} onChange={e => setFilterClass(e.target.value)} className="text-xs bg-secondary border border-border/60 rounded px-2 py-1.5 font-display uppercase tracking-widest">
            {["ALL", "HOSTILE", "UNKNOWN", "NEUTRAL", "FRIENDLY"].map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1.5 text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded font-display font-bold uppercase tracking-widest hover:opacity-90">
            <Plus className="w-3.5 h-3.5" /> New Profile
          </button>
        </div>
      </div>

      {showForm && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-5 space-y-4">
          <p className="text-xs font-display font-bold uppercase tracking-widest text-primary">New Threat Profile</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Unit Name *</label>
              <input value={form.target_group_name} onChange={e => setForm(p => ({ ...p, target_group_name: e.target.value }))} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Tag / Abbreviation</label>
              <input value={form.target_group_tag} onChange={e => setForm(p => ({ ...p, target_group_tag: e.target.value }))} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm" placeholder="e.g. [WOLF]" />
            </div>
            <div>
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Classification</label>
              <select value={form.classification} onChange={e => setForm(p => ({ ...p, classification: e.target.value }))} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm">
                {["FRIENDLY", "NEUTRAL", "UNKNOWN", "HOSTILE"].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Threat Level</label>
              <select value={form.threat_level} onChange={e => setForm(p => ({ ...p, threat_level: e.target.value }))} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm">
                {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Known Games (comma-separated)</label>
              <input value={form.known_games} onChange={e => setForm(p => ({ ...p, known_games: e.target.value }))} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm" placeholder="e.g. Arma 3, Squad" />
            </div>
            <div>
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Known Tactics</label>
              <textarea value={form.known_tactics} onChange={e => setForm(p => ({ ...p, known_tactics: e.target.value }))} rows={3} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm resize-none" />
            </div>
            <div>
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Known Equipment</label>
              <textarea value={form.known_equipment} onChange={e => setForm(p => ({ ...p, known_equipment: e.target.value }))} rows={3} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm resize-none" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Engagement History</label>
              <textarea value={form.engagement_history} onChange={e => setForm(p => ({ ...p, engagement_history: e.target.value }))} rows={3} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm resize-none" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1">Analyst Notes</label>
              <textarea value={form.analyst_notes} onChange={e => setForm(p => ({ ...p, analyst_notes: e.target.value }))} rows={3} className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm resize-none" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground text-xs px-4 py-2 rounded font-display font-bold uppercase tracking-widest hover:opacity-90 disabled:opacity-50">
              {saving ? "Saving..." : "Create Profile"}
            </button>
            <button onClick={() => setShowForm(false)} className="text-xs px-4 py-2 rounded border border-border/60 font-display font-bold uppercase tracking-widest hover:bg-secondary">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div> : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <ShieldAlert className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No profiles yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(p => (
            <div key={p.id} className="rounded-lg border border-border/60 bg-card/40 p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display font-black uppercase tracking-wider text-sm">{p.target_group_name}</span>
                    {p.target_group_tag && <span className="text-xs text-muted-foreground">[{p.target_group_tag}]</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[10px] font-display font-black uppercase tracking-widest px-2 py-0.5 rounded border ${CLASS_COLOR[p.classification] || "text-muted-foreground border-border/40"}`}>{p.classification}</span>
                    <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <span className={`w-2 h-2 rounded-full ${THREAT_DOT[p.threat_level] || "bg-gray-400"}`} />
                      {p.threat_level}
                    </span>
                  </div>
                </div>
              </div>
              {p.known_games?.length > 0 && (
                <div className="flex gap-1 flex-wrap">{p.known_games.map((g: string) => <span key={g} className="text-[10px] bg-secondary px-1.5 py-0.5 rounded">{g}</span>)}</div>
              )}
              {p.known_tactics && <p className="text-xs text-muted-foreground"><span className="font-bold text-foreground/70">Tactics:</span> {p.known_tactics}</p>}
              {p.known_equipment && <p className="text-xs text-muted-foreground"><span className="font-bold text-foreground/70">Equipment:</span> {p.known_equipment}</p>}
              {p.analyst_notes && <p className="text-xs text-muted-foreground border-t border-border/30 pt-2 italic">{p.analyst_notes}</p>}
              <p className="text-[10px] text-muted-foreground">Filed by {p.created_by_callsign}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PJHQ CONTAINER TABS — second-level navigation shell + leaf component wiring
// ALL sub-tabs call LEAF components directly — no old wrapper tabs used
// ═══════════════════════════════════════════════════════════════════════════════

function PJHQShell({ subs, children, fullHeight }: {
  subs: { id: string; label: string; icon: React.ReactNode; pro?: boolean }[];
  children: (sub: string) => React.ReactNode;
  fullHeight?: boolean;
}) {
  const [sub, setSub] = React.useState(subs[0]?.id || "");
  return (
    <div className="flex gap-0 min-h-[600px]">
      <div className="shrink-0 w-44 border-r border-border/60 pr-2 space-y-0.5 pt-1">
        {subs.map((s) => (
          <button
            key={s.id}
            onClick={() => setSub(s.id)}
            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded text-left font-display font-bold uppercase tracking-widest text-[10px] transition-colors ${
              sub === s.id
                ? "bg-primary/15 text-primary border-l-2 border-primary pl-2.5"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
            }`}
          >
            {s.icon}
            <span className="truncate">{s.label}</span>
            {s.pro && <Crown className="ml-auto w-3 h-3 text-yellow-400 shrink-0" />}
          </button>
        ))}
      </div>
      <div className={`flex-1 pl-5 min-w-0 ${fullHeight ? "flex flex-col overflow-hidden" : "overflow-y-auto"}`}>
        {children(sub)}
      </div>
    </div>
  );
}

// ── J1 Personnel ─────────────────────────────────────────────────────────────
function PersonnelTab({ group, onUpdated, showMsg, globalIsPro }: any) {
  return (
    <PJHQShell key="personnel" subs={[
      { id: "roster",     label: "Roster",           icon: <Users className="w-3.5 h-3.5" /> },
      { id: "roles",      label: "Roles",            icon: <Crosshair className="w-3.5 h-3.5" /> },
      { id: "ranks",      label: "Ranks",            icon: <Award className="w-3.5 h-3.5" /> },
      { id: "reputation", label: "Service Files",    icon: <Star className="w-3.5 h-3.5" /> },
      { id: "discharges", label: "Discharges",       icon: <UserMinus className="w-3.5 h-3.5" /> },
    ]}>
      {(sub) => (<>
        {sub === "roster"     && <RosterTab         group={group} onUpdated={onUpdated} showMsg={showMsg} />}
        {sub === "roles"      && <RolesTab          group={group} onUpdated={onUpdated} showMsg={showMsg} />}
        {sub === "ranks"      && <RanksTab          group={group} onUpdated={onUpdated} showMsg={showMsg} />}
        {sub === "reputation" && <ReputationTab     group={group} />}
        {sub === "discharges" && <DischargesPanel   group={group} showMsg={showMsg} />}
      </>)}
    </PJHQShell>
  );
}

// ── Shared helper: Archive any report to J2 Intel Documents ─────────────────
async function archiveToIntel(group: any, user: any, roster: any, payload: {
  title: string; doc_type: string; classification: string; content: string; tags?: string[];
}, showMsg: (m: string, t?: "success" | "error") => void) {
  try {
    await apiFetch("/milsimGroupsB?path=intel-docs/create", {
      method: "POST",
      body: JSON.stringify({
        group_id: group.id,
        title: payload.title,
        doc_type: payload.doc_type,
        classification: payload.classification,
        content: payload.content,
        tags: payload.tags ?? [],
        authored_by: user?.id,
        authored_by_callsign: roster?.callsign || user?.username || "Unknown",
        is_current: true,
      }),
    });
    showMsg("Archived to J2 Intel", "success");
  } catch { showMsg("Failed to archive to Intel", "error"); }
}

// ── Ops Reports Panel (collapsed sub-tabs) ────────────────────────────────────
function OpsReportsPanel({ group, showMsg }: any) {
  return (
    <PJHQShell key="reports" subs={[
      { id: "aars",    label: "AARs",            icon: <ClipboardList className="w-3.5 h-3.5" /> },
      { id: "warnos",  label: "WARNOs",          icon: <AlertTriangle className="w-3.5 h-3.5" /> },
      { id: "lace",    label: "LACE",            icon: <Radio className="w-3.5 h-3.5" /> },
      { id: "sitrep",  label: "SITREPs",         icon: <Target className="w-3.5 h-3.5" /> },
      { id: "salute",  label: "SALUTE",          icon: <Crosshair className="w-3.5 h-3.5" /> },
      { id: "conduct", label: "Conduct",         icon: <ShieldAlert className="w-3.5 h-3.5" /> },
      { id: "medevac", label: "MEDEVAC",         icon: <Radio className="w-3.5 h-3.5" /> },
      { id: "intel",   label: "Op Intel",        icon: <BrainCircuit className="w-3.5 h-3.5" /> },
    ]}>
      {(sub) => (<>
        {sub === "aars"    && <AARsTab              group={group} showMsg={showMsg} />}
        {sub === "warnos"  && <WarnosTab            group={group} showMsg={showMsg} />}
        {sub === "lace"    && <LaceTab              group={group} showMsg={showMsg} />}
        {sub === "sitrep"  && <SitrepTab            group={group} showMsg={showMsg} />}
        {sub === "salute"  && <SaluteReportsPanel   group={group} showMsg={showMsg} />}
        {sub === "conduct" && <ConductReportTab     group={group} showMsg={showMsg} />}
        {sub === "medevac" && <MedevacTab           group={group} showMsg={showMsg} />}
        {sub === "intel"   && <OpIntelTab           group={group} showMsg={showMsg} />}
      </>)}
    </PJHQShell>
  );
}

// ── COC Standalone Tab ───────────────────────────────────────────────────────
function COCStandaloneTab({ group, onUpdated, showMsg }: any) {
  return (
    <div className="p-4">
      <ChainOfCommandTab group={group} onUpdated={onUpdated} showMsg={showMsg} />
    </div>
  );
}

// ── J3 Operations Room ────────────────────────────────────────────────────────
function OperationsRoomTab({ group, showMsg, globalIsPro }: any) {
  return (
    <PJHQShell key="operations" subs={[
      { id: "ops",      label: "Operations",   icon: <Siren className="w-3.5 h-3.5" /> },
      { id: "calendar", label: "Ops Calendar", icon: <CalendarDays className="w-3.5 h-3.5" />, pro: true },
      { id: "campaigns",label: "Campaigns",    icon: <Flag className="w-3.5 h-3.5" />, pro: true },
    ]}>
      {(sub) => (<>
        {sub === "ops"       && <OpsTab              group={group} showMsg={showMsg} onNavTo={() => {}} />}
        {sub === "calendar"  && <ActivityCalendarTab group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
        {sub === "campaigns" && <CampaignsTab        group={group} globalIsPro={globalIsPro} />}
      </>)}
    </PJHQShell>
  );
}

// ── J4 Logistics ──────────────────────────────────────────────────────────────
function LogisticsTab({ group, showMsg, globalIsPro }: any) {
  return (
    <PJHQShell key="logistics" subs={[
      { id: "loadouts", label: "Loadout Kits", icon: <Package className="w-3.5 h-3.5" /> },
      { id: "servers",  label: "Game Servers", icon: <Server className="w-3.5 h-3.5" /> },
      { id: "duty",     label: "Duty Roster",  icon: <ClipboardList className="w-3.5 h-3.5" />, pro: true },
    ]}>
      {(sub) => (<>
        {sub === "loadouts" && <LoadoutKitsTab group={group} showMsg={showMsg} />}
        {sub === "servers"  && <GameServersTab group={group} showMsg={showMsg} />}
        {sub === "duty"     && <DutyRosterTab  group={group} globalIsPro={globalIsPro} showMsg={showMsg} />}
      </>)}
    </PJHQShell>
  );
}

// ── J5 Planning ───────────────────────────────────────────────────────────────
function PlanningTab({ group, showMsg, globalIsPro }: any) {
  return (
    <PJHQShell key="planning" fullHeight subs={[
      { id: "planning",   label: "Planning Room",    icon: <Layers className="w-3.5 h-3.5" />, pro: true },
      { id: "comms",      label: "Comms Plan",       icon: <PhoneCall className="w-3.5 h-3.5" />, pro: true },
      { id: "briefings",  label: "Briefings",        icon: <FileText className="w-3.5 h-3.5" /> },
      { id: "pub_plans",  label: "Published Plans",  icon: <Send className="w-3.5 h-3.5" /> },
    ]}>
      {(sub) => (<>
        {sub === "planning"   && <OpPlanningRoomTab group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
        {sub === "comms"      && <CommsPlanTab      group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
        {sub === "briefings"  && <BriefingsTab      group={group} showMsg={showMsg} />}
        {sub === "pub_plans"  && <PublishedPlansPanel group={group} showMsg={showMsg} />}
      </>)}
    </PJHQShell>
  );
}

// ─── Published Plans Panel ──────────────────────────────────────────────────
function PublishedPlansPanel({ group, showMsg }: any) {
  const [ops, setOps] = React.useState<any[]>([]);
  const [briefings, setBriefings] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!group?.id) return;
    Promise.all([
      apiFetch<any[]>(`/milsimOps?path=${group.id}/list`).catch(() => []),
      apiFetch<any>(`/milsimBriefings?path=list&group_id=${group.id}`).catch(() => ({ briefings: [] })),
    ]).then(([opsRes, brRes]) => {
      const opList = Array.isArray(opsRes) ? opsRes : [];
      const brList: any[] = normaliseBriefings(brRes);
      setOps(opList);
      // Filter to planning room saves + comms plans
      const published = brList.filter((b: any) =>
        b.title?.startsWith("COMMS PLAN:")
      );
      setBriefings(published);
      setLoading(false);
    });
  }, [group?.id]);

  // Also pull plan data from op descriptions
  const [planOps, setPlanOps] = React.useState<any[]>([]);
  React.useEffect(() => {
    if (!ops.length) return;
    const withPlans = ops.filter((op: any) => {
      try {
        const d = JSON.parse(op.description ?? "{}");
        return d.__planFields && Object.keys(d.__planFields).length > 0;
      } catch { return false; }
    });
    setPlanOps(withPlans);
  }, [ops]);

  const getOpName = (opId: string) => ops.find((o: any) => o.id === opId)?.name ?? opId;

  if (loading) return <div className="py-16 text-center text-muted-foreground font-sans text-sm">Loading plans...</div>;

  const hasAnything = planOps.length > 0 || briefings.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-display font-bold text-base uppercase tracking-widest text-foreground flex items-center gap-2">
          <Send className="w-4 h-4 text-primary" /> Published Plans
        </h3>
        <p className="text-xs text-muted-foreground font-sans mt-0.5">
          Op Planning Room documents and Comms Plans published by HQ — linked to operations
        </p>
      </div>

      {!hasAnything && (
        <div className="py-16 text-center border border-dashed border-border rounded-lg text-muted-foreground">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-display text-sm uppercase tracking-widest">No plans published yet</p>
          <p className="text-xs mt-1 font-sans">Use the Planning Room or Comms Plan tab and click <strong>Publish &amp; Notify</strong></p>
        </div>
      )}

      {/* Planning Room Docs */}
      {planOps.length > 0 && (
        <div className="space-y-3">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Layers className="w-3 h-3" /> Op Planning Room</p>
          {planOps.map((op: any) => {
            let planData: any = {};
            try { planData = JSON.parse(op.description ?? "{}"); } catch {}
            const filled = Object.values(planData.__planFields ?? {}).filter((v: any) => v?.trim()).length;
            const total = PLAN_PHASES.flatMap((p: any) => p.items.flatMap((i: any) => i.fields)).length;
            const pct = Math.round((filled / total) * 100);
            const isOpen = expanded === `plan-${op.id}`;
            return (
              <div key={op.id} className="rounded-lg border border-border bg-secondary/10 overflow-hidden">
                <button
                  onClick={() => setExpanded(isOpen ? null : `plan-${op.id}`)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-secondary/20 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                    <div>
                      <p className="text-xs font-display font-bold uppercase tracking-widest text-foreground">{op.name}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">
                        {op.scheduled_at ? new Date(op.scheduled_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "Date TBC"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className={`text-xs font-display font-bold ${pct >= 80 ? "text-green-400" : pct >= 40 ? "text-amber-400" : "text-muted-foreground"}`}>{pct}% complete</p>
                      <div className="w-20 h-1 rounded-full bg-secondary mt-1"><div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} /></div>
                    </div>
                  </div>
                </button>
                {isOpen && (
                  <div className="divide-y divide-border/40 border-t border-border">
                    {PLAN_PHASES.map((phase: any) => {
                      const phaseEntries = phase.items.flatMap((item: any) =>
                        item.fields
                          .map((f: any) => ({ label: f.label, value: planData.__planFields?.[`${phase.id}::${item.id}::${f.key}`] ?? "" }))
                          .filter((e: any) => e.value?.trim())
                          .map((e: any) => ({ ...e, section: item.title }))
                      );
                      if (!phaseEntries.length) return null;
                      return (
                        <div key={phase.id} className="px-4 py-3">
                          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary mb-2">{phase.label}</p>
                          <div className="space-y-2">
                            {phaseEntries.map((e: any, idx: number) => (
                              <div key={idx}>
                                <p className="text-[10px] text-muted-foreground font-sans uppercase">{e.section} — {e.label}</p>
                                <p className="text-xs font-sans text-foreground whitespace-pre-wrap">{e.value}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {planData.__planChat?.length > 0 && (
                      <div className="px-4 py-3">
                        <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Planning Discussion ({planData.__planChat.length} messages)</p>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {planData.__planChat.map((msg: any) => (
                            <div key={msg.id} className="flex gap-2 items-start">
                              <span className="text-[10px] font-display font-bold text-primary shrink-0">{msg.callsign}</span>
                              <span className="text-xs font-sans text-foreground/80">{msg.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Comms Plans */}
      {briefings.length > 0 && (
        <div className="space-y-3">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Antenna className="w-3 h-3" /> Comms Plans</p>
          {briefings.map((b: any) => {
            let commsData: any = {};
            try { commsData = JSON.parse(b.content ?? "{}"); } catch {}
            const isOpen = expanded === `comms-${b.id}`;
            return (
              <div key={b.id} className="rounded-lg border border-border bg-secondary/10 overflow-hidden">
                <button
                  onClick={() => setExpanded(isOpen ? null : `comms-${b.id}`)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-secondary/20 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                    <div>
                      <p className="text-xs font-display font-bold uppercase tracking-widest text-foreground">{b.title}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">Linked Op: {getOpName(b.op_id)}</p>
                    </div>
                  </div>
                  <span className="text-[9px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border border-primary/30 text-primary bg-primary/10">
                    {commsData.nets?.length ?? 0} nets
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-border divide-y divide-border/40">
                    {commsData.nets?.length > 0 && (
                      <div className="px-4 py-3 space-y-2">
                        <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary flex items-center gap-1"><Radio className="w-3 h-3" /> Radio Nets</p>
                        {commsData.nets.map((net: any) => (
                          <div key={net.id} className="flex items-center gap-4 text-xs font-sans">
                            <span className={`text-[9px] font-display font-bold uppercase px-1.5 py-0.5 rounded border shrink-0 ${net.primary ? "text-primary border-primary/30 bg-primary/10" : "text-muted-foreground border-border bg-secondary"}`}>
                              {net.primary ? "PRIMARY" : "ALT"}
                            </span>
                            <span className="font-bold text-foreground">{net.name}</span>
                            <span className="text-muted-foreground font-mono">{net.callsign}</span>
                            <span className="text-muted-foreground font-mono">{net.freq} {net.band}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {commsData.codewords?.length > 0 && (
                      <div className="px-4 py-3 space-y-1">
                        <p className="text-[10px] font-display font-bold uppercase tracking-widest text-primary flex items-center gap-1"><Flag className="w-3 h-3" /> Codewords</p>
                        {commsData.codewords.map((cw: any) => (
                          <div key={cw.id} className="flex items-center gap-3 text-xs font-sans">
                            <span className="font-mono font-bold text-foreground w-24 shrink-0">{cw.word}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="text-foreground/80">{cw.meaning}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Thin wrapper so Battle Planner renders cleanly inside the PJHQ shell
function BattlePlannerPanel({ group, showMsg }: any) {
  return (
    <div className="flex-1 flex flex-col" style={{height:"calc(100vh - 220px)"}}>
      <TacticalPlanner group={group} showMsg={showMsg} />
    </div>
  );
}

// ── AO Map — standalone full-page tab ────────────────────────────────────────
function AOMapFullTab({ group, showMsg }: any) {
  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 160px)" }}>
      <TacticalPlanner group={group} showMsg={showMsg} />
    </div>
  );
}

// ── J6 Comms & Info Systems ───────────────────────────────────────────────────

// ── Social Links Tab ──────────────────────────────────────────────────────────
function SocialLinksTab({ group, showMsg, onUpdated }: any) {
  const [form, setForm] = React.useState({
    discord_url:   group.discord_url   ?? "",
    website_url:   group.website_url   ?? "",
    steam_group_url: group.steam_group_url ?? "",
    twitter_url:   group.twitter_url   ?? "",
    instagram_url: group.instagram_url ?? "",
    tiktok_url:    group.tiktok_url    ?? "",
    youtube_url:   group.youtube_url   ?? "",
    twitch_url:    group.twitch_url    ?? "",
  });
  const [saving, setSaving] = React.useState(false);

  const LINKS: { key: keyof typeof form; label: string; placeholder: string; icon: React.ReactNode; color: string; hint?: string }[] = [
    { key: "discord_url",    label: "Discord",         placeholder: "https://discord.gg/...",            icon: <MessageSquare className="w-4 h-4" />, color: "text-indigo-400",  hint: "Invite link or server URL" },
    { key: "website_url",    label: "Website",         placeholder: "https://yourunit.com",               icon: <Globe className="w-4 h-4" />,         color: "text-blue-400",    hint: "Official unit website" },
    { key: "twitter_url",    label: "X / Twitter",     placeholder: "https://x.com/yourunit",             icon: <Twitter className="w-4 h-4" />,       color: "text-sky-400",     hint: "Profile URL" },
    { key: "instagram_url",  label: "Instagram",       placeholder: "https://instagram.com/yourunit",     icon: <Instagram className="w-4 h-4" />,     color: "text-pink-400",    hint: "Profile URL" },
    { key: "tiktok_url",     label: "TikTok",          placeholder: "https://tiktok.com/@yourunit",       icon: <Music2 className="w-4 h-4" />,         color: "text-cyan-400",    hint: "Profile URL" },
    { key: "youtube_url",    label: "YouTube",         placeholder: "https://youtube.com/@yourunit",       icon: <Youtube className="w-4 h-4" />,       color: "text-red-400",     hint: "Channel URL" },
    { key: "twitch_url",     label: "Twitch",          placeholder: "https://twitch.tv/yourunit",           icon: <Tv2 className="w-4 h-4" />,            color: "text-purple-400",  hint: "Channel URL" },
    { key: "steam_group_url",label: "Steam Group",     placeholder: "https://steamcommunity.com/groups/...", icon: <Gamepad2 className="w-4 h-4" />,  color: "text-green-400",   hint: "Steam group URL" },
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, string | null> = {};
      LINKS.forEach(l => { payload[l.key] = form[l.key].trim() || null; });
      const updated = await apiFetch(`/milsimGroups?path=${group.id}/info`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      onUpdated({ ...group, ...payload });
      showMsg("Social links saved", "success");
    } catch (e: any) {
      showMsg(e.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const activeCount = LINKS.filter(l => form[l.key].trim()).length;

  return (
    <div className="max-w-xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest text-base">Social Links</h3>
          <p className="text-xs text-muted-foreground mt-0.5 font-sans">
            Displayed publicly on your unit page · {activeCount} of {LINKS.length} configured
          </p>
        </div>
        {activeCount > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {LINKS.filter(l => form[l.key].trim()).map(l => (
              <span key={l.key} className={`${l.color} text-[10px] font-display font-bold uppercase tracking-widest`}>{l.label}</span>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        {LINKS.map(({ key, label, placeholder, icon, color, hint }) => (
          <div key={key} className="space-y-1">
            <label className="flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest text-muted-foreground">
              <span className={color}>{icon}</span>{label}
              {hint && <span className="text-[10px] font-normal normal-case tracking-normal text-muted-foreground/60">— {hint}</span>}
            </label>
            <div className="relative">
              <input
                type="url"
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                placeholder={placeholder}
                className="mf-input w-full pl-3 pr-8 text-sm"
              />
              {form[key].trim() && (
                <button
                  onClick={() => setForm(f => ({ ...f, [key]: "" }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  title="Clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {form[key].trim() && (() => {
              try { new URL(form[key]); return null; }
              catch { return <p className="text-[10px] text-red-400 font-sans">Must be a valid URL (include https://)</p>; }
            })()}
          </div>
        ))}
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-display font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded clip-angled-sm transition-all disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        Save Social Links
      </button>

      {/* Preview strip */}
      {activeCount > 0 && (
        <div className="pt-4 border-t border-border/50">
          <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-3">Public Page Preview</p>
          <div className="flex flex-wrap gap-2">
            {LINKS.filter(l => form[l.key].trim()).map(l => {
              let valid = true;
              try { new URL(form[l.key]); } catch { valid = false; }
              return (
                <a
                  key={l.key}
                  href={valid ? form[l.key] : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-display font-bold uppercase tracking-wider transition-all ${valid ? "hover:opacity-80 cursor-pointer" : "opacity-40 cursor-not-allowed"} ${l.color} border-current/30 bg-current/5`}
                  style={{ borderColor: "currentColor", backgroundColor: "transparent" }}
                >
                  {l.icon}{l.label}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ── J6 Tab — Comms & Info Systems ────────────────────────────────────────────

function J6Tab({ group, showMsg, globalIsPro, onUpdated }: any) {
  type J6Sub = "comms" | "developer" | "stream" | "exports";
  const [sub, setSub] = useState<J6Sub>("comms");
  const J6_SUBS: { id: J6Sub; label: string; icon: any; pro?: boolean }[] = [
    { id: "comms",     label: "Group Comms",   icon: <Hash className="w-3.5 h-3.5" /> },
    { id: "developer", label: "API & Webhooks", icon: <GitBranch className="w-3.5 h-3.5" />, pro: true },
    { id: "stream",    label: "Stream",         icon: <Radio className="w-3.5 h-3.5" />, pro: true },
    { id: "exports",   label: "Export Tools",   icon: <Download className="w-3.5 h-3.5" /> },
  ];
  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-border pb-3 flex-wrap">
        {J6_SUBS.map(s => (
          <button key={s.id} onClick={() => setSub(s.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-display font-bold uppercase tracking-wider transition-colors ${sub === s.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>
            {s.icon} {s.label} {s.pro && <Crown className="w-3 h-3 text-amber-400" />}
          </button>
        ))}
      </div>
      {sub === "comms"     && <GroupCommsTab group={group} showMsg={showMsg} />}
      {sub === "developer" && <DeveloperTab  group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
      {sub === "stream"    && (globalIsPro ? <StreamTab group={group} onUpdated={onUpdated} showMsg={showMsg} /> : (
        <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
          <Radio className="w-10 h-10 text-muted-foreground/30" />
          <p className="font-display font-bold uppercase tracking-widest text-sm text-muted-foreground">Stream integration is a Commander Pro feature</p>
        </div>
      ))}
      {sub === "exports"   && <ExportToolsPanel group={group} showMsg={showMsg} />}
    </div>
  );
}

// ── Group Comms Tab (J6 — Discord-like channels) ────────────────────────────

const COMMS_BASE = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions/milsimGroupsB";

function GroupCommsTab({ group, showMsg }: any) {
  const { user: authUser } = useAuth();
  const [channels, setChannels] = useState<any[]>([]);
  const [activeChannel, setActiveChannel] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [msgInput, setMsgInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [myRoster, setMyRoster] = useState<any>(null);
  const [newCh, setNewCh] = useState({ name: "", description: "", category: "general", channel_type: "text", is_readonly: false });
  const msgEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const jwt = sessionStorage.getItem("tag_auth_token") ?? localStorage.getItem("tag_auth_token") ?? "";
  const authH = { Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" };

  const fetchChannels = async () => {
    try {
      const r = await fetch(`${COMMS_BASE}?path=group-channels&group_id=${group.id}`, { headers: authH });
      if (r.ok) { const data = await r.json(); setChannels(Array.isArray(data) ? data : []); }
    } catch { /* ignore */ }
  };

  const fetchRoster = async () => {
    try {
      const r = await fetch(`${COMMS_BASE}?path=roster-mine&group_id=${group.id}`, { headers: authH });
      if (r.ok) { const data = await r.json(); setMyRoster(data ?? null); }
    } catch { /* ignore */ }
  };

  const fetchMessages = async (chId: string) => {
    try {
      const r = await fetch(`${COMMS_BASE}?path=group-messages&channel_id=${chId}&limit=80`, { headers: authH });
      if (r.ok) { const data = await r.json(); setMessages(Array.isArray(data) ? data : []); }
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchChannels(); fetchRoster(); }, [group.id]);

  useEffect(() => {
    if (!activeChannel) return;
    fetchMessages(activeChannel.id);
    pollRef.current = setInterval(() => fetchMessages(activeChannel.id), 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeChannel?.id]);

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const isHQ = () => {
    if (group.owner_id && authUser?.id === group.owner_id) return true;
    if (!myRoster) return false;
    const p = myRoster.hq_permissions ?? [];
    return Array.isArray(p) ? (p.length > 0) : Object.keys(p).length > 0;
  };

  const sendMessage = async () => {
    if (!msgInput.trim() || !activeChannel || sending) return;
    setSending(true);
    try {
      const r = await fetch(`${COMMS_BASE}?path=group-messages/create`, {
        method: "POST", headers: authH,
        body: JSON.stringify({ channel_id: activeChannel.id, group_id: group.id, author_id: authUser?.id, author_callsign: myRoster?.callsign ?? authUser?.username ?? "Unknown", author_rank: "", content: msgInput.trim(), image_url: "", mentions: [] })
      });
      if (r.ok) { setMsgInput(""); await fetchMessages(activeChannel.id); }
      else { showMsg("Failed to send", "error"); }
    } finally { setSending(false); }
  };

  const deleteMessage = async (msgId: string) => {
    await fetch(`${COMMS_BASE}?path=group-messages/${msgId}`, { method: "DELETE", headers: authH });
    setMessages(prev => prev.filter(m => m.id !== msgId));
  };

  const createChannel = async () => {
    if (!newCh.name.trim()) return;
    setLoading(true);
    try {
      const r = await fetch(`${COMMS_BASE}?path=group-channels/create`, {
        method: "POST", headers: authH,
        body: JSON.stringify({ group_id: group.id, name: newCh.name.trim(), description: newCh.description, category: newCh.category, channel_type: newCh.channel_type, allowed_role_ids: [], is_readonly: newCh.is_readonly, sort_order: channels.length, created_by: authUser?.id, created_by_callsign: myRoster?.callsign ?? authUser?.username ?? "" })
      });
      if (r.ok) {
        const ch = await r.json();
        await fetchChannels();
        setActiveChannel(ch);
        setShowCreate(false);
        setNewCh({ name: "", description: "", category: "general", channel_type: "text", is_readonly: false });
        showMsg("Channel created", "success");
      } else {
        const err = await r.json().catch(() => ({}));
        showMsg(err.error ?? "Failed to create channel", "error");
      }
    } finally { setLoading(false); }
  };

  const deleteChannel = async (chId: string) => {
    if (!confirm("Delete this channel and all its messages?")) return;
    await fetch(`${COMMS_BASE}?path=group-channels/${chId}`, { method: "DELETE", headers: authH });
    setChannels(prev => prev.filter(c => c.id !== chId));
    if (activeChannel?.id === chId) setActiveChannel(null);
    showMsg("Channel deleted", "success");
  };

  const CHANNEL_ICONS: Record<string, any> = { text: Hash, announcements: Megaphone, ops: Crosshair, intel: Eye };

  const grouped = channels.reduce((acc: any, ch: any) => {
    const cat = ch.category ?? "general";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(ch);
    return acc;
  }, {} as Record<string, any[]>);

  return (
    <div className="flex h-[calc(100vh-220px)] min-h-[500px] rounded-lg border border-border overflow-hidden bg-background">

      {/* ── Sidebar ── */}
      <div className="w-56 shrink-0 bg-card border-r border-border flex flex-col">
        <div className="p-3 border-b border-border flex items-center justify-between">
          <span className="font-display font-bold text-xs uppercase tracking-widest text-muted-foreground">Channels</span>
          {isHQ() && (
            <button onClick={() => setShowCreate(true)} className="text-muted-foreground hover:text-foreground transition-colors" title="New Channel">
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto py-2 space-y-4 px-2">
          {Object.keys(grouped).length === 0 && (
            <div className="text-center py-8">
              <Hash className="w-5 h-5 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground/60">No channels yet.</p>
              {isHQ() && <button onClick={() => setShowCreate(true)} className="text-xs text-primary hover:underline mt-1">Create one</button>}
            </div>
          )}
          {Object.entries(grouped).map(([cat, chs]) => (
            <div key={cat}>
              <p className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground/50 px-1 mb-1">{cat}</p>
              {(chs as any[]).map(ch => {
                const Icon = CHANNEL_ICONS[ch.channel_type] ?? Hash;
                const isActive = activeChannel?.id === ch.id;
                return (
                  <button key={ch.id} onClick={() => setActiveChannel(ch)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors group ${isActive ? "bg-primary/20 text-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                    <Icon className="w-3.5 h-3.5 shrink-0 opacity-70" />
                    <span className="text-xs font-sans truncate flex-1">{ch.name}</span>
                    {isHQ() && (
                      <button onClick={e => { e.stopPropagation(); deleteChannel(ch.id); }}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all shrink-0">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ── Main Area ── */}
      {!activeChannel ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
          <Hash className="w-10 h-10 text-muted-foreground/30" />
          <p className="text-muted-foreground font-display font-bold uppercase tracking-wider text-sm">Select a channel</p>
          <p className="text-xs text-muted-foreground/50">or create one using the + button</p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          <div className="h-12 border-b border-border flex items-center gap-3 px-4 bg-card shrink-0">
            {(() => { const Icon = CHANNEL_ICONS[activeChannel.channel_type] ?? Hash; return <Icon className="w-4 h-4 text-muted-foreground" />; })()}
            <span className="font-display font-bold text-sm uppercase tracking-wide">{activeChannel.name}</span>
            {activeChannel.description && <span className="text-xs text-muted-foreground border-l border-border pl-3">{activeChannel.description}</span>}
            {activeChannel.is_readonly && <span className="ml-auto text-xs text-amber-400 font-display font-bold uppercase tracking-wider">Read-only</span>}
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center gap-2">
                <Hash className="w-8 h-8 text-muted-foreground/30" />
                <p className="text-xs text-muted-foreground/60">No messages yet. Be the first.</p>
              </div>
            )}
            {messages.map((m: any, i: number) => {
              const prev = messages[i - 1];
              const showHeader = !prev || prev.author_id !== m.author_id || (new Date(m.created_date).getTime() - new Date(prev.created_date).getTime()) > 5 * 60 * 1000;
              return (
                <div key={m.id} className={`group flex gap-2 ${showHeader ? "mt-3" : "mt-0.5"} hover:bg-secondary/30 rounded px-2 py-0.5`}>
                  {showHeader ? (
                    <div className="w-7 h-7 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[9px] font-display font-bold text-primary uppercase">{(m.author_callsign ?? "?")[0]}</span>
                    </div>
                  ) : <div className="w-7 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    {showHeader && (
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="text-xs font-display font-bold text-foreground">
                          {m.author_rank ? <span className="text-muted-foreground mr-1">[{m.author_rank}]</span> : null}
                          {m.author_callsign}
                        </span>
                        <span className="text-[10px] text-muted-foreground/50">{new Date(m.created_date).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    )}
                    <p className="text-xs text-foreground/90 font-sans break-words">{m.content}</p>
                    {m.image_url && <img src={m.image_url} alt="" className="mt-1 rounded max-w-xs max-h-48 object-cover" />}
                  </div>
                  <button onClick={() => deleteMessage(m.id)}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all shrink-0 self-start mt-0.5">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
            <div ref={msgEndRef} />
          </div>

          {!activeChannel.is_readonly && (
            <div className="border-t border-border p-3 flex gap-2 bg-card shrink-0">
              <input
                type="text"
                value={msgInput}
                onChange={e => setMsgInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder={`Message #${activeChannel.name}`}
                className="flex-1 bg-background border border-border rounded px-3 py-2 text-sm font-sans focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button onClick={sendMessage} disabled={sending || !msgInput.trim()}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded font-display font-bold uppercase tracking-widest text-xs disabled:opacity-50 transition-colors flex items-center gap-2">
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-lg w-full max-w-md mx-4 shadow-2xl">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-display font-bold uppercase tracking-wider text-sm">Create Channel</h3>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">Channel Name *</label>
                <input value={newCh.name} onChange={e => setNewCh(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. general, ops-planning"
                  className="w-full bg-background border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
              </div>
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">Description</label>
                <input value={newCh.description} onChange={e => setNewCh(p => ({ ...p, description: e.target.value }))}
                  placeholder="Optional description"
                  className="w-full bg-background border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">Category</label>
                  <select value={newCh.category} onChange={e => setNewCh(p => ({ ...p, category: e.target.value }))}
                    className="w-full bg-background border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary">
                    <option value="general">General</option>
                    <option value="operations">Operations</option>
                    <option value="intel">Intel</option>
                    <option value="admin">Admin</option>
                    <option value="social">Social</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1">Type</label>
                  <select value={newCh.channel_type} onChange={e => setNewCh(p => ({ ...p, channel_type: e.target.value }))}
                    className="w-full bg-background border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary">
                    <option value="text">Text</option>
                    <option value="announcements">Announcements</option>
                    <option value="ops">Ops</option>
                    <option value="intel">Intel</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={newCh.is_readonly} onChange={e => setNewCh(p => ({ ...p, is_readonly: e.target.checked }))} className="rounded" />
                <span className="text-xs text-muted-foreground">Read-only (HQ post only)</span>
              </label>
            </div>
            <div className="p-5 border-t border-border flex gap-3">
              <button onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 border border-border rounded text-xs font-display font-bold uppercase tracking-wider hover:bg-secondary transition-colors">Cancel</button>
              <button onClick={createChannel} disabled={loading || !newCh.name.trim()}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50 hover:bg-primary/90 transition-colors">
                {loading ? "Creating..." : "Create Channel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── J7 Development & Training ─────────────────────────────────────────────────
function TrainingTab({ group, showMsg, globalIsPro, onUpdated }: any) {
  return (
    <PJHQShell key="training" subs={[
      { id: "sops",    label: "SOPs",            icon: <BookOpen className="w-3.5 h-3.5" /> },
      { id: "docs",    label: "Training Docs",   icon: <Brain className="w-3.5 h-3.5" /> },
      { id: "roe",     label: "ROE",             icon: <Shield className="w-3.5 h-3.5" /> },
      { id: "reviews", label: "Training Reviews", icon: <ClipboardCheck className="w-3.5 h-3.5" /> },
      { id: "awards",  label: "Awards",          icon: <Medal className="w-3.5 h-3.5" />, pro: true },
      { id: "quals",   label: "Qualifications",  icon: <GraduationCap className="w-3.5 h-3.5" />, pro: true },
    ]}>
      {(sub) => (<>
        {sub === "sops"    && <SopsOnlyTab   group={group} onSaved={onUpdated} setSaving={() => {}} saving={false} showMsg={showMsg} />}
        {sub === "docs"    && <TrainingDocsTab group={group} showMsg={showMsg} />}
        {sub === "roe"     && <ROEPanel      group={group} showMsg={showMsg} />}
        {sub === "reviews" && <TrainingReviewStubPanel group={group} showMsg={showMsg} />}
        {sub === "awards"  && <AwardsTab     group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
        {sub === "quals"   && (globalIsPro ? <QualsTab group={group} showMsg={showMsg} /> : (
          <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
            <Crown className="w-10 h-10 text-yellow-400 opacity-60" />
            <h3 className="font-display font-black text-2xl uppercase tracking-wider text-foreground">Commander Pro Required</h3>
            <p className="text-muted-foreground font-sans text-sm max-w-sm">The Qualifications system — including custom qualification types and badge grants — is a Commander Pro feature.</p>
            <a href="/commander-pro" className="inline-flex items-center gap-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 text-yellow-300 font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded transition-all mt-2">
              <Crown className="w-3.5 h-3.5" /> Upgrade to Commander Pro
            </a>
          </div>
        ))}
      </>)}
    </PJHQShell>
  );
}

// ── J1b Human Resources ───────────────────────────────────────────────────────
function HRTab({ group, showMsg, globalIsPro }: any) {
  return (
    <PJHQShell key="hr" subs={[
      { id: "loa",            label: "LOA Manager",    icon: <PlaneTakeoff className="w-3.5 h-3.5" /> },
      { id: "inactivity",     label: "Inactivity Purge", icon: <UserMinus className="w-3.5 h-3.5" />, pro: true },
      { id: "engagement",     label: "Engagement Score", icon: <Activity className="w-3.5 h-3.5" />, pro: true },
      { id: "promotion",      label: "Promotions",     icon: <ChevronUp className="w-3.5 h-3.5" />, pro: true },
      { id: "conduct",        label: "Conduct Tracker", icon: <ShieldAlert className="w-3.5 h-3.5" /> },
      { id: "accountability", label: "Accountability", icon: <Flame className="w-3.5 h-3.5" />, pro: true },
    ]}>
      {(sub) => (<>
        {sub === "loa"            && <LOATab             group={group} showMsg={showMsg} />}
        {sub === "inactivity"     && <InactivityPurgeTab group={group} globalIsPro={globalIsPro} showMsg={showMsg} />}
        {sub === "engagement"     && <EngagementScoreTab group={group} globalIsPro={globalIsPro} />}
        {sub === "promotion"      && <PromotionEngineTab group={group} globalIsPro={globalIsPro} showMsg={showMsg} />}
        {sub === "conduct"        && <ConductTrackerTab  group={group} showMsg={showMsg} />}
        {sub === "accountability" && <AccountabilityTab  group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
      </>)}
    </PJHQShell>
  );
}

// ── Unit Publish Tab (News & Media posts) ────────────────────────────────────
function UnitPublishTab({ group, showMsg }: any) {
  const [postType, setPostType] = React.useState<"unit"|"media">("unit");
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [mediaUrl, setMediaUrl] = React.useState("");
  const [mediaKind, setMediaKind] = React.useState<"image"|"video"|null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [posts, setPosts] = React.useState<any[]>([]);
  const [loadingPosts, setLoadingPosts] = React.useState(true);
  const imageInputRef = React.useRef<HTMLInputElement>(null);
  const videoInputRef = React.useRef<HTMLInputElement>(null);

  const MAX_VIDEO_DURATION = 120; // 2 minutes in seconds
  const MAX_VIDEO_SIZE = 200 * 1024 * 1024; // 200MB
  const MAX_IMAGE_SIZE = 70 * 1024 * 1024;  // 70MB

  const loadPosts = async () => {
    setLoadingPosts(true);
    try {
      const data = await apiFetch<any[]>(`/posts?category=${postType}&milsim_group_id=${group.id}&limit=20`);
      setPosts(Array.isArray(data) ? data : []);
    } catch { setPosts([]); }
    finally { setLoadingPosts(false); }
  };

  React.useEffect(() => { loadPosts(); }, [postType, group.id]);

  const clearMedia = () => { setMediaUrl(""); setMediaKind(null); setUploadProgress(""); };

  const handleImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { showMsg("Please select an image file.", "error"); return; }
    if (file.size > MAX_IMAGE_SIZE) { showMsg("Max image size is 70MB.", "error"); return; }
    clearMedia();
    setUploading(true);
    setUploadProgress("Uploading image...");
    try {
      const url = await uploadImageToStorage(file);
      if (url) { setMediaUrl(url); setMediaKind("image"); setUploadProgress(""); }
      else showMsg("Upload returned no URL.", "error");
    } catch (err: any) { showMsg(err.message ?? "Upload failed.", "error"); }
    setUploading(false);
    e.target.value = "";
  };

  const handleVideoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) { showMsg("Please select a video file.", "error"); return; }
    if (file.size > MAX_VIDEO_SIZE) { showMsg("Max video size is 200MB.", "error"); return; }

    // Check duration before upload
    const duration = await new Promise<number>((resolve) => {
      const vid = document.createElement("video");
      vid.preload = "metadata";
      const objUrl = URL.createObjectURL(file);
      vid.onloadedmetadata = () => { URL.revokeObjectURL(objUrl); resolve(vid.duration); };
      vid.onerror = () => { URL.revokeObjectURL(objUrl); resolve(0); };
      vid.src = objUrl;
    });

    if (duration > MAX_VIDEO_DURATION) {
      const mins = Math.floor(duration / 60);
      const secs = Math.round(duration % 60);
      showMsg(`Video is ${mins}m ${secs}s — max allowed is 2 minutes.`, "error");
      e.target.value = "";
      return;
    }

    clearMedia();
    setUploading(true);
    setUploadProgress("Uploading video...");
    try {
      const url = await uploadImageToStorage(file);
      if (url) { setMediaUrl(url); setMediaKind("video"); setUploadProgress(""); }
      else showMsg("Upload returned no URL.", "error");
    } catch (err: any) { showMsg(err.message ?? "Upload failed.", "error"); }
    setUploading(false);
    e.target.value = "";
  };

  const handleSubmit = async () => {
    if (!title.trim() || !body.trim()) { showMsg("Title and content are required.", "error"); return; }
    if (uploading) { showMsg("Please wait for upload to finish.", "error"); return; }
    setSubmitting(true);
    try {
      await apiFetch("/posts", {
        method: "POST",
        body: JSON.stringify({
          title: title.trim(),
          postBody: body.trim(),
          category: postType,
          milsim_group_id: group.id,
          image_url: mediaUrl.trim() || undefined,
        }),
      });
      showMsg("Post published to " + (postType === "unit" ? "Unit News" : "Unit Media") + "!", "success");
      setTitle(""); setBody(""); clearMedia();
      loadPosts();
    } catch (e: any) {
      showMsg(e.message ?? "Failed to publish.", "error");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (postId: string) => {
    try {
      await apiFetch(`/posts/${postId}`, { method: "DELETE" });
      setPosts(prev => prev.filter(p => p.id !== postId));
      showMsg("Post deleted.", "success");
    } catch (e: any) { showMsg(e.message ?? "Failed to delete.", "error"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-display font-black uppercase tracking-widest text-lg">Publish News & Media</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Post directly to Unit News or Unit Media on the Recruitment Marketplace</p>
      </div>

      {/* Type toggle */}
      <div className="flex gap-2">
        <button onClick={() => setPostType("unit")}
          className={`flex items-center gap-2 px-4 py-2 rounded text-xs font-display font-bold uppercase tracking-wider border transition-colors ${postType === "unit" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50 text-muted-foreground"}`}>
          <Newspaper className="w-3.5 h-3.5" /> Unit News
        </button>
        <button onClick={() => setPostType("media")}
          className={`flex items-center gap-2 px-4 py-2 rounded text-xs font-display font-bold uppercase tracking-wider border transition-colors ${postType === "media" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50 text-muted-foreground"}`}>
          <Film className="w-3.5 h-3.5" /> Unit Media
        </button>
      </div>

      {/* Compose */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        <p className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground">New Post</p>
        <input
          value={title} onChange={e => setTitle(e.target.value)}
          placeholder="Post title..."
          className="w-full bg-background border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-primary"
        />
        <textarea
          value={body} onChange={e => setBody(e.target.value)}
          placeholder={postType === "unit" ? "Share unit news, updates, announcements..." : "Share media, videos, screenshots, after action highlights..."}
          rows={4}
          className="w-full bg-background border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-primary resize-none"
        />

        {/* Media upload row */}
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => imageInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-3 py-2 rounded border border-border hover:border-primary/50 text-xs font-display font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50">
            {uploading && mediaKind === null ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
            Image
          </button>
          <button type="button" onClick={() => videoInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-3 py-2 rounded border border-border hover:border-primary/50 text-xs font-display font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50">
            {uploading && mediaKind === null ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Video className="w-3.5 h-3.5" />}
            Video <span className="text-muted-foreground/60 normal-case font-normal">(max 2 min)</span>
          </button>

          {uploading && (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" /> {uploadProgress}
            </span>
          )}

          {mediaUrl && !uploading && (
            <div className="flex items-center gap-2 w-full mt-1">
              {mediaKind === "image" ? (
                <img src={mediaUrl} alt="" className="w-20 h-14 rounded object-cover border border-border shrink-0" />
              ) : (
                <video src={mediaUrl} className="w-32 h-20 rounded object-cover border border-border shrink-0" controls muted />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground truncate">{mediaUrl.split("/").pop()}</p>
                <p className="text-[10px] text-muted-foreground/50">{mediaKind === "video" ? "Video clip" : "Image"} ready</p>
              </div>
              <button onClick={clearMedia} className="shrink-0 p-1.5 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageFile} />
        <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/mov" className="hidden" onChange={handleVideoFile} />

        <button onClick={handleSubmit} disabled={submitting || uploading || !title.trim() || !body.trim()}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-xs font-display font-bold uppercase tracking-wider hover:opacity-90 disabled:opacity-50 transition-opacity">
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {submitting ? "Publishing..." : "Publish Post"}
        </button>
      </div>

      {/* Recent posts */}
      <div>
        <p className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground mb-3">
          Recent {postType === "unit" ? "Unit News" : "Unit Media"} Posts
        </p>
        {loadingPosts ? (
          <p className="text-xs text-muted-foreground">Loading...</p>
        ) : posts.length === 0 ? (
          <p className="text-xs text-muted-foreground">No posts yet. Publish your first one above.</p>
        ) : (
          <div className="space-y-2">
            {posts.map(p => {
              const isVid = p.image_url && /\.(mp4|webm|mov|quicktime)(\?|$)/i.test(p.image_url);
              return (
                <div key={p.id} className="bg-card border border-border rounded-lg p-3 flex items-start gap-3">
                  {p.image_url && (
                    isVid
                      ? <video src={p.image_url} className="w-20 h-14 rounded object-cover border border-border shrink-0" muted />
                      : <img src={p.image_url} alt="" className="w-14 h-14 rounded object-cover border border-border shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{p.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{p.body ?? p.postBody ?? ""}</p>
                    <p className="text-[10px] text-muted-foreground/60 mt-1">{p.created_date ? new Date(p.created_date).toLocaleDateString() : ""}</p>
                  </div>
                  <button onClick={() => handleDelete(p.id)}
                    className="shrink-0 p-1.5 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Media & Recruitment ───────────────────────────────────────────────────────
function MediaTab({ group, showMsg, globalIsPro, onUpdated }: any) {
  return (
    <PJHQShell key="media" subs={[
      { id: "social",      label: "Social Links",   icon: <Globe className="w-3.5 h-3.5" /> },
      { id: "recruitment", label: "Recruitment",    icon: <Megaphone className="w-3.5 h-3.5" />, pro: true },
      { id: "pipeline",    label: "Pipeline",       icon: <UserCheck className="w-3.5 h-3.5" /> },
      { id: "questions",   label: "App Questions",  icon: <FileText className="w-3.5 h-3.5" /> },
      { id: "criteria",    label: "Selection",      icon: <Target className="w-3.5 h-3.5" /> },
      { id: "vacancies",   label: "Vacancies",      icon: <Briefcase className="w-3.5 h-3.5" /> },
      { id: "featured",    label: "Featured",       icon: <Star className="w-3.5 h-3.5" />, pro: true },
      { id: "publish",     label: "News & Media",   icon: <Newspaper className="w-3.5 h-3.5" /> },
      { id: "legacy",      label: "Unit Legacy",    icon: <Archive className="w-3.5 h-3.5" />, pro: true },
    ]}>
      {(sub) => (<>
        {sub === "social"      && <SocialLinksTab         group={group} showMsg={showMsg} onUpdated={onUpdated} />}
        {sub === "recruitment" && <RecruitmentSchedulerTab group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
        {sub === "pipeline"    && <RecruitPipelineTab      group={group} showMsg={showMsg} />}
        {sub === "questions"   && <QuestionsTab            group={group} onUpdated={onUpdated} showMsg={showMsg} />}
        {sub === "criteria"    && <SelectionCriteriaTab    group={group} onUpdated={onUpdated} showMsg={showMsg} />}
        {sub === "vacancies"   && <VacancyBoardTab         group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
        {sub === "featured"    && <FeaturedListingTab      group={group} showMsg={showMsg} globalIsPro={globalIsPro} />}
        {sub === "publish"     && <UnitPublishTab          group={group} showMsg={showMsg} />}
        {sub === "legacy"      && <UnitLegacyTab           group={group} globalIsPro={globalIsPro} />}
      </>)}
    </PJHQShell>
  );
}

// ── Command Analytics ─────────────────────────────────────────────────────────

// ╔══════════════════════════════════════════════════════════════╗
// ║           STRUCTURE HEALTH TAB                               ║
// ╚══════════════════════════════════════════════════════════════╝
function StructureHealthTab({ group, globalIsPro }: any) {
  const ranks: any[] = group.ranks ?? [];
  const roster: any[] = (group.roster ?? []).filter((r: any) => !r.status || r.status === "active" || r.status === "Active");
  const rosterSize = roster.length;
  const doctrine = group.structure_doctrine ?? "british";
  const lockRoles = group.structure_lock_roles !== false;
  const currentTier = getCurrentTier(doctrine, rosterSize);
  const nextTier = getNextTier(doctrine, rosterSize);
  const tiers = getDoctrineTemplate(doctrine);
  const classCounts = getRankClassCounts(roster, ranks);
  const total = rosterSize || 1;

  // Rank breakdown
  const rankById: Record<string, any> = {};
  for (const r of ranks) rankById[r.id] = r;
  const rankCounts: Record<string, number> = {};
  for (const m of roster) { if (m.rankId) rankCounts[m.rankId] = (rankCounts[m.rankId] ?? 0) + 1; }

  const lockedRanks = ranks.filter(r => !isRankUnlocked(r, currentTier, rosterSize));
  const unlockedRanks = ranks.filter(r => isRankUnlocked(r, currentTier, rosterSize));

  // Health warnings
  const warnings: string[] = [];
  const officerPct = Math.round((classCounts.officer ?? 0) / total * 100);
  const sncoPct = Math.round((classCounts.snco ?? 0) / total * 100);
  const enlistedPct = Math.round((classCounts.enlisted ?? 0) / total * 100);
  if (currentTier.maxOfficers > 0 && (classCounts.officer ?? 0) > currentTier.maxOfficers) warnings.push(`⚠ ${classCounts.officer} Officers — exceeds cap of ${currentTier.maxOfficers} for ${currentTier.label} strength`);
  if (currentTier.maxSNCOs > 0 && (classCounts.snco ?? 0) > currentTier.maxSNCOs) warnings.push(`⚠ ${classCounts.snco} SNCOs — exceeds cap of ${currentTier.maxSNCOs} for ${currentTier.label} strength`);
  if (enlistedPct < 40 && rosterSize > 4) warnings.push(`⚠ Only ${enlistedPct}% enlisted — top-heavy unit detected`);
  if (rosterSize < 4) warnings.push(`⚠ Unit below minimum viable size (4 members)`);

  const tierProgressPct = nextTier
    ? Math.min(100, Math.round(((rosterSize - currentTier.minHeadcount) / (nextTier.minHeadcount - currentTier.minHeadcount)) * 100))
    : 100;

  // Save doctrine preference
  const [saving, setSaving] = React.useState(false);
  const [localDoctrine, setLocalDoctrine] = React.useState(doctrine);
  const [localLock, setLocalLock] = React.useState(lockRoles);
  const saveDoctrine = async () => {
    setSaving(true);
    try { await apiFetch(`/milsimGroups?path=${group.id}/info`, { method: "PATCH", body: JSON.stringify({ structure_doctrine: localDoctrine, structure_lock_roles: localLock }) }); }
    catch {}
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="font-display font-black uppercase tracking-widest text-lg">Structure Engine</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Rank tier enforcement based on actual roster headcount</p>
      </div>

      {/* Doctrine selector */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        <p className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground">Doctrine</p>
        <div className="flex gap-2 flex-wrap">
          {[{id:"british",label:"British Army"},{id:"us",label:"US Army"},{id:"custom",label:"Custom (Unlocked)"}].map(d => (
            <button key={d.id} onClick={() => setLocalDoctrine(d.id)}
              className={`px-3 py-1.5 rounded text-xs font-display font-bold uppercase tracking-wider border transition-colors ${localDoctrine === d.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
              {d.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={localLock} onChange={e => setLocalLock(e.target.checked)} className="rounded" />
          Lock promotions to tier-appropriate ranks
        </label>
        <button onClick={saveDoctrine} disabled={saving} className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-bold">
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>

      {/* Current Strength Card */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1">
            <p className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground">Current Strength</p>
            <p className="text-3xl font-display font-black uppercase tracking-widest mt-1 text-foreground">{currentTier.label}</p>
            <p className="text-[11px] text-primary/80 font-display font-bold uppercase tracking-wider mt-0.5">{currentTier.sublabel}</p>
            <p className="text-xs text-muted-foreground mt-1">{currentTier.description}</p>
            {/* Breakdown: sections / squads */}
            {(() => {
              if (currentTier.id === "section") {
                const sections = Math.max(1, Math.floor(rosterSize / 8));
                const label = localDoctrine === "us" ? "squad" : "section";
                const pluralLabel = sections === 1 ? label : label + "s";
                return <p className="text-xs text-amber-400 font-bold mt-1">{sections} {pluralLabel} ({rosterSize} members)</p>;
              }
              if (currentTier.id === "platoon") {
                const label = localDoctrine === "us" ? "squads" : "sections";
                const sections = Math.floor(rosterSize / 9);
                const officersAllowed = Math.floor(rosterSize / 36);
                return <p className="text-xs text-amber-400 font-bold mt-1">~{sections} {label} — {officersAllowed} officer{officersAllowed !== 1 ? "s" : ""} allowed</p>;
              }
              return <p className="text-xs text-muted-foreground mt-1">{rosterSize} active members</p>;
            })()}
          </div>
          {nextTier && (
            <div className="min-w-[200px]">
              <p className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground mb-1">
                Next: <span className="text-foreground">{nextTier.label}</span>
                <span className="text-muted-foreground/60 ml-1">({nextTier.sublabel})</span>
              </p>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${tierProgressPct}%` }} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">{rosterSize}/{nextTier.minHeadcount} members ({tierProgressPct}%)</p>
            </div>
          )}
          {!nextTier && <div className="text-xs font-bold text-yellow-400 font-display uppercase tracking-wider">Max Strength Reached</div>}
        </div>
      </div>

      {/* Tier progression map — scrollable horizontal timeline */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        <p className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground">Strength Progression</p>
        <div className="overflow-x-auto pb-1">
          <div className="flex gap-2 min-w-max">
            {tiers.map((t) => {
              const active = t.id === currentTier.id;
              const unlocked = rosterSize >= t.minHeadcount;
              const ncoLabel = localDoctrine === "us" ? "NCOs" : "JNCOs";
              // Sub-unit breakdown label per tier
              const subDetail = (() => {
                if (t.id === "fireteam") return `${t.maxNCOs} ${ncoLabel} max`;
                if (t.id === "section")  return `1 ${ncoLabel.slice(0,-1)} per 8 men`;
                if (t.id === "platoon")  return `1 officer per 36, 2 SNCOs`;
                if (t.id === "company")  return `${t.maxOfficers} officers, ${t.maxSNCOs} SNCOs`;
                if (t.id === "battalion")return `${t.maxOfficers} officers, ${t.maxSNCOs} SNCOs`;
                if (t.id === "regiment") return `${t.maxOfficers} officers, ${t.maxSNCOs} SNCOs`;
                return `${t.maxOfficers} officers, ${t.maxSNCOs} SNCOs`;
              })();
              // Section count context
              const contextLine = (() => {
                if (t.id === "section") {
                  const secLabel = localDoctrine === "us" ? "squad" : "section";
                  if (t.minHeadcount <= 8 && t.maxHeadcount >= 35) {
                    return ["1 " + secLabel, "2 " + secLabel + "s", "3 " + secLabel + "s", "→ Platoon"];
                  }
                }
                return null;
              })();
              return (
                <div key={t.id} className={[
                  "w-[140px] shrink-0 rounded-lg border p-3 transition-all",
                  active ? "border-primary bg-primary/10" : unlocked ? "border-green-500/30 bg-green-500/5" : "border-border bg-card opacity-40"
                ].join(" ")}>
                  <p className="text-[11px] font-display font-black uppercase tracking-wider text-foreground">{t.label}</p>
                  <p className="text-[10px] text-primary/70 font-display font-bold uppercase tracking-wider">{t.sublabel}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{subDetail}</p>
                  {contextLine && (
                    <div className="mt-1.5 space-y-0.5">
                      {contextLine.map((l, li) => (
                        <p key={li} className="text-[9px] text-muted-foreground/60 font-display">
                          {li < contextLine.length - 1 ? `${li + 1}×` : "→"} {l}
                        </p>
                      ))}
                    </div>
                  )}
                  {active && <p className="text-[10px] text-primary font-bold font-display mt-1.5">◀ YOU ARE HERE</p>}
                  {!active && !unlocked && nextTier?.id === t.id && (
                    <p className="text-[9px] text-muted-foreground/60 mt-1">{nextTier.minHeadcount - rosterSize} more needed</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Rank Pyramid */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        <p className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground">Rank Class Distribution</p>
        <div className="space-y-2">
          {(["general","senior_officer","junior_officer","snco","nco","enlisted","unranked"] as const).map(cls => {
            const count = (cls === "general" || cls === "senior_officer" || cls === "junior_officer")
              ? (classCounts[cls] ?? 0)
              : (classCounts[cls] ?? 0);
            const pct = total > 0 ? Math.round(count / total * 100) : 0;
            const ideal = currentTier.idealRatios[cls as keyof typeof currentTier.idealRatios] ?? 0;
            const totalOfficers = (classCounts["junior_officer"] ?? 0) + (classCounts["senior_officer"] ?? 0) + (classCounts["general"] ?? 0);
            const over = (cls === "general" || cls === "senior_officer" || cls === "junior_officer") && currentTier.maxOfficers > 0 && totalOfficers > currentTier.maxOfficers;
            const overSnco = cls === "snco" && currentTier.maxSNCOs > 0 && count > currentTier.maxSNCOs;
            const warn = over || overSnco || (cls === "enlisted" && pct < 40 && rosterSize > 4);
            return (
              <div key={cls}>
                <div className="flex justify-between text-xs mb-0.5">
                  <span className="font-display font-bold uppercase tracking-wider" style={{ color: RANK_CLASS_COLOURS[cls] }}>{RANK_CLASS_LABELS[cls]}</span>
                  <span className={warn ? "text-red-400 font-bold" : "text-muted-foreground"}>{count} ({pct}%) {warn ? "⚠" : ""} <span className="opacity-50">ideal {ideal}%</span></span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden flex">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: warn ? "#ef4444" : RANK_CLASS_COLOURS[cls] }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 space-y-1">
          <p className="text-xs font-display font-bold uppercase tracking-wider text-red-400 mb-2">Structure Warnings</p>
          {warnings.map((w, i) => <p key={i} className="text-xs text-red-300">{w}</p>)}
        </div>
      )}

      {/* Locked Ranks */}
      {lockedRanks.length > 0 && localDoctrine !== "custom" && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3">
          <p className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground">Locked Ranks</p>
          <p className="text-xs text-muted-foreground">These ranks cannot be assigned until your unit reaches the required strength.</p>
          <div className="space-y-1.5">
            {lockedRanks.map(r => {
              // Use min_roster_size on the rank if set, otherwise fall back to the first tier that unlocks the rank class
              const minNeeded = (r.min_roster_size && r.min_roster_size > 0) ? r.min_roster_size : null;
              const next = minNeeded
                ? tiers.find(t => t.minHeadcount >= minNeeded)
                : tiers.find(t => t.unlocksRankClasses.includes(r.rank_class ?? "enlisted") && t.minHeadcount > rosterSize);
              const displayMin = minNeeded ?? next?.minHeadcount ?? "?";
              return (
                <div key={r.id} className="flex items-center justify-between gap-2 text-xs bg-muted/30 rounded px-3 py-2 opacity-60">
                  <span className="font-display font-bold uppercase tracking-wider">{r.abbreviation || r.name}</span>
                  <span className="text-muted-foreground">🔒 Unlocks at {next?.label ?? "higher"} ({displayMin}+ members)</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Active Ranks breakdown */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        <p className="text-xs font-display font-bold uppercase tracking-wider text-muted-foreground">Active Rank Slots</p>
        {unlockedRanks.length === 0 && <p className="text-xs text-muted-foreground">No ranks configured.</p>}
        <div className="space-y-1.5">
          {unlockedRanks.sort((a: any, b: any) => (b.tier ?? 0) - (a.tier ?? 0)).map((r: any) => {
            const count = rankCounts[r.id] ?? 0;
            const max = r.max_holders ?? 0;
            const full = max > 0 && count >= max;
            return (
              <div key={r.id} className="flex items-center justify-between gap-2 text-xs bg-muted/20 rounded px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: RANK_CLASS_COLOURS[r.rank_class ?? "enlisted"] }} />
                  <span className="font-display font-bold uppercase tracking-wider">{r.abbreviation || r.name}</span>
                  <span className="text-muted-foreground opacity-60">{RANK_CLASS_LABELS[r.rank_class ?? "enlisted"]}</span>
                </div>
                <span className={full ? "text-red-400 font-bold" : "text-muted-foreground"}>
                  {count}{max > 0 ? `/${max}` : ""} {full ? "FULL" : ""}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AnalyticsHubTab({ group, showMsg, globalIsPro }: any) {
  return (
    <PJHQShell key="analytics" subs={[
      { id: "structure",   label: "Structure Engine", icon: <Layers className="w-3.5 h-3.5" /> },
      { id: "health",      label: "Unit Health", icon: <Heart className="w-3.5 h-3.5" />, pro: true },
      { id: "readiness",   label: "Readiness",   icon: <Activity className="w-3.5 h-3.5" /> },
      { id: "analytics",   label: "Unit Analytics", icon: <BarChart3 className="w-3.5 h-3.5" />, pro: true },
      { id: "leaderboard", label: "Member Scores", icon: <Trophy className="w-3.5 h-3.5" />, pro: true },
    ]}>
      {(sub) => (<>
        {sub === "structure"   && <StructureHealthTab  group={group} globalIsPro={globalIsPro} />}
        {sub === "health"      && <UnitHealthDashboard group={group} globalIsPro={globalIsPro} />}
        {sub === "readiness"   && <ReadinessTab        group={group} />}
        {sub === "analytics"   && <AnalyticsTab        group={group} globalIsPro={globalIsPro} />}
        {sub === "leaderboard" && <EngagementScoreTab group={group} globalIsPro={globalIsPro} />}
      </>)}
    </PJHQShell>
  );
}

// ── Stub panels for new features ──────────────────────────────────────────────

function DischargesPanel({ group, showMsg }: any) {
  const [discharges, setDischarges] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    if (!group?.id) return;
    apiFetch(`/milsimGroups?path=${group.id}/discharges`)
      .then((r: any) => { setDischarges(Array.isArray(r) ? r : r?.items || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.id]);
  const TYPE_COLOR: Record<string, string> = {
    Honourable: "text-green-400", Medical: "text-blue-400",
    Administrative: "text-yellow-400", Disciplinary: "text-red-400", AWOL: "text-orange-400",
  };
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display font-black uppercase tracking-widest">Discharges</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Personnel separation records</p>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : discharges.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <UserMinus className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No discharge records</p>
        </div>
      ) : (
        <div className="space-y-3">
          {discharges.map((d: any) => (
            <div key={d.id} className="rounded-lg border border-border/60 bg-card/40 p-4 flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-black uppercase tracking-wider text-sm">{d.callsign}</span>
                  <span className={`text-[10px] font-display font-black uppercase tracking-widest ${TYPE_COLOR[d.discharge_type] || "text-muted-foreground"}`}>{d.discharge_type}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{d.reason}</p>
                <p className="text-[10px] text-muted-foreground mt-1">Conducted by {d.conducted_by_username} · {d.effective_date ? new Date(d.effective_date).toLocaleDateString() : ""}</p>
              </div>
              {d.final_rank && <span className="text-[10px] bg-secondary px-2 py-1 rounded shrink-0">{d.final_rank}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ROEPanel({ group, showMsg }: any) {
  const [editing, setEditing] = React.useState(false);
  const [roeContent, setRoeContent] = React.useState(group?.roe ?? "");
  const [saving, setSaving] = React.useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch(`/milsimGroups?path=${group.id}/info`, {
        method: "PATCH",
        body: JSON.stringify({ roe: roeContent }),
      });
      showMsg("ROE saved", "success");
      setEditing(false);
      // Update local group prop so re-open shows latest content
      if (group) group.roe = roeContent;
    } catch (e: any) {
      showMsg(e?.message || "Failed to save ROE", "error");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest">Rules of Engagement</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Standing ROE — visible to all members</p>
        </div>
        <button onClick={() => setEditing(!editing)} className="text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded font-display font-bold uppercase tracking-widest hover:opacity-90">
          {editing ? "Cancel" : "Edit ROE"}
        </button>
      </div>
      {editing ? (
        <div className="space-y-3">
          <textarea
            value={roeContent}
            onChange={e => setRoeContent(e.target.value)}
            rows={20}
            className="w-full bg-background border border-border/60 rounded px-3 py-2 text-sm resize-none font-mono"
            placeholder="Enter your Rules of Engagement..."
          />
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground text-xs px-4 py-2 rounded font-display font-bold uppercase tracking-widest hover:opacity-90 disabled:opacity-50">
              {saving ? "Saving..." : "Save ROE"}
            </button>
            <button onClick={() => setEditing(false)} className="text-xs px-4 py-2 rounded border border-border/60 font-display font-bold uppercase tracking-widest hover:bg-secondary">Cancel</button>
          </div>
        </div>
      ) : roeContent ? (
        <div className="rounded-lg border border-border/60 bg-card/40 p-5 whitespace-pre-wrap font-mono text-sm text-foreground/80">{roeContent}</div>
      ) : (
        <div className="text-center py-16 text-muted-foreground">
          <Shield className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No ROE defined yet</p>
        </div>
      )}
    </div>
  );
}

function ExportToolsPanel({ group, showMsg }: any) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display font-black uppercase tracking-widest">Export Tools</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Push unit data to Google Workspace</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: "Google Slides", desc: "Export briefing as presentation",      icon: <FileText className="w-5 h-5" />,  action: () => showMsg("Navigate to J5 Planning → Briefings then use the Slides export button") },
          { label: "Google Docs",   desc: "Export AAR or briefing as document",   icon: <FileEdit className="w-5 h-5" />,  action: () => showMsg("Navigate to J3 Operations Room → Operations then use the Docs export button") },
          { label: "Google Sheets", desc: "Export roster or ops log",             icon: <BarChart3 className="w-5 h-5" />, action: () => showMsg("Navigate to J1 Personnel → Roster then use the Sheets export button") },
        ].map(t => (
          <button key={t.label} onClick={t.action} className="rounded-lg border border-border/60 bg-card/40 hover:bg-card/80 p-5 text-left transition-colors space-y-2">
            <div className="text-primary">{t.icon}</div>
            <p className="font-display font-black uppercase tracking-widest text-sm">{t.label}</p>
            <p className="text-xs text-muted-foreground">{t.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function TrainingReviewStubPanel({ group, showMsg }: any) {
  const [reviews, setReviews] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    if (!group?.id) return;
    apiFetch(`/milsimTrainingReviews?group_id=${group.id}`)
      .then((r: any) => { setReviews(Array.isArray(r) ? r : r?.items || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [group?.id]);
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-display font-black uppercase tracking-widest">Training Reviews</h3>
        <p className="text-xs text-muted-foreground mt-0.5">Post-exercise assessment records</p>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Brain className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No training reviews yet</p>
          <p className="text-xs mt-1">Submit reviews via the Operations system after each training exercise</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r: any) => (
            <div key={r.id} className="rounded-lg border border-border/60 bg-card/40 p-4 space-y-1">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-display font-black uppercase tracking-wider text-sm">{r.exercise_name}</span>
                {r.ai_score != null && (
                  <span className="text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border border-primary/30 bg-primary/10 text-primary">
                    AI Score: {r.ai_score}/10
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Conducted by {r.conducted_by} · {r.exercise_date ? new Date(r.exercise_date).toLocaleDateString() : ""} · {r.participant_count} participants
              </p>
              {r.ai_summary && <p className="text-xs mt-1 italic text-foreground/70">{r.ai_summary}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Orders & Memos Tab ────────────────────────────────────────────────────────
function OrdersMemosTab({ group, showMsg }: any) {
  const { user } = useAuth();
  const [items, setItems]       = useState<any[]>([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter]     = useState<"all"|"issued"|"draft"|"archived">("all");
  const [selected, setSelected] = useState<any>(null);

  const BLANK = {
    type: "Order", classification: "unclassified", subject: "", body: "",
    audience: "all", audience_role_id: "", audience_role_name: "",
    audience_rank_id: "", audience_rank_name: "",
    priority: "routine", requires_ack: false, ack_by: "", status: "draft",
  };
  const [form, setForm] = useState<any>(BLANK);

  const load = () => {
    setLoading(true);
    apiFetch<any[]>(`/milsimOrderMemos?path=group/${group.id}`)
      .then(d => setItems(d ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (group?.id) load(); }, [group?.id]);

  const roles  = group?.roles  ?? [];
  const ranks  = group?.ranks  ?? [];

  const handleSubmit = async (publish: boolean) => {
    if (!form.subject.trim()) { showMsg("Subject is required", "error"); return; }
    if (!form.body.trim())    { showMsg("Body is required", "error"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        group_id: group.id, group_name: group.name,
        author_id: (user as any)?.id,
        author_callsign: (user as any)?.username ?? "Unknown",
        status: publish ? "issued" : "draft",
        acknowledged_by: [],
      };
      if (form.audience === "role" && form.audience_role_id) {
        const r = roles.find((x: any) => x.id === form.audience_role_id);
        payload.audience_role_name = r?.name ?? "";
      }
      if (form.audience === "rank" && form.audience_rank_id) {
        const r = ranks.find((x: any) => x.id === form.audience_rank_id);
        payload.audience_rank_name = r?.name ?? "";
      }
      await apiFetch(`/milsimOrderMemos`, { method: "POST", body: JSON.stringify(payload) });
      showMsg(publish ? "Order issued to unit" : "Saved as draft", "success");
      setShowForm(false);
      setForm(BLANK);
      load();
    } catch { showMsg("Failed to save", "error"); }
    setSubmitting(false);
  };

  const handlePublish = async (item: any) => {
    await apiFetch(`/milsimOrderMemos?path=${item.id}`, { method: "PATCH", body: JSON.stringify({ status: "issued" }) });
    showMsg("Order issued", "success");
    load();
  };

  const handleArchive = async (item: any) => {
    await apiFetch(`/milsimOrderMemos?path=${item.id}`, { method: "PATCH", body: JSON.stringify({ status: "archived" }) });
    showMsg("Archived", "success");
    load();
  };

  const handleDelete = async (item: any) => {
    await apiFetch(`/milsimOrderMemos?path=${item.id}`, { method: "DELETE" });
    showMsg("Deleted", "success");
    load();
  };

  const PRIORITY_COL: Record<string, string> = {
    routine:   "text-muted-foreground border-border bg-secondary/30",
    priority:  "text-blue-400 border-blue-500/30 bg-blue-500/10",
    immediate: "text-orange-400 border-orange-500/30 bg-orange-500/10",
    flash:     "text-red-400 border-red-500/30 bg-red-500/10",
  };
  const CL: Record<string, string> = {
    unclassified: "text-green-400 border-green-500/30 bg-green-500/10",
    confidential: "text-blue-400 border-blue-500/30 bg-blue-500/10",
    classified:   "text-yellow-400 border-yellow-500/30 bg-yellow-500/10",
    "top-secret": "text-red-400 border-red-500/30 bg-red-500/10",
  };
  const STATUS_COL: Record<string, string> = {
    draft:    "text-muted-foreground border-border",
    issued:   "text-green-400 border-green-500/30 bg-green-500/10",
    archived: "text-muted-foreground border-border opacity-50",
  };

  const filtered = items.filter(i => filter === "all" || i.status === filter);
  const inp = "w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans focus:outline-none focus:border-primary/60";
  const lbl = "block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground mb-1";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-black uppercase tracking-widest">Orders & Memos</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Issue orders, directives, and bulletins to your unit</p>
        </div>
        <button onClick={() => { setShowForm(v => !v); setForm(BLANK); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-primary/40 bg-primary/10 text-primary text-[10px] font-display font-bold uppercase tracking-widest hover:bg-primary/20 transition-all">
          <ScrollText className="w-3.5 h-3.5" />
          {showForm ? "Cancel" : "New Order / Memo"}
        </button>
      </div>

      {/* Compose form */}
      {showForm && (
        <div className="border border-primary/30 bg-primary/5 rounded-lg p-5 space-y-4">
          <div className="text-[10px] font-display font-bold uppercase tracking-widest text-primary">Compose</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className={lbl}>Type</label>
              <select className={inp} value={form.type} onChange={e => setForm((f: any) => ({...f, type: e.target.value}))}>
                {["Order","Memo","Directive","Bulletin"].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Priority</label>
              <select className={inp} value={form.priority} onChange={e => setForm((f: any) => ({...f, priority: e.target.value}))}>
                {["routine","priority","immediate","flash"].map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Classification</label>
              <select className={inp} value={form.classification} onChange={e => setForm((f: any) => ({...f, classification: e.target.value}))}>
                {["unclassified","confidential","classified","top-secret"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Audience</label>
              <select className={inp} value={form.audience} onChange={e => setForm((f: any) => ({...f, audience: e.target.value, audience_role_id: "", audience_rank_id: ""}))}>
                <option value="all">Entire Unit</option>
                {roles.length > 0 && <option value="role">By Role</option>}
                {ranks.length > 0 && <option value="rank">By Rank</option>}
              </select>
            </div>
          </div>
          {form.audience === "role" && roles.length > 0 && (
            <div>
              <label className={lbl}>Target Role</label>
              <select className={inp} value={form.audience_role_id} onChange={e => setForm((f: any) => ({...f, audience_role_id: e.target.value}))}>
                <option value="">— Select role —</option>
                {roles.map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}
          {form.audience === "rank" && ranks.length > 0 && (
            <div>
              <label className={lbl}>Target Rank</label>
              <select className={inp} value={form.audience_rank_id} onChange={e => setForm((f: any) => ({...f, audience_rank_id: e.target.value}))}>
                <option value="">— Select rank —</option>
                {ranks.map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className={lbl}>Subject *</label>
            <input className={inp} value={form.subject} onChange={e => setForm((f: any) => ({...f, subject: e.target.value}))} placeholder="e.g. Section Assault Drill — Friday 0900" />
          </div>
          <div>
            <label className={lbl}>Body *</label>
            <textarea rows={6} className={inp} value={form.body} onChange={e => setForm((f: any) => ({...f, body: e.target.value}))} placeholder="Full text of the order or memo..." />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="req_ack" checked={form.requires_ack} onChange={e => setForm((f: any) => ({...f, requires_ack: e.target.checked}))} className="accent-primary" />
              <label htmlFor="req_ack" className="text-xs font-sans text-foreground cursor-pointer">Requires acknowledgement</label>
            </div>
            {form.requires_ack && (
              <div>
                <label className={lbl}>Acknowledge by (date)</label>
                <input type="date" className={inp} value={form.ack_by} onChange={e => setForm((f: any) => ({...f, ack_by: e.target.value}))} />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => handleSubmit(false)} disabled={submitting}
              className="px-4 py-2 rounded border border-border text-sm font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">
              Save Draft
            </button>
            <button onClick={() => handleSubmit(true)} disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 rounded bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-display font-bold uppercase tracking-widest transition-all disabled:opacity-50">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Issue to Unit
            </button>
          </div>
        </div>
      )}

      {/* Detail view */}
      {selected && (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border/40 bg-secondary/20 flex-wrap">
            <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-[10px] font-display uppercase tracking-widest text-muted-foreground hover:text-foreground mr-2">
              <ChevronLeft className="w-3.5 h-3.5" /> Back
            </button>
            <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${PRIORITY_COL[selected.priority]}`}>{(selected.priority ?? "routine").toUpperCase()}</span>
            <span className={`text-[10px] font-display font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${CL[selected.classification]}`}>{selected.classification}</span>
            <span className="font-display font-black text-sm uppercase tracking-wider flex-1 min-w-0 truncate">{selected.type}: {selected.subject}</span>
            <span className="text-[10px] text-muted-foreground font-sans">{selected.author_callsign} · {new Date(selected.created_date).toLocaleDateString()}</span>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex flex-wrap gap-3 text-[10px] font-display uppercase tracking-widest text-muted-foreground">
              <span>To: <span className="text-foreground font-bold">{selected.audience === "all" ? "Entire Unit" : selected.audience === "role" ? `Role: ${selected.audience_role_name}` : `Rank: ${selected.audience_rank_name}`}</span></span>
              {selected.requires_ack && <span>Ack by: <span className="text-foreground font-bold">{selected.ack_by || "—"}</span></span>}
              <span className={`px-2 py-0.5 rounded border ${STATUS_COL[selected.status]}`}>{selected.status}</span>
            </div>
            <p className="text-sm font-sans text-foreground whitespace-pre-wrap leading-relaxed">{selected.body}</p>
            {selected.requires_ack && selected.acknowledged_by?.length > 0 && (
              <div>
                <div className={lbl}>Acknowledged by ({selected.acknowledged_by.length})</div>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {selected.acknowledged_by.map((c: string, i: number) => <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-green-500/10 border border-green-500/30 text-green-400 font-display font-bold uppercase tracking-widest">{c}</span>)}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-3 border-t border-border/40 flex-wrap">
              {selected.status === "draft" && <button onClick={() => { handlePublish(selected); setSelected(null); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-primary text-primary-foreground text-[10px] font-display font-bold uppercase tracking-widest"><Send className="w-3 h-3" />Issue Now</button>}
              {selected.status === "issued" && <button onClick={() => { handleArchive(selected); setSelected(null); }} className="px-3 py-1.5 rounded border border-border text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground">Archive</button>}
              {selected.status === "draft" && <button onClick={() => { handleDelete(selected); setSelected(null); }} className="px-3 py-1.5 rounded border border-red-500/30 text-[10px] font-display font-bold uppercase tracking-widest text-red-400 hover:bg-red-500/10">Delete</button>}
            </div>
          </div>
        </div>
      )}

      {/* Filter strip + list */}
      {!selected && (
        <>
          <div className="flex gap-1.5 flex-wrap">
            {(["all","issued","draft","archived"] as const).map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded border text-[10px] font-display font-bold uppercase tracking-widest transition-all ${filter === f ? "bg-primary/15 text-primary border-primary/40" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}>
                {f === "all" ? `All (${items.length})` : `${f.charAt(0).toUpperCase()+f.slice(1)} (${items.filter(i=>i.status===f).length})`}
              </button>
            ))}
          </div>
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground"><ScrollText className="w-8 h-8 mx-auto mb-3 opacity-20" /><p className="text-sm">No orders or memos yet</p></div>
          ) : (
            <div className="space-y-2">
              {filtered.map((item: any) => (
                <div key={item.id} onClick={() => setSelected(item)} className="flex items-center gap-3 p-4 border border-border/60 rounded-lg hover:border-primary/40 hover:bg-secondary/30 cursor-pointer transition-all">
                  <ScrollText className="w-4 h-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${PRIORITY_COL[item.priority]}`}>{(item.priority ?? "routine").toUpperCase()}</span>
                      <span className="font-display font-bold text-sm uppercase tracking-wider truncate">{item.type}: {item.subject}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground font-sans">
                      <span>To: {item.audience === "all" ? "Entire Unit" : item.audience === "role" ? `Role — ${item.audience_role_name}` : `Rank — ${item.audience_rank_name}`}</span>
                      <span>{item.author_callsign}</span>
                      {item.requires_ack && <span className="text-amber-400">Ack required</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${STATUS_COL[item.status]}`}>{item.status}</span>
                    <span className="text-[10px] text-muted-foreground font-sans">{new Date(item.created_date).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
const _BUILD_ID = '2026-04-03T01-fix-quals-gate';

// ─── Alliances Tab ────────────────────────────────────────────────────────────
const ALLIANCES_URL = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions/unitAlliances";

function AlliancesTab({ group, showMsg, globalIsPro }: any) {
  const { token } = useAuth() as any;
  const [alliances, setAlliances] = useState<any[]>([]);
  const [pending, setPending] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState<"active" | "pending" | "send">("active");

  const authH = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [al, pe] = await Promise.all([
        fetch(`${ALLIANCES_URL}?path=list&group_id=${group.id}`, { headers: authH }).then(r => r.json()),
        fetch(`${ALLIANCES_URL}?path=pending&group_id=${group.id}`, { headers: authH }).then(r => r.json()),
      ]);
      setAlliances(Array.isArray(al) ? al.filter((a: any) => a.status === "active") : []);
      setPending(Array.isArray(pe) ? pe : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [group.id]);

  const searchGroups = async (q: string) => {
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await apiFetch<any>(`/api/milsim-groups?path=search&q=${encodeURIComponent(q)}`);
      const arr = Array.isArray(res) ? res : (res?.groups ?? []);
      setSearchResults(arr.filter((g: any) => g.id !== group.id));
    } catch { setSearchResults([]); }
    setSearching(false);
  };

  const sendRequest = async (target: any) => {
    setSending(true);
    try {
      const res = await fetch(`${ALLIANCES_URL}?path=request`, {
        method: "POST",
        headers: { ...authH, "Content-Type": "application/json" },
        body: JSON.stringify({
          requester_group_id: group.id,
          requester_group_name: group.name,
          requester_group_tag: group.tag ?? "",
          requester_logo_url: group.logo_url ?? "",
          addressee_group_id: target.id,
          addressee_group_name: target.name,
          addressee_group_tag: target.tag ?? "",
          addressee_logo_url: target.logo_url ?? "",
          message,
        }),
      });
      const data = await res.json();
      if (!res.ok) { showMsg(data.error ?? "Failed to send request", "error"); }
      else { showMsg("Alliance request sent", "success"); setMessage(""); setSearchQuery(""); setSearchResults([]); setTab("active"); load(); }
    } finally { setSending(false); }
  };

  const respond = async (allianceId: string, action: "accept" | "reject") => {
    try {
      const res = await fetch(`${ALLIANCES_URL}?path=respond`, {
        method: "POST",
        headers: { ...authH, "Content-Type": "application/json" },
        body: JSON.stringify({ alliance_id: allianceId, action, group_id: group.id }),
      });
      const data = await res.json();
      if (!res.ok) showMsg(data.error ?? "Failed", "error");
      else { showMsg(action === "accept" ? "Alliance accepted" : "Request declined", "success"); load(); }
    } catch { showMsg("Error", "error"); }
  };

  const dissolve = async (allianceId: string) => {
    if (!confirm("Dissolve this alliance? This cannot be undone.")) return;
    try {
      const res = await fetch(`${ALLIANCES_URL}?path=dissolve`, {
        method: "POST",
        headers: { ...authH, "Content-Type": "application/json" },
        body: JSON.stringify({ alliance_id: allianceId, group_id: group.id }),
      });
      const data = await res.json();
      if (!res.ok) showMsg(data.error ?? "Failed", "error");
      else { showMsg("Alliance dissolved", "success"); load(); }
    } catch { showMsg("Error", "error"); }
  };

  const getAllyInfo = (a: any) => {
    const isReq = a.requester_group_id === group.id;
    return {
      name: isReq ? (a.addressee_group_name ?? "Unknown") : (a.requester_group_name ?? "Unknown"),
      tag:  isReq ? (a.addressee_group_tag  ?? "")         : (a.requester_group_tag  ?? ""),
      logo: isReq ? (a.addressee_logo_url   ?? "")         : (a.requester_logo_url   ?? ""),
      gid:  isReq ? a.addressee_group_id                   : a.requester_group_id,
    };
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display font-black text-xl uppercase tracking-wider text-foreground">Unit Alliances</h2>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Formally ally with other units. Allied units display on your public profile.</p>
        </div>
        <div className="flex gap-2">
          {(["active", "pending", "send"] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded text-[10px] font-display font-bold uppercase tracking-widest border transition-all ${tab === t ? "bg-primary/10 border-primary/30 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {t === "active" ? `Active (${alliances.length})` : t === "pending" ? `Incoming (${pending.length})` : "Send Request"}
            </button>
          ))}
        </div>
      </div>

      {/* Active alliances */}
      {tab === "active" && (
        alliances.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-lg">
            <Shield className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
            <p className="font-display font-bold uppercase tracking-widest text-muted-foreground text-sm">No active alliances</p>
            <p className="text-xs text-muted-foreground font-sans mt-1">Send an alliance request to another unit to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {alliances.map((a: any) => {
              const ally = getAllyInfo(a);
              return (
                <div key={a.id} className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
                  <div className="w-10 h-10 shrink-0 bg-secondary border border-border rounded flex items-center justify-center overflow-hidden">
                    {ally.logo ? <img src={ally.logo} className="w-full h-full object-contain p-1" onError={e => (e.currentTarget.style.display = "none")} /> : <Shield className="w-5 h-5 text-muted-foreground/40" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-black text-sm uppercase tracking-wider text-foreground truncate">{ally.name}</div>
                    {ally.tag && <div className="text-[10px] text-primary font-display font-bold uppercase tracking-widest">[{ally.tag}]</div>}
                    <div className="text-[10px] text-muted-foreground font-sans">Allied {new Date(a.created_date).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a href={`/milsim/${ally.gid}`} target="_blank" className="p-1.5 rounded border border-border text-muted-foreground hover:text-foreground transition-colors">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <button onClick={() => dissolve(a.id)} className="p-1.5 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Pending incoming */}
      {tab === "pending" && (
        pending.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-lg">
            <Shield className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
            <p className="font-display font-bold uppercase tracking-widest text-muted-foreground text-sm">No pending requests</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pending.map((a: any) => {
              const requester = { name: a.requester_group_name ?? "Unknown", tag: a.requester_group_tag ?? "", logo: a.requester_logo_url ?? "", gid: a.requester_group_id };
              return (
                <div key={a.id} className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 shrink-0 bg-secondary border border-border rounded flex items-center justify-center overflow-hidden">
                      {requester.logo ? <img src={requester.logo} className="w-full h-full object-contain p-1" onError={e => (e.currentTarget.style.display = "none")} /> : <Shield className="w-5 h-5 text-muted-foreground/40" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-black text-sm uppercase tracking-wider text-foreground">{requester.name}</div>
                      {requester.tag && <div className="text-[10px] text-primary font-display font-bold uppercase tracking-widest">[{requester.tag}]</div>}
                    </div>
                    <a href={`/milsim/${requester.gid}`} target="_blank" className="p-1.5 rounded border border-border text-muted-foreground hover:text-foreground transition-colors">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                  {a.message && <p className="text-xs font-sans text-muted-foreground italic mb-3 border-l-2 border-border pl-3">{a.message}</p>}
                  <div className="flex gap-2">
                    <button onClick={() => respond(a.id, "accept")} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-primary hover:bg-primary/90 text-primary-foreground text-[10px] font-display font-bold uppercase tracking-widest transition-all">
                      <Check className="w-3 h-3" /> Accept
                    </button>
                    <button onClick={() => respond(a.id, "reject")} className="px-3 py-1.5 rounded border border-border text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all">
                      Decline
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Send request */}
      {tab === "send" && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-lg p-5 space-y-4">
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1.5">Search for a unit</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input
                  className="w-full bg-background border border-border rounded pl-9 pr-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/50"
                  placeholder="Search by unit name..."
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); searchGroups(e.target.value); }}
                />
              </div>
            </div>
            {searching && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching...</div>}
            {searchResults.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {searchResults.map((g: any) => (
                  <div key={g.id} className="flex items-center gap-3 p-3 rounded border border-border bg-secondary/30 hover:bg-secondary/60 transition-colors">
                    <div className="w-8 h-8 shrink-0 bg-background border border-border rounded flex items-center justify-center overflow-hidden">
                      {g.logo_url ? <img src={g.logo_url} className="w-full h-full object-contain p-0.5" onError={e => (e.currentTarget.style.display = "none")} /> : <Shield className="w-4 h-4 text-muted-foreground/40" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-xs uppercase tracking-wider text-foreground truncate">{g.name}</div>
                      <div className="text-[10px] text-muted-foreground font-sans">{(g.games ?? []).slice(0, 2).join(", ")}</div>
                    </div>
                    <button onClick={() => sendRequest(g)} disabled={sending}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary text-[10px] font-display font-bold uppercase tracking-widest transition-all disabled:opacity-50">
                      {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Request
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground block mb-1.5">Message (optional)</label>
              <textarea
                className="w-full bg-background border border-border rounded px-3 py-2 text-sm font-sans text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/50 resize-none"
                rows={3}
                placeholder="Introduce your unit..."
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Vacancy Board Tab (HQ) ───────────────────────────────────────────────────
const MARKET_URL = "https://agent-tag-lead-developer-cff87ae4.base44.app/functions/recruitmentMarket";

const VACANCY_ROLE_TAGS = [
  "Section Commander","Platoon Commander","Company Commander",
  "Rifleman","Automatic Rifleman","Grenadier","Designated Marksman","Sniper",
  "JTAC","Mortar Operator","Machine Gunner","Anti-Tank",
  "Medic","Combat Engineer","Breacher","EOD",
  "Pilot","Co-Pilot","Crew Chief","Door Gunner",
  "Vehicle Commander","Driver","Gunner",
  "Intelligence Officer","Signals","Logistics","Staff Officer"
];

const VACANCY_GAMES = [
  "Arma 3","Arma Reforger","Squad","Hell Let Loose","Ready Or Not",
  "EFT","Ground Branch","DayZ","GZW","Body Cam","Operator","Exfil"
];

function VacancyBoardTab({ group, showMsg, globalIsPro }: any) {
  const { token } = useAuth() as any;
  const [vacancies, setVacancies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<"list" | "create" | "applications">("list");
  const [selectedVacancy, setSelectedVacancy] = useState<any | null>(null);
  const [applications, setApplications] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", role_tag: "", description: "", games: [] as string[],
    min_ops: "", min_reputation_score: "", require_no_blacklist: true,
    require_prior_leadership: false, preferred_timezone: "",
    preferred_availability: "", comms_standard: "",
    our_standard: "", selection_process: "", probation_detail: "",
  });

  const authH = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    fetch(`${MARKET_URL}?path=/vacancies/group/${group.id}`, { headers: authH })
      .then(r => r.json()).then(d => { setVacancies(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [group.id]);

  const create = async () => {
    if (!form.title || !form.role_tag) { showMsg(false, "Title and role are required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${MARKET_URL}?path=/vacancies`, {
        method: "POST", headers: authH,
        body: JSON.stringify({ ...form, group_id: group.id, min_ops: form.min_ops ? Number(form.min_ops) : null, min_reputation_score: form.min_reputation_score ? Number(form.min_reputation_score) : null }),
      });
      const d = await res.json();
      if (!res.ok) { showMsg(false, d.error ?? "Failed to create vacancy"); }
      else { showMsg(true, "Vacancy posted to Recruitment Marketplace"); setView("list"); load(); setForm({ title: "", role_tag: "", description: "", games: [], min_ops: "", min_reputation_score: "", require_no_blacklist: true, require_prior_leadership: false, preferred_timezone: "", preferred_availability: "", comms_standard: "", our_standard: "", selection_process: "", probation_detail: "" }); }
    } finally { setSaving(false); }
  };

  const closeVacancy = async (id: string) => {
    await fetch(`${MARKET_URL}?path=/vacancies/${id}`, { method: "PATCH", headers: authH, body: JSON.stringify({ status: "closed" }) });
    showMsg(true, "Vacancy closed"); load();
  };

  const deleteVacancy = async (id: string) => {
    if (!confirm("Delete this vacancy?")) return;
    await fetch(`${MARKET_URL}?path=/vacancies/${id}`, { method: "DELETE", headers: authH });
    showMsg(true, "Vacancy deleted"); load();
  };

  const loadApplications = async (v: any) => {
    setSelectedVacancy(v); setView("applications");
    const res = await fetch(`${MARKET_URL}?path=/vacancies/${v.id}/applications`, { headers: authH });
    setApplications(Array.isArray(await res.json()) ? await res.json() : []);
    // re-fetch properly
    fetch(`${MARKET_URL}?path=/vacancies/${v.id}/applications`, { headers: authH })
      .then(r => r.json()).then(d => setApplications(Array.isArray(d) ? d : []));
  };

  const reviewApplication = async (appId: string, status: string, note = "") => {
    await fetch(`${MARKET_URL}?path=/vacancies/applications/${appId}`, { method: "PATCH", headers: authH, body: JSON.stringify({ status, review_note: note }) });
    showMsg(true, `Application ${status}`);
    if (selectedVacancy) loadApplications(selectedVacancy);
  };

  const toggleGame = (g: string) => setForm(f => ({ ...f, games: f.games.includes(g) ? f.games.filter(x => x !== g) : [...f.games, g] }));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  /* ── Applications view ── */
  if (view === "applications" && selectedVacancy) return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => setView("list")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="w-3 h-3" />Back</button>
        <div>
          <h3 className="font-display font-bold uppercase tracking-wider text-sm">{selectedVacancy.title} — Applications</h3>
          <p className="text-xs text-muted-foreground">{applications.length} applicant{applications.length !== 1 ? "s" : ""}</p>
        </div>
      </div>
      {applications.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <Users className="w-8 h-8 mx-auto mb-3 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground font-display uppercase tracking-widest">No applications yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {applications.map((a: any) => (
            <div key={a.id} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display font-bold text-sm uppercase tracking-wide text-foreground">{(a.applicant_username ?? "Unknown").toUpperCase()}</div>
                  <div className={`text-xs mt-0.5 font-mono px-1.5 py-0.5 rounded border w-fit ${
                    a.status === "accepted" ? "text-green-400 border-green-500/30 bg-green-500/10" :
                    a.status === "rejected" ? "text-red-400 border-red-500/30 bg-red-500/10" :
                    "text-amber-400 border-amber-500/30 bg-amber-500/10"
                  }`}>{(a.status ?? "pending").toUpperCase()}</div>
                </div>
                {a.status === "pending" && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => reviewApplication(a.id, "accepted")} className="text-xs px-3 py-1.5 bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 rounded font-bold uppercase tracking-wide transition-colors flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />Accept
                    </button>
                    <button onClick={() => reviewApplication(a.id, "rejected")} className="text-xs px-3 py-1.5 bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 rounded font-bold uppercase tracking-wide transition-colors flex items-center gap-1">
                      <X className="w-3 h-3" />Decline
                    </button>
                  </div>
                )}
              </div>
              {a.cover_note && <p className="text-xs text-muted-foreground font-sans mt-3 border-l-2 border-border pl-3 italic">{a.cover_note}</p>}
              <div className="mt-3">
                <Link href={`/u/${a.applicant_username}`} className="text-xs text-primary hover:underline flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />View operator profile
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /* ── Create form ── */
  if (view === "create") return (
    <div className="space-y-5 max-w-2xl">
      <div className="flex items-center gap-3">
        <button onClick={() => setView("list")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><ChevronLeft className="w-3 h-3" />Back</button>
        <h3 className="font-display font-bold uppercase tracking-wider text-sm">Post New Vacancy</h3>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1 block">Vacancy Title *</label>
          <input className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-primary" placeholder="e.g. Looking for experienced Section Commander" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1 block">Role *</label>
          <select className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-primary text-foreground" value={form.role_tag} onChange={e => setForm(f => ({ ...f, role_tag: e.target.value }))}>
            <option value="">Select role...</option>
            {VACANCY_ROLE_TAGS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1 block">Min Ops</label>
          <input type="number" min="0" className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-primary" placeholder="0 = no minimum" value={form.min_ops} onChange={e => setForm(f => ({ ...f, min_ops: e.target.value }))} />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2 block">Games</label>
        <div className="flex flex-wrap gap-1.5">
          {VACANCY_GAMES.map(g => (
            <button key={g} type="button" onClick={() => toggleGame(g)}
              className={`text-xs px-2 py-1 rounded border transition-colors ${form.games.includes(g) ? "bg-primary/20 text-primary border-primary/40" : "bg-secondary text-muted-foreground border-border hover:border-primary/30"}`}>
              {g}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1 block">Description</label>
        <textarea className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm resize-none h-24 focus:outline-none focus:border-primary" placeholder="What are you looking for in a candidate?" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1 block">Preferred Timezone</label>
          <input className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-primary" placeholder="e.g. GMT, EST" value={form.preferred_timezone} onChange={e => setForm(f => ({ ...f, preferred_timezone: e.target.value }))} />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1 block">Availability</label>
          <input className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-primary" placeholder="e.g. Weekends, Evenings" value={form.preferred_availability} onChange={e => setForm(f => ({ ...f, preferred_availability: e.target.value }))} />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1 block">Comms Standard</label>
          <input className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-primary" placeholder="e.g. Discord, TeamSpeak" value={form.comms_standard} onChange={e => setForm(f => ({ ...f, comms_standard: e.target.value }))} />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1 block">Our Standard</label>
          <input className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-primary" placeholder="e.g. Milsim, Tactical Casual" value={form.our_standard} onChange={e => setForm(f => ({ ...f, our_standard: e.target.value }))} />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1 block">Selection Process</label>
        <textarea className="w-full bg-secondary border border-border rounded px-3 py-2 text-sm resize-none h-20 focus:outline-none focus:border-primary" placeholder="Describe your selection process..." value={form.selection_process} onChange={e => setForm(f => ({ ...f, selection_process: e.target.value }))} />
      </div>

      <div className="flex gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.require_no_blacklist} onChange={e => setForm(f => ({ ...f, require_no_blacklist: e.target.checked }))} className="accent-primary" />
          <span className="text-sm">Require no blacklist</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.require_prior_leadership} onChange={e => setForm(f => ({ ...f, require_prior_leadership: e.target.checked }))} className="accent-primary" />
          <span className="text-sm">Require prior leadership</span>
        </label>
      </div>

      <button onClick={create} disabled={saving} className="px-6 py-2.5 bg-primary text-primary-foreground font-display font-bold uppercase tracking-widest text-xs rounded hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2">
        {saving && <Loader2 className="w-3 h-3 animate-spin" />}
        Post Vacancy
      </button>
    </div>
  );

  /* ── Vacancy list ── */
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-display font-black text-xl uppercase tracking-wider text-foreground">Vacancy Board</h2>
          <p className="text-xs text-muted-foreground font-sans mt-0.5">Post roles to the public Recruitment Marketplace. Operators can apply directly.</p>
        </div>
        <button onClick={() => setView("create")} className="flex items-center gap-2 text-xs bg-primary text-primary-foreground px-4 py-2 rounded font-display font-bold uppercase tracking-wide hover:bg-primary/90 transition-colors">
          <Plus className="w-3 h-3" />Post Vacancy
        </button>
      </div>

      {vacancies.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <Briefcase className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
          <p className="font-display font-bold uppercase tracking-widest text-muted-foreground text-sm">No vacancies posted</p>
          <p className="text-xs text-muted-foreground font-sans mt-1">Post your first vacancy to appear on the public Recruitment Marketplace.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {vacancies.map((v: any) => (
            <div key={v.id} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-sm uppercase tracking-wide text-foreground">{v.title}</span>
                    <span className="text-xs border border-primary/30 text-primary bg-primary/10 px-1.5 py-0.5 rounded font-mono">{v.role_tag}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded border font-mono ${v.status === "open" ? "text-green-400 border-green-500/30 bg-green-500/10" : "text-muted-foreground border-border"}`}>{(v.status ?? "open").toUpperCase()}</span>
                  </div>
                  {Array.isArray(v.games) && v.games.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {v.games.map((g: string) => <span key={g} className="text-xs bg-secondary text-muted-foreground px-2 py-0.5 rounded-full border border-border">{g}</span>)}
                    </div>
                  )}
                  <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                    {v.min_ops && <span>{v.min_ops}+ ops required</span>}
                    {v.require_no_blacklist && <span className="text-green-400">No blacklist</span>}
                    <span>{v.applicant_count ?? 0} applicant{(v.applicant_count ?? 0) !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => loadApplications(v)} className="text-xs px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground rounded font-bold uppercase tracking-wide transition-colors flex items-center gap-1">
                    <Users className="w-3 h-3" />Applications
                  </button>
                  {v.status === "open" && (
                    <button onClick={() => closeVacancy(v.id)} className="text-xs px-3 py-1.5 border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 rounded font-bold uppercase tracking-wide transition-colors">Close</button>
                  )}
                  <button onClick={() => deleteVacancy(v.id)} className="p-1.5 border border-red-500/30 text-red-400 hover:bg-red-500/10 rounded transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
